import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { setTenantHeader, setCompanyHeader, API } from '../lib/api';
import {
  LayoutDashboard, Building2, ShoppingCart, FileText, Wallet, DollarSign,
  CreditCard, Landmark, Receipt, BarChart3, Plus, Minus, X, Save, LogOut,
  Menu as MenuIcon, ChevronDown, ChevronUp, ChevronRight, CheckCircle, Clock, XCircle, ArrowUpRight, ArrowDownRight,
  TrendingUp, Package, Search, Filter, Eye, Trash2, Edit, RefreshCw,
  Users, Shield, Crown, Boxes, Activity, AlertTriangle, Printer, UserCheck, Share2, QrCode, Sparkles, Download, FileUp, Maximize2, Minimize2,
  Monitor, Smartphone, Calculator, Calendar, Wifi, WifiOff, ChevronLeft, Settings as SettingsIcon,
  ArrowRightLeft, ArrowDownUp, ArrowDownToLine, Truck, Send, PackageCheck, RotateCcw, Check, ExternalLink, Tag, Target, CircleDollarSign, PackagePlus, Store, Copy, SlidersHorizontal
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { TenantManifest } from '../components/TenantManifest';
import { DataTable } from '../components/DataTable';
import { LabelsPage, LabelPrintModal } from './LabelsPage';
import { MetasPage, MetaDashboardCard } from './MetasPage';
import { NaturezaOperacaoPage } from './NaturezaOperacaoPage';
import { InvoicesPage } from './InvoicesPage';
import { ManifestoPage } from './ManifestoPage';
import { PurchasesPage, PurchaseRequestsPage } from './PurchasesPage';
import PharmacyPage from './PharmacyPage';
// iter156dz: Ordens de Servico / Locacao (v1)
import ServiceOrdersPage from './ServiceOrdersPage';
import { FormModal } from '../components/FormModal';
import { SettingsManagement } from './SettingsManagement';
import { localDateISO, localFirstOfMonthISO, localDaysAgoISO } from '../lib/dateUtils';
import { confirmDialog } from '../components/ConfirmDialog';

const ERP = `${API}/erp`;

/* ========== Helper: fmtDateBR — date in BRT timezone (handles UTC ISO strings) ==========
   For full ISO strings (e.g. "2026-06-09T01:00:00+00:00"), uses Date+toLocaleDateString('pt-BR')
   which respects the user's local timezone. For date-only strings (YYYY-MM-DD), reverses to DD/MM/YYYY
   without timezone shift (since date-only has no tz). */
const fmtDateBR = (val) => {
  if (!val) return '—';
  const s = String(val);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return s.split('-').reverse().join('/');
  }
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s.slice(0, 10).split('-').reverse().join('/');
    return d.toLocaleDateString('pt-BR');
  } catch {
    return s.slice(0, 10).split('-').reverse().join('/');
  }
};

// iter156dt2: helper de mascara decimal automatica por unidade de medida (pt-BR).
// SEMPRE reformata a partir dos digitos crus — evita bugs como "0.001.0000" quando
// o usuario digitava "." explicitamente. Formato pt-BR com virgula.
//   KG/L -> 3 decimais (base = grama/mL)
//   M    -> 2 decimais (base = cm)
//   Outras (UN, PC, CX, etc.) -> 0 decimais.
// Exemplos: unit=KG "500" -> "0,500" (=500g); "1000" -> "1,000" (=1kg); "12345" -> "12,345".
const decimalsForUnit = (unit) => {
  const u = String(unit || 'UN').toUpperCase();
  if (u === 'KG' || u === 'L') return 3;
  if (u === 'M') return 2;
  return 0;
};
const smartQtyMask = (raw, unit) => {
  const dec = decimalsForUnit(unit);
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (dec === 0) return String(parseInt(digits, 10));
  const n = parseInt(digits, 10);
  // pt-BR com separador de milhar: "12345" (KG) -> "12,345" ; "1234567" -> "1.234,567"
  return (n / Math.pow(10, dec)).toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};
// iter156dt2: parseia string mascarada pt-BR ("1.234,567") ou US ("1234.567") em Number.
const smartQtyParse = (masked) => {
  if (masked == null || masked === '') return NaN;
  const s = String(masked).trim();
  // pt-BR: virgula = decimal, ponto = milhar
  if (s.includes(',')) return parseFloat(s.replace(/\./g, '').replace(',', '.'));
  return parseFloat(s);
};



// === 8IP BRAND TOKENS ===
// Primary: midnight #0a0a4a, Accent gradient: violet-600 → cyan-500
const inputClass = "w-full px-3 py-2.5 bg-white border-2 border-slate-300 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-violet-600 focus:ring-2 focus:ring-violet-500/20 transition-all";
const btnPrimary = "btn-pill-primary";

// iter77ct.100: helpers de mascara BRL - digitacao progressiva "5" -> "0,05", "5999" -> "59,99"
// Aceita string ou number; retorna string formatada com virgula e milhares (pt-BR).
const brlMaskDisplay = (raw) => {
  if (raw == null || raw === '') return '';
  // Se ja vier como number, converte de reais para centavos primeiro (2 casas)
  let digits;
  if (typeof raw === 'number') {
    digits = Math.round(raw * 100).toString();
  } else {
    digits = String(raw).replace(/\D/g, '');
  }
  if (!digits) return '';
  const n = parseInt(digits, 10);
  return (n / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// Converte string mascarada ou "12,50" ou "12.50" em Number.
const brlMaskParse = (masked) => {
  if (masked == null || masked === '') return NaN;
  const s = String(masked).replace(/\./g, '').replace(',', '.');
  return parseFloat(s);
};

// ============== Business Type Context ==============
// Expose `pharmacy_module` and other BT flags to deep child components without prop drilling.
// Components do: const { isPharmacy } = useBusinessType();
export const BusinessTypeContext = React.createContext({ pharmacy_module: false, business_type_slug: null, business_type_name: null, cashback_enabled: false });
export const useBusinessType = () => {
  const ctx = React.useContext(BusinessTypeContext);
  return { ...ctx, isPharmacy: !!ctx.pharmacy_module, isCashback: !!ctx.cashback_enabled };
};

const ERPDashboard = () => {
  const { user, logout } = useAuth();
  const params = useParams();
  const location = useLocation();
  const slug = params.slug || '';
  const [tenantReady, setTenantReady] = useState(false);
  const [companies, setCompanies] = useState([]);
  // iter77ct.96: persiste a empresa selecionada em localStorage por tenant (slug)
  // para restaurar quando o usuario atualizar a pagina ou logar novamente.
  const lastCompanyKey = slug ? `erp_last_company_${slug}` : 'erp_last_company';
  const [selectedCompany, setSelectedCompany] = useState(null);
  // Wrapper que salva no localStorage a cada troca
  const selectCompany = useCallback((c) => {
    setSelectedCompany(c);
    try {
      if (c?.id) localStorage.setItem(lastCompanyKey, c.id);
      else localStorage.removeItem(lastCompanyKey);
    } catch { /* ignore */ }
  }, [lastCompanyKey]);
  // iter77f: sincroniza header x-company-id com a empresa selecionada para
  // que endpoints como GET /api/erp/purchase-requests filtrem corretamente.
  useEffect(() => {
    setCompanyHeader(selectedCompany?.id || null);
  }, [selectedCompany]);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => { try { return localStorage.getItem('erp_sidebar_collapsed') === '1'; } catch { return false; } });
  // Collapsible sidebar groups (accordion) — start expanded only for "Principal" + the active section group
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('erp_sidebar_open_groups') || '{}'); }
    catch { return {}; }
  });
  const toggleGroup = useCallback((title) => {
    setOpenGroups(prev => {
      const next = { ...prev, [title]: !prev[title] };
      try { localStorage.setItem('erp_sidebar_open_groups', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed(v => { try { localStorage.setItem('erp_sidebar_collapsed', !v ? '1' : '0'); } catch { /* ignore */ } return !v; });
  }, []);
  const [section, setSection] = useState('dashboard');
  const [allowedModules, setAllowedModules] = useState(null);
  const [moduleActions, setModuleActions] = useState({});
  const [pdvPerms, setPdvPermsApp] = useState(null);
  const [dashboardCards, setDashboardCards] = useState({});
  const [businessType, setBusinessType] = useState({ pharmacy_module: false, business_type_slug: null, business_type_name: null, cashback_enabled: false });
  const [menuOpen, setMenuOpen] = useState(false);
  const [pdvFullscreen, setPdvFullscreen] = useState(false);
  // Desktop top-nav dropdown state (which group dropdown is open). Mobile is unaffected.
  const [openTopGroup, setOpenTopGroup] = useState(null);
  useEffect(() => {
    if (!openTopGroup) return;
    const close = (e) => {
      if (!e.target.closest?.('[data-topnav-root]')) setOpenTopGroup(null);
    };
    const esc = (e) => { if (e.key === 'Escape') setOpenTopGroup(null); };
    window.addEventListener('click', close);
    window.addEventListener('keydown', esc);
    return () => { window.removeEventListener('click', close); window.removeEventListener('keydown', esc); };
  }, [openTopGroup]);
  // Force PDV fullscreen off when navigating away from PDV
  useEffect(() => { if (section !== 'pdv') setPdvFullscreen(false); }, [section]);

  useEffect(() => {
    const resolve = async () => {
      if (slug) {
        try {
          const { data } = await axios.get(`${API}/tenant/resolve?subdomain=${slug}`);
          setTenantHeader(data.id);
        } catch { /* ignore */ }
      }
      setTenantReady(true);
    };
    resolve();
  }, [slug]);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ERP}/companies`, { withCredentials: true });
      const list = data.data || [];
      setCompanies(list);
      if (!selectedCompany && list.length > 0) {
        // iter77ct.96: tenta restaurar a ultima empresa acessada; se nao existir mais,
        // cai para a primeira da lista (comportamento antigo).
        let toSelect = list[0];
        try {
          const lastId = localStorage.getItem(lastCompanyKey);
          if (lastId) {
            const found = list.find(c => c.id === lastId);
            if (found) toSelect = found;
          }
        } catch { /* ignore */ }
        setSelectedCompany(toSelect);
      }
    } catch { /* ignore */ }
  }, [selectedCompany, lastCompanyKey]);

  useEffect(() => {
    if (tenantReady) fetchCompanies();
  }, [tenantReady, fetchCompanies]);

  useEffect(() => {
    if (tenantReady) {
      const fetchModules = async () => {
        try {
          const { data } = await axios.get(`${ERP}/allowed-modules`, { withCredentials: true });
          setAllowedModules(data.modules || {});
          setModuleActions(data.actions || {});
          setPdvPermsApp(data.pdv_perms || null);
          setDashboardCards(data.dashboard_cards || {});
          setBusinessType(data.business_type || { pharmacy_module: false });
        } catch { setAllowedModules({}); setModuleActions({}); setPdvPermsApp(null); setDashboardCards({}); setBusinessType({ pharmacy_module: false }); }
      };
      fetchModules();
    }
  }, [tenantReady]);

  useEffect(() => { setSidebarOpen(false); }, [section]);

  // URL-based section mapping: /{slug}/vendas -> section 'sales'
  const URL_TO_SECTION = {
    'dashboard': 'dashboard', 'pdv': 'pdv', 'vendas': 'sales', 'pedidos-venda': 'sales-orders',
    'caixa': 'cash', 'relatorio': 'sales-report', 'estoque': 'stock', 'tabela-precos': 'price-tables',
    'produtos': 'products', 'categorias': 'categories', 'pedidos': 'orders', 'config-catalogo': 'settings', 'catalogo': 'settings',
    'movimentacao-estoque': 'stock-movements', 'transferencia-estoque': 'stock-transfers',
    'clientes': 'clients', 'fornecedores': 'suppliers', 'funcionarios': 'employees',
    'financeiro': 'financial', 'plano-contas': 'chart-accounts', 'banco': 'bank-accounts',
    'formas-pagamento': 'payment-methods', 'fechamento': 'closing', 'recibos': 'receipts',
    'empresas': 'companies', 'usuarios': 'users', 'perfis-acesso': 'access-profiles',
    'config-empresa': 'company-settings', 'configuracao': 'company-settings', 'configuracoes': 'company-settings', 'meu-plano': 'plans',
    'integracoes': 'integrations',
    'ia': 'ai-insights', 'insights': 'ai-insights', 'ai-insights': 'ai-insights',
    'dre': 'dre', 'gerencial': 'dre',
    'lucro-direto': 'profit-direct', 'profit-direct': 'profit-direct',
    'comissoes': 'commission', 'comissao': 'commission', 'commission': 'commission',
    'etiquetas': 'labels', 'labels': 'labels',
    'metas': 'metas',
    // iter156aq: aliases dos novos modulos
    'ponto': 'ponto', 'ponto-eletronico': 'ponto',
    // iter156bh: relatorio de ponto no menu Gerencial
    'relatorio-ponto': 'ponto-report', 'ponto-report': 'ponto-report',
    'producao': 'production-orders', 'ordens-producao': 'production-orders', 'production-orders': 'production-orders',
    'mdfe': 'mdfe', 'mdf-e': 'mdfe',
    'natureza-operacao': 'natureza-operacao', 'naturezas': 'natureza-operacao',
    'nf': 'invoices', 'notas-fiscais': 'invoices', 'invoices': 'invoices',
    'manifesto': 'manifesto', 'nf-entrada': 'manifesto', 'nf-de-entrada': 'manifesto',
    'compras': 'purchases', 'purchases': 'purchases',
    'solicitacoes-compra': 'purchase-requests', 'solicitacoes-de-compra': 'purchase-requests', 'purchase-requests': 'purchase-requests',
    'farmacia': 'pharmacy', 'pharmacy': 'pharmacy',
    'cashback': 'cashback',
    'logs': 'logs', 'auditoria': 'logs',
  };
  const SECTION_TO_URL = Object.fromEntries(Object.entries(URL_TO_SECTION).map(([k, v]) => [v, k]));

  const pathSection = location.pathname.split('/').filter(Boolean).pop();
  useEffect(() => {
    const mapped = URL_TO_SECTION[pathSection];
    if (mapped) setSection(mapped);
  }, [pathSection]);

  if (!tenantReady || allowedModules === null) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-10 w-10 border-2 border-teal-600 border-t-transparent" /></div>;

  const isAllowed = (key) => allowedModules[key] !== false;
  const canDo = (action, moduleKey) => {
    const a = moduleActions[moduleKey];
    if (!a) return isAllowed(moduleKey); // legacy fallback
    return !!a[action];
  };
  // Expose canDo globally so any descendant can call it
  if (typeof window !== 'undefined') window.__canDo = canDo;

  const goToSection = (key, opts = {}) => {
    setSection(key);
    setMenuOpen(false);
    setSidebarOpen(false);
    const urlPart = SECTION_TO_URL[key] || key;
    const qs = opts.query ? `?${new URLSearchParams(opts.query).toString()}` : '';
    window.history.replaceState(null, '', `/${slug}/${urlPart}${qs}`);
  };
  // iter77cz: expose goToSection globally for cross-section navigation
  // (Sales → Invoices with highlight/preselect).
  if (typeof window !== 'undefined') window.__goToSection = goToSection;

  const allModules = [
    { key: 'dashboard', icon: LayoutDashboard, label: 'Inicio', group: 'Principal' },
    { key: 'ai-insights', icon: Sparkles, label: 'IA Insights', group: 'Gerencial' },
    { key: 'dre', icon: TrendingUp, label: 'DRE', group: 'Gerencial' },
    { key: 'clients', icon: Users, label: 'Clientes', group: 'Cadastros' },
    { key: 'suppliers', icon: Building2, label: 'Fornecedores', group: 'Cadastros' },
    { key: 'employees', icon: UserCheck, label: 'Funcionarios', group: 'Cadastros' },
    { key: 'products', icon: Package, label: 'Produtos', group: 'Itens' },
    { key: 'categories', icon: FileText, label: 'Categorias', group: 'Itens' },
    { key: 'stock', icon: Boxes, label: 'Estoque', group: 'Itens' },
    { key: 'stock-movements', icon: ArrowDownUp, label: 'Movimentacao Estoque', group: 'Itens' },
    { key: 'stock-transfers', icon: ArrowRightLeft, label: 'Transferencia Estoque', group: 'Itens' },
    { key: 'price-tables', icon: DollarSign, label: 'Tabela de Precos', group: 'Itens' },
    { key: 'labels', icon: Tag, label: 'Etiquetas', group: 'Itens' },
    { key: 'settings', moduleKey: 'catalog-settings', icon: Sparkles, label: 'Catalogo', group: 'Itens' },
    { key: 'sales-orders', icon: FileText, label: 'Pedidos de Venda', group: 'Vendas' },
    { key: 'sales', icon: BarChart3, label: 'Vendas', group: 'Vendas' },
    // iter156dz: Ordem de Servico logo abaixo de Vendas (aparece so quando SuperAdmin liga)
    ...(businessType?.os_module_enabled ? [{ key: 'service-orders', icon: FileText, label: businessType?.os_module_label || 'Ordem de Servico', group: 'Vendas' }] : []),
    { key: 'cash', icon: Wallet, label: 'Caixa', group: 'Vendas' },
    { key: 'sales-report', icon: BarChart3, label: 'Relatorio', group: 'Vendas' },
    { key: 'metas', icon: Target, label: 'Metas', group: 'Vendas' },
    { key: 'profit-direct', icon: TrendingUp, label: 'Lucro Direto', group: 'Gerencial' },
    { key: 'commission', icon: UserCheck, label: 'Comissoes', group: 'Gerencial' },
    { key: 'financial', icon: DollarSign, label: 'Financeiro', group: 'Financeiro' },
    { key: 'chart-accounts', icon: FileText, label: 'Plano Contas', group: 'Financeiro' },
    { key: 'bank-accounts', icon: Landmark, label: 'Banco', group: 'Financeiro' },
    { key: 'payment-methods', icon: CreditCard, label: 'Formas Pgto', group: 'Financeiro' },
    { key: 'closing', icon: DollarSign, label: 'Fechamento', group: 'Financeiro' },
    { key: 'receipts', icon: Receipt, label: 'Recibos', group: 'Financeiro' },
    { key: 'companies', icon: Building2, label: 'Empresas', group: 'Administracao' },
    { key: 'company-settings', icon: TrendingUp, label: 'Configuracao', group: 'Administracao' },
    { key: 'users', icon: Users, label: 'Usuarios', group: 'Administracao' },
    { key: 'access-profiles', icon: Shield, label: 'Perfis Acesso', group: 'Administracao' },
    { key: 'natureza-operacao', icon: FileText, label: 'Config. de NF', group: 'Fiscal' },
    { key: 'invoices', icon: Receipt, label: 'NF de Venda', group: 'Fiscal' },
    { key: 'manifesto', icon: ArrowDownToLine, label: 'NF de Entrada', group: 'Fiscal' },
    { key: 'purchases', icon: ShoppingCart, label: 'Compras', group: 'Compras' },
    { key: 'purchase-requests', icon: FileText, label: 'Solicitacoes de Compra', group: 'Compras' },
    { key: 'pharmacy', icon: Sparkles, label: 'Farmacia', group: 'Farmacia' },
    ...(businessType?.cashback_enabled ? [{ key: 'cashback', icon: Wallet, label: 'Cashback', group: 'Cadastros' }] : []),
    // iter156aq/aw: modulos toggle-avel via SuperAdmin — ganham menu quando BT libera
    // E o perfil de acesso tem permissao (isAllowed cobre esse segundo teste)
    ...(businessType?.ponto_module_enabled ? [{ key: 'ponto', icon: UserCheck, label: 'Ponto Eletronico', group: 'Cadastros' }] : []),
    // iter156bh: Relatorio de Ponto — menu Gerencial (gate: ponto habilitado + perfil libera 'ponto-report')
    ...(businessType?.ponto_module_enabled ? [{ key: 'ponto-report', icon: Clock, label: 'Relatorio de Ponto', group: 'Gerencial' }] : []),
    ...(businessType?.production_module_enabled ? [{ key: 'production-orders', icon: Package, label: 'Ordens de Producao', group: 'Itens' }] : []),
    ...(businessType?.mdfe_module_enabled ? [{ key: 'mdfe', icon: FileText, label: 'MDF-e', group: 'Fiscal' }] : []),
    { key: 'plans', icon: Crown, label: 'Meu Plano', group: 'Administracao' },
    { key: 'integrations', icon: Share2, label: 'AgentCRM', group: 'Administracao' },
    { key: 'logs', icon: FileText, label: 'Logs de Auditoria', group: 'Administracao' },
  ].filter(i => isAllowed(i.moduleKey || i.key));

  const navGroups = [...new Set(allModules.map(m => m.group))].map(g => ({
    title: g, items: allModules.filter(m => m.group === g)
  }));

  // Bottom nav items for mobile — iter77ct.13: usa config do BusinessType quando disponivel
  // Icones/labels centralizados. Fallback: [dashboard, pdv, stock, financial] (comportamento antigo).
  const MOBILE_ICON_MAP = {
    dashboard: { icon: LayoutDashboard, label: 'Inicio' },
    pdv: { icon: ShoppingCart, label: 'PDV' },
    sales: { icon: Receipt, label: 'Vendas' },
    quotes: { icon: FileText, label: 'Orcamentos' },
    sales_orders: { icon: Package, label: 'Pedidos' },
    cash: { icon: Wallet, label: 'Caixa' },
    stock: { icon: Boxes, label: 'Estoque' },
    stock_movements: { icon: TrendingUp, label: 'Mov. Estoque' },
    stock_transfers: { icon: PackageCheck, label: 'Transf.' },
    financial: { icon: DollarSign, label: 'Financeiro' },
    receivables: { icon: Wallet, label: 'Receber' },
    payables: { icon: Landmark, label: 'Pagar' },
    bank_accounts: { icon: Landmark, label: 'Contas' },
    clients: { icon: Users, label: 'Clientes' },
    suppliers: { icon: Users, label: 'Fornec.' },
    products: { icon: Package, label: 'Produtos' },
    purchases: { icon: ShoppingCart, label: 'Compras' },
    purchase_requests: { icon: Send, label: 'Solicit.' },
    sales_report: { icon: BarChart3, label: 'Relat.' },
  };
  const configuredNav = (businessType?.mobile_bottom_nav || []).filter(Boolean);
  const bottomNavItems = (configuredNav.length > 0
    ? configuredNav.slice(0, 4).map(key => {
        const conf = MOBILE_ICON_MAP[key] || { icon: LayoutDashboard, label: key };
        return { key, icon: conf.icon, label: conf.label };
      })
    : [
        { key: 'dashboard', icon: LayoutDashboard, label: 'Inicio' },
        { key: 'pdv', icon: ShoppingCart, label: 'PDV' },
        { key: 'stock', icon: Boxes, label: 'Estoque' },
        { key: 'financial', icon: DollarSign, label: 'Financeiro' },
      ]
  ).filter(i => isAllowed(i.key));

  const sectionLabel = allModules.find(i => i.key === section)?.label || 'Inicio';

  return (
    <BusinessTypeContext.Provider value={businessType}>
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="erp-dashboard">
      <TenantManifest />

      {/* ===== DESKTOP TOP NAV BAR (hidden on mobile) ===== */}
      {!pdvFullscreen && (
        <header className="hidden md:block sticky top-0 z-40 brand-bg-midnight border-b border-violet-700/40 shadow-md shadow-violet-900/20" data-topnav-root data-testid="desktop-topnav">
          <div className="px-3 lg:px-5 flex items-center gap-2 h-11">
            {/* Logo + brand — slim */}
            <button onClick={() => goToSection('dashboard')} className="flex items-center gap-2 flex-shrink-0 hover:opacity-90 transition-opacity" data-testid="topnav-brand">
              {(() => {
                const logo = selectedCompany?.logo_url || companies.find(c => c.logo_url)?.logo_url;
                return logo ? (
                  <img src={logo.startsWith('http') ? logo : `${API.replace(/\/api$/, '')}${logo}`} alt="" className="w-7 h-7 rounded-md object-contain bg-white/95 border border-violet-400/40" />
                ) : (
                  <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-md shadow-violet-500/40"><TrendingUp size={13} className="text-white" /></div>
                );
              })()}
            </button>

            {/* Nav groups as dropdowns — slim. Administracao is rendered separately on the right side as a gear icon. */}
            <nav className="flex items-center gap-0.5 flex-1 min-w-0" data-testid="topnav-groups">
              {navGroups.filter(g => g.title !== 'Administracao').map((group) => {
                const groupHasActive = group.items.some(i => i.key === section);
                // Principal (Inicio) — single direct button, no dropdown
                if (group.title === 'Principal' && group.items.length === 1) {
                  const it = group.items[0];
                  const active = section === it.key;
                  return (
                    <button key={group.title} onClick={() => goToSection(it.key)} data-testid={`topnav-${it.key}`}
                      title={it.label}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap ${active ? 'bg-white/15 text-white' : 'text-slate-200 hover:bg-white/10 hover:text-white'}`}>
                      <it.icon size={14} />
                      <span className="hidden lg:inline">{it.label}</span>
                    </button>
                  );
                }
                const isOpen = openTopGroup === group.title;
                return (
                  <div key={group.title} className="relative">
                    <button onClick={(e) => { e.stopPropagation(); setOpenTopGroup(isOpen ? null : group.title); }} data-testid={`topnav-group-${group.title}`}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap ${groupHasActive ? 'bg-white/15 text-white' : 'text-slate-200 hover:bg-white/10 hover:text-white'} ${isOpen ? 'bg-white/15 text-white' : ''}`}>
                      <span>{group.title}</span>
                      <ChevronDown size={11} className={`transition-transform ${isOpen ? 'rotate-180' : ''} opacity-70`} />
                    </button>
                    {isOpen && (
                      <div className="absolute top-full left-0 mt-1 min-w-[220px] bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden z-50" data-testid={`topnav-dropdown-${group.title}`}>
                        <div className="h-0.5 bg-gradient-to-r from-blue-600 to-violet-600" />
                        <div className="py-1">
                          {group.items.map(item => {
                            const active = section === item.key;
                            return (
                              <button key={item.key} onClick={() => { goToSection(item.key); setOpenTopGroup(null); }} data-testid={`nav-${item.key}`}
                                className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-xs transition-colors ${active ? 'bg-gradient-to-r from-blue-50 to-violet-50 text-violet-700 font-bold' : 'text-slate-700 hover:bg-slate-50'}`}>
                                <item.icon size={14} className={active ? 'text-violet-600' : 'text-slate-400'} />
                                <span>{item.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Right side: Administracao (gear) + company + catalog + user + logout */}
            <div className="flex items-center gap-0.5 flex-shrink-0">
              {(() => {
                const adminGroup = navGroups.find(g => g.title === 'Administracao');
                if (!adminGroup) return null;
                const groupHasActive = adminGroup.items.some(i => i.key === section);
                const isOpen = openTopGroup === 'Administracao';
                return (
                  <div className="relative">
                    <button onClick={(e) => { e.stopPropagation(); setOpenTopGroup(isOpen ? null : 'Administracao'); }} data-testid="topnav-group-Administracao"
                      title="Administracao"
                      className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-bold transition-all ${groupHasActive ? 'bg-white/15 text-white' : 'text-slate-200 hover:bg-white/10 hover:text-white'} ${isOpen ? 'bg-white/15 text-white' : ''}`}>
                      <SettingsIcon size={15} />
                      <ChevronDown size={10} className={`transition-transform ${isOpen ? 'rotate-180' : ''} opacity-70`} />
                    </button>
                    {isOpen && (
                      <div className="absolute top-full right-0 mt-1 min-w-[220px] bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden z-50" data-testid="topnav-dropdown-Administracao">
                        <div className="h-0.5 bg-gradient-to-r from-blue-600 to-violet-600" />
                        <p className="px-3 pt-2 pb-1 text-[10px] font-black text-slate-400 uppercase tracking-widest">Administracao</p>
                        <div className="py-1">
                          {adminGroup.items.map(item => {
                            const active = section === item.key;
                            return (
                              <button key={item.key} onClick={() => { goToSection(item.key); setOpenTopGroup(null); }} data-testid={`nav-${item.key}`}
                                className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-xs transition-colors ${active ? 'bg-gradient-to-r from-blue-50 to-violet-50 text-violet-700 font-bold' : 'text-slate-700 hover:bg-slate-50'}`}>
                                <item.icon size={14} className={active ? 'text-violet-600' : 'text-slate-400'} />
                                <span>{item.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* iter77ct.43: mostra CODIGO + parte boa do NOME da filial (nao so o codigo) */}
              <button onClick={() => setCompanyModalOpen(true)} title={`Empresa: ${selectedCompany?.name || 'selecionar'}`} className="px-2 py-1.5 rounded-md text-cyan-200 hover:text-white hover:bg-white/10 transition-colors flex items-center gap-1.5" data-testid="topnav-company-btn">
                <Building2 size={14} />
                <span className="text-[11px] font-bold hidden md:inline truncate max-w-[180px] lg:max-w-[240px] xl:max-w-[320px]">
                  {selectedCompany
                    ? <><span className="text-cyan-400/90">{selectedCompany.code}</span><span className="mx-1 opacity-50">·</span><span className="text-white/90">{selectedCompany.name}</span></>
                    : 'Empresa'}
                </span>
                <ChevronDown size={10} className="opacity-70" />
              </button>
              {/* iter77dj: so aparece se o tenant tem `catalog-settings` habilitado no business type */}
              {isAllowed('catalog-settings') && canDo('view', 'catalog-settings') && (
                <a href={`/${slug}/catalogo`} target="_blank" rel="noopener noreferrer" title="Abrir catalogo publico" className="px-2 py-1.5 rounded-md text-violet-200 hover:text-white hover:bg-white/10 transition-colors flex items-center gap-1" data-testid="open-catalog-link">
                  <Sparkles size={14} />
                </a>
              )}
              <div className="w-px h-5 bg-white/10 mx-1" />
              <div className="flex items-center gap-1.5 px-1.5" data-testid="topnav-user" title={user?.name}>
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-sm shadow-violet-500/40 flex-shrink-0"><span className="text-[10px] font-black text-white">{user?.name?.charAt(0) || 'A'}</span></div>
              </div>
              <button onClick={() => logout()} title="Sair" className="px-2 py-1.5 rounded-md text-slate-300 hover:bg-rose-500/15 hover:text-rose-300 transition-colors" data-testid="topnav-logout">
                <LogOut size={14} />
              </button>
            </div>
          </div>
        </header>
      )}

      {/* ===== MAIN CONTENT ===== */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto min-w-0 pb-20 md:pb-0">
        {!pdvFullscreen && (
        <div className="md:hidden sticky top-0 z-30 brand-bg-midnight border-b-2 border-violet-600/40 px-4 py-3 flex items-center justify-between gap-3 shadow-lg shadow-violet-900/30" data-testid="page-header">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            {(() => {
              const logo = selectedCompany?.logo_url || companies.find(c => c.logo_url)?.logo_url;
              return logo ? (
                <img src={logo.startsWith('http') ? logo : `${API.replace(/\/api$/, '')}${logo}`} alt="" className="w-8 h-8 rounded-lg object-contain bg-white border border-violet-500/30 flex-shrink-0 md:hidden" data-testid="header-logo" />
              ) : null;
            })()}
            <h2 className="font-black text-white text-xl sm:text-2xl tracking-tight truncate">{sectionLabel}</h2>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0">
            {selectedCompany && <span className="text-xs text-violet-100 bg-violet-600/30 border border-violet-500/30 px-3 py-1 rounded-full hidden sm:inline">{selectedCompany.code} | {selectedCompany.name}</span>}
            <button onClick={() => setCompanyModalOpen(true)} className="md:hidden p-2 text-violet-200 hover:text-white" data-testid="mobile-company-btn"><Building2 size={18} /></button>
          </div>
        </div>
        )}
        <div className={pdvFullscreen ? 'p-2 md:p-4' : 'p-4 md:p-6 max-w-7xl mx-auto'}>
          {/* iter77cd: Dashboard sempre agrega todas as filiais que o usuário tem acesso — companyId=null → 'all' */}
          {section === 'dashboard' && isAllowed('dashboard') && <DashHome companyId={null} selectedCompanyId={selectedCompany?.id} hiddenCards={dashboardCards} userRole={user?.role} userName={user?.name || user?.email} goToSection={goToSection} businessType={businessType} />}
          {section === 'ai-insights' && <AIInsightsPage companyId={selectedCompany?.id} companies={companies} />}
          {section === 'dre' && isAllowed('dre') && <DREPage companyId={selectedCompany?.id} />}
          {section === 'profit-direct' && isAllowed('profit-direct') && <ProfitDirectReportPage companyId={selectedCompany?.id} />}
          {section === 'commission' && isAllowed('commission') && <CommissionReportPage companyId={selectedCompany?.id} />}
          {section === 'companies' && isAllowed('companies') && <CompaniesPage companies={companies} onRefresh={fetchCompanies} />}
          {section === 'quotes' && isAllowed('quotes') && <SalesPage companyId={selectedCompany?.id} type="orcamento" title="Orcamentos" pdvPerms={pdvPerms} />}
          {section === 'sales-orders' && isAllowed('sales-orders') && <SalesPage companyId={selectedCompany?.id} type="pedido" title="Pedidos de Venda" pdvPerms={pdvPerms} />}
          {section === 'sales' && isAllowed('sales') && <SalesPage companyId={selectedCompany?.id} type="venda" title="Vendas" pdvPerms={pdvPerms} />}
          {section === 'cash' && isAllowed('cash') && <CashPage companyId={selectedCompany?.id} />}
          {section === 'sales-report' && isAllowed('sales-report') && <SalesReportPage companyId={selectedCompany?.id} />}
          {section === 'stock' && isAllowed('stock') && <StockPage />}
          {section === 'stock-movements' && isAllowed('stock-movements') && <StockMovementsPage companyId={selectedCompany?.id} companies={companies} />}
          {section === 'stock-transfers' && isAllowed('stock-transfers') && <StockTransfersPage companyId={selectedCompany?.id} companies={companies} />}
          {section === 'price-tables' && isAllowed('price-tables') && <PriceTablesPage />}
          {section === 'labels' && isAllowed('labels') && <LabelsPage companyId={selectedCompany?.id} />}
          {section === 'metas' && isAllowed('metas') && <MetasPage companyId={selectedCompany?.id} />}
          {section === 'natureza-operacao' && isAllowed('natureza-operacao') && <NaturezaOperacaoPage companyId={selectedCompany?.id} companies={companies} />}
          {section === 'invoices' && isAllowed('invoices') && <InvoicesPage companyId={selectedCompany?.id} />}
          {section === 'manifesto' && isAllowed('manifesto') && <ManifestoPage companyId={selectedCompany?.id} />}
          {section === 'purchases' && isAllowed('purchases') && <PurchasesPage companyId={selectedCompany?.id} />}
          {section === 'purchase-requests' && isAllowed('purchase-requests') && <PurchaseRequestsPage companyId={selectedCompany?.id} />}
          {section === 'pharmacy' && isAllowed('pharmacy') && <PharmacyPage companyId={selectedCompany?.id} />}
          {section === 'cashback' && businessType?.cashback_enabled && <CashbackPage />}
          {/* iter156aq/aw: modulos toggle-avel via SuperAdmin — respeitam BT + perfil de acesso */}
          {section === 'ponto' && businessType?.ponto_module_enabled && isAllowed('ponto') && <PontoPage companyId={selectedCompany?.id} />}
          {/* iter156bh: Relatorio de Ponto no menu Gerencial */}
          {section === 'ponto-report' && businessType?.ponto_module_enabled && isAllowed('ponto-report') && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Clock size={20} className="text-teal-600" />
                <h1 className="text-xl font-black text-slate-900">Relatorio de Ponto</h1>
              </div>
              <PontoRelatorio companyId={selectedCompany?.id} />
            </div>
          )}
          {section === 'production-orders' && businessType?.production_module_enabled && isAllowed('production-orders') && <ProductionOrdersPage companyId={selectedCompany?.id} companies={companies} />}
          {section === 'mdfe' && businessType?.mdfe_module_enabled && isAllowed('mdfe') && <MDFePage companyId={selectedCompany?.id} companies={companies} />}
          {/* iter156dz: OS / Locacao — gate BT flag + permissao granular. Label customizavel via SuperAdmin. */}
          {section === 'service-orders' && businessType?.os_module_enabled && isAllowed('service-orders') && <ServiceOrdersPage companyId={selectedCompany?.id} companies={companies} moduleLabel={businessType?.os_module_label || 'Ordem de Servico'} />}
          {section === 'clients' && isAllowed('clients') && <ClientsPage companyId={selectedCompany?.id} companies={companies} />}
          {section === 'suppliers' && isAllowed('suppliers') && <SuppliersPage />}
          {section === 'employees' && isAllowed('employees') && <EmployeesPage companyId={selectedCompany?.id} companies={companies} />}
          {section === 'financial' && isAllowed('financial') && <UnifiedFinancialPage companyId={selectedCompany?.id} />}
          {section === 'receivables' && <UnifiedFinancialPage companyId={selectedCompany?.id} defaultTab="receivable" />}
          {section === 'payables' && <UnifiedFinancialPage companyId={selectedCompany?.id} defaultTab="payable" />}
          {section === 'pdv' && isAllowed('pdv') && <PDVPage companyId={selectedCompany?.id} onSaleComplete={() => {}} fullscreen={pdvFullscreen} onToggleFullscreen={() => setPdvFullscreen(s => !s)} onClose={() => goToSection('sales')} canEditItem={!!(moduleActions?.pdv?.edit !== false)} pdvPerms={pdvPerms} />}
          {section === 'settings' && isAllowed('catalog-settings') && <SettingsManagement extra={<CompanySettingsPage onlyTab="catalogo" />} />}
          {section === 'company-settings' && (isAllowed('company-settings') || isAllowed('companies')) && <CompanySettingsPage companyId={selectedCompany?.id} />}
          {section === 'products' && isAllowed('products') && <ProductsManagement slug={slug} companyId={selectedCompany?.id} productionEnabled={!!businessType?.production_module_enabled} osEnabled={!!businessType?.os_module_enabled} />}
          {section === 'categories' && isAllowed('categories') && <CategoriesManagement slug={slug} />}
          {section === 'orders' && isAllowed('orders') && <OrdersManagement slug={slug} />}
          {section === 'chart-accounts' && isAllowed('chart-accounts') && <ChartAccountsPage companies={companies} />}
          {section === 'bank-accounts' && isAllowed('bank-accounts') && <BankAccountsPage companyId={selectedCompany?.id} />}
          {section === 'payment-methods' && isAllowed('payment-methods') && <PaymentMethodsPage companyId={selectedCompany?.id} />}
          {section === 'closing' && isAllowed('closing') && <ClosingPage companyId={selectedCompany?.id} />}
          {section === 'receipts' && isAllowed('receipts') && <ReceiptsPage companyId={selectedCompany?.id} />}
          {section === 'users' && isAllowed('users') && <UsersPage />}
          {section === 'access-profiles' && isAllowed('access-profiles') && <AccessProfilesPage allowedModules={allowedModules} osLabel={businessType?.os_module_label || 'Ordem de Servico'} />}
          {section === 'plans' && isAllowed('plans') && <MyPlanPage />}
          {section === 'integrations' && isAllowed('integrations') && <IntegrationsPage />}
          {section === 'logs' && isAllowed('logs') && <LogsPage companyId={selectedCompany?.id} />}
          {!isAllowed(section) && section !== 'dashboard' && (
            <div className="text-center py-12"><XCircle size={40} className="mx-auto text-gray-300 mb-3" /><p className="text-gray-500 font-medium">Modulo nao disponivel</p></div>
          )}
        </div>
      </main>

      {/* ===== MOBILE BOTTOM NAV BAR (iter77ct.13: modernized) ===== */}
      {!pdvFullscreen && (
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden backdrop-blur-xl bg-[#0f0f24]/95 border-t border-violet-500/30 safe-area-bottom shadow-[0_-8px_24px_rgba(139,92,246,0.15)]" data-testid="mobile-bottom-nav">
        <div className="flex items-end justify-around h-16 px-1">
          {bottomNavItems.slice(0, 2).map(item => {
            const active = section === item.key;
            return (
              <button key={item.key} onClick={() => goToSection(item.key)} data-testid={`bnav-${item.key}`}
                className={`relative flex flex-col items-center justify-center flex-1 h-full py-1 transition-all ${active ? 'text-cyan-300' : 'text-violet-200/70 hover:text-white'}`}>
                {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-b-full bg-gradient-to-r from-cyan-400 to-teal-400"></span>}
                <div className={`transition-transform ${active ? 'scale-110' : ''}`}>
                  <item.icon size={20} strokeWidth={active ? 2.5 : 2} />
                </div>
                <span className={`text-[9px] font-bold mt-0.5 tracking-wide ${active ? '' : 'opacity-90'}`}>{item.label}</span>
              </button>
            );
          })}
          {/* CENTER MENU BUTTON */}
          <button onClick={() => setMenuOpen(true)} data-testid="bnav-menu"
            className="relative flex flex-col items-center justify-center flex-1 py-1 -mt-6">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl shadow-violet-600/50 transition-all bg-gradient-to-br from-cyan-500 via-blue-500 to-violet-600 ring-2 ring-white/10 ${menuOpen ? 'scale-95 rotate-45' : 'hover:scale-105'}`}>
              <MenuIcon size={24} className="text-white" strokeWidth={2.5} />
            </div>
            <span className="text-[9px] font-black mt-1 text-white tracking-wider">MENU</span>
          </button>
          {bottomNavItems.slice(2, 4).map(item => {
            const active = section === item.key;
            return (
              <button key={item.key} onClick={() => goToSection(item.key)} data-testid={`bnav-${item.key}`}
                className={`relative flex flex-col items-center justify-center flex-1 h-full py-1 transition-all ${active ? 'text-cyan-300' : 'text-violet-200/70 hover:text-white'}`}>
                {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-b-full bg-gradient-to-r from-cyan-400 to-teal-400"></span>}
                <div className={`transition-transform ${active ? 'scale-110' : ''}`}>
                  <item.icon size={20} strokeWidth={active ? 2.5 : 2} />
                </div>
                <span className={`text-[9px] font-bold mt-0.5 tracking-wide ${active ? '' : 'opacity-90'}`}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* ===== MOBILE MENU OVERLAY ===== */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden bg-white overflow-y-auto" data-testid="mobile-menu-overlay">
          <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-gray-100">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{selectedCompany?.name || 'Menu'}</h3>
              <p className="text-xs text-gray-400">Todas as opcoes do sistema</p>
            </div>
            <button onClick={() => setMenuOpen(false)} className="p-2 text-gray-400 hover:text-gray-600" data-testid="close-menu-btn"><X size={22} /></button>
          </div>
          <div className="p-4 pb-24">
            {navGroups.map((group, gi) => {
              const groupHasActive = group.items.some(i => i.key === section);
              const isOpen = openGroups[`m_${group.title}`] ?? true; // mobile: aberto por padrao (estilo Instagram/8life)
              const groupColor = {
                'Principal': 'text-sky-600',
                'Cadastros': 'text-emerald-600',
                'Itens': 'text-violet-600',
                'Vendas': 'text-pink-600',
                'Financeiro': 'text-amber-600',
                'Administracao': 'text-slate-600',
              }[group.title] || 'text-slate-500';
              return (
                <div key={gi} className="mb-5">
                  <button onClick={() => toggleGroup(`m_${group.title}`)} data-testid={`mgroup-toggle-${group.title}`}
                    className="w-full flex items-center justify-between mb-3 px-1">
                    <p className={`text-[13px] font-black uppercase tracking-wider flex items-center gap-2 ${groupColor}`}>
                      {groupHasActive && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
                      {group.title}
                    </p>
                    <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                  </button>
                  {isOpen && (
                    <div className="grid grid-cols-4 gap-3" data-testid={`mgroup-${group.title}`}>
                      {group.items.map(item => {
                        const active = section === item.key;
                        return (
                          <button key={item.key} onClick={() => goToSection(item.key)} data-testid={`menu-${item.key}`}
                            className="flex flex-col items-center gap-1.5 group">
                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${active ? 'bg-gradient-to-br from-blue-50 to-violet-50 ring-2 ring-violet-500 shadow-md shadow-violet-500/20' : 'bg-slate-100 group-hover:bg-slate-200'}`}>
                              <item.icon size={22} className={active ? 'text-violet-600' : 'text-slate-700'} />
                            </div>
                            <span className={`text-[11px] leading-tight text-center ${active ? 'text-violet-700 font-bold' : 'text-slate-700 font-medium'}`}>{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <button onClick={() => { logout(); }} className="w-full flex items-center justify-center gap-2 py-3 mt-4 rounded-xl border border-slate-200 text-slate-600 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-colors" data-testid="menu-logout">
              <LogOut size={16} /><span className="text-sm font-bold">Sair da conta</span>
            </button>
          </div>
        </div>
      )}
      {/* Company selector modal — iter156s: modernizado (busca + ordenacao alfabetica ascendente + header gradiente) */}
      {companyModalOpen && (
        <CompanySelectorModal
          companies={companies}
          selectedCompany={selectedCompany}
          onSelect={(c) => { selectCompany(c); setCompanyModalOpen(false); }}
          onClose={() => setCompanyModalOpen(false)}
        />
      )}
    </div>
    </BusinessTypeContext.Provider>
  );
};


/* ========== CompanySelectorModal — iter156s: modal moderno c/ busca + ordenacao alfabetica ========== */
const CompanySelectorModal = ({ companies, selectedCompany, onSelect, onClose }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  useEffect(() => {
    // Auto-foca a busca ao abrir (mobile-friendly: opcional se teclado atrapalhar)
    if (inputRef.current) {
      try { inputRef.current.focus({ preventScroll: true }); } catch { /* noop */ }
    }
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const active = React.useMemo(
    () => (companies || []).filter(c => c.active !== false),
    [companies]
  );
  // Normalizador SEM acentos p/ busca amigavel ("goias" acha "GOIÁS")
  const _norm = (s) => (s || '').toString().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const sorted = React.useMemo(() => {
    return [...active].sort((a, b) =>
      _norm(a.name).localeCompare(_norm(b.name), 'pt-BR', { sensitivity: 'base' })
    );
  }, [active]);
  const filtered = React.useMemo(() => {
    const q = _norm(query.trim());
    if (!q) return sorted;
    return sorted.filter(c =>
      _norm(c.name).includes(q) ||
      _norm(c.code || '').includes(q) ||
      _norm(c.cnpj || '').includes(q) ||
      _norm(c.trade_name || '').includes(q)
    );
  }, [sorted, query]);

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="company-modal-overlay"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden ring-1 ring-slate-200"
        onClick={e => e.stopPropagation()}
        data-testid="company-modal"
      >
        {/* Header gradient */}
        <div className="px-6 pt-5 pb-4 bg-gradient-to-br from-violet-600 via-indigo-600 to-cyan-500 text-white">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/70">Empresas ativas</p>
              <h3 className="text-xl font-black tracking-tight">Selecione uma Empresa</h3>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-1.5 transition"
              data-testid="company-modal-close"
              aria-label="Fechar"
            >
              <X size={18} />
            </button>
          </div>
          {/* Busca */}
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Buscar por nome, código ou CNPJ..."
              className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-white text-slate-800 placeholder:text-slate-400 text-sm font-medium outline-none focus:ring-2 focus:ring-cyan-300 shadow-sm"
              data-testid="company-modal-search"
              autoComplete="off"
              inputMode="search"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Limpar"
                data-testid="company-modal-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="mt-2 text-[11px] text-white/80 font-semibold">
            {filtered.length} de {sorted.length} empresa{sorted.length === 1 ? '' : 's'}
          </div>
        </div>
        {/* Lista */}
        <div className="p-3 max-h-[420px] overflow-y-auto space-y-1.5 bg-slate-50">
          {filtered.map(c => {
            const isSel = selectedCompany?.id === c.id;
            return (
              <button
                key={c.id}
                onClick={() => onSelect(c)}
                data-testid={`select-company-${c.code}`}
                className={`w-full text-left px-4 py-3 rounded-xl flex items-center justify-between transition-all border ${
                  isSel
                    ? 'bg-white border-teal-400 ring-2 ring-teal-200 shadow-sm'
                    : 'bg-white border-slate-200 hover:border-violet-300 hover:shadow-sm'
                }`}
              >
                <div className="min-w-0 flex-1 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-black ${
                    isSel ? 'bg-teal-500 text-white' : 'bg-gradient-to-br from-violet-100 to-cyan-100 text-violet-700'
                  }`}>
                    {String(c.code || '?').slice(0, 3)}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-slate-900 truncate">{c.name}</p>
                    {c.cnpj && <p className="text-[11px] text-slate-500 truncate">{c.cnpj}</p>}
                  </div>
                </div>
                {isSel && <CheckCircle size={18} className="text-teal-600 flex-shrink-0 ml-2" />}
              </button>
            );
          })}
          {filtered.length === 0 && sorted.length > 0 && (
            <p className="text-center text-slate-500 text-sm py-10 font-medium">
              Nenhuma empresa encontrada para <strong className="text-slate-800">&quot;{query}&quot;</strong>.
            </p>
          )}
          {sorted.length === 0 && (
            <p className="text-center text-slate-500 text-sm py-10 font-medium">
              Nenhuma empresa cadastrada. Va em &quot;Empresas&quot; para criar.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};


/* ========== AI INSIGHTS PAGE — narrative + KPI cards + chat bubble ========== */
const AIInsightsPage = ({ companyId, companies }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [scopeCompanyId, setScopeCompanyId] = useState('');
  const [includeNarrative, setIncludeNarrative] = useState(true);

  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSession, setChatSession] = useState(null);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatBottomRef = useRef(null);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch insights WITHOUT narrative first (instant)
      const body = { period_days: period, include_narrative: false };
      if (scopeCompanyId) body.company_id = scopeCompanyId;
      const { data: d } = await axios.post(`${API}/ai/insights`, body, { withCredentials: true });
      setData(d);
      setLoading(false);
      // Then fetch narrative in background (LLM call ~5-15s)
      if (includeNarrative) {
        try {
          const { data: dn } = await axios.post(`${API}/ai/insights`, { ...body, include_narrative: true }, { withCredentials: true });
          setData(dn);
        } catch { /* keep insights without narrative */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar IA');
      setLoading(false);
    }
  }, [period, scopeCompanyId, includeNarrative]);
  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  useEffect(() => {
    if (chatBottomRef.current) chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMsgs]);

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatSending) return;
    setChatSending(true);
    setChatMsgs(m => [...m, { role: 'user', content: text }]);
    setChatInput('');
    try {
      const { data: r } = await axios.post(`${API}/ai/chat`, { session_id: chatSession, message: text, company_id: scopeCompanyId || null }, { withCredentials: true });
      setChatSession(r.session_id);
      setChatMsgs(m => [...m, { role: 'assistant', content: r.answer }]);
    } catch (err) {
      setChatMsgs(m => [...m, { role: 'assistant', content: '(erro ao consultar IA)' }]);
    } finally { setChatSending(false); }
  };

  if (loading && !data) return <Loader />;
  const ins = data?.insights || {};
  const s = ins.sales || {};
  const st = ins.stock || {};
  const cl = ins.clients || {};
  const fi = ins.financial || {};
  const bd = ins.best_day || {};
  const tp = ins.top_products || [];
  const growthColor = s.growth_pct >= 0 ? 'text-emerald-600' : 'text-rose-600';
  const periodOptions = [{ d: 7, l: '7 dias' }, { d: 30, l: '30 dias' }, { d: 60, l: '60 dias' }, { d: 90, l: '90 dias' }];

  return (
    <div data-testid="ai-insights-page" className="relative">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center shadow-md shadow-violet-500/30">
            <Sparkles size={16} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-900">IA Insights</h2>
            <p className="text-xs text-slate-500">Analise do seu negocio com inteligencia artificial • {data?.model || 'Gemini'}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={scopeCompanyId} onChange={e => setScopeCompanyId(e.target.value)} className="px-3 py-1.5 text-xs rounded-full border border-slate-200 bg-white" data-testid="ai-company-filter">
            <option value="">Todas as empresas</option>
            {(companies || []).map(c => <option key={c.id} value={c.id}>{c.code} | {c.name}</option>)}
          </select>
          {periodOptions.map(p => (
            <button key={p.d} onClick={() => setPeriod(p.d)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${period === p.d ? 'bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-400'}`} data-testid={`ai-period-${p.d}`}>{p.l}</button>
          ))}
          <button onClick={fetchInsights} className="btn-pill-outline" data-testid="ai-refresh"><RefreshCw size={12} /><span>Atualizar</span></button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <p className="text-[10px] uppercase font-bold text-slate-400">Vendas no Periodo</p>
          <p className="text-xl font-black text-slate-900 mt-0.5">R$ {(s.current_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
          <p className={`text-[11px] font-bold mt-0.5 ${growthColor}`}>{s.growth_pct >= 0 ? '+' : ''}{(s.growth_pct || 0).toFixed(1)}% vs anterior</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <p className="text-[10px] uppercase font-bold text-slate-400">Ticket Medio</p>
          <p className="text-xl font-black text-slate-900 mt-0.5">R$ {(s.ticket_medio || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">{s.current_count || 0} vendas</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <p className="text-[10px] uppercase font-bold text-slate-400">Ruptura</p>
          <p className="text-xl font-black text-rose-600 mt-0.5">{st.out_of_stock || 0}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">{st.low_stock_count || 0} com estoque baixo</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <p className="text-[10px] uppercase font-bold text-slate-400">Titulos Vencidos</p>
          <p className="text-xl font-black text-amber-600 mt-0.5">{fi.overdue_count || 0}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">R$ {(fi.overdue_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Narrative */}
        <div className="lg:col-span-2 bg-white border-2 border-blue-100 rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-violet-600" />
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-violet-600" />
            <h3 className="font-bold text-sm text-slate-900">Resumo Executivo IA</h3>
          </div>
          {data?.narrative ? (
            <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="ai-narrative">{data.narrative}</div>
          ) : includeNarrative ? (
            <div className="flex items-center gap-2 text-xs text-violet-500"><RefreshCw size={12} className="animate-spin" /> Gerando analise IA... (5-15s)</div>
          ) : (
            <div className="text-xs text-slate-400 italic">Narrativa desativada.</div>
          )}
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <h4 className="font-bold text-sm text-slate-900 mb-2 flex items-center gap-1.5"><TrendingUp size={14} className="text-emerald-500" /> Top Produtos</h4>
            {tp.length === 0 ? <p className="text-xs text-slate-400">Sem vendas no periodo</p> : (
              <div className="space-y-2">
                {tp.map((p, i) => (
                  <div key={p.id || i} className="flex items-center justify-between text-xs">
                    <div className="min-w-0 flex-1 mr-2">
                      <p className="font-semibold text-slate-900 truncate">{p.name}</p>
                      <p className="text-[10px] text-slate-400">{p.sku} • {p.qty} un</p>
                    </div>
                    <span className="text-emerald-600 font-bold whitespace-nowrap">R$ {p.revenue.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {bd?.name && (
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <h4 className="font-bold text-sm text-slate-900 mb-1 flex items-center gap-1.5"><Calendar size={14} className="text-blue-500" /> Melhor Dia</h4>
              <p className="text-xs text-slate-700">{bd.name.toUpperCase()} — R$ {(bd.revenue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} em {bd.sales} vendas</p>
            </div>
          )}
          {(st.low_stock_items || []).length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <h4 className="font-bold text-sm text-amber-900 mb-1.5 flex items-center gap-1.5"><AlertTriangle size={14} /> Estoque Baixo</h4>
              <div className="space-y-1">
                {(st.low_stock_items || []).slice(0, 6).map((it, i) => (
                  <div key={i} className="text-xs text-amber-900 flex justify-between"><span className="truncate mr-2">{it.name}</span><span className="font-bold">{it.stock}</span></div>
                ))}
              </div>
            </div>
          )}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4">
            <h4 className="font-bold text-sm text-rose-900 mb-1 flex items-center gap-1.5"><Users size={14} /> Clientes Inativos (60d)</h4>
            <p className="text-2xl font-black text-rose-700">{cl.inactive_60d || 0}</p>
            <p className="text-[10px] text-rose-700/70">Considere campanha de reativacao</p>
          </div>
        </div>
      </div>

      {/* Floating chat bubble */}
      {!chatOpen && (
        <button onClick={() => setChatOpen(true)} className="fixed bottom-24 md:bottom-5 right-4 md:right-5 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-2xl shadow-violet-500/40 flex items-center justify-center hover:scale-105 transition-transform" title="Pergunte a IA" data-testid="ai-chat-bubble">
          <Sparkles size={22} />
        </button>
      )}
      {chatOpen && (
        <div className="fixed bottom-24 md:bottom-5 right-4 md:right-5 z-50 w-[92vw] max-w-sm h-[65vh] md:h-[70vh] bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="ai-chat-panel">
          <div className="bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2"><Sparkles size={16} className="text-white" /><span className="text-white font-bold text-sm">Pergunte a IA</span></div>
            <button onClick={() => setChatOpen(false)} className="text-white/80 hover:text-white"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
            {chatMsgs.length === 0 && (
              <div className="text-xs text-slate-500 text-center p-4">
                <p className="font-semibold mb-2">Exemplos:</p>
                <ul className="space-y-1 text-left max-w-xs mx-auto">
                  <li>• &quot;Quanto vendi hoje?&quot;</li>
                  <li>• &quot;Quais produtos com ruptura?&quot;</li>
                  <li>• &quot;Sugestao de acao para melhorar vendas&quot;</li>
                </ul>
              </div>
            )}
            {chatMsgs.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs ${m.role === 'user' ? 'ml-auto bg-gradient-to-r from-blue-600 to-violet-600 text-white' : 'bg-white border border-slate-200 text-slate-800'}`} data-testid={`ai-chat-msg-${m.role}`}>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            ))}
            {chatSending && <div className="text-xs text-slate-400 italic">IA digitando...</div>}
            <div ref={chatBottomRef} />
          </div>
          <div className="p-2 border-t border-slate-200 bg-white flex gap-2">
            <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') sendChat(); }} placeholder="Pergunte algo sobre o seu negocio..." className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-full focus:outline-none focus:border-blue-500" data-testid="ai-chat-input" />
            <button onClick={sendChat} disabled={chatSending || !chatInput.trim()} className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 text-white flex items-center justify-center disabled:opacity-40" data-testid="ai-chat-send"><Send size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
};


/* ========== DASHBOARD HOME ========== */

/* ========== DRE — Demonstrativo de Resultados (Simples) ========== */
const DREPage = ({ companyId }) => {
  const [period, setPeriod] = useState('month'); // today | week | month | year | custom
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDespesas, setShowDespesas] = useState(false);   // iter156fe: detalhe das despesas consideradas
  const [applied, setApplied] = useState({ start: '', end: '' }); // iter156fe: datas personalizadas so valem apos "Filtrar"

  const fetchDRE = useCallback(async () => {
    if (!companyId) { setLoading(false); return; }
    if (period === 'custom' && !(applied.start && applied.end)) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ company_id: companyId, period });
      if (period === 'custom') { params.set('start_date', applied.start); params.set('end_date', applied.end); }
      const { data } = await axios.get(`${ERP}/dre?${params}`, { withCredentials: true });
      setData(data);
    } catch (err) { toast.error('Erro ao carregar DRE'); }
    finally { setLoading(false); }
  }, [companyId, period, applied]);
  useEffect(() => { fetchDRE(); }, [fetchDRE]);

  const periods = [
    { key: 'today', label: 'Hoje' },
    { key: 'week', label: '7 dias' },
    { key: 'month', label: 'Mes' },
    { key: 'prev_month', label: 'Mes anterior' },
    { key: 'year', label: 'Ano' },
    { key: 'custom', label: 'Personalizado' },
  ];

  const customBar = period === 'custom' && (
    <div className="flex flex-wrap items-center gap-2 mb-4 bg-slate-50 border border-slate-200 rounded-xl p-3">
      <label className="flex items-center gap-1.5 text-xs"><span className="text-slate-500">De:</span><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="px-2 py-1 border border-slate-200 rounded-lg text-xs" data-testid="dre-start-date" /></label>
      <label className="flex items-center gap-1.5 text-xs"><span className="text-slate-500">Ate:</span><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="px-2 py-1 border border-slate-200 rounded-lg text-xs" data-testid="dre-end-date" /></label>
      <button onClick={() => { if (!startDate || !endDate) { toast.error('Informe as duas datas'); return; } setApplied({ start: startDate, end: endDate }); }} className="px-3 py-1 rounded-lg bg-emerald-600 text-white text-xs font-bold flex items-center gap-1" data-testid="dre-apply-btn"><Search size={12} /> Filtrar</button>
      {!(applied.start && applied.end) && <span className="text-[11px] text-slate-500">Escolha o periodo e clique em Filtrar.</span>}
    </div>
  );
  const periodBar = (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div>
        <h2 className="text-lg font-black text-slate-900 flex items-center gap-2"><TrendingUp size={20} className="text-emerald-600" />DRE — Demonstrativo de Resultados</h2>
        {data && <p className="text-xs text-slate-500 mt-0.5">Modelo Simples · Periodo: {data.period.start} a {data.period.end}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {periods.map(p => (
          <button key={p.key} onClick={() => setPeriod(p.key)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${period === p.key ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid={`dre-period-${p.key}`}>{p.label}</button>
        ))}
      </div>
    </div>
  );

  if (loading) return <Loader />;
  if (!data) return <div data-testid="dre-page">{periodBar}{customBar}<EmptyState icon={TrendingUp} text={period === 'custom' ? 'Escolha as datas e clique em Filtrar' : 'Sem dados'} /></div>;
  const m = data.metrics;

  return (
    <div data-testid="dre-page">
      {periodBar}
      {customBar}

      {/* Hero KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-2xl p-4 shadow-lg shadow-emerald-500/20">
          <p className="text-[11px] uppercase tracking-wider opacity-80">Receita Bruta</p>
          <p className="text-2xl font-black mt-1" data-testid="dre-revenue">{fmtBRL(m.revenue)}</p>
          <p className="text-[10px] opacity-75 mt-1">{m.sales_count} vendas · {m.items_sold} itens vendidos</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wider text-slate-400">Lucro Bruto</p>
          <p className={`text-2xl font-black mt-1 ${m.gross_profit >= 0 ? 'text-slate-800' : 'text-rose-600'}`} data-testid="dre-gross-profit">{fmtBRL(m.gross_profit)}</p>
          <p className="text-[10px] text-slate-500 mt-1">Margem Bruta: <strong>{m.gross_margin_pct}%</strong></p>
        </div>
        <div className={`rounded-2xl p-4 shadow-lg ${m.net_profit >= 0 ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-blue-500/20' : 'bg-gradient-to-br from-rose-500 to-rose-600 text-white shadow-rose-500/20'}`}>
          <p className="text-[11px] uppercase tracking-wider opacity-80">Resultado Liquido</p>
          <p className="text-2xl font-black mt-1" data-testid="dre-net-profit">{fmtBRL(m.net_profit)}</p>
          <p className="text-[10px] opacity-75 mt-1">Margem Liquida: <strong>{m.net_margin_pct}%</strong></p>
        </div>
      </div>

      {/* DRE estrutura */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="dre-breakdown">
        <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
          <p className="text-xs font-black text-slate-600 uppercase tracking-wider">Estrutura do DRE</p>
        </div>
        <div className="divide-y divide-slate-100">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm font-bold text-emerald-700 flex items-center gap-2">(+) Receita Bruta de Vendas</span>
            <span className="text-sm font-mono font-bold text-emerald-700">{fmtBRL(m.revenue)}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-rose-600 flex items-center gap-2 pl-4">(−) CMV (Custo da Mercadoria Vendida)</span>
            <span className="text-sm font-mono text-rose-600">- {fmtBRL(m.cmv)}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
            <span className="text-sm font-black text-slate-800">(=) Lucro Bruto</span>
            <span className={`text-sm font-mono font-black ${m.gross_profit >= 0 ? 'text-slate-800' : 'text-rose-700'}`}>{fmtBRL(m.gross_profit)}</span>
          </div>
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <button type="button" onClick={() => setShowDespesas(v => !v)} className="text-sm text-rose-600 flex items-center gap-2 pl-4 hover:underline" data-testid="dre-despesas-toggle">
                <ChevronDown size={14} className={`transition-transform ${showDespesas ? 'rotate-180' : ''}`} /> (−) Despesas Operacionais (pagas) <span className="text-[10px] text-slate-400 font-normal">{m.despesas_count} lancamento(s) · clique para detalhar</span>
              </button>
              <span className="text-sm font-mono text-rose-600">- {fmtBRL(m.despesas)}</span>
            </div>
            {showDespesas && (
              <div className="mt-3 ml-4 rounded-xl border border-rose-100 bg-rose-50/40 overflow-hidden" data-testid="dre-despesas-detail">
                <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-rose-700 border-b border-rose-100">Por plano de contas (so contas marcadas para DRE)</div>
                <div className="divide-y divide-rose-100">
                  {(data.despesas_por_conta || []).map((c, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs"><span className="text-slate-700">{c.code ? `${c.code} · ` : ''}{c.account} <span className="text-slate-400">({c.count})</span></span><span className="font-mono text-rose-700">{fmtBRL(c.total)}</span></div>
                  ))}
                </div>
                <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-rose-700 border-y border-rose-100">Lancamentos</div>
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-white/60 text-slate-500"><tr><th className="text-left px-3 py-1">Pago em</th><th className="text-left px-3 py-1">Cod.</th><th className="text-left px-3 py-1">Descricao</th><th className="text-left px-3 py-1">Pl. Contas</th><th className="text-left px-3 py-1">F. Pgto</th><th className="text-right px-3 py-1">Valor</th></tr></thead>
                    <tbody>
                      {(data.despesas_detalhe || []).map(d => (
                        <tr key={d.id} className="border-t border-rose-100/60"><td className="px-3 py-1 font-mono text-slate-600">{d.paid_date ? new Date(d.paid_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}</td><td className="px-3 py-1 font-mono">{d.entry_code || '-'}</td><td className="px-3 py-1 text-slate-800 truncate max-w-[260px]" title={d.description}>{d.description || d.supplier_name || '-'}</td><td className="px-3 py-1 text-slate-600">{d.account}</td><td className="px-3 py-1 text-slate-500">{d.payment_method || '-'}</td><td className="px-3 py-1 text-right font-mono text-rose-700">{fmtBRL(d.amount)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {m.despesas_excluidas > 0 && <div className="px-3 py-2 text-[10px] text-slate-500 border-t border-rose-100">Nao considerados: {fmtBRL(m.despesas_excluidas)} (contas desmarcadas para DRE ou faturas de compra de mercadoria — ja contabilizadas no CMV). Transferencias entre contas nunca entram.</div>}
              </div>
            )}
          </div>
          {m.outras_receitas > 0 && (
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-emerald-600 flex items-center gap-2 pl-4">(+) Outras Receitas</span>
              <span className="text-sm font-mono text-emerald-600">+ {fmtBRL(m.outras_receitas)}</span>
            </div>
          )}
          <div className={`flex items-center justify-between px-4 py-3 font-black ${m.net_profit >= 0 ? 'bg-emerald-50' : 'bg-rose-50'}`}>
            <span className={`text-base ${m.net_profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>(=) Resultado Liquido do Periodo</span>
            <span className={`text-base font-mono ${m.net_profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmtBRL(m.net_profit)}</span>
          </div>
        </div>
        {/* iter156fe: informativo — em aberto NAO entra no resultado */}
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs" data-testid="dre-open-info">
          <div><p className="text-[10px] uppercase font-bold text-slate-400">Despesas em aberto (venc. no periodo)</p><p className="font-mono font-bold text-amber-700">{fmtBRL(m.despesas_abertas || 0)}</p></div>
          <div><p className="text-[10px] uppercase font-bold text-slate-400">Receitas a receber (venc. no periodo)</p><p className="font-mono font-bold text-amber-700">{fmtBRL(m.receitas_abertas || 0)}</p></div>
          <div><p className="text-[10px] uppercase font-bold text-slate-400">Total geral de despesas (pagas + em aberto)</p><p className="font-mono font-bold text-slate-800">{fmtBRL(m.total_geral_despesas || 0)}</p></div>
        </div>
        <div className="bg-amber-50 border-t border-amber-200 px-4 py-2 text-[10px] text-amber-800">
          <strong>Notas:</strong> O resultado usa somente o REALIZADO: receita de vendas concluidas, CMV = soma de (custo unitario × qtde vendida), despesas PAGAS no periodo (data do pagamento) e outras receitas RECEBIDAS. Valores em aberto aparecem apenas como informacao. Transferencias entre contas e faturas de compra de mercadoria (ja no CMV) nao entram. So contas do Plano de Contas marcadas "considerar na DRE" sao somadas.
        </div>
      </div>
    </div>
  );
};



/* ========== Relatorio Lucro Direto — Sintetico (Profit Direct) ========== */
const ProfitDirectReportPage = ({ companyId }) => {
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState(''); // texto digitado (não dispara fetch)
  const [appliedSearch, setAppliedSearch] = useState(''); // texto REAL usado na query
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter77cl: multi-empresa + toggle de agrupamento
  const [companies, setCompanies] = useState([]); // lista de todas as filiais para o multi-select
  const [selectedCids, setSelectedCids] = useState(() => companyId ? [companyId] : []);
  const [groupByCompany, setGroupByCompany] = useState(false); // iter77cp: default DESAGRUPADO (visualização plana)
  const [showCompanyPicker, setShowCompanyPicker] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(''); // filtro dentro do picker
  // iter77cn: paginação client-side (25/50/100) — reduz DOM e re-render
  const [pageSize, setPageSize] = useState(25);
  const [pageIdx, setPageIdx] = useState(0); // 0-based
  // Ordenacao por cabecalho (clique alterna asc/desc)
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'name' || key === 'sku' || key === 'company_name' ? 'asc' : 'desc'); }
    setPageIdx(0);
  };
  const SortTh = ({ k, children, right }) => (
    <th className={`px-3 py-2 cursor-pointer select-none hover:text-violet-700 whitespace-nowrap ${right ? 'text-right' : ''}`} onClick={() => toggleSort(k)} data-testid={`profit-sort-${k}`} title="Clique para ordenar">
      {children}{sortKey === k ? <span className="ml-1 text-violet-600">{sortDir === 'asc' ? '▲' : '▼'}</span> : <span className="ml-1 text-slate-300">↕</span>}
    </th>
  );

  // sincroniza sempre que companyId ativo muda (ao entrar na tela)
  useEffect(() => { if (companyId && selectedCids.length === 0) setSelectedCids([companyId]); }, [companyId]); // eslint-disable-line

  useEffect(() => {
    axios.get(`${ERP}/companies`, { withCredentials: true })
      .then(({ data: d }) => setCompanies(d.data || []))
      .catch(() => setCompanies([]));
  }, []);

  const fetchData = useCallback(async () => {
    if (!selectedCids.length) { setLoading(false); return; }
    setLoading(true);
    try {
      const p = new URLSearchParams({ company_ids: selectedCids.join(','), period });
      if (period === 'custom' && startDate && endDate) { p.set('start_date', startDate); p.set('end_date', endDate); }
      if (appliedSearch) p.set('search', appliedSearch);
      // iter156au: primeiro fetch rapido — so 25 linhas para mostrar UI ja com dados.
      // Depois, em background, buscamos o restante e concatenamos (progressivo).
      p.set('limit', '25'); p.set('offset', '0');
      const { data: initialPage } = await axios.get(`${ERP}/reports/profit-direct?${p}`, { withCredentials: true });
      setData(initialPage);
      setPageIdx(0);
      setLoading(false);
      // Se ha mais linhas, carrega o restante em background (sem loading spinner)
      if (initialPage.has_more) {
        try {
          const p2 = new URLSearchParams(p);
          p2.delete('limit'); p2.delete('offset');  // pega tudo agora
          const { data: fullData } = await axios.get(`${ERP}/reports/profit-direct?${p2}`, { withCredentials: true });
          setData(fullData);
        } catch { /* mantem o initialPage — usuario ja tem 25 linhas */ }
      }
    } catch { toast.error('Erro ao carregar relatorio'); setLoading(false); }
  }, [selectedCids, period, startDate, endDate, appliedSearch]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // iter77cn: busca dispara SÓ ao clicar botão ou pressionar Enter
  const applySearch = () => { setAppliedSearch(search); };
  const clearSearch = () => { setSearch(''); setAppliedSearch(''); };

  const toggleCid = (id) => setSelectedCids(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAllCids = () => setSelectedCids(companies.map(c => c.id));
  const selectNoneCids = () => setSelectedCids(companyId ? [companyId] : []);

  const exportXLSX = useCallback(async () => {
    if (!data) return;
    const t = data.totals;
    const XLSX = await import('xlsx');
    const bcRows = (data.by_company || []).flatMap(bc => {
      const rowsCid = (data.data || []).filter(r => r.company_id === bc.company_id);
      return [
        [],
        [`FILIAL: ${bc.company_name}${bc.company_code ? ` (${bc.company_code})` : ''}`],
        ['#', 'SKU', 'Produto', 'Qtd Entrada', 'Qtd Saida', 'Vl Unit Custo', 'Vl Total Custo', 'Vl Unit Saida', 'Vl Total Saida', 'Vl Lucro', 'Estoque'],
        ...rowsCid.map((r, i) => [i + 1, r.sku, r.name, r.qty_in || 0, r.qty, r.cost_unit, r.cost_total, r.sale_unit, r.sale_total, r.profit, r.stock_current]),
        ['', '', `Subtotal — ${bc.products_count} produtos`, bc.qty_in || 0, bc.qty, '', bc.cost_total, '', bc.sale_total, bc.profit, bc.stock_total],
      ];
    });
    const rows = [
      ['Lucro Direto Produto — Multi-empresa'],
      [`Periodo: ${data.period.start} a ${data.period.end}`],
      [`Filiais: ${(data.by_company || []).map(bc => bc.company_name).join(', ') || '(todas)'}`],
      [`Filtro busca: ${search || '(nenhum)'}`],
      [],
      ['TOTAL GERAL'],
      ['#', 'SKU', 'Produto', 'Qtd Entrada', 'Qtd Saida', 'Vl Unit Custo', 'Vl Total Custo', 'Vl Unit Saida', 'Vl Total Saida', 'Vl Lucro', 'Estoque'],
      ['', '', `TOTAL (${t.products_count} produtos, ${t.companies_count || 1} filial(is))`, t.qty_in || 0, t.qty, '', t.cost_total, '', t.sale_total, t.profit, t.stock_total],
      ...bcRows,
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 5 }, { wch: 12 }, { wch: 36 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lucro Direto');
    XLSX.writeFile(wb, `lucro-direto-${data.period.start}_a_${data.period.end}.xlsx`);
  }, [data, search]);

  const printPDF = useCallback(() => {
    if (!data) return;
    const t = data.totals;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Bloqueio de pop-up. Permita pop-ups para imprimir.'); return; }
    // Renderiza grupos por filial + subtotais
    const groupsHtml = (data.by_company || []).map(bc => {
      const rowsCid = (data.data || []).filter(r => r.company_id === bc.company_id);
      const trs = rowsCid.map((r, i) => `<tr><td>${i + 1}</td><td>${r.sku || ''}</td><td>${r.name || ''}</td><td style="text-align:right">${r.qty_in || 0}</td><td style="text-align:right">${r.qty}</td><td style="text-align:right;color:#c2410c">${fmtBRL(r.cost_unit)}</td><td style="text-align:right;color:#c2410c">${fmtBRL(r.cost_total)}</td><td style="text-align:right;color:#047857">${fmtBRL(r.sale_unit)}</td><td style="text-align:right;color:#047857">${fmtBRL(r.sale_total)}</td><td style="text-align:right;color:${r.profit >= 0 ? '#1d4ed8' : '#be123c'};font-weight:700">${fmtBRL(r.profit)}</td><td style="text-align:right">${r.stock_current}</td></tr>`).join('');
      return `<div class="grp"><div class="grp-hdr">🏢 ${bc.company_name}${bc.company_code ? ` <span class="grp-code">(${bc.company_code})</span>` : ''}</div>
        <table><thead><tr><th>#</th><th>SKU</th><th>Produto</th><th style="text-align:right">Qtd Ent</th><th style="text-align:right">Qtd Sai</th><th style="text-align:right">Vl Unit Custo</th><th style="text-align:right">Vl Total Custo</th><th style="text-align:right">Vl Unit Saida</th><th style="text-align:right">Vl Total Saida</th><th style="text-align:right">Vl Lucro</th><th style="text-align:right">Est.</th></tr></thead>
        <tbody>${trs}
        <tr class="subtotal"><td colspan="3">Subtotal — ${bc.products_count} produtos</td><td style="text-align:right">${bc.qty_in || 0}</td><td style="text-align:right">${bc.qty}</td><td></td><td style="text-align:right">${fmtBRL(bc.cost_total)}</td><td></td><td style="text-align:right">${fmtBRL(bc.sale_total)}</td><td style="text-align:right">${fmtBRL(bc.profit)}</td><td style="text-align:right">${bc.stock_total}</td></tr>
        </tbody></table></div>`;
    }).join('');
    const html = `<!doctype html><html><head><title>Lucro Direto — ${data.period.start} a ${data.period.end}</title><style>
      body{font-family:Arial,sans-serif;margin:18px;color:#0f172a;}
      h1{margin:0 0 4px;font-size:20px;}
      h2{margin:0 0 14px;font-size:12px;font-weight:400;color:#64748b;}
      .cards{display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap;}
      .card{flex:1;min-width:120px;border:1px solid #e2e8f0;border-radius:8px;padding:8px;font-size:11px;}
      .card .lbl{font-weight:700;font-size:10px;text-transform:uppercase;color:#64748b;}
      .card .v{font-size:14px;font-weight:900;margin-top:2px;}
      .grp{margin:14px 0;page-break-inside:avoid;}
      .grp-hdr{background:linear-gradient(90deg,#1e40af 0%,#3b82f6 100%);color:white;padding:8px 12px;font-size:13px;font-weight:800;border-radius:6px 6px 0 0;}
      .grp-code{opacity:0.8;font-weight:500;font-size:11px;}
      table{width:100%;border-collapse:collapse;font-size:11px;}
      th{background:#f1f5f9;text-align:left;padding:6px;border-bottom:2px solid #cbd5e1;font-size:10px;text-transform:uppercase;color:#475569;}
      td{padding:5px 6px;border-bottom:1px solid #f1f5f9;}
      tr.subtotal{background:#e0e7ff;font-weight:800;color:#3730a3;}
      @media print{button{display:none;}}
    </style></head><body>
      <h1>Lucro Direto Produto — Multi-empresa</h1>
      <h2>Periodo: ${data.period.start} a ${data.period.end} · ${t.products_count} produto(s) · ${t.companies_count || 1} filial(is)${search ? ` · busca: "${search}"` : ''}</h2>
      <div class="cards">
        <div class="card" style="background:#f0fdfa;"><div class="lbl" style="color:#0d9488">Qtde Entrada</div><div class="v" style="color:#0f766e">${t.qty_in || 0}</div></div>
        <div class="card"><div class="lbl">Qtde Saida</div><div class="v">${t.qty}</div></div>
        <div class="card" style="background:#fff1f2;"><div class="lbl" style="color:#e11d48">Vl Custo</div><div class="v" style="color:#be123c">${fmtBRL(t.cost_total)}</div></div>
        <div class="card" style="background:#ecfdf5;"><div class="lbl" style="color:#059669">Vl Saida</div><div class="v" style="color:#047857">${fmtBRL(t.sale_total)}</div></div>
        <div class="card" style="background:#eff6ff;"><div class="lbl" style="color:#2563eb">Vl Lucro</div><div class="v" style="color:#1d4ed8">${fmtBRL(t.profit)}</div></div>
      </div>
      <button onclick="window.print()" style="background:#2563eb;color:white;padding:8px 16px;border:none;border-radius:6px;font-weight:700;cursor:pointer;margin-bottom:10px;">Imprimir / Salvar como PDF</button>
      ${groupsHtml || '<p style="text-align:center;color:#94a3b8;padding:20px;">Nenhuma movimentação no periodo</p>'}
    </body></html>`;
    w.document.write(html);
    w.document.close();
  }, [data, search]);

  if (loading) return <Loader />;
  if (!data) return <EmptyState icon={BarChart3} text="Selecione ao menos 1 filial" />;
  const t = data.totals;
  const periods = [{ k: 'today', l: 'Hoje' }, { k: 'week', l: '7 dias' }, { k: 'month', l: 'Mes' }, { k: 'year', l: 'Ano' }, { k: 'custom', l: 'Personalizado' }];
  const byCompany = data.by_company || [];
  const selectedCompanyLabels = companies.filter(c => selectedCids.includes(c.id)).map(c => c.code || c.name);
  // iter77cn: paginação client-side
  const allRows = (() => {
    const rows = [...(data.data || [])];
    if (!sortKey) return rows;
    const textKeys = new Set(['name', 'sku', 'company_name']);
    const dir = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (textKeys.has(sortKey)) {
        const va = sortKey === 'company_name' ? `${a.company_code || ''} ${a.company_name || ''}` : String(a[sortKey] || '');
        const vb = sortKey === 'company_name' ? `${b.company_code || ''} ${b.company_name || ''}` : String(b[sortKey] || '');
        return va.localeCompare(vb, 'pt-BR', { sensitivity: 'base', numeric: true }) * dir;
      }
      return ((Number(a[sortKey]) || 0) - (Number(b[sortKey]) || 0)) * dir;
    });
    return rows;
  })();
  const totalRows = allRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePageIdx = Math.min(pageIdx, totalPages - 1);
  const visibleRows = allRows.slice(safePageIdx * pageSize, safePageIdx * pageSize + pageSize);
  const visibleCidSet = new Set(visibleRows.map(r => r.company_id));
  const pageStart = totalRows === 0 ? 0 : (safePageIdx * pageSize + 1);
  const pageEnd = Math.min((safePageIdx + 1) * pageSize, totalRows);

  return (
    <div data-testid="profit-direct-page">
      {/* iter77cp: Header COMPACTO — título + período + filial-picker compacto (badge) + ações em uma linha só */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-3 flex-1 min-w-[280px]">
          <div>
            <h2 className="text-lg font-black text-slate-900 flex items-center gap-2"><BarChart3 size={20} className="text-blue-600" />Lucro Direto Produto</h2>
            <p className="text-[11px] text-slate-500">{data.period.start} a {data.period.end}</p>
          </div>
          {/* iter77cp: filial picker COMPACTO — botão pill com badge de contagem */}
          <div className="relative">
            <button type="button" onClick={() => setShowCompanyPicker(v => !v)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border-2 border-blue-200 hover:border-blue-400 text-xs font-bold text-blue-700 transition-colors" data-testid="profit-company-picker-btn" title="Selecionar filiais">
              <Building2 size={13} />
              <span>Filiais</span>
              <span className="bg-blue-600 text-white text-[10px] rounded-full px-1.5 py-0 min-w-[18px] text-center font-black">{selectedCids.length}</span>
              <ChevronDown size={11} className={`transition-transform ${showCompanyPicker ? 'rotate-180' : ''}`} />
            </button>
            {showCompanyPicker && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setShowCompanyPicker(false)} />
                <div className="absolute z-30 left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl w-[380px] max-h-[380px] flex flex-col" data-testid="profit-company-picker">
                  <div className="p-2 border-b border-slate-100 flex items-center gap-2">
                    <Search size={13} className="text-slate-400 ml-1" />
                    <input type="text" value={companyFilter} onChange={e => setCompanyFilter(e.target.value)} placeholder="Filtrar filial..." className="flex-1 text-xs border-0 focus:outline-none placeholder:text-slate-400" autoFocus data-testid="profit-company-filter" />
                  </div>
                  <div className="flex justify-between px-3 py-1.5 border-b border-slate-100 bg-slate-50">
                    <button onClick={selectAllCids} className="text-[11px] font-bold text-blue-600 hover:underline">✓ Todas</button>
                    <span className="text-[10px] text-slate-500">{selectedCids.length} de {companies.length}</span>
                    <button onClick={selectNoneCids} className="text-[11px] font-bold text-slate-500 hover:underline">Só atual</button>
                  </div>
                  <div className="flex-1 overflow-y-auto py-1">
                    {companies
                      .filter(c => {
                        const q = companyFilter.trim().toLowerCase();
                        if (!q) return true;
                        return (c.name || '').toLowerCase().includes(q) || (c.code || '').toLowerCase().includes(q);
                      })
                      .map(c => {
                        const sel = selectedCids.includes(c.id);
                        return (
                          <button key={c.id} type="button" onClick={() => toggleCid(c.id)} className={`w-full flex items-center gap-2 px-3 py-1.5 hover:bg-blue-50 border-l-4 ${sel ? 'border-blue-500 bg-blue-50/50' : 'border-transparent'}`} data-testid={`profit-cid-${c.id}`}>
                            <span className={`w-4 h-4 rounded border-2 flex items-center justify-center ${sel ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                              {sel && <CheckCircle size={11} className="text-white" />}
                            </span>
                            <span className="text-xs font-semibold text-slate-700 flex-1 text-left">{c.name}</span>
                            {c.code && <span className="text-[10px] font-mono text-slate-400">{c.code}</span>}
                          </button>
                        );
                      })}
                    {!companies.length && <p className="text-xs text-slate-400 px-3 py-4 text-center">Nenhuma filial</p>}
                  </div>
                  <div className="p-2 border-t border-slate-100 flex justify-end">
                    <button onClick={() => setShowCompanyPicker(false)} className="px-3 py-1 rounded-lg bg-slate-700 text-white text-[11px] font-bold hover:bg-slate-800" data-testid="profit-picker-close">Fechar</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {periods.map(p => <button key={p.k} onClick={() => setPeriod(p.k)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${period === p.k ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid={`profit-period-${p.k}`}>{p.l}</button>)}
          <button onClick={exportXLSX} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 ml-1" data-testid="profit-export-xlsx" title="Exportar para Excel">
            <Download size={12} /> Excel
          </button>
          <button onClick={printPDF} className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-xs font-bold flex items-center gap-1.5" data-testid="profit-print-pdf" title="Imprimir/PDF">
            <Printer size={12} /> PDF
          </button>
        </div>
      </div>
      {/* iter77cp: (removida a barra de chips e barra de paginação do topo — agora tudo mais compacto)  */}
      {period === 'custom' && (
        <div className="flex flex-wrap items-center gap-2 mb-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="px-2 py-1 border border-slate-200 rounded-lg text-xs" />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="px-2 py-1 border border-slate-200 rounded-lg text-xs" />
          <button onClick={fetchData} className="px-3 py-1 rounded-lg bg-blue-600 text-white text-xs font-bold">Aplicar</button>
        </div>
      )}
      {/* iter77cp: busca e paginação-topo REMOVIDAS daqui — busca vai depois dos KPIs, paginação vai no rodapé */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <div className="bg-teal-50 border border-teal-100 rounded-xl p-3" title="Soma das quantidades que entraram via movimento de entrada/balanco positivo OU transferencia recebida no periodo"><p className="text-[10px] font-bold text-teal-600 uppercase">Qtde Total Entrada</p><p className="text-base font-black text-teal-700" data-testid="profit-card-qty-in">{t.qty_in || 0}</p></div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3" title="Soma das quantidades vendidas no periodo (saidas via venda)"><p className="text-[10px] font-bold text-slate-400 uppercase">Qtde Total Saida</p><p className="text-base font-black text-slate-800" data-testid="profit-card-qty-out">{t.qty}</p></div>
        <div className="bg-rose-50 border border-rose-100 rounded-xl p-3" title="Custo total dos produtos vendidos (CMV)"><p className="text-[10px] font-bold text-rose-500 uppercase">Vl Custo Total</p><p className="text-base font-black text-rose-700">{fmtBRL(t.cost_total)}</p></div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3" title="Receita liquida (subtotal - descontos) das vendas no periodo"><p className="text-[10px] font-bold text-emerald-600 uppercase">Vl Saida Total</p><p className="text-base font-black text-emerald-700">{fmtBRL(t.sale_total)}</p></div>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3" title="Lucro = Vl Saida Total - Vl Custo Total"><p className="text-[10px] font-bold text-blue-500 uppercase">Vl Lucro</p><p className="text-base font-black text-blue-700">{fmtBRL(t.profit)}</p></div>
      </div>
      {/* iter77cp: busca AGORA POSICIONADA logo em cima da lista (após os KPIs) + toggle "Agrupar" */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex-1 min-w-[240px] flex items-center gap-1 bg-white border border-slate-200 rounded-lg overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition">
          <Search size={13} className="text-slate-400 ml-2 flex-shrink-0" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applySearch(); } }}
            placeholder="Digite código, SKU ou nome e pressione Enter…"
            className="flex-1 px-2 py-1.5 text-xs border-0 focus:outline-none"
            data-testid="profit-search-input"
          />
          {search && (
            <button type="button" onClick={clearSearch} className="p-1 text-slate-400 hover:text-slate-600" title="Limpar" data-testid="profit-search-clear"><X size={13} /></button>
          )}
          <button type="button" onClick={applySearch} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold flex items-center gap-1" data-testid="profit-search-apply" title="Buscar (ou pressione Enter no input)">
            <Search size={11} />Buscar
          </button>
        </div>
        {appliedSearch && (
          <span className="text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 flex items-center gap-1" title="Filtro ativo">
            Buscando: "{appliedSearch}"
            <button onClick={clearSearch} className="text-blue-500 hover:text-blue-700"><X size={11} /></button>
          </span>
        )}
        <label className="flex items-center gap-1.5 text-xs font-bold text-slate-600 whitespace-nowrap cursor-pointer">
          <input type="checkbox" checked={groupByCompany} onChange={e => setGroupByCompany(e.target.checked)} className="w-4 h-4 accent-blue-600" data-testid="profit-group-toggle" />
          Agrupar por filial
        </label>
      </div>
      {groupByCompany ? (
        // iter77cl: visualização agrupada por filial (uma seção por empresa com header + subtotal)
        // iter77cn: filtra grupos que têm rows visíveis na página atual (mantém subtotais totais)
        <div className="space-y-4" data-testid="profit-grouped-view">
          {byCompany.filter(bc => visibleCidSet.has(bc.company_id)).map(bc => {
            const rowsCid = visibleRows.filter(r => r.company_id === bc.company_id);
            return (
              <div key={bc.company_id || 'noc'} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`profit-group-${bc.company_id}`}>
                <div className="bg-gradient-to-r from-blue-700 to-blue-500 text-white px-4 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 size={16} />
                    <span className="font-black text-sm">{bc.company_name}</span>
                    {bc.company_code && <span className="text-white/70 text-xs font-mono">({bc.company_code})</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="opacity-90"><b>{bc.products_count}</b> produtos</span>
                    <span className="opacity-90">Saida: <b>{fmtBRL(bc.sale_total)}</b></span>
                    <span className="bg-white/20 rounded px-2 py-0.5 font-bold">Lucro: {fmtBRL(bc.profit)}</span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px] text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-slate-500">
                        <th className="px-3 py-2">#</th>
                        <SortTh k="sku">SKU</SortTh>
                        <SortTh k="name">Produto</SortTh>
                        <SortTh k="qty_in" right>Qtd Ent</SortTh>
                        <SortTh k="qty" right>Qtd Sai</SortTh>
                        <SortTh k="cost_unit" right>Vl Unit Custo</SortTh>
                        <SortTh k="cost_total" right>Vl Total Custo</SortTh>
                        <SortTh k="sale_unit" right>Vl Unit Saida</SortTh>
                        <SortTh k="sale_total" right>Vl Total Saida</SortTh>
                        <SortTh k="profit" right>Vl Lucro</SortTh>
                        <SortTh k="stock_current" right>Est.</SortTh>
                      </tr>
                    </thead>
                    <tbody>
                      {rowsCid.map((r, i) => (
                        <tr key={`${r.company_id}-${r.product_id}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid="profit-row">
                          <td className="px-3 py-1.5 text-slate-400">{i + 1}</td>
                          <td className="px-3 py-1.5 font-mono text-slate-600">{r.sku}</td>
                          <td className="px-3 py-1.5 text-slate-800 truncate max-w-[260px]">{r.name}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-teal-700">{r.qty_in || 0}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{r.qty}</td>
                          <td className="px-3 py-1.5 text-right text-rose-600">{fmtBRL(r.cost_unit)}</td>
                          <td className="px-3 py-1.5 text-right text-rose-600">{fmtBRL(r.cost_total)}</td>
                          <td className="px-3 py-1.5 text-right text-emerald-600">{fmtBRL(r.sale_unit)}</td>
                          <td className="px-3 py-1.5 text-right text-emerald-600">{fmtBRL(r.sale_total)}</td>
                          <td className={`px-3 py-1.5 text-right font-bold ${r.profit >= 0 ? 'text-blue-700' : 'text-rose-700'}`}>{fmtBRL(r.profit)}</td>
                          <td className="px-3 py-1.5 text-right text-slate-500">{r.stock_current}</td>
                        </tr>
                      ))}
                      <tr className="bg-indigo-50 border-t-2 border-indigo-200 font-bold text-indigo-800">
                        <td colSpan={3} className="px-3 py-2">Subtotal — {bc.products_count} produtos</td>
                        <td className="px-3 py-2 text-right">{bc.qty_in || 0}</td>
                        <td className="px-3 py-2 text-right">{bc.qty}</td>
                        <td />
                        <td className="px-3 py-2 text-right">{fmtBRL(bc.cost_total)}</td>
                        <td />
                        <td className="px-3 py-2 text-right">{fmtBRL(bc.sale_total)}</td>
                        <td className="px-3 py-2 text-right">{fmtBRL(bc.profit)}</td>
                        <td className="px-3 py-2 text-right">{bc.stock_total}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
          {byCompany.length === 0 && <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-400 text-sm">Nenhuma movimentação no período para as filiais selecionadas</div>}
          {/* Total geral */}
          {byCompany.length > 1 && (
            <div className="bg-blue-100 border border-blue-300 rounded-2xl p-4 flex items-center justify-between" data-testid="profit-grand-total">
              <div className="font-black text-blue-900 text-sm">🎯 TOTAL GERAL — {t.companies_count} filial(is) · {t.products_count} linhas</div>
              <div className="flex items-center gap-4 text-xs font-bold text-blue-900">
                <span>Ent: <b>{t.qty_in || 0}</b></span>
                <span>Sai: <b>{t.qty}</b></span>
                <span>Custo: <b>{fmtBRL(t.cost_total)}</b></span>
                <span>Saida: <b>{fmtBRL(t.sale_total)}</b></span>
                <span className="bg-blue-600 text-white rounded-lg px-3 py-1">Lucro: {fmtBRL(t.profit)}</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Visualização plana (tabela única com coluna Filial)
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full min-w-[1000px] text-xs" data-testid="profit-direct-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-500">
                <th className="px-3 py-2">#</th>
                <SortTh k="company_name">Filial</SortTh>
                <SortTh k="sku">SKU</SortTh>
                <SortTh k="name">Produto</SortTh>
                <SortTh k="qty_in" right>Qtd Ent</SortTh>
                <SortTh k="qty" right>Qtd Sai</SortTh>
                <SortTh k="cost_unit" right>Vl Unit Custo</SortTh>
                <SortTh k="cost_total" right>Vl Total Custo</SortTh>
                <SortTh k="sale_unit" right>Vl Unit Saida</SortTh>
                <SortTh k="sale_total" right>Vl Total Saida</SortTh>
                <SortTh k="profit" right>Vl Lucro</SortTh>
                <SortTh k="stock_current" right>Est.</SortTh>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-blue-50 border-b border-blue-100 font-bold text-blue-700">
                <td colSpan={4} className="px-3 py-2">TOTAL ({t.products_count} linhas · {t.companies_count} filial(is))</td>
                <td className="px-3 py-2 text-right">{t.qty_in || 0}</td>
                <td className="px-3 py-2 text-right">{t.qty}</td>
                <td />
                <td className="px-3 py-2 text-right">{fmtBRL(t.cost_total)}</td>
                <td />
                <td className="px-3 py-2 text-right">{fmtBRL(t.sale_total)}</td>
                <td className="px-3 py-2 text-right">{fmtBRL(t.profit)}</td>
                <td className="px-3 py-2 text-right">{t.stock_total}</td>
              </tr>
              {visibleRows.map((r, i) => (
                <tr key={`${r.company_id}-${r.product_id}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid="profit-row">
                  <td className="px-3 py-1.5 text-slate-400">{safePageIdx * pageSize + i + 1}</td>
                  <td className="px-3 py-1.5">
                    <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold">
                      {r.company_code || ''}{r.company_code ? ' · ' : ''}{r.company_name}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-slate-600">{r.sku}</td>
                  <td className="px-3 py-1.5 text-slate-800 truncate max-w-[260px]">{r.name}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-teal-700">{r.qty_in || 0}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{r.qty}</td>
                  <td className="px-3 py-1.5 text-right text-rose-600">{fmtBRL(r.cost_unit)}</td>
                  <td className="px-3 py-1.5 text-right text-rose-600">{fmtBRL(r.cost_total)}</td>
                  <td className="px-3 py-1.5 text-right text-emerald-600">{fmtBRL(r.sale_unit)}</td>
                  <td className="px-3 py-1.5 text-right text-emerald-600">{fmtBRL(r.sale_total)}</td>
                  <td className={`px-3 py-1.5 text-right font-bold ${r.profit >= 0 ? 'text-blue-700' : 'text-rose-700'}`}>{fmtBRL(r.profit)}</td>
                  <td className="px-3 py-1.5 text-right text-slate-500">{r.stock_current}</td>
                </tr>
              ))}
              {data.data.length === 0 && <tr><td colSpan={12} className="px-3 py-6 text-center text-slate-400">Nenhuma movimentacao no periodo</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {/* iter77cp: RODAPÉ com paginação (page size 25/50/100 + navegação) */}
      {totalRows > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 mt-4 p-2.5 bg-slate-50 border border-slate-200 rounded-xl sticky bottom-0 shadow-sm" data-testid="profit-footer-pagination">
          <div className="flex items-center gap-1.5" data-testid="profit-page-size-bar">
            <span className="text-[10px] font-black text-slate-500 uppercase">Linhas por página:</span>
            {[25, 50, 100].map(sz => (
              <button key={sz} onClick={() => { setPageSize(sz); setPageIdx(0); }}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors ${pageSize === sz ? 'bg-blue-600 text-white shadow' : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-300'}`}
                data-testid={`profit-page-size-${sz}`}>{sz}</button>
            ))}
            <span className="ml-3 text-[11px] text-slate-500">Mostrando <b>{pageStart}–{pageEnd}</b> de <b>{totalRows}</b></span>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button onClick={() => setPageIdx(0)} disabled={safePageIdx === 0} className="px-2 py-1 rounded bg-white border border-slate-200 text-slate-500 disabled:opacity-30 hover:bg-slate-100 text-[10px] font-bold" data-testid="profit-page-first">«</button>
              <button onClick={() => setPageIdx(i => Math.max(0, i - 1))} disabled={safePageIdx === 0} className="px-2.5 py-1 rounded bg-white border border-slate-200 text-slate-500 disabled:opacity-30 hover:bg-slate-100 text-[10px] font-bold" data-testid="profit-page-prev">‹ Anterior</button>
              <span className="text-[11px] font-bold text-slate-700 px-2">{safePageIdx + 1} / {totalPages}</span>
              <button onClick={() => setPageIdx(i => Math.min(totalPages - 1, i + 1))} disabled={safePageIdx >= totalPages - 1} className="px-2.5 py-1 rounded bg-white border border-slate-200 text-slate-500 disabled:opacity-30 hover:bg-slate-100 text-[10px] font-bold" data-testid="profit-page-next">Próxima ›</button>
              <button onClick={() => setPageIdx(totalPages - 1)} disabled={safePageIdx >= totalPages - 1} className="px-2 py-1 rounded bg-white border border-slate-200 text-slate-500 disabled:opacity-30 hover:bg-slate-100 text-[10px] font-bold" data-testid="profit-page-last">»</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


/* ========== Relatorio de Comissao de Vendedores ========== */
const CommissionReportPage = ({ companyId }) => {
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const p = new URLSearchParams({ company_id: companyId, period });
      if (period === 'custom' && startDate && endDate) { p.set('start_date', startDate); p.set('end_date', endDate); }
      const { data } = await axios.get(`${ERP}/reports/commission?${p}`, { withCredentials: true });
      setData(data);
    } catch { toast.error('Erro ao carregar comissoes'); }
    finally { setLoading(false); }
  }, [companyId, period, startDate, endDate]);
  useEffect(() => { fetchData(); }, [fetchData]);
  if (loading) return <Loader />;
  if (!data) return <EmptyState icon={UserCheck} text="Sem dados" />;
  const t = data.totals;
  const periods = [{ k: 'today', l: 'Hoje' }, { k: 'week', l: '7 dias' }, { k: 'month', l: 'Mes' }, { k: 'year', l: 'Ano' }, { k: 'custom', l: 'Personalizado' }];
  return (
    <div data-testid="commission-report-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-black text-slate-900 flex items-center gap-2"><UserCheck size={20} className="text-violet-600" />Comissao de Vendedores</h2>
          <p className="text-xs text-slate-500">% definido em cada funcionario · {data.period.start} a {data.period.end}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {periods.map(p => <button key={p.k} onClick={() => setPeriod(p.k)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${period === p.k ? 'bg-violet-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid={`commission-period-${p.k}`}>{p.l}</button>)}
        </div>
      </div>
      {period === 'custom' && (
        <div className="flex flex-wrap items-center gap-2 mb-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="px-2 py-1 border border-slate-200 rounded-lg text-xs" />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="px-2 py-1 border border-slate-200 rounded-lg text-xs" />
          <button onClick={fetchData} className="px-3 py-1 rounded-lg bg-violet-600 text-white text-xs font-bold">Aplicar</button>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3"><p className="text-[10px] font-bold text-slate-400 uppercase">Vendedores</p><p className="text-base font-black text-slate-800">{t.vendors_count}</p></div>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3"><p className="text-[10px] font-bold text-blue-500 uppercase">Vendas</p><p className="text-base font-black text-blue-700">{t.vendas_count}</p></div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3"><p className="text-[10px] font-bold text-emerald-600 uppercase">Total Vendido</p><p className="text-base font-black text-emerald-700">{fmtBRL(t.total_sold)}</p></div>
        <div className="bg-violet-50 border border-violet-100 rounded-xl p-3"><p className="text-[10px] font-bold text-violet-500 uppercase">Comissao Total</p><p className="text-base font-black text-violet-700">{fmtBRL(t.commission_amount)}</p></div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-xs" data-testid="commission-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-slate-500">
              <th className="px-3 py-2">Vendedor</th>
              <th className="px-3 py-2 text-right">Vendas</th>
              <th className="px-3 py-2 text-right">Total Vendido</th>
              <th className="px-3 py-2 text-right">% Comissao</th>
              <th className="px-3 py-2 text-right">Comissao a Pagar</th>
            </tr>
          </thead>
          <tbody>
            {data.data.map(r => (
              <tr key={r.vendor_id || 'no-vendor'} className={`border-b border-slate-100 ${!r.vendor_id ? 'bg-slate-50/40 italic text-slate-500' : 'hover:bg-violet-50/40'}`} data-testid="commission-row">
                <td className="px-3 py-2 font-semibold text-slate-800">{r.name}</td>
                <td className="px-3 py-2 text-right font-mono">{r.vendas_count}</td>
                <td className="px-3 py-2 text-right text-emerald-600">{fmtBRL(r.total_sold)}</td>
                <td className="px-3 py-2 text-right text-slate-600 font-mono">{r.commission_pct}%</td>
                <td className="px-3 py-2 text-right font-bold text-violet-700">{fmtBRL(r.commission_amount)}</td>
              </tr>
            ))}
            {data.data.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Sem vendas no periodo</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const DashHome = ({ companyId, selectedCompanyId = null, hiddenCards = {}, userRole = 'operator', userName = '', goToSection = () => {}, businessType = null }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (period === 'custom' && (customFrom || customTo)) {
          if (customFrom) params.append('date_from', customFrom);
          if (customTo) params.append('date_to', customTo);
        } else {
          params.append('period', period);
        }
        const { data } = await axios.get(`${ERP}/dashboard/${companyId || 'all'}?${params}`, { withCredentials: true });
        setStats(data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetch();
  }, [companyId, period, customFrom, customTo]);
  const fmtMoney = (v) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // iter77bu: forcado a mostrar numero INTEIRO completo (sem abreviacao K/M) mesmo em telas pequenas.
  const fmtInt = (v) => Math.round(Number(v) || 0).toLocaleString('pt-BR');
  const periodLabel = { yesterday: 'Mes Anterior', today: 'Hoje', week: 'Esta Semana', month: 'Este Mes', all: 'Tudo', custom: 'Periodo' }[period];
  const PERIOD_CHIPS = [
    // iter77ct.78: "Anterior" (Ontem) foi adicionado antes de "Hoje" — vendas retroativas comuns
    // iter156s: rotulo renomeado para "Mes Anterior" (chip agora representa mes anterior completo,
    // nao mais "ontem"). Ver iter156p onde o backend foi ajustado.
    { key: 'yesterday', label: 'Mes Anterior' },
    { key: 'today', label: 'Hoje' },
    { key: 'week', label: 'Esta Semana' },
    { key: 'month', label: 'Este Mes' },
    // iter77ct.78: removido "Tudo" — ocupava espaco e "Personalizado" cobre o caso raro
    { key: 'custom', label: 'Personalizado' },
  ];
  // iter77br: SEMANTICA INVERTIDA — dashboard_cards[key] === true significa VISIVEL (antes era oculto).
  // Admin/superadmin veem tudo. Quando hiddenCards eh vazio (legado nao migrado), tambem mostra tudo
  // para evitar dashboards em branco (transicao suave).
  const isAdminish = userRole === 'admin' || userRole === 'superadmin';
  const hasAnyConfig = hiddenCards && Object.keys(hiddenCards).length > 0;
  const showCard = (key) => isAdminish || !hasAnyConfig || hiddenCards[key] === true;
  const cards = stats ? [
    { key: 'vendas', label: `Vendas ${periodLabel}`, value: `R$ ${fmtMoney(stats.total_vendas)}`, icon: DollarSign, gradient: 'from-emerald-500 to-teal-600', iconBg: 'bg-emerald-50 text-emerald-600' },
    { key: 'qtd_vendas', label: 'Qtd. de Vendas', value: fmtInt(stats.qtd_vendas), icon: ShoppingCart, gradient: 'from-sky-500 to-blue-600', iconBg: 'bg-sky-50 text-sky-600' },
    // iter77ct.78: novo card Ticket Medio (total_vendas / qtd_vendas). Zero-safe.
    { key: 'ticket_medio', label: 'Ticket Medio', value: `R$ ${fmtMoney((stats.qtd_vendas || 0) > 0 ? (stats.total_vendas || 0) / stats.qtd_vendas : 0)}`, icon: Calculator, gradient: 'from-fuchsia-500 to-pink-600', iconBg: 'bg-fuchsia-50 text-fuchsia-600' },
    { key: 'qtd_itens', label: 'Produtos Vendidos', value: fmtInt(stats.qtd_itens), icon: Package, gradient: 'from-violet-500 to-purple-600', iconBg: 'bg-violet-50 text-violet-600' },
    { key: 'saldo_estoque', label: 'Saldo em Estoque (un.)', value: fmtInt(stats.saldo_estoque), icon: Package, gradient: 'from-amber-500 to-orange-600', iconBg: 'bg-amber-50 text-amber-600' },
    { key: 'a_receber', label: 'A Receber', value: `R$ ${fmtMoney(stats.total_receber)}`, icon: ArrowUpRight, gradient: 'from-blue-500 to-violet-600', iconBg: 'bg-blue-50 text-blue-600' },
    { key: 'a_pagar', label: 'A Pagar', value: `R$ ${fmtMoney(stats.total_pagar)}`, icon: ArrowDownRight, gradient: 'from-rose-500 to-red-600', iconBg: 'bg-rose-50 text-rose-600' },
  ].filter(c => showCard(c.key)) : [];
  // iter77br: Atalhos clicaveis no Dashboard — controlados pelo mesmo dashboard_cards (chave shortcut_*).
  // iter156bh: novo atalho `shortcut_ponto` — dispara direto uma batida de ponto (nao navega),
  // gated pelo BT/tenant `ponto_module_enabled`. iter156bi: modal moderno centralizado com
  // nome/data/hora substituindo o toast simples.
  // iter156ey: fluxo unificado (hook) — checa o dia, alerta duplicidade/sequencia e mostra resumo.
  const ponto = usePontoFlow(selectedCompanyId, 'shortcut', userName);
  const _punchPonto = () => ponto.start(null);
  const SHORTCUTS = [
    { key: 'shortcut_sales',             section: 'sales',             label: 'Vendas',                icon: BarChart3,  gradient: 'from-emerald-600 to-teal-600',  iconBg: 'bg-emerald-50 text-emerald-700' },
    { key: 'shortcut_clients',           section: 'clients',           label: 'Clientes',              icon: Users,      gradient: 'from-blue-600 to-indigo-600',   iconBg: 'bg-blue-50 text-blue-700' },
    { key: 'shortcut_products',          section: 'products',          label: 'Produtos',              icon: Package,    gradient: 'from-violet-600 to-purple-600', iconBg: 'bg-violet-50 text-violet-700' },
    { key: 'shortcut_invoices',          section: 'invoices',          label: 'NF-e / NFC-e',          icon: Receipt,    gradient: 'from-cyan-600 to-blue-600',     iconBg: 'bg-cyan-50 text-cyan-700' },
    { key: 'shortcut_purchase_requests', section: 'purchase-requests', label: 'Solicitacao de Compra', icon: FileText,   gradient: 'from-amber-600 to-orange-600',  iconBg: 'bg-amber-50 text-amber-700' },
    // iter156bh: Atalho Ponto — so aparece se o modulo ponto esta habilitado pelo tenant/BT
    ...(businessType?.ponto_module_enabled ? [{ key: 'shortcut_ponto', label: 'Bater Ponto', icon: Clock, gradient: 'from-teal-600 to-cyan-600', iconBg: 'bg-teal-50 text-teal-700', onClick: _punchPonto }] : []),
  ].filter(s => showCard(s.key));
  return (
    <div data-testid="erp-dash-home" className="overflow-x-hidden">
      {/* iter77ct.98: chips em linha unica com scroll horizontal (sem wrap) — segue mesmo padrao dos atalhos */}
      <div className="mb-4 flex items-center gap-1.5 overflow-x-auto snap-x snap-mandatory pb-1 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="dash-period-chips">
        {PERIOD_CHIPS.map(p => (
          <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`period-chip-${p.key}`}
            className={`flex-shrink-0 snap-start px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${period === p.key ? 'bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md shadow-violet-500/30' : 'bg-white border border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700'}`}>{p.label}</button>
        ))}
        {period === 'custom' && (
          <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="text-xs border border-slate-200 rounded-full px-3 py-1.5 bg-white focus:ring-2 focus:ring-violet-300 outline-none" data-testid="period-from" />
            <span className="text-xs text-slate-400">ate</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="text-xs border border-slate-200 rounded-full px-3 py-1.5 bg-white focus:ring-2 focus:ring-violet-300 outline-none" data-testid="period-to" />
          </div>
        )}
      </div>
      {loading && <Loader />}
      {!loading && !stats && <p className="text-slate-500 text-center py-12">Sem dados</p>}
      {!loading && stats && (
      <>
      {/* iter77by: layout flex-wrap responsivo — cards se acomodam bem mesmo quando o perfil oculta a maioria.
          iter77ct.96: em mobile, se o card ficar sozinho na linha (odd count), expande para 100%. */}
      <div className="flex flex-wrap gap-3 md:gap-4 mb-6" data-testid="dash-cards">
        {cards.map((c, i) => (
          <div
            key={i}
            className="group relative overflow-hidden bg-white rounded-2xl border border-slate-200/70 shadow-sm hover:shadow-xl hover:shadow-violet-500/10 hover:border-violet-300 hover:-translate-y-0.5 transition-all duration-200 flex-1 basis-[160px] min-w-[160px] md:max-w-[260px] p-4"
            data-testid={`dash-card-${i}`}
          >
            <div className={`absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r ${c.gradient}`} />
            <div className={`absolute -right-4 -top-4 w-20 h-20 rounded-full bg-gradient-to-br ${c.gradient} opacity-[0.06] group-hover:opacity-[0.12] transition-opacity`} />
            <div className="flex items-start justify-between mb-3 mt-1 relative">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider leading-tight pr-2">{c.label}</span>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${c.iconBg}`}><c.icon size={16} /></div>
            </div>
            <p className="text-lg md:text-xl font-black text-slate-900 tracking-tight break-words leading-tight tabular-nums relative">{c.value}</p>
          </div>
        ))}
      </div>
      {/* iter77ct.96: Atalhos redondos estilo Instagram Stories — scroll horizontal em linha unica,
          gesto de deslizar (snap), sem quebra pra segunda linha. Em desktop ganham hover elevando. */}
      {SHORTCUTS.length > 0 && (
        <div className="mb-6" data-testid="dash-shortcuts">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-4 rounded-full bg-gradient-to-b from-blue-600 to-violet-600" />
            <p className="text-[10px] font-black text-slate-600 uppercase tracking-[0.18em]">Atalhos</p>
          </div>
          {/* iter77ct.98: centralizado no desktop com "rails" horizontais slim de cada
              lado (linhas gradiente que se estendem ate as bordas), dando harmonia visual
              e evitando o "flutuar" no meio. No mobile so as bolinhas em scroll horizontal. */}
          <div className="flex items-center gap-3 md:gap-4">
            <div className="hidden md:block flex-1 h-px bg-gradient-to-r from-transparent via-slate-300/70 to-slate-300/70" />
            <div className="flex gap-3 md:gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none pb-2 md:pb-0 -mx-1 px-1 md:mx-0 md:px-0 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-thumb]:rounded-full">
              {SHORTCUTS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => (s.onClick ? s.onClick() : goToSection(s.section))}
                  className="group flex-shrink-0 snap-start flex flex-col items-center gap-1.5 w-[76px] active:scale-95 transition-transform"
                  data-testid={`dash-shortcut-${s.key}`}
                  title={s.onClick ? s.label : `Ir para ${s.label}`}
                >
                  <div className={`w-[68px] h-[68px] rounded-full p-[3px] bg-gradient-to-br ${s.gradient} shadow-md shadow-slate-900/10 group-hover:shadow-lg group-hover:-translate-y-0.5 transition-all`}>
                    <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                      <div className={`w-[54px] h-[54px] rounded-full bg-gradient-to-br ${s.gradient} flex items-center justify-center text-white`}>
                        <s.icon size={22} />
                      </div>
                    </div>
                  </div>
                  <p className="text-[10.5px] font-black text-slate-700 leading-tight text-center max-w-[76px] truncate">{s.label}</p>
                </button>
              ))}
            </div>
            <div className="hidden md:block flex-1 h-px bg-gradient-to-l from-transparent via-slate-300/70 to-slate-300/70" />
          </div>
        </div>
      )}
      {showCard('vendas_por_empresa') && stats.top_empresas?.length > 0 && (
        <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden relative">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-violet-600" />
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Performance</p>
              <h3 className="font-black text-slate-900 text-lg tracking-tight">Vendas por Empresa</h3>
            </div>
            <span className="text-[10px] font-bold text-violet-700 bg-gradient-to-r from-violet-50 to-cyan-50 border border-violet-200 px-2.5 py-1 rounded-full">{stats.top_empresas.length} empresa{stats.top_empresas.length > 1 ? 's' : ''}</span>
          </div>
          <div className="divide-y divide-slate-50">
            {stats.top_empresas.map((e, i) => (
              <div key={i} className="px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors" data-testid="empresa-row">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center flex-shrink-0 text-white font-black text-xs shadow-md shadow-violet-500/30">{i + 1}</div>
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-slate-900 truncate leading-tight">{e.name}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{e.code} · {e.qtd_vendas} venda{e.qtd_vendas !== 1 ? 's' : ''} · {e.qtd_itens} iten{e.qtd_itens !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-black text-sm text-slate-900 tracking-tight">R$ {fmtMoney(e.valor_total ?? e.valor_liquido)}</p>
                  <p className="text-[10px] text-slate-400">total</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* iter156p: quando o chip "Anterior" (mes anterior) esta ativo, passa referenceDate = ultimo dia do mes anterior
          para trazer as metas vigentes naquele periodo. Formato YYYY-MM-DD. */}
      {showCard('meta') && <MetaDashboardCard companyId={companyId} referenceDate={period === 'yesterday' ? (() => {
        const d = new Date();
        d.setDate(1); // primeiro do mes atual
        d.setDate(0); // dia 0 = ultimo dia do mes anterior
        const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${dd}`;
      })() : null} />}
      </>
      )}
      {/* iter156bi/iter156ey: modais do ponto (alerta de sequencia + confirmacao com resumo do dia). */}
      {ponto.modals}
    </div>
  );
};

/* ========== iter156ey: PONTO — regras de sequencia compartilhadas (atalho + pagina Ponto) ========== */
const PONTO_LABELS = { entrada: 'Entrada', almoco_ini: 'Saida para Almoco', almoco_fim: 'Retorno do Almoco', saida: 'Saida' };
const PONTO_COLORS = { entrada: 'from-emerald-500 to-teal-600', almoco_ini: 'from-amber-500 to-orange-600', almoco_fim: 'from-orange-500 to-rose-500', saida: 'from-rose-500 to-red-600' };
const pontoHHMM = (ts) => new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

// Decide se bate direto ou pergunta. `today` = GET /time-punches/today; `requested` = tipo clicado (null = proximo sugerido).
const pontoDecide = (today, requested) => {
  const summary = today?.data || [];
  const done = summary.map(p => p.punch_type);
  const next = today?.next_type || null;
  const last = today?.last_punch || null;
  const L = (t) => PONTO_LABELS[t] || t;
  const recent = last && last.minutes_ago != null && last.minutes_ago < 60;
  if (!requested) {
    if (!next) return { mode: 'prompt', kind: 'complete', title: 'Jornada completa', message: 'Voce ja registrou os 4 pontos de hoje. Nenhum novo registro e necessario.', summary, options: [] };
    if (recent) return { mode: 'prompt', kind: 'recent', title: `${L(last.punch_type)} ja registrado`, message: `Seu ponto de ${L(last.punch_type)} ja foi registrado as ${pontoHHMM(last.ts)} (ha ${last.minutes_ago} min). Deseja registrar ${L(next)} agora?`, summary, options: [{ type: next, label: `Sim, registrar ${L(next)}`, primary: true }] };
    return { mode: 'direct', type: next };
  }
  if (done.includes(requested)) {
    const dup = summary.find(p => p.punch_type === requested);
    return { mode: 'prompt', kind: 'dup', title: `${L(requested)} ja registrado`, message: `Seu ponto de ${L(requested)} ja foi registrado hoje as ${pontoHHMM(dup.ts)}.` + (next ? ` Deseja registrar ${L(next)}?` : ' Os 4 pontos de hoje ja foram registrados.'), summary, options: [...(next ? [{ type: next, label: `Registrar ${L(next)}`, primary: true }] : []), { type: requested, label: `Registrar ${L(requested)} novamente`, force: true, danger: true }] };
  }
  if (next && requested !== next) return { mode: 'prompt', kind: 'order', title: 'Sequencia diferente', message: `O proximo ponto sugerido e ${L(next)}. Deseja registrar ${L(requested)} mesmo assim?`, summary, options: [{ type: next, label: `Registrar ${L(next)}`, primary: true }, { type: requested, label: `Registrar ${L(requested)}` }] };
  if (recent) return { mode: 'prompt', kind: 'recent', title: `${L(last.punch_type)} ja registrado`, message: `Seu ponto de ${L(last.punch_type)} ja foi registrado as ${pontoHHMM(last.ts)} (ha ${last.minutes_ago} min). Confirma o registro de ${L(requested)}?`, summary, options: [{ type: requested, label: `Sim, registrar ${L(requested)}`, primary: true }] };
  return { mode: 'direct', type: requested };
};

const PontoDaySummary = ({ summary, highlightId }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="ponto-day-summary">
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Resumo do dia</p>
    {(!summary || summary.length === 0) ? <p className="text-xs text-slate-400">Nenhum ponto registrado hoje.</p> : (
      <ul className="space-y-1.5">
        {['entrada', 'almoco_ini', 'almoco_fim', 'saida'].map(t => {
          const p = summary.find(x => x.punch_type === t);
          return (
            <li key={t} className={`flex items-center justify-between text-xs ${p ? 'text-slate-800' : 'text-slate-300'}`} data-testid={`ponto-summary-${t}`}>
              <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${p ? (p.id === highlightId ? 'bg-emerald-500 ring-2 ring-emerald-200' : 'bg-teal-500') : 'bg-slate-200'}`} />{PONTO_LABELS[t]}</span>
              <span className="font-mono font-bold tabular-nums">{p ? pontoHHMM(p.ts) : '--:--'}</span>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

const PontoPromptModal = ({ prompt, busy, onPick, onClose }) => (
  <div className="fixed inset-0 z-[300] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="ponto-prompt-modal">
    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
      <div className={`px-6 py-6 text-center bg-gradient-to-br ${prompt.kind === 'complete' ? 'from-sky-500 to-indigo-600' : 'from-amber-500 to-orange-600'}`}>
        <div className="w-14 h-14 mx-auto mb-2 rounded-full bg-white/25 flex items-center justify-center border border-white/30">
          {prompt.kind === 'complete' ? <CheckCircle size={28} className="text-white" /> : <AlertTriangle size={28} className="text-white" />}
        </div>
        <p className="text-[11px] font-black text-white/90 uppercase tracking-[0.22em]">{prompt.kind === 'complete' ? 'Ponto do dia' : 'Atencao'}</p>
        <p className="text-xl font-black text-white mt-1" data-testid="ponto-prompt-title">{prompt.title}</p>
      </div>
      <div className="px-6 py-5 space-y-4">
        <p className="text-sm text-slate-700 text-center" data-testid="ponto-prompt-message">{prompt.message}</p>
        <PontoDaySummary summary={prompt.summary} />
      </div>
      <div className="px-6 pb-6 space-y-2">
        {prompt.options.map(o => (
          <button key={o.type + (o.force ? '-f' : '')} disabled={busy} onClick={() => onPick(o)}
            className={`w-full py-3 rounded-xl text-sm font-black uppercase tracking-wider transition-all disabled:opacity-50 ${o.primary ? `bg-gradient-to-r ${PONTO_COLORS[o.type] || 'from-teal-500 to-cyan-600'} text-white shadow-lg hover:scale-[1.02]` : o.danger ? 'border border-rose-200 text-rose-600 hover:bg-rose-50' : 'border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
            data-testid={`ponto-prompt-opt-${o.type}${o.force ? '-force' : ''}`}>
            {busy ? 'Registrando...' : o.label}
          </button>
        ))}
        <button onClick={onClose} className="w-full py-2.5 rounded-xl text-xs font-bold text-slate-500 hover:text-slate-900" data-testid="ponto-prompt-cancel">{prompt.options.length ? 'Cancelar' : 'Fechar'}</button>
      </div>
    </div>
  </div>
);

// Hook: start(requestedType|null) -> consulta o dia, decide, bate ou pergunta; `modals` deve ser renderizado pelo caller.
const usePontoFlow = (companyId, source, fallbackName, onDone) => {
  const [prompt, setPrompt] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const doPunch = async (type, force = false) => {
    setBusy(true);
    try {
      let lat = null, lng = null;
      try {
        await new Promise((resolve) => {
          if (!navigator.geolocation) return resolve();
          const to = setTimeout(resolve, 1500);
          navigator.geolocation.getCurrentPosition(
            (pos) => { lat = pos.coords.latitude; lng = pos.coords.longitude; clearTimeout(to); resolve(); },
            () => { clearTimeout(to); resolve(); },
            { timeout: 1400, maximumAge: 60000 }
          );
        });
      } catch { /* no geolocation */ }
      const { data: created } = await axios.post(`${ERP}/time-punches`, { punch_type: type, company_id: companyId || '', source, lat, lng, force }, { withCredentials: true });
      setPrompt(null);
      setConfirm({ id: created?.id, name: created?.employee_name || fallbackName || 'Usuario', ts: created?.ts || new Date().toISOString(), punch_type: created?.punch_type || type, today: created?.today || null });
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao registrar ponto', { duration: 7000 });
    } finally { setBusy(false); }
  };
  const start = async (requested = null) => {
    let today = { data: [], next_type: 'entrada' };
    try { const { data } = await axios.get(`${ERP}/time-punches/today`, { withCredentials: true }); today = data; } catch { /* usa default */ }
    const d = pontoDecide(today, requested);
    if (d.mode === 'direct') return doPunch(d.type);
    setPrompt(d);
  };
  const modals = (
    <>
      {prompt && <PontoPromptModal prompt={prompt} busy={busy} onPick={(o) => doPunch(o.type, !!o.force)} onClose={() => setPrompt(null)} />}
      {confirm && <PontoConfirmModal info={confirm} onClose={() => setConfirm(null)} />}
    </>
  );
  return { start, modals, busy };
};

/* ========== iter156bi: PONTO CONFIRM MODAL (modal centralizado apos bater ponto) ========== */
const PontoConfirmModal = ({ info, onClose }) => {
  const { name, ts, punch_type, today, id } = info || {};
  const LABEL = PONTO_LABELS[punch_type] || 'Ponto';
  const COLOR = PONTO_COLORS[punch_type] || 'from-teal-500 to-cyan-600';
  const complete = !!today?.complete;
  const dt = ts ? new Date(ts) : new Date();
  const dateStr = dt.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  const timeStr = dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  // ESC closes modal
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[300] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="ponto-confirm-modal"
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-white/40 animate-in zoom-in-95 duration-200">
        <div className={`bg-gradient-to-br ${COLOR} px-6 py-8 text-center relative overflow-hidden`}>
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 blur-xl" />
          <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-white/10 blur-xl" />
          <div className="relative">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-white/25 backdrop-blur-sm flex items-center justify-center border border-white/30 shadow-lg">
              <CheckCircle size={32} className="text-white drop-shadow" />
            </div>
            <p className="text-[11px] font-black text-white/90 uppercase tracking-[0.22em]">{complete ? 'Jornada completa' : 'Ponto Registrado'}</p>
            <p className="text-2xl font-black text-white mt-1 drop-shadow">{LABEL}</p>
            {complete && <p className="text-[11px] text-white/80 mt-1">Todos os 4 pontos de hoje foram registrados.</p>}
          </div>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="text-center">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Colaborador</p>
            <p className="text-base font-black text-slate-900" data-testid="ponto-confirm-name">{name}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100">
            <div className="text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Data</p>
              <p className="text-xs font-bold text-slate-700 capitalize" data-testid="ponto-confirm-date">{dateStr}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Horario</p>
              <p className="text-lg font-black text-slate-900 tabular-nums tracking-tight" data-testid="ponto-confirm-time">{timeStr}</p>
            </div>
          </div>
          {today && <PontoDaySummary summary={today.data} highlightId={id} />}
          {today && !complete && today.next_label && <p className="text-[11px] text-center text-slate-500">Proximo registro: <b>{PONTO_LABELS[today.next_type] || today.next_label}</b></p>}
        </div>
        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            className={`w-full py-3 rounded-xl bg-gradient-to-r ${COLOR} text-white text-sm font-black uppercase tracking-wider shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all`}
            data-testid="ponto-confirm-close"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};


/* ========== COMPANIES PICKER (multi-loja sharing) ==========
   Reused across Cliente, Fornecedor, Funcionario, Tabela de Preco, Usuario.
   `value=[]` (vazio) = visivel/aplicavel em TODAS as filiais (default).
   Marcar 1+ filiais = somente as marcadas enxergam/usam o cadastro.
*/
const CompaniesPicker = ({ value = [], onChange, label = 'Filiais com acesso', testId = 'companies-picker' }) => {
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    let m = true;
    axios.get(`${ERP}/companies`, { withCredentials: true })
      .then(({ data }) => { if (m) setCompanies(data.data || []); })
      .catch(() => {});
    return () => { m = false; };
  }, []);
  const toggle = (id) => {
    const set = new Set(value || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    onChange(Array.from(set));
  };
  const allSelected = (value || []).length === 0;
  return (
    <div data-testid={testId}>
      <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
      <div className="border border-gray-200 rounded-xl bg-white p-2.5">
        <button type="button" onClick={() => onChange([])} className={`mb-1.5 w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold transition-colors ${allSelected ? 'bg-violet-100 text-violet-700 border border-violet-200' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-transparent'}`} data-testid={`${testId}-all`}>
          {allSelected ? '✓ ' : ''}TODAS as filiais (compartilhado)
        </button>
        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
          {companies.map(c => {
            const on = (value || []).includes(c.id);
            return (
              <button type="button" key={c.id} onClick={() => toggle(c.id)} className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${on ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'}`} data-testid={`${testId}-${c.id}`}>
                {on && '✓ '}{c.name}
              </button>
            );
          })}
          {companies.length === 0 && <span className="text-[11px] text-gray-400 italic">Carregando filiais...</span>}
        </div>
        <p className="text-[10px] text-gray-400 mt-1.5">Vazio = TODAS as filiais. Marque 1+ filiais para restringir visibilidade.</p>
      </div>
    </div>
  );
};


/* ========== COMPANIES PAGE ========== */
/* ===== CompanyDefaultClientPicker (iter77bz) — seleção do cliente padrão do PDV por filial ===== */
const CompanyDefaultClientPicker = ({ companyId, value, onChange }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    axios.get(`${ERP}/clients?company_id=${companyId}`, { withCredentials: true })
      .then(({ data }) => setClients(data.data || []))
      .catch(() => setClients([]))
      .finally(() => setLoading(false));
  }, [companyId]);
  const currentClient = clients.find(c => c.id === value);
  return (
    <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3" data-testid="company-default-client">
      <label className="block text-xs font-bold text-cyan-800 mb-1.5 flex items-center gap-1.5">
        <Users size={13} /> Cliente Padrão do PDV
      </label>
      <p className="text-[11px] text-cyan-700 mb-2">
        Cliente que será pré-selecionado ao abrir o PDV desta filial. Evita usar crédito de devolução de outra filial acidentalmente.
      </p>
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        disabled={loading}
        className={inputClass}
        data-testid="company-default-client-select"
      >
        <option value="">— Sem cliente padrão (Consumidor Final) —</option>
        {clients.map(c => (
          <option key={c.id} value={c.id}>
            {c.name}{c.document ? ` · ${c.document}` : ''}{c.is_default_pdv_consumer ? ' ★' : ''}
          </option>
        ))}
      </select>
      {currentClient && (
        <p className="text-[11px] text-cyan-800 mt-1.5"><strong>Selecionado:</strong> {currentClient.name}</p>
      )}
    </div>
  );
};

const CompaniesPage = ({ companies, onRefresh }) => {
  const blankForm = {
    name: '', code: '', cnpj: '', address: '', phone: '', logo_url: '',
    is_matriz: false,
    razao_social: '', nome_fantasia: '', ie: '', im: '', iest: '', cnae: '', crt: '1',
    endereco_logradouro: '', endereco_numero: '', endereco_complemento: '',
    endereco_bairro: '', endereco_municipio: '', endereco_cod_municipio: '',
    endereco_uf: '', endereco_cep: '',
    default_client_id: '',
    allow_negative_stock_sale: null, // iter77cm: null = herda tenant global; true/false override per-filial
  };
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [formTab, setFormTab] = useState('basico'); // basico | fiscal
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  // iter77y: state + helper para lookup CNPJ via BrasilAPI
  const [cnpjLookingUp, setCnpjLookingUp] = useState(false);
  const lookupCnpjAndFill = async (rawCnpj) => {
    const digits = (rawCnpj || '').replace(/\D/g, '');
    if (digits.length !== 14) { toast.info('Digite um CNPJ com 14 digitos para buscar'); return; }
    setCnpjLookingUp(true);
    try {
      const { data } = await axios.get(`${API}/lookup/cnpj/${digits}`, { withCredentials: true });
      setForm(f => ({
        ...f,
        cnpj: data.cnpj || f.cnpj,
        razao_social: data.razao_social || f.razao_social,
        nome_fantasia: data.nome_fantasia || f.nome_fantasia,
        cnae: data.cnae_fiscal || f.cnae,
        endereco_logradouro: data.logradouro || f.endereco_logradouro,
        endereco_numero: data.numero || f.endereco_numero,
        endereco_complemento: data.complemento || f.endereco_complemento,
        endereco_bairro: data.bairro || f.endereco_bairro,
        endereco_cep: data.cep || f.endereco_cep,
        endereco_municipio: data.municipio || f.endereco_municipio,
        endereco_cod_municipio: data.codigo_municipio_ibge || f.endereco_cod_municipio,
        endereco_uf: data.uf || f.endereco_uf,
        phone: f.phone || data.telefone || '',
      }));
      toast.success(`Dados de ${data.razao_social || 'empresa'} carregados`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao consultar CNPJ';
      toast.error(detail);
    } finally { setCnpjLookingUp(false); }
  };
  const resetForm = () => { setForm(blankForm); setEditing(null); setFormTab('basico'); };
  // iter156u: campo de busca (nome, codigo, CNPJ, razao social, nome fantasia)
  const [searchQuery, setSearchQuery] = useState('');
  // iter156fk: filiais desativadas pelo Super Admin nao vem no GET padrao; busca-as aqui para exibir esmaecidas
  const [disabledCompanies, setDisabledCompanies] = useState([]);
  useEffect(() => {
    axios.get(`${ERP}/companies?include_disabled=1`, { withCredentials: true })
      .then(({ data }) => setDisabledCompanies((data.data || []).filter(c => c.disabled_by_superadmin)))
      .catch(() => setDisabledCompanies([]));
  }, [companies]);
  const _normalizeSearch = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const filteredCompanies = React.useMemo(() => {
    const q = _normalizeSearch(searchQuery.trim());
    const list = [...(companies || []).filter(c => c.active !== false && !c.disabled_by_superadmin), ...disabledCompanies];
    if (!q) return list;
    return list.filter(c =>
      _normalizeSearch(c.name).includes(q) ||
      _normalizeSearch(c.code || '').includes(q) ||
      _normalizeSearch(c.cnpj || '').includes(q) ||
      _normalizeSearch(c.razao_social || '').includes(q) ||
      _normalizeSearch(c.nome_fantasia || '').includes(q)
    );
  }, [companies, searchQuery, disabledCompanies]);
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo max 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, logo_url: data.url }));
      toast.success('Logo enviado');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro no upload'); }
    finally { setUploading(false); }
  };
  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editing) { await axios.put(`${ERP}/companies/${editing.id}`, form, { withCredentials: true }); toast.success('Empresa atualizada!'); }
      else { await axios.post(`${ERP}/companies`, form, { withCredentials: true }); toast.success('Empresa criada!'); }
      setShowForm(false); resetForm(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSubmitting(false); }
  };
  const handleDelete = async (id) => { if (!(await confirmDialog({ title: 'Excluir empresa?\n\nSe tiver vendas/financeiro/movimentacoes, sera apenas inativada.', confirmLabel: 'Confirmar', danger: true }))) return; try { const { data } = await axios.delete(`${ERP}/companies/${id}`, { withCredentials: true }); if (data.deactivated) toast.info(data.message); else toast.success(data.message || 'Excluida'); onRefresh(); } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } };
  const resolveLogo = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API.replace(/\/api$/, '')}${url}`;
  };
  return (
    <div data-testid="companies-page">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">{companies.length} empresas</p>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className={btnPrimary} data-testid="add-company-btn"><Plus size={16} /><span>Nova Empresa</span></button>
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-teal-200 rounded-xl p-5 mb-5" data-testid="company-form">
          <h3 className="font-bold text-gray-900 mb-4">{editing ? 'Editar Empresa' : 'Nova Empresa'}</h3>
          <div className="flex items-start space-x-4 mb-4" data-testid="company-logo-field">
            <div className="flex-shrink-0">
              {form.logo_url ? (
                <div className="relative w-24 h-24 rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                  <img src={resolveLogo(form.logo_url)} alt="Logo" className="w-full h-full object-contain" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, logo_url: '' }))} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center" data-testid="remove-logo-btn"><X size={12} /></button>
                </div>
              ) : (
                <label className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center cursor-pointer hover:border-teal-400 transition-colors" data-testid="upload-logo-label">
                  <Plus size={18} className="text-gray-400" />
                  <span className="text-[10px] text-gray-500 mt-1">{uploading ? 'Enviando...' : 'Logo'}</span>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" disabled={uploading} />
                </label>
              )}
            </div>
            <div className="flex-1 text-xs text-gray-500 pt-1">
              <p className="font-semibold mb-1">Logo da empresa</p>
              <p>PNG, JPG ou WebP • Máx 5MB • Recomendado 512×512px</p>
            </div>
          </div>
          <div className="flex gap-2 mb-4 border-b border-slate-200" data-testid="company-form-tabs">
            <button type="button" onClick={() => setFormTab('basico')} className={`px-3 py-2 text-xs font-bold ${formTab === 'basico' ? 'border-b-2 border-violet-600 text-violet-700' : 'text-slate-500'}`} data-testid="company-tab-basico">Basico</button>
            <button type="button" onClick={() => setFormTab('fiscal')} className={`px-3 py-2 text-xs font-bold ${formTab === 'fiscal' ? 'border-b-2 border-violet-600 text-violet-700' : 'text-slate-500'}`} data-testid="company-tab-fiscal">Fiscal (NF-e/NFC-e)</button>
          </div>
          {formTab === 'basico' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Codigo *</label><input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} className={inputClass} required placeholder="001" data-testid="company-code" disabled={!!editing} /></div>
            <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Nome *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} required placeholder="MATRIZ" data-testid="company-name" /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Telefone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputClass} /></div>
            <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Endereco</label><input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className={inputClass} placeholder="Resumo livre (o endereco fiscal completo fica na aba Fiscal)" /></div>
            <div className="md:col-span-3 bg-violet-50 border border-violet-200 rounded-xl p-3 mt-1">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="company-matriz-label">
                <input type="checkbox" checked={!!form.is_matriz} onChange={e => setForm({ ...form, is_matriz: e.target.checked })} className="mt-1 accent-violet-600" data-testid="company-is-matriz" />
                <span className="text-xs">
                  <span className="font-bold text-violet-800">Esta empresa e a MATRIZ</span>
                  <span className="block text-violet-700 mt-0.5">Quando marcada, esta empresa recebera automaticamente todas as Solicitacoes de Compra criadas pelas demais filiais. Apenas UMA empresa pode ser matriz por vez — marcar esta desmarca as demais.</span>
                </span>
              </label>
            </div>
            {/* iter77bz: Cliente Padrão do PDV */}
            {editing ? (
              <div className="md:col-span-3">
                <CompanyDefaultClientPicker companyId={editing.id} value={form.default_client_id} onChange={(id) => setForm(f => ({ ...f, default_client_id: id }))} />
              </div>
            ) : (
              <div className="md:col-span-3 bg-cyan-50 border border-cyan-200 rounded-xl p-3 text-xs text-cyan-800">
                <strong>💡 Cliente padrão do PDV:</strong> ao criar esta filial, o sistema irá gerar automaticamente um cliente <em>"Consumidor {form.name || '<nome da filial>'}"</em> vinculado somente a esta filial e defini-lo como cliente padrão do PDV. Você poderá trocar depois em "Editar filial".
              </div>
            )}
            {/* iter77cm: override PER-FILIAL do parâmetro "Permitir venda com estoque negativo" */}
            <div className="md:col-span-3 border border-slate-200 rounded-xl p-3 bg-slate-50">
              <p className="text-xs font-black text-slate-700 uppercase mb-2">📦 Estoque negativo nesta filial</p>
              <p className="text-[11px] text-slate-500 mb-2">Controla se o PDV desta filial pode concluir venda quando o saldo do produto está zerado/negativo. Por default herda a configuração global do tenant (Configurações → Estoque).</p>
              <div className="flex flex-col md:flex-row gap-2">
                {[
                  { v: null, label: '⚙️ Herdar do global', c: 'slate' },
                  { v: false, label: '🛑 Bloquear (default)', c: 'rose' },
                  { v: true, label: '✅ Permitir', c: 'emerald' },
                ].map(opt => {
                  const active = form.allow_negative_stock_sale === opt.v;
                  return (
                    <button key={String(opt.v)} type="button" onClick={() => setForm(f => ({ ...f, allow_negative_stock_sale: opt.v }))}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-colors border-2 ${active
                        ? (opt.c === 'emerald' ? 'bg-emerald-600 text-white border-emerald-600' : opt.c === 'rose' ? 'bg-rose-600 text-white border-rose-600' : 'bg-slate-700 text-white border-slate-700')
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                      data-testid={`neg-stock-${opt.v === null ? 'inherit' : opt.v}`}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          )}
          {formTab === 'fiscal' && (
          <div className="space-y-4 mb-4" data-testid="company-fiscal-tab">
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 text-xs text-violet-800">
              Dados obrigatorios para emissao de NF-e / NFC-e via SEFAZ. Codigo IBGE do Municipio (7 digitos) e essencial — consulte em <a href="https://www.ibge.gov.br/explica/codigos-dos-municipios.php" target="_blank" rel="noopener noreferrer" className="underline font-bold">ibge.gov.br/codigos</a>.
            </div>
            {/* iter77y: CNPJ no topo da aba Fiscal + autopreencher via BrasilAPI */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-500 mb-1">CNPJ <span className="text-violet-600 font-bold">(autopreenche os dados)</span></label>
                <div className="flex gap-2">
                  <input value={form.cnpj} onChange={e => setForm({ ...form, cnpj: e.target.value })} onBlur={() => lookupCnpjAndFill(form.cnpj)} className={inputClass} placeholder="00.000.000/0000-00" data-testid="company-cnpj" />
                  <button type="button" onClick={() => lookupCnpjAndFill(form.cnpj)} disabled={cnpjLookingUp || !form.cnpj} className="px-3 py-2 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 disabled:from-slate-300 disabled:to-slate-400 text-white text-xs font-bold shadow-md flex items-center gap-1 flex-shrink-0" data-testid="company-cnpj-lookup">
                    {cnpjLookingUp ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}<span className="hidden sm:inline">{cnpjLookingUp ? 'Buscando' : 'Buscar'}</span>
                  </button>
                </div>
              </div>
              <div className="flex items-end"><span className="text-[10px] text-slate-500 leading-tight">Digite o CNPJ e clique em <strong className="text-violet-700">Buscar</strong> (ou perca o foco) para autopreencher Razao Social, IE quando disponivel, endereco completo, codigo IBGE do municipio e UF.</span></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Razao Social *</label><input value={form.razao_social} onChange={e => setForm({ ...form, razao_social: e.target.value })} className={inputClass} placeholder="MINHA EMPRESA LTDA" data-testid="company-razao-social" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Nome Fantasia</label><input value={form.nome_fantasia} onChange={e => setForm({ ...form, nome_fantasia: e.target.value })} className={inputClass} data-testid="company-nome-fantasia" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Inscricao Estadual (IE) *</label><input value={form.ie} onChange={e => setForm({ ...form, ie: e.target.value })} className={inputClass} placeholder="ISENTO ou numero" data-testid="company-ie" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">IE Subst. Trib.</label><input value={form.iest} onChange={e => setForm({ ...form, iest: e.target.value })} className={inputClass} /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Inscricao Municipal</label><input value={form.im} onChange={e => setForm({ ...form, im: e.target.value })} className={inputClass} /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">CNAE Fiscal</label><input value={form.cnae} onChange={e => setForm({ ...form, cnae: e.target.value })} className={inputClass} placeholder="0000-0/00" /></div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Regime Tributario (CRT) *</label>
                <select value={form.crt} onChange={e => setForm({ ...form, crt: e.target.value })} className={inputClass} data-testid="company-crt">
                  <option value="1">1 - Simples Nacional</option>
                  <option value="2">2 - Simples Nacional - excesso</option>
                  <option value="3">3 - Regime Normal</option>
                  <option value="4">4 - Simples Nacional MEI</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Logradouro *</label><input value={form.endereco_logradouro} onChange={e => setForm({ ...form, endereco_logradouro: e.target.value })} className={inputClass} placeholder="Rua das Flores" data-testid="company-end-logradouro" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Numero *</label><input value={form.endereco_numero} onChange={e => setForm({ ...form, endereco_numero: e.target.value })} className={inputClass} placeholder="123" data-testid="company-end-numero" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Complemento</label><input value={form.endereco_complemento} onChange={e => setForm({ ...form, endereco_complemento: e.target.value })} className={inputClass} /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Bairro *</label><input value={form.endereco_bairro} onChange={e => setForm({ ...form, endereco_bairro: e.target.value })} className={inputClass} data-testid="company-end-bairro" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">CEP *</label><input value={form.endereco_cep} onChange={e => setForm({ ...form, endereco_cep: e.target.value })} className={inputClass} placeholder="74000000" data-testid="company-end-cep" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Municipio *</label><input value={form.endereco_municipio} onChange={e => setForm({ ...form, endereco_municipio: e.target.value })} className={inputClass} placeholder="Goiania" data-testid="company-end-municipio" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Codigo IBGE Municipio *</label><input value={form.endereco_cod_municipio} onChange={e => setForm({ ...form, endereco_cod_municipio: e.target.value })} className={inputClass} placeholder="5208707" maxLength={7} data-testid="company-end-cod-municipio" /></div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">UF *</label>
                <select value={form.endereco_uf} onChange={e => setForm({ ...form, endereco_uf: e.target.value })} className={inputClass} data-testid="company-end-uf">
                  <option value="">--</option>
                  {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
          </div>
          )}
          <div className="flex gap-2"><button type="submit" disabled={submitting || uploading} className={btnPrimary}><Save size={14} /><span>{submitting ? 'Salvando...' : editing ? 'Atualizar' : 'Criar'}</span></button><button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2.5 text-sm text-gray-500">Cancelar</button></div>
        </form>
      )}
      <div className="space-y-2">
        {/* iter156u: campo de busca — filtra por nome, codigo, CNPJ, razao social ou nome fantasia */}
        {(companies || []).filter(c => c.active !== false).length > 4 && (
          <div className="relative mb-3">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar por nome, codigo, CNPJ ou razao social..."
              className="w-full pl-9 pr-9 py-2 rounded-xl bg-white border border-slate-200 focus:border-teal-400 focus:ring-2 focus:ring-teal-100 text-sm outline-none"
              data-testid="companies-search-input"
              autoComplete="off"
              inputMode="search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                data-testid="companies-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}
        {filteredCompanies.map(c => (
          <div key={c.id} className={`bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between ${c.disabled_by_superadmin ? 'opacity-50 grayscale bg-gray-50' : ''}`} data-testid={c.disabled_by_superadmin ? `company-row-disabled-${c.id}` : 'company-row'} title={c.disabled_by_superadmin ? 'Filial desativada pelo administrador do sistema' : ''}>
            <div className="flex items-center space-x-3 min-w-0">
              {c.logo_url ? (
                <img src={resolveLogo(c.logo_url)} alt={c.name} className="w-10 h-10 rounded-lg object-contain bg-white border border-gray-100" onError={e => { e.target.style.display = 'none'; }} data-testid="company-logo-img" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0"><Building2 size={18} className="text-teal-600" /></div>
              )}
              <div className="min-w-0"><p className="font-semibold text-sm text-gray-900 truncate flex items-center gap-2">{c.code} | {c.name} {c.is_matriz && <span className="text-[9px] font-extrabold bg-violet-600 text-white px-1.5 py-0.5 rounded-full" data-testid={`company-matriz-badge-${c.id}`}>MATRIZ</span>}{c.disabled_by_superadmin && <span className="text-[9px] font-extrabold bg-gray-500 text-white px-1.5 py-0.5 rounded-full" data-testid={`company-disabled-badge-${c.id}`}>DESATIVADA PELO ADMINISTRADOR</span>}</p><p className="text-xs text-gray-500 truncate">{c.cnpj || 'Sem CNPJ'} {c.phone ? `• ${c.phone}` : ''}</p></div>
            </div>
            {c.disabled_by_superadmin ? (
              <div className="text-[10px] text-gray-400 font-medium flex-shrink-0 text-right">Contate o suporte<br />para reativar</div>
            ) : (
            <div className="flex items-center space-x-1 flex-shrink-0">
              <button onClick={() => { setEditing(c); setForm({ ...blankForm, name: c.name, code: c.code, cnpj: c.cnpj || '', address: c.address || '', phone: c.phone || '', logo_url: c.logo_url || '', is_matriz: !!c.is_matriz, razao_social: c.razao_social || '', nome_fantasia: c.nome_fantasia || '', ie: c.ie || '', im: c.im || '', iest: c.iest || '', cnae: c.cnae || '', crt: c.crt || '1', endereco_logradouro: c.endereco_logradouro || '', endereco_numero: c.endereco_numero || '', endereco_complemento: c.endereco_complemento || '', endereco_bairro: c.endereco_bairro || '', endereco_municipio: c.endereco_municipio || '', endereco_cod_municipio: c.endereco_cod_municipio || '', endereco_uf: c.endereco_uf || '', endereco_cep: c.endereco_cep || '', default_client_id: c.default_client_id || '', allow_negative_stock_sale: (c.allow_negative_stock_sale === undefined ? null : c.allow_negative_stock_sale) }); setFormTab('basico'); setShowForm(true); }} className="p-2 text-gray-400 hover:text-teal-600" data-testid={`edit-company-btn-${c.id}`}><Edit size={14} /></button>
              <button onClick={() => handleDelete(c.id)} className="p-2 text-gray-400 hover:text-red-500" data-testid="delete-company-btn"><Trash2 size={14} /></button>
            </div>
            )}
          </div>
        ))}
        {filteredCompanies.length === 0 && searchQuery && (
          <div className="text-center py-8 text-sm text-slate-400 font-medium">
            Nenhuma empresa encontrada para &quot;{searchQuery}&quot;.
          </div>
        )}
      </div>
    </div>
  );
};

/* ========== SALES PAGE (com PDV integrado) ========== */
/* iter77dc: ========== PrintActionsButton — dropdown com opcoes de impressao ==========
   Icone da impressora nas listagens de venda/pedido/orcamento. Abre menu FLUTUANTE
   (position: fixed, coords via getBoundingClientRect) para nao ser cortado pelo
   overflow da tabela quando a lista tem poucos registros. Opcoes:
     - Cupom Nao-Fiscal (PDF) — sempre disponivel
     - Imprimir Venda (Duplo|Via Unica) — quando print_sale_model esta setado
     - Imprimir Etiqueta — quando ha template de etiqueta 'produto' cadastrado */
/* iter156ez: print_sale_model aceita 1+ modelos separados por virgula ("via_unica,duplo_simples") */
const PRINT_SALE_MODELS = [
  { key: 'via_unica', label: 'Via Unica', desc: 'Imprimir Venda (via unica A4 — cabecalho + tabela detalhada + pagamento + total)' },
  { key: 'duplo_simples', label: 'Duplo', desc: 'Imprimir Venda Duplo (duas vias A4 lado a lado, com assinatura)' },
];
const parsePrintModels = (v) => String(v || '').split(',').map(x => x.trim()).filter(x => PRINT_SALE_MODELS.some(m => m.key === x));
const printModelLabel = (k) => (PRINT_SALE_MODELS.find(m => m.key === k) || {}).label || k;

const PrintActionsButton = ({ saleId, onPrintCupom, printSaleModel, onPrintLabel, disabled = false, testIdSuffix = '',
  allowEmitNfe = false, allowEmitNfce = false, saleInvoices = null,
  onEmitNfe = null, onEmitNfce = null, onPrintNfe = null, onPrintNfce = null }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);
  const saleModels = parsePrintModels(printSaleModel);  // iter156ez: 1+ modelos
  // iter77cz: estado da NF-e/NFC-e ja vinculada a essa venda (se houver)
  const nfeInv = saleInvoices?.nfe || null;
  const nfceInv = saleInvoices?.nfce || null;
  const toggleMenu = (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const MENU_W = 240;
      const left = Math.max(8, Math.min(window.innerWidth - MENU_W - 8, r.right - MENU_W));
      const top = r.bottom + 4;
      setCoords({ top, left });
    }
    setOpen(true);
  };
  return (
    <>
      <button
        ref={btnRef}
        onClick={toggleMenu}
        disabled={disabled}
        className="p-1 text-slate-500 hover:text-violet-700 hover:bg-violet-50 rounded disabled:opacity-40"
        title="Opcoes de impressao"
        data-testid={`print-menu-btn${testIdSuffix ? '-' + testIdSuffix : ''}`}
      >
        <Printer size={12} />
      </button>
      {open && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: 240, zIndex: 9999 }}
          className="bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid={`print-menu-list${testIdSuffix ? '-' + testIdSuffix : ''}`}
        >
          <button
            onClick={() => { setOpen(false); onPrintCupom(saleId); }}
            className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-violet-50 flex items-center gap-2"
            data-testid={`print-menu-cupom${testIdSuffix ? '-' + testIdSuffix : ''}`}
          >
            <Printer size={12} className="text-violet-600" />
            <span>Cupom Nao-Fiscal (PDF)</span>
          </button>
          {saleModels.map((mk, mi) => (
            <button
              key={mk}
              onClick={() => { setOpen(false); window.open(`${ERP}/sales/${saleId}/print/${mk.replace(/_/g, '-')}`, '_blank'); }}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-blue-50 flex items-center gap-2 border-t border-slate-100"
              data-testid={`print-menu-sale${mi === 0 ? '' : '-' + mk}${testIdSuffix ? '-' + testIdSuffix : ''}`}
            >
              <Printer size={12} className="text-blue-600" />
              <span>Imprimir Venda ({printModelLabel(mk)})</span>
            </button>
          ))}
          {/* iter77cz: NF-e (gerar rascunho ou imprimir a existente) */}
          {allowEmitNfe && onEmitNfe && !nfeInv && (
            <button
              onClick={() => { setOpen(false); onEmitNfe(saleId); }}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-emerald-50 flex items-center gap-2 border-t border-slate-100"
              data-testid={`print-menu-emit-nfe${testIdSuffix ? '-' + testIdSuffix : ''}`}
            >
              <Printer size={12} className="text-emerald-600" />
              <span>Gerar NF-e</span>
            </button>
          )}
          {allowEmitNfe && onPrintNfe && nfeInv && (
            <button
              onClick={() => { setOpen(false); onPrintNfe(nfeInv, 'nfe'); }}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-emerald-50 flex items-center gap-2 border-t border-slate-100"
              data-testid={`print-menu-print-nfe${testIdSuffix ? '-' + testIdSuffix : ''}`}
            >
              <Printer size={12} className="text-emerald-600" />
              <span>Imprimir NF-e {nfeInv.numero ? `(#${nfeInv.numero})` : ''}</span>
            </button>
          )}
          {/* iter77cz: NFC-e (gerar rascunho ou imprimir a existente) */}
          {allowEmitNfce && onEmitNfce && !nfceInv && (
            <button
              onClick={() => { setOpen(false); onEmitNfce(saleId); }}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-sky-50 flex items-center gap-2 border-t border-slate-100"
              data-testid={`print-menu-emit-nfce${testIdSuffix ? '-' + testIdSuffix : ''}`}
            >
              <Printer size={12} className="text-sky-600" />
              <span>Gerar NFC-e</span>
            </button>
          )}
          {allowEmitNfce && onPrintNfce && nfceInv && (
            <button
              onClick={() => { setOpen(false); onPrintNfce(nfceInv, 'nfce'); }}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-sky-50 flex items-center gap-2 border-t border-slate-100"
              data-testid={`print-menu-print-nfce${testIdSuffix ? '-' + testIdSuffix : ''}`}
            >
              <Printer size={12} className="text-sky-600" />
              <span>Imprimir NFC-e {nfceInv.numero ? `(#${nfceInv.numero})` : ''}</span>
            </button>
          )}
          {onPrintLabel && (
            <button
              onClick={() => { setOpen(false); onPrintLabel(saleId); }}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-orange-50 flex items-center gap-2 border-t border-slate-100"
              data-testid={`print-menu-label${testIdSuffix ? '-' + testIdSuffix : ''}`}
            >
              <Tag size={12} className="text-orange-600" />
              <span>Imprimir Etiqueta</span>
            </button>
          )}
        </div>
      )}
    </>
  );
};

/* iter156fs: tag discreta antes do nome — FUN (funcionario) / CLI (cliente) / FOR (fornecedor) / PLC (plano de contas) */
const partyTagOf = (r) => {
  if (!r) return null;
  const t = (r.party_type || '').toLowerCase();
  if (r.employee_id || r.customer_is_employee || t === 'funcionario' || t === 'employee') return { k: 'FUN', title: 'Funcionario', c: 'bg-amber-50 text-amber-700 border-amber-200' };
  if (t === 'fornecedor' || t === 'supplier' || r.supplier_id) return { k: 'FOR', title: 'Fornecedor', c: 'bg-sky-50 text-sky-700 border-sky-200' };
  if (t === 'plano_conta') return { k: 'PLC', title: 'Plano de Contas', c: 'bg-slate-100 text-slate-600 border-slate-200' };
  if (t === 'cliente' || t === 'client' || r.client_id || r.customer_name) return { k: 'CLI', title: 'Cliente', c: 'bg-violet-50 text-violet-700 border-violet-200' };
  return null;
};
const PartyTag = ({ r }) => {
  const t = partyTagOf(r);
  if (!t) return null;
  return <span title={t.title} className={`inline-flex items-center px-1 rounded text-[8px] leading-[14px] font-black tracking-wider border mr-1.5 align-middle ${t.c}`} data-testid={`party-tag-${t.k.toLowerCase()}`}>{t.k}</span>;
};

const SalesPage = ({ companyId, type, title, pdvPerms = null }) => {
  // Default tab: history (user request — open Vendas showing history)
  const [tab, setTab] = useState('list');
  const [sales, setSales] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [periodChip, setPeriodChip] = useState('today'); // today | week | month | all | custom
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false); // collapsed by default per user request
  // Apply chip → set dateFrom/dateTo (LOCAL timezone — evita pular dia à noite)
  useEffect(() => {
    if (periodChip === 'custom') return; // user controls manually
    const now = new Date();
    const fmt = localDateISO;
    let from = '', to = '';
    if (periodChip === 'today') { from = fmt(now); to = fmt(now); }
    else if (periodChip === 'week') { const d = new Date(now); d.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1)); from = fmt(d); to = fmt(now); }
    else if (periodChip === 'month') { from = fmt(new Date(now.getFullYear(), now.getMonth(), 1)); to = fmt(now); }
    else if (periodChip === 'all') { from = ''; to = ''; }
    setDateFrom(from); setDateTo(to);
  }, [periodChip]);
  const [reopenPedido, setReopenPedido] = useState(null); // open pedido in PDV (confirm into venda)
  const [editPedido, setEditPedido] = useState(null);     // open pedido in PDV in edit mode (PUT)
  const [returnSale, setReturnSale] = useState(null);     // sale being returned in modal
  const [viewSale, setViewSale] = useState(null);         // iter77bx: sale being viewed (details modal)
  const [filterBy, setFilterBy] = useState('');           // iter77bx: 'product' | 'client' | 'number' | 'payment_method' | ''
  const [filterValue, setFilterValue] = useState('');     // iter77bx: value to search when filterBy set
  const [paymentMethods, setPaymentMethods] = useState([]); // iter156ao: para filtro por forma de pagamento
  // iter156bl: lista de vendedores (funcionarios ativos com type=vendedor) para filtro em Todas as Vendas
  const [sellers, setSellers] = useState([]);
  const [printSaleModel, setPrintSaleModel] = useState(''); // iter77dc: config global do modelo de impressao
  const [printLabelSale, setPrintLabelSale] = useState(null); // iter77dc.3: sale para imprimir etiquetas
  // iter77cz: emissao NF-e/NFC-e a partir de venda ja concluida
  const [allowEmitNfe, setAllowEmitNfe] = useState(false);
  const [allowEmitNfce, setAllowEmitNfce] = useState(false);
  const [defaultNatopId, setDefaultNatopId] = useState('');
  const [invoicesBySale, setInvoicesBySale] = useState({}); // sale_id -> { nfe: {id,status,numero}, nfce: {...} }
  const [emitBusy, setEmitBusy] = useState(false);
  const fetchSales = useCallback(async () => {
    if (!companyId) { setLoading(false); return; }
    try {
      // iter156ah: filtros server-side — evita perder vendas alem das primeiras 100
      // (bug reportado: filtro por SKU nao encontrava venda que existia no Direct report).
      const qs = new URLSearchParams({ company_id: companyId, type });
      if (dateFrom) qs.set('date_from', dateFrom);
      if (dateTo) qs.set('date_to', dateTo);
      if (filterBy === 'status' && filterValue.trim()) {
        qs.set('status', filterValue.trim());               // iter156ex: filtro por status (backend)
      } else if (filterBy && filterValue.trim()) {
        qs.set('filter_by', filterBy);
        qs.set('filter_value', filterValue.trim());
      }
      // iter156cc: SEMPRE expande o limite pro backend nao truncar em 100 vendas
      // (o card "220 vendas" no relatorio nao batia com 99 na Lista de Vendas).
      qs.set('limit', '5000');
      // iter156ex: pedidos pendentes — sem limite default (100) e sem recorte de data:
      // um pedido pendente antigo continua acionavel (antes "18" apareciam de 22+).
      const qsPed = new URLSearchParams({ company_id: companyId, type: 'pedido', status: 'pending', limit: '5000' });
      const [s, p] = await Promise.all([
        axios.get(`${ERP}/sales?${qs.toString()}`, { withCredentials: true }),
        type === 'venda' ? axios.get(`${ERP}/sales?${qsPed.toString()}`, { withCredentials: true }) : Promise.resolve({ data: { data: [] } })
      ]);
      setSales(s.data.data || []);
      setPedidos(p.data.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [companyId, type, dateFrom, dateTo, filterBy, filterValue]);
  useEffect(() => { fetchSales(); }, [fetchSales]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/company-settings${companyId ? `?company_id=${companyId}` : ''}`, { withCredentials: true });
        if (data && data.print_sale_model) setPrintSaleModel(String(data.print_sale_model));
        // iter77cz: le flags de emissao no PDV/desktop
        if (data) {
          setAllowEmitNfe(!!data.pdv_emit_nfe);
          setAllowEmitNfce(!!data.pdv_emit_nfce);
          setDefaultNatopId(data.pdv_default_natop_id || '');
        }
      } catch { /* silent */ }
    })();
  }, [companyId]);
  // iter156ao: carrega formas de pagamento para o filtro
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/payment-methods${companyId ? `?company_id=${companyId}` : ''}`, { withCredentials: true });
        setPaymentMethods(data?.data || data || []);
      } catch { setPaymentMethods([]); }
    })();
  }, [companyId]);
  // iter156bl: carrega vendedores (para filtro por vendedor em "Todas as Vendas")
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/employees${companyId ? `?company_id=${companyId}` : ''}`, { withCredentials: true });
        const rows = (data?.data || []).filter(e => e.type === 'vendedor' || !e.type);
        setSellers(rows);
      } catch { setSellers([]); }
    })();
  }, [companyId]);
  // iter77cz: carrega mapa de invoices (nfe/nfce) por sale_id — usado pra decidir "Gerar" vs "Imprimir"
  const fetchInvoicesMap = useCallback(async () => {
    if (!companyId) { setInvoicesBySale({}); return; }
    try {
      const { data } = await axios.get(`${ERP}/invoices?company_id=${companyId}&limit=1000`, { withCredentials: true });
      const rows = data?.data || [];
      const map = {};
      for (const r of rows) {
        const sid = r.sale_id;
        if (!sid) continue;
        const t = r.tipo;
        if (t !== 'nfe' && t !== 'nfce') continue;
        if (!map[sid]) map[sid] = {};
        // se ha varios, prefere o "issued" > "pending" > "draft" > "rejected" > "cancelled"
        const rank = { issued: 5, pending: 4, draft: 3, rejected: 2, cancelled: 1 };
        const prev = map[sid][t];
        if (!prev || (rank[r.status] || 0) > (rank[prev.status] || 0)) {
          map[sid][t] = { id: r.id, status: r.status, numero: r.numero, chave: r.chave_acesso };
        }
      }
      setInvoicesBySale(map);
    } catch { /* silent */ }
  }, [companyId]);
  useEffect(() => { fetchInvoicesMap(); }, [fetchInvoicesMap]);
  const convertQuote = async (id) => {
    try { await axios.post(`${ERP}/sales/${id}/convert`, {}, { withCredentials: true }); toast.success('Orcamento convertido!'); fetchSales(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };
  const printReceipt = async (saleId) => {
    try {
      const { data: blob } = await axios.get(`${ERP}/sales/${saleId}/receipt-pdf`, {
        responseType: 'blob',
        withCredentials: true,
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const win = window.open(objectUrl, '_blank');
      if (!win) {
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `cupom-${saleId}.pdf`;
        a.click();
      }
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) toast.error('Cupom nao disponivel para esta venda');
      else if (status === 401 || status === 403) toast.error('Sessao expirada. Faca login novamente.');
      else toast.error('Erro ao gerar cupom PDF');
    }
  };

  // iter77cz: modal pos-geracao ou pos-impressao (mostra opcoes DANFE / detalhes / ir para lista)
  const [invoicePrintModal, setInvoicePrintModal] = useState(null); // {tipo, invoice, missing}
  // iter77cz: gera NF-e/NFC-e a partir de uma venda existente
  const handleEmitInvoice = async (saleId, tipo) => {
    if (emitBusy) return;
    setEmitBusy(true);
    try {
      const { data } = await axios.post(
        `${ERP}/invoices/emit-from-sale/${saleId}`,
        { tipo, natureza_op_id: defaultNatopId || undefined },
        { withCredentials: true, timeout: 20000 },
      );
      const inv = data.invoice || {};
      const missing = data.missing_fields || [];
      // atualiza mapa local
      setInvoicesBySale(prev => ({ ...prev, [saleId]: { ...(prev[saleId] || {}), [tipo]: { id: inv.id, status: inv.status, numero: inv.numero } } }));
      const label = tipo === 'nfe' ? 'NF-e' : 'NFC-e';
      if (missing.length > 0) {
        toast.error(
          `${label} ${inv.numero ? '#' + inv.numero : ''} gerada como rascunho, mas faltam dados:\n• ${missing.join('\n• ')}`,
          { duration: 8000 }
        );
        // Navega direto pra edicao
        if (window.__goToSection) {
          window.__goToSection('invoices', { query: { highlight: inv.id, tipo } });
        }
      } else {
        toast.success(`${label} ${inv.numero ? '#' + inv.numero : ''} gerada como rascunho`);
        setInvoicePrintModal({ tipo, invoice: inv, missing: [] });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erro ao gerar nota fiscal');
    } finally {
      setEmitBusy(false);
    }
  };
  // iter77cz: acao "Imprimir" quando ja existe rascunho/emitida
  const handlePrintInvoice = async (inv, tipo) => {
    if (!inv || !inv.id) return;
    if (inv.status === 'issued') {
      // Ja tem DANFE: abre diretamente
      try {
        const { data: blob } = await axios.get(`${ERP}/invoices/${inv.id}/danfe`, {
          responseType: 'blob', withCredentials: true,
        });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } catch {
        toast.error('Erro ao abrir DANFE');
      }
    } else {
      // Rascunho: abre modal com opcoes
      setInvoicePrintModal({ tipo, invoice: inv, missing: [] });
    }
  };

  const [cascadeDelete, setCascadeDelete] = useState(null); // iter77cc: {sale} — dialog cascade delete

  const deleteSale = async (sale) => {
    const isVenda = (sale.type || 'venda') === 'venda';
    const label = isVenda ? 'venda' : (sale.type === 'pedido' ? 'pedido' : 'orcamento');
    // iter77cc: se a venda tem devoluções ativas, abre dialog de cascata com 2 opções.
    if (isVenda && (sale.returned_total || 0) > 0) {
      setCascadeDelete(sale);
      return;
    }
    const willRevert = isVenda && sale.status === 'completed';
    const msg = willRevert
      ? `Excluir ${label} ${sale.number}?\n\nO estoque sera DEVOLVIDO automaticamente e o lancamento financeiro sera cancelado.`
      : `Excluir ${label} ${sale.number}?`;
    if (!(await confirmDialog({ title: msg, confirmLabel: 'Confirmar', danger: false }))) return;
    try {
      const { data } = await axios.delete(`${ERP}/sales/${sale.id}`, { withCredentials: true });
      toast.success(data.message || `${label} excluida`);
      fetchSales();
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) toast.error('Voce nao tem permissao para excluir');
      else toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  // iter77cc: cascade delete — executa DELETE em cadeia (devoluções → venda)
  const runCascadeDelete = async (sale, mode /* 'return-only' | 'return-and-sale' */) => {
    try {
      // 1) Lista as devoluções ativas
      const { data: retData } = await axios.get(`${ERP}/sales/${sale.id}/returns`, { withCredentials: true });
      const activeReturns = (retData.data || []).filter(r => r.status !== 'cancelled');
      // 2) Deleta cada devolução (backend valida se crédito foi consumido)
      for (const r of activeReturns) {
        try {
          await axios.delete(`${ERP}/sales/${sale.id}/returns/${r.id}`, { withCredentials: true });
        } catch (err) {
          const detail = err.response?.data?.detail || 'Erro ao excluir devolução';
          toast.error(detail);
          setCascadeDelete(null);
          fetchSales();
          return;
        }
      }
      // 3) Se modo cascata completa, deleta a venda também
      if (mode === 'return-and-sale') {
        const { data } = await axios.delete(`${ERP}/sales/${sale.id}`, { withCredentials: true });
        toast.success(data.message || 'Venda e devolução excluídas — estoque e financeiro ajustados');
      } else {
        toast.success(`${activeReturns.length} devolução(ões) excluída(s) — venda preservada`);
      }
      setCascadeDelete(null);
      fetchSales();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro no processo em cascata');
    }
  };

  const shareWhatsApp = (s) => {
    const lines = [];
    lines.push(`*Cupom Nao-Fiscal ${s.number}*`);
    if (s.customer_name) lines.push(`Cliente: ${s.customer_name}`);
    lines.push(`Data: ${new Date(s.created_at).toLocaleDateString('pt-BR')} ${new Date(s.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`);
    lines.push('');
    lines.push('*Itens:*');
    (s.items || []).forEach(it => {
      const qty = it.quantity || 1;
      const unit = (it.unit_price || 0).toFixed(2).replace('.', ',');
      const tot = (it.total || qty * (it.unit_price || 0)).toFixed(2).replace('.', ',');
      lines.push(`- ${qty}x ${it.name || it.product_name || 'Item'} — R$ ${unit} = R$ ${tot}`);
    });
    if (s.discount) lines.push(`Desconto: R$ ${(s.discount || 0).toFixed(2).replace('.', ',')}`);
    lines.push('');
    lines.push(`*Total: R$ ${(s.total || 0).toFixed(2).replace('.', ',')}*`);
    if (s.payment_method) lines.push(`Pagamento: ${s.payment_method}`);
    lines.push('');
    lines.push('_Documento sem valor fiscal._');
    const text = encodeURIComponent(lines.join('\n'));
    // Try to extract digits from customer_doc if it looks like a phone (10-13 digits)
    const digits = (s.customer_phone || s.customer_doc || '').replace(/\D/g, '');
    const phone = digits.length >= 10 && digits.length <= 13 ? digits : '';
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
  };
  const openPedidoInPDV = (pedido) => {
    setReopenPedido(pedido);
    setEditPedido(null);
    setTab('pdv');
  };
  const openPedidoForEdit = (pedido) => {
    setEditPedido(pedido);
    setReopenPedido(null);
    setTab(type === 'venda' ? 'pdv' : 'pedido');
  };
  if (!companyId) return <NoCompany />;
  const statusBadge = { completed: 'bg-green-50 text-green-700', pending: 'bg-amber-50 text-amber-700', cancelled: 'bg-red-50 text-red-700', converted: 'bg-blue-50 text-blue-700', confirmed: 'bg-blue-50 text-blue-700' };
  const statusLabel = { completed: 'Concluida', pending: 'Pendente', cancelled: 'Cancelada', converted: 'Convertido', confirmed: 'Confirmado' };
  const vendors = Array.from(new Set(sales.map(s => s.vendedor_name || s.created_by_name).filter(Boolean)));
  const filteredSales = sales.filter(s => {
    const vname = s.vendedor_name || s.created_by_name;
    if (vendorFilter && vname !== vendorFilter) return false;
    if (dateFrom) { const d = new Date(s.created_at); if (d < new Date(dateFrom + 'T00:00:00')) return false; }
    if (dateTo) { const d = new Date(s.created_at); if (d > new Date(dateTo + 'T23:59:59')) return false; }
    // iter77bx: filtro dinâmico "Filtrar por" (produto/cliente/número/pagamento)
    if (filterBy && filterValue) {
      const q = filterValue.toLowerCase().trim();
      if (filterBy === 'product') {
        const hit = (s.items || []).some(it => `${it.name || ''} ${it.sku || ''}`.toLowerCase().includes(q));
        if (!hit) return false;
      } else if (filterBy === 'client') {
        if (!`${s.customer_name || ''} ${s.customer_doc || ''}`.toLowerCase().includes(q)) return false;
      } else if (filterBy === 'number') {
        if (!String(s.number || '').toLowerCase().includes(q)) return false;
      } else if (filterBy === 'payment_method') {
        // iter156ao: filterValue = payment method id. Match:
        //   1) top-level payment_method_id (venda simples)
        //   2) qualquer entry em payments[].method_id (splits)
        //   3) fallback: type comparison quando id nao esta salvo (compat com vendas antigas)
        const pmDoc = paymentMethods.find(pm => pm.id === q || pm.id === filterValue);
        const pmType = ((pmDoc?.type) || '').toLowerCase();
        const pmName = ((pmDoc?.name) || '').toLowerCase();
        const rawPays = Array.isArray(s.payments) ? s.payments : [];
        const hit =
          (s.payment_method_id === filterValue) ||
          rawPays.some(p => (p?.method_id === filterValue)) ||
          (pmType && (s.payment_method || '').toLowerCase() === pmType) ||
          (pmType && rawPays.some(p => (p?.method || '').toLowerCase() === pmType)) ||
          (pmName && rawPays.some(p => (p?.name || '').toLowerCase() === pmName));
        if (!hit) return false;
      } else if (filterBy === 'status') {
        if ((s.status || '') !== filterValue) return false;
      } else if (filterBy === 'seller') {
        // iter156bl: filterValue = employee (vendedor) id. Match por id direto e fallback por nome.
        const sellerDoc = sellers.find(x => x.id === filterValue);
        const sName = ((sellerDoc?.name) || '').toLowerCase();
        const hit =
          (s.vendedor_id === filterValue) ||
          (sName && ((s.vendedor_name || '').toLowerCase() === sName));
        if (!hit) return false;
      }
    }
    return true;
  });
  const totalVendas = filteredSales.filter(s => s.status !== 'cancelled').reduce((s, v) => s + (v.total || 0), 0);
  const clearFilters = () => { setDateFrom(''); setDateTo(''); setVendorFilter(''); setFilterBy(''); setFilterValue(''); };
  const pendingPedidos = pedidos.filter(p => p.status === 'pending');

  return (
    <div data-testid={`${type}-page`}>
      {/* Tabs: Historico | PDV (venda) | Historico | Novo Pedido (pedido) */}
      {type === 'venda' && (
        <div className="grid grid-cols-2 gap-3 mb-5">
          <button onClick={() => { setTab('list'); setReopenPedido(null); fetchSales(); }} data-testid="sales-tab-list" className={`relative overflow-hidden p-4 rounded-2xl border-2 transition-all text-left ${tab === 'list' ? 'border-violet-500 bg-gradient-to-br from-violet-50 to-cyan-50 shadow-lg shadow-violet-500/20' : 'border-slate-200 bg-white hover:border-violet-300'}`}>
            {tab === 'list' && <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-violet-600" />}
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tab === 'list' ? 'bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-md shadow-violet-500/40' : 'bg-slate-100 text-slate-500'}`}>
                <BarChart3 size={18} />
              </div>
              <div className="min-w-0">
                <p className={`text-[10px] font-black uppercase tracking-widest ${tab === 'list' ? 'text-violet-700' : 'text-slate-400'}`}>Historico</p>
                <p className={`text-sm font-black truncate ${tab === 'list' ? 'text-slate-900' : 'text-slate-700'}`}>Todas as Vendas</p>
              </div>
            </div>
          </button>
          <button onClick={() => { setTab('pdv'); setReopenPedido(null); }} data-testid="sales-tab-pdv" className={`relative overflow-hidden p-4 rounded-2xl border-2 transition-all text-left ${tab === 'pdv' && !reopenPedido ? 'border-violet-500 bg-gradient-to-br from-violet-50 to-cyan-50 shadow-lg shadow-violet-500/20' : 'border-slate-200 bg-white hover:border-violet-300'}`}>
            {tab === 'pdv' && !reopenPedido && <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-violet-600" />}
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tab === 'pdv' && !reopenPedido ? 'bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-md shadow-violet-500/40' : 'bg-slate-100 text-slate-500'}`}>
                <ShoppingCart size={18} />
              </div>
              <div className="min-w-0">
                <p className={`text-[10px] font-black uppercase tracking-widest ${tab === 'pdv' && !reopenPedido ? 'text-violet-700' : 'text-slate-400'}`}>Nova Venda</p>
                <p className={`text-sm font-black truncate ${tab === 'pdv' && !reopenPedido ? 'text-slate-900' : 'text-slate-700'}`}>Abrir PDV</p>
              </div>
            </div>
          </button>
        </div>
      )}
      {type === 'pedido' && (
        <div className="grid grid-cols-2 gap-3 mb-5">
          <button onClick={() => { setTab('list'); fetchSales(); }} data-testid="pedido-tab-list" className={`relative overflow-hidden p-4 rounded-2xl border-2 transition-all text-left ${tab === 'list' ? 'border-violet-500 bg-gradient-to-br from-violet-50 to-cyan-50 shadow-lg shadow-violet-500/20' : 'border-slate-200 bg-white hover:border-violet-300'}`}>
            {tab === 'list' && <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-violet-600" />}
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tab === 'list' ? 'bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-md shadow-violet-500/40' : 'bg-slate-100 text-slate-500'}`}>
                <FileText size={18} />
              </div>
              <div className="min-w-0">
                <p className={`text-[10px] font-black uppercase tracking-widest ${tab === 'list' ? 'text-violet-700' : 'text-slate-400'}`}>Historico</p>
                <p className={`text-sm font-black truncate ${tab === 'list' ? 'text-slate-900' : 'text-slate-700'}`}>Pedidos de Venda</p>
              </div>
            </div>
          </button>
          <button onClick={() => setTab('pedido')} data-testid="pedido-tab-new" className={`relative overflow-hidden p-4 rounded-2xl border-2 transition-all text-left ${tab === 'pedido' ? 'border-violet-500 bg-gradient-to-br from-violet-50 to-cyan-50 shadow-lg shadow-violet-500/20' : 'border-slate-200 bg-white hover:border-violet-300'}`}>
            {tab === 'pedido' && <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-violet-600" />}
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tab === 'pedido' ? 'bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-md shadow-violet-500/40' : 'bg-slate-100 text-slate-500'}`}>
                <Plus size={18} />
              </div>
              <div className="min-w-0">
                <p className={`text-[10px] font-black uppercase tracking-widest ${tab === 'pedido' ? 'text-violet-700' : 'text-slate-400'}`}>Criar</p>
                <p className={`text-sm font-black truncate ${tab === 'pedido' ? 'text-slate-900' : 'text-slate-700'}`}>Novo Pedido</p>
              </div>
            </div>
          </button>
        </div>
      )}
      {tab === 'pdv' && type === 'venda' && (
        <PDVPage
          companyId={companyId}
          pdvPerms={pdvPerms}
          saleType="venda"
          initialItems={(editPedido || reopenPedido)?.items || null}
          initialPedidoId={reopenPedido?.id || null}
          initialSaleDate={reopenPedido?.created_at ? String(reopenPedido.created_at).slice(0, 10) : null}
          editPedidoId={editPedido?.id || null}
          editPedidoType={editPedido?.type || null}
          initialClient={(editPedido || reopenPedido) ? { id: (editPedido || reopenPedido).client_id, name: (editPedido || reopenPedido).customer_name, document: (editPedido || reopenPedido).customer_doc } : null}
          initialVendedorId={(editPedido || reopenPedido)?.vendedor_id || null}
          initialPaymentMethod={(editPedido || reopenPedido)?.payment_method || ''}
          initialPayments={(editPedido || reopenPedido)?.payments || null}
          initialDiscount={(editPedido || reopenPedido)?.discount || 0}
          initialSurcharge={(editPedido || reopenPedido)?.surcharge || 0}
          initialFreight={(editPedido || reopenPedido)?.freight || 0}
          initialNotes={(editPedido || reopenPedido)?.notes || ''}
          onSaleComplete={(_data, actionParam) => {
            // iter77bo: nova venda (saleType=venda) — so navega para a lista quando o usuario
            // explicitamente escolheu "Finalizar e Sair" (actionParam='exit'). Para 'stay' (main
            // button), 'comprovante', 'nfce' e 'nfe' fica no PDV. Pedidos sempre navegam (proximo handler).
            if (actionParam === 'exit') {
              setReopenPedido(null); setEditPedido(null); setTab('list'); fetchSales();
            } else {
              // Stay on PDV — apenas refresca dados em background.
              setReopenPedido(null); setEditPedido(null); fetchSales();
            }
          }}
          onClose={() => { setReopenPedido(null); setEditPedido(null); setTab('list'); fetchSales(); }}
        />
      )}
      {tab === 'pedido' && type === 'pedido' && (
        <PDVPage
          companyId={companyId}
          pdvPerms={pdvPerms}
          saleType="pedido"
          initialItems={editPedido?.items || null}
          editPedidoId={editPedido?.id || null}
          editPedidoType={editPedido?.type || null}
          initialClient={editPedido ? { id: editPedido.client_id, name: editPedido.customer_name, document: editPedido.customer_doc } : null}
          initialVendedorId={editPedido?.vendedor_id || null}
          initialPaymentMethod={editPedido?.payment_method || ''}
          initialPayments={editPedido?.payments || null}
          initialDiscount={editPedido?.discount || 0}
          initialSurcharge={editPedido?.surcharge || 0}
          initialFreight={editPedido?.freight || 0}
          initialNotes={editPedido?.notes || ''}
          onSaleComplete={() => { setEditPedido(null); setTab('list'); fetchSales(); }}
          onClose={() => { setEditPedido(null); setTab('list'); fetchSales(); }}
        />
      )}

      {/* List/History Tab */}
      {(tab === 'list' || (type !== 'venda' && type !== 'pedido')) && (
        <>
          {(type === 'venda' || type === 'pedido') && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-white border-2 border-slate-200 rounded-2xl p-3 text-center relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-violet-600" />
                  <p className="text-[10px] text-slate-500 font-black uppercase tracking-wider mt-1">Total</p>
                  <p className="text-base font-black brand-text-gradient">R$ {totalVendas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="bg-white border-2 border-slate-200 rounded-2xl p-3 text-center relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
                  <p className="text-[10px] text-slate-500 font-black uppercase tracking-wider mt-1">Qtd Vendas</p>
                  <p className="text-base font-black text-slate-900">{filteredSales.filter(s => s.status !== 'cancelled').length}</p>
                </div>
                <div className="bg-white border-2 border-slate-200 rounded-2xl p-3 text-center relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
                  <p className="text-[10px] text-slate-500 font-black uppercase tracking-wider mt-1">Qtde Produtos</p>
                  <p className="text-base font-black text-slate-900" data-testid="sales-card-total-qty">{filteredSales.filter(s => s.status !== 'cancelled').reduce((s, v) => s + (v.items || []).reduce((a, it) => a + (parseFloat(it.quantity) || 0), 0), 0).toLocaleString('pt-BR')}</p>
                </div>
                <div className="bg-white border-2 border-slate-200 rounded-2xl p-3 text-center relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 to-pink-500" />
                  <p className="text-[10px] text-slate-500 font-black uppercase tracking-wider mt-1">Troca/Devolucao</p>
                  <p className="text-base font-black text-amber-700" data-testid="sales-card-returned">R$ {filteredSales.reduce((s, v) => s + (v.returned_total || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
              {/* Slim chip filter + collapsible advanced */}
              <div className="mb-4" data-testid="history-filters">
                <div className="flex flex-wrap items-center gap-1.5">
                  {[
                    { k: 'today', l: 'Hoje' },
                    { k: 'week', l: 'Esta Semana' },
                    { k: 'month', l: 'Este Mes' },
                    { k: 'all', l: 'Tudo' },
                    { k: 'custom', l: 'Personalizado' },
                  ].map(p => (
                    <button key={p.k} onClick={() => { setPeriodChip(p.k); if (p.k === 'custom') setFiltersOpen(true); }} data-testid={`sales-period-${p.k}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${periodChip === p.k ? 'bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md shadow-violet-500/30' : 'bg-white border border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700'}`}>{p.l}</button>
                  ))}
                  <button onClick={() => setFiltersOpen(v => !v)} className="px-3 py-1.5 rounded-full text-xs font-bold bg-white border border-slate-200 text-slate-500 hover:border-violet-300 hover:text-violet-700 flex items-center gap-1" data-testid="toggle-advanced-filters">
                    <Filter size={11} /><span className="hidden sm:inline">Avancado</span>
                    <ChevronDown size={10} className={`transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {(dateFrom || dateTo || vendorFilter || filterBy || filterValue) && <button onClick={clearFilters} data-testid="clear-filters" className="text-[11px] text-violet-600 font-bold hover:text-violet-800">Limpar</button>}
                </div>
                {filtersOpen && (
                  <div className="mt-2 bg-white border border-slate-200 rounded-xl p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div><label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">De</label><input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPeriodChip('custom'); }} data-testid="filter-date-from" className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm" /></div>
                    <div><label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Ate</label><input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPeriodChip('custom'); }} data-testid="filter-date-to" className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm" /></div>
                    {vendors.length > 0 && <div><label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Vendedor</label><select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} data-testid="filter-vendor" className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm"><option value="">Todos</option>{vendors.map(v => <option key={v} value={v}>{v}</option>)}</select></div>}
                    {/* iter77bx: Filtrar por (produto/cliente/número/pagamento) */}
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Filtrar por</label>
                      <select value={filterBy} onChange={e => { setFilterBy(e.target.value); if (!e.target.value) setFilterValue(''); else setFilterValue(''); }} data-testid="filter-by-field" className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm">
                        <option value="">— Selecione —</option>
                        <option value="product">Produto (nome/SKU)</option>
                        <option value="client">Cliente (nome/CPF)</option>
                        <option value="number">Número da venda</option>
                        <option value="payment_method">Forma de Pagamento</option>
                        <option value="seller">Vendedor</option>
                        <option value="status">Status</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Valor do filtro</label>
                      {filterBy === 'status' ? (
                        <select
                          value={filterValue}
                          onChange={e => setFilterValue(e.target.value)}
                          data-testid="filter-by-value-status"
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-white"
                        >
                          <option value="">Todos</option>
                          {(type === 'pedido'
                            ? [['pending', 'Pendente'], ['converted', 'Convertido'], ['confirmed', 'Confirmado'], ['cancelled', 'Cancelado']]
                            : type === 'orcamento'
                              ? [['pending', 'Pendente'], ['converted', 'Convertido'], ['cancelled', 'Cancelado']]
                              : [['completed', 'Concluida'], ['pending', 'Pendente'], ['cancelled', 'Cancelada']]
                          ).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      ) : filterBy === 'payment_method' ? (
                        <select
                          value={filterValue}
                          onChange={e => setFilterValue(e.target.value)}
                          data-testid="filter-by-value-payment"
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-white"
                        >
                          <option value="">Todas</option>
                          {paymentMethods.map(pm => (
                            <option key={pm.id} value={pm.id}>{pm.name || pm.type}</option>
                          ))}
                        </select>
                      ) : filterBy === 'seller' ? (
                        <select
                          value={filterValue}
                          onChange={e => setFilterValue(e.target.value)}
                          data-testid="filter-by-value-seller"
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-white"
                        >
                          <option value="">Todos</option>
                          {sellers.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={filterValue}
                          onChange={e => setFilterValue(e.target.value)}
                          disabled={!filterBy}
                          placeholder={filterBy === 'product' ? 'Ex: Amoxicilina, SKU123...' : filterBy === 'client' ? 'Ex: Maria, 12345678900...' : filterBy === 'number' ? 'Ex: 42' : 'Selecione um filtro'}
                          data-testid="filter-by-value"
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50 disabled:text-slate-400"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          {loading ? <Loader /> : (
            <div className="space-y-2">
              {/* Pending pedidos accordion (collapsed by default) */}
              {type === 'venda' && pendingPedidos.length > 0 && (
                <div className="mb-3 bg-amber-50/50 border border-amber-200 rounded-xl overflow-hidden" data-testid="pending-pedidos-section">
                  <button type="button" onClick={() => setPendingOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-amber-100/50 transition-colors" data-testid="pending-pedidos-toggle">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-amber-600" />
                      <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Pedidos Pendentes</span>
                      <span className="text-[10px] font-black bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">{pendingPedidos.length}</span>
                    </div>
                    <ChevronDown size={16} className={`text-amber-600 transition-transform ${pendingOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {pendingOpen && (
                  <div className="px-2 pb-2 divide-y divide-amber-100 bg-white/70 rounded-lg">
                    {pendingPedidos.map(p => (
                      <div key={p.id} className="flex items-center gap-2 sm:gap-3 px-2 py-1.5 hover:bg-amber-50 transition-colors" data-testid="pedido-row">
                        <span className="text-[10px] font-mono font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded shrink-0">#{p.number}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate leading-tight">{p.customer_name || 'Consumidor'}</p>
                          <p className="text-[10px] text-slate-400 truncate">{p.items?.length || 0} itens · {p.vendedor_name || p.created_by_name || '—'} · {new Date(p.created_at).toLocaleDateString('pt-BR')}</p>
                        </div>
                        <span className="font-bold text-sm text-slate-900 tabular-nums shrink-0">{fmtBRL(p.total || 0)}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          {(window.__canDo && window.__canDo('edit', 'sales-orders')) && (
                            <button onClick={() => openPedidoForEdit(p)} title="Editar pedido" className="h-7 w-7 sm:w-auto sm:px-2.5 rounded-full border border-amber-300 text-amber-700 hover:bg-amber-100 flex items-center justify-center gap-1 text-[11px] font-bold transition-colors" data-testid="edit-pedido-btn">
                              <Edit size={12} /><span className="hidden sm:inline">Editar</span>
                            </button>
                          )}
                          {/* iter156dy: gate por acao "confirm" no perfil de acesso */}
                          {(!(window.__canDo) || window.__canDo('confirm', 'sales-orders')) && (
                            <button onClick={() => openPedidoInPDV(p)} title="Confirmar venda" className="h-7 w-7 sm:w-auto sm:px-2.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white flex items-center justify-center gap-1 text-[11px] font-bold transition-colors" data-testid="open-pedido-pdv-btn">
                              <ShoppingCart size={12} /><span className="hidden sm:inline">Confirmar</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  )}
                </div>
              )}
              {type === 'orcamento' && (
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-gray-500">{filteredSales.length} registros</p>
                </div>
              )}
              {(() => {
                const STATUS_COL = { completed: 'bg-emerald-500', pending: 'bg-amber-500', cancelled: 'bg-rose-500' };
                const STATUS_LBL = { completed: 'Concluida', pending: 'Pendente', cancelled: 'Cancelada' };
                const salesColumns = [
                  { key: 'status', label: 'Status', render: r => <span className={`inline-block w-3 h-3 rounded-full ${STATUS_COL[r.status] || 'bg-slate-300'}`} title={STATUS_LBL[r.status] || r.status} />, sortValue: r => r.status },
                  { key: 'number', label: 'N', render: r => (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="font-mono text-xs text-slate-700">{r.number}</span>
                      {(r.returned_total || 0) > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-full" title={`Devolvido: R$ ${(r.returned_total || 0).toFixed(2)}`} data-testid="returned-badge">
                          <ArrowDownRight size={9} />DEVOLVIDO
                        </span>
                      )}
                    </span>
                  ), sortValue: r => r.number },
                  { key: 'date',   label: 'Data', render: r => {
                    // iter77ct.52: mostra data + hora (HH:MM)
                    const d = new Date(r.created_at);
                    return <span className="text-xs text-slate-600" data-testid={`sale-date-${r.id}`}>{d.toLocaleDateString('pt-BR')} <span className="text-[10px] text-slate-400">{d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span></span>;
                  }, sortValue: r => r.created_at },
                  { key: 'customer', label: 'Cliente', render: r => <span className="text-xs font-medium text-slate-900">{(r.customer_is_employee || r.employee_id) && <PartyTag r={r} />}{r.customer_name || 'Consumidor'}</span>, sortValue: r => r.customer_name },
                  { key: 'doc',    label: 'Documento', render: r => <span className="text-xs text-slate-600">{r.customer_doc || '—'}</span>, sortValue: r => r.customer_doc },
                  { key: 'items',  label: 'Qtde', align: 'right', render: r => <span className="text-xs font-mono text-slate-700">{(r.items || []).reduce((a, it) => a + (parseFloat(it.quantity) || 0), 0)}</span>, sortValue: r => (r.items || []).reduce((a, it) => a + (parseFloat(it.quantity) || 0), 0) },
                  { key: 'pay',    label: 'Pagamento', render: r => <PaymentBadge sale={r} />, sortValue: r => r.payment_method },
                  { key: 'vendor', label: 'Vendedor', render: r => <span className="text-xs text-slate-600">{r.vendedor_name || r.created_by_name || '—'}</span>, sortValue: r => r.vendedor_name || r.created_by_name },
                  { key: 'subtotal', label: 'Subtotal', align: 'right', render: r => <span className="text-xs text-slate-600">{fmtBRL(r.subtotal || r.total)}</span>, sortValue: r => r.subtotal },
                  { key: 'discount', label: 'Desconto', align: 'right', render: r => <span className="text-xs text-rose-600">{r.discount ? `-${fmtBRL(r.discount)}` : '—'}</span>, sortValue: r => r.discount || 0 },
                  { key: 'returned', label: 'Troca/Devolucao', align: 'right', render: r => <span className="text-xs text-amber-700">{(r.returned_total || 0) > 0 ? fmtBRL(r.returned_total) : '—'}</span>, sortValue: r => r.returned_total || 0 },
                  { key: 'total',  label: 'Total', align: 'right', render: r => <span className="text-xs font-bold text-slate-900">{fmtBRL(r.total)}</span>, sortValue: r => r.total },
                  { key: 'origin', label: 'Origem', render: r => {
                    // iter156dt4: badge diferenciado para vendas sincronizadas offline
                    if (r.offline_synced) {
                      const dt = r.offline_sale_at ? new Date(r.offline_sale_at) : null;
                      const local = dt ? dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                      return <span className="text-[10px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300" title={`Venda feita offline em ${local}, sincronizada com servidor depois`}>PDV OFFLINE{local ? ` · ${local}` : ''}</span>;
                    }
                    return <span className="text-[10px] uppercase tracking-wider text-slate-500">{r.origin || (r.catalog_order_id ? 'CATALOGO' : 'PDV')}</span>;
                  }, sortValue: r => r.origin },
                ];
                const visDefault = ['status', 'number', 'date', 'customer', 'items', 'pay', 'returned', 'total'];
                const orderDefault = ['status', 'number', 'date', 'customer', 'doc', 'items', 'pay', 'vendor', 'subtotal', 'discount', 'returned', 'total', 'origin'];
                const fmtBRL = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                const renderSaleActions = (s) => (
                  <div className="flex items-center gap-1 justify-end">
                    {/* iter77bx: Visualizar detalhes (todos os tipos e status) */}
                    <button onClick={(e) => { e.stopPropagation(); setViewSale(s); }} className="p-1 text-slate-500 hover:text-violet-700 hover:bg-violet-50 rounded" title="Visualizar detalhes" data-testid="view-sale-btn"><Eye size={12} /></button>
                    {type === 'orcamento' && s.status === 'pending' && (
                      <button onClick={(e) => { e.stopPropagation(); convertQuote(s.id); }} className="text-[11px] text-teal-700 font-bold hover:underline" data-testid="convert-quote-btn">Converter</button>
                    )}
                    {type === 'pedido' && s.status === 'pending' && (window.__canDo && window.__canDo('edit', 'sales-orders')) && (
                      <button onClick={(e) => { e.stopPropagation(); openPedidoForEdit(s); }} className="p-1 text-violet-700 hover:bg-violet-50 rounded" title="Editar" data-testid="edit-row-pedido-btn"><Edit size={12} /></button>
                    )}
                    {(s.status === 'completed' || (type === 'pedido' && s.status !== 'cancelled')) && (
                      <PrintActionsButton
                        saleId={s.id}
                        onPrintCupom={printReceipt}
                        printSaleModel={printSaleModel}
                        onPrintLabel={() => setPrintLabelSale(s)}
                        testIdSuffix={s.id}
                        allowEmitNfe={allowEmitNfe && type === 'venda' && s.status === 'completed'}
                        allowEmitNfce={allowEmitNfce && type === 'venda' && s.status === 'completed'}
                        saleInvoices={invoicesBySale[s.id] || null}
                        onEmitNfe={(sid) => handleEmitInvoice(sid, 'nfe')}
                        onEmitNfce={(sid) => handleEmitInvoice(sid, 'nfce')}
                        onPrintNfe={handlePrintInvoice}
                        onPrintNfce={handlePrintInvoice}
                      />
                    )}
                    {(s.status === 'completed' || (type === 'pedido' && s.status !== 'cancelled')) && (
                      <button onClick={(e) => { e.stopPropagation(); shareWhatsApp(s); }} className="p-1 text-green-600 hover:bg-green-50 rounded" title="WhatsApp" data-testid="share-whatsapp-btn"><Share2 size={12} /></button>
                    )}
                    {/* Devolucao: apenas vendas concluidas */}
                    {type === 'venda' && s.status === 'completed' && (
                      <button onClick={(e) => { e.stopPropagation(); setReturnSale(s); }} className="p-1 text-amber-700 hover:bg-amber-50 rounded" title="Devolucao" data-testid="return-sale-btn"><ArrowDownRight size={12} /></button>
                    )}
                    {s.status !== 'cancelled' && (window.__canDo && window.__canDo('delete', type === 'pedido' ? 'sales-orders' : 'sales')) && (
                      <button onClick={(e) => { e.stopPropagation(); deleteSale(s); }} className="p-1 text-rose-600 hover:bg-rose-50 rounded" title="Excluir" data-testid="delete-sale-btn"><Trash2 size={12} /></button>
                    )}
                  </div>
                );
                return (
                  <DataTable
                    prefKey={`sales-${type}`}
                    columns={salesColumns}
                    defaultVisible={visDefault}
                    defaultOrder={orderDefault}
                    rows={filteredSales}
                    keyField="id"
                    emptyText={
                      // iter156aj: empty state contextual — se ha filtro ativo mas retorna 0,
                      // sugere ampliar o periodo (fluxo comum: buscar produto sem lembrar da data exata).
                      sales.length === 0 && (filterBy || filterValue.trim())
                        ? <span>Nenhum registro encontrado para <b>&quot;{filterBy === 'status' ? (statusLabel[filterValue] || filterValue) : filterValue}&quot;</b> no periodo <b>{{today:'de hoje',week:'desta semana',month:'deste mes',all:'total',custom:'selecionado'}[periodChip] || 'atual'}</b>.<br/><button type="button" onClick={() => setPeriodChip('month')} className="mt-2 px-3 py-1 text-xs font-bold text-violet-700 bg-violet-100 rounded-full hover:bg-violet-200">Ampliar para Este Mes</button> <button type="button" onClick={() => setPeriodChip('all')} className="mt-2 ml-1 px-3 py-1 text-xs font-bold text-slate-700 bg-slate-100 rounded-full hover:bg-slate-200">Ver Tudo</button></span>
                        : sales.length === 0 ? `Nenhum ${type === 'venda' ? 'venda' : type === 'pedido' ? 'pedido' : 'orcamento'} no periodo` : 'Nenhum registro para os filtros'
                    }
                    renderActions={renderSaleActions}
                    mobileBadge={(r) => <span className={`block w-3 h-3 rounded-full ${STATUS_COL[r.status] || 'bg-slate-300'}`} />}
                    mobilePrimary={(r) => (
                      <>
                        {r.number} · {r.customer_name || 'Consumidor'}
                        {(r.returned_total || 0) > 0 && (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-full align-middle" title={`Devolvido: ${fmtBRL(r.returned_total)}`}>
                            <ArrowDownRight size={8} />DEVOL
                          </span>
                        )}
                      </>
                    )}
                    mobileSecondary={(r) => <>{new Date(r.created_at).toLocaleDateString('pt-BR')} · {fmtBRL(r.total)}</>}
                    totalLabel={`${filteredSales.length} registros`}
                  />
                );
              })()}
            </div>
          )}
        </>
      )}
      {returnSale && <SaleReturnModal sale={returnSale} onClose={() => setReturnSale(null)} onSaved={() => { setReturnSale(null); fetchSales(); }} onViewDetails={() => setViewSale(returnSale)} />}
      {viewSale && <SaleDetailsModal sale={viewSale} onClose={() => setViewSale(null)} onUpdated={(s) => { setViewSale(s); fetchSales(); }} />}
      {printLabelSale && (() => {
        // iter77dc.3/.6: gera lista de produtos vindos da venda. Se item tiver
        // store_distribution, cada loja vira 1 linha (repetimos o product_id).
        // Injeta _store_name e _store_qty em cada linha + monta saleContext
        // com nome da empresa e nome do cliente pra popular as novas variaveis
        // 'company_name', 'client_name', 'store_name' e 'store_qty' na etiqueta.
        const rows = [];
        for (const it of (printLabelSale.items || [])) {
          const pid = it.product_id;
          if (!pid) continue;
          const dist = Array.isArray(it.store_distribution) ? it.store_distribution : [];
          if (dist.length > 0) {
            for (const d of dist) {
              rows.push({
                id: pid, name: it.name, sku: it.sku,
                _store_name: String(d.store || ''),
                _store_qty: String(d.quantity ?? ''),
              });
            }
          } else {
            rows.push({
              id: pid, name: it.name, sku: it.sku,
              _store_name: '',
              _store_qty: String(it.quantity ?? ''),
            });
          }
        }
        if (rows.length === 0) {
          toast.error('Nenhum produto valido para etiqueta nesta venda');
          setTimeout(() => setPrintLabelSale(null), 0);
          return null;
        }
        const saleCtx = {
          company_id: printLabelSale.company_id || '',
          company_name: printLabelSale.company_name || printLabelSale.company?.nome_fantasia || printLabelSale.company?.name || '',
          client_name: printLabelSale.customer_name || printLabelSale.client_name || printLabelSale.client?.name || '',
        };
        return (
          <LabelPrintModal
            template={{ module: 'venda' }}
            initialProducts={rows}
            saleContext={saleCtx}
            onClose={() => setPrintLabelSale(null)}
          />
        );
      })()}
      {/* iter77cz: modal pos-geracao de NF-e/NFC-e — opcoes de impressao/edicao */}
      {invoicePrintModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="invoice-print-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
            <div className={`px-5 py-3 border-b flex items-center gap-3 ${invoicePrintModal.tipo === 'nfe' ? 'bg-gradient-to-r from-emerald-50 to-cyan-50 border-emerald-200' : 'bg-gradient-to-r from-sky-50 to-cyan-50 border-sky-200'}`}>
              <div className={`w-9 h-9 rounded-lg text-white flex items-center justify-center shadow-md ${invoicePrintModal.tipo === 'nfe' ? 'bg-gradient-to-br from-emerald-600 to-cyan-600 shadow-emerald-500/40' : 'bg-gradient-to-br from-sky-600 to-cyan-600 shadow-sky-500/40'}`}><Printer size={16} /></div>
              <div className="flex-1">
                <h2 className="font-black text-base text-slate-900">{invoicePrintModal.tipo === 'nfe' ? 'NF-e' : 'NFC-e'} #{invoicePrintModal.invoice?.numero || '—'} gerada</h2>
                <p className="text-[11px] text-slate-500">Status: <span className="uppercase font-bold">{invoicePrintModal.invoice?.status || 'draft'}</span></p>
              </div>
              <button onClick={() => setInvoicePrintModal(null)} className="p-1 text-slate-400 hover:text-slate-700" data-testid="invoice-print-modal-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-700">O documento foi criado como <strong>rascunho</strong>. Escolha uma acao:</p>
              {invoicePrintModal.invoice?.status === 'issued' && (
                <button
                  onClick={async () => {
                    const inv = invoicePrintModal.invoice;
                    try {
                      const { data: blob } = await axios.get(`${ERP}/invoices/${inv.id}/danfe`, { responseType: 'blob', withCredentials: true });
                      const url = window.URL.createObjectURL(blob);
                      window.open(url, '_blank');
                      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                    } catch { toast.error('Erro ao abrir DANFE'); }
                  }}
                  className="w-full text-left p-3 rounded-xl border border-violet-300 bg-violet-50 hover:bg-violet-100 transition-all"
                  data-testid="invoice-print-modal-danfe"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-100 border border-violet-300 flex items-center justify-center flex-shrink-0"><Printer size={14} className="text-violet-700" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black text-violet-800">Imprimir DANFE (PDF)</p>
                      <p className="text-[11px] text-violet-700 mt-0.5">Abre o DANFE em nova aba para impressao.</p>
                    </div>
                  </div>
                </button>
              )}
              <button
                onClick={() => {
                  const inv = invoicePrintModal.invoice;
                  setInvoicePrintModal(null);
                  if (window.__goToSection) {
                    window.__goToSection('invoices', { query: { highlight: inv.id, tipo: invoicePrintModal.tipo } });
                  }
                }}
                className="w-full text-left p-3 rounded-xl border border-cyan-300 bg-cyan-50 hover:bg-cyan-100 transition-all"
                data-testid="invoice-print-modal-open"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-100 border border-cyan-300 flex items-center justify-center flex-shrink-0"><Eye size={14} className="text-cyan-700" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black text-cyan-800">Ver / Editar na lista de {invoicePrintModal.tipo === 'nfe' ? 'NF-e' : 'NFC-e'}</p>
                    <p className="text-[11px] text-cyan-700 mt-0.5">Abre a nota para revisao, complemento de dados ou emissao SEFAZ.</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => setInvoicePrintModal(null)}
                className="w-full py-2 text-sm text-slate-500 hover:text-slate-700"
                data-testid="invoice-print-modal-cancel"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* iter77cc: modal de exclusão em cascata (venda com devolução) */}
      {cascadeDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="cascade-delete-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b bg-gradient-to-r from-rose-50 to-amber-50 border-amber-200 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-rose-600 to-amber-600 text-white flex items-center justify-center shadow-md shadow-rose-500/40"><ArrowDownRight size={16} /></div>
              <div className="flex-1">
                <h2 className="font-black text-base text-slate-900">Venda #{cascadeDelete.number} tem devolução</h2>
                <p className="text-[11px] text-slate-500">Escolha como deseja proceder</p>
              </div>
              <button onClick={() => setCascadeDelete(null)} className="p-1 text-slate-400 hover:text-slate-700" data-testid="cascade-delete-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-700">
                Esta venda possui devolução(ões) totalizando <strong className="text-amber-700">{fmtBRL(cascadeDelete.returned_total)}</strong>. O que deseja fazer?
              </p>
              <button
                onClick={() => runCascadeDelete(cascadeDelete, 'return-only')}
                className="w-full text-left p-3 rounded-xl border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-all"
                data-testid="cascade-delete-return-only"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 border border-amber-300 flex items-center justify-center flex-shrink-0"><ArrowDownRight size={14} className="text-amber-700" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black text-amber-800">Excluir apenas a devolução</p>
                    <p className="text-[11px] text-amber-700 mt-0.5">Estoque devolvido volta ao inventário. Crédito loja é cancelado. A venda original permanece.</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => runCascadeDelete(cascadeDelete, 'return-and-sale')}
                className="w-full text-left p-3 rounded-xl border border-rose-300 bg-rose-50 hover:bg-rose-100 transition-all"
                data-testid="cascade-delete-return-and-sale"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-rose-100 border border-rose-300 flex items-center justify-center flex-shrink-0"><Trash2 size={14} className="text-rose-700" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black text-rose-800">Excluir a devolução E a venda</p>
                    <p className="text-[11px] text-rose-700 mt-0.5">Reverte a devolução (estoque volta), depois exclui a venda (estoque sai novamente + financeiro cancelado). Estado final: como se a venda nunca tivesse existido.</p>
                  </div>
                </div>
              </button>
              <button onClick={() => setCascadeDelete(null)} className="w-full py-2 text-xs font-bold text-slate-500 hover:text-slate-700" data-testid="cascade-delete-cancel">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ===== SaleReturnModal — Devolucao de Mercadoria com geracao de credito loja ===== */
const SaleReturnModal = ({ sale, onClose, onSaved, onViewDetails }) => {
  const [items, setItems] = useState([]); // [{checked, max, qty, ...}]
  const [generateCredit, setGenerateCredit] = useState(true);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [prevReturns, setPrevReturns] = useState({}); // product_id -> already-returned qty
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/sales/${sale.id}/returns`, { withCredentials: true });
        const map = {};
        (data.data || []).forEach(r => (r.items || []).forEach(it => { map[it.product_id] = (map[it.product_id] || 0) + (it.quantity || 0); }));
        setPrevReturns(map);
      } catch { /* ignore */ }
    })();
    setItems((sale.items || []).map((it, i) => ({
      key: `${it.product_id}_${i}`,
      product_id: it.product_id,
      name: it.name,
      sku: it.sku || '',
      unit_price: it.unit_price,
      sold_qty: it.quantity,
      checked: false,
      qty: 0,
    })));
  }, [sale]);

  const toggle = (idx) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, checked: !it.checked, qty: !it.checked ? Math.max(0, it.sold_qty - (prevReturns[it.product_id] || 0)) : 0 } : it));
  };
  const setQty = (idx, q) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, qty: Math.max(0, Math.min(q, it.sold_qty - (prevReturns[it.product_id] || 0))) } : it));
  };
  const selectedItems = items.filter(it => it.checked && it.qty > 0);
  const totalReturn = selectedItems.reduce((s, it) => s + (it.qty * it.unit_price), 0);

  const submit = async () => {
    if (selectedItems.length === 0) { toast.error('Selecione ao menos 1 item'); return; }
    setSubmitting(true);
    try {
      const payload = {
        items: selectedItems.map(it => ({ product_id: it.product_id, name: it.name, sku: it.sku, quantity: it.qty, unit_price: it.unit_price })),
        generate_store_credit: generateCredit,
        reason, notes,
      };
      const { data } = await axios.post(`${ERP}/sales/${sale.id}/return`, payload, { withCredentials: true });
      toast.success(data.message);
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="return-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 flex items-center justify-between border-b bg-amber-50 border-amber-200">
          <h2 className="font-black text-base flex items-center gap-2 text-amber-800"><ArrowDownRight size={18} />Devolucao da Venda #{sale.number}</h2>
          <div className="flex items-center gap-1">
            {onViewDetails && (
              <button onClick={onViewDetails} className="px-2.5 py-1 rounded-md text-xs font-bold text-violet-700 bg-white border border-violet-200 hover:bg-violet-50 flex items-center gap-1.5" title="Ver detalhes completos da venda" data-testid="return-view-details-btn">
                <Eye size={12} />Ver Detalhes
              </button>
            )}
            <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><span className="text-slate-500">Cliente:</span> <span className="font-bold">{sale.customer_name || 'Consumidor'}</span></div>
            <div><span className="text-slate-500">Total Venda:</span> <span className="font-bold">R$ {(sale.total || 0).toFixed(2)}</span></div>
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">Itens da venda</p>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {items.map((it, i) => {
                const already = prevReturns[it.product_id] || 0;
                const remaining = it.sold_qty - already;
                return (
                  <div key={it.key} className={`flex items-center gap-3 p-2.5 rounded-lg border ${it.checked ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200'}`}>
                    <input type="checkbox" checked={it.checked} disabled={remaining <= 0} onChange={() => toggle(i)} className="w-4 h-4" data-testid={`return-item-check-${i}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{it.name}</p>
                      <p className="text-xs text-slate-500">{it.sku && `${it.sku} · `}Vendido: {it.sold_qty} · Ja devolvido: {already}{remaining <= 0 && ' · Nada a devolver'}</p>
                    </div>
                    {it.checked && (
                      <input type="number" min="0" max={remaining} step="1" value={it.qty} onChange={e => setQty(i, parseFloat(e.target.value) || 0)} className="w-20 px-2 py-1.5 border border-amber-300 rounded text-sm text-right font-bold" data-testid={`return-item-qty-${i}`} />
                    )}
                    <span className="text-xs font-bold text-slate-600 w-24 text-right">R$ {(it.qty * it.unit_price).toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Motivo</label>
              <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Defeito, troca, arrependimento..." className={inputClass} data-testid="return-reason" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Observacoes</label>
              <input value={notes} onChange={e => setNotes(e.target.value)} className={inputClass} data-testid="return-notes" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm bg-violet-50 border border-violet-200 rounded-lg p-3 cursor-pointer">
            <input type="checkbox" checked={generateCredit} onChange={e => setGenerateCredit(e.target.checked)} className="w-4 h-4" data-testid="return-generate-credit" />
            <span className="text-violet-900"><strong>Gerar Credito Loja</strong> de R$ {totalReturn.toFixed(2)} para o cliente — pode ser usado em vendas futuras via forma "Credito Devolucao".</span>
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
          <div className="text-sm font-bold text-slate-700">Total da Devolucao: <span className="text-amber-700">R$ {totalReturn.toFixed(2)}</span></div>
          <div className="flex gap-2">
            <button onClick={onClose} type="button" className="btn-pill-ghost">Cancelar</button>
            <button onClick={submit} disabled={submitting || selectedItems.length === 0} className="btn-pill-primary disabled:opacity-50" data-testid="return-submit-btn">
              <Save size={13} />{submitting ? 'Salvando...' : 'Confirmar Devolucao'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ===== SaleDetailsModal — iter77bx: Visualizador de venda (somente leitura) ===== */
const SaleDetailsModal = ({ sale, onClose, onUpdated }) => {
  const [returns, setReturns] = useState([]);
  const [loadingRet, setLoadingRet] = useState(false);
  const [deletingRet, setDeletingRet] = useState(null);
  const canDeleteRet = !!(window.__canDo && window.__canDo('delete', 'sales'));
  // iter156fr: alterar cliente/funcionario de uma venda ja feita (propaga para o financeiro)
  const canEditSale = !!(window.__canDo && window.__canDo('edit', 'sales'));
  const [custPicker, setCustPicker] = useState(null); // { clients, employees }
  const [savingCust, setSavingCust] = useState(false);
  const openCustPicker = async () => {
    try {
      const [cl, em] = await Promise.all([
        axios.get(`${ERP}/clients?limit=50000${sale.company_id ? `&company_id=${sale.company_id}` : ''}`, { withCredentials: true }),
        axios.get(`${ERP}/employees${sale.company_id ? `?company_id=${sale.company_id}` : ''}`, { withCredentials: true }),
      ]);
      setCustPicker({ clients: cl.data.data || [], employees: em.data.data || [] });
    } catch { toast.error('Erro ao carregar clientes'); }
  };
  const saveCustomer = async (c) => {
    setSavingCust(true);
    try {
      const { data } = await axios.patch(`${ERP}/sales/${sale.id}/customer`, { client_id: c.id || null, employee_id: c.employee_id || null, customer_name: c.name || '', customer_doc: c.document || '' }, { withCredentials: true });
      toast.success(`Cliente alterado para ${data.customer_name}${data.financial_updated ? ` · ${data.financial_updated} lancamento(s) atualizado(s)` : ''}`);
      setCustPicker(null);
      if (onUpdated) onUpdated({ ...sale, customer_name: data.customer_name, customer_doc: data.customer_doc, client_id: data.client_id, employee_id: data.employee_id });
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao alterar cliente'); }
    finally { setSavingCust(false); }
  };
  const loadReturns = useCallback(() => {
    if (!sale?.id) return;
    setLoadingRet(true);
    axios.get(`${ERP}/sales/${sale.id}/returns`, { withCredentials: true })
      .then(({ data }) => setReturns((data.data || []).filter(r => r.status !== 'cancelled')))
      .catch(() => {})
      .finally(() => setLoadingRet(false));
  }, [sale?.id]);
  useEffect(() => { loadReturns(); }, [loadReturns]);
  const handleDeleteReturn = async (ret) => {
    if (!(await confirmDialog({ title: `Excluir esta devolução?\n\nO estoque devolvido será removido novamente do inventário e o crédito loja será cancelado.`, confirmLabel: 'Confirmar', danger: true }))) return;
    setDeletingRet(ret.id);
    try {
      const { data } = await axios.delete(`${ERP}/sales/${sale.id}/returns/${ret.id}`, { withCredentials: true });
      toast.success(data.message || 'Devolução excluída');
      loadReturns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir devolução');
    } finally { setDeletingRet(null); }
  };
  const fmtBRL = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // iter156aa: legado consolidado — sempre 2 casas decimais no BRL, evita "R$ 12,8725" no PDV/relatorios.
  const statusMeta = { completed: { l: 'Concluída', c: 'bg-emerald-100 text-emerald-700 border-emerald-300' }, pending: { l: 'Pendente', c: 'bg-amber-100 text-amber-700 border-amber-300' }, cancelled: { l: 'Cancelada', c: 'bg-rose-100 text-rose-700 border-rose-300' }, converted: { l: 'Convertido', c: 'bg-blue-100 text-blue-700 border-blue-300' }, confirmed: { l: 'Confirmado', c: 'bg-blue-100 text-blue-700 border-blue-300' } };
  const stMeta = statusMeta[sale.status] || { l: sale.status || '—', c: 'bg-slate-100 text-slate-700 border-slate-300' };
  const items = sale.items || [];
  const totalItens = items.reduce((s, it) => s + (parseFloat(it.quantity) || 0), 0);
  const subtotal = sale.subtotal || items.reduce((s, it) => s + ((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0)), 0);
  const typeLabel = { venda: 'Venda', pedido: 'Pedido', orcamento: 'Orçamento' }[sale.type] || 'Venda';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="sale-details-modal">
      {custPicker && (
        <ClientPDVModal
          clients={custPicker.clients}
          employees={custPicker.employees}
          companyId={sale.company_id}
          onClose={() => setCustPicker(null)}
          onSelectExisting={saveCustomer}
          onAttachDoc={({ doc_type, document }) => saveCustomer({ id: null, name: `Consumidor (${doc_type.toUpperCase()})`, document })}
          onCreated={saveCustomer}
        />
      )}
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 flex items-center justify-between border-b bg-gradient-to-r from-blue-50 to-violet-50 border-violet-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-violet-600 text-white flex items-center justify-center shadow-md shadow-violet-500/40"><Eye size={16} /></div>
            <div>
              <h2 className="font-black text-base text-slate-900">{typeLabel} #{sale.number}</h2>
              <p className="text-[11px] text-slate-500">{new Date(sale.created_at).toLocaleString('pt-BR')}</p>
            </div>
            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${stMeta.c}`}>{stMeta.l}</span>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="sale-details-close"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Cabeçalho: Cliente + Vendedor */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Cliente{sale.customer_is_employee || sale.employee_id ? ' (Funcionario)' : ''}</p>
                {canEditSale && sale.type === 'venda' && sale.status !== 'cancelled' && (
                  <button onClick={openCustPicker} disabled={savingCust} className="text-[10px] font-bold text-violet-700 hover:text-violet-900 hover:underline flex items-center gap-1" data-testid="sale-change-customer-btn"><Edit size={10} /> Alterar</button>
                )}
              </div>
              <p className="text-sm font-bold text-slate-900" data-testid="sale-detail-customer-name">{sale.customer_name || 'Consumidor'}</p>
              {sale.customer_doc && <p className="text-xs text-slate-500 mt-0.5">{sale.customer_doc}</p>}
              {sale.customer_phone && <p className="text-xs text-slate-500">{sale.customer_phone}</p>}
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Vendedor</p>
              <p className="text-sm font-bold text-slate-900">{sale.vendedor_name || sale.created_by_name || '—'}</p>
              <p className="text-xs text-slate-500 mt-0.5">Origem: {sale.origin || (sale.catalog_order_id ? 'CATÁLOGO' : 'PDV')}</p>
              {sale.offline_synced && sale.offline_sale_at && (
                <p className="text-[11px] font-bold text-amber-700 mt-1" data-testid="sale-detail-offline-banner">
                  ⚠ Venda feita offline em {new Date(sale.offline_sale_at).toLocaleString('pt-BR')} · sincronizada depois
                </p>
              )}
            </div>
          </div>
          {/* Itens */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-black uppercase tracking-wider text-slate-500">Itens da venda</p>
              <span className="text-[11px] font-bold text-slate-500">{items.length} produtos · {totalItens} unidades</span>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-1.5">SKU</th>
                    <th className="text-left px-2 py-1.5">Produto</th>
                    <th className="text-right px-2 py-1.5">Qtd</th>
                    <th className="text-right px-2 py-1.5">Unit.</th>
                    <th className="text-right px-2 py-1.5">Total</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {items.map((it, i) => (
                    <React.Fragment key={i}>
                      <tr className="border-t border-slate-100">
                        <td className="px-2 py-1.5 font-mono text-slate-500">{it.sku || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-900">{it.name || it.product_name || '—'}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{it.quantity}</td>
                        <td className="px-2 py-1.5 text-right">{fmtBRL(it.unit_price)}</td>
                        <td className="px-2 py-1.5 text-right font-bold">{fmtBRL(it.total || (it.quantity * it.unit_price))}</td>
                      </tr>
                      {Array.isArray(it.store_distribution) && it.store_distribution.length > 0 && (
                        <tr className="bg-purple-50/50">
                          <td colSpan={5} className="px-2 py-1.5 text-[10px] text-purple-800">
                            <span className="font-black uppercase tracking-wider mr-2 inline-flex items-center gap-1"><Store size={10} />Lojas:</span>
                            {it.store_distribution.map((d, k) => (
                              <span key={k} className="inline-block mr-2 font-mono">{d.store}: <strong>{d.quantity}</strong>{k < it.store_distribution.length - 1 ? ' ·' : ''}</span>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Pagamento */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Forma de Pagamento</p>
              {Array.isArray(sale.payments) && sale.payments.length > 0 ? (
                <div className="space-y-1">
                  {sale.payments.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      {/* iter77ct.75: prioriza p.name (nome custom da PM) sobre p.method (type generico) */}
                      <span className="font-bold text-slate-700">{p.name || PAY_LABEL[(p.method || '').toLowerCase()] || p.method || p.payment_method || '—'}</span>
                      <span className="font-mono text-slate-900">{fmtBRL(p.amount || p.value)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm font-bold text-slate-900">{sale.payment_method || '—'}</p>
              )}
              {sale.installments > 1 && <p className="text-xs text-slate-500 mt-1">{sale.installments}x parcelas</p>}
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Totais</p>
              <div className="space-y-0.5 text-xs">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal:</span> <span className="font-mono">{fmtBRL(subtotal)}</span></div>
                {(sale.discount || 0) > 0 && <div className="flex justify-between text-rose-600"><span>Desconto:</span> <span className="font-mono">- {fmtBRL(sale.discount)}</span></div>}
                {(sale.surcharge || 0) > 0 && <div className="flex justify-between text-blue-700"><span>Acréscimo:</span> <span className="font-mono">+ {fmtBRL(sale.surcharge)}</span></div>}
                {(sale.freight || 0) > 0 && <div className="flex justify-between text-slate-600"><span>Frete:</span> <span className="font-mono">+ {fmtBRL(sale.freight)}</span></div>}
                <div className="flex justify-between border-t pt-1 mt-1"><span className="font-black text-slate-700">TOTAL:</span> <span className="font-black text-slate-900">{fmtBRL(sale.total)}</span></div>
              </div>
            </div>
          </div>
          {sale.notes && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-[10px] font-black uppercase tracking-wider text-amber-700 mb-1">Observações</p>
              <p className="text-xs text-slate-700 whitespace-pre-wrap">{sale.notes}</p>
            </div>
          )}
          {/* Histórico de devoluções */}
          {(returns.length > 0 || loadingRet) && (
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2"><ArrowDownRight size={12} className="text-amber-600" /> Devoluções desta venda</p>
              {loadingRet ? (
                <p className="text-xs text-slate-400">Carregando…</p>
              ) : (
                <div className="space-y-1.5">
                  {returns.map((r, i) => (
                    <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-amber-800">{new Date(r.created_at).toLocaleString('pt-BR')}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-amber-800">{fmtBRL(r.total)}</span>
                          {canDeleteRet && (
                            <button
                              onClick={() => handleDeleteReturn(r)}
                              disabled={deletingRet === r.id}
                              className="p-1 text-rose-600 hover:bg-rose-100 rounded disabled:opacity-50"
                              title="Excluir esta devolução (reverte estoque e cancela crédito)"
                              data-testid={`delete-return-btn-${r.id}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="text-slate-600 mt-1">
                        {(r.items || []).map((it, j) => (
                          <div key={j} className="flex items-center justify-between">
                            <span>{it.quantity}x {it.name}</span>
                            <span className="font-mono">{fmtBRL(it.total)}</span>
                          </div>
                        ))}
                      </div>
                      {r.store_credit_generated && <p className="text-[10px] text-violet-700 mt-1">✓ Crédito loja gerado</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-pill-ghost" data-testid="sale-details-close-btn">Fechar</button>
        </div>
      </div>
    </div>
  );
};

/* ========== CASH REGISTER ========== */
const CashPage = ({ companyId }) => {
  const [registers, setRegisters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openBalance, setOpenBalance] = useState('');
  const fetchRegisters = useCallback(async () => {
    if (!companyId) { setLoading(false); return; }
    try { const { data } = await axios.get(`${ERP}/cash-register?company_id=${companyId}`, { withCredentials: true }); setRegisters(data.data || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, [companyId]);
  useEffect(() => { fetchRegisters(); }, [fetchRegisters]);
  const openRegister = async () => {
    try { await axios.post(`${ERP}/cash-register/open`, { company_id: companyId, opening_balance: parseFloat(openBalance) || 0 }, { withCredentials: true }); toast.success('Caixa aberto!'); setOpenBalance(''); fetchRegisters(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };
  const closeRegister = async (id) => {
    try { const { data } = await axios.post(`${ERP}/cash-register/close/${id}`, {}, { withCredentials: true }); toast.success(`Caixa fechado! Saldo: R$ ${data.closing_balance?.toFixed(2)}`); fetchRegisters(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };
  if (!companyId) return <NoCompany />;
  if (loading) return <Loader />;
  const openReg = registers.find(r => r.status === 'open');
  return (
    <div data-testid="cash-page">
      {!openReg ? (
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center mb-5">
          <Wallet size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium mb-4">Nenhum caixa aberto</p>
          <div className="flex items-center justify-center gap-2">
            <input type="number" value={openBalance} onChange={e => setOpenBalance(e.target.value)} className={`${inputClass} w-40`} placeholder="Saldo inicial" />
            <button onClick={openRegister} className={btnPrimary} data-testid="open-cash-btn"><Plus size={14} /><span>Abrir Caixa</span></button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-teal-200 rounded-xl p-5 mb-5" data-testid="open-cash-register">
          <div className="flex items-center justify-between mb-3">
            <div><p className="font-bold text-gray-900">Caixa Aberto</p><p className="text-xs text-gray-500">Aberto por {openReg.opened_by} em {new Date(openReg.opened_at).toLocaleString('pt-BR')}</p></div>
            <button onClick={() => closeRegister(openReg.id)} className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold" data-testid="close-cash-btn">Fechar Caixa</button>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Saldo Inicial</p><p className="font-bold text-gray-900">R$ {(openReg.opening_balance || 0).toFixed(2)}</p></div>
            <div className="bg-green-50 rounded-lg p-3"><p className="text-xs text-gray-500">Entradas</p><p className="font-bold text-green-700">R$ {(openReg.movements?.filter(m => m.type === 'in').reduce((s, m) => s + m.amount, 0) || 0).toFixed(2)}</p></div>
            <div className="bg-red-50 rounded-lg p-3"><p className="text-xs text-gray-500">Saídas</p><p className="font-bold text-red-700">R$ {(openReg.movements?.filter(m => m.type === 'out').reduce((s, m) => s + Math.abs(m.amount), 0) || 0).toFixed(2)}</p></div>
          </div>
        </div>
      )}
      <h4 className="font-semibold text-gray-700 mb-3 text-sm">Histórico</h4>
      <div className="space-y-2">
        {registers.filter(r => r.status === 'closed').map(r => (
          <div key={r.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid="cash-history-row">
            <div><p className="text-sm font-medium text-gray-900">{new Date(r.opened_at).toLocaleDateString('pt-BR')}</p><p className="text-xs text-gray-500">{r.opened_by} • Fechado {r.closed_at ? new Date(r.closed_at).toLocaleTimeString('pt-BR') : ''}</p></div>
            <div className="text-right"><p className="font-bold text-sm text-gray-900">R$ {(r.closing_balance || 0).toFixed(2)}</p><p className="text-[10px] text-gray-500">Saldo final</p></div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ========== SALES REPORT (Relatorio dinamico) ========== */
/* ========== SearchableSelect — iter156ai: dropdown com busca embutida ==========
    Ideal para listas grandes (clientes/produtos) onde <select> puro fica lento.
    Prop `items`: [{ id, label }]. `value` = id selecionado. `onChange(id)`. */
const SearchableSelect = ({ value, onChange, items, placeholder = 'Todos', emptyLabel = 'Todos', testId }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickAway);
    setTimeout(() => { try { inputRef.current && inputRef.current.focus(); } catch { /* noop */ } }, 20);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open]);
  const _norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const filtered = React.useMemo(() => {
    const q = _norm(query.trim());
    if (!q) return items;
    return items.filter(it => _norm(it.label).includes(q));
  }, [items, query]);
  const selected = items.find(it => it.id === value);
  return (
    <div ref={wrapperRef} className="relative" data-testid={testId}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none flex items-center justify-between gap-2 hover:border-slate-300">
        <span className={`truncate ${selected ? 'text-slate-800 font-semibold' : 'text-slate-500'}`}>
          {selected ? selected.label : emptyLabel}
        </span>
        <ChevronDown size={12} className="text-slate-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 max-h-64 overflow-hidden flex flex-col">
          <div className="p-1.5 border-b border-slate-100 flex-shrink-0">
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded outline-none focus:border-violet-400"
              autoComplete="off"
              inputMode="search"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <div className="overflow-y-auto">
            <button type="button" onClick={() => { onChange(''); setOpen(false); setQuery(''); }} className={`w-full text-left px-3 py-1.5 text-xs hover:bg-violet-50 ${!value ? 'bg-violet-100 text-violet-700 font-bold' : 'text-slate-600'}`}>
              {emptyLabel}
            </button>
            {filtered.map(it => (
              <button
                key={it.id}
                type="button"
                onClick={() => { onChange(it.id); setOpen(false); setQuery(''); }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-violet-50 ${value === it.id ? 'bg-violet-100 text-violet-700 font-bold' : 'text-slate-700'} truncate`}
                title={it.label}
              >
                {it.label}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-xs text-slate-400 italic text-center">Nenhum resultado</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


const SalesReportPage = ({ companyId }) => {
  const today = localDateISO();
  const [dateFrom, setDateFrom] = useState(today); // iter77ct.95: default = "Hoje"
  const [dateTo, setDateTo] = useState(today);
  // iter77ct.95: chip de periodo (default = hoje). "custom" libera edicao manual dos inputs.
  const [periodChip, setPeriodChip] = useState('today'); // today | week | month | custom
  useEffect(() => {
    if (periodChip === 'custom') return;
    const now = new Date();
    const fmt = localDateISO;
    if (periodChip === 'today') { setDateFrom(fmt(now)); setDateTo(fmt(now)); }
    else if (periodChip === 'week') {
      const d = new Date(now); d.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
      setDateFrom(fmt(d)); setDateTo(fmt(now));
    }
    else if (periodChip === 'month') {
      setDateFrom(fmt(new Date(now.getFullYear(), now.getMonth(), 1)));
      setDateTo(fmt(now));
    }
    else if (periodChip === 'prev_month') {
      setDateFrom(fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
      setDateTo(fmt(new Date(now.getFullYear(), now.getMonth(), 0)));
    }
  }, [periodChip]); // eslint-disable-line react-hooks/exhaustive-deps
  // iter77ct.65: filtro de Forma de Pagamento agora e por method_id (nome customizado) em vez do type generico
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [clientId, setClientId] = useState('');
  const [productId, setProductId] = useState('');
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('overview'); // overview | clients | products | sales
  // iter77ct.75: ordenacao clicavel no cabecalho da Lista de Vendas
  const [sortBy, setSortBy] = useState('number');
  const [sortDir, setSortDir] = useState('desc');
  // iter156ga: sintetico por dia (agrupa as vendas filtradas por data local)
  const [synthetic, setSynthetic] = useState(false);
  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  // Load clients + products + payment methods once for filter selects
  useEffect(() => {
    const f = async () => {
      try {
        const [c, p, pm] = await Promise.all([
          axios.get(`${ERP}/clients`, { withCredentials: true }),
          axios.get(`${API}/products?limit=5000`, { withCredentials: true }),
          axios.get(`${ERP}/payment-methods${companyId ? `?company_id=${companyId}` : ''}`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
        ]);
        setClients(c.data.data || []);
        setProducts(p.data.data || p.data || []);
        setPaymentMethods(pm.data.data || pm.data || []);
      } catch { /* ignore */ }
    };
    f();
  }, [companyId]);

  const fetchReport = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ company_id: companyId });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      // iter77ct.65: envia payment_method_id (nome customizado) — backend filtra pelo id da PM
      if (paymentMethodId) params.set('payment_method_id', paymentMethodId);
      if (clientId) params.set('client_id', clientId);
      if (productId) params.set('product_id', productId);
      const { data: d } = await axios.get(`${ERP}/sales/report?${params.toString()}`, { withCredentials: true });
      setData(d);
    } catch (err) { toast.error('Erro ao carregar relatorio'); }
    finally { setLoading(false); }
  }, [companyId, dateFrom, dateTo, paymentMethodId, clientId, productId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const clearFilters = () => { setPeriodChip('today'); setDateFrom(today); setDateTo(today); setPaymentMethodId(''); setClientId(''); setProductId(''); };

  const exportCSV = () => {
    if (!data?.sales) return;
    const pmFiltered = !!paymentMethodId;
    const head = pmFiltered
      ? ['Numero', 'Data', 'Cliente', 'Itens', 'Subtotal', 'Desconto', 'Total Venda', 'Parcela Forma Selecionada', 'Pagamento', 'Vendedor', 'Status']
      : ['Numero', 'Data', 'Cliente', 'Itens', 'Subtotal', 'Desconto', 'Total', 'Pagamento', 'Vendedor', 'Status'];
    const br = (n) => Number(n || 0).toFixed(2).replace('.', ',');
    const rows = data.sales.map(s => {
      const base = [
        s.number, new Date(s.created_at).toLocaleString('pt-BR'),
        (s.customer_name || 'Consumidor').replace(/;/g, ','),
        (s.items?.length || 0),
        br(s.subtotal),
        br(s.discount),
        br(s.total),
      ];
      const tail = [
        s.payment_method || '',
        (s.vendedor_name || s.created_by_name || '').replace(/;/g, ','),
        s.status || '',
      ];
      if (pmFiltered) {
        return [...base, br(s.filtered_amount ?? s.total ?? 0), ...tail].join(';');
      }
      return [...base, ...tail].join(';');
    });
    const csv = [head.join(';'), ...rows].join('\n');
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `relatorio-vendas-${dateFrom}_${dateTo}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!companyId) return <NoCompany />;

  const total = data?.total || 0;
  const methods = data?.by_payment_method || [];
  const byClient = data?.by_client || [];
  const byProduct = data?.by_product || [];
  const sales = data?.sales || [];
  // iter156an: quando ha filtro por Forma de Pagamento, exibe o VALOR PARCIAL
  // (soma so daquela forma na venda). Sem esse fix, a coluna Total mostrava
  // o valor cheio da venda mesmo em splits (ex: venda R$86 = R$60 cartao + R$26 dinheiro
  // aparecia R$86 mesmo filtrando por Dinheiro), causando confusao pois o card
  // do topo somava apenas R$26.
  const isPMFiltered = !!paymentMethodId;
  const displayTotal = (s) => isPMFiltered && (s.filtered_amount != null) ? s.filtered_amount : (s.total || 0);
  // iter156ga: linhas do SINTETICO por dia (data local do navegador = BRT do usuario)
  const dailyRows = (() => {
    const map = {};
    const wd = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'];
    (sales || []).forEach(s => {
      const d = new Date(s.created_at);
      if (isNaN(d)) return;
      const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const row = map[day] || (map[day] = { day, weekday: wd[d.getDay()], count: 0, items: 0, discount: 0, surcharge: 0, freight: 0, total: 0 });
      row.count += 1;
      row.items += (s.items || []).reduce((a, i) => a + Number(i.quantity || 0), 0);
      row.discount += Number(s.discount || 0);
      row.surcharge += Number(s.surcharge || 0);
      row.freight += Number(s.freight || 0);
      row.total += Number(displayTotal(s));
    });
    return Object.values(map).sort((a, b) => b.day.localeCompare(a.day));
  })();
  // iter77ct.75: derivada ordenada da lista de vendas (nao muta a original)
  const paymentLabel = (s) => {
    const pays = Array.isArray(s?.payments) ? s.payments.filter(p => p && (parseFloat(p.amount) || 0) > 0) : [];
    if (pays.length > 0) return (pays[0].name || pays[0].method || '').toLowerCase();
    return (s?.payment_method || '').toLowerCase();
  };
  const sortedSales = [...sales].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    let va, vb;
    switch (sortBy) {
      case 'number': va = a.number || 0; vb = b.number || 0; break;
      case 'date': va = a.created_at || ''; vb = b.created_at || ''; break;
      case 'customer': va = (a.customer_name || 'Consumidor').toLowerCase(); vb = (b.customer_name || 'Consumidor').toLowerCase(); break;
      case 'payment': va = paymentLabel(a); vb = paymentLabel(b); break;
      case 'items': va = a.items?.length || 0; vb = b.items?.length || 0; break;
      case 'discount': va = Number(a.discount || 0); vb = Number(b.discount || 0); break;
      case 'surcharge': va = Number(a.surcharge || 0); vb = Number(b.surcharge || 0); break;
      case 'freight': va = Number(a.freight || 0); vb = Number(b.freight || 0); break;
      case 'total': va = displayTotal(a); vb = displayTotal(b); break;
      default: return 0;
    }
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });
  const maxMethod = methods.reduce((mx, m) => Math.max(mx, m.total), 0) || 1;
  const maxClient = byClient.reduce((mx, c) => Math.max(mx, c.total), 0) || 1;
  const maxProduct = byProduct.reduce((mx, p) => Math.max(mx, p.total), 0) || 1;

  const activeFilters = [dateFrom, dateTo, paymentMethodId, clientId, productId].filter(Boolean).length;

  return (
    <div data-testid="sales-report-page" className="space-y-4">
      {/* Header with KPIs — iter77ct.97: card mais compacto, sem a data grande no titulo
          (redundante com os chips de periodo abaixo) */}
      <div className="bg-gradient-to-br from-[#0a0a4a] via-violet-800 to-cyan-700 rounded-2xl p-3 md:p-4 text-white shadow-lg shadow-violet-900/30">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[10px] uppercase tracking-widest opacity-70 font-bold">Relatorio de Vendas</p>
            {isPMFiltered && (
              <span className="inline-flex items-center gap-1 bg-amber-400/25 border border-amber-300/50 text-amber-100 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider" title="Vendas com pagamento misto contam apenas a parcela recebida na forma selecionada">
                <Filter size={9} /> Total considera so a parcela da forma selecionada
              </span>
            )}
          </div>
          <button onClick={exportCSV} disabled={sales.length === 0} className="hidden sm:flex bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white px-2.5 py-1.5 rounded-lg text-[11px] font-bold items-center gap-1.5" data-testid="export-csv-btn"><Download size={12} />Exportar CSV</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="bg-white/10 backdrop-blur rounded-xl p-2.5"><p className="text-[9px] uppercase opacity-70 font-bold">Total</p><p className="text-base md:text-lg font-black mt-0.5" data-testid="report-total">{fmtBRL(total)}</p></div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-2.5"><p className="text-[9px] uppercase opacity-70 font-bold">Vendas</p><p className="text-base md:text-lg font-black mt-0.5" data-testid="report-count">{data?.sales_count || 0}</p></div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-2.5"><p className="text-[9px] uppercase opacity-70 font-bold">Itens</p><p className="text-base md:text-lg font-black mt-0.5">{data?.items_count || 0}</p></div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-2.5"><p className="text-[9px] uppercase opacity-70 font-bold">Desconto</p><p className="text-base md:text-lg font-black mt-0.5 text-rose-300">{fmtBRL(data?.discount_total || 0)}</p></div>
        </div>
      </div>

      {/* Filters — iter77ct.95: chips de periodo (Hoje/Esta Semana/Este Mes/Personalizado)
          + inputs de data e dropdowns modernizados */}
      <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Filter size={14} className="text-slate-500" /><span className="text-xs font-black text-slate-700 uppercase tracking-wider">Filtros</span>{activeFilters > 0 && <span className="text-[10px] bg-teal-500 text-white px-1.5 py-0.5 rounded font-bold">{activeFilters} ativo{activeFilters > 1 ? 's' : ''}</span>}</div>
          <button onClick={clearFilters} className="text-[11px] text-teal-600 font-bold hover:underline" data-testid="clear-report-filters">Limpar filtros</button>
        </div>
        {/* Chips de periodo */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {[
            { k: 'prev_month', l: 'Mes Anterior' },
            { k: 'today', l: 'Hoje' },
            { k: 'week', l: 'Esta Semana' },
            { k: 'month', l: 'Este Mes' },
            { k: 'custom', l: 'Personalizado' },
          ].map(chip => (
            <button
              key={chip.k}
              onClick={() => setPeriodChip(chip.k)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all ${periodChip === chip.k
                ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md shadow-violet-500/30'
                : 'bg-white text-slate-600 border border-slate-200 hover:border-violet-300 hover:text-violet-700'}`}
              data-testid={`period-chip-${chip.k}`}>
              {chip.l}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
          <div><label className="text-[9px] font-black text-slate-500 uppercase block mb-0.5">De</label><input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPeriodChip('custom'); }} className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none" data-testid="report-date-from" /></div>
          <div><label className="text-[9px] font-black text-slate-500 uppercase block mb-0.5">Ate</label><input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPeriodChip('custom'); }} className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none" data-testid="report-date-to" /></div>
          <div>
            <label className="text-[9px] font-black text-slate-500 uppercase block mb-0.5">Forma Pgto</label>
            <select value={paymentMethodId} onChange={e => setPaymentMethodId(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none" data-testid="report-payment-method">
              <option value="">Todas</option>
              {paymentMethods.map(pm => <option key={pm.id} value={pm.id}>{pm.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[9px] font-black text-slate-500 uppercase block mb-0.5">Cliente</label>
            <SearchableSelect
              value={clientId}
              onChange={setClientId}
              items={clients.map(c => ({
                id: c.id,
                // iter156af/ah: Nome Fantasia como principal quando PJ tem trade_name; senao c.name
                label: (c.doc_type === 'cnpj' && c.trade_name) ? `${c.trade_name} · ${c.name || c.legal_name || ''}` : (c.name || c.legal_name || ''),
              }))}
              placeholder="Buscar cliente (nome, fantasia, CPF/CNPJ)..."
              emptyLabel="Todos"
              testId="report-client"
            />
          </div>
          <div>
            <label className="text-[9px] font-black text-slate-500 uppercase block mb-0.5">Produto</label>
            <SearchableSelect
              value={productId}
              onChange={setProductId}
              items={products.map(p => ({
                id: p.id,
                label: p.sku ? `${p.sku} — ${p.name}` : p.name,
              }))}
              placeholder="Buscar produto (nome ou SKU)..."
              emptyLabel="Todos"
              testId="report-product"
            />
          </div>
        </div>
      </div>

      {/* View tabs — iter77ct.95: aba ativa com gradiente violeta forte, badge sutil */}
      <div className="flex gap-1 bg-slate-800/95 rounded-xl p-1.5 overflow-x-auto shadow-lg shadow-slate-900/10">
        {[
          { key: 'overview', label: 'Visao Geral', icon: BarChart3 },
          { key: 'clients', label: 'Por Cliente', icon: Users },
          { key: 'products', label: 'Por Produto', icon: Package },
          { key: 'sales', label: 'Lista de Vendas', icon: FileText },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setView(t.key)}
            data-testid={`view-${t.key}`}
            className={`flex-1 py-2.5 px-3 rounded-lg text-xs font-black transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${view === t.key
              ? 'bg-gradient-to-r from-violet-500 to-indigo-500 text-white shadow-md shadow-violet-500/40'
              : 'text-slate-300 hover:text-white hover:bg-slate-700/60'}`}>
            <t.icon size={13} />{t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="bg-white rounded-2xl p-12 text-center text-gray-400 border border-gray-200">Carregando relatorio...</div> : (
        <>
          {view === 'overview' && (
            <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="overview-view">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-3">Por Forma de Pagamento</p>
              {methods.length === 0 ? <p className="text-sm text-gray-400 text-center py-8">Sem vendas no periodo</p> : (
                <div className="space-y-2">
                  {methods.map(m => {
                    const pct = (m.total / maxMethod) * 100;
                    const share = total > 0 ? (m.total / total) * 100 : 0;
                    // iter77ct.62: chave unica por method_id (2 PIX distintos, cada um sua linha)
                    return (
                      <div key={m.method_id || m.method} className="border border-gray-100 rounded-xl p-3" data-testid={`overview-row-${m.method_id || m.method}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-bold text-gray-900">{m.name || PAY_LABEL[m.method] || m.method}</span>
                          <span className="text-base font-black text-teal-700">{fmtBRL(m.total)}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-1.5"><div className="h-full bg-gradient-to-r from-teal-400 to-teal-600 rounded-full" style={{ width: `${pct}%` }} /></div>
                        <div className="flex items-center justify-between text-[11px] text-gray-400">
                          <span>{m.count} venda{m.count === 1 ? '' : 's'}</span>
                          <span>{share.toFixed(1)}% do total</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {view === 'clients' && (
            <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="clients-view">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-3">Top {byClient.length} Clientes</p>
              {byClient.length === 0 ? <p className="text-sm text-gray-400 text-center py-8">Sem vendas</p> : (
                <div className="space-y-1.5">
                  {byClient.map((c, i) => {
                    const pct = (c.total / maxClient) * 100;
                    return (
                      <div key={c.client_id} className="flex items-center gap-3 px-2 py-1.5 hover:bg-gray-50 rounded-lg" data-testid="client-row">
                        <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-[10px] font-black text-gray-500">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1"><div className="h-full bg-teal-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-black text-gray-900">{fmtBRL(c.total)}</p>
                          <p className="text-[10px] text-gray-400">{c.count} venda{c.count === 1 ? '' : 's'}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {view === 'products' && (
            <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="products-view">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-3">Top {byProduct.length} Produtos</p>
              {byProduct.length === 0 ? <p className="text-sm text-gray-400 text-center py-8">Sem vendas</p> : (
                <div className="space-y-1.5">
                  {byProduct.map((p, i) => {
                    const pct = (p.total / maxProduct) * 100;
                    return (
                      <div key={p.product_id} className="flex items-center gap-3 px-2 py-1.5 hover:bg-gray-50 rounded-lg" data-testid="product-row">
                        <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-[10px] font-black text-gray-500">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1"><div className="h-full bg-amber-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-black text-gray-900">{fmtBRL(p.total)}</p>
                          <p className="text-[10px] text-gray-400">{p.quantity} un.</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {view === 'sales' && (
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="sales-list-view">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider">{sales.length} venda{sales.length === 1 ? '' : 's'} encontrada{sales.length === 1 ? '' : 's'}{synthetic ? ` · ${dailyRows.length} dia(s)` : ''}</p>
                <div className="flex items-center gap-3">
                  {/* iter156ga: sintetico por dia (marcado) x analitico (desmarcado) */}
                  <label className={`inline-flex items-center gap-1.5 cursor-pointer select-none px-2 py-1 rounded-full border text-[10px] font-black uppercase tracking-wide transition-colors ${synthetic ? 'bg-violet-600 border-violet-600 text-white' : 'bg-white border-slate-200 text-slate-500 hover:border-violet-300'}`} data-testid="sales-synthetic-toggle">
                    <input type="checkbox" checked={synthetic} onChange={e => setSynthetic(e.target.checked)} className="accent-violet-600 w-3 h-3" data-testid="sales-synthetic-checkbox" />
                    Sintetico por dia
                  </label>
                  <button onClick={exportCSV} disabled={sales.length === 0} className="sm:hidden text-[11px] text-teal-700 font-bold flex items-center gap-1"><Download size={12} />CSV</button>
                </div>
              </div>
              {sales.length === 0 ? <p className="text-sm text-gray-400 text-center py-12">Sem vendas para os filtros aplicados</p> : synthetic ? (
                <div className="overflow-x-auto" data-testid="sales-daily-table">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50"><tr>
                      {[['Dia', 'left'], ['Vendas', 'right'], ['Itens', 'right'], ['Desc.', 'right'], ['Acresc.', 'right'], ['Frete', 'right'], ['Total', 'right'], ['Ticket medio', 'right']].map(([l, a]) => (
                        <th key={l} className={`px-3 py-2 text-[10px] font-black uppercase text-gray-500 ${a === 'right' ? 'text-right' : 'text-left'}`}>{l}</th>
                      ))}
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {dailyRows.map(d => (
                        <tr key={d.day} className="hover:bg-gray-50" data-testid={`sales-daily-row-${d.day}`}>
                          <td className="px-3 py-2 text-xs font-bold text-gray-900">{fmtDateBR(d.day)} <span className="text-[10px] font-normal text-gray-400">{d.weekday}</span></td>
                          <td className="px-3 py-2 text-right text-gray-700 tabular-nums">{d.count}</td>
                          <td className="px-3 py-2 text-right text-gray-600 tabular-nums">{d.items}</td>
                          <td className="px-3 py-2 text-right text-rose-700 font-semibold tabular-nums">{d.discount > 0 ? `-${fmtBRL(d.discount)}` : '—'}</td>
                          <td className="px-3 py-2 text-right text-emerald-700 font-semibold tabular-nums">{d.surcharge > 0 ? `+${fmtBRL(d.surcharge)}` : '—'}</td>
                          <td className="px-3 py-2 text-right text-sky-700 font-semibold tabular-nums">{d.freight > 0 ? fmtBRL(d.freight) : '—'}</td>
                          <td className="px-3 py-2 text-right font-black text-gray-900 tabular-nums" data-testid={`sales-daily-total-${d.day}`}>{fmtBRL(d.total)}</td>
                          <td className="px-3 py-2 text-right text-gray-500 tabular-nums">{fmtBRL(d.count ? d.total / d.count : 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t-2 border-slate-200"><tr className="font-black text-gray-900">
                      <td className="px-3 py-2 text-xs uppercase">Total do periodo</td>
                      <td className="px-3 py-2 text-right tabular-nums">{dailyRows.reduce((a, d) => a + d.count, 0)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{dailyRows.reduce((a, d) => a + d.items, 0)}</td>
                      <td className="px-3 py-2 text-right text-rose-700 tabular-nums">{fmtBRL(dailyRows.reduce((a, d) => a + d.discount, 0))}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 tabular-nums">{fmtBRL(dailyRows.reduce((a, d) => a + d.surcharge, 0))}</td>
                      <td className="px-3 py-2 text-right text-sky-700 tabular-nums">{fmtBRL(dailyRows.reduce((a, d) => a + d.freight, 0))}</td>
                      <td className="px-3 py-2 text-right tabular-nums" data-testid="sales-daily-grand-total">{fmtBRL(dailyRows.reduce((a, d) => a + d.total, 0))}</td>
                      <td className="px-3 py-2 text-right text-gray-500 tabular-nums">{fmtBRL((() => { const c = dailyRows.reduce((a, d) => a + d.count, 0); return c ? dailyRows.reduce((a, d) => a + d.total, 0) / c : 0; })())}</td>
                    </tr></tfoot>
                  </table>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50"><tr>
                      {[
                        { key: 'number', label: '#', align: 'left' },
                        { key: 'date', label: 'Data', align: 'left' },
                        { key: 'customer', label: 'Cliente', align: 'left' },
                        { key: 'payment', label: 'Pgto', align: 'left' },
                        { key: 'items', label: 'Itens', align: 'right' },
                        { key: 'discount', label: 'Desc.', align: 'right' },
                        { key: 'surcharge', label: 'Acresc.', align: 'right' },
                        { key: 'freight', label: 'Frete', align: 'right' },
                        { key: 'total', label: 'Total', align: 'right' },
                      ].map(col => {
                        const active = sortBy === col.key;
                        const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '↕';
                        const alignCls = col.align === 'right' ? 'text-right' : 'text-left';
                        return (
                          <th key={col.key} className={`px-3 py-2 ${alignCls} text-[10px] font-black uppercase select-none cursor-pointer hover:bg-gray-100 transition-colors ${active ? 'text-teal-700' : 'text-gray-500'}`}
                            onClick={() => toggleSort(col.key)} data-testid={`sales-sort-${col.key}`}>
                            <span className="inline-flex items-center gap-1">
                              {col.label}
                              <span className={`text-[9px] ${active ? 'opacity-100' : 'opacity-30'}`}>{arrow}</span>
                            </span>
                          </th>
                        );
                      })}
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {sortedSales.map(s => (
                        <tr key={s.id} className="hover:bg-gray-50" data-testid="report-sale-row">
                          <td className="px-3 py-2 font-mono text-xs text-gray-500">{s.number}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{new Date(s.created_at).toLocaleDateString('pt-BR')}</td>
                          <td className="px-3 py-2 text-gray-900 max-w-[180px] truncate">{s.customer_name || 'Consumidor'}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 uppercase"><PaymentBadge sale={s} /></td>
                          <td className="px-3 py-2 text-right text-gray-600">{s.items?.length || 0}</td>
                          {/* iter156ca: colunas Desc / Acresc / Frete inseridos na hora da venda */}
                          <td className="px-3 py-2 text-right text-rose-700 font-semibold tabular-nums" data-testid={`sale-discount-${s.number}`}>{Number(s.discount || 0) > 0 ? `-${fmtBRL(s.discount)}` : '—'}</td>
                          <td className="px-3 py-2 text-right text-emerald-700 font-semibold tabular-nums" data-testid={`sale-surcharge-${s.number}`}>{Number(s.surcharge || 0) > 0 ? `+${fmtBRL(s.surcharge)}` : '—'}</td>
                          <td className="px-3 py-2 text-right text-sky-700 font-semibold tabular-nums" data-testid={`sale-freight-${s.number}`}>{Number(s.freight || 0) > 0 ? fmtBRL(s.freight) : '—'}</td>
                          <td className="px-3 py-2 text-right font-bold text-gray-900" title={isPMFiltered && s.filtered_amount != null && s.filtered_amount !== s.total ? `Valor total da venda: ${fmtBRL(s.total)} · Parcela nesta forma: ${fmtBRL(s.filtered_amount)}` : ''}>
                            {fmtBRL(displayTotal(s))}
                            {isPMFiltered && s.filtered_amount != null && s.filtered_amount !== s.total && (
                              <div className="text-[9px] font-normal text-gray-400 mt-0.5">de {fmtBRL(s.total)}</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ========== FINANCIAL PAGE (Receivables/Payables) ========== */
const FinancialPage = ({ companyId, type, title }) => {
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ description: '', amount: '', due_date: '', category: '', payment_method: '' });
  const [submitting, setSubmitting] = useState(false);
  const fetchEntries = useCallback(async () => {
    if (!companyId) { setLoading(false); return; }
    try { const { data } = await axios.get(`${ERP}/financial?company_id=${companyId}&type=${type}`, { withCredentials: true }); setEntries(data.data || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, [companyId, type]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try { await axios.post(`${ERP}/financial`, { company_id: companyId, type, ...form, amount: parseFloat(form.amount) || 0 }, { withCredentials: true }); toast.success('Lançamento criado!'); setShowForm(false); setForm({ description: '', amount: '', due_date: '', category: '', payment_method: '' }); fetchEntries(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSubmitting(false); }
  };
  const markPaid = async (id) => {
    try { await axios.put(`${ERP}/financial/${id}/pay`, { payment_method: 'manual' }, { withCredentials: true }); toast.success('Marcado como pago!'); fetchEntries(); }
    catch { toast.error('Erro'); }
  };
  if (!companyId) return <NoCompany />;
  if (loading) return <Loader />;
  const totalPending = entries.filter(e => e.status === 'pending').reduce((s, e) => s + (e.amount || 0), 0);
  const totalPaid = entries.filter(e => e.status === 'paid').reduce((s, e) => s + (e.amount || 0), 0);
  return (
    <div data-testid={`${type}-page`}>
      <div className="grid grid-cols-2 gap-4 mb-5">
        <div className={`bg-white border rounded-xl p-4 text-center ${type === 'receivable' ? 'border-amber-200' : 'border-red-200'}`}><p className="text-xs text-gray-500">Pendente</p><p className="text-xl font-bold text-amber-600">R$ {totalPending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p></div>
        <div className="bg-white border border-green-200 rounded-xl p-4 text-center"><p className="text-xs text-gray-500">{type === 'receivable' ? 'Recebido' : 'Pago'}</p><p className="text-xl font-bold text-green-600">R$ {totalPaid.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p></div>
      </div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{entries.length} lançamentos</p>
        <button onClick={() => setShowForm(!showForm)} className={btnPrimary} data-testid={`add-${type}-btn`}><Plus size={16} /><span>Novo Lançamento</span></button>
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-teal-200 rounded-xl p-5 mb-5" data-testid="financial-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Descrição *</label><input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className={inputClass} required placeholder="Ex: Pagamento fornecedor" data-testid="fin-description" /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Valor (R$) *</label><input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className={inputClass} required data-testid="fin-amount" /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Vencimento *</label><input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className={inputClass} required /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Categoria</label><input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className={inputClass} placeholder="Ex: Vendas" /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Forma Pgto</label><input value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} className={inputClass} placeholder="Ex: PIX" /></div>
          </div>
          <div className="flex gap-2"><button type="submit" disabled={submitting} className={btnPrimary}><Save size={14} /><span>{submitting ? 'Salvando...' : 'Criar'}</span></button><button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 text-sm text-gray-500">Cancelar</button></div>
        </form>
      )}
      <div className="space-y-2">
        {entries.map(e => (
          <div key={e.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid="financial-row">
            <div>
              <p className="font-medium text-sm text-gray-900">{e.description}</p>
              <p className="text-xs text-gray-500">{e.due_date ? new Date(e.due_date).toLocaleDateString('pt-BR') : ''} {e.category ? `• ${e.category}` : ''}</p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="font-bold text-sm text-gray-900">R$ {(e.amount || 0).toFixed(2)}</span>
              {e.status === 'pending' ? (
                <button onClick={() => markPaid(e.id)} className="text-xs font-semibold px-2 py-1 rounded bg-amber-50 text-amber-700 hover:bg-amber-100" data-testid="mark-paid-btn"><Clock size={12} className="inline mr-1" />Pendente</button>
              ) : e.status === 'paid' ? (
                <span className="text-xs font-semibold px-2 py-1 rounded bg-green-50 text-green-700"><CheckCircle size={12} className="inline mr-1" />Pago</span>
              ) : (
                <span className="text-xs font-semibold px-2 py-1 rounded bg-red-50 text-red-700">Cancelado</span>
              )}
            </div>
          </div>
        ))}
        {entries.length === 0 && <EmptyState icon={FileText} text="Nenhum lançamento" />}
      </div>
    </div>
  );
};

/* ========== CHART OF ACCOUNTS ========== */
const ChartAccountsPage = ({ companies = [] }) => {
  const [accounts, setAccounts] = useState([]);
  // iter156ff: filtros superiores + mesclar duplicadas + compartilhamento por filial
  const [fType, setFType] = useState('');
  const [fDre, setFDre] = useState('');
  const [fSearch, setFSearch] = useState('');
  const [merging, setMerging] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', type: 'income', parent_id: '', dre_include: true, dre_line: '' });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const fetchAll = useCallback(async () => { try { const { data } = await axios.get(`${ERP}/chart-accounts`, { withCredentials: true }); setAccounts(data.data || []); } catch { /* ignore */ } finally { setLoading(false); } }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  const openNew = () => { setEditing(null); setForm({ code: '', name: '', type: 'income', parent_id: '', dre_include: true, dre_line: '', company_ids: [] }); setShowForm(true); };
  const openEdit = (a) => { setEditing(a); setForm({
    code: a.code || '',
    name: a.name || '',
    type: a.type || 'income',
    parent_id: a.parent_id || '',
    dre_include: a.dre_include !== false, // default true (compat legado)
    dre_line: a.dre_line || '',
    company_ids: a.company_ids || [],
  }); setShowForm(true); };
  const save = async () => {
    setSubmitting(true);
    try {
      // iter156dj: normaliza payload — parent_id vazio vira null, dre_line vazio vira null
      const payload = {
        code: form.code,
        name: form.name,
        type: form.type,
        parent_id: form.parent_id || null,
        dre_include: !!form.dre_include,
        dre_line: form.dre_line || null,
        company_ids: form.company_ids || [],
      };
      if (editing) { await axios.put(`${ERP}/chart-accounts/${editing.id}`, payload, { withCredentials: true }); toast.success('Conta atualizada'); }
      else { await axios.post(`${ERP}/chart-accounts`, payload, { withCredentials: true }); toast.success('Conta criada'); }
      setShowForm(false); setEditing(null); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSubmitting(false); }
  };
  const handleDelete = async (id) => { if (!(await confirmDialog({ title: 'Excluir esta conta?', confirmLabel: 'Confirmar', danger: true }))) return; try { await axios.delete(`${ERP}/chart-accounts/${id}`, { withCredentials: true }); toast.success('Excluida'); fetchAll(); } catch { toast.error('Erro'); } };
  const typeLabels = { income: 'Receita', expense: 'Despesa', asset: 'Ativo', liability: 'Passivo' };
  const typeColors = { income: 'bg-emerald-50 text-emerald-700', expense: 'bg-rose-50 text-rose-700', asset: 'bg-sky-50 text-sky-700', liability: 'bg-amber-50 text-amber-700' };
  // iter156dj: opcoes de linha DRE — usadas quando quer mover a conta para outro bucket.
  const DRE_LINES = [
    { v: '', label: 'Automatico (segue o tipo)' },
    { v: 'cmv', label: 'CMV — Custo da Mercadoria Vendida' },
    { v: 'deducao', label: 'Deducao da Receita (impostos sobre vendas, devolucoes)' },
    { v: 'despesa', label: 'Despesa Operacional' },
    { v: 'financeiro', label: 'Despesa Financeira (juros, taxas)' },
    { v: 'impostos_lucro', label: 'Impostos sobre o Lucro (IRPJ, CSLL)' },
    { v: 'outras_receitas', label: 'Outras Receitas' },
  ];
  const dupKeys = (() => { const seen = {}; accounts.forEach(a => { const k = `${(a.code || '').trim()}|${(a.name || '').trim().toLowerCase()}`; seen[k] = (seen[k] || 0) + 1; }); return seen; })();
  const dupCount = Object.values(dupKeys).reduce((n, c) => n + (c > 1 ? c - 1 : 0), 0);
  const visible = accounts.filter(a => {
    if (fType && a.type !== fType) return false;
    if (fDre === 'in' && a.dre_include === false) return false;
    if (fDre === 'out' && a.dre_include !== false) return false;
    if (fDre === 'dup' && dupKeys[`${(a.code || '').trim()}|${(a.name || '').trim().toLowerCase()}`] < 2) return false;
    if (fSearch) { const q = fSearch.toLowerCase(); if (!(`${a.code} ${a.name}`.toLowerCase().includes(q))) return false; }
    return true;
  });
  const mergeDuplicates = async () => {
    if (!(await confirmDialog({ title: 'Mesclar contas duplicadas?', message: `${dupCount} conta(s) duplicada(s) (mesmo codigo e nome) serao unificadas: a mais antiga e mantida e os lancamentos das demais passam a apontar para ela.`, confirmLabel: 'Mesclar', danger: true }))) return;
    setMerging(true);
    try {
      const { data } = await axios.post(`${ERP}/chart-accounts/merge-duplicates`, {}, { withCredentials: true });
      toast.success(`${data.merged_groups} grupo(s) mesclado(s) · ${data.removed} conta(s) removida(s) · ${data.repointed_entries} lancamento(s) reapontado(s)`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao mesclar'); }
    finally { setMerging(false); }
  };
  const companyName = (id) => { const c = companies.find(x => x.id === id); return c ? (c.code ? `${c.code} · ` : '') + (c.nome_fantasia || c.name || c.razao_social || id) : id; };
  const selCls = 'px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white';
  return (
    <div data-testid="chart-accounts-page">
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="chart-filters">
        <div className="relative"><Search size={13} className="absolute left-2 top-2 text-slate-400" /><input value={fSearch} onChange={e => setFSearch(e.target.value)} placeholder="Buscar codigo ou nome..." className={selCls + ' pl-7 w-52'} data-testid="chart-filter-search" /></div>
        <select value={fType} onChange={e => setFType(e.target.value)} className={selCls} data-testid="chart-filter-type">
          <option value="">Tipo: todos</option><option value="income">Receita</option><option value="expense">Despesa</option><option value="asset">Ativo</option><option value="liability">Passivo</option>
        </select>
        <select value={fDre} onChange={e => setFDre(e.target.value)} className={selCls} data-testid="chart-filter-dre">
          <option value="">DRE: todas</option><option value="in">Exibe na DRE</option><option value="out">Fora da DRE</option><option value="dup">Somente duplicadas</option>
        </select>
        <p className="text-xs text-slate-500">{visible.length} de {accounts.length} contas</p>
        <div className="ml-auto flex items-center gap-2">
          {dupCount > 0 && <button onClick={mergeDuplicates} disabled={merging} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50" data-testid="chart-merge-dups-btn">{merging ? 'Mesclando...' : `Mesclar ${dupCount} duplicada(s)`}</button>}
          <button onClick={openNew} className={btnPrimary} data-testid="chart-new-btn"><Plus size={14} /><span>Nova Conta</span></button>
        </div>
      </div>
      {loading ? <Loader /> : (
      <div className="space-y-2">
        {visible.map(a => (
          <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between" data-testid={`chart-row-${a.id}`}>
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <span className="text-xs font-mono text-slate-500 flex-shrink-0">{a.code}</span>
              <span className="font-medium text-sm text-slate-900 truncate">{a.name}</span>
              {/* iter156dj: badges informativos de exposicao na DRE */}
              {a.dre_include === false && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 uppercase" title="Nao entra na DRE">Fora DRE</span>}
              {a.dre_line && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 uppercase" title={`Reclassificada para ${a.dre_line}`}>DRE: {a.dre_line}</span>}
              {(a.company_ids || []).length > 0
                ? <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 uppercase inline-flex items-center gap-1" title={`Compartilhada com: ${a.company_ids.map(companyName).join(', ')}`} data-testid={`chart-share-${a.id}`}><Building2 size={10} /> {a.company_ids.length} filial(is)</span>
                : <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 uppercase inline-flex items-center gap-1" title="Compartilhada com todas as filiais"><Building2 size={10} /> Todas</span>}
              {dupKeys[`${(a.code || '').trim()}|${(a.name || '').trim().toLowerCase()}`] > 1 && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 uppercase">Duplicada</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] font-bold px-2 py-1 rounded ${typeColors[a.type] || ''}`}>{typeLabels[a.type] || a.type}</span>
              <button onClick={() => openEdit(a)} className="p-1.5 text-slate-400 hover:text-violet-600" data-testid={`chart-edit-${a.id}`}><Edit size={13} /></button>
              <button onClick={() => handleDelete(a.id)} className="p-1.5 text-slate-400 hover:text-rose-500" data-testid={`chart-del-${a.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {visible.length === 0 && <EmptyState icon={FileText} text={accounts.length === 0 ? 'Nenhum plano de contas cadastrado' : 'Nenhuma conta com esses filtros'} />}
      </div>)}
      <FormModal open={showForm} title={editing ? 'Editar Conta' : 'Inserir Conta'} icon={<FileText size={18} />}
        onClose={() => { setShowForm(false); setEditing(null); }} onSave={save} submitting={submitting}
        saveLabel={editing ? 'Atualizar' : 'Salvar'} testId="chart-account-modal">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">* Codigo</label>
            <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} className={inputClass} required placeholder="1.1.01" data-testid="chart-form-code" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">* Nome</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} required placeholder="Caixa" data-testid="chart-form-name" />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-bold text-slate-600 mb-1">Tipo</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className={inputClass} data-testid="chart-form-type">
              <option value="income">Receita</option>
              <option value="expense">Despesa</option>
              <option value="asset">Ativo</option>
              <option value="liability">Passivo</option>
            </select>
          </div>
          {/* iter156dj: Conta Pai — permite montar hierarquia. Filtra a propria conta em edicao. */}
          <div className="md:col-span-3">
            <label className="block text-xs font-bold text-slate-600 mb-1">Conta Pai (opcional)</label>
            <select value={form.parent_id} onChange={e => setForm({ ...form, parent_id: e.target.value })} className={inputClass} data-testid="chart-form-parent">
              <option value="">— Sem conta pai (raiz) —</option>
              {accounts.filter(a => !editing || a.id !== editing.id).map(a => (
                <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
              ))}
            </select>
            <p className="text-[10px] text-slate-400 mt-0.5">Use para agrupar contas (ex: 3.1 Receitas Operacionais &rarr; 3.1.01 Vendas).</p>
          </div>
          {/* iter156ff: compartilhamento por filial — vazio = todas */}
          <div className="md:col-span-3 bg-sky-50/40 border-2 border-sky-100 rounded-xl p-3" data-testid="chart-form-share-block">
            <label className="flex items-center gap-2 mb-2 cursor-pointer">
              <input type="checkbox" checked={(form.company_ids || []).length === 0} onChange={e => setForm({ ...form, company_ids: e.target.checked ? [] : [companies[0]?.id].filter(Boolean) })} className="w-4 h-4 accent-sky-600" data-testid="chart-form-share-all" />
              <span className="text-xs font-bold text-slate-800 inline-flex items-center gap-1"><Building2 size={13} /> Compartilhar com todas as filiais</span>
            </label>
            {(form.company_ids || []).length > 0 && (
              <div className="ml-6 grid grid-cols-1 sm:grid-cols-2 gap-1">
                {companies.map(c => (
                  <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={form.company_ids.includes(c.id)} onChange={e => setForm({ ...form, company_ids: e.target.checked ? [...form.company_ids, c.id] : form.company_ids.filter(x => x !== c.id) })} className="accent-sky-600" data-testid={`chart-form-share-${c.id}`} />
                    <span>{companyName(c.id)}</span>
                  </label>
                ))}
                <p className="sm:col-span-2 text-[10px] text-slate-400">Desmarcado "todas": a conta so aparece nos lancamentos das filiais selecionadas.</p>
              </div>
            )}
          </div>
          {/* iter156dj: bloco DRE — controla se conta aparece e onde */}
          <div className="md:col-span-3 bg-violet-50/40 border-2 border-violet-100 rounded-xl p-3">
            <label className="flex items-center gap-2 mb-2 cursor-pointer">
              <input type="checkbox" checked={!!form.dre_include} onChange={e => setForm({ ...form, dre_include: e.target.checked })} className="w-4 h-4 accent-violet-600" data-testid="chart-form-dre-include" />
              <span className="text-xs font-bold text-slate-800">Exibir esta conta na DRE</span>
            </label>
            <p className="text-[10px] text-slate-500 mb-2 ml-6">Desmarque quando os lancamentos desta conta NAO devem aparecer no resultado da DRE (ex: transferencias internas, aportes de socio).</p>
            <label className="block text-xs font-bold text-slate-600 mb-1">Linha DRE (opcional — muda o &ldquo;lugar&rdquo; da conta)</label>
            <select value={form.dre_line} onChange={e => setForm({ ...form, dre_line: e.target.value })} className={inputClass} disabled={!form.dre_include} data-testid="chart-form-dre-line">
              {DRE_LINES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <p className="text-[10px] text-slate-400 mt-1">Em branco: a conta entra na DRE pelo tipo (Despesa &rarr; despesa operacional; Receita &rarr; outras receitas). Escolha uma linha so para tirar a conta do lugar padrao.</p>
          </div>
        </div>
      </FormModal>
    </div>
  );
};

/* ========== BANK ACCOUNTS ========== */
const BankAccountsPage = ({ companyId }) => {
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const _blankBankForm = { bank_name: '', agency: '', account_number: '', initial_balance: '', selectable_in_pdv: false, show_balance_in_pdv: false, default_transfer_from: false, default_transfer_to: false, default_expense: false };
  const [form, setForm] = useState(_blankBankForm);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const fetchAll = useCallback(async () => { if (!companyId) { setLoading(false); return; } try { const { data } = await axios.get(`${ERP}/bank-accounts?company_id=${companyId}`, { withCredentials: true }); setAccounts(data.data || []); } catch { /* ignore */ } finally { setLoading(false); } }, [companyId]);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  const openNew = () => { setEditing(null); setForm(_blankBankForm); setShowForm(true); };
  const openEdit = (a) => { setEditing(a); setForm({ bank_name: a.bank_name || '', agency: a.agency || '', account_number: a.account_number || '', initial_balance: a.balance ?? '', selectable_in_pdv: !!(a.selectable_in_pdv ?? a.show_in_pdv), show_balance_in_pdv: !!(a.show_balance_in_pdv ?? a.show_in_pdv), default_transfer_from: !!a.default_transfer_from, default_transfer_to: !!a.default_transfer_to, default_expense: !!a.default_expense }); setShowForm(true); };
  const save = async () => {
    setSubmitting(true);
    try {
      const balanceNum = parseFloat(form.initial_balance) || 0;
      if (editing) {
        // iter77ct.12: PUT usa `balance` (nao `initial_balance` — que so vale no POST).
        // Antes o saldo editado nao persistia porque o backend ignorava `initial_balance`.
        const putPayload = {
          bank_name: form.bank_name,
          agency: form.agency,
          account_number: form.account_number,
          balance: balanceNum,
          selectable_in_pdv: !!form.selectable_in_pdv,
          show_balance_in_pdv: !!form.show_balance_in_pdv,
          // iter156y: flags de padrao (autopreenchimento em Transferencia/Despesa)
          default_transfer_from: !!form.default_transfer_from,
          default_transfer_to: !!form.default_transfer_to,
          default_expense: !!form.default_expense,
        };
        await axios.put(`${ERP}/bank-accounts/${editing.id}`, putPayload, { withCredentials: true });
        toast.success('Conta atualizada');
      } else {
        const postPayload = { company_id: companyId, ...form, initial_balance: balanceNum, selectable_in_pdv: !!form.selectable_in_pdv, show_balance_in_pdv: !!form.show_balance_in_pdv };
        await axios.post(`${ERP}/bank-accounts`, postPayload, { withCredentials: true });
        toast.success('Conta criada');
      }
      setShowForm(false); setEditing(null); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSubmitting(false); }
  };
  const handleDelete = async (id) => { if (!(await confirmDialog({ title: 'Excluir esta conta bancaria?', confirmLabel: 'Confirmar', danger: true }))) return; try { await axios.delete(`${ERP}/bank-accounts/${id}`, { withCredentials: true }); toast.success('Excluida'); fetchAll(); } catch { toast.error('Erro'); } };
  if (!companyId) return <NoCompany />;
  return (
    <div data-testid="bank-accounts-page">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-slate-500">{accounts.length} contas</p>
        <button onClick={openNew} className={btnPrimary} data-testid="bank-new-btn"><Plus size={14} /><span>Nova Conta</span></button>
      </div>
      {loading ? <Loader /> : (
      <div className="space-y-2">
        {accounts.map(a => (
          <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between" data-testid={`bank-row-${a.id}`}>
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <Landmark size={18} className="text-violet-600 flex-shrink-0" />
              <div className="min-w-0"><p className="font-medium text-sm text-slate-900 truncate">{a.bank_name}{a.is_default ? ' · padrao' : ''}</p><p className="text-xs text-slate-500">Ag: {a.agency || '-'} · CC: {a.account_number || '-'}</p></div>
            </div>
            <div className="flex items-center gap-2">
              <p className="font-bold text-sm text-slate-900 hidden sm:block">R$ {(a.balance || 0).toFixed(2)}</p>
              <button onClick={() => openEdit(a)} className="p-1.5 text-slate-400 hover:text-violet-600" data-testid={`bank-edit-${a.id}`}><Edit size={13} /></button>
              <button onClick={() => handleDelete(a.id)} className="p-1.5 text-slate-400 hover:text-rose-500" data-testid={`bank-del-${a.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {accounts.length === 0 && <EmptyState icon={Landmark} text="Nenhuma conta bancaria" />}
      </div>)}
      <FormModal open={showForm} title={editing ? 'Editar Conta Banco' : 'Inserir Conta Banco'} icon={<Landmark size={18} />}
        onClose={() => { setShowForm(false); setEditing(null); }} onSave={save} submitting={submitting}
        saveLabel={editing ? 'Atualizar' : 'Salvar'} testId="bank-account-modal">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">* Banco</label>
            <input value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} className={inputClass} required placeholder="Banco do Brasil" data-testid="bank-form-name" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Agencia</label>
            <input value={form.agency} onChange={e => setForm({ ...form, agency: e.target.value })} className={inputClass} placeholder="0001" data-testid="bank-form-agency" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Conta</label>
            <input value={form.account_number} onChange={e => setForm({ ...form, account_number: e.target.value })} className={inputClass} placeholder="12345-6" data-testid="bank-form-number" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">Saldo {editing ? 'Atual' : 'Inicial'}</label>
            <input type="number" step="0.01" value={form.initial_balance} onChange={e => setForm({ ...form, initial_balance: e.target.value })} className={inputClass} placeholder="0,00" data-testid="bank-form-balance" />
          </div>
          {/* iter77bw: dois flags separados — 1) selecionavel para uso em Despesa/Transferencia/Fechamento, 2) exibir saldo no header do PDV */}
          <div className="md:col-span-2 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer bg-violet-50/70 border border-violet-200 rounded-lg p-2.5">
              <input type="checkbox" checked={!!form.selectable_in_pdv} onChange={e => setForm({ ...form, selectable_in_pdv: e.target.checked })} className="w-4 h-4 accent-violet-600" data-testid="bank-form-selectable-in-pdv" />
              <div className="flex-1">
                <p className="text-xs font-bold text-slate-700">Aparecer para selecao no PDV</p>
                <p className="text-[10px] text-slate-500">Conta fica disponivel nos dropdowns de Lancamento de Despesa, Transferencia e Fechamento de Caixa.</p>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer bg-cyan-50/70 border border-cyan-200 rounded-lg p-2.5">
              <input type="checkbox" checked={!!form.show_balance_in_pdv} onChange={e => setForm({ ...form, show_balance_in_pdv: e.target.checked })} className="w-4 h-4 accent-cyan-600" data-testid="bank-form-show-balance-in-pdv" />
              <div className="flex-1">
                <p className="text-xs font-bold text-slate-700">Exibir saldo no header do PDV</p>
                <p className="text-[10px] text-slate-500">Saldo desta conta fica visivel no topo do PDV para acompanhamento em tempo real.</p>
              </div>
            </label>
            {/* iter156y: 3 flags de padrao — 1 conta por company_id pode ser padrao para cada tipo.
                O backend enforca unicidade (desmarca outras antes de marcar esta). */}
            <div className="pt-2 mt-1 border-t border-slate-200">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">Autopreenchimento nos modais</p>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer bg-sky-50/70 border border-sky-200 rounded-lg p-2.5">
                  <input type="checkbox" checked={!!form.default_transfer_from} onChange={e => setForm({ ...form, default_transfer_from: e.target.checked })} className="w-4 h-4 accent-sky-600" data-testid="bank-form-default-transfer-from" />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-700">Padrao em <span className="text-sky-700">DE (ORIGEM)</span> — Transferencia entre Contas</p>
                    <p className="text-[10px] text-slate-500">Ao abrir o modal &quot;Transferencia entre Contas&quot;, esta conta ja vira pre-selecionada como origem.</p>
                  </div>
                </label>
                <label className="flex items-center gap-2 cursor-pointer bg-indigo-50/70 border border-indigo-200 rounded-lg p-2.5">
                  <input type="checkbox" checked={!!form.default_transfer_to} onChange={e => setForm({ ...form, default_transfer_to: e.target.checked })} className="w-4 h-4 accent-indigo-600" data-testid="bank-form-default-transfer-to" />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-700">Padrao em <span className="text-indigo-700">PARA (DESTINO)</span> — Transferencia entre Contas</p>
                    <p className="text-[10px] text-slate-500">Ao abrir o modal, esta conta ja vira pre-selecionada como destino.</p>
                  </div>
                </label>
                <label className="flex items-center gap-2 cursor-pointer bg-rose-50/70 border border-rose-200 rounded-lg p-2.5">
                  <input type="checkbox" checked={!!form.default_expense} onChange={e => setForm({ ...form, default_expense: e.target.checked })} className="w-4 h-4 accent-rose-600" data-testid="bank-form-default-expense" />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-700">Padrao em <span className="text-rose-700">CONTA BANCO/CAIXA</span> — Lancamento de Despesa</p>
                    <p className="text-[10px] text-slate-500">Ao abrir o modal &quot;Lancamento de Despesa&quot;, esta conta ja vira pre-selecionada.</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </FormModal>
    </div>
  );
};

/* ========== PAYMENT METHODS ========== */
const PaymentMethodsPage = ({ companyId }) => {
  const [methods, setMethods] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [companies, setCompanies] = useState([]);          // iter77bq: todas as filiais
  const [bankAccountsByCompany, setBankAccountsByCompany] = useState({}); // iter77bq: { company_id: [banks] }
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', type: 'dinheiro', active: true, bank_account_id: '',
    available_in_sales: true, available_in_purchases: true, available_in_financial: true, available_in_invoices: true,
    is_cashback: false,
    owner_company_id: '',          // iter77bq
    shared_with: [],               // iter77bq: [{ company_id, bank_account_id }]
    fee_percent: 0,                // iter77ct.102: taxa da operadora (%)
    fee_tiers: [],                 // iter156ep: [{installments, fee_percent}]
    show_fee_in_sales: false,      // iter156et: exibir taxa/liquido no PDV
    default_for_expenses: false,   // iter156gl: pre-selecionada ao lancar despesa (PDV / Contas a Pagar)
  });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  // iter77af: include_inactive=true para a tela de gestao mostrar inativas (reativar)
  const fetchMethods = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ERP}/payment-methods?include_inactive=true`, { withCredentials: true });
      setMethods(data.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  // Carrega contas banco da empresa atual (para o dropdown legado de bank_account_id)
  useEffect(() => {
    const f = async () => {
      if (!companyId) { setBankAccounts([]); return; }
      try {
        const { data } = await axios.get(`${ERP}/bank-accounts?company_id=${companyId}`, { withCredentials: true });
        setBankAccounts(data?.data || []);
      } catch { setBankAccounts([]); }
    };
    f();
  }, [companyId]);
  // iter77bq: carrega TODAS as filiais + contas banco de cada uma
  useEffect(() => {
    const loadAll = async () => {
      try {
        const { data } = await axios.get(`${ERP}/companies`, { withCredentials: true });
        const comps = data?.data || [];
        setCompanies(comps);
        // Para cada filial, busca contas banco em paralelo
        const banksMap = {};
        await Promise.all(comps.map(async (c) => {
          try {
            const r = await axios.get(`${ERP}/bank-accounts?company_id=${c.id}`, { withCredentials: true });
            banksMap[c.id] = r.data?.data || [];
          } catch { banksMap[c.id] = []; }
        }));
        setBankAccountsByCompany(banksMap);
      } catch { setCompanies([]); setBankAccountsByCompany({}); }
    };
    loadAll();
  }, []);
  useEffect(() => { fetchMethods(); }, [fetchMethods]);
  // iter77bq: helper — Caixa Local default de uma filial
  const defaultBankOf = (cid) => {
    const list = bankAccountsByCompany[cid] || [];
    return list.find(b => b.is_default_cash || b.is_default) || list[0] || null;
  };
  const openNew = () => {
    setEditing(null);
    // iter77bq: defaults — matriz como owner, compartilha com matriz no Caixa Local
    const matriz = companies.find(c => c.is_matriz) || companies[0];
    const matrizDefBank = matriz ? defaultBankOf(matriz.id) : null;
    setForm({
      name: '', type: 'dinheiro', active: true,
      bank_account_id: matrizDefBank?.id || '',
      available_in_sales: true, available_in_purchases: true, available_in_financial: true, available_in_invoices: true,
      is_cashback: false,
      owner_company_id: matriz?.id || '',
      shared_with: matriz ? [{ company_id: matriz.id, bank_account_id: matrizDefBank?.id || '' }] : [],
      fee_percent: 0,
      fee_tiers: [],
      show_fee_in_sales: false,
      default_for_expenses: !methods.some(m => m.default_for_expenses), // iter156gl: Dinheiro nasce como padrao se ainda nao houver um
    });
    setShowForm(true);
  };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name || '',
      type: m.type || 'dinheiro',
      active: m.active !== false,
      bank_account_id: m.bank_account_id || '',
      available_in_sales: m.available_in_sales !== false,
      available_in_purchases: m.available_in_purchases !== false,
      available_in_financial: m.available_in_financial !== false,
      available_in_invoices: m.available_in_invoices !== false,  // iter156dt5
      is_cashback: !!m.is_cashback,
      owner_company_id: m.owner_company_id || '',
      shared_with: Array.isArray(m.shared_with) ? m.shared_with.map(s => ({ company_id: s.company_id, bank_account_id: s.bank_account_id || '' })) : [],
      fee_percent: Number(m.fee_percent || 0),
      fee_tiers: (Array.isArray(m.fee_tiers) && m.fee_tiers.length > 0)
        ? m.fee_tiers.map(t => ({ installments: Number(t.installments) || 1, fee_percent: Number(t.fee_percent) || 0 }))
        : (Number(m.fee_percent) > 0 ? [{ installments: 1, fee_percent: Number(m.fee_percent) }] : []),
      show_fee_in_sales: !!m.show_fee_in_sales,
      default_for_expenses: !!m.default_for_expenses,
    });
    setShowForm(true);
  };
  // iter77bq: toggle compartilhamento por filial
  const toggleShareCompany = (cid) => {
    const idx = form.shared_with.findIndex(s => s.company_id === cid);
    if (idx >= 0) {
      // Desmarcar — remove
      const next = form.shared_with.filter(s => s.company_id !== cid);
      setForm({ ...form, shared_with: next });
    } else {
      // Marcar — adiciona com Caixa Local default da filial
      const defBank = defaultBankOf(cid);
      const next = [...form.shared_with, { company_id: cid, bank_account_id: defBank?.id || '' }];
      setForm({ ...form, shared_with: next });
    }
  };
  // iter77bq: muda conta banco da entry shared_with daquela filial
  const setShareBankAccount = (cid, bankId) => {
    const next = form.shared_with.map(s => s.company_id === cid ? { ...s, bank_account_id: bankId } : s);
    setForm({ ...form, shared_with: next });
  };
  // iter77bq: compartilhar com todas as filiais (cada uma com seu Caixa Local)
  const shareWithAll = () => {
    const next = companies.map(c => {
      const existing = form.shared_with.find(s => s.company_id === c.id);
      if (existing) return existing;
      const defBank = defaultBankOf(c.id);
      return { company_id: c.id, bank_account_id: defBank?.id || '' };
    });
    setForm({ ...form, shared_with: next });
  };
  const save = async () => {
    setSubmitting(true);
    try {
      if (editing) { await axios.put(`${ERP}/payment-methods/${editing.id}`, form, { withCredentials: true }); toast.success('Atualizada'); }
      else { await axios.post(`${ERP}/payment-methods`, form, { withCredentials: true }); toast.success('Criada'); }
      setShowForm(false); setEditing(null); fetchMethods();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSubmitting(false); }
  };
  const handleDelete = async (m) => {
    if (!(await confirmDialog({ title: `Desativar "${m.name}"?\n\nNao aparecera em PDV, Compras ou Financeiro. Historico nos relatorios preservado. Voce pode reativar a qualquer momento.`, confirmLabel: 'Confirmar', danger: true }))) return;
    try { await axios.delete(`${ERP}/payment-methods/${m.id}`, { withCredentials: true }); toast.success('Desativada'); fetchMethods(); }
    catch { toast.error('Erro'); }
  };
  const toggleActive = async (m) => {
    try {
      await axios.put(`${ERP}/payment-methods/${m.id}`, { ...m, active: !m.active }, { withCredentials: true });
      toast.success(m.active ? 'Desativada' : 'Ativada');
      fetchMethods();
    } catch { toast.error('Erro'); }
  };
  return (
    <div data-testid="payment-methods-page">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-slate-500">{methods.length} formas <span className="text-slate-400">({methods.filter(m => m.active !== false).length} ativas)</span></p>
        <button onClick={openNew} className={btnPrimary} data-testid="pm-new-btn"><Plus size={14} /><span>Nova Forma</span></button>
      </div>
      {loading ? <Loader /> : (
      <div className="space-y-2">
        {methods.map((m, mi) => {
          const isInactive = m.active === false;
          // iter156gc: reordenar (ordem reflete no PDV)
          const move = async (dir) => {
            const j = mi + dir;
            if (j < 0 || j >= methods.length) return;
            const arr = methods.slice();
            [arr[mi], arr[j]] = [arr[j], arr[mi]];
            setMethods(arr);
            try { await axios.post(`${ERP}/payment-methods/reorder`, { ids: arr.map(x => x.id) }, { withCredentials: true }); }
            catch { toast.error('Erro ao salvar ordem'); fetchMethods(); }
          };
          return (
          <div key={m.id} className={`bg-white border rounded-xl p-3 flex items-center justify-between ${isInactive ? 'border-slate-200 opacity-60' : 'border-slate-200'}`} data-testid={`pm-row-${m.id}`}>
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="flex flex-col -my-1 shrink-0">
                <button type="button" onClick={() => move(-1)} disabled={mi === 0} className="p-0.5 text-slate-400 hover:text-violet-600 disabled:opacity-20" title="Mover para cima" data-testid={`pm-move-up-${m.id}`}><ChevronUp size={14} /></button>
                <button type="button" onClick={() => move(1)} disabled={mi === methods.length - 1} className="p-0.5 text-slate-400 hover:text-violet-600 disabled:opacity-20" title="Mover para baixo" data-testid={`pm-move-down-${m.id}`}><ChevronDown size={14} /></button>
              </div>
              <CreditCard size={16} className={isInactive ? 'text-slate-400 flex-shrink-0' : 'text-violet-600 flex-shrink-0'} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-slate-900">{m.name}</span>
                  <span className="text-[10px] text-slate-500 capitalize bg-slate-100 px-1.5 py-0.5 rounded">{m.type}</span>
                  {(Array.isArray(m.fee_tiers) && m.fee_tiers.length > 0) ? (
                    <span className="text-[10px] text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded font-mono font-bold" title="Taxa da operadora por parcelas" data-testid={`pm-fee-${m.id}`}>
                      {m.fee_tiers.map(t => `${t.installments}x ${Number(t.fee_percent).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`).join(' · ')}
                    </span>
                  ) : Number(m.fee_percent) > 0 && (
                    <span className="text-[10px] text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded font-mono font-bold" title="Taxa da operadora" data-testid={`pm-fee-${m.id}`}>
                      Taxa {Number(m.fee_percent).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                    </span>
                  )}
                  {m.is_cashback && <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">CASHBACK</span>}
                  {isInactive && <span className="text-[10px] text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">INATIVA</span>}
                </div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  {m.available_in_sales !== false && <span className="text-[9px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">Vendas</span>}
                  {m.available_in_purchases !== false && <span className="text-[9px] text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">Compras</span>}
                  {m.available_in_financial !== false && <span className="text-[9px] text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded">Financeiro</span>}
                  {m.default_for_expenses && <span className="text-[9px] text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded font-bold" data-testid={`pm-badge-default-expense-${m.id}`}>Padrao despesa</span>}
                  {m.available_in_invoices !== false && <span className="text-[9px] text-cyan-700 bg-cyan-50 border border-cyan-200 px-1.5 py-0.5 rounded">NF-e/NFC-e</span>}
                  {/* iter77bq: filiais compartilhadas */}
                  {Array.isArray(m.shared_with) && m.shared_with.length > 0 && (
                    <span className="text-[9px] text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 px-1.5 py-0.5 rounded" data-testid={`pm-share-count-${m.id}`}>
                      {m.shared_with.length} {m.shared_with.length === 1 ? 'filial' : 'filiais'}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button onClick={() => toggleActive(m)} className={`text-[10px] font-bold px-2 py-1 rounded ${isInactive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'}`} data-testid={`pm-toggle-active-${m.id}`}>{isInactive ? 'Ativar' : 'Desativar'}</button>
              <button onClick={() => openEdit(m)} className="p-1.5 text-slate-400 hover:text-violet-600" data-testid={`pm-edit-${m.id}`}><Edit size={13} /></button>
              {!m.is_cashback && <button onClick={() => handleDelete(m)} className="p-1.5 text-slate-400 hover:text-rose-500" data-testid={`pm-del-${m.id}`}><Trash2 size={13} /></button>}
            </div>
          </div>
        );})}
        {methods.length === 0 && <EmptyState icon={CreditCard} text="Nenhuma forma cadastrada" />}
      </div>)}
      <FormModal open={showForm} title={editing ? 'Editar Forma de Pagamento' : 'Inserir Forma de Pagamento'} icon={<CreditCard size={18} />}
        onClose={() => { setShowForm(false); setEditing(null); }} onSave={save} submitting={submitting}
        saveLabel={editing ? 'Atualizar' : 'Salvar'} testId="payment-method-modal">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">* Nome</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} required placeholder="PIX" data-testid="pm-form-name" disabled={form.is_cashback && !!editing} />
            {form.is_cashback && <p className="text-[10px] text-amber-700 mt-1">Forma de sistema (Cashback) — nome bloqueado.</p>}
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Tipo</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value, is_cashback: e.target.value === 'cashback' || form.is_cashback, default_for_expenses: (!editing && e.target.value === 'dinheiro') ? true : form.default_for_expenses })} className={inputClass} data-testid="pm-form-type" disabled={form.is_cashback && !!editing}>
              <option value="dinheiro">Dinheiro</option>
              <option value="credito">Cartao Credito</option>
              <option value="debito">Cartao Debito</option>
              <option value="pix">PIX</option>
              <option value="boleto">Boleto</option>
              <option value="transferencia">Transferencia</option>
              <option value="em_aberto">Em Aberto (Fiado / A Prazo)</option>
              <option value="credito_devolucao">Credito Devolucao</option>
              <option value="cashback">Cashback</option>
              {/* iter156bj: tipo especial — quando selecionado no PDV zera o total da venda (cortesia/bonificacao). */}
              <option value="sem_pagamento">Sem Pagamento (Cortesia)</option>
              <option value="outro">Outro</option>
            </select>
          </div>
          {/* iter77ct.102: taxa da operadora — aparece para tipos que costumam ter taxa (credito/debito/pix/outro).
              Dinheiro, boleto, transferencia, em_aberto, cashback, credito_devolucao ficam ocultos. */}
          {['credito', 'debito', 'pix', 'outro'].includes(form.type) && (
            <div className="md:col-span-2" data-testid="pm-fee-tiers">
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-bold text-slate-600">Taxa da Operadora por Parcelas</label>
                <button
                  type="button"
                  onClick={() => {
                    const used = new Set((form.fee_tiers || []).map(t => Number(t.installments)));
                    let n = 1; while (used.has(n)) n += 1;
                    setForm({ ...form, fee_tiers: [...(form.fee_tiers || []), { installments: n, fee_percent: 0 }] });
                  }}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 px-2.5 py-1 rounded-full transition-colors"
                  data-testid="pm-fee-tier-add"
                ><Plus size={12} /> Adicionar parcela</button>
              </div>
              {(form.fee_tiers || []).length === 0 && (
                <p className="text-[11px] text-slate-400 italic border border-dashed border-slate-300 rounded-lg px-3 py-2">Nenhuma taxa cadastrada. Clique em <strong>Adicionar parcela</strong> para informar a taxa de 1x, 2x, 3x...</p>
              )}
              <div className="space-y-1.5">
                {(form.fee_tiers || []).map((t, idx) => (
                  <div key={idx} className="grid grid-cols-[110px_minmax(0,1fr)_auto] gap-2 items-center bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5" data-testid={`pm-fee-tier-row-${idx}`}>
                    <div className="flex items-center gap-1">
                      <input
                        type="number" min="1" max="48" step="1"
                        value={t.installments}
                        onChange={e => {
                          const v = Math.max(1, Math.min(48, parseInt(e.target.value) || 1));
                          setForm({ ...form, fee_tiers: form.fee_tiers.map((x, i) => i === idx ? { ...x, installments: v } : x) });
                        }}
                        className="w-16 px-2 py-1.5 bg-white border-2 border-slate-300 rounded-lg text-sm font-bold text-center focus:border-violet-600 outline-none"
                        data-testid={`pm-fee-tier-installments-${idx}`}
                      />
                      <span className="text-xs font-black text-slate-500">vez{t.installments > 1 ? 'es' : ''}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number" step="0.01" min="0" max="100"
                        value={t.fee_percent}
                        onChange={e => setForm({ ...form, fee_tiers: form.fee_tiers.map((x, i) => i === idx ? { ...x, fee_percent: Math.max(0, parseFloat(e.target.value) || 0) } : x) })}
                        className="w-full px-2 py-1.5 bg-white border-2 border-slate-300 rounded-lg text-sm font-bold text-right focus:border-violet-600 outline-none"
                        placeholder="Ex: 3,49"
                        data-testid={`pm-fee-tier-percent-${idx}`}
                      />
                      <span className="text-sm font-black text-slate-500">%</span>
                    </div>
                    <button type="button" onClick={() => setForm({ ...form, fee_tiers: form.fee_tiers.filter((_, i) => i !== idx) })} className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg" data-testid={`pm-fee-tier-remove-${idx}`}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 mt-1.5">
                Informe a taxa cobrada pela operadora para cada quantidade de vezes (1x, 2x, 3x...). Nas vendas, o financeiro grava o <strong>valor bruto</strong> e o <strong>valor liquido</strong> (ja descontada a taxa da parcela escolhida). No PDV aparecem <strong>apenas as parcelas cadastradas aqui</strong>.
              </p>
              <label className="flex items-center gap-2 mt-2 p-2 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer" data-testid="pm-show-fee-in-sales-label">
                <input type="checkbox" checked={!!form.show_fee_in_sales} onChange={e => setForm({ ...form, show_fee_in_sales: e.target.checked })} className="accent-violet-600 w-4 h-4" data-testid="pm-show-fee-in-sales" />
                <span className="text-xs text-slate-700"><strong>Exibir taxa e valor liquido no PDV</strong> — se desmarcado, o vendedor ve apenas o <strong>valor da parcela</strong> (taxa fica como informacao interna do financeiro).</span>
              </label>
            </div>
          )}
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">Conta Banco padrao (fallback)</label>
            <select value={form.bank_account_id} onChange={e => setForm({ ...form, bank_account_id: e.target.value })} className={inputClass} data-testid="pm-form-bank-account">
              <option value="">— Sem vinculo —</option>
              {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.bank_name}{b.is_default ? ' (padrao)' : ''}</option>)}
            </select>
            <p className="text-[10px] text-slate-500 mt-1">Conta usada como <strong>fallback</strong> se a filial nao tiver mapeamento especifico abaixo. Recomendado: vincular ao Caixa Local da matriz.</p>
          </div>
          {/* iter77bq: Compartilhamento por filial */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-bold text-slate-600">Compartilhar com filiais</label>
              <button type="button" onClick={shareWithAll} className="text-[10px] font-bold px-2 py-1 rounded bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100" data-testid="pm-form-share-all">Compartilhar com todas</button>
            </div>
            <p className="text-[10px] text-slate-500 mb-2">Marque as filiais que poderao usar esta forma. Para cada filial, escolha a <strong>conta banco</strong> que sera movimentada quando a forma for usada naquela filial (padrao: Caixa Local da filial). Filiais nao marcadas <strong>nao</strong> verao esta forma no PDV/Vendas/Financeiro.</p>
            <div className="space-y-1.5 max-h-64 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-slate-50">
              {companies.length === 0 && <p className="text-xs text-slate-400 text-center py-2">Nenhuma filial cadastrada</p>}
              {companies.map(c => {
                const checked = form.shared_with.some(s => s.company_id === c.id);
                const entry = form.shared_with.find(s => s.company_id === c.id);
                const banks = bankAccountsByCompany[c.id] || [];
                const isOwner = form.owner_company_id === c.id;
                return (
                  <div key={c.id} className={`flex items-center gap-2 px-2 py-1.5 rounded bg-white border ${checked ? 'border-violet-200' : 'border-slate-200'}`} data-testid={`pm-form-share-row-${c.id}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleShareCompany(c.id)} disabled={isOwner} className="flex-shrink-0" data-testid={`pm-form-share-check-${c.id}`} />
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <span className={`text-xs font-medium truncate ${checked ? 'text-slate-900' : 'text-slate-500'}`}>{c.name}</span>
                      {c.is_matriz && <span className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 px-1 py-0.5 rounded">MATRIZ</span>}
                      {isOwner && <span className="text-[9px] text-violet-700 bg-violet-50 border border-violet-200 px-1 py-0.5 rounded">DONA</span>}
                    </div>
                    {checked && (
                      <select
                        value={entry?.bank_account_id || ''}
                        onChange={e => setShareBankAccount(c.id, e.target.value)}
                        className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 bg-white max-w-[160px] flex-shrink-0"
                        data-testid={`pm-form-share-bank-${c.id}`}
                      >
                        <option value="">— Selecionar —</option>
                        {banks.map(b => <option key={b.id} value={b.id}>{b.bank_name}{b.is_default_cash ? ' (Caixa)' : ''}</option>)}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-2">Modulos onde aparece</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100" data-testid="pm-form-scope-sales">
                <input type="checkbox" checked={form.available_in_sales} onChange={e => setForm({ ...form, available_in_sales: e.target.checked })} />
                <span className="text-xs font-bold text-slate-700">Vendas / PDV</span>
              </label>
              <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100" data-testid="pm-form-scope-purchases">
                <input type="checkbox" checked={form.available_in_purchases} onChange={e => setForm({ ...form, available_in_purchases: e.target.checked })} />
                <span className="text-xs font-bold text-slate-700">Compras</span>
              </label>
              <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100" data-testid="pm-form-scope-financial">
                <input type="checkbox" checked={form.available_in_financial} onChange={e => setForm({ ...form, available_in_financial: e.target.checked })} />
                <span className="text-xs font-bold text-slate-700">Financeiro</span>
              </label>
              <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100" data-testid="pm-form-scope-invoices" title="iter156dt5: quando marcado, aparece no dropdown da emissao de NF-e/NFC-e (util para 'Sem Pagamento' em transferencia entre filiais)">
                <input type="checkbox" checked={form.available_in_invoices !== false} onChange={e => setForm({ ...form, available_in_invoices: e.target.checked })} />
                <span className="text-xs font-bold text-slate-700">NF-e / NFC-e</span>
              </label>
            </div>
            {form.is_cashback && (
              <p className="text-[10px] text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded p-2">
                <strong>Cashback:</strong> mesmo com modulos marcados, so aparece quando o tipo de negocio do tenant tem cashback habilitado E o cliente selecionado tem saldo positivo.
              </p>
            )}
            {/* iter156gl: padrao para despesas */}
            <label className="flex items-center gap-2 mt-2 p-2 rounded-lg border border-rose-200 bg-rose-50/60 cursor-pointer" data-testid="pm-default-for-expenses-label">
              <input type="checkbox" checked={!!form.default_for_expenses} onChange={e => setForm({ ...form, default_for_expenses: e.target.checked })} className="accent-rose-600 w-4 h-4" data-testid="pm-default-for-expenses" />
              <span className="text-xs text-slate-700"><strong>Padrao ao lancar despesa</strong> — ja vem selecionada em PDV &gt; Lancamento de Despesa e em Contas a Pagar. Apenas uma forma pode ser padrao; se nenhuma estiver marcada, o usuario precisa escolher.</span>
            </label>
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100" data-testid="pm-form-active">
              <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />
              <span className="text-xs font-bold text-slate-700">Forma de pagamento ativa</span>
              <span className="text-[10px] text-slate-500 ml-auto">Inativas nao aparecem em modulos operacionais (so em relatorios historicos).</span>
            </label>
          </div>
        </div>
      </FormModal>
    </div>
  );
};

/* ========== FINANCIAL CLOSING ========== */
const ClosingPage = ({ companyId }) => {
  const [closings, setClosings] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { const f = async () => { if (!companyId) { setLoading(false); return; } try { const { data } = await axios.get(`${ERP}/financial-closings?company_id=${companyId}`, { withCredentials: true }); setClosings(data.data || []); } catch {} finally { setLoading(false); } }; f(); }, [companyId]);
  const generateClosing = async () => {
    try { const { data } = await axios.post(`${ERP}/financial-closing`, { company_id: companyId, period: localDateISO().slice(0, 7) }, { withCredentials: true }); toast.success(`Fechamento gerado! Saldo: R$ ${data.balance?.toFixed(2)}`); const res = await axios.get(`${ERP}/financial-closings?company_id=${companyId}`, { withCredentials: true }); setClosings(res.data.data || []); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };
  if (!companyId) return <NoCompany />;
  if (loading) return <Loader />;
  return (
    <div data-testid="closing-page">
      <div className="flex items-center justify-between mb-5"><p className="text-sm text-gray-500">{closings.length} fechamentos</p><button onClick={generateClosing} className={btnPrimary} data-testid="generate-closing-btn"><RefreshCw size={16} /><span>Gerar Fechamento</span></button></div>
      <div className="space-y-2">
        {closings.map(c => (
          <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid="closing-row">
            <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium text-gray-900">{c.period || 'Geral'}</span><span className="text-xs text-gray-500">{new Date(c.created_at).toLocaleDateString('pt-BR')}</span></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-xs">
              <div className="bg-green-50 rounded p-2"><p className="text-gray-500">Recebido</p><p className="font-bold text-green-700">R$ {(c.total_received || 0).toFixed(2)}</p></div>
              <div className="bg-red-50 rounded p-2"><p className="text-gray-500">Pago</p><p className="font-bold text-red-700">R$ {(c.total_paid || 0).toFixed(2)}</p></div>
              <div className="bg-amber-50 rounded p-2"><p className="text-gray-500">A Receber</p><p className="font-bold text-amber-700">R$ {(c.total_pending_receivable || 0).toFixed(2)}</p></div>
              <div className="bg-blue-50 rounded p-2"><p className="text-gray-500">Saldo</p><p className="font-bold text-blue-700">R$ {(c.balance || 0).toFixed(2)}</p></div>
            </div>
          </div>
        ))}
        {closings.length === 0 && <EmptyState icon={DollarSign} text="Nenhum fechamento" />}
      </div>
    </div>
  );
};

/* ========== RECEIPTS ========== */
const ReceiptsPage = ({ companyId }) => {
  const [receipts, setReceipts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ payer_name: '', payer_doc: '', amount: '', description: '', payment_method: '' });
  const [loading, setLoading] = useState(true);
  useEffect(() => { const f = async () => { if (!companyId) { setLoading(false); return; } try { const { data } = await axios.get(`${ERP}/receipts?company_id=${companyId}`, { withCredentials: true }); setReceipts(data.data || []); } catch {} finally { setLoading(false); } }; f(); }, [companyId]);
  const handleSubmit = async (e) => { e.preventDefault(); try { await axios.post(`${ERP}/receipts`, { company_id: companyId, ...form, amount: parseFloat(form.amount) || 0 }, { withCredentials: true }); toast.success('Recibo criado!'); setShowForm(false); setForm({ payer_name: '', payer_doc: '', amount: '', description: '', payment_method: '' }); const { data } = await axios.get(`${ERP}/receipts?company_id=${companyId}`, { withCredentials: true }); setReceipts(data.data || []); } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } };
  if (!companyId) return <NoCompany />;
  if (loading) return <Loader />;
  return (
    <div data-testid="receipts-page">
      <div className="flex items-center justify-between mb-5"><p className="text-sm text-gray-500">{receipts.length} recibos</p><button onClick={() => setShowForm(!showForm)} className={btnPrimary}><Plus size={16} /><span>Novo Recibo</span></button></div>
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-teal-200 rounded-xl p-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Pagador *</label><input value={form.payer_name} onChange={e => setForm({ ...form, payer_name: e.target.value })} className={inputClass} required placeholder="Nome do pagador" /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">CPF/CNPJ</label><input value={form.payer_doc} onChange={e => setForm({ ...form, payer_doc: e.target.value })} className={inputClass} /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Valor (R$) *</label><input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className={inputClass} required /></div>
            <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Descrição *</label><input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className={inputClass} required placeholder="Referente a..." /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Forma Pgto</label><input value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} className={inputClass} placeholder="PIX" /></div>
          </div>
          <div className="flex gap-2"><button type="submit" className={btnPrimary}><Save size={14} /><span>Criar</span></button><button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 text-sm text-gray-500">Cancelar</button></div>
        </form>
      )}
      <div className="space-y-2">
        {receipts.map(r => (
          <div key={r.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid="receipt-row">
            <div><p className="font-medium text-sm text-gray-900">Recibo #{r.number}</p><p className="text-xs text-gray-500">{r.payer_name} • {r.description}</p></div>
            <div className="text-right"><p className="font-bold text-sm text-gray-900">R$ {(r.amount || 0).toFixed(2)}</p><p className="text-[10px] text-gray-500">{new Date(r.created_at).toLocaleDateString('pt-BR')}</p></div>
          </div>
        ))}
        {receipts.length === 0 && <EmptyState icon={Receipt} text="Nenhum recibo" />}
      </div>
    </div>
  );
};

/* ========== USERS PAGE ========== */
const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', access_profile_id: '', allowed_company_ids: [], is_employee: false, is_vendor: false, max_discount_pct: '' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const resetForm = () => { setForm({ name: '', email: '', password: '', access_profile_id: '', allowed_company_ids: [], is_employee: false, is_vendor: false, max_discount_pct: '' }); setEditing(null); };
  // iter156u: campo de busca (nome, email, perfil)
  const [searchQuery, setSearchQuery] = useState('');
  const _normalizeSearch = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const fetchUsers = async () => {
    const { data } = await axios.get(`${ERP}/users`, { withCredentials: true });
    setUsers(data.data || []);
  };
  useEffect(() => {
    const f = async () => {
      try {
        const [u, p] = await Promise.all([
          axios.get(`${ERP}/users`, { withCredentials: true }),
          axios.get(`${ERP}/access-profiles`, { withCredentials: true })
        ]);
        setUsers(u.data.data || []);
        setProfiles(p.data.data || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    f();
  }, []);
  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    // iter156cr: normaliza max_discount_pct — vazio ou 0 => null (usa perfil como fallback)
    const _mdRaw = (form.max_discount_pct === '' || form.max_discount_pct === null || form.max_discount_pct === undefined) ? null : Number(form.max_discount_pct);
    const _maxDiscount = (_mdRaw && _mdRaw > 0) ? _mdRaw : null;
    try {
      if (editing) {
        const payload = {
          name: form.name,
          access_profile_id: form.access_profile_id || null,
          allowed_company_ids: form.allowed_company_ids || [],
          is_employee: !!form.is_employee,
          is_vendor: !!(form.is_employee && form.is_vendor),
          max_discount_pct: _maxDiscount,
        };
        if (form.password) payload.password = form.password;
        await axios.put(`${ERP}/users/${editing.id}`, payload, { withCredentials: true });
        toast.success('Usuario atualizado!');
      } else {
        await axios.post(`${ERP}/users`, {
          ...form,
          role: 'operator',
          access_profile_id: form.access_profile_id || null,
          is_employee: !!form.is_employee,
          is_vendor: !!(form.is_employee && form.is_vendor),
          max_discount_pct: _maxDiscount,
        }, { withCredentials: true });
        toast.success('Usuario criado!');
      }
      setShowForm(false); resetForm();
      await fetchUsers();
    } catch (err) {
      // iter77ct.25: surfacia detalhes reais (status + detail) para facilitar diagnostico
      const st = err.response?.status ? ` (${err.response.status})` : '';
      const detail = err.response?.data?.detail || err.message || 'Erro desconhecido';
      toast.error(`${typeof detail === 'string' ? detail : JSON.stringify(detail)}${st}`);
    }
    finally { setSubmitting(false); }
  };
  const toggleActive = async (id, active) => {
    try {
      await axios.put(`${ERP}/users/${id}`, { active: !active }, { withCredentials: true });
      toast.success(active ? 'Usuario desativado' : 'Usuario ativado');
      await fetchUsers();
    } catch { toast.error('Erro'); }
  };
  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Desativar este usuario?', confirmLabel: 'Confirmar', danger: true }))) return;
    try {
      await axios.delete(`${ERP}/users/${id}`, { withCredentials: true });
      toast.success('Usuario desativado');
      await fetchUsers();
    } catch { toast.error('Erro'); }
  };
  const startEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || '',
      email: u.email || '',
      password: '',
      access_profile_id: u.access_profile_id || '',
      allowed_company_ids: u.allowed_company_ids || [],
      is_employee: !!u.is_employee,
      is_vendor: !!u.is_vendor,
      max_discount_pct: (u.max_discount_pct === null || u.max_discount_pct === undefined) ? '' : String(u.max_discount_pct),
    });
    setShowForm(true);
  };
  if (loading) return <Loader />;
  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">{users.length} usuarios</p>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className={btnPrimary} data-testid="add-user-btn"><Plus size={16} /><span>Novo Usuario</span></button>
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-teal-200 rounded-xl p-5 mb-5" data-testid="user-form">
          <h3 className="font-bold text-gray-900 mb-4">{editing ? 'Editar Usuario' : 'Novo Usuario'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Nome *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} required placeholder="Nome completo" data-testid="user-name" /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">E-mail *</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputClass} required placeholder="email@empresa.com" data-testid="user-email" disabled={!!editing} /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">{editing ? 'Nova Senha (opcional)' : 'Senha *'}</label><input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className={inputClass} required={!editing} placeholder={editing ? 'Deixe em branco para manter' : 'Min. 6 caracteres'} data-testid="user-password" /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Perfil de Acesso</label>
              <select value={form.access_profile_id} onChange={e => setForm({ ...form, access_profile_id: e.target.value })} className={inputClass} data-testid="user-access-profile">
                <option value="">Nenhum (acesso restrito)</option>
                {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">O perfil define quais menus o usuário verá.</p>
            </div>
            {/* iter156cr: desconto maximo por usuario — sobrescreve o do perfil quando > 0 */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Desconto Maximo (%) — sobrescreve o do perfil</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.max_discount_pct}
                onChange={e => setForm({ ...form, max_discount_pct: e.target.value })}
                className={inputClass}
                placeholder="Ex.: 15 (deixe vazio para usar o do perfil)"
                data-testid="user-max-discount-pct"
              />
              <p className="text-[10px] text-gray-400 mt-1">Se vazio ou 0, usa o limite do Perfil de Acesso.</p>
            </div>
          </div>
          <div className="mt-3"><CompaniesPicker value={form.allowed_company_ids} onChange={(ids) => setForm({ ...form, allowed_company_ids: ids })} testId="user-companies-picker" label="Filiais onde pode operar" /></div>
          {/* iter77bi: vinculo Usuario <-> Funcionario via checkboxes "E funcionario" + "E vendedor" */}
          <div className="mt-3 bg-violet-50/60 border border-violet-200 rounded-xl p-3" data-testid="user-employee-link">
            <p className="text-[10px] font-black text-violet-700 uppercase tracking-wider mb-2">Vinculo com Funcionario</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.is_employee}
                onChange={e => setForm({ ...form, is_employee: e.target.checked, is_vendor: e.target.checked ? form.is_vendor : false })}
                className="w-4 h-4 accent-violet-600"
                data-testid="user-is-employee"
              />
              <span className="text-xs font-bold text-slate-700">E funcionario (cria registro em Cadastros &gt; Funcionarios)</span>
            </label>
            <label className={`flex items-center gap-2 mt-2 ${form.is_employee ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
              <input
                type="checkbox"
                checked={!!form.is_vendor}
                disabled={!form.is_employee}
                onChange={e => setForm({ ...form, is_vendor: e.target.checked })}
                className="w-4 h-4 accent-violet-600 disabled:accent-slate-300"
                data-testid="user-is-vendor"
              />
              <span className="text-xs font-bold text-slate-700">E vendedor (aparece no seletor do PDV)</span>
            </label>
            <p className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">
              Marque &quot;Funcionario&quot; para criar/manter o registro vinculado. Marque &quot;Vendedor&quot; para que este funcionario apareca no dropdown de vendedor do PDV. Desmarcar &quot;Funcionario&quot; inativa o registro (preserva historico de vendas).
            </p>
          </div>
          <div className="flex gap-2 mt-3"><button type="submit" disabled={submitting} className={btnPrimary}><Save size={14} /><span>{submitting ? 'Salvando...' : editing ? 'Atualizar' : 'Criar'}</span></button><button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2.5 text-sm text-gray-500">Cancelar</button></div>
        </form>
      )}
      <div className="space-y-2">
        {/* iter156u: campo de busca — filtra por nome, email ou perfil */}
        {users.length > 4 && (
          <div className="relative mb-3">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar por nome, email ou perfil..."
              className="w-full pl-9 pr-9 py-2 rounded-xl bg-white border border-slate-200 focus:border-teal-400 focus:ring-2 focus:ring-teal-100 text-sm outline-none"
              data-testid="users-search-input"
              autoComplete="off"
              inputMode="search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                data-testid="users-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}
        {(() => {
          const q = _normalizeSearch(searchQuery.trim());
          const profileById = Object.fromEntries((profiles || []).map(p => [p.id, p.name || '']));
          const filtered = q
            ? users.filter(u =>
                _normalizeSearch(u.name).includes(q) ||
                _normalizeSearch(u.email).includes(q) ||
                _normalizeSearch(profileById[u.access_profile_id] || '').includes(q)
              )
            : users;
          if (filtered.length === 0 && q) {
            return <div className="text-center py-8 text-sm text-slate-400 font-medium">Nenhum usuario encontrado para &quot;{searchQuery}&quot;.</div>;
          }
          return filtered.map(u => (
          <div key={u.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid="user-row">
            <div className="flex items-center space-x-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0"><span className="text-sm font-bold text-teal-700">{u.name?.charAt(0) || '?'}</span></div>
              <div className="min-w-0"><p className="font-medium text-sm text-gray-900 truncate">{u.name}</p><p className="text-xs text-gray-500 truncate">{u.email}</p></div>
            </div>
            <div className="flex items-center space-x-1.5 flex-shrink-0">
              <span className={`text-[10px] font-semibold px-2 py-1 rounded ${u.active !== false ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{u.active !== false ? 'Ativo' : 'Inativo'}</span>
              <button onClick={() => toggleActive(u.id, u.active !== false)} className="p-1.5 text-gray-400 hover:text-gray-600" title={u.active !== false ? 'Desativar' : 'Ativar'} data-testid="toggle-user-btn">
                {u.active !== false ? <XCircle size={14} /> : <CheckCircle size={14} />}
              </button>
              <button onClick={() => startEdit(u)} className="p-1.5 text-gray-400 hover:text-teal-600" title="Editar" data-testid="edit-user-btn"><Edit size={14} /></button>
              <button onClick={() => handleDelete(u.id)} className="p-1.5 text-gray-400 hover:text-red-500" title="Excluir" data-testid="delete-user-btn"><Trash2 size={14} /></button>
            </div>
          </div>
        ));
        })()}
        {users.length === 0 && <EmptyState icon={Users} text="Nenhum usuario cadastrado" />}
      </div>
    </div>
  );
};

/* ========== ACCESS PROFILES PAGE ========== */
// iter156fv: seletor compacto de filiais (multi) — padrao = filial logada
const BranchMultiSelect = ({ value = [], onChange, companies = [], testId = 'branch-multi', className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = (id) => { const s = new Set(value); if (s.has(id)) s.delete(id); else s.add(id); onChange(Array.from(s)); };
  const label = value.length === 0 ? 'Todas permitidas'
    : value.length === 1 ? (() => { const c = companies.find(x => x.id === value[0]); return c ? `${c.code ? c.code + ' · ' : ''}${c.name}` : '1 filial'; })()
    : `${value.length} filiais`;
  return (
    <div ref={ref} className={`relative ${className}`} data-testid={testId}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-2 text-sm border border-slate-200 rounded-lg px-2 py-2 bg-white text-left hover:border-violet-300" data-testid={`${testId}-toggle`}>
        <span className="truncate font-medium text-slate-800">{label}</span>
        <ChevronDown size={14} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[240px] bg-white border border-slate-200 rounded-xl shadow-xl p-1.5 max-h-64 overflow-y-auto">
          <button type="button" onClick={() => onChange([])} className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold ${value.length === 0 ? 'bg-violet-50 text-violet-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`${testId}-all`}>Todas permitidas</button>
          {companies.map(c => {
            const on = value.includes(c.id);
            return (
              <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs cursor-pointer hover:bg-slate-50" data-testid={`${testId}-opt-${c.id}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(c.id)} className="accent-violet-600" />
                <span className={`truncate ${on ? 'font-bold text-slate-900' : 'text-slate-600'}`}>{c.code ? `${c.code} · ` : ''}{c.name}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

// iter77bt: Logs de Auditoria — rastreabilidade de edicoes e exclusoes
const LogsPage = ({ companyId: currentCompanyId }) => {
  // iter156ce: 2 abas — 'audit' (logs de edicoes/exclusoes) e 'product-trail' (rastrear produto)
  const [tab, setTab] = useState('audit');
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ modules: [], actions: [], users: [] });
  const [filters, setFilters] = useState({ module: '', action: '', user_id: '', date_from: '', date_to: '' });
  const [detail, setDetail] = useState(null);
  // iter156fv: filial(is) — padrao = filial logada
  const [companies, setCompanies] = useState([]);
  const [branchIds, setBranchIds] = useState(currentCompanyId ? [currentCompanyId] : []);
  useEffect(() => { setBranchIds(currentCompanyId ? [currentCompanyId] : []); }, [currentCompanyId]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, comps] = await Promise.all([
          axios.get(`${ERP}/audit-logs/facets`, { withCredentials: true }),
          axios.get(`${ERP}/companies`, { withCredentials: true }),
        ]);
        setFacets(data);
        setCompanies(comps.data.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      if (branchIds.length) params.append('company_id', branchIds.join(','));
      params.append('limit', '200');
      const { data } = await axios.get(`${ERP}/audit-logs?${params}`, { withCredentials: true });
      setLogs(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Sem permissao para visualizar logs. Peca acesso ao administrador.');
    } finally { setLoading(false); }
  }, [filters, branchIds]);
  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      const brt = new Date(d.getTime() - 3 * 3600 * 1000);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(brt.getUTCDate())}/${pad(brt.getUTCMonth()+1)}/${brt.getUTCFullYear()} ${pad(brt.getUTCHours())}:${pad(brt.getUTCMinutes())}`;
    } catch { return iso.slice(0, 16).replace('T', ' '); }
  };
  const actionBadge = (a) => a === 'delete'
    ? <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">Excluiu</span>
    : <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Editou</span>;
  const moduleLabel = (m) => facets.modules.find(f => f.key === m)?.label || m;
  const userLabel = (uid) => {
    const u = facets.users.find(f => f.id === uid);
    return u ? (u.name || u.email) : (uid || '-');
  };

  return (
    <div className="space-y-4" data-testid="logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Auditoria</h1>
          <p className="text-xs text-slate-500 mt-0.5">Rastreabilidade completa: logs de edicoes/exclusoes e trilha de eventos por produto.</p>
        </div>
        {tab === 'audit' && <div className="text-xs font-bold text-slate-500">{total} registros</div>}
      </div>

      {/* iter156ce: abas Auditoria x Rastrear Produto */}
      <div className="border-b border-slate-200 flex gap-1">
        {[
          { key: 'audit', label: 'Logs de Edicao/Exclusao' },
          { key: 'product-trail', label: 'Rastrear Produto' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`logs-tab-${t.key}`}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition ${
              tab === t.key
                ? 'border-violet-600 text-violet-700'
                : 'border-transparent text-slate-400 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'product-trail' && <ProductTrailPanel currentCompanyId={currentCompanyId} />}
      {tab === 'audit' && (
      <>
      <div className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-wrap gap-2 items-end">
        <div className="min-w-[200px]">
          <label className="block text-[10px] font-bold text-slate-500 mb-1">Filial</label>
          <BranchMultiSelect value={branchIds} onChange={setBranchIds} companies={companies} testId="logs-filter-branch" />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-1">Modulo</label>
          <select value={filters.module} onChange={e => setFilters({ ...filters, module: e.target.value })} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white min-w-[160px]" data-testid="logs-filter-module">
            <option value="">Todos</option>
            {facets.modules.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-1">Acao</label>
          <select value={filters.action} onChange={e => setFilters({ ...filters, action: e.target.value })} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white" data-testid="logs-filter-action">
            <option value="">Todas</option>
            <option value="update">Editou</option>
            <option value="delete">Excluiu</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-1">Usuario</label>
          <select value={filters.user_id} onChange={e => setFilters({ ...filters, user_id: e.target.value })} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white min-w-[160px]" data-testid="logs-filter-user">
            <option value="">Todos</option>
            {facets.users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-1">De</label>
          <input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white" />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 mb-1">Ate</label>
          <input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white" />
        </div>
        {(filters.module || filters.action || filters.user_id || filters.date_from || filters.date_to) && (
          <button onClick={() => setFilters({ module: '', action: '', user_id: '', date_from: '', date_to: '' })} className="text-xs font-bold px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200" data-testid="logs-clear-filters">Limpar</button>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-xs">Carregando...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-xs">Nenhum log encontrado</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left font-bold text-slate-600">Data/Hora</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-600">Acao</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-600">Modulo</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-600">Registro</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-600">Usuario</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-600"> </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map(lg => (
                  <tr key={lg.id} className="hover:bg-slate-50" data-testid={`logs-row-${lg.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{fmtDate(lg.timestamp)}</td>
                    <td className="px-3 py-2">{actionBadge(lg.action)}</td>
                    <td className="px-3 py-2 text-slate-700 font-medium">{moduleLabel(lg.module)}</td>
                    <td className="px-3 py-2 text-slate-900 font-medium">{lg.resource_name || lg.resource_id || '-'}</td>
                    <td className="px-3 py-2 text-slate-600">{userLabel(lg.user_id) || lg.user_email || '-'}</td>
                    <td className="px-3 py-2 text-right">
                      {(lg.diff && Object.keys(lg.diff).length > 0) && (
                        <button onClick={() => setDetail(lg)} className="text-[10px] font-bold px-2 py-1 rounded bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100" data-testid={`logs-detail-${lg.id}`}>Ver detalhes</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {detail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-black text-slate-900">Detalhe da alteracao</h3>
                <p className="text-xs text-slate-500 mt-0.5">{fmtDate(detail.timestamp)} · {moduleLabel(detail.module)} · {detail.resource_name}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="text-xs mb-2"><strong>Por:</strong> {detail.user_name || detail.user_email} {detail.ip && <span className="text-slate-400 ml-2">IP: {detail.ip}</span>}</div>
            <div className="mt-3 border-t border-slate-100 pt-3">
              <p className="text-[11px] font-bold text-slate-500 uppercase mb-2">Campos alterados</p>
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-2 py-1 text-left font-bold text-slate-600">Campo</th>
                    <th className="px-2 py-1 text-left font-bold text-slate-600">Antes</th>
                    <th className="px-2 py-1 text-left font-bold text-slate-600">Depois</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Object.entries(detail.diff || {}).map(([k, v]) => (
                    <tr key={k}>
                      <td className="px-2 py-1 font-mono text-[10px] text-slate-500">{k}</td>
                      <td className="px-2 py-1 text-rose-700">{String(v?.from ?? '-')}</td>
                      <td className="px-2 py-1 text-emerald-700">{String(v?.to ?? '-')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

// iter156ce: Painel Rastrear Produto — trilha completa de eventos por produto/filial/periodo
const ProductTrailPanel = ({ currentCompanyId }) => {
  const [companies, setCompanies] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  // iter156fv: filial(is) — padrao = filial logada; multi-selecao
  const [branchIds, setBranchIds] = useState(currentCompanyId ? [currentCompanyId] : []);
  useEffect(() => { setBranchIds(currentCompanyId ? [currentCompanyId] : []); }, [currentCompanyId]);
  const companyId = branchIds.length === 1 ? branchIds[0] : '';
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [action, setAction] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${ERP}/companies`, { withCredentials: true });
        setCompanies(d.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (!productSearch || productSearch.length < 2) { setProductResults([]); return; }
    const t = setTimeout(async () => {
      try {
        // iter156ce fix: usar mesmo endpoint da Lista de Vendas/PDV (`/pdv/products?q=`)
        // pois `${ERP}/products` nao existe (404) e o correto e `${API}/products` com `search=`.
        // `/pdv/products` ja e case-insensitive, tolerante a acentos, matcheia SKU/nome/EAN.
        const params = new URLSearchParams({ q: productSearch, limit: '15' });
        if (branchIds.length === 1) params.set('company_id', branchIds[0]);
        const { data: d } = await axios.get(`${ERP}/pdv/products?${params.toString()}`, { withCredentials: true });
        setProductResults(d.data || []);
        setShowResults(true);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [productSearch, branchIds]);

  const runTrail = async () => {
    if (!selectedProduct) { toast.error('Selecione um produto'); return; }
    setLoading(true);
    setData(null);
    try {
      const params = new URLSearchParams();
      params.set('product_id', selectedProduct.id);
      if (branchIds.length) params.set('company_id', branchIds.join(','));
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (action) params.set('action', action);
      const { data: d } = await axios.get(`${ERP}/audit-logs/product-trail?${params}`, { withCredentials: true });
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao rastrear produto');
    } finally { setLoading(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      const brt = new Date(d.getTime() - 3 * 3600 * 1000);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(brt.getUTCDate())}/${pad(brt.getUTCMonth()+1)} ${pad(brt.getUTCHours())}:${pad(brt.getUTCMinutes())}`;
    } catch { return iso.slice(0, 16); }
  };

  const actionColor = (a) => ({
    divergencia: 'bg-red-600 text-white border-red-700',
    venda: 'bg-rose-100 text-rose-700 border-rose-200',
    compra: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    entrada: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    saida: 'bg-rose-100 text-rose-700 border-rose-200',
    ajuste: 'bg-amber-100 text-amber-700 border-amber-200',
    balanco: 'bg-blue-100 text-blue-700 border-blue-200',
    transferencia: 'bg-violet-100 text-violet-700 border-violet-200',
    cadastro: 'bg-teal-100 text-teal-700 border-teal-200',
    edicao_cadastro: 'bg-slate-100 text-slate-700 border-slate-200',
  }[a] || 'bg-slate-100 text-slate-700 border-slate-200');

  return (
    <div className="space-y-4" data-testid="product-trail-panel">
      <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Produto *</label>
            <input
              value={selectedProduct ? `${selectedProduct.name} · ${selectedProduct.sku || 'sem SKU'}` : productSearch}
              onChange={e => {
                setProductSearch(e.target.value);
                setSelectedProduct(null);
              }}
              onFocus={() => productResults.length && setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
              placeholder="Digite nome ou SKU (min 2 caracteres)..."
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2"
              data-testid="trail-product-search"
            />
            {showResults && productResults.length > 0 && !selectedProduct && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto z-10">
                {productResults.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { setSelectedProduct(p); setShowResults(false); setProductSearch(''); }}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0"
                    data-testid={`trail-product-option-${p.id}`}
                  >
                    <div className="text-sm font-semibold text-slate-800 truncate">{p.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">SKU {p.sku || '-'} · EAN {p.ean || '-'}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Filial</label>
            <BranchMultiSelect value={branchIds} onChange={setBranchIds} companies={companies} testId="trail-company" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Acao</label>
            <select value={action} onChange={e => setAction(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2" data-testid="trail-action">
              <option value="">Todas</option>
              <option value="venda">Venda</option>
              <option value="compra">Compra (NF Entrada)</option>
              <option value="entrada">Entrada de Estoque</option>
              <option value="saida">Saida de Estoque</option>
              <option value="ajuste">Ajuste</option>
              <option value="balanco">Balanco</option>
              <option value="transferencia">Transferencia</option>
              <option value="cadastro">Cadastro / Importacao</option>
              <option value="edicao_cadastro">Edicao no Cadastro</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Data De</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Data Ate</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2" />
          </div>
          <div className="flex items-end">
            <button
              onClick={runTrail}
              disabled={!selectedProduct || loading}
              className="w-full bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white font-black text-sm py-2 rounded-lg"
              data-testid="trail-run-btn"
            >{loading ? 'Buscando...' : 'Rastrear'}</button>
          </div>
        </div>
      </div>

      {data && (
        <>
          {/* Header do produto + resumo */}
          <div className="bg-gradient-to-r from-violet-600 to-fuchsia-500 rounded-2xl p-4 text-white">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="col-span-2">
                <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Produto</p>
                <h2 className="text-lg font-black">{data.product.name}</h2>
                <p className="text-[10px] font-mono opacity-90">SKU {data.product.sku || '-'} · EAN {data.product.ean || '-'} · UN {data.product.unit}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{data.company_id ? 'Estoque Atual (nesta filial)' : (data.product.current_stock_company !== undefined ? 'Estoque Atual (filiais selecionadas)' : 'Estoque Atual (todas as filiais)')}</p>
                <p className="text-2xl font-black tabular-nums" data-testid="trail-current-stock">{Number((data.product.current_stock_company !== undefined ? data.product.current_stock_company : data.product.current_stock_total) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</p>
                {data.product.current_stock_company !== undefined && <p className="text-[10px] opacity-75">total em todas as filiais: {Number(data.product.current_stock_total || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</p>}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Entradas no periodo</p>
                <p className="text-xl font-black text-emerald-100 tabular-nums">+{Number(data.totals.total_in || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Saidas no periodo</p>
                <p className="text-xl font-black text-rose-100 tabular-nums">-{Number(data.totals.total_out || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</p>
              </div>
            </div>
            <p className="text-[10px] opacity-80 mt-3">{data.totals.events_count} evento{data.totals.events_count === 1 ? '' : 's'} entre {dateFrom.split('-').reverse().join('/')} e {dateTo.split('-').reverse().join('/')} · Saldo do periodo: <strong>{data.totals.net >= 0 ? '+' : ''}{Number(data.totals.net || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</strong></p>
          </div>

          {/* Tabela de eventos */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {data.events.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-400" data-testid="trail-empty">Nenhum evento encontrado para os filtros selecionados.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[10px] font-bold text-slate-500 uppercase">
                    <th className="px-3 py-2">Data</th>
                    <th className="px-3 py-2">Acao</th>
                    <th className="px-3 py-2">Filial</th>
                    <th className="px-3 py-2">Documento</th>
                    <th className="px-3 py-2 text-right">Qtde</th>
                    <th className="px-3 py-2 text-right">Preco/Custo</th>
                    <th className="px-3 py-2 text-right">Valor</th>
                    <th className="px-3 py-2 text-right">Estoque Apos</th>
                    <th className="px-3 py-2">Usuario</th>
                    <th className="px-3 py-2">Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events.map((e, i) => (
                    <tr key={`${e.source_module}-${e.source_id}-${i}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`trail-event-${i}`}>
                      <td className="px-3 py-2 font-mono text-slate-500">{fmtDate(e.timestamp)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded border ${actionColor(e.action)}`}>{e.action_label}</span>
                        {e.recorded_mismatch && <span className="ml-1 text-[9px] font-bold text-amber-700" title={e.notes}>⚠</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-600 truncate max-w-[120px]" title={e.company_name}>{e.company_name || '-'}</td>
                      <td className="px-3 py-2 font-mono text-slate-500">{e.document_number || '-'}</td>
                      <td className={`px-3 py-2 text-right font-black tabular-nums ${e.direction === 'in' ? 'text-emerald-700' : e.direction === 'out' ? 'text-rose-700' : 'text-slate-500'}`}>
                        {e.direction === 'in' ? '+' : e.direction === 'out' ? '-' : ''}{Number(e.quantity || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-600 tabular-nums">
                        {e.unit_price ? `R$ ${Number(e.unit_price).toFixed(2).replace('.', ',')}` : e.unit_cost ? `R$ ${Number(e.unit_cost).toFixed(2).replace('.', ',')}` : '-'}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700 font-bold tabular-nums">
                        {e.total_value ? `R$ ${Number(e.total_value).toFixed(2).replace('.', ',')}` : '-'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-800" data-testid="trail-stock-after">
                        {e.stock_after != null ? Number(e.stock_after).toLocaleString('pt-BR', { maximumFractionDigits: 3 }) : (e.new_stock != null ? Number(e.new_stock).toLocaleString('pt-BR', { maximumFractionDigits: 3 }) : '-')}
                      </td>
                      <td className="px-3 py-2 text-slate-600 truncate max-w-[100px]" title={e.user_name}>{e.user_name || '-'}</td>
                      <td className="px-3 py-2 text-slate-500 truncate max-w-[150px]" title={e.notes || e.reason}>{e.notes || e.reason || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {!data && !loading && (
        <div className="text-center text-sm text-slate-400 py-12 border border-dashed border-slate-200 rounded-2xl">
          Selecione um produto e clique em <strong>Rastrear</strong> para ver a trilha completa de eventos.
        </div>
      )}

    </div>
  );
};

const AccessProfilesPage = ({ allowedModules = {}, osLabel = 'Ordem de Servico' }) => {
  const [profiles, setProfiles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', permissions: {}, pdv_edit_unit_price: false, pdv_give_discount: false, pdv_max_discount_pct: 0, pdv_give_surcharge: false, pdv_show_pieces_movement: false, pdv_hide_stock_qty: false, purchase_request_force_edit: false, bank_edit_balance: false, sales_only_own: false, dashboard_cards: {} });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Mirror of sidebar menus (ERPDashboard allModules) — grouped for UX.
  // Each item is filtered against `allowedModules` (resolved from the tenant
  // business_type at /api/erp/allowed-modules) so the access profile can only
  // grant access to menus that the merchant's nicho actually exposes.
  const ALL_PERM_GROUPS = [
    { title: 'Principal', items: [
      { key: 'dashboard', label: 'Inicio (sempre)', locked: true },
    ] },
    { title: 'Gerencial', items: [
      { key: 'ai-insights', label: 'IA Insights (Pulse AI)' },
      { key: 'dre', label: 'DRE (Demonstrativo de Resultados)' },
      { key: 'profit-direct', label: 'Lucro Direto Produto' },
      { key: 'commission', label: 'Comissao de Vendedores' },
      // iter156bh: Relatorio de Ponto — gated pelo BT/tenant ponto_module_enabled
      { key: 'ponto-report', label: 'Relatorio de Ponto' },
    ] },
    { title: 'Vendas', items: [
      { key: 'pdv', label: 'PDV' },
      { key: 'sales-orders', label: 'Pedidos de Venda' },
      { key: 'sales', label: 'Vendas' },
      // iter156dz: Ordem de Servico logo abaixo de Vendas. So aparece quando SuperAdmin
      // libera o modulo (filtro `allowedModules[key] !== false` na linha ~5813 tira este
      // item quando os_module_enabled=False no tenant/BT).
      // iter156ea: label customizavel via SuperAdmin (default "Ordem de Servico")
      { key: 'service-orders', label: osLabel },
      { key: 'cash', label: 'Caixa' },
      { key: 'sales-report', label: 'Relatorio' },
      { key: 'metas', label: 'Metas' },
    ]},
    { title: 'Itens', items: [
      { key: 'products', label: 'Produtos' },
      { key: 'categories', label: 'Categorias' },
      { key: 'stock', label: 'Estoque' },
      { key: 'stock-movements', label: 'Movimentacao Estoque' },
      { key: 'stock-transfers', label: 'Transferencia Estoque' },
      // iter156el: modulo Compras (Pedidos de Compra / Entrada de NF) — antes nao aparecia nos
      // perfis, entao usuarios com perfil perdiam o menu "Compras" (Layer 2 negava a chave).
      { key: 'purchases', label: 'Compras (Pedidos de Compra / Entrada NF)' },
      { key: 'purchase-requests', label: 'Solicitacao de Compra' },
      { key: 'price-tables', label: 'Tabela de Precos' },
      { key: 'labels', label: 'Etiquetas' },
      { key: 'settings', label: 'Catalogo (Vitrine)', aliases: ['catalog-settings'] },
      // iter156aw: modulo opt-in — aparece so quando BT liga "production"
      { key: 'production-orders', label: 'Ordens de Producao' },
    ]},
    { title: 'Cadastros', items: [
      { key: 'clients', label: 'Clientes' },
      { key: 'suppliers', label: 'Fornecedores' },
      { key: 'employees', label: 'Funcionarios' },
      // iter156aw: modulos opt-in — aparecem so quando BT ligou. Filtro em PERM_GROUPS
      // (allowedModules[key] !== false) mantem eles ocultos se BT nao liberou.
      { key: 'ponto', label: 'Ponto Eletronico' },
    ]},
    { title: 'Fiscal', items: [
      { key: 'mdfe', label: 'MDF-e (Manifesto Eletronico)' },
    ]},
    { title: 'Financeiro', items: [
      { key: 'financial', label: 'Financeiro' },
      { key: 'chart-accounts', label: 'Plano Contas' },
      { key: 'bank-accounts', label: 'Banco' },
      { key: 'payment-methods', label: 'Formas Pgto' },
      { key: 'closing', label: 'Fechamento' },
      { key: 'receipts', label: 'Recibos' },
    ]},
    { title: 'Administracao', items: [
      { key: 'companies', label: 'Empresas' },
      { key: 'company-settings', label: 'Configuracao (Empresa & Defaults)' },
      { key: 'users', label: 'Usuarios' },
      { key: 'access-profiles', label: 'Perfis Acesso' },
      { key: 'natureza-operacao', label: 'Config. de NF (Natureza de Operacao)' },
      { key: 'invoices', label: 'NF (Notas Fiscais — emissao)' },
      { key: 'manifesto', label: 'NF de Entrada (Manifesto)' },
      { key: 'plans', label: 'Meu Plano' },
      { key: 'integrations', label: 'AgentCRM' },
      { key: 'logs', label: 'Logs de Auditoria' },
    ]},
  ];
  // Filter to only the modules allowed by the tenant's business_type (nicho do
  // negocio configurado no Super Admin). Locked items (always-on like Inicio)
  // are kept regardless. Empty groups are dropped from the UI.
  const PERM_GROUPS = ALL_PERM_GROUPS.map(g => ({
    title: g.title,
    items: g.items.filter(i => i.locked || allowedModules[i.key] !== false)
  })).filter(g => g.items.length > 0);
  const allKeys = PERM_GROUPS.flatMap(g => g.items.filter(i => !i.locked).map(i => i.key));
  const resetForm = () => { setForm({ name: '', permissions: {}, pdv_edit_unit_price: false, pdv_give_discount: false, pdv_max_discount_pct: 0, pdv_give_surcharge: false, pdv_show_pieces_movement: false, pdv_hide_stock_qty: false, purchase_request_force_edit: false, bank_edit_balance: false, sales_only_own: false, dashboard_cards: {} }); setEditing(null); };
  const fetchProfiles = async () => { const { data } = await axios.get(`${ERP}/access-profiles`, { withCredentials: true }); setProfiles(data.data || []); };
  useEffect(() => {
    const f = async () => { try { await fetchProfiles(); } catch {} finally { setLoading(false); } };
    f();
  }, []);
  // ACTIONS_BY_MODULE: define quais acoes (V/I/E/D) fazem sentido para cada modulo
  // Padrao = todas. Modulos read-only listados aqui.
  const ACTIONS_BY_MODULE = {
    'dashboard': ['view'],
    'sales-report': ['view'],
    'cash': ['view', 'insert'],  // pode abrir/fechar caixa (insert) mas nao editar/excluir
    'closing': ['view'],
    'receipts': ['view', 'insert'],
    'plans': ['view'],
    'settings': ['view', 'edit'],  // configuracao nao tem create/delete
    'integrations': ['view', 'edit'],
    // iter156bh: Relatorio de Ponto — read-only
    'ponto-report': ['view'],
    // iter77ch: adiciona 'blind_conference' como ação especial em stock-transfers.
    // Quando marcada, força o usuário a fazer conferência cega (não vê a qtd enviada).
    'stock-transfers': ['view', 'insert', 'edit', 'delete', 'blind_conference'],
    // iter77ct.23: 'view_suggestions' controla visibilidade da aba "Sugestao de Compra"
    'purchase-requests': ['view', 'insert', 'edit', 'delete', 'view_suggestions'],
    'purchases': ['view', 'insert', 'edit', 'delete'],
    // iter156dy: 'confirm' controla o botao "Confirmar Venda" em Pedidos de Venda.
    // Sem essa acao, o operador ve o pedido mas nao consegue transforma-lo em venda.
    'sales-orders': ['view', 'insert', 'edit', 'delete', 'confirm'],
  };
  const ALL_ACTIONS = ['view', 'insert', 'edit', 'delete'];
  const ACTION_LABEL = { view: 'Ver', insert: 'Inserir', edit: 'Editar', delete: 'Excluir', blind_conference: 'Conf. Cega', view_suggestions: 'Ver Sugestao', confirm: 'Confirmar Venda' };
  const actionsFor = (key) => ACTIONS_BY_MODULE[key] || ALL_ACTIONS;

  // Normalize permission entry: accepts legacy boolean OR new dict {view,insert,edit,delete[,blind_conference][,view_suggestions][,confirm]}
  const normalizePerm = (raw) => {
    if (raw === true) return { view: true, insert: true, edit: true, delete: true, blind_conference: true, view_suggestions: true, confirm: true };
    if (!raw || typeof raw !== 'object') return { view: false, insert: false, edit: false, delete: false };
    // iter77ch: preserva blind_conference alem do V/I/E/D
    // iter77ct.23: preserva view_suggestions (aba Sugestao de Compra)
    // iter156dy: preserva confirm (transformar pedido em venda)
    return { view: !!raw.view, insert: !!raw.insert, edit: !!raw.edit, delete: !!raw.delete, blind_conference: !!raw.blind_conference, view_suggestions: !!raw.view_suggestions, confirm: !!raw.confirm };
  };
  const getPerm = (key) => normalizePerm(form.permissions?.[key]);
  const setPerm = (key, action, value) => setForm(p => {
    const current = normalizePerm(p.permissions?.[key]);
    const next = { ...current, [action]: value };
    // Se desmarcar view, desmarca tudo (pessoa nao pode editar sem ver)
    if (action === 'view' && !value) { next.insert = false; next.edit = false; next.delete = false; next.blind_conference = false; next.view_suggestions = false; next.confirm = false; }
    // Se marcar qualquer outra, garante view
    if (value && action !== 'view') next.view = true;
    return { ...p, permissions: { ...p.permissions, [key]: next } };
  });
  const togglePerm = (key) => {
    // Click no card todo: alterna entre "tudo marcado" e "nada marcado"
    const current = normalizePerm(form.permissions?.[key]);
    const allOn = actionsFor(key).every(a => current[a]);
    const allOff = actionsFor(key).every(a => !current[a]);
    const newPerm = { view: false, insert: false, edit: false, delete: false };
    if (allOn) {
      // turn all off
    } else if (allOff) {
      actionsFor(key).forEach(a => { newPerm[a] = true; });
    } else {
      // partial → turn all on
      actionsFor(key).forEach(a => { newPerm[a] = true; });
    }
    setForm(p => ({ ...p, permissions: { ...p.permissions, [key]: newPerm } }));
  };
  const toggleGroup = (items, on) => setForm(p => {
    const next = { ...p.permissions };
    items.filter(i => !i.locked).forEach(i => {
      if (on) {
        const fullPerm = { view: false, insert: false, edit: false, delete: false };
        actionsFor(i.key).forEach(a => { fullPerm[a] = true; });
        next[i.key] = fullPerm;
      } else {
        next[i.key] = { view: false, insert: false, edit: false, delete: false };
      }
    });
    return { ...p, permissions: next };
  });
  const selectAll = () => setForm(p => ({
    ...p,
    permissions: Object.fromEntries(allKeys.map(k => {
      const fp = { view: false, insert: false, edit: false, delete: false };
      actionsFor(k).forEach(a => { fp[a] = true; });
      return [k, fp];
    }))
  }));
  const clearAll = () => setForm(p => ({ ...p, permissions: {} }));
  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      // iter156fq: grava tambem nos aliases (ex: settings -> catalog-settings) para bater com /allowed-modules
      const permissions = { ...form.permissions };
      ALL_PERM_GROUPS.forEach(g => g.items.forEach(i => (i.aliases || []).forEach(a => { if (permissions[i.key] !== undefined) permissions[a] = permissions[i.key]; })));
      const payload = { ...form, permissions };
      if (editing) {
        await axios.put(`${ERP}/access-profiles/${editing.id}`, payload, { withCredentials: true });
        toast.success('Perfil atualizado!');
      } else {
        await axios.post(`${ERP}/access-profiles`, payload, { withCredentials: true });
        toast.success('Perfil criado!');
      }
      setShowForm(false); resetForm(); await fetchProfiles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSubmitting(false); }
  };
  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '',
      permissions: p.permissions || {},
      pdv_edit_unit_price: !!p.pdv_edit_unit_price,
      pdv_give_discount: !!p.pdv_give_discount,
      pdv_max_discount_pct: Number(p.pdv_max_discount_pct || 0),
      pdv_give_surcharge: !!p.pdv_give_surcharge,
      pdv_show_pieces_movement: !!p.pdv_show_pieces_movement,  // iter156m
      pdv_hide_stock_qty: !!p.pdv_hide_stock_qty,  // iter156gj
      purchase_request_force_edit: !!p.purchase_request_force_edit,
      bank_edit_balance: !!p.bank_edit_balance,  // iter77ct.11
      sales_only_own: !!p.sales_only_own,
      dashboard_cards: p.dashboard_cards || {},
    });
    setShowForm(true);
  };
  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Desativar este perfil?', confirmLabel: 'Confirmar', danger: true }))) return;
    try { await axios.delete(`${ERP}/access-profiles/${id}`, { withCredentials: true }); toast.success('Perfil desativado'); await fetchProfiles(); }
    catch { toast.error('Erro'); }
  };
  const countPerms = (perms) => {
    if (!perms) return 0;
    return Object.values(perms).filter(p => {
      if (p === true) return true;
      if (!p || typeof p !== 'object') return false;
      return p.view || p.insert || p.edit || p.delete;
    }).length;
  };
  if (loading) return <Loader />;
  return (
    <div data-testid="access-profiles-page">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">{profiles.length} perfis</p>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className={btnPrimary} data-testid="add-profile-btn"><Plus size={16} /><span>Novo Perfil</span></button>
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-teal-200 rounded-xl p-5 mb-5" data-testid="profile-form">
          <h3 className="font-bold text-gray-900 mb-4">{editing ? 'Editar Perfil de Acesso' : 'Novo Perfil de Acesso'}</h3>
          <div className="mb-4"><label className="block text-xs font-semibold text-gray-500 mb-1">Nome do Perfil *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} required placeholder="Ex: Vendedor" data-testid="profile-name" /></div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-gray-500">Permissoes por Menu <span className="text-gray-400 font-normal">({countPerms(form.permissions)} selecionadas)</span></label>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={selectAll} className="text-[11px] text-teal-600 font-semibold px-2 py-1 rounded hover:bg-teal-50" data-testid="perm-select-all">Marcar tudo</button>
                <button type="button" onClick={clearAll} className="text-[11px] text-gray-500 font-semibold px-2 py-1 rounded hover:bg-gray-50" data-testid="perm-clear-all">Limpar</button>
              </div>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">Estes itens controlam quais menus e telas o usuario com este perfil podera acessar. Inicio sempre liberado. Administradores ignoram restricoes.</p>
            <div className="space-y-3">
              {PERM_GROUPS.map(group => {
                const groupItems = group.items.filter(i => !i.locked);
                const isOn = (k) => {
                  const p = normalizePerm(form.permissions?.[k]);
                  return actionsFor(k).some(a => p[a]);
                };
                const allOn = groupItems.length > 0 && groupItems.every(i => isOn(i.key));
                const someOn = groupItems.some(i => isOn(i.key));
                return (
                  <div key={group.title} className="border border-gray-200 rounded-lg p-3" data-testid={`perm-group-${group.title.toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{group.title}</p>
                      {groupItems.length > 0 && (
                        <button type="button" onClick={() => toggleGroup(group.items, !allOn)} className={`text-[10px] font-semibold px-2 py-0.5 rounded ${allOn ? 'bg-teal-600 text-white' : someOn ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}`} data-testid={`perm-group-toggle-${group.title.toLowerCase()}`}>
                          {allOn ? 'Todos' : someOn ? 'Parcial' : 'Nenhum'}
                        </button>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      {group.items.map(p => {
                        const perm = p.locked ? { view: true, insert: false, edit: false, delete: false } : getPerm(p.key);
                        const acts = actionsFor(p.key);
                        const anyOn = acts.some(a => perm[a]);
                        return (
                          <div key={p.key} className={`border-2 rounded-lg p-2.5 transition-colors ${p.locked ? 'border-slate-100 bg-slate-50' : anyOn ? 'border-violet-300 bg-violet-50/40' : 'border-slate-200 bg-white'}`} data-testid={`perm-card-${p.key}`}>
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <button type="button" onClick={() => !p.locked && togglePerm(p.key)} disabled={p.locked} className="flex-1 text-left flex items-center gap-1.5 min-w-0">
                                <span className={`text-xs font-bold truncate ${p.locked ? 'text-slate-400' : anyOn ? 'text-violet-700' : 'text-slate-700'}`}>{p.label}</span>
                                {p.locked && <span className="text-[9px] text-slate-400 font-semibold">(sempre)</span>}
                              </button>
                              <span className="text-[9px] font-bold text-slate-400 uppercase">{acts.length === 1 ? 'Ver' : `${acts.filter(a => perm[a]).length}/${acts.length}`}</span>
                            </div>
                            <div className={`grid gap-1 ${acts.length >= 5 ? 'grid-cols-5' : 'grid-cols-4'}`}>
                              {/* iter77ct.23: itera sobre `acts` (nao ALL_ACTIONS) para renderizar acoes extendidas
                                  como `view_suggestions` (Sugestao de Compra) e `blind_conference` (Conf. Cega) */}
                              {acts.map(a => {
                                const isOn = !!perm[a];
                                return (
                                  <label key={a} className={`flex items-center justify-center gap-1 py-1 px-1 rounded text-[10px] font-bold transition-colors ${isOn ? 'bg-gradient-to-br from-blue-600 to-violet-600 text-white cursor-pointer shadow-sm' : 'bg-white border border-slate-200 text-slate-500 cursor-pointer hover:border-violet-300'}`}>
                                    <input type="checkbox" checked={isOn} disabled={p.locked} onChange={e => setPerm(p.key, a, e.target.checked)} className="sr-only" data-testid={`perm-${p.key}-${a}`} />
                                    {isOn && <CheckCircle size={9} />}
                                    <span>{ACTION_LABEL[a] || a}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* PDV / Vendas — granular permissions appear ONLY inside the Vendas group */}
                    {group.title === 'Vendas' && (
                      <div className="mt-3 pt-3 border-t border-teal-100 bg-gradient-to-br from-teal-50/60 to-blue-50/40 -mx-3 -mb-3 px-3 pb-3 rounded-b-lg" data-testid="pdv-perms-block">
                        <p className="text-[10px] font-black text-teal-800 uppercase tracking-wider mb-2 flex items-center gap-1.5"><ShoppingCart size={10} />PDV — Acoes especificas</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <label className="flex items-center gap-2 bg-white/80 border border-white rounded-lg p-2 cursor-pointer">
                            <input type="checkbox" checked={!!form.pdv_edit_unit_price} onChange={e => setForm({ ...form, pdv_edit_unit_price: e.target.checked })} className="w-4 h-4 accent-teal-600" data-testid="perm-edit-unit-price" />
                            <div>
                              <p className="text-xs font-bold text-slate-800">Editar valor unitario</p>
                              <p className="text-[10px] text-slate-500">Alterar preco do item no PDV</p>
                            </div>
                          </label>
                          <label className="flex items-center gap-2 bg-white/80 border border-white rounded-lg p-2 cursor-pointer">
                            <input type="checkbox" checked={!!form.pdv_give_surcharge} onChange={e => setForm({ ...form, pdv_give_surcharge: e.target.checked })} className="w-4 h-4 accent-teal-600" data-testid="perm-give-surcharge" />
                            <div>
                              <p className="text-xs font-bold text-slate-800">Aplicar acrescimo</p>
                              <p className="text-[10px] text-slate-500">Adicionar acrescimo na venda</p>
                            </div>
                          </label>
                          <label className="flex items-center gap-2 bg-white/80 border border-white rounded-lg p-2 cursor-pointer">
                            <input type="checkbox" checked={!!form.pdv_give_discount} onChange={e => setForm({ ...form, pdv_give_discount: e.target.checked })} className="w-4 h-4 accent-teal-600" data-testid="perm-give-discount" />
                            <div>
                              <p className="text-xs font-bold text-slate-800">Aplicar desconto</p>
                              <p className="text-[10px] text-slate-500">Dar desconto na venda</p>
                            </div>
                          </label>
                          <div className={`flex items-center gap-2 bg-white/80 border border-white rounded-lg p-2 ${form.pdv_give_discount ? '' : 'opacity-50'}`}>
                            <div className="flex-1">
                              <p className="text-xs font-bold text-slate-800">% max de desconto</p>
                              <p className="text-[10px] text-slate-500">0 a 100 (ex: 15)</p>
                            </div>
                            <input type="number" min="0" max="100" step="0.5" value={form.pdv_max_discount_pct} onChange={e => setForm({ ...form, pdv_max_discount_pct: parseFloat(e.target.value) || 0 })} disabled={!form.pdv_give_discount} className="w-16 px-2 py-1.5 border border-slate-200 rounded-lg text-right text-sm font-bold focus:border-teal-500 outline-none" data-testid="perm-max-discount-pct" />
                          </div>
                          {/* iter156m: nova permissao — mostrar movimentacao de pecas no fechamento */}
                          <label className="flex items-center gap-2 bg-white/80 border border-white rounded-lg p-2 cursor-pointer md:col-span-2">
                            <input type="checkbox" checked={!!form.pdv_show_pieces_movement} onChange={e => setForm({ ...form, pdv_show_pieces_movement: e.target.checked })} className="w-4 h-4 accent-teal-600" data-testid="perm-show-pieces-movement" />
                            <div>
                              <p className="text-xs font-bold text-slate-800">Ver mov. de pecas no fechamento</p>
                              <p className="text-[10px] text-slate-500">Exibe linha "Ontem / Vendidas / Atual" no fechamento e no WhatsApp</p>
                            </div>
                          </label>
                          {/* iter156gj: ocultar quantidade em estoque no PDV / pedido */}
                          <label className="flex items-center gap-2 bg-white/80 border border-white rounded-lg p-2 cursor-pointer md:col-span-2">
                            <input type="checkbox" checked={!!form.pdv_hide_stock_qty} onChange={e => setForm({ ...form, pdv_hide_stock_qty: e.target.checked })} className="w-4 h-4 accent-teal-600" data-testid="perm-hide-stock-qty" />
                            <div>
                              <p className="text-xs font-bold text-slate-800">Ocultar quantidade em estoque (PDV / Pedido)</p>
                              <p className="text-[10px] text-slate-500">O usuario nao ve quantas pecas ha em estoque ao vender. Aparece apenas "sem estoque" quando a filial controla estoque e o produto esta zerado.</p>
                            </div>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* iter77ad: Permissoes especiais — Vendas */}
          <div className="mb-4 border border-sky-200 bg-gradient-to-br from-sky-50/60 to-blue-50/40 rounded-lg p-3" data-testid="sales-perm-block">
            <p className="text-[10px] font-black text-sky-800 uppercase tracking-wider mb-1 flex items-center gap-1.5"><Shield size={10} />Vendas (Permissoes Especiais)</p>
            <label className="flex items-start gap-2 bg-white/80 border border-white rounded-lg p-2.5 cursor-pointer text-xs">
              <input type="checkbox" checked={!!form.sales_only_own} onChange={e => setForm({ ...form, sales_only_own: e.target.checked })} className="mt-0.5 w-4 h-4 accent-sky-600" data-testid="perm-sales-only-own" />
              <div className="flex-1">
                <span className="font-bold text-slate-800">Ve somente as proprias vendas</span>
                <p className="text-[10px] text-slate-500 mt-0.5">Quando ativo, usuarios deste perfil enxergam apenas vendas que eles mesmos lancaram (PDV, Pedidos de Venda, etc). Admin sempre ve todas. Desmarcado (padrao) = ve todas as vendas da filial.</p>
              </div>
            </label>
          </div>

          {/* iter77m: Permissoes especiais — Solicitacao de Compra */}
          <div className="mb-4 border border-amber-200 bg-gradient-to-br from-amber-50/60 to-orange-50/40 rounded-lg p-3" data-testid="purchase-admin-block">
            <p className="text-[10px] font-black text-amber-800 uppercase tracking-wider mb-1 flex items-center gap-1.5"><Shield size={10} />Solicitacao de Compra (Permissoes Especiais)</p>
            <label className="flex items-start gap-2 bg-white/80 border border-white rounded-lg p-2.5 cursor-pointer text-xs">
              <input type="checkbox" checked={!!form.purchase_request_force_edit} onChange={e => setForm({ ...form, purchase_request_force_edit: e.target.checked })} className="w-4 h-4 accent-amber-600 mt-0.5" data-testid="perm-purchase-force-edit" />
              <div className="flex-1">
                <span className="font-bold text-slate-800">Editar / Excluir a qualquer momento</span>
                <p className="text-[10px] text-slate-500 mt-0.5">Quando ativo, este usuario pode editar ou excluir uma Solicitacao de Compra mesmo depois de aprovada pela matriz. Use com criterio.</p>
              </div>
            </label>
          </div>

          {/* iter77ct.11: Permissao especial — editar saldo de conta bancaria (auditado) */}
          <div className="mb-4 border border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-teal-50/40 rounded-lg p-3" data-testid="bank-perm-block">
            <p className="text-[10px] font-black text-emerald-800 uppercase tracking-wider mb-1 flex items-center gap-1.5"><Shield size={10} />Conta Banco (Permissoes Especiais)</p>
            <label className="flex items-start gap-2 bg-white/80 border border-white rounded-lg p-2.5 cursor-pointer text-xs">
              <input type="checkbox" checked={!!form.bank_edit_balance} onChange={e => setForm({ ...form, bank_edit_balance: e.target.checked })} className="w-4 h-4 accent-emerald-600 mt-0.5" data-testid="perm-bank-edit-balance" />
              <div className="flex-1">
                <span className="font-bold text-slate-800">Editar saldo da conta banco</span>
                <p className="text-[10px] text-slate-500 mt-0.5">Quando ativo, este usuario pode ajustar o saldo de contas bancarias diretamente no cadastro. TODA alteracao fica registrada no log de auditoria com valor anterior, novo saldo e motivo.</p>
              </div>
            </label>
          </div>

          {/* Dashboard Cards visibility (iter77br: SEMANTICA INVERTIDA — marcar = MOSTRAR) */}
          <div className="mb-4 border border-violet-200 bg-gradient-to-br from-violet-50/60 to-cyan-50/40 rounded-lg p-3" data-testid="dashboard-cards-block">
            <p className="text-[10px] font-black text-violet-800 uppercase tracking-wider mb-1 flex items-center gap-1.5"><LayoutDashboard size={10} />Cards e Atalhos do Dashboard (Inicio)</p>
            <p className="text-[10px] text-slate-500 mb-2"><strong>Marque para MOSTRAR</strong>. Cards desmarcados ficam ocultos para este perfil. Administradores sempre veem tudo.</p>
            <p className="text-[9px] font-bold text-slate-500 uppercase mt-1 mb-1.5">Cards de KPI</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {[
                { key: 'meta', label: 'Card Meta Atual' },
                { key: 'vendas', label: 'Vendas (R$)' },
                { key: 'qtd_vendas', label: 'Qtd de Vendas' },
                { key: 'ticket_medio', label: 'Ticket Medio' },
                { key: 'qtd_itens', label: 'Produtos Vendidos' },
                { key: 'saldo_estoque', label: 'Saldo em Estoque (un.)' },
                { key: 'a_receber', label: 'A Receber' },
                { key: 'a_pagar', label: 'A Pagar' },
                { key: 'vendas_por_empresa', label: 'Vendas por Empresa (ranking)' },
              ].map(c => {
                const visible = !!form.dashboard_cards?.[c.key];
                return (
                  <label key={c.key} className="flex items-center gap-2 bg-white/80 border border-white rounded-lg p-2 cursor-pointer text-xs">
                    <input type="checkbox" checked={visible} onChange={e => setForm(p => ({ ...p, dashboard_cards: { ...(p.dashboard_cards || {}), [c.key]: e.target.checked } }))} className="w-4 h-4 accent-violet-600" data-testid={`dash-card-toggle-${c.key}`} />
                    <span className={`font-bold ${visible ? 'text-violet-700' : 'text-slate-400'}`}>{c.label}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-[9px] font-bold text-slate-500 uppercase mt-3 mb-1.5">Atalhos (cards clicaveis)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {[
                { key: 'shortcut_sales', label: 'Atalho: Vendas' },
                { key: 'shortcut_clients', label: 'Atalho: Clientes' },
                { key: 'shortcut_products', label: 'Atalho: Produtos' },
                { key: 'shortcut_invoices', label: 'Atalho: NF-e / NFC-e' },
                { key: 'shortcut_purchase_requests', label: 'Atalho: Solicitacao de Compra' },
                // iter156bh: atalho Ponto — so aparece quando o modulo ponto esta habilitado no BT/tenant
                ...(allowedModules?.ponto !== false ? [{ key: 'shortcut_ponto', label: 'Atalho: Bater Ponto' }] : []),
              ].map(c => {
                const visible = !!form.dashboard_cards?.[c.key];
                return (
                  <label key={c.key} className="flex items-center gap-2 bg-white/80 border border-white rounded-lg p-2 cursor-pointer text-xs">
                    <input type="checkbox" checked={visible} onChange={e => setForm(p => ({ ...p, dashboard_cards: { ...(p.dashboard_cards || {}), [c.key]: e.target.checked } }))} className="w-4 h-4 accent-violet-600" data-testid={`dash-shortcut-toggle-${c.key}`} />
                    <span className={`font-bold ${visible ? 'text-violet-700' : 'text-slate-400'}`}>{c.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex gap-2"><button type="submit" disabled={submitting} className={btnPrimary} data-testid="save-profile-btn"><Save size={14} /><span>{submitting ? 'Salvando...' : editing ? 'Atualizar' : 'Criar'}</span></button><button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2.5 text-sm text-gray-500">Cancelar</button></div>
        </form>
      )}
      <div className="space-y-2">
        {profiles.map(p => {
          const allowedLabels = PERM_GROUPS.flatMap(g => g.items).filter(i => {
            const np = normalizePerm(p.permissions?.[i.key]);
            return actionsFor(i.key).some(a => np[a]);
          }).map(i => {
            const np = normalizePerm(p.permissions?.[i.key]);
            const acts = actionsFor(i.key);
            const all = acts.every(a => np[a]);
            return { label: i.label, badge: all ? '' : acts.filter(a => np[a]).map(a => ACTION_LABEL[a][0]).join('') };
          });
          return (
            <div key={p.id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid="profile-row">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2"><Shield size={16} className="text-teal-600" /><span className="font-medium text-sm text-gray-900">{p.name}</span><span className="text-[10px] font-semibold text-gray-400">{allowedLabels.length} permissoes</span></div>
                <div className="flex items-center space-x-1">
                  <button onClick={() => startEdit(p)} className="p-1.5 text-gray-400 hover:text-teal-600" title="Editar" data-testid="edit-profile-btn"><Edit size={14} /></button>
                  <button onClick={() => handleDelete(p.id)} className="p-1.5 text-gray-400 hover:text-red-500" title="Desativar" data-testid="delete-profile-btn"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {allowedLabels.slice(0, 8).map(l => (
                  <span key={l.label} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gradient-to-r from-violet-50 to-cyan-50 text-violet-700 border border-violet-200">{l.label}{l.badge && <span className="ml-1 text-cyan-700">[{l.badge}]</span>}</span>
                ))}
                {allowedLabels.length > 8 && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-50 text-slate-500">+{allowedLabels.length - 8}</span>}
                {allowedLabels.length === 0 && <span className="text-[10px] text-slate-400">Sem permissoes (somente Inicio)</span>}
              </div>
            </div>
          );
        })}
        {profiles.length === 0 && <EmptyState icon={Shield} text="Nenhum perfil de acesso" />}
      </div>
    </div>
  );
};

/* ========== MY PLAN PAGE ========== */
/* ========== CASHBACK PAGE — config + history (iter76) ========== */
const CashbackPage = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchCfg = async () => {
    try { const { data } = await axios.get(`${API}/erp/cashback/config`, { withCredentials: true }); setCfg(data); }
    catch { toast.error('Erro ao carregar config cashback'); setCfg({ enabled: false, pct: 0, validity_days: 90, min_purchase: 0 }); }
  };
  useEffect(() => { fetchCfg(); }, []);

  const save = async () => {
    if (!cfg) return;
    if (cfg.pct < 0 || cfg.pct > 100) { toast.error('Percentual entre 0 e 100'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/erp/cashback/config`, { pct: Number(cfg.pct), validity_days: Number(cfg.validity_days), min_purchase: Number(cfg.min_purchase) }, { withCredentials: true });
      toast.success('Configuracao salva');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center text-gray-400">Carregando...</div>;
  if (!cfg.enabled) {
    return (
      <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 text-center" data-testid="cashback-not-enabled">
        <Wallet size={32} className="mx-auto text-amber-500 mb-2" />
        <p className="text-base font-black text-amber-900">Modulo Cashback desativado</p>
        <p className="text-xs text-amber-700 mt-1">Solicite ao SuperAdmin a ativacao do modulo para o seu tenant.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="cashback-page">
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center space-x-2">
          <Wallet size={18} className="text-amber-600" /><span>Configuracao do Cashback</span>
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          A cada venda que atingir o valor minimo, o cliente recebe um credito no percentual configurado. O credito pode ser usado em compras futuras dentro do prazo de validade.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">% Cashback</label>
            <div className="relative">
              <input type="number" min="0" max="100" step="0.1" value={cfg.pct} onChange={e => setCfg({ ...cfg, pct: e.target.value })} className="w-full px-3 py-2.5 pr-8 bg-white border border-gray-200 rounded-lg text-sm text-gray-900" data-testid="cashback-pct" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Validade (dias)</label>
            <input type="number" min="1" value={cfg.validity_days} onChange={e => setCfg({ ...cfg, validity_days: e.target.value })} className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900" data-testid="cashback-validity" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Valor Minimo (R$)</label>
            <input type="number" min="0" step="0.01" value={cfg.min_purchase} onChange={e => setCfg({ ...cfg, min_purchase: e.target.value })} className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900" data-testid="cashback-min-purchase" />
          </div>
        </div>
        <button onClick={save} disabled={saving} className="mt-4 px-5 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white text-xs font-black" data-testid="cashback-save">
          {saving ? 'Salvando...' : 'Salvar Configuracao'}
        </button>
      </div>
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-2">Como funciona</h3>
        <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
          <li>O cliente precisa estar cadastrado e selecionado no PDV no momento da compra.</li>
          <li>A venda precisa atingir o valor minimo. O cashback acumulado vence em N dias (FIFO ao consumir).</li>
          <li>No PDV, ao selecionar a forma de pagamento <strong>Cashback / Saldo</strong>, o sistema exibe o saldo disponivel e abate ate o limite.</li>
          <li>Vendas canceladas estornam automaticamente creditos e debitos associados.</li>
        </ul>
      </div>
    </div>
  );
};

/* ========== MY PLAN PAGE ========== */
/* ========== INTEGRATIONS PAGE (CRM WhatsApp) ========== */
/* ========================= iter77ao: AgentCRM Mensagens de Resultado ========================= */
const ResultReportsManager = () => {
  const [reports, setReports] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [previewing, setPreviewing] = useState(false);

  const blank = {
    group_name: '', access_profile_id: '', schedule_type: 'daily',
    schedule_time: '09:00', schedule_day_of_week: 0, schedule_day_of_month: 1,
    scope: 'consolidated', company_ids: [], variables: [], active: true,
  };
  const [form, setForm] = useState(blank);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, p, c, v] = await Promise.all([
        axios.get(`${API}/agentcrm/reports`, { withCredentials: true }),
        axios.get(`${ERP}/access-profiles`, { withCredentials: true }),
        axios.get(`${ERP}/companies`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/agentcrm/variables`, { withCredentials: true }),
      ]);
      setReports(r.data.data || []);
      setProfiles(p.data.data || []);
      setCompanies(c.data.data || c.data || []);
      setVariables(v.data.data || []);
    } catch (e) { toast.error('Erro ao carregar mensagens de resultado'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setShowForm(true); setPreviewText(''); };
  const openEdit = (r) => { setEditing(r); setForm({ ...blank, ...r }); setShowForm(true); setPreviewText(''); };

  const save = async () => {
    if (!form.group_name) { toast.error('Nome do grupo obrigatorio'); return; }
    if (!form.access_profile_id) { toast.error('Selecione o Perfil de Acesso'); return; }
    if (!form.variables.length) { toast.error('Selecione ao menos uma variavel'); return; }
    try {
      if (editing) await axios.put(`${API}/agentcrm/reports/${editing.id}`, form, { withCredentials: true });
      else await axios.post(`${API}/agentcrm/reports`, form, { withCredentials: true });
      toast.success(editing ? 'Atualizado' : 'Criado');
      setShowForm(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
  };
  const remove = async (r) => {
    if (!(await confirmDialog({ title: `Excluir grupo "${r.group_name}"?`, confirmLabel: 'Confirmar', danger: true }))) return;
    try { await axios.delete(`${API}/agentcrm/reports/${r.id}`, { withCredentials: true }); toast.success('Excluido'); fetchAll(); }
    catch { toast.error('Erro ao excluir'); }
  };
  const sendNow = async (r) => {
    if (!(await confirmDialog({ title: `Enviar relatorio "${r.group_name}" agora para todos do perfil?`, confirmLabel: 'Confirmar', danger: false }))) return;
    try {
      const { data } = await axios.post(`${API}/agentcrm/reports/${r.id}/send-now`, {}, { withCredentials: true });
      toast.success(`Enviado: ${data.sent || 0} | Falhas: ${data.failed || 0} | Grupos: ${data.groups || 0}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao disparar'); }
  };
  const preview = async () => {
    setPreviewing(true);
    try {
      const { data } = await axios.post(`${API}/agentcrm/reports/preview`, form, { withCredentials: true });
      setPreviewText((data.groups || []).map(g => g.text).join('\n\n— — — — — — — — —\n\n'));
    } catch (e) { toast.error('Erro ao pre-visualizar'); }
    finally { setPreviewing(false); }
  };

  const toggleVar = (k) => setForm(f => ({ ...f, variables: f.variables.includes(k) ? f.variables.filter(x => x !== k) : [...f.variables, k] }));
  const toggleCompany = (id) => setForm(f => ({ ...f, company_ids: f.company_ids.includes(id) ? f.company_ids.filter(x => x !== id) : [...f.company_ids, id] }));

  const fmtDow = { 0: 'Seg', 1: 'Ter', 2: 'Qua', 3: 'Qui', 4: 'Sex', 5: 'Sab', 6: 'Dom' };
  const fmtScope = { consolidated: 'Consolidado', single: 'Uma loja', store_by_store: 'Loja a loja' };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3" data-testid="result-reports-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <span>Mensagens de Resultado</span>
            <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded font-bold">NOVO</span>
          </h2>
          <p className="text-xs text-gray-500">Relatorios automaticos (diario/semanal/mensal) enviados a todos os usuarios de um perfil de acesso.</p>
        </div>
        <button onClick={openNew} className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="new-result-report-btn">
          <Plus size={14} /> Novo Grupo
        </button>
      </div>

      {loading ? <p className="text-xs text-gray-400">Carregando...</p> : reports.length === 0 ? (
        <p className="text-xs text-gray-400 italic">Nenhum grupo cadastrado. Clique em "Novo Grupo" para criar a primeira mensagem de resultado.</p>
      ) : (
        <div className="space-y-2" data-testid="result-reports-list">
          {reports.map(r => (
            <div key={r.id} className="border border-gray-100 rounded-xl p-3 bg-gray-50 flex flex-wrap items-center gap-3 justify-between" data-testid={`result-report-${r.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">{r.group_name} {!r.active && <span className="text-[10px] bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded ml-1">inativo</span>}</p>
                <p className="text-[11px] text-gray-500">
                  {r.schedule_type === 'daily' ? `Diario as ${r.schedule_time}` :
                   r.schedule_type === 'weekly' ? `Semanal (${fmtDow[r.schedule_day_of_week]}) as ${r.schedule_time}` :
                   `Mensal dia ${r.schedule_day_of_month} as ${r.schedule_time}`}
                  {' · '}{fmtScope[r.scope]}
                  {' · '}{r.variables?.length || 0} variavel(eis)
                </p>
                {r.last_run_at && <p className="text-[10px] text-gray-400">Ultima execucao: {new Date(r.last_run_at).toLocaleString('pt-BR')}</p>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => sendNow(r)} className="text-xs px-2 py-1 bg-white border border-teal-200 text-teal-700 rounded hover:bg-teal-50 font-bold" data-testid={`report-send-now-${r.id}`}>Disparar agora</button>
                <button onClick={() => openEdit(r)} className="p-1.5 text-violet-600 hover:bg-violet-50 rounded" data-testid={`report-edit-${r.id}`}><Edit size={14} /></button>
                <button onClick={() => remove(r)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" data-testid={`report-delete-${r.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="result-report-modal">
          <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
            <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-5 py-3 text-white flex items-center justify-between">
              <p className="text-base font-black">{editing ? 'Editar Grupo de Mensagem' : 'Novo Grupo de Mensagem'}</p>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-white/20 rounded"><X size={18} /></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">Nome do Grupo *</label>
                  <input value={form.group_name} onChange={e => setForm({ ...form, group_name: e.target.value })} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Ex: Resultado Diario Gerencia" data-testid="report-form-name" />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">Perfil de Acesso (destinatarios) *</label>
                  <select value={form.access_profile_id} onChange={e => setForm({ ...form, access_profile_id: e.target.value })} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="report-form-profile">
                    <option value="">— Selecione —</option>
                    {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl p-3">
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Agendamento (fuso do tenant)</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-bold text-gray-500">Periodicidade *</label>
                    <select value={form.schedule_type} onChange={e => setForm({ ...form, schedule_type: e.target.value })} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="report-form-type">
                      <option value="daily">Diario</option>
                      <option value="weekly">Semanal</option>
                      <option value="monthly">Mensal</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500">Horario (HH:MM)</label>
                    <input type="time" value={form.schedule_time} onChange={e => setForm({ ...form, schedule_time: e.target.value })} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="report-form-time" />
                  </div>
                  {form.schedule_type === 'weekly' && (
                    <div>
                      <label className="text-xs font-bold text-gray-500">Dia da Semana</label>
                      <select value={form.schedule_day_of_week} onChange={e => setForm({ ...form, schedule_day_of_week: parseInt(e.target.value) })} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm">
                        {[0, 1, 2, 3, 4, 5, 6].map(d => <option key={d} value={d}>{fmtDow[d]}</option>)}
                      </select>
                    </div>
                  )}
                  {form.schedule_type === 'monthly' && (
                    <div>
                      <label className="text-xs font-bold text-gray-500">Dia do Mes</label>
                      <input type="number" min="1" max="31" value={form.schedule_day_of_month} onChange={e => setForm({ ...form, schedule_day_of_month: parseInt(e.target.value) || 1 })} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                    </div>
                  )}
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl p-3">
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Escopo das Lojas</p>
                <div className="space-y-1.5">
                  {[
                    { v: 'consolidated', l: 'Consolidado (soma de todas as lojas selecionadas — ou todas se nenhuma marcada)' },
                    { v: 'single', l: 'Uma loja apenas (a primeira selecionada)' },
                    { v: 'store_by_store', l: 'Loja a loja (1 mensagem por loja selecionada)' },
                  ].map(o => (
                    <label key={o.v} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`report-form-scope-${o.v}`}>
                      <input type="radio" name="scope" checked={form.scope === o.v} onChange={() => setForm({ ...form, scope: o.v })} className="accent-teal-600" />
                      <span>{o.l}</span>
                    </label>
                  ))}
                </div>
                {companies.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {companies.map(c => (
                      <button type="button" key={c.id} onClick={() => toggleCompany(c.id)} className={`text-[11px] px-2 py-1 rounded-full border ${form.company_ids.includes(c.id) ? 'bg-teal-100 border-teal-400 text-teal-800 font-bold' : 'bg-white border-gray-200 text-gray-500'}`} data-testid={`report-form-company-${c.id}`}>
                        {c.name}
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-1.5">Vazio = todas as filiais (consolidado).</p>
              </div>

              <div className="border border-gray-200 rounded-xl p-3">
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Variaveis a Enviar *</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {variables.map(v => (
                    <label key={v.key} className="flex items-center gap-2 cursor-pointer text-sm p-1.5 rounded hover:bg-gray-50">
                      <input type="checkbox" checked={form.variables.includes(v.key)} onChange={() => toggleVar(v.key)} className="accent-teal-600" data-testid={`report-form-var-${v.key}`} />
                      <span className="text-gray-700">{v.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="accent-teal-600" data-testid="report-form-active" />
                <span>Grupo ativo (sera disparado pelo agendador)</span>
              </label>

              {previewText && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="report-preview">
                  <p className="text-[10px] font-bold text-emerald-700 uppercase mb-2">Pre-visualizacao da Mensagem</p>
                  <pre className="text-xs whitespace-pre-wrap text-emerald-900 font-mono">{previewText}</pre>
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 bg-gray-50 px-5 py-3 flex justify-between gap-2">
              <button onClick={preview} disabled={previewing} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50" data-testid="report-preview-btn">{previewing ? 'Gerando...' : 'Pre-visualizar'}</button>
              <div className="flex gap-2">
                <button onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 text-sm font-bold">Cancelar</button>
                <button onClick={save} className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-bold flex items-center gap-1.5" data-testid="report-save-btn"><Save size={14} /> Salvar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const IntegrationsPage = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Teste de integracao ERP -> CRM');
  // iter77ao: AgentCRM tabs
  const [activeTab, setActiveTab] = useState('comunicacao');

  const fetchCfg = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await axios.get(`${API}/integrations/crm`, { withCredentials: true });
      setCfg(data);
    } catch (e) {
      // Avoid the stuck-spinner UX: render the form with a safe default and surface the error inline.
      const detail = e.response?.data?.detail || e.message || 'Erro desconhecido';
      const status = e.response?.status ? `${e.response.status} ` : '';
      setLoadError(`${status}${detail}`);
      toast.error(`Erro ao carregar configuracoes: ${status}${detail}`);
      setCfg({
        gateway_url: '', payload_phone_field: 'celular', payload_message_field: 'message', extra_headers: {},
        base_url: '', token: '', app_id: '', active: false,
        events: { new_order: false, birthday: false, inactive_client: false },
        templates: {}, cupom_code: '', cupom_discount_pct: 0, inactive_days: 30
      });
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchCfg(); }, []);

  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));
  const setEvent = (k, v) => setCfg(prev => ({ ...prev, events: { ...(prev.events || {}), [k]: v } }));
  const setTpl = (k, v) => setCfg(prev => ({ ...prev, templates: { ...(prev.templates || {}), [k]: v } }));

  // When admin pastes a URL containing "agentcrm" or "/sgp/gateway/send/", auto-reset
  // the JSON key names to the SGP defaults (celular/message). This recovers from
  // accidents where the admin copy/pasted the Config JSON into the key-name fields.
  const setGatewayUrl = (v) => setCfg(prev => {
    const looksLikeSgp = /agentcrm|\/sgp\/gateway\/send\//i.test(v || '');
    const looksBroken = (prev.payload_phone_field || '').length > 16 || (prev.payload_message_field || '').length > 16;
    return {
      ...prev,
      gateway_url: v,
      ...(looksLikeSgp ? { payload_phone_field: 'celular', payload_message_field: 'message' } : {}),
      ...(looksBroken ? { payload_phone_field: 'celular', payload_message_field: 'message' } : {})
    };
  });
  const resetSgpDefaults = () => setCfg(prev => ({ ...prev, payload_phone_field: 'celular', payload_message_field: 'message' }));

  const save = async () => {
    setSaving(true);
    try { await axios.put(`${API}/integrations/crm`, cfg, { withCredentials: true }); toast.success('Configuracoes salvas'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };
  const testConnection = async () => {
    setTesting(true);
    try {
      // Save first so the backend reads fresh config
      await axios.put(`${API}/integrations/crm`, cfg, { withCredentials: true });
      const { data } = await axios.post(`${API}/integrations/crm/test`, {
        phone: testPhone,
        message: testMessage
      }, { withCredentials: true });
      if (data.sent && data.status_code >= 200 && data.status_code < 300) {
        toast.success(`Mensagem enviada! HTTP ${data.status_code}`);
      } else if (data.sent) {
        toast.error(`CRM respondeu HTTP ${data.status_code}: ${(data.response || '').slice(0, 80)}`);
      } else {
        toast.error(`Falhou: ${data.reason || ''} ${data.error || ''}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro no teste'); }
    finally { setTesting(false); }
  };
  const runBirthday = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/integrations/crm/triggers/birthday`, {}, { withCredentials: true });
      toast.success(`Aniversarios enviados: ${data.sent} (de ${data.total_clients_with_birthdate} clientes c/ data)`);
    } catch { toast.error('Erro ao disparar'); } finally { setRunning(false); }
  };
  const runInactive = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/integrations/crm/triggers/inactive`, {}, { withCredentials: true });
      toast.success(`Reativacao enviada: ${data.sent} (clientes inativos ha ${data.inactive_days}+ dias)`);
    } catch { toast.error('Erro ao disparar'); } finally { setRunning(false); }
  };
  const loadLogs = async () => {
    try { const { data } = await axios.get(`${API}/integrations/crm/logs`, { withCredentials: true }); setLogs(data.data || []); setShowLogs(true); }
    catch { toast.error('Erro ao carregar logs'); }
  };

  if (loading || !cfg) return <Loader />;

  const inputCls = 'w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500';
  const cardCls = 'bg-white border border-gray-200 rounded-2xl p-5';
  const labelCls = 'block text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider';
  const sectionTitle = 'text-base font-bold text-gray-900 mb-4 flex items-center space-x-2';

  // iter77ao: open the AgentCRM admin panel inferred from the gateway URL.
  const openCrmPanel = () => {
    const u = (cfg?.gateway_url || cfg?.base_url || '').trim();
    if (!u) { toast.error('Configure primeiro a URL do Gateway para abrir o painel.'); return; }
    try {
      const url = new URL(u);
      window.open(`${url.protocol}//${url.host}`, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('URL invalida — confira em Configuracoes.');
    }
  };

  return (
    <div data-testid="integrations-page" className="space-y-4">
      {/* iter77ao: header AgentCRM + Painel button + tabs */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl p-4 flex items-center justify-between text-white shadow">
        <div>
          <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Gerencial</p>
          <h1 className="text-xl font-black">AgentCRM — Integracao WhatsApp</h1>
          <p className="text-xs opacity-90 mt-0.5">Mensagens automaticas, eventos e relatorios via SGP Gateway.</p>
        </div>
        <button onClick={openCrmPanel} className="bg-white/15 hover:bg-white/25 border border-white/30 text-white px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2" data-testid="open-crm-panel-btn">
          <Share2 size={14} /> Abrir Painel AgentCRM
        </button>
      </div>
      <div className="flex gap-1 border-b border-slate-200" data-testid="agentcrm-tabs">
        <button
          onClick={() => setActiveTab('comunicacao')}
          className={`px-4 py-2 text-sm font-bold transition-colors border-b-2 -mb-px ${activeTab === 'comunicacao' ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-comunicacao"
        >COMUNICACAO</button>
        <button
          onClick={() => setActiveTab('configuracoes')}
          className={`px-4 py-2 text-sm font-bold transition-colors border-b-2 -mb-px ${activeTab === 'configuracoes' ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-configuracoes"
        >CONFIGURACOES</button>
      </div>

      {loadError && (
        <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-4 flex items-start justify-between gap-3" data-testid="integrations-load-error">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <span className="text-red-700 text-base font-black">!</span>
            <div className="text-xs">
              <p className="font-black text-red-900">Erro ao carregar configuracoes</p>
              <p className="text-red-700 break-words">{loadError}</p>
              <p className="text-red-600 mt-1">A pagina foi exibida com valores padrao. Voce pode preencher e tentar salvar — se persistir, recarregue ou contate o suporte.</p>
            </div>
          </div>
          <button onClick={fetchCfg} className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-black" data-testid="integrations-retry">Tentar novamente</button>
        </div>
      )}
      {activeTab === 'configuracoes' && (
      <div className={cardCls}>
        <h2 className={sectionTitle}>
          <Share2 size={18} className="text-teal-600" /><span>Conexao com CRM WhatsApp (SGP Gateway)</span>
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          Conecte seu ERP a um CRM WhatsApp via Gateway SGP (ex.: AgentCRM em agentcrm.8ip.com.br). No CRM, va em <strong>SGP Gateway</strong> &rarr; copie a <strong>URL de envio</strong> e cole abaixo. O token ja vem embutido na URL.
        </p>
        <div className="space-y-3 mb-3">
          <div>
            <label className={labelCls}>URL de Envio (Gateway SGP)</label>
            <input
              value={cfg.gateway_url || ''}
              onChange={e => setGatewayUrl(e.target.value)}
              placeholder="https://agentcrm.8ip.com.br/api/sgp/gateway/send/<seu-token>"
              className={inputCls}
              data-testid="crm-gateway-url"
            />
            <p className="text-[10px] text-gray-400 mt-1">Cole a URL completa exibida em <strong>SGP Gateway &rarr; Copiar URL</strong> no painel do CRM.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 border-t border-gray-100">
            <div>
              <label className={labelCls}>Telefone para teste</label>
              <input
                value={testPhone}
                onChange={e => setTestPhone(e.target.value)}
                placeholder="+5562994320308"
                className={inputCls}
                data-testid="crm-test-phone"
              />
              <p className="text-[10px] text-gray-400 mt-1">Numero que recebera a mensagem de teste (com codigo do pais).</p>
            </div>
            <div>
              <label className={labelCls}>Mensagem de teste</label>
              <input
                value={testMessage}
                onChange={e => setTestMessage(e.target.value)}
                className={inputCls}
                data-testid="crm-test-message"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={testConnection}
              disabled={testing || !cfg.gateway_url || !testPhone}
              className="bg-teal-600 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-teal-700 disabled:opacity-50 flex items-center space-x-2"
              data-testid="test-integration-btn-inline"
            >
              <Share2 size={14} /><span>{testing ? 'Enviando...' : 'Enviar Teste'}</span>
            </button>
            <span className="text-[11px] text-gray-500">Salva a configuracao e envia uma mensagem real para o telefone acima.</span>
          </div>

          <details className="text-xs pt-2 border-t border-gray-100">
            <summary className="cursor-pointer text-gray-500 hover:text-gray-700 select-none">Opcoes avancadas (formato do payload JSON)</summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
              <p className="md:col-span-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                ATENCAO: aqui voce define os <strong>nomes das chaves JSON</strong> que o CRM espera receber — NAO o valor.
                Para <strong>AgentCRM/SGP</strong> use <code className="bg-white px-1 py-0.5 rounded">celular</code> e <code className="bg-white px-1 py-0.5 rounded">message</code>.
              </p>
              <div>
                <label className={labelCls}>Nome da chave do telefone</label>
                <input value={cfg.payload_phone_field || ''} onChange={e => set('payload_phone_field', e.target.value)} placeholder="celular" className={inputCls} data-testid="crm-phone-field" />
              </div>
              <div>
                <label className={labelCls}>Nome da chave da mensagem</label>
                <input value={cfg.payload_message_field || ''} onChange={e => set('payload_message_field', e.target.value)} placeholder="message" className={inputCls} data-testid="crm-message-field" />
              </div>
              <div className="md:col-span-2 flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[10px] text-gray-500">Preview do POST: <code className="bg-white px-1 py-0.5 rounded">{`{ "${cfg.payload_phone_field || 'celular'}": "5562...", "${cfg.payload_message_field || 'message'}": "<texto>" }`}</code></p>
                <button type="button" onClick={resetSgpDefaults} className="text-[11px] text-teal-700 bg-white border border-teal-200 px-2 py-1 rounded hover:bg-teal-50" data-testid="reset-sgp-defaults-btn">
                  Resetar para padrao AgentCRM/SGP
                </button>
              </div>
            </div>
          </details>
        </div>
        <label className="flex items-center space-x-2 cursor-pointer">
          <input type="checkbox" checked={!!cfg.active} onChange={e => set('active', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" data-testid="crm-active" />
          <span className="text-sm text-gray-700">Integracao ativa (mensagens serao enviadas)</span>
        </label>
      </div>
      )}

      {activeTab === 'comunicacao' && (
      <div className={cardCls}>
        <h2 className={sectionTitle}><span>Eventos & Mensagens</span></h2>
        <p className="text-xs text-gray-500 mb-4">Selecione os eventos do ERP que devem disparar mensagens via CRM. Use <code>{'{{name}}'}</code>, <code>{'{{store}}'}</code>, <code>{'{{order_number}}'}</code>, <code>{'{{total}}'}</code>, <code>{'{{cupom}}'}</code>, <code>{'{{discount}}'}</code>, <code>{'{{days}}'}</code> nos templates.</p>

        {/* New Order */}
        <div className="border border-gray-100 rounded-xl p-3 mb-3 bg-gray-50">
          <label className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-900">Novo Pedido</span>
            <input type="checkbox" checked={!!cfg.events?.new_order} onChange={e => setEvent('new_order', e.target.checked)} className="w-4 h-4 rounded text-teal-600" data-testid="event-new-order" />
          </label>
          <textarea value={cfg.templates?.new_order || ''} onChange={e => setTpl('new_order', e.target.value)} rows={2} className={inputCls + ' resize-none'} placeholder="Mensagem enviada quando um pedido eh criado" />
        </div>

        {/* Birthday */}
        <div className="border border-gray-100 rounded-xl p-3 mb-3 bg-gray-50">
          <label className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-900">Aniversario do Cliente</span>
            <input type="checkbox" checked={!!cfg.events?.birthday} onChange={e => setEvent('birthday', e.target.checked)} className="w-4 h-4 rounded text-teal-600" data-testid="event-birthday" />
          </label>
          <textarea value={cfg.templates?.birthday || ''} onChange={e => setTpl('birthday', e.target.value)} rows={2} className={inputCls + ' resize-none'} placeholder="Mensagem enviada no dia do aniversario" />
          <button type="button" onClick={runBirthday} disabled={running || !cfg.active} className="mt-2 text-xs bg-white border border-teal-200 text-teal-700 px-3 py-1.5 rounded-lg hover:bg-teal-50 disabled:opacity-50">Disparar agora (hoje)</button>
        </div>

        {/* Inactive */}
        <div className="border border-gray-100 rounded-xl p-3 mb-3 bg-gray-50">
          <label className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-900">Cliente Inativo</span>
            <input type="checkbox" checked={!!cfg.events?.inactive_client} onChange={e => setEvent('inactive_client', e.target.checked)} className="w-4 h-4 rounded text-teal-600" data-testid="event-inactive" />
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
            <div>
              <label className={labelCls}>Dias sem comprar</label>
              <input type="number" min="1" value={cfg.inactive_days || 30} onChange={e => set('inactive_days', parseInt(e.target.value || '30'))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Cupom de Desconto</label>
              <input value={cfg.cupom_code || ''} onChange={e => set('cupom_code', e.target.value)} placeholder="VOLTA10" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Desconto (%)</label>
              <input type="number" min="0" max="100" value={cfg.cupom_discount_pct || 0} onChange={e => set('cupom_discount_pct', parseFloat(e.target.value || '0'))} className={inputCls} />
            </div>
          </div>
          <textarea value={cfg.templates?.inactive_client || ''} onChange={e => setTpl('inactive_client', e.target.value)} rows={2} className={inputCls + ' resize-none'} placeholder="Mensagem de reativacao com cupom" />
          <button type="button" onClick={runInactive} disabled={running || !cfg.active} className="mt-2 text-xs bg-white border border-teal-200 text-teal-700 px-3 py-1.5 rounded-lg hover:bg-teal-50 disabled:opacity-50">Disparar agora</button>
        </div>
      </div>
      )}

      {/* iter77ao: Mensagens de Resultado (Comunicacao tab) */}
      {activeTab === 'comunicacao' && <ResultReportsManager />}

      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center space-x-2 shadow-sm disabled:opacity-50" data-testid="save-integration-btn">
          <Save size={16} /><span>{saving ? 'Salvando...' : 'Salvar'}</span>
        </button>
        <button onClick={testConnection} disabled={testing || !(cfg.gateway_url || cfg.base_url)} className="bg-white border border-gray-200 text-gray-700 px-5 py-2.5 rounded-lg font-semibold text-sm hover:border-teal-300 hover:text-teal-700 flex items-center space-x-2 transition-colors disabled:opacity-50" data-testid="test-integration-btn">
          <Share2 size={16} /><span>{testing ? 'Testando...' : 'Testar Conexao'}</span>
        </button>
        <button onClick={loadLogs} className="bg-white border border-gray-200 text-gray-600 px-5 py-2.5 rounded-lg font-semibold text-sm hover:border-gray-300 flex items-center space-x-2 transition-colors" data-testid="logs-integration-btn">
          <FileText size={16} /><span>Ver Logs</span>
        </button>
      </div>

      {showLogs && (
        <div className={cardCls}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold text-gray-900">Ultimos Eventos Enviados</h3>
            <button onClick={() => setShowLogs(false)} className="text-xs text-gray-500 hover:text-gray-700">Fechar</button>
          </div>
          {logs.length === 0 ? <p className="text-sm text-gray-500">Nenhum evento registrado.</p> : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto">
              {logs.map(l => (
                <div key={l.id} className="text-xs border border-gray-100 rounded-lg p-2.5 bg-gray-50 flex justify-between items-start">
                  <div>
                    <span className={`inline-block px-2 py-0.5 rounded font-semibold mr-2 ${(l.status_code || 0) >= 200 && (l.status_code || 0) < 300 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{l.status_code || 'ERR'}</span>
                    <span className="font-semibold text-gray-700">{l.event_type}</span>
                    <span className="text-gray-400 ml-2">{new Date(l.created_at).toLocaleString('pt-BR')}</span>
                    {l.error && <p className="text-red-600 mt-1">{l.error}</p>}
                    {l.response && <p className="text-gray-500 mt-1 truncate max-w-md">{l.response}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MyPlanPage = () => {
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const f = async () => { try { const { data } = await axios.get(`${ERP}/my-plan`, { withCredentials: true }); setPlanData(data); } catch {} finally { setLoading(false); } };
    f();
  }, []);
  if (loading) return <Loader />;
  const plan = planData?.plan;
  const tenant = planData?.tenant;
  return (
    <div data-testid="my-plan-page">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Tenant info */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center space-x-2"><Building2 size={18} className="text-teal-600" /><span>Informacoes da Conta</span></h3>
          <div className="space-y-3">
            <div className="flex justify-between"><span className="text-sm text-gray-500">Empresa</span><span className="text-sm font-medium text-gray-900">{tenant?.company_name || '-'}</span></div>
            <div className="flex justify-between"><span className="text-sm text-gray-500">Subdominio</span><span className="text-sm font-medium text-gray-900">{tenant?.subdomain || '-'}</span></div>
            <div className="flex justify-between"><span className="text-sm text-gray-500">Status</span><span className={`text-xs font-semibold px-2 py-1 rounded ${tenant?.license_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{tenant?.license_active ? 'Ativa' : 'Inativa'}</span></div>
            {tenant?.trial_expires_at && <div className="flex justify-between"><span className="text-sm text-gray-500">Trial expira</span><span className="text-sm font-medium text-orange-600">{new Date(tenant.trial_expires_at).toLocaleDateString('pt-BR')}</span></div>}
            <div className="flex justify-between"><span className="text-sm text-gray-500">Criada em</span><span className="text-sm text-gray-700">{tenant?.created_at ? new Date(tenant.created_at).toLocaleDateString('pt-BR') : '-'}</span></div>
          </div>
        </div>
        {/* Plan info */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center space-x-2"><Crown size={18} className="text-amber-500" /><span>Plano Atual</span></h3>
          {plan ? (
            <div>
              <div className="mb-4">
                <p className="text-2xl font-bold text-gray-900">{plan.name}</p>
                <p className="text-lg font-bold text-teal-600">R$ {(plan.price || 0).toFixed(2)}<span className="text-sm text-gray-500 font-normal">/{plan.billing_cycle || 'mes'}</span></p>
              </div>
              <div className="space-y-2 mb-4">
                {plan.max_products && <p className="text-xs text-gray-500">Ate {plan.max_products} produtos</p>}
                {plan.max_orders && <p className="text-xs text-gray-500">Ate {plan.max_orders} pedidos/mes</p>}
                {!plan.max_products && <p className="text-xs text-gray-500">Produtos ilimitados</p>}
              </div>
              {plan.features?.length > 0 && (
                <div className="space-y-1.5">
                  {plan.features.map((f, i) => (
                    <div key={i} className="flex items-start space-x-2"><CheckCircle size={14} className="text-teal-500 flex-shrink-0 mt-0.5" /><span className="text-sm text-gray-700">{f}</span></div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Nenhum plano associado</p>
          )}
        </div>
      </div>
    </div>
  );
};

/* ========== STOCK PAGE ========== */
const StockPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editStock, setEditStock] = useState('');
  useEffect(() => {
    const f = async () => { try { const { data } = await axios.get(`${ERP}/stock`, { withCredentials: true }); setData(data); } catch {} finally { setLoading(false); } };
    f();
  }, []);
  const handleSaveStock = async (id) => {
    try { await axios.put(`${ERP}/stock/${id}`, { stock: parseInt(editStock) || 0 }, { withCredentials: true }); toast.success('Estoque atualizado!'); setEditingId(null); const { data: d } = await axios.get(`${ERP}/stock`, { withCredentials: true }); setData(d); }
    catch { toast.error('Erro'); }
  };
  if (loading) return <Loader />;
  if (!data) return <p className="text-center text-gray-500 py-12">Sem dados</p>;
  const filtered = data.products?.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.sku.toLowerCase().includes(searchTerm.toLowerCase())) || [];
  return (
    <div data-testid="stock-page">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white border border-gray-200 rounded-xl p-4"><p className="text-[10px] font-bold text-gray-400 uppercase">Produtos</p><p className="text-xl font-bold text-gray-900">{data.summary?.total_items || 0}</p></div>
        <div className="bg-white border border-gray-200 rounded-xl p-4"><p className="text-[10px] font-bold text-gray-400 uppercase">Unidades</p><p className="text-xl font-bold text-gray-900">{(data.summary?.total_stock || 0).toLocaleString('pt-BR')}</p></div>
        <div className="bg-white border border-gray-200 rounded-xl p-4"><p className="text-[10px] font-bold text-gray-400 uppercase">Valor Total</p><p className="text-xl font-bold text-teal-600">R$ {(data.summary?.total_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p></div>
        <div className="bg-white border border-gray-200 rounded-xl p-4"><p className="text-[10px] font-bold text-gray-400 uppercase">Estoque Baixo</p><p className={`text-xl font-bold ${data.summary?.low_stock_count > 0 ? 'text-red-600' : 'text-gray-900'}`}>{data.summary?.low_stock_count || 0}</p></div>
      </div>
      <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-center gap-2" data-testid="stock-readonly-notice">
        <AlertTriangle size={14} className="flex-shrink-0" />
        <span>O estoque e atualizado <strong>automaticamente</strong> via <strong>Movimentacao</strong> (entrada/saida/balanco), <strong>Transferencia</strong> recebida e <strong>Compras</strong>. Esta tela e apenas para consulta.</span>
      </div>
      <div className="mb-4"><input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar produto..." className={inputClass} data-testid="stock-search" /></div>
      <div className="space-y-2">
        {filtered.map(p => (
          <div key={p.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid="stock-row">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm text-gray-900 truncate">{p.name}</p>
              <p className="text-xs text-gray-500">{p.sku} {p.brand ? `• ${p.brand}` : ''}</p>
            </div>
            <div className="flex items-center space-x-3 flex-shrink-0">
              <div className="text-right">
                <span className={`text-sm font-bold ${p.stock <= 5 ? 'text-red-600' : 'text-gray-900'}`}>{p.stock}</span>
                <p className="text-[10px] text-gray-400">R$ {p.price.toFixed(2)}</p>
              </div>
              {p.stock <= 5 && <AlertTriangle size={14} className="text-red-500" />}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <EmptyState icon={Boxes} text="Nenhum produto encontrado" />}
      </div>
    </div>
  );
};

/* ========== STOCK MOVEMENTS PAGE (Entrada / Saida / Balanco) ========== */
const StockMovementsPage = ({ companyId, companies }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  // iter156dk: settings do tenant (permite duplicar item + modo)
  const [dupAllow, setDupAllow] = useState(false);
  const [dupMode, setDupMode] = useState('aggregate');
  // iter156eb: setting Bipar-adiciona-qty-1
  const [scanAddOneEnabled, setScanAddOneEnabled] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/company-settings${companyId ? `?company_id=${companyId}` : ''}`, { withCredentials: true });
        setDupAllow(!!data?.stock_allow_duplicate_items);
        setDupMode(data?.stock_duplicate_mode || 'aggregate');
        setScanAddOneEnabled(!!data?.stock_scan_add_one);
      } catch { /* usa defaults */ }
    })();
  }, [companyId]);
  // Default: ultimos 7 dias (LOCAL timezone)
  const defaultStart = localDaysAgoISO(6);
  const defaultEnd = localDateISO();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [itemSearch, setItemSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    type: 'entrada', // entrada | saida | balanco
    document_date: localDateISO(),
    document_number: '', reason: '', notes: '', items: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingQty, setPendingQty] = useState('1');
  // iter77n: navegacao por teclado nas sugestoes de produto
  const [activeIdx, setActiveIdx] = useState(0);
  const qtyInputRef = useRef(null);
  const productSearchRef = useRef(null); // iter77cd: refocus produto após adicionar
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null); // movement being viewed
  // iter77ct.40: modal de cadastro rapido de produto
  const [showQuickProduct, setShowQuickProduct] = useState(false);
  // iter77ct.54: quando editando produto ja selecionado (lapisinho), guarda o produto sendo editado
  const [editingProduct, setEditingProduct] = useState(null);

  const fetchList = useCallback(async () => {
    if (!companyId) { setLoading(false); return; }
    try {
      const params = new URLSearchParams({ company_id: companyId });
      if (filterType) params.set('type', filterType);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (itemSearch) params.set('item_search', itemSearch);
      const { data } = await axios.get(`${ERP}/stock-movements?${params}`, { withCredentials: true });
      setList(data.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [companyId, filterType, startDate, endDate, itemSearch]);
  useEffect(() => { fetchList(); }, [fetchList]);

  // Product autocomplete
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) { setSearchResults([]); setActiveIdx(0); return; }
    const t = setTimeout(async () => {
      try {
        // iter156ea: exclui servicos (nao movimentam estoque)
        const { data } = await axios.get(`${ERP}/pdv/products?q=${encodeURIComponent(searchTerm)}&limit=10&exclude_services=true${companyId ? `&company_id=${companyId}` : ''}`, { withCredentials: true });
        setSearchResults(data.data || []);
        setActiveIdx(0);
      } catch { setSearchResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [searchTerm, companyId]);

  const selectProduct = (p) => {
    // iter156dk: bloqueio original substituido por politica configuravel.
    //   - dupAllow=false: comportamento antigo (bloqueia com toast).
    //   - dupAllow=true + dupMode=aggregate: aceita, commitPending vai SOMAR no existente.
    //   - dupAllow=true + dupMode=separate: aceita, commitPending vai criar linha nova.
    if (form.items.find(i => i.product_id === p.id && (i.variant_id || '') === (p.variant_id || '')) && !dupAllow) {
      toast.info('Produto ja na lista'); return;
    }
    setPendingProduct(p);
    // iter156dt2: formata a qtd inicial com decimais da UoM em pt-BR (KG stock=1234 vira "1.234,000")
    const _u0 = (p?.fiscal?.unit_commercial || p?.unit || 'UN');
    const _dec0 = decimalsForUnit(_u0);
    const _initVal = form.type === 'balanco' ? Number(p.stock || 0) : 1;
    setPendingQty(_dec0 > 0 ? _initVal.toLocaleString('pt-BR', { minimumFractionDigits: _dec0, maximumFractionDigits: _dec0 }) : String(_initVal));
    setSearchTerm(''); setSearchResults([]);
    // iter77n: foco automatico no input de quantidade
    setTimeout(() => { if (qtyInputRef.current) { qtyInputRef.current.focus(); qtyInputRef.current.select(); } }, 30);
  };

  // iter156eb: adicao rapida via BIP (scanner de codigo de barras).
  //   - Sempre qty=1. Se produto ja na lista, incrementa (independente de dupAllow).
  //   - Nao abre o painel "Confirmar Adicao" — direto na lista, fluxo continuo do scanner.
  const scanAddOne = (p) => {
    setForm(f => {
      const existingIdx = f.items.findIndex(i => i.product_id === p.id && (i.variant_id || '') === (p.variant_id || ''));
      const _u = (p?.fiscal?.unit_commercial || p?.unit || 'UN');
      const _dec = decimalsForUnit(_u);
      const _fmt = (n) => _dec > 0 ? n.toLocaleString('pt-BR', { minimumFractionDigits: _dec, maximumFractionDigits: _dec }) : String(n);
      if (existingIdx >= 0) {
        // incrementa
        return { ...f, items: f.items.map((it, i) => {
          if (i !== existingIdx) return it;
          if (f.type === 'balanco') {
            const cur = smartQtyParse(it.new_quantity) || 0;
            return { ...it, new_quantity: _fmt(cur + 1) };
          }
          const cur = smartQtyParse(it.quantity) || 0;
          return { ...it, quantity: _fmt(cur + 1) };
        })};
      }
      // adiciona novo
      const base = {
        product_id: p.id, product_name: p.name, name: p.name, sku: p.sku,
        variant_id: p.variant_id || null, variant_label: p.variant_label || '',
        unit: _u, stock: Number(p.stock || 0),
      };
      if (f.type === 'balanco') return { ...f, items: [...f.items, { ...base, quantity: _fmt(0), new_quantity: _fmt(1) }] };
      return { ...f, items: [...f.items, { ...base, quantity: _fmt(1) }] };
    });
    setSearchTerm(''); setSearchResults([]);
    // Refocus para proximo scan
    setTimeout(() => { if (productSearchRef.current) productSearchRef.current.focus(); }, 30);
  };

  // iter77n: navegacao por teclado no campo de busca
  const onSearchKeyDown = (e) => {
    if (searchResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // iter156eb: quando scanAddOne ativo, prioriza match exato por sku OU barcode
      // (fluxo do leitor de codigo de barras). Se ha match exato -> add direto qty=1.
      if (scanAddOneEnabled) {
        const q = String(searchTerm || '').trim().toLowerCase();
        if (q) {
          const exact = searchResults.find(p =>
            String(p.sku || '').toLowerCase() === q ||
            String(p.barcode || '').toLowerCase() === q ||
            String(p.ean || '').toLowerCase() === q ||
            String(p.gtin || '').toLowerCase() === q
          );
          if (exact) { scanAddOne(exact); return; }
        }
      }
      const p = searchResults[activeIdx];
      if (p) selectProduct(p);
    } else if (e.key === 'Escape') {
      setSearchResults([]);
    }
  };
  const commitPending = () => {
    if (!pendingProduct) { toast.info('Selecione um produto'); return; }
    const qty = smartQtyParse(pendingQty) || 0;
    // iter156fn: no inventario (balanco) a contagem ZERO e valida — zera o estoque do produto
    const isBalanco = form.type === 'balanco';
    if (isBalanco && String(pendingQty ?? '').trim() === '') { toast.error('Informe a quantidade contada (0 e permitido no inventario)'); return; }
    if (!isBalanco && qty <= 0) { toast.error('Informe uma quantidade maior que zero'); return; }
    if (qty < 0) { toast.error('Quantidade nao pode ser negativa'); return; }
    const p = pendingProduct;
    setForm(f => {
      // iter156dk: quando duplicar for permitido em modo AGREGAR, procura item ja
      // existente e SOMA (para balanco: soma new_quantity; senao: soma quantity).
      const existingIdx = f.items.findIndex(i => i.product_id === p.id && (i.variant_id || '') === (p.variant_id || ''));
      if (existingIdx >= 0 && dupAllow && dupMode === 'aggregate') {
        const items = [...f.items];
        const cur = items[existingIdx];
        if (f.type === 'balanco') {
          items[existingIdx] = { ...cur, new_quantity: (parseFloat(cur.new_quantity) || 0) + qty };
        } else {
          items[existingIdx] = { ...cur, quantity: (parseFloat(cur.quantity) || 0) + qty };
        }
        return { ...f, items };
      }
      // Comportamento padrao ou modo SEPARATE: sempre adiciona nova linha.
      return {
        ...f,
        items: [...f.items, {
          product_id: p.id, name: p.name, sku: p.sku || '',
          variant_id: p.variant_id || null, variant_label: p.variant_label || '',
          current_stock: p.stock || 0,
          quantity: f.type === 'balanco' ? 0 : qty,
          new_quantity: f.type === 'balanco' ? qty : null,
          unit_cost: p.cost_price || 0
        }]
      };
    });
    setPendingProduct(null); setPendingQty('1');
    // iter77cd: devolve foco ao campo produto após adicionar (fluxo mais rápido de digitação)
    setTimeout(() => { if (productSearchRef.current) { productSearchRef.current.focus(); productSearchRef.current.select?.(); } }, 30);
  };
  const updItem = (idx, patch) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const rmItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const resetForm = () => { setForm({ type: 'entrada', document_date: localDateISO(), document_number: '', reason: '', notes: '', items: [] }); setPendingProduct(null); setPendingQty('1'); };

  const submit = async () => {
    if (!companyId) { toast.error('Selecione uma empresa'); return; }
    if (!form.items.length) { toast.error('Adicione ao menos 1 item'); return; }
    setSaving(true);
    try {
      const payload = {
        company_id: companyId,
        type: form.type,
        document_date: form.document_date,
        document_number: form.document_number,
        reason: form.reason,
        notes: form.notes,
        items: form.items.map(it => ({
          product_id: it.product_id, name: it.name, sku: it.sku,
          variant_id: it.variant_id || null, variant_label: it.variant_label || '',
          quantity: parseFloat(it.quantity) || 0,
          new_quantity: form.type === 'balanco' ? (parseFloat(it.new_quantity) || 0) : null,
          previous_quantity: form.type === 'balanco' ? (parseFloat(it.current_stock) || 0) : null,
          unit_cost: parseFloat(it.unit_cost) || 0
        }))
      };
      await axios.post(`${ERP}/stock-movements`, payload, { withCredentials: true });
      toast.success(`Movimentacao ${form.type} registrada!`);
      setShowForm(false); resetForm(); fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao registrar movimentacao');
    } finally { setSaving(false); }
  };

  const typeColors = { entrada: 'bg-emerald-50 text-emerald-700 border-emerald-200', saida: 'bg-rose-50 text-rose-700 border-rose-200', balanco: 'bg-indigo-50 text-indigo-700 border-indigo-200' };
  const typeLabel = { entrada: 'Entrada', saida: 'Saida', balanco: 'Balanco' };

  if (loading) return <Loader />;

  return (
    <div data-testid="stock-movements-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-black text-slate-900">Movimentacao de Estoque</h2>
          <p className="text-xs text-slate-500">Entradas, saidas e balancos por empresa</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className={btnPrimary} data-testid="new-stock-movement-btn">
          <Plus size={16} /><span>Nova Movimentacao</span>
        </button>
      </div>

      {/* Filtros: tipo + periodo + busca por item */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="mov-filters">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Buscar Item</label>
          <input value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder="Nome ou SKU do produto" className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs" data-testid="mov-filter-item-search" />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">De</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs" data-testid="mov-filter-start" />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Ate</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs" data-testid="mov-filter-end" />
        </div>
        <button type="button" onClick={() => { setItemSearch(''); setStartDate(defaultStart); setEndDate(defaultEnd); setFilterType(''); }} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-100" data-testid="mov-filter-reset">Limpar (7 dias)</button>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[{ k: '', l: 'Todas' }, { k: 'entrada', l: 'Entradas' }, { k: 'saida', l: 'Saidas' }, { k: 'balanco', l: 'Balancos' }].map(f => (
          <button key={f.k} onClick={() => setFilterType(f.k)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${filterType === f.k ? 'bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-400'}`} data-testid={`mov-filter-${f.k || 'all'}`}>{f.l}</button>
        ))}
      </div>

      {/* List — colunas (Cod / Data / Qtd Entradas / Valor Entradas / Qtd Saidas / Valor Saidas / Acoes) */}
      {(() => {
        const handleDelete = async (m) => {
          if (!(await confirmDialog({ title: `Excluir movimentacao #${m.code ?? m.number ?? m.document_number ?? '—'}?\n\nO estoque sera REVERTIDO ao estado anterior desta movimentacao.`, confirmLabel: 'Confirmar', danger: true }))) return;
          try {
            const { data } = await axios.delete(`${ERP}/stock-movements/${m.id}`, { withCredentials: true });
            toast.success(data.message || 'Movimentacao excluida');
            fetchList();
          } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao excluir'); }
        };
        const sumEntries = (m) => (m.items || []).filter(it => (it.delta || 0) > 0).reduce((a, it) => a + Math.abs(it.delta || 0), 0);
        const sumExits = (m) => (m.items || []).filter(it => (it.delta || 0) < 0).reduce((a, it) => a + Math.abs(it.delta || 0), 0);
        const valEntries = (m) => (m.items || []).filter(it => (it.delta || 0) > 0).reduce((a, it) => a + Math.abs(it.delta || 0) * (parseFloat(it.unit_cost) || 0), 0);
        const valExits = (m) => (m.items || []).filter(it => (it.delta || 0) < 0).reduce((a, it) => a + Math.abs(it.delta || 0) * (parseFloat(it.unit_cost) || 0), 0);
        const valTotal = (m) => (m.items || []).reduce((a, it) => a + Math.abs(it.delta || 0) * (parseFloat(it.unit_cost) || 0), 0);
        const TYPE_BADGE = { entrada: 'bg-emerald-100 text-emerald-700', saida: 'bg-rose-100 text-rose-700', balanco: 'bg-indigo-100 text-indigo-700' };
        const columns = [
          // iter156cx: mostra APENAS o code numerico (backend faz backfill nas legadas).
          // Removido fallback pra r.id?.slice(-6) que era alfanumerico e confundia o usuario.
          { key: 'code', label: 'Cod', render: r => <span className="font-mono text-xs text-slate-700">{r.code ?? r.number ?? '—'}</span>, sortValue: r => r.code ?? r.number ?? 0 },
          { key: 'type', label: 'Tipo', render: r => <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${TYPE_BADGE[r.type] || 'bg-slate-100 text-slate-600'}`}>{(r.type || '').toUpperCase()}</span>, sortValue: r => r.type },
          { key: 'date', label: 'Data', render: r => <span className="text-xs text-slate-600">{fmtDateBR(r.document_date || r.created_at)}</span>, sortValue: r => r.document_date || r.created_at },
          { key: 'qtd_entries', label: 'Qtd. Entradas', align: 'right', render: r => <span className="text-xs font-mono text-emerald-700">{sumEntries(r) > 0 ? sumEntries(r) : '—'}</span>, sortValue: r => sumEntries(r) },
          { key: 'val_entries', label: 'Valor Entradas', align: 'right', render: r => <span className="text-xs text-emerald-700">{valEntries(r) > 0 ? fmtBRL(valEntries(r)) : '—'}</span>, sortValue: r => valEntries(r) },
          { key: 'qtd_exits', label: 'Qtd. Saidas', align: 'right', render: r => <span className="text-xs font-mono text-rose-700">{sumExits(r) > 0 ? sumExits(r) : '—'}</span>, sortValue: r => sumExits(r) },
          { key: 'val_exits', label: 'Valor Saidas', align: 'right', render: r => <span className="text-xs text-rose-700">{valExits(r) > 0 ? fmtBRL(valExits(r)) : '—'}</span>, sortValue: r => valExits(r) },
          { key: 'val_total', label: 'Valor Total', align: 'right', render: r => <span className="text-xs font-bold text-slate-900">{valTotal(r) > 0 ? fmtBRL(valTotal(r)) : '—'}</span>, sortValue: r => valTotal(r) },
          // iter156cx: renomeado "Motivo" -> "Descricao" (mesmo campo backend `reason`, apenas label na UI).
          { key: 'reason', label: 'Descricao', render: r => <span className="text-xs text-slate-500 truncate">{r.reason || '—'}</span>, sortValue: r => r.reason },
        ];
        const renderActions = (r) => (
          <div className="flex items-center gap-1">
            <button onClick={() => setViewing(r)} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors" title="Visualizar" data-testid={`mov-view-${r.id}`}>
              <Eye size={14} />
            </button>
            <button onClick={() => handleDelete(r)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors" title="Excluir e reverter estoque" data-testid={`mov-delete-${r.id}`}>
              <Trash2 size={14} />
            </button>
          </div>
        );
        return (
          <DataTable
            prefKey="stock-movements"
            columns={columns}
            defaultVisible={columns.map(c => c.key)}
            defaultOrder={columns.map(c => c.key)}
            rows={list}
            keyField="id"
            emptyText="Nenhuma movimentacao registrada"
            renderActions={renderActions}
            mobilePrimary={(r) => <><span className="uppercase font-black">{typeLabel[r.type] || (r.type || '').toUpperCase()}</span>{r.reason ? <span className="text-slate-500 font-normal"> | {r.reason}</span> : null}</>}
            mobileSecondary={(r) => <><span className="font-mono">#{r.code ?? r.number ?? '—'}</span> · {fmtDateBR(r.document_date || r.created_at)} · {(r.items || []).length} itens</>}
            totalLabel={`${list.length} movimentacoes`}
          />
        );
      })()}

      {/* View movement modal */}
      {viewing && (() => {
        const TYPE_BADGE = { entrada: 'bg-emerald-100 text-emerald-700', saida: 'bg-rose-100 text-rose-700', balanco: 'bg-indigo-100 text-indigo-700' };
        return (
          <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" data-testid="mov-view-modal">
            <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-2xl md:rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-gradient-to-r from-slate-800 to-slate-900 px-5 py-4 flex items-center justify-between z-10">
                <h3 className="text-white font-black text-sm flex items-center gap-2"><Eye size={16} /> Movimentacao #{viewing.code ?? viewing.number ?? '—'}</h3>
                <button onClick={() => setViewing(null)} className="text-white/80 hover:text-white" data-testid="mov-view-close"><X size={18} /></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-50 rounded-lg p-2.5"><p className="text-[10px] font-bold text-slate-400 uppercase">Tipo</p><p className={`mt-1 inline-block px-2 py-0.5 rounded text-[10px] font-bold ${TYPE_BADGE[viewing.type] || 'bg-slate-200 text-slate-700'}`}>{(viewing.type || '').toUpperCase()}</p></div>
                  <div className="bg-slate-50 rounded-lg p-2.5"><p className="text-[10px] font-bold text-slate-400 uppercase">Data</p><p className="text-sm font-bold text-slate-800 mt-0.5">{fmtDateBR(viewing.document_date || viewing.created_at)}</p></div>
                  <div className="bg-slate-50 rounded-lg p-2.5"><p className="text-[10px] font-bold text-slate-400 uppercase">Doc Nº</p><p className="text-sm font-bold text-slate-800 mt-0.5 truncate">{viewing.document_number || '—'}</p></div>
                  <div className="bg-slate-50 rounded-lg p-2.5"><p className="text-[10px] font-bold text-slate-400 uppercase">Itens</p><p className="text-sm font-bold text-slate-800 mt-0.5">{(viewing.items || []).length}</p></div>
                </div>
                {viewing.reason && <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5"><p className="text-[10px] font-bold text-amber-700 uppercase">Descricao</p><p className="text-sm text-slate-700 mt-0.5">{viewing.reason}</p></div>}
                {viewing.notes && <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5"><p className="text-[10px] font-bold text-slate-500 uppercase">Observacoes</p><p className="text-sm text-slate-700 mt-0.5">{viewing.notes}</p></div>}
                <div className="border border-slate-200 rounded-xl overflow-x-auto">
                  <table className="w-full text-xs min-w-[600px]">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-slate-500">
                        <th className="px-3 py-2">#</th>
                        <th className="px-3 py-2">SKU</th>
                        <th className="px-3 py-2">Produto</th>
                        <th className="px-3 py-2 text-right">Antes</th>
                        <th className="px-3 py-2 text-right">Delta</th>
                        <th className="px-3 py-2 text-right">Depois</th>
                        <th className="px-3 py-2 text-right">Vl Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewing.items || []).map((it, i) => (
                        <tr key={i} className="border-b border-slate-100 last:border-0">
                          <td className="px-3 py-1.5 text-slate-400">{i + 1}</td>
                          <td className="px-3 py-1.5 font-mono text-slate-600">{it.sku || '—'}</td>
                          <td className="px-3 py-1.5 text-slate-800">{it.name || it.product_id}</td>
                          <td className="px-3 py-1.5 text-right text-slate-500 font-mono">{it.previous_quantity ?? '—'}</td>
                          <td className={`px-3 py-1.5 text-right font-mono font-bold ${(it.delta || 0) > 0 ? 'text-emerald-600' : (it.delta || 0) < 0 ? 'text-rose-600' : 'text-slate-600'}`}>{(it.delta || 0) > 0 ? '+' : ''}{it.delta || 0}</td>
                          <td className="px-3 py-1.5 text-right text-slate-800 font-mono font-bold">{it.new_quantity ?? '—'}</td>
                          <td className="px-3 py-1.5 text-right text-slate-500">{it.unit_cost != null ? fmtBRL(it.unit_cost) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Form modal — Alvotec-style compact */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="stock-movement-form-modal">
          <div className="bg-white w-full max-w-5xl h-[98vh] md:h-auto md:max-h-[94vh] overflow-y-auto rounded-t-2xl md:rounded-2xl shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-3 flex items-center justify-between z-10">
              <h3 className="text-white font-bold text-sm flex items-center gap-2"><ArrowDownUp size={16} /> Inserir Movimentacao de Estoque</h3>
              <button onClick={() => setShowForm(false)} className="text-white/80 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              {/* Atencao block */}
              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-3 text-[11px] text-slate-700">
                <p className="font-bold text-amber-700 mb-1 flex items-center gap-1"><AlertTriangle size={12} /> Atencao!</p>
                <p>• Se foi uma <strong>entrada errada</strong> → lance uma saida de ajuste.</p>
                <p>• Se foi uma <strong>saida errada</strong> → lance uma entrada de ajuste.</p>
                <p>• Se for <strong>diferenca em contagem</strong> → use balanco para igualar o fisico ao sistema.</p>
              </div>

              {/* Header line: Tipo | Data | NDoc | Descricao — mobile-first layout
                  iter156cy: distribuicao pedida pelo usuario:
                    linha 1 (50/50): Tipo | Data
                    linha 2 (25/75): N Documento | Descricao (maior)
                  Usando grid-cols-4 uniforme (mesmo padrao mobile+desktop) para nao
                  precisar de breakpoints. `min-w-0` nas celulas garante que o input
                  date nao expanda alem do container (bug do campo Data sendo cortado). */}
              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-2 min-w-0">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Tipo</label>
                  <select value={form.type} onChange={e => { const t = e.target.value; setForm(f => ({ ...f, type: t, items: f.items.map(it => ({ ...it, quantity: t === 'balanco' ? it.current_stock : 1, new_quantity: t === 'balanco' ? it.current_stock : null })) })); }} className={`${inputClass} w-full`} data-testid="mov-type-select">
                    <option value="entrada">Entrada</option>
                    <option value="saida">Saida</option>
                    <option value="balanco">Balanco</option>
                  </select>
                </div>
                <div className="col-span-2 min-w-0">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Data</label>
                  {/* iter156cz: iOS Safari renderiza <input type="date"> com largura nativa maior
                      que o container. `appearance:none` + `w-full` + `min-w-0` forcam respeito ao grid. */}
                  <input
                    type="date"
                    value={form.document_date}
                    onChange={e => setForm(f => ({ ...f, document_date: e.target.value }))}
                    className={`${inputClass} w-full min-w-0 box-border`}
                    style={{ WebkitAppearance: 'none', appearance: 'none', maxWidth: '100%' }}
                    data-testid="mov-doc-date"
                  />
                </div>
                <div className="col-span-1 min-w-0">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5 whitespace-nowrap">N Doc.</label>
                  <input value={form.document_number} onChange={e => setForm(f => ({ ...f, document_number: e.target.value }))} className={`${inputClass} w-full`} placeholder="NF" data-testid="mov-doc-number" />
                </div>
                <div className="col-span-3 min-w-0">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Descricao</label>
                  <input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} className={`${inputClass} w-full`} placeholder="compra, perda, ajuste..." data-testid="mov-reason" />
                </div>
              </div>

              {/* Product add line: mobile-friendly stacking
                  iter156cx: quantidade + botao Adicionar agora ocupam a linha inteira
                  no mobile pra impedir "campos apertados". Grid: mobile 1 coluna;
                  md 12 colunas com 7/2/3 split (mesmo do desktop). */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end pt-2 border-t border-slate-100">
                <div className="md:col-span-7 relative">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Produto</label>
                  {pendingProduct ? (
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-2 border-blue-300 rounded-lg text-xs">
                      <Search size={13} className="text-blue-500 flex-shrink-0" />
                      <span className="font-mono text-slate-500">({pendingProduct.sku})</span>
                      <span className="font-bold text-slate-900 truncate flex-1">{pendingProduct.name}</span>
                      <span className="text-slate-500">R$ {(pendingProduct.cost_price || 0).toFixed(2).replace('.', ',')} | Qt: {pendingProduct.stock || 0}</span>
                      {/* iter77ct.54: lapisinho para editar produto in-place — atualiza cost/price/name/etc */}
                      <button
                        type="button"
                        onClick={() => { setEditingProduct(pendingProduct); setShowQuickProduct(true); }}
                        className="text-amber-600 hover:text-amber-700 flex-shrink-0"
                        title="Editar produto (custo, venda, nome...)"
                        data-testid="mov-pending-edit"
                      ><Edit size={13} /></button>
                      <button type="button" onClick={() => setPendingProduct(null)} className="text-rose-500 hover:text-rose-700 flex-shrink-0" data-testid="mov-pending-clear"><X size={13} /></button>
                    </div>
                  ) : (
                    <div className="flex gap-1.5">
                      <div className="relative flex-1">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input ref={productSearchRef} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onKeyDown={onSearchKeyDown} placeholder="Buscar (setas + Enter, ou clique)..." className={`${inputClass} pl-7`} data-testid="mov-product-search" />
                        {searchResults.length > 0 && (
                          <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                            {searchResults.map((p, idx) => (
                              <button key={p.id} type="button" onClick={() => selectProduct(p)} onMouseEnter={() => setActiveIdx(idx)} className={`w-full text-left px-3 py-1.5 border-b last:border-b-0 border-slate-100 flex items-center justify-between text-xs ${idx === activeIdx ? 'bg-blue-100' : 'hover:bg-blue-50'}`} data-testid={`mov-product-opt-${p.id}`}>
                                <div className="min-w-0"><p className="font-semibold text-slate-900 truncate">({p.sku}) {p.name}{idx === activeIdx && <span className="ml-2 text-[9px] font-extrabold text-blue-700">(Enter)</span>}</p><p className="text-[10px] text-slate-500">R$ {(p.cost_price || 0).toFixed(2).replace('.', ',')} • Estoque: {p.stock || 0}</p></div>
                                <Plus size={12} className="text-blue-600 flex-shrink-0" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* iter77ct.40+.54: cadastro rapido de produto (icone PackagePlus) */}
                      <button
                        type="button"
                        onClick={() => { setEditingProduct(null); setShowQuickProduct(true); }}
                        className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg flex items-center gap-1.5 flex-shrink-0 shadow-md transition-colors"
                        title="Cadastrar produto rapido"
                        data-testid="mov-quick-add-product-btn"
                      >
                        <PackagePlus size={15} strokeWidth={2.5} />
                        <span className="text-xs font-bold hidden sm:inline">Novo</span>
                      </button>
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">
                    {form.type === 'balanco' ? 'Nova Qtd' : 'Quantidade'}
                    {/* iter156dt: badge com a unidade + dica de casas decimais */}
                    {pendingProduct && (() => {
                      const _u = (pendingProduct?.fiscal?.unit_commercial || pendingProduct?.unit || 'UN').toUpperCase();
                      const _d = decimalsForUnit(_u);
                      return _d > 0
                        ? <span className="ml-1 text-[9px] font-black px-1 py-0.5 rounded bg-violet-100 text-violet-700">{_u} · {_d} dec</span>
                        : <span className="ml-1 text-[9px] font-black px-1 py-0.5 rounded bg-slate-100 text-slate-600">{_u}</span>;
                    })()}
                  </label>
                  <input
                    ref={qtyInputRef}
                    type="text"
                    inputMode="decimal"
                    value={pendingQty}
                    onChange={e => {
                      // iter156dt: aplica mascara auto so quando unit=KG/L/M e o usuario
                      // NAO digitou "." ou "," — quem prefere ponto direto mantem controle.
                      const _u = (pendingProduct?.fiscal?.unit_commercial || pendingProduct?.unit || 'UN');
                      setPendingQty(smartQtyMask(e.target.value, _u));
                    }}
                    onFocus={e => e.target.select()}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitPending(); } }}
                    className={inputClass}
                    data-testid="mov-pending-qty"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="hidden md:block text-[10px] font-bold text-slate-500 uppercase mb-0.5 invisible">.</label>
                  <button type="button" onClick={commitPending} disabled={!pendingProduct} className={`w-full ${btnPrimary}`} data-testid="mov-add-btn"><Plus size={13} /><span>Adicionar</span></button>
                </div>
              </div>

              {/* Items table — iter156cx: overflow-x-auto no wrapper para mobile
                  (antes cortava colunas em telas <sm). Cards nao substituem tabela porque
                  os campos de qtd/custo precisam ser editaveis inline. */}
              {form.items.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-x-auto -mx-4 md:mx-0">
                  <table className="w-full text-xs min-w-[560px]">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-[10px] font-bold text-slate-500 uppercase">
                        <th className="px-2 py-2 w-8">#</th>
                        <th className="px-2 py-2 w-20">SKU</th>
                        <th className="px-2 py-2">Nome</th>
                        <th className="px-2 py-2 w-20 text-right">Qtd.Disp</th>
                        <th className="px-2 py-2 w-24 text-right">{form.type === 'balanco' ? 'Nova Qtd' : 'Qtd'}</th>
                        {form.type !== 'balanco' && <th className="px-2 py-2 w-24 text-right">Vl.Unit R$</th>}
                        {form.type !== 'balanco' && <th className="px-2 py-2 w-24 text-right">Vl.Total</th>}
                        {form.type === 'balanco' && <th className="px-2 py-2 w-20 text-right">Diferenca</th>}
                        <th className="px-2 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((it, idx) => {
                        const qty = parseFloat(it.quantity) || 0;
                        const cost = parseFloat(it.unit_cost) || 0;
                        const diff = (parseFloat(it.new_quantity) || 0) - it.current_stock;
                        return (
                          <tr key={idx} className="border-b last:border-b-0 border-slate-100 hover:bg-slate-50/50" data-testid={`mov-item-${idx}`}>
                            <td className="px-2 py-1.5 text-slate-500 font-mono">{idx + 1}</td>
                            <td className="px-2 py-1.5 text-slate-600 font-mono text-[11px] truncate max-w-[80px]" title={it.sku}>{it.sku}</td>
                            <td className="px-2 py-1.5 text-slate-900 font-semibold truncate" title={it.name}>{it.name}</td>
                            <td className="px-2 py-1.5 text-right text-slate-500 font-mono">{it.current_stock}</td>
                            <td className="px-2 py-1.5 text-right">
                              {/* iter156aa: onWheel bloqueia scroll do mouse (que alterava o valor
                                  para numeros astronomicos ao rolar a pagina). onKeyDown bloqueia
                                  as setas Cima/Baixo (comportamento nativo do input number). */}
                              <input type="number" step="0.01" min="0"
                                value={form.type === 'balanco' ? it.new_quantity : it.quantity}
                                onChange={e => updItem(idx, form.type === 'balanco' ? { new_quantity: e.target.value } : { quantity: e.target.value })}
                                onWheel={e => e.currentTarget.blur()}
                                onKeyDown={e => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
                                className="w-20 px-2 py-1 border border-slate-300 rounded text-xs text-right focus:border-blue-500 focus:outline-none" data-testid={`mov-item-qty-${idx}`} />
                            </td>
                            {form.type !== 'balanco' && (
                              <td className="px-2 py-1.5 text-right">
                                {/* iter156aa: mesmo bloqueio + rounding 2 casas no blur (evita "12,8725") */}
                                <input type="number" step="0.01" min="0"
                                  value={it.unit_cost}
                                  onChange={e => updItem(idx, { unit_cost: e.target.value })}
                                  onBlur={e => {
                                    const v = parseFloat(e.target.value);
                                    if (isFinite(v)) updItem(idx, { unit_cost: v.toFixed(2) });
                                  }}
                                  onWheel={e => e.currentTarget.blur()}
                                  onKeyDown={e => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
                                  className="w-20 px-2 py-1 border border-slate-300 rounded text-xs text-right focus:border-blue-500 focus:outline-none" />
                              </td>
                            )}
                            {form.type !== 'balanco' && (
                              <td className="px-2 py-1.5 text-right text-slate-900 font-bold">{(qty * cost).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                            )}
                            {form.type === 'balanco' && (
                              <td className={`px-2 py-1.5 text-right font-mono font-bold ${diff >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{diff >= 0 ? '+' : ''}{diff.toFixed(2)}</td>
                            )}
                            <td className="px-2 py-1.5 text-right">
                              <button onClick={() => rmItem(idx)} className="text-rose-500 hover:text-rose-700" data-testid={`mov-item-remove-${idx}`}><Trash2 size={13} /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                      <tr className="text-xs font-bold">
                        <td colSpan={4} className="px-2 py-2 text-slate-600">Total: {form.items.length} {form.items.length === 1 ? 'item' : 'itens'} | Qtd: {form.items.reduce((s, it) => s + (parseFloat(form.type === 'balanco' ? it.new_quantity : it.quantity) || 0), 0).toFixed(2)}</td>
                        <td className="px-2 py-2"></td>
                        {form.type !== 'balanco' && <td className="px-2 py-2 text-right text-slate-500">Subtotal:</td>}
                        {form.type !== 'balanco' && <td className="px-2 py-2 text-right text-blue-700 font-black">R$ {form.items.reduce((s, it) => s + ((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_cost) || 0)), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>}
                        {form.type === 'balanco' && <td className="px-2 py-2"></td>}
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Observacoes */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Observacoes</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={inputClass} placeholder="Faca aqui suas observacoes..." data-testid="mov-notes" />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-2.5 flex justify-end gap-2 z-10">
              <button onClick={() => setShowForm(false)} className="px-4 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">Cancelar</button>
              <button onClick={submit} disabled={saving || !form.items.length} className={btnPrimary} data-testid="mov-submit-btn"><Save size={13} /><span>{saving ? 'Salvando...' : 'Registrar'}</span></button>
            </div>
          </div>
        </div>
      )}
      {/* iter77ct.40+.54: modal de cadastro/edicao rapida de produto (mobile-first) */}
      {showQuickProduct && (
        <QuickProductModal
          initialProduct={editingProduct}
          onClose={() => { setShowQuickProduct(false); setEditingProduct(null); }}
          onCreated={(newProd) => {
            setShowQuickProduct(false);
            setEditingProduct(null);
            // auto-seleciona/atualiza o produto na linha de busca
            setPendingProduct(newProd);
            setSearchTerm('');
            setSearchResults([]);
            setTimeout(() => { if (qtyInputRef.current) { qtyInputRef.current.focus(); qtyInputRef.current.select?.(); } }, 60);
          }}
        />
      )}
    </div>
  );
};

/* ========== QUICK PRODUCT MODAL (iter77ct.40) ==========
 * Cadastro rapido usado em fluxos onde o usuario esta lancando uma movimentacao
 * e precisa criar um produto novo sem sair da tela. Campos minimos + QR-code
 * scan visivel apenas no mobile. Ao salvar, chama onCreated(produtoCompleto)
 * para o parent auto-selecionar o produto na linha de busca.
 */
const QuickProductModal = ({ onClose, onCreated, initialProduct = null }) => {
  const isEdit = !!initialProduct?.id;
  const [form, setForm] = useState(() => ({
    sku: initialProduct?.sku || '',
    name: initialProduct?.name || '',
    barcode: initialProduct?.barcode || '',
    cost_price: initialProduct?.cost_price != null ? String(initialProduct.cost_price) : '',
    price: initialProduct?.price != null ? String(initialProduct.price) : '',
    category_id: initialProduct?.category_id || '',
  }));
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  // detecta mobile (aparelho pequeno OU touch) para exibir o botao QR-code
  const isMobile = typeof window !== 'undefined' && (window.matchMedia?.('(max-width: 640px)').matches || 'ontouchstart' in window);
  const nameRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/categories`, { withCredentials: true });
        setCategories(Array.isArray(data) ? data : (data?.data || []));
      } catch { /* ignore */ }
    })();
    setTimeout(() => nameRef.current?.focus(), 80);
  }, []);

  const parsePrice = (v) => {
    const n = parseFloat(String(v || '').replace(',', '.'));
    return isFinite(n) ? n : 0;
  };

  const generateSku = () => {
    const ts = Date.now().toString(36).toUpperCase().slice(-5);
    setForm(f => ({ ...f, sku: `Q-${ts}` }));
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Informe o nome do produto'); return; }
    if (!form.sku.trim()) { toast.error('Informe o SKU (ou clique em Gerar)'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        sku: form.sku.trim(),
        barcode: form.barcode.trim() || null,
        cost_price: parsePrice(form.cost_price),
        price: parsePrice(form.price),
        category_id: form.category_id || null,
      };
      let data;
      if (isEdit) {
        // iter77ct.54: modo edicao — PUT preserva stock existente e outros campos
        await axios.put(`${API}/products/${initialProduct.id}`, payload, { withCredentials: true });
        // PUT retorna apenas {message} — reconstroi o produto atualizado com os campos novos + os herdados.
        data = { ...initialProduct, ...payload };
        toast.success('Produto atualizado! Continua selecionado abaixo.');
      } else {
        payload.stock = 0;
        const resp = await axios.post(`${API}/products`, payload, { withCredentials: true });
        data = resp.data;
        toast.success('Produto cadastrado! Ja selecionado abaixo.');
      }
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? 'Erro ao atualizar produto' : 'Erro ao cadastrar produto'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" data-testid="quick-product-modal">
      <form
        onSubmit={e => { e.preventDefault(); submit(); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }}
        className="bg-white w-full max-w-md h-[92vh] md:h-auto md:max-h-[92vh] overflow-y-auto rounded-t-2xl md:rounded-2xl shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className={`sticky top-0 bg-gradient-to-r ${isEdit ? 'from-amber-500 to-orange-600' : 'from-emerald-500 to-teal-600'} px-4 py-3 flex items-center justify-between z-10`}>
          <h3 className="text-white font-bold text-sm flex items-center gap-2">
            {isEdit ? <><Edit size={16} /> Editar Produto</> : <><PackagePlus size={16} /> Cadastro Rapido de Produto</>}
          </h3>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-[11px] text-slate-500 -mt-1">Preencha o essencial para conseguir movimentar agora. Voce pode complementar (imagens, tabela de precos, fiscal) depois no cadastro completo.</p>

          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">SKU *</label>
            <div className="flex gap-1.5">
              <input
                value={form.sku}
                onChange={e => setForm(f => ({ ...f, sku: e.target.value }))}
                placeholder="Ex: PROD-001"
                className="flex-1 px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                data-testid="quick-product-sku"
              />
              <button type="button" onClick={generateSku} className="px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-xs font-bold text-slate-700 hover:bg-slate-200 flex items-center gap-1" title="Gerar SKU automatico">
                <Sparkles size={13} /> Gerar
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Nome *</label>
            <input
              ref={nameRef}
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Nome do produto"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
              data-testid="quick-product-name"
            />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Codigo de Barras</label>
            <div className="flex gap-1.5">
              <input
                value={form.barcode}
                onChange={e => setForm(f => ({ ...f, barcode: e.target.value }))}
                placeholder="Bipe ou digite"
                inputMode="numeric"
                className="flex-1 px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                data-testid="quick-product-barcode"
              />
              {isMobile && (
                <button
                  type="button"
                  onClick={() => setShowScanner(true)}
                  className="px-3 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-1"
                  title="Escanear QR/Codigo de barras"
                  data-testid="quick-product-scan-btn"
                >
                  <QrCode size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Preco Custo</label>
              <input
                value={form.cost_price}
                onChange={e => setForm(f => ({ ...f, cost_price: e.target.value }))}
                placeholder="0,00"
                inputMode="decimal"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                data-testid="quick-product-cost"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Preco Venda</label>
              <input
                value={form.price}
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                placeholder="0,00"
                inputMode="decimal"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                data-testid="quick-product-price"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Categoria</label>
            <select
              value={form.category_id}
              onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-emerald-500 focus:outline-none bg-white"
              data-testid="quick-product-category"
            >
              <option value="">Sem categoria</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-3 flex justify-end gap-2 z-10">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-semibold text-slate-500 hover:text-slate-700">Cancelar</button>
          <button
            type="submit"
            disabled={saving}
            className={`px-4 py-2 ${isEdit ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-500 hover:bg-emerald-600'} text-white rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50`}
            data-testid="quick-product-save"
          >
            <Save size={13} /> {saving ? 'Salvando...' : (isEdit ? 'Atualizar' : 'Cadastrar e Selecionar')}
          </button>
        </div>
        {showScanner && (
          <QRScannerModal
            onScan={(code) => { setForm(f => ({ ...f, barcode: code })); setShowScanner(false); toast.success('Codigo lido'); }}
            onClose={() => setShowScanner(false)}
          />
        )}
      </form>
    </div>
  );
};

/* ========== STOCK TRANSFERS PAGE (Empresa Origem -> Destino, com recebimento cego) ========== */
const StockTransfersPage = ({ companyId, companies }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showReceive, setShowReceive] = useState(null); // transfer obj
  const [blindReceipt, setBlindReceipt] = useState(false);
  const [canEditTransfers, setCanEditTransfers] = useState(false);  // iter77ct.17
  const [viewing, setViewing] = useState(null); // transfer being viewed
  // iter77ct.17: busca livre + ordenacao clicavel
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');  // created_at | code | origin | destination | status
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };
  const [form, setForm] = useState({
    origin_company_id: companyId || '', destination_company_id: '',
    document_date: localDateISO(), document_number: '', notes: '', items: [], packages: []
  });
  // iter77ct.4: tipos de volume (fardo/sacola/caixa)
  const [packagingTypes, setPackagingTypes] = useState([]);
  const [pkgTypeId, setPkgTypeId] = useState('');
  const [pkgQty, setPkgQty] = useState('1');
  const [pkgDropOpen, setPkgDropOpen] = useState(false);
  const [newPkgTypeName, setNewPkgTypeName] = useState('');
  const pkgDropRef = useRef(null);
  useEffect(() => {
    if (!pkgDropOpen) return;
    const onDoc = (e) => { if (pkgDropRef.current && !pkgDropRef.current.contains(e.target)) setPkgDropOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pkgDropOpen]);
  const fetchPackagingTypes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${ERP}/packaging-types`, { withCredentials: true });
      setPackagingTypes(data.data || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { fetchPackagingTypes(); }, [fetchPackagingTypes]);
  const createPackagingType = async () => {
    const name = (newPkgTypeName || '').trim();
    if (!name) { toast.info('Digite um nome'); return; }
    try {
      const { data } = await axios.post(`${ERP}/packaging-types`, { name }, { withCredentials: true });
      toast.success(`Tipo "${data.name}" adicionado`);
      setNewPkgTypeName('');
      fetchPackagingTypes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };
  const removePackagingType = async (id) => {
    if (!(await confirmDialog({ title: 'Excluir este tipo de volume?', message: 'Registros historicos NAO serao afetados.', danger: true }))) return;
    try {
      await axios.delete(`${ERP}/packaging-types/${id}`, { withCredentials: true });
      toast.success('Tipo excluido');
      fetchPackagingTypes();
      if (pkgTypeId === id) setPkgTypeId('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };
  const addPackage = () => {
    if (!pkgTypeId) { toast.info('Selecione um tipo de volume'); return; }
    const qty = parseFloat(pkgQty) || 0;
    if (qty <= 0) { toast.error('Informe uma quantidade maior que zero'); return; }
    const t = packagingTypes.find(p => p.id === pkgTypeId);
    if (!t) return;
    setForm(f => ({ ...f, packages: [...(f.packages || []), { type_id: t.id, name: t.name, qty_sent: qty }] }));
    setPkgTypeId(''); setPkgQty('1');
  };
  const rmPackage = (idx) => setForm(f => ({ ...f, packages: (f.packages || []).filter((_, i) => i !== idx) }));
  const updPackage = (idx, patch) => setForm(f => ({ ...f, packages: (f.packages || []).map((p, i) => i === idx ? { ...p, ...patch } : p) }));
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingQty, setPendingQty] = useState('1');
  // iter77n: navegacao por teclado nas sugestoes de produto (transfer)
  const [activeIdx, setActiveIdx] = useState(0);
  const qtyInputRef = useRef(null);
  const productSearchRef = useRef(null); // iter77cd: refocus produto após adicionar
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(f => ({ ...f, origin_company_id: companyId || f.origin_company_id }));
  }, [companyId]);

  const [tab, setTab] = useState('all'); // 'all' | 'out' | 'in'
  const fetchList = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      if (tab !== 'all') params.set('scope', tab);
      const { data } = await axios.get(`${ERP}/stock-transfers?${params}`, { withCredentials: true });
      setList(data.data || []);
      setCanEditTransfers(!!data.can_edit_transfers);  // iter77ct.17: usado para desmascarar blind na UI
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [companyId, tab]);
  useEffect(() => { fetchList(); }, [fetchList]);

  // Load tenant setting for blind receipt
  // iter77ch: também respeita permissão por perfil de acesso `stock_transfers.blind_conference` — força cego mesmo com config OFF.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/company-settings`, { withCredentials: true });
        const tenantBlind = !!data?.stock_blind_receipt;
        const profileBlind = !!(window.__canDo && window.__canDo('blind_conference', 'stock_transfers'));
        setBlindReceipt(tenantBlind || profileBlind);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) { setSearchResults([]); setActiveIdx(0); return; }
    const t = setTimeout(async () => {
      try {
        // iter156ea: exclui servicos (nao movimentam estoque)
        const { data } = await axios.get(`${ERP}/pdv/products?q=${encodeURIComponent(searchTerm)}&limit=10&exclude_services=true${companyId ? `&company_id=${companyId}` : ''}`, { withCredentials: true });
        setSearchResults(data.data || []);
        setActiveIdx(0);
      } catch { setSearchResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [searchTerm, companyId]);

  const selectProduct = (p) => {
    // iter77cj: permite lançar o mesmo produto várias vezes (linhas separadas), apenas alerta
    if (form.items.find(i => i.product_id === p.id)) {
      toast.info(`"${p.name}" já está na lista — será adicionada nova linha`);
    }
    setPendingProduct(p);
    // iter156dt2: formata a qtd inicial com decimais da UoM em pt-BR (KG=1 vira "1,000")
    const _uT = (p?.fiscal?.unit_commercial || p?.unit || 'UN');
    const _decT = decimalsForUnit(_uT);
    setPendingQty(_decT > 0 ? (1).toLocaleString('pt-BR', { minimumFractionDigits: _decT, maximumFractionDigits: _decT }) : '1');
    setSearchTerm(''); setSearchResults([]);
    // iter77n: foco automatico no input de quantidade
    setTimeout(() => { if (qtyInputRef.current) { qtyInputRef.current.focus(); qtyInputRef.current.select(); } }, 30);
  };

  // iter77n: navegacao por teclado no campo de busca (transfer)
  const onSearchKeyDown = (e) => {
    if (searchResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const p = searchResults[activeIdx];
      if (p) selectProduct(p);
    } else if (e.key === 'Escape') {
      setSearchResults([]);
    }
  };
  const commitPending = () => {
    if (!pendingProduct) { toast.info('Selecione um produto'); return; }
    const qty = smartQtyParse(pendingQty) || 0;
    if (qty <= 0) { toast.error('Informe uma quantidade maior que zero'); return; }
    const p = pendingProduct;
    setForm(f => ({ ...f, items: [...f.items, { product_id: p.id, name: p.name, sku: p.sku || '', current_stock: p.stock || 0, quantity: qty }] }));
    setPendingProduct(null); setPendingQty('1');
    // iter77cd: devolve foco ao campo produto após adicionar
    setTimeout(() => { if (productSearchRef.current) { productSearchRef.current.focus(); productSearchRef.current.select?.(); } }, 30);
  };
  const updItem = (idx, patch) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const rmItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const [editingId, setEditingId] = useState(null); // iter77cj: id da transferência sendo editada
  const resetForm = () => { setForm({ origin_company_id: companyId || '', destination_company_id: '', document_date: localDateISO(), document_number: '', notes: '', items: [], packages: [] }); setPendingProduct(null); setPendingQty('1'); setPkgTypeId(''); setPkgQty('1'); setEditingId(null); };
  // iter77cj: abre o form em modo edição, pré-populando com os dados da transferência
  // iter77ct.19: se status='received', abre o modal de EDICAO das quantidades RECEBIDAS
  //             (nao mais o form de edicao da transferencia, que so faz sentido em draft/rejected).
  const doEdit = (t) => {
    if (t.status === 'received') {
      setShowReceive({ ...t, __editMode: true });
      return;
    }
    setEditingId(t.id);
    setForm({
      origin_company_id: t.origin_company_id || '',
      destination_company_id: t.destination_company_id || '',
      document_date: (t.document_date || '').slice(0, 10) || localDateISO(),
      document_number: t.document_number || '',
      notes: t.notes || '',
      items: (t.items || []).map(it => ({ product_id: it.product_id, name: it.name, sku: it.sku || '', current_stock: 0, quantity: it.quantity || 0 })),
      packages: (t.packages || []).map(p => ({ type_id: p.type_id, name: p.name, qty_sent: p.qty_sent || 0 })),
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.origin_company_id || !form.destination_company_id) { toast.error('Selecione origem e destino'); return; }
    if (form.origin_company_id === form.destination_company_id) { toast.error('Origem e destino nao podem ser iguais'); return; }
    if (!form.items.length) { toast.error('Adicione ao menos 1 item'); return; }
    setSaving(true);
    try {
      const payload = {
        origin_company_id: form.origin_company_id,
        destination_company_id: form.destination_company_id,
        document_date: form.document_date,
        document_number: form.document_number,
        notes: form.notes,
        items: form.items.map(it => ({ product_id: it.product_id, name: it.name, sku: it.sku, quantity: parseFloat(it.quantity) || 0 })),
        // iter77ct.4: volumes (fardo/sacola/caixa) — opcional
        packages: (form.packages || []).map(p => ({ type_id: p.type_id, name: p.name, qty_sent: parseFloat(p.qty_sent) || 0 })).filter(p => p.qty_sent > 0),
      };
      if (editingId) {
        await axios.put(`${ERP}/stock-transfers/${editingId}`, payload, { withCredentials: true });
        toast.success('Transferencia atualizada');
      } else {
        await axios.post(`${ERP}/stock-transfers`, payload, { withCredentials: true });
        toast.success('Transferencia criada como rascunho');
      }
      setShowForm(false); resetForm(); fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSaving(false); }
  };

  // Modern confirm modal — substitui window.confirm
  const [confirmAction, setConfirmAction] = useState(null); // { title, body, color, onConfirm }
  const askConfirm = (cfg) => setConfirmAction(cfg);

  const doSend = async (t) => {
    askConfirm({
      title: `Enviar Transferencia #${t.code || t.id?.slice(-6)}?`,
      body: `O estoque sera DEBITADO da empresa de origem (${compName(t.origin_company_id)}).\n\nApos o envio, somente o destino (${compName(t.destination_company_id)}) podera receber ou estornar.`,
      color: 'blue',
      icon: 'send',
      label: 'Enviar agora',
      onConfirm: async () => {
        try {
          await axios.post(`${ERP}/stock-transfers/${t.id}/send`, {}, { withCredentials: true });
          toast.success('Transferencia enviada — estoque debitado da origem');
          fetchList();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
      }
    });
  };

  // iter77ct.71: alimenta o estoque de uma transferencia recebida em modo manual.
  // O botao so aparece quando status='received' && stock_fed=false. Apos click, o
  // backend seta stock_fed=true e o botao some (idempotencia — sem duplo credito).
  const doFeedStock = async (t) => {
    askConfirm({
      title: `Alimentar Estoque da Transferencia #${t.code || t.id?.slice(-6)}?`,
      body: `As quantidades recebidas serao CREDITADAS no estoque da filial ${compName(t.destination_company_id)}. Esta acao e irreversivel: uma vez alimentado, o botao ficara inativo (nao permite duplo credito).`,
      color: 'emerald',
      icon: 'boxes',
      label: 'Alimentar Estoque',
      onConfirm: async () => {
        try {
          const { data } = await axios.post(`${ERP}/stock-transfers/${t.id}/feed-stock`, {}, { withCredentials: true });
          toast.success(data?.message || 'Estoque alimentado');
          fetchList();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao alimentar estoque'); }
      }
    });
  };

  const doReceive = async (transfer, receivedMap) => {
    try {
      // iter77cj: envia items na MESMA ORDEM do transfer (matching por índice no backend)
      const items = (transfer.items || []).map((it, idx) => ({ product_id: it.product_id, received_quantity: parseFloat(receivedMap.items?.[idx] ?? receivedMap[idx]) || 0 }));
      // iter77ct.4: volumes recebidos por indice
      const packages = (transfer.packages || []).map((p, idx) => ({ qty_received: parseFloat(receivedMap.packages?.[idx]) || 0 }));
      // iter77ct.41: observacoes do recebimento (visualizadas no viewer e edicao)
      const received_notes = receivedMap.received_notes || null;
      // iter77ct.19: se estamos no modo EDICAO de recebimento, chama endpoint diferente
      if (transfer.__editMode) {
        await axios.put(`${ERP}/stock-transfers/${transfer.id}/edit-received`, { items, packages, received_notes }, { withCredentials: true });
        toast.success('Quantidades recebidas atualizadas — estoque ajustado');
      } else {
        await axios.post(`${ERP}/stock-transfers/${transfer.id}/receive`, { items, packages, received_notes }, { withCredentials: true });
        toast.success('Transferencia recebida (estoque creditado)');
      }
      setShowReceive(null); fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const statusBadge = { draft: 'bg-amber-100 text-amber-700', sent: 'bg-blue-100 text-blue-700', received: 'bg-emerald-100 text-emerald-700', rejected: 'bg-rose-100 text-rose-700' };
  // iter77bk: status label e CONTEXTO-DEPENDENTE. Para 'sent', a origem ve "ENVIADO" e o
  // destino ve "A RECEBER". Sem companyId (visao consolidada) mostra o label combinado legacy.
  const statusLabelFor = (r) => {
    const base = { draft: 'A enviar', received: 'Recebida', rejected: 'Estornada' };
    if (r.status !== 'sent') {
      // iter77ct.71: quando recebida em modo manual e ainda nao alimentada, mostra estado extra
      if (r.status === 'received' && r.stock_fed === false) return 'Recebida - pend. alimentar';
      return base[r.status] || r.status || '';
    }
    if (!companyId) return 'Enviado / A receber';
    if (companyId === r.origin_company_id) return 'Enviado';
    if (companyId === r.destination_company_id) return 'A receber';
    return 'Enviado / A receber';
  };
  const statusDot = { draft: 'bg-amber-500', sent: 'bg-blue-600', received: 'bg-emerald-500', rejected: 'bg-rose-500' };
  const compName = (id) => companies.find(c => c.id === id)?.name || id?.slice(-6) || '-';

  const doReject = async (t) => {
    askConfirm({
      title: `Estornar transferencia #${t.code || t.id?.slice(-6)}?`,
      body: `O estoque sera DEVOLVIDO a empresa de origem (${compName(t.origin_company_id)}).\n\nApos o estorno, a origem podera editar e reenviar OU excluir.`,
      color: 'rose',
      icon: 'rotate',
      label: 'Estornar',
      onConfirm: async () => {
        try {
          await axios.post(`${ERP}/stock-transfers/${t.id}/reject`, {}, { withCredentials: true });
          toast.success('Transferencia estornada — estoque devolvido a origem');
          fetchList();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
      }
    });
  };
  const doDelete = async (t) => {
    askConfirm({
      title: `Excluir transferencia #${t.code || t.id?.slice(-6)}?`,
      body: `Esta acao nao pode ser desfeita.`,
      color: 'rose',
      icon: 'trash',
      label: 'Excluir',
      onConfirm: async () => {
        try {
          await axios.delete(`${ERP}/stock-transfers/${t.id}`, { withCredentials: true });
          toast.success('Transferencia excluida');
          fetchList();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
      }
    });
  };

  if (loading) return <Loader />;
  return (
    <div data-testid="stock-transfers-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-black text-slate-900">Transferencia de Estoque</h2>
          <p className="text-xs text-slate-500">Movimentacao entre empresas {blindReceipt && <span className="ml-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-bold text-[10px]">Recebimento Cego ATIVO</span>}</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className={btnPrimary} data-testid="new-stock-transfer-btn">
          <Plus size={16} /><span>Nova Transferencia</span>
        </button>
      </div>

      {/* Scope tabs: Todas / Saidas (out) / Entradas (in) */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="transfer-scope-tabs">
        {[{ k: 'all', l: 'Todas' }, { k: 'out', l: 'Saidas (origem)' }, { k: 'in', l: 'Entradas (destino)' }].map(s => (
          <button key={s.k} onClick={() => setTab(s.k)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${tab === s.k ? 'bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-400'}`} data-testid={`transfer-scope-${s.k}`}>{s.l}</button>
        ))}
      </div>

      {/* List — colunas: Cod | Situacao | Qtd.Enviada | Qtd.Confirmada | Divergencia | Destino/Origem | Acoes */}
      {(() => {
        const sumSent = (t) => (t.items || []).reduce((a, it) => a + (parseFloat(it.quantity) || 0), 0);
        const sumRecv = (t) => (t.items || []).reduce((a, it) => a + (parseFloat(it.received_quantity) || 0), 0);
        const divergence = (t) => {
          if (t.status !== 'received') return 0;
          return sumRecv(t) - sumSent(t);
        };
        const columns = [
          { key: 'code', label: 'Cod', render: r => (
              <span className="font-mono text-xs text-slate-700 inline-flex items-center gap-1.5">
                {/* iter77aa: bolinha sky indica transferencia gerada por Solicitacao de Compra */}
                {r.source === 'purchase_request' && (
                  <span className="inline-block w-2 h-2 rounded-full bg-sky-500 ring-2 ring-sky-200" title="Gerada por Solicitacao de Compra" data-testid={`transfer-source-dot-${r.id}`}></span>
                )}
                {r.code || r.document_number || r.id?.slice(-6) || '-'}
              </span>
            ), sortValue: r => r.code || r.document_number || r.id },
          { key: 'status', label: 'Situacao', render: r => (
              <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded ${statusBadge[r.status] || 'bg-slate-100 text-slate-600'}`}>
                <span className={`w-2 h-2 rounded-full ${statusDot[r.status] || 'bg-slate-400'}`} />
                {statusLabelFor(r).toUpperCase()}
              </span>
            ), sortValue: r => r.status },
          { key: 'date', label: 'Data', render: r => <span className="text-xs text-slate-600">{fmtDateBR(r.document_date || r.created_at)}</span>, sortValue: r => r.document_date || r.created_at },
          { key: 'qty_sent', label: 'Qtd. Enviada', align: 'right', render: r => <span className="text-xs font-mono text-slate-700">{sumSent(r)}</span>, sortValue: r => sumSent(r) },
          { key: 'qty_confirmed', label: 'Qtd. Confirmada', align: 'right', render: r => <span className="text-xs font-mono text-slate-700">{r.status === 'received' ? sumRecv(r) : '—'}</span>, sortValue: r => sumRecv(r) },
          { key: 'divergence', label: 'Divergencia', align: 'right', render: r => {
              const d = divergence(r);
              if (r.status !== 'received') return <span className="text-xs text-slate-400">—</span>;
              if (d === 0) return <span className="text-xs text-emerald-600 font-mono">0</span>;
              return <span className={`text-xs font-mono font-bold ${d > 0 ? 'text-blue-600' : 'text-rose-600'}`}>{d > 0 ? '+' : ''}{d}</span>;
            }, sortValue: r => divergence(r) },
          { key: 'route', label: 'Destino/Origem', render: r => (
              <span className="text-xs text-slate-600 inline-flex items-center gap-1">
                <span className="px-1.5 py-0.5 rounded bg-slate-100 truncate max-w-[120px]">{compName(r.origin_company_id)}</span>
                <ArrowRightLeft size={10} className="text-slate-400" />
                <span className="px-1.5 py-0.5 rounded bg-slate-100 truncate max-w-[120px]">{compName(r.destination_company_id)}</span>
              </span>
            ), sortValue: r => compName(r.origin_company_id) },
        ];
        const renderActions = (r) => {
          // iter77bk: visibilidade das acoes "Receber" e "Estornar" depende da empresa em uso.
          // - Origem ve apenas Estornar (nao recebe a propria saida).
          // - Destino ve apenas Receber (so podes confirmar quem te chegou).
          // - Sem companyId (visao consolidada/admin) mostra ambos para conveniencia.
          const isOrigin = companyId && companyId === r.origin_company_id;
          const isDestination = companyId && companyId === r.destination_company_id;
          const showReceive = r.status === 'sent' && (!companyId || isDestination);
          const showReject = r.status === 'sent' && (!companyId || isOrigin);
          // iter77ct.71/73: botao "Alimentar Estoque" — mesma permissao do "Editar Recebimento"
          // (stock-transfers.edit). Aparece so quando recebida em modo manual e ainda nao alimentada.
          const showFeedStock = r.status === 'received' && r.stock_fed === false && canEditTransfers && (!companyId || isDestination);
          // iter77ct.20: apos o destino receber, a origem nao pode mais editar a transferencia.
          // Somente destino (ou admin em visao consolidada sem companyId) pode ajustar quantidades recebidas.
          const showEditReceived = r.status === 'received' && canEditTransfers && (!companyId || isDestination);
          const showEditDraft = (r.status === 'draft' || r.status === 'rejected') && (!companyId || isOrigin);
          return (
            <div className="flex items-center gap-1">
              <button onClick={() => setViewing(r)} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg" title="Visualizar" data-testid={`transfer-view-${r.id}`}><Eye size={13} /></button>
              {(showEditDraft || showEditReceived) && <button onClick={() => doEdit(r)} className="p-1.5 text-violet-600 hover:bg-violet-50 rounded-lg" title={showEditReceived ? 'Editar Recebimento' : 'Editar'} data-testid={`transfer-edit-${r.id}`}><Edit size={13} /></button>}
              {r.status === 'draft' && (!companyId || isOrigin) && <button onClick={() => doSend(r)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Enviar" data-testid={`transfer-send-${r.id}`}><Send size={13} /></button>}
              {showReceive && <button onClick={() => setShowReceive(r)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Receber" data-testid={`transfer-receive-${r.id}`}><PackageCheck size={13} /></button>}
              {showFeedStock && <button onClick={() => doFeedStock(r)} className="p-1.5 text-emerald-700 hover:bg-emerald-100 rounded-lg border border-emerald-300 animate-pulse" title="Alimentar Estoque (pendente)" data-testid={`transfer-feed-stock-${r.id}`}><Boxes size={13} /></button>}
              {showReject && <button onClick={() => doReject(r)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg" title="Estornar" data-testid={`transfer-reject-${r.id}`}><RotateCcw size={13} /></button>}
              {(r.status === 'draft' || r.status === 'rejected') && (!companyId || isOrigin) && <button onClick={() => doDelete(r)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg" title="Excluir" data-testid={`transfer-delete-${r.id}`}><Trash2 size={13} /></button>}
            </div>
          );
        };
        // iter77ct.17: filtra a lista por termo livre (codigo, filiais, produtos, sku)
        const term = (search || '').trim().toLowerCase();
        const filtered = !term ? list : list.filter(r => {
            const hay = [
              r.code, r.document_number,
              compName(r.origin_company_id),
              compName(r.destination_company_id),
              ...(r.items || []).flatMap(it => [it.name, it.sku, it.product_id]),
              ...(r.packages || []).map(p => p.name),
              r.notes,
            ].filter(Boolean).join(' | ').toLowerCase();
            return hay.includes(term);
          });
        return (
          <DataTable
            prefKey="stock-transfers"
            columns={columns}
            defaultVisible={columns.map(c => c.key)}
            defaultOrder={columns.map(c => c.key)}
            rows={filtered}
            keyField="id"
            emptyText={term ? "Nenhuma transferencia com este termo" : "Nenhuma transferencia registrada"}
            renderActions={renderActions}
            toolbarLeft={
              <div className="flex-1 flex items-center gap-2">
                <div className="relative flex-1 max-w-sm">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por codigo, filial, SKU, produto..." className="w-full pl-9 pr-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:border-violet-500 focus:outline-none" data-testid="transfer-search-input" />
                </div>
                {term && <button onClick={() => setSearch('')} className="text-[11px] text-slate-500 hover:text-rose-600 font-bold" data-testid="transfer-search-clear">Limpar</button>}
              </div>
            }
            mobilePrimary={(r) => <>{r.document_number || r.id?.slice(-6)} · {statusLabelFor(r).toUpperCase()}</>}
            mobileSecondary={(r) => <>{compName(r.origin_company_id)} → {compName(r.destination_company_id)} · {sumSent(r)} und</>}
            totalLabel={`${filtered.length} de ${list.length} transferencias`}
          />
        );
      })()}

      {/* Create transfer — Alvotec-style compact */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="stock-transfer-form-modal">
          <div className="bg-white w-full max-w-5xl max-h-[94vh] overflow-y-auto rounded-t-2xl md:rounded-2xl shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-3 flex items-center justify-between z-10">
              <h3 className="text-white font-bold text-sm flex items-center gap-2"><ArrowRightLeft size={16} /> {editingId ? 'Editar Transferencia' : 'Nova Transferencia'} de Estoque</h3>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-white/80 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-3 text-[11px] text-slate-700">
                <p className="font-bold text-blue-700 mb-0.5 flex items-center gap-1"><AlertTriangle size={12} /> Fluxo da Transferencia</p>
                <p>1) <strong>Salvar Rascunho</strong> (nao mexe estoque). 2) <strong>Enviar</strong> (debita estoque da Origem). 3) Destino faz <strong>Receber</strong> (credita estoque no Destino).</p>
              </div>

              {/* Header line: Origem | Destino | Data | NDoc */}
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-12 md:col-span-4">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Empresa Origem</label>
                  <select value={form.origin_company_id} onChange={e => setForm(f => ({ ...f, origin_company_id: e.target.value }))} className={inputClass} data-testid="transfer-origin">
                    <option value="">Selecione</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.code} | {c.name}</option>)}
                  </select>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Empresa Destino</label>
                  <select value={form.destination_company_id} onChange={e => setForm(f => ({ ...f, destination_company_id: e.target.value }))} className={inputClass} data-testid="transfer-destination">
                    <option value="">Selecione</option>
                    {companies.filter(c => c.id !== form.origin_company_id).map(c => <option key={c.id} value={c.id}>{c.code} | {c.name}</option>)}
                  </select>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Data</label>
                  <input type="date" value={form.document_date} onChange={e => setForm(f => ({ ...f, document_date: e.target.value }))} className={inputClass} data-testid="transfer-doc-date" />
                </div>
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">N Doc</label>
                  <input value={form.document_number} onChange={e => setForm(f => ({ ...f, document_number: e.target.value }))} className={inputClass} placeholder="OS / interno" data-testid="transfer-doc-number" />
                </div>
              </div>

              {/* Product add line: Produto search + Qt + Adicionar */}
              <div className="grid grid-cols-12 gap-2 items-end pt-2 border-t border-slate-100">
                <div className="col-span-12 md:col-span-7 relative">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Produto</label>
                  {pendingProduct ? (
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-2 border-blue-300 rounded-lg text-xs">
                      <Search size={13} className="text-blue-500 flex-shrink-0" />
                      <span className="font-mono text-slate-500">({pendingProduct.sku})</span>
                      <span className="font-bold text-slate-900 truncate flex-1">{pendingProduct.name}</span>
                      <span className="text-slate-500">Estoque Origem: {pendingProduct.stock || 0}</span>
                      <button type="button" onClick={() => setPendingProduct(null)} className="text-rose-500 hover:text-rose-700 flex-shrink-0" data-testid="transfer-pending-clear"><X size={13} /></button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input ref={productSearchRef} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onKeyDown={onSearchKeyDown} placeholder="Buscar (setas + Enter, ou clique)..." className={`${inputClass} pl-7`} data-testid="transfer-product-search" />
                      {searchResults.length > 0 && (
                        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                          {searchResults.map((p, idx) => (
                            <button key={p.id} type="button" onClick={() => selectProduct(p)} onMouseEnter={() => setActiveIdx(idx)} className={`w-full text-left px-3 py-1.5 border-b last:border-b-0 border-slate-100 flex items-center justify-between text-xs ${idx === activeIdx ? 'bg-blue-100' : 'hover:bg-blue-50'}`} data-testid={`transfer-product-opt-${p.id}`}>
                              <div className="min-w-0"><p className="font-semibold text-slate-900 truncate">({p.sku}) {p.name}{idx === activeIdx && <span className="ml-2 text-[9px] font-extrabold text-blue-700">(Enter)</span>}</p><p className="text-[10px] text-slate-500">Estoque Origem: {p.stock || 0}</p></div>
                              <Plus size={12} className="text-blue-600 flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Quantidade</label>
                  <input ref={qtyInputRef} type="text" inputMode="decimal" value={pendingQty} onChange={e => { const _u = (pendingProduct?.fiscal?.unit_commercial || pendingProduct?.unit || 'UN'); setPendingQty(smartQtyMask(e.target.value, _u)); }} onFocus={e => e.target.select()} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitPending(); } }} className={inputClass} data-testid="transfer-pending-qty" />
                </div>
                <div className="col-span-6 md:col-span-3">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5 invisible">.</label>
                  <button type="button" onClick={commitPending} disabled={!pendingProduct} className={`w-full ${btnPrimary}`} data-testid="transfer-add-btn"><Plus size={13} /><span>Adicionar</span></button>
                </div>
              </div>

              {/* Items table */}
              {form.items.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-[10px] font-bold text-slate-500 uppercase">
                        <th className="px-2 py-2 w-8">#</th>
                        <th className="px-2 py-2 w-20">SKU</th>
                        <th className="px-2 py-2">Nome</th>
                        <th className="px-2 py-2 w-24 text-right">Estoque Origem</th>
                        <th className="px-2 py-2 w-24 text-right">Qtd Transferir</th>
                        <th className="px-2 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((it, idx) => (
                        <tr key={idx} className="border-b last:border-b-0 border-slate-100 hover:bg-slate-50/50" data-testid={`transfer-item-${idx}`}>
                          <td className="px-2 py-1.5 text-slate-500 font-mono">{idx + 1}</td>
                          <td className="px-2 py-1.5 text-slate-600 font-mono text-[11px] truncate max-w-[80px]" title={it.sku}>{it.sku}</td>
                          <td className="px-2 py-1.5 text-slate-900 font-semibold truncate" title={it.name}>{it.name}</td>
                          <td className="px-2 py-1.5 text-right text-slate-500 font-mono">{it.current_stock}</td>
                          <td className="px-2 py-1.5 text-right">
                            <input type="number" step="0.01" value={it.quantity} onChange={e => updItem(idx, { quantity: e.target.value })} className="w-20 px-2 py-1 border border-slate-300 rounded text-xs text-right focus:border-blue-500 focus:outline-none" data-testid={`transfer-item-qty-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <button onClick={() => rmItem(idx)} className="text-rose-500 hover:text-rose-700"><Trash2 size={13} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                      <tr className="text-xs font-bold">
                        <td colSpan={4} className="px-2 py-2 text-slate-600">Total: {form.items.length} {form.items.length === 1 ? 'item' : 'itens'}</td>
                        <td className="px-2 py-2 text-right text-blue-700 font-black">{form.items.reduce((s, it) => s + (parseFloat(it.quantity) || 0), 0).toFixed(2)} un</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* iter77ct.4: Volumes (fardo/sacola/caixa) — dropdown compacto com gerenciamento inline */}
              <div className="mt-3 rounded-xl border border-purple-200 bg-purple-50/40 p-2" data-testid="transfer-packages-section">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Package size={12} className="text-purple-700" />
                  <span className="text-[10px] font-black text-purple-700 uppercase tracking-wider">Volumes (opcional)</span>
                </div>
                <div className="grid grid-cols-[1fr_100px_auto] gap-1.5">
                  {/* Custom dropdown com gerenciamento inline */}
                  <div className="relative" ref={pkgDropRef}>
                    <button type="button" onClick={() => setPkgDropOpen(v => !v)} className="w-full flex items-center justify-between px-2 py-2 border-2 border-purple-200 rounded-lg text-sm font-bold focus:border-purple-500 outline-none bg-white text-left" data-testid="pkg-type-select">
                      <span className={pkgTypeId ? 'text-slate-800' : 'text-slate-400 font-normal'}>
                        {pkgTypeId ? (packagingTypes.find(t => t.id === pkgTypeId)?.name || 'Selecionar tipo...') : 'Selecionar tipo...'}
                      </span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-purple-500 shrink-0"><polyline points="6 9 12 15 18 9" /></svg>
                    </button>
                    {pkgDropOpen && (
                      <div className="absolute z-20 mt-1 left-0 right-0 bg-white border-2 border-purple-200 rounded-lg shadow-xl overflow-hidden" data-testid="pkg-type-dropdown">
                        <div className="max-h-40 overflow-y-auto">
                          {packagingTypes.length === 0 && (
                            <div className="px-2 py-2 text-[11px] text-slate-400 text-center">Nenhum tipo — cadastre abaixo</div>
                          )}
                          {packagingTypes.map(t => (
                            <div key={t.id} className={`flex items-center justify-between px-2 py-1.5 hover:bg-purple-50 cursor-pointer text-xs ${pkgTypeId === t.id ? 'bg-purple-100 font-black' : 'font-semibold'}`} onClick={() => { setPkgTypeId(t.id); setPkgDropOpen(false); }} data-testid={`pkg-type-option-${t.id}`}>
                              <span className="text-slate-800 truncate">{t.name}</span>
                              <button type="button" onClick={(e) => { e.stopPropagation(); removePackagingType(t.id); }} className="ml-2 text-slate-400 hover:text-rose-600 shrink-0" title="Excluir tipo" data-testid={`pkg-type-remove-${t.id}`}>
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-purple-200 bg-purple-50/50 px-1.5 py-1.5 flex gap-1">
                          <input value={newPkgTypeName} onChange={e => setNewPkgTypeName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createPackagingType(); } }} placeholder="+ novo tipo" className="flex-1 px-2 py-1 border border-purple-300 rounded text-[11px] focus:outline-none focus:border-purple-500" data-testid="pkg-type-new-input" />
                          <button type="button" onClick={createPackagingType} className="px-2 py-1 rounded bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-black uppercase" data-testid="pkg-type-new-btn">OK</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <input type="number" step="1" min="1" value={pkgQty} onChange={e => setPkgQty(e.target.value)} placeholder="Qtd" className="px-2 py-2 border-2 border-purple-200 rounded-lg text-sm font-bold text-right focus:border-purple-500 outline-none" data-testid="pkg-qty-input" />
                  <button type="button" onClick={addPackage} className="px-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center justify-center transition-colors" data-testid="pkg-add-btn"><Plus size={16} /></button>
                </div>
                {(form.packages || []).length > 0 && (
                  <div className="mt-2 bg-white/70 rounded-lg border border-purple-200 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-purple-50 border-b border-purple-200">
                        <tr className="text-left text-[10px] font-bold text-purple-700 uppercase">
                          <th className="px-2 py-1.5">Tipo</th>
                          <th className="px-2 py-1.5 w-20 text-right">Qtd</th>
                          <th className="px-2 py-1.5 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(form.packages || []).map((p, idx) => (
                          <tr key={idx} className="border-b last:border-b-0 border-purple-100" data-testid={`pkg-row-${idx}`}>
                            <td className="px-2 py-1.5 text-slate-900 font-semibold">{p.name}</td>
                            <td className="px-2 py-1.5 text-right">
                              <input type="number" step="1" min="0" value={p.qty_sent} onChange={e => updPackage(idx, { qty_sent: e.target.value })} className="w-16 px-2 py-1 border border-slate-300 rounded text-xs text-right focus:border-purple-500 focus:outline-none" data-testid={`pkg-qty-${idx}`} />
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              <button type="button" onClick={() => rmPackage(idx)} className="text-rose-500 hover:text-rose-700"><Trash2 size={13} /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Observacoes</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={inputClass} placeholder="Faca aqui suas observacoes..." />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-2.5 flex justify-end gap-2 z-10">
              <button onClick={() => setShowForm(false)} className="px-4 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">Cancelar</button>
              <button onClick={submit} disabled={saving || !form.items.length} className={btnPrimary} data-testid="transfer-submit-btn"><Save size={13} /><span>{saving ? 'Salvando...' : 'Salvar Rascunho'}</span></button>
            </div>
          </div>
        </div>
      )}

      {/* Receive modal (with blind receipt support) */}
      {showReceive && (
        <TransferReceiveModal transfer={showReceive} blind={blindReceipt} onClose={() => setShowReceive(null)} onSubmit={(map) => doReceive(showReceive, map)} />
      )}

      {/* View transfer modal — destino com blind=ON oculta qtd enviada */}
      {viewing && (() => {
        const isDestino = viewing.destination_company_id === companyId && viewing.origin_company_id !== companyId;
        // iter77ct.17: usuario com permissao de EDITAR ve tudo (mesmo com blind ON).
        // Sem edit perm + blind ON + destino → oculta ate mesmo depois de recebido (nao ve
        // o que foi enviado nem a divergencia — so o que ele preencheu).
        const hideQty = blindReceipt && isDestino && !canEditTransfers && viewing.status !== 'draft' && viewing.origin_company_id !== companyId;
        return (
          <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" data-testid="transfer-view-modal">
            <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-2xl md:rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-gradient-to-r from-slate-800 to-slate-900 px-5 py-4 flex items-center justify-between z-10">
                <h3 className="text-white font-black text-sm flex items-center gap-2"><Eye size={16} /> Transferencia #{viewing.code || viewing.document_number || viewing.id?.slice(-6)}</h3>
                <button onClick={() => setViewing(null)} className="text-white/80 hover:text-white" data-testid="transfer-view-close"><X size={18} /></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-50 rounded-lg p-2.5"><p className="text-[10px] font-bold text-slate-400 uppercase">Situacao</p><p className={`mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold ${statusBadge[viewing.status] || 'bg-slate-200 text-slate-700'}`}><span className={`w-1.5 h-1.5 rounded-full ${statusDot[viewing.status] || 'bg-slate-400'}`} />{statusLabelFor(viewing).toUpperCase()}</p></div>
                  <div className="bg-slate-50 rounded-lg p-2.5"><p className="text-[10px] font-bold text-slate-400 uppercase">Data</p><p className="text-sm font-bold text-slate-800 mt-0.5">{fmtDateBR(viewing.document_date || viewing.created_at)}</p></div>
                  <div className="bg-slate-50 rounded-lg p-2.5"><p className="text-[10px] font-bold text-slate-400 uppercase">Origem</p><p className="text-sm font-bold text-slate-800 mt-0.5 truncate">{compName(viewing.origin_company_id)}</p></div>
                  <div className="bg-slate-50 rounded-lg p-2.5"><p className="text-[10px] font-bold text-slate-400 uppercase">Destino</p><p className="text-sm font-bold text-slate-800 mt-0.5 truncate">{compName(viewing.destination_company_id)}</p></div>
                </div>
                {hideQty && <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-xs text-indigo-700"><strong>Conferencia Cega:</strong> a quantidade enviada esta oculta. Voce devera contar e digitar a quantidade real ao receber.</div>}
                {viewing.notes && <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5"><p className="text-[10px] font-bold text-slate-500 uppercase">Observacoes (Envio)</p><p className="text-sm text-slate-700 mt-0.5">{viewing.notes}</p></div>}
                {/* iter77ct.41: observacao do recebimento (visivel apos recebimento) */}
                {viewing.received_notes && <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-2.5" data-testid="viewer-received-notes"><p className="text-[10px] font-bold text-amber-700 uppercase">Observacoes do Recebimento</p><p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">{viewing.received_notes}</p></div>}
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-slate-500">
                        <th className="px-3 py-2">#</th>
                        <th className="px-3 py-2">SKU</th>
                        <th className="px-3 py-2">Produto</th>
                        <th className="px-3 py-2 text-right">Qtd Enviada</th>
                        <th className="px-3 py-2 text-right">Qtd Recebida</th>
                        <th className="px-3 py-2 text-right">Divergencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewing.items || []).map((it, i) => {
                        const sent = hideQty ? null : (it.quantity ?? 0);
                        const recv = it.received_quantity;
                        const div = (recv != null && sent != null) ? (recv - sent) : null;
                        return (
                          <tr key={i} className="border-b border-slate-100 last:border-0">
                            <td className="px-3 py-1.5 text-slate-400">{i + 1}</td>
                            <td className="px-3 py-1.5 font-mono text-slate-600">{it.sku || '—'}</td>
                            <td className="px-3 py-1.5 text-slate-800 truncate max-w-[260px]">{it.name || it.product_id}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{hideQty ? <span className="text-indigo-400">●●●</span> : (sent ?? '—')}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{recv ?? '—'}</td>
                            <td className={`px-3 py-1.5 text-right font-mono font-bold ${div == null ? 'text-slate-400' : div === 0 ? 'text-emerald-600' : div > 0 ? 'text-blue-600' : 'text-rose-600'}`}>{div == null ? '—' : (div > 0 ? `+${div}` : div)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* iter77ct.4: Volumes na visualizacao — respeita blind receipt */}
                {Array.isArray(viewing.packages) && viewing.packages.length > 0 && (
                  <div className="mt-3 rounded-xl border-2 border-purple-200 bg-purple-50/50 overflow-hidden" data-testid="view-packages-section">
                    <div className="bg-gradient-to-r from-purple-100 to-fuchsia-100 px-3 py-2 border-b border-purple-200">
                      <p className="text-[11px] font-black text-purple-800 uppercase tracking-wider flex items-center gap-1.5"><Package size={12} /> Volumes</p>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-white/70 border-b border-purple-200">
                        <tr className="text-left text-[10px] font-bold text-purple-700 uppercase">
                          <th className="px-3 py-2">Tipo</th>
                          <th className="px-3 py-2 w-24 text-right">Qtd Enviada</th>
                          <th className="px-3 py-2 w-24 text-right">Qtd Recebida</th>
                          <th className="px-3 py-2 w-24 text-right">Divergencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(viewing.packages || []).map((p, i) => {
                          const sent = hideQty ? null : (p.qty_sent ?? null);
                          const recv = p.qty_received;
                          const div = (recv != null && sent != null) ? (recv - sent) : null;
                          return (
                            <tr key={i} className="border-b border-purple-100 last:border-0">
                              <td className="px-3 py-1.5 text-slate-800 font-semibold">{p.name}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{hideQty ? <span className="text-indigo-400">●●●</span> : (sent ?? '—')}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{recv ?? '—'}</td>
                              <td className={`px-3 py-1.5 text-right font-mono font-bold ${div == null ? 'text-slate-400' : div === 0 ? 'text-emerald-600' : div > 0 ? 'text-blue-600' : 'text-rose-600'}`}>{div == null ? '—' : (div > 0 ? `+${div}` : div)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modern confirm modal (substitui window.confirm) */}
      {confirmAction && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="transfer-confirm-modal">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className={`px-5 py-4 ${confirmAction.color === 'rose' ? 'bg-gradient-to-r from-rose-500 to-rose-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600'} text-white flex items-center gap-3`}>
              {confirmAction.icon === 'send' && <Send size={22} />}
              {confirmAction.icon === 'rotate' && <RotateCcw size={22} />}
              {confirmAction.icon === 'trash' && <Trash2 size={22} />}
              <h3 className="font-black text-base">{confirmAction.title}</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-slate-600 whitespace-pre-line">{confirmAction.body}</p>
            </div>
            <div className="bg-slate-50 px-5 py-3 flex items-center justify-end gap-2 border-t border-slate-200">
              <button onClick={() => setConfirmAction(null)} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200 rounded-lg" data-testid="confirm-cancel">Cancelar</button>
              <button onClick={async () => { const fn = confirmAction.onConfirm; setConfirmAction(null); await fn(); }} className={`px-4 py-2 text-sm font-bold text-white rounded-lg shadow-md ${confirmAction.color === 'rose' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-blue-600 hover:bg-blue-700'}`} data-testid="confirm-ok">{confirmAction.label || 'Confirmar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TransferReceiveModal = ({ transfer, blind, onClose, onSubmit }) => {
  // iter77ct.19: modo edicao de recebimento (transferencia ja com status 'received')
  const editMode = !!transfer.__editMode;
  const effectiveBlind = editMode ? false : blind;  // em edicao, exibe sempre 'enviado' + 'recebido'
  // iter77cj: map keyed by ÍNDICE (não product_id) para permitir linhas duplicadas do mesmo produto
  // iter77ct.19: em editMode, pré-preenche com received_quantity ao invés de quantity enviada
  const [map, setMap] = useState(() => Object.fromEntries((transfer.items || []).map((it, idx) => {
    // iter77ct.41: em editMode, se received_quantity nao esta gravado no item (legado)
    // mas a transferencia ja tem status='received', assume a quantidade ENVIADA como
    // valor atual — evita a UI mostrar "0" e induzir o usuario a re-lancar +20.
    if (editMode) {
      if (it.received_quantity != null) return [idx, it.received_quantity];
      if (transfer.status === 'received') return [idx, it.quantity || 0];
      return [idx, 0];
    }
    return [idx, effectiveBlind ? 0 : (it.quantity || 0)];
  })));
  // iter77ct.41: observacao do recebimento (bate no campo received_notes da transferencia)
  const [receivedNotes, setReceivedNotes] = useState(transfer.received_notes || '');
  // iter77ct.4: recebimento de volumes (packages) — só aparece se a transferencia foi enviada com volumes
  const hasPackages = Array.isArray(transfer.packages) && transfer.packages.length > 0;
  const [pkgMap, setPkgMap] = useState(() => Object.fromEntries((transfer.packages || []).map((p, idx) => {
    if (editMode) return [idx, p.qty_received != null ? p.qty_received : 0];
    return [idx, effectiveBlind ? 0 : (p.qty_sent || 0)];
  })));
  // iter77ct.18: busca + ordenacao dos itens dentro do modal (usuario tinha lista grande)
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('idx');  // idx | sku | name | quantity
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('asc'); }
  };
  const arrow = (k) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  const displayItems = useMemo(() => {
    const withIdx = (transfer.items || []).map((it, idx) => ({ ...it, idx }));
    const term = (search || '').trim().toLowerCase();
    const filtered = !term ? withIdx : withIdx.filter(it => (
      String(it.sku || '').toLowerCase().includes(term) ||
      String(it.name || '').toLowerCase().includes(term) ||
      String(it.product_id || '').toLowerCase().includes(term)
    ));
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (it) => {
      if (sortKey === 'sku') return String(it.sku || '').toLowerCase();
      if (sortKey === 'name') return String(it.name || '').toLowerCase();
      if (sortKey === 'quantity') return Number(it.quantity || 0);
      return it.idx;
    };
    return [...filtered].sort((a, b) => {
      const va = val(a); const vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [transfer.items, search, sortKey, sortDir]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="transfer-receive-modal">
      <div className="bg-white w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-t-2xl md:rounded-2xl shadow-2xl">
        <div className={`sticky top-0 px-4 py-3 flex items-center justify-between z-10 ${editMode ? 'bg-gradient-to-r from-violet-600 to-purple-500' : 'bg-gradient-to-r from-emerald-600 to-teal-500'}`}>
          <h3 className="text-white font-bold text-sm flex items-center gap-2">
            {editMode ? <Edit size={16} /> : <PackageCheck size={16} />}
            {editMode ? `Editar Recebimento #${transfer.code || (transfer.id || '').slice(-6)}` : 'Receber Transferencia'}
          </h3>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          {editMode && (
            <div className="text-[11px] bg-violet-50 border-l-4 border-violet-500 text-violet-800 rounded-lg p-2.5" data-testid="edit-received-banner">
              <strong>Editar Quantidade Recebida:</strong> ajustes aumentam ou reduzem o estoque do destino automaticamente. Se voce reduzir e a filial nao tiver estoque suficiente, o ajuste sera bloqueado.
            </div>
          )}
          {effectiveBlind && <div className="text-[11px] bg-indigo-50 border-l-4 border-indigo-500 text-indigo-800 rounded-lg p-2.5"><strong>Recebimento Cego:</strong> a quantidade enviada esta oculta. Conte fisicamente os itens e informe abaixo.</div>}
          {/* iter77ct.18: busca livre dentro do modal para listas grandes */}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por SKU ou nome do produto..."
              className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:border-emerald-500 focus:outline-none"
              data-testid="receive-search-input"
            />
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] font-bold text-slate-500 uppercase">
                  <th className="px-2 py-2 w-8"><button onClick={() => toggleSort('idx')} className="hover:text-emerald-600" data-testid="receive-sort-idx">#{arrow('idx')}</button></th>
                  <th className="px-2 py-2 w-20"><button onClick={() => toggleSort('sku')} className="hover:text-emerald-600" data-testid="receive-sort-sku">SKU{arrow('sku')}</button></th>
                  <th className="px-2 py-2"><button onClick={() => toggleSort('name')} className="hover:text-emerald-600" data-testid="receive-sort-name">Nome{arrow('name')}</button></th>
                  {!effectiveBlind && <th className="px-2 py-2 w-24 text-right"><button onClick={() => toggleSort('quantity')} className="hover:text-emerald-600" data-testid="receive-sort-quantity">Enviado{arrow('quantity')}</button></th>}
                  <th className="px-2 py-2 w-28 text-right">{editMode ? 'Nova Qtd Recebida' : 'Qtd Recebida'}</th>
                </tr>
              </thead>
              <tbody>
                {displayItems.length === 0 && (
                  <tr><td colSpan={effectiveBlind ? 4 : 5} className="px-2 py-4 text-center text-slate-400 text-[11px]">Nenhum item encontrado.</td></tr>
                )}
                {displayItems.map((it) => {
                  const idx = it.idx;
                  return (
                    <tr key={idx} className="border-b last:border-b-0 border-slate-100 hover:bg-slate-50/50" data-testid={`receive-item-${idx}`}>
                      <td className="px-2 py-1.5 text-slate-500 font-mono">{idx + 1}</td>
                      <td className="px-2 py-1.5 text-slate-600 font-mono text-[11px] truncate max-w-[80px]" title={it.sku}>{it.sku}</td>
                      <td className="px-2 py-1.5 text-slate-900 font-semibold truncate" title={it.name}>{it.name || it.product_id}</td>
                      {!effectiveBlind && <td className="px-2 py-1.5 text-right text-slate-600 font-mono">{it.quantity}</td>}
                      <td className="px-2 py-1.5 text-right">
                        <input type="number" step="1" min="0" value={map[idx]} onChange={e => {
                          // iter77ct.17: impede valor negativo/decimal; se blind, limita ao qty_sent
                          let v = e.target.value === '' ? '' : Math.max(0, Math.floor(parseFloat(e.target.value) || 0));
                          if (!effectiveBlind && v !== '' && it.quantity != null && v > it.quantity) v = it.quantity;
                          setMap(m => ({ ...m, [idx]: v }));
                        }} className={`w-24 px-2 py-1 border rounded text-xs text-right focus:outline-none ${editMode ? 'border-violet-300 focus:border-violet-500' : 'border-slate-300 focus:border-emerald-500'}`} data-testid={`receive-item-qty-${idx}`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* iter77ct.4: Volumes recebidos — só aparece quando enviados */}
          {hasPackages && (
            <div className="rounded-lg border-2 border-purple-200 bg-purple-50/60 overflow-hidden" data-testid="receive-packages-section">
              <div className="bg-gradient-to-r from-purple-100 to-fuchsia-100 px-3 py-2 border-b border-purple-200">
                <p className="text-[11px] font-black text-purple-800 uppercase tracking-wider flex items-center gap-1.5"><Package size={12} /> Volumes recebidos</p>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-white/70 border-b border-purple-200">
                  <tr className="text-left text-[10px] font-bold text-purple-700 uppercase">
                    <th className="px-2 py-1.5">Tipo</th>
                    {!effectiveBlind && <th className="px-2 py-1.5 w-20 text-right">Enviado</th>}
                    <th className="px-2 py-1.5 w-24 text-right">Recebido</th>
                  </tr>
                </thead>
                <tbody>
                  {(transfer.packages || []).map((p, idx) => (
                    <tr key={idx} className="border-b last:border-b-0 border-purple-100" data-testid={`receive-pkg-${idx}`}>
                      <td className="px-2 py-1.5 text-slate-900 font-semibold">{p.name}</td>
                      {!effectiveBlind && <td className="px-2 py-1.5 text-right text-slate-600 font-mono">{p.qty_sent}</td>}
                      <td className="px-2 py-1.5 text-right">
                        <input type="number" step="1" min="0" value={pkgMap[idx]} onChange={e => {
                          // iter77ct.17: nao permite negativo; nao permite exceder qty_sent quando visivel
                          let v = e.target.value === '' ? '' : Math.max(0, Math.floor(parseFloat(e.target.value) || 0));
                          if (!effectiveBlind && v !== '' && p.qty_sent != null && v > p.qty_sent) v = p.qty_sent;
                          setPkgMap(m => ({ ...m, [idx]: v }));
                        }} className="w-20 px-2 py-1 border border-purple-300 rounded text-xs text-right focus:border-purple-500 focus:outline-none" data-testid={`receive-pkg-qty-${idx}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* iter77ct.41: observacao do recebimento — visivel no modo Receber E no Editar */}
          <div className="rounded-lg border-2 border-amber-200 bg-amber-50/60 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-100 to-orange-100 px-3 py-2 border-b border-amber-200">
              <p className="text-[11px] font-black text-amber-800 uppercase tracking-wider flex items-center gap-1.5"><FileText size={12} /> Observacoes do Recebimento</p>
            </div>
            <textarea
              value={receivedNotes}
              onChange={e => setReceivedNotes(e.target.value)}
              placeholder="Ex: avaria em 2 unidades, embalagem violada, faltou nota fiscal..."
              rows={3}
              className="w-full px-3 py-2 text-xs bg-white text-slate-800 focus:outline-none resize-none"
              data-testid="receive-notes-textarea"
            />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-2.5 flex justify-end gap-2 z-10">
          <button onClick={onClose} className="px-4 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">Cancelar</button>
          <button onClick={() => onSubmit({ items: map, packages: pkgMap, received_notes: receivedNotes })} className={editMode ? 'btn-pill-primary' : 'btn-pill-success'} data-testid="receive-submit-btn">
            {editMode ? <><Edit size={13} /><span>Salvar Ajustes</span></> : <><PackageCheck size={13} /><span>Confirmar Recebimento</span></>}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ========== UNIFIED FINANCIAL (3 tabs) ========== */
/* iter156et: origem de um lancamento financeiro gerado por outro modulo (null = manual) */
const finOrigin = (e) => {
  if (!e) return null;
  if (e.sale_id) return { short: `Venda${e.sale_number ? ` #${e.sale_number}` : ''}`, label: `Vendas${e.sale_number ? ` (Venda #${e.sale_number})` : ''}`, hint: `Para reverter, cancele a venda${e.sale_number ? ` #${e.sale_number}` : ''} em Vendas > Historico de Vendas.` };
  if (e.os_id) return { short: `OS${e.os_number ? ` #${e.os_number}` : ''}`, label: `Ordem de Servico${e.os_number ? ` #${e.os_number}` : ''}`, hint: 'Para reverter, cancele/reabra a OS em Ordens de Servico.' };
  if (e.purchase_id) return { short: 'Compra', label: 'Compras', hint: 'Para reverter, cancele a compra em Compras > Pedidos de Compra.' };
  if (e.origin === 'pdv_expense') return { short: 'Despesa PDV', label: 'PDV (Despesa de caixa)', hint: 'Para reverter, exclua a despesa em PDV > Acoes > Despesas / Fechamento de Caixa.' };
  if (e.origin === 'pdv_transfer') return { short: 'Fechamento', label: 'PDV (Fechamento de caixa)', hint: 'Transferencias entre contas sao ajustadas no Fechamento de Caixa.' };
  return null;
};

/* iter156er: pílula de filtro com menu suspenso (estilo Linear/Notion). options = [[value,label]] */
const FilterPill = ({ label, value, defaultValue, options, onChange, accent, testid }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const current = options.find(o => o[0] === value);
  const changed = value !== defaultValue;
  return (
    <div className="relative" ref={ref} data-testid={`${testid}-pill`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testid}-trigger`}
        className={`inline-flex items-center gap-1.5 h-8 pl-3 pr-2 rounded-full border text-[11px] transition-colors duration-150 ${changed ? `${accent.pill} font-bold` : 'bg-white border-slate-300 text-slate-700 font-semibold hover:border-slate-400 hover:bg-slate-50'} ${open ? 'ring-2 ring-offset-1 ring-slate-300' : ''}`}
      >
        <span className={changed ? 'opacity-80' : 'text-slate-400 font-medium'}>{label}</span>
        <span className="max-w-[140px] truncate">{current ? current[1] : value}</span>
        <ChevronDown size={12} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <motion.ul
          initial={{ opacity: 0, y: -4, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.12 }}
          role="listbox"
          className="absolute left-0 top-full mt-1.5 z-40 min-w-[180px] bg-white border border-slate-200 rounded-xl shadow-xl p-1"
          data-testid={`${testid}-menu`}
        >
          {options.map(([k, l]) => {
            const sel = k === value;
            return (
              <li key={k}>
                <button
                  type="button"
                  role="option"
                  aria-selected={sel}
                  onClick={() => { onChange(k); setOpen(false); }}
                  data-testid={`${testid}-${k}`}
                  className={`w-full flex items-center justify-between gap-3 px-2.5 py-1.5 rounded-lg text-[12px] text-left transition-colors ${sel ? `${accent.pill} font-bold` : 'text-slate-700 hover:bg-slate-100 font-medium'}`}
                >
                  <span>{l}</span>
                  {sel && <Check size={13} />}
                </button>
              </li>
            );
          })}
        </motion.ul>
      )}
    </div>
  );
};


const UnifiedFinancialPage = ({ companyId, defaultTab }) => {
  const [tab, setTab] = useState(defaultTab || 'all');
  // iter156eq: busca SEM filtro de tipo e filtra por aba no cliente -> badges de todas as abas
  const [allEntries, setAllEntries] = useState([]);
  const entries = useMemo(() => (tab === 'all' ? allEntries : allEntries.filter(e => e.type === tab)), [allEntries, tab]);
  const [moreFilters, setMoreFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('receivable');
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [companiesList, setCompaniesList] = useState([]);
  // iter77bp: estado do modal de visualizacao de lancamento financeiro (movido para topo p/ corrigir hooks order)
  const [viewing, setViewing] = useState(null);
  // iter156bq: modal de edicao rapida de lancamento financeiro
  const [editingEntry, setEditingEntry] = useState(null);
  // iter77ct.92: estado do modal "disfarcado" de importacao de lancamentos via Excel
  const [importOpen, setImportOpen] = useState(false);
  // iter156bm: undo do ultimo lancamento cancelado (30s window)
  const [lastCancelledId, setLastCancelledId] = useState(null);
  const undoTimerRef = React.useRef(null);
  useEffect(() => () => { if (undoTimerRef.current) clearTimeout(undoTimerRef.current); }, []);
  // ---- Dynamic filters (Alvotec-style) ----
  const todayStr = localDateISO();
  const [filterStatus, setFilterStatus] = useState('todas'); // iter156ab: reset ao trocar aba (opts diferem por contexto)
  // Reset filtro ao trocar de aba — evita "recebidas" persistir na aba A Pagar
  useEffect(() => { setFilterStatus('todas'); }, [tab]);
  const [dateField, setDateField] = useState('document_date'); // document_date | due_date | paid_date | created_at
  const [period, setPeriod] = useState('today'); // today | week | month | last_month | year | custom
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);
  const [filterBy, setFilterBy] = useState(''); // client | empresa | funcionario | fornecedor | chart_account | bank_account | payment_method | ref | document_number | value
  const [filterValue, setFilterValue] = useState('');
  const [filterValueLabel, setFilterValueLabel] = useState(''); // nome legivel do valor (cliente/fornecedor/etc) para o chip
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // iter156dm: por padrao NAO traz transferencias entre contas do fechamento de caixa
  const [showTransfers, setShowTransfers] = useState(false);

  const buildQuery = () => {
    const qp = new URLSearchParams();
    if (companyId) qp.set('company_id', companyId);
    if (filterStatus !== 'todas') qp.set('filter_status', filterStatus);
    if (dateField) qp.set('date_field', dateField);
    if (period) qp.set('period', period);
    if (period === 'custom') {
      if (dateFrom) qp.set('date_from', dateFrom);
      if (dateTo) qp.set('date_to', dateTo);
    }
    if (filterBy && filterValue) {
      qp.set('filter_by', filterBy);
      qp.set('filter_value', filterValue);
    }
    if (showTransfers) qp.set('show_transfers', 'true');
    return qp.toString();
  };
  const fetchEntries = useCallback(async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${ERP}/financial?${buildQuery()}`, { withCredentials: true });
      setAllEntries(data.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, filterStatus, dateField, period, dateFrom, dateTo, filterBy, filterValue, showTransfers]);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const [cli, sup, chart, bnk, pm, emp, co] = await Promise.all([
          // iter156dv: passa company_id para filtrar clientes/fornecedores vinculados
          // a filial ativa (mesmo comportamento do cadastro em Cadastros > Clientes).
          axios.get(`${ERP}/clients?company_id=${companyId}`, { withCredentials: true }),
          axios.get(`${ERP}/suppliers?company_id=${companyId}`, { withCredentials: true }),
          axios.get(`${ERP}/chart-accounts?company_id=${companyId}`, { withCredentials: true }),
          axios.get(`${ERP}/bank-accounts?company_id=${companyId}`, { withCredentials: true }),
          axios.get(`${ERP}/payment-methods?scope=financial&company_id=${companyId}`, { withCredentials: true }),
          axios.get(`${ERP}/employees${companyId ? `?company_id=${companyId}` : ''}`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
          axios.get(`${ERP}/companies`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
        ]);
        setClients(cli.data?.data || []);
        setSuppliers(sup.data?.data || []);
        setChartAccounts(chart.data?.data || []);
        setBankAccounts(bnk.data?.data || []);
        setPaymentMethods(pm.data?.data || []);
        setEmployees(emp.data?.data || []);
        setCompaniesList(co.data?.data || []);
      } catch { /* ignore */ }
    })();
  }, [companyId]);
  const openInsert = (type) => { setFormType(type); setShowForm(true); };
  // iter77ct.101: Estado do modal de baixa (single OR bulk). Recebe array de entries.
  const [settleEntries, setSettleEntries] = useState(null); // null => fechado; array => aberto
  const openSettle = (entriesToSettle) => {
    if (!entriesToSettle || !entriesToSettle.length) { toast.info('Selecione ao menos um titulo em aberto.'); return; }
    // filtra somente pendentes
    const pending = entriesToSettle.filter(e => e.status === 'pending');
    if (!pending.length) { toast.info('Nenhum titulo pendente na selecao.'); return; }
    if (pending.length < entriesToSettle.length) {
      toast.info(`${entriesToSettle.length - pending.length} titulo(s) ja quitado(s) foram ignorados.`);
    }
    setSettleEntries(pending);
  };
  const markPaid = (id) => {
    const entry = entries.find(e => e.id === id);
    if (entry) openSettle([entry]);
  };
  const deleteEntry = async (id) => {
    if (!(await confirmDialog({ title: 'Cancelar este lancamento?', confirmLabel: 'Confirmar', danger: true }))) return;
    try {
      await axios.delete(`${ERP}/financial/${id}`, { withCredentials: true });
      // iter156bm: guarda ultimo cancelado para permitir undo (30s)
      setLastCancelledId(id);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(() => setLastCancelledId(null), 30000);
      toast.success('Cancelado');
      fetchEntries();
    } catch { toast.error('Erro'); }
  };
  const undoDelete = async () => {
    if (!lastCancelledId) return;
    try {
      await axios.post(`${ERP}/financial/${lastCancelledId}/restore`, {}, { withCredentials: true });
      toast.success('Cancelamento desfeito');
      setLastCancelledId(null);
      if (undoTimerRef.current) { clearTimeout(undoTimerRef.current); undoTimerRef.current = null; }
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao desfazer'); }
  };
  if (!companyId) return <NoCompany />;
  // iter156bm: totals — excluem 'cancelled' pra nao continuar somando lancamentos excluidos
  const _live = entries.filter(e => e.status !== 'cancelled');
  // iter156ep: valor liquido (bruto - taxa da forma de pagamento); fallback = amount
  const _net = (e) => (e.net_amount != null ? Number(e.net_amount) : Number(e.amount || 0));
  // iter156et: pendente = saldo (amount - received_amount); pago inclui parciais ja recebidas
  const _remaining = (e) => Math.max(0, Number(e.amount || 0) - Number(e.received_amount || 0));
  const totalPending = _live.filter(e => e.status === 'pending').reduce((s, e) => s + _remaining(e), 0);
  const totalPaid = _live.filter(e => e.status === 'paid').reduce((s, e) => s + (e.amount || 0), 0)
    + _live.filter(e => e.status === 'pending').reduce((s, e) => s + Number(e.received_amount || 0), 0);
  const totalAll = _live.reduce((s, e) => s + (e.amount || 0), 0);
  // iter156ep: FLUXO DE CAIXA (aba "todas") — funciona como extrato bancario:
  // entradas (a receber) MENOS saidas (a pagar) = saldo liquido. Antes somava tudo.
  const cfIn = _live.filter(e => e.type === 'receivable');
  const cfOut = _live.filter(e => e.type === 'payable');
  const cfInGross = cfIn.reduce((s, e) => s + (e.amount || 0), 0);
  const cfInNet = cfIn.reduce((s, e) => s + _net(e), 0);
  const cfOutTotal = cfOut.reduce((s, e) => s + _net(e), 0);
  const cfBalance = cfInNet - cfOutTotal;
  const cfPaidBalance = cfIn.filter(e => e.status === 'paid').reduce((s, e) => s + _net(e), 0) - cfOut.filter(e => e.status === 'paid').reduce((s, e) => s + _net(e), 0);
  const cfPendingIn = cfIn.filter(e => e.status === 'pending').reduce((s, e) => s + _remaining(e), 0);
  const cfPendingOut = cfOut.filter(e => e.status === 'pending').reduce((s, e) => s + _remaining(e), 0);
  // iter156eq: abas modernas (underline animado) com badge de quantidade + valor por modulo
  const _allLive = allEntries.filter(e => e.status !== 'cancelled');
  const _recv = _allLive.filter(e => e.type === 'receivable');
  const _pay = _allLive.filter(e => e.type === 'payable');
  const _sum = (arr) => arr.reduce((s, e) => s + _net(e), 0);
  const tabs = [
    { key: 'all', label: 'Fluxo de Caixa', icon: ArrowDownUp, count: _allLive.length, value: _sum(_recv) - _sum(_pay),
      accent: 'cyan', text: 'text-cyan-600', pill: 'bg-cyan-50 text-cyan-700 border-cyan-200', pillActive: 'bg-cyan-600 text-white border-cyan-600', bar: 'from-cyan-500 to-violet-600', glow: 'shadow-[0_2px_10px_rgba(6,182,212,0.55)]', border: 'border-l-cyan-500' },
    { key: 'receivable', label: 'A Receber', icon: ArrowDownRight, count: _recv.length, value: _sum(_recv),
      accent: 'emerald', text: 'text-emerald-600', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', pillActive: 'bg-emerald-600 text-white border-emerald-600', bar: 'from-emerald-500 to-teal-400', glow: 'shadow-[0_2px_10px_rgba(16,185,129,0.55)]', border: 'border-l-emerald-500' },
    { key: 'payable', label: 'A Pagar', icon: ArrowUpRight, count: _pay.length, value: _sum(_pay),
      accent: 'rose', text: 'text-rose-600', pill: 'bg-rose-50 text-rose-700 border-rose-200', pillActive: 'bg-rose-600 text-white border-rose-600', bar: 'from-rose-500 to-pink-500', glow: 'shadow-[0_2px_10px_rgba(244,63,94,0.55)]', border: 'border-l-rose-500' },
  ];
  const activeTab = tabs.find(t => t.key === tab) || tabs[0];

  // ---- Export CSV (Excel-friendly) ----
  const exportCSV = () => {
    if (!entries.length) { toast.info('Nenhum lancamento para exportar'); return; }
    const headers = ['Status', 'Tipo', 'Ref', 'Forma Pgto', 'Nome', 'Descricao', 'Plano Contas', 'Dt.Lanc', 'Dt.Docto', 'Dt.Vencto', 'Dt.Pgto', 'N Documento', 'Historico', 'Valor'];
    const rows = entries.map(e => [
      e.status === 'paid' ? 'Quitada' : e.status === 'cancelled' ? 'Cancelada' : 'A receber/pagar',
      e.type === 'receivable' ? 'RECEBER' : 'PAGAR',
      e.ref || e.id?.slice(-6) || '',
      e.payment_method || '',
      e.party_name || e.customer_name || '',
      e.description || '',
      e.category || '',
      e.created_at ? fmtDateBR(e.created_at) : '',
      e.document_date ? fmtDateBR(e.document_date) : '',
      e.due_date ? fmtDateBR(e.due_date) : '',
      e.paid_date ? fmtDateBR(e.paid_date) : '',
      e.document_number || '',
      (e.notes || '').replace(/[\r\n;]/g, ' '),
      (e.amount || 0).toFixed(2).replace('.', ','),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `financeiro-${localDateISO()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  // ---- "Filtro por" dropdown — value control varies by selected field ----
  const renderFilterValueControl = () => {
    const cls = inputClass + ' text-xs';
    const pick = (v, opt) => { setFilterValue(v); setFilterValueLabel(opt?.label || ''); };
    const placeholderInput = (ph) => (
      <input value={filterValue} onChange={e => setFilterValue(e.target.value)} placeholder={ph} className={cls} data-testid="fin-filter-value" />
    );
    if (!filterBy) return <input disabled placeholder="Selecione um filtro" className={cls + ' bg-slate-50'} />;
    // iter156dt: substitui <select> por Combobox pesquisavel — mesmas keys/values
    // iter156du: para listas grandes (>5000), usa busca REMOTA (?search=) para achar
    // clientes/fornecedores/funcionarios que nao foram carregados na primeira leva.
    // iter156dv: PASSA company_id para respeitar filial ativa — antes trazia clientes
    // de outras filiais (bug reportado: filial JEANS tinha 1 "DREINE - ITABERAI", mas
    // a busca remota mostrava 3 nomes vindos de outras filiais).
    const _cidQuery = companyId ? `&company_id=${encodeURIComponent(companyId)}` : '';
    if (filterBy === 'client') {
      const opts = clients.map(c => ({ value: c.id, label: c.name, keywords: c.document || '' }));
      const remoteSearch = async (q) => {
        try {
          const { data } = await axios.get(`${ERP}/clients?search=${encodeURIComponent(q)}&limit=50${_cidQuery}`, { withCredentials: true });
          return (data.data || []).map(c => ({ value: c.id, label: c.name, keywords: c.document || '', subtitle: c.document || '' }));
        } catch { return []; }
      };
      return <Combobox value={filterValue} options={opts} onChange={pick} placeholder="Selecione cliente..." testId="fin-filter-value" allowClear onSearch={remoteSearch} />;
    }
    if (filterBy === 'fornecedor') {
      const opts = suppliers.map(s => ({ value: s.id, label: s.name, keywords: s.document || '' }));
      const remoteSearch = async (q) => {
        try {
          const { data } = await axios.get(`${ERP}/suppliers?search=${encodeURIComponent(q)}&limit=50${_cidQuery}`, { withCredentials: true });
          return (data.data || []).map(s => ({ value: s.id, label: s.name, keywords: s.document || '', subtitle: s.document || '' }));
        } catch { return []; }
      };
      return <Combobox value={filterValue} options={opts} onChange={pick} placeholder="Selecione fornecedor..." testId="fin-filter-value" allowClear onSearch={remoteSearch} />;
    }
    if (filterBy === 'funcionario') {
      const opts = employees.map(em => ({ value: em.id, label: em.name }));
      const remoteSearch = async (q) => {
        try {
          const { data } = await axios.get(`${ERP}/employees?search=${encodeURIComponent(q)}&limit=50${_cidQuery}`, { withCredentials: true });
          return (data.data || []).map(em => ({ value: em.id, label: em.name }));
        } catch { return []; }
      };
      return <Combobox value={filterValue} options={opts} onChange={pick} placeholder="Selecione funcionario..." testId="fin-filter-value" allowClear onSearch={remoteSearch} />;
    }
    if (filterBy === 'empresa') {
      const opts = companiesList.map(co => ({ value: co.id, label: `${co.code || ''} · ${co.name || ''}`.trim(), keywords: `${co.code || ''} ${co.name || ''}` }));
      return <Combobox value={filterValue} options={opts} onChange={pick} placeholder="Selecione empresa..." testId="fin-filter-value" allowClear />;
    }
    if (filterBy === 'chart_account') {
      const opts = chartAccounts.map(ca => ({ value: ca.id, label: `${ca.code || ''} · ${ca.name || ''}`.trim(), keywords: `${ca.code || ''} ${ca.name || ''}` }));
      return <Combobox value={filterValue} options={opts} onChange={pick} placeholder="Selecione plano..." testId="fin-filter-value" allowClear />;
    }
    if (filterBy === 'bank_account') {
      const opts = bankAccounts.map(ba => ({ value: ba.id, label: ba.name || ba.description || ba.bank_name || 'Conta', keywords: `${ba.bank_name || ''} ${ba.agency || ''}` }));
      return <Combobox value={filterValue} options={opts} onChange={pick} placeholder="Selecione conta..." testId="fin-filter-value" allowClear />;
    }
    if (filterBy === 'payment_method') {
      const opts = paymentMethods.map(pm => ({ value: pm.name, label: pm.name, subtitle: pm.type || '' }));
      return <Combobox value={filterValue} options={opts} onChange={pick} placeholder="Selecione forma..." testId="fin-filter-value" allowClear />;
    }
    if (filterBy === 'value') return placeholderInput('R$ (ex: 100.50)');
    if (filterBy === 'ref') return placeholderInput('Referencia/Nº interno');
    if (filterBy === 'document_number') return placeholderInput('Nº do Documento');
    return placeholderInput('Valor do filtro');
  };

  // ---- DataTable column definitions (per-user persisted) ----
  const STATUS_COLOR = { pending: 'bg-amber-500', paid: 'bg-emerald-500', cancelled: 'bg-rose-500' };
  const STATUS_LABEL = { pending: 'A receber/pagar', paid: 'Quitada', cancelled: 'Cancelada' };
  const fmtBRL = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // iter156aa: legado consolidado — sempre 2 casas decimais no BRL, evita "R$ 12,8725" no PDV/relatorios.
  // iter77ct.91: usa fmtDateBR (definido no topo do arquivo) que trata date-only
  // YYYY-MM-DD sem shift de timezone. Antes usavamos new Date().toLocaleDateString()
  // que parseia "2026-07-10" como UTC midnight e no fuso do Brasil (UTC-3) exibia
  // como 09/07/2026 na lista (enquanto o modal de detalhes mostrava a data crua correta).
  const fmtDate = (d) => fmtDateBR(d);

  const finColumns = [
    { key: 'color',  label: 'Status',       render: r => <span className={`inline-block w-3 h-3 rounded-full ${STATUS_COLOR[r.status] || 'bg-slate-300'}`} title={STATUS_LABEL[r.status]} />, sortValue: r => r.status },
    { key: 'type',   label: 'Tipo',         render: r => <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${r.type === 'receivable' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{r.type === 'receivable' ? 'RECEBER' : 'PAGAR'}</span>, sortValue: r => r.type },
    // iter77bp: Codigo de referencia agora sempre numerico (decimal). Para entradas vindas do PDV usa
    // sale_number; para outras converte os ultimos 6 chars hex do _id em inteiro decimal.
    // iter156ej: entry_code sequencial (parcelas: 8899.1, 8899.2) tem prioridade.
    { key: 'ref',    label: 'Cod.',          render: r => <span className="font-mono text-xs text-slate-700 font-bold" data-testid={`fin-code-${r.id}`}>{r.entry_code || r.sale_number || (r.id ? parseInt(r.id.slice(-6), 16) : '—')}</span>, sortValue: r => r.entry_code ? parseFloat(r.entry_code) : (r.sale_number || (r.id ? parseInt(r.id.slice(-6), 16) : 0)) },
    { key: 'pgto',   label: 'F.Pgto',       render: r => <span className="text-xs text-slate-700">{r.payment_method || '—'}</span>, sortValue: r => r.payment_method },
    // iter77ct.101: Nome (cliente/fornecedor) e Descricao agora sao colunas separadas.
    { key: 'name',   label: 'Nome',         render: r => <span className="text-xs font-medium text-slate-900"><PartyTag r={r} />{r.party_name || r.customer_name || '—'}</span>, sortValue: r => r.party_name || r.customer_name || '' },
    { key: 'desc',   label: 'Descricao',    render: r => <span className="text-xs text-slate-700">{r.origin === 'pedido' && <span className="inline-flex items-center px-1 rounded text-[8px] leading-[14px] font-black tracking-wider border mr-1.5 align-middle bg-orange-50 text-orange-700 border-orange-200" title="Gerado por Pedido de Venda" data-testid="fin-pedido-tag">PEDIDO</span>}{r.description || '—'}</span>, sortValue: r => r.description || '' },
    { key: 'plan',   label: 'Pl.Contas',    render: r => {
      // iter156di: exibe nome do plano de contas (mais didatico) ao inves so do codigo.
      // Prioridade: match por chart_account_id -> match por category (code) -> fallback texto salvo.
      let planName = null;
      if (r.chart_account_id) {
        const ca = chartAccounts.find(a => a.id === r.chart_account_id);
        if (ca) planName = ca.name;
      }
      if (!planName && r.category) {
        const ca = chartAccounts.find(a => a.code === r.category);
        if (ca) planName = ca.name;
      }
      const display = planName || r.category || '—';
      return <span className="text-xs text-slate-600" title={r.category ? `Cod. ${r.category}` : ''}>{display}</span>;
    }, sortValue: r => {
      if (r.chart_account_id) {
        const ca = chartAccounts.find(a => a.id === r.chart_account_id);
        if (ca) return ca.name;
      }
      if (r.category) {
        const ca = chartAccounts.find(a => a.code === r.category);
        if (ca) return ca.name;
      }
      return r.category || '';
    } },
    // iter77bp: nova coluna numero da venda (so faz sentido quando vinculado a uma venda do PDV)
    { key: 'nvenda', label: 'N Venda',      render: r => <span className="font-mono text-xs text-slate-600">{r.sale_number || '—'}</span>, sortValue: r => r.sale_number || 0 },
    { key: 'docto',  label: 'Dt.Docto',     render: r => fmtDate(r.document_date || r.created_at), sortValue: r => r.document_date || r.created_at },
    { key: 'lanc',   label: 'Dt.Lanc',      render: r => fmtDate(r.created_at), sortValue: r => r.created_at },
    { key: 'venc',   label: 'Dt.Vencto',    render: r => fmtDate(r.due_date), sortValue: r => r.due_date },
    { key: 'receb',  label: 'Dt.Receb',     render: r => fmtDate(r.paid_date), sortValue: r => r.paid_date },
    { key: 'doc',    label: 'N Documento',  render: r => <span className="text-xs text-slate-600">{r.document_number || '—'}</span>, sortValue: r => r.document_number },
    { key: 'notes',  label: 'Historico',    render: r => <span className="text-xs text-slate-600">{r.notes || '—'}</span>, sortValue: r => r.notes },
    { key: 'amount', label: 'V.Bruto',      align: 'right', render: r => <span className={`text-xs font-bold ${r.type === 'receivable' ? 'text-emerald-700' : 'text-rose-700'}`}>{fmtBRL(r.amount)}</span>, sortValue: r => r.amount },
    // iter156ep: taxa da forma de pagamento e valor liquido (bruto - taxa)
    { key: 'fee',    label: 'Taxa',         align: 'right', render: r => (Number(r.fee_amount) > 0
        ? <span className="text-xs text-orange-700" title={`${Number(r.fee_percent || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%${r.installments > 1 ? ` · ${r.installments}x` : ''}`} data-testid={`fin-fee-${r.id}`}>-{fmtBRL(r.fee_amount)} <span className="text-[10px] text-slate-400">({Number(r.fee_percent || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%{r.installments > 1 ? ` ${r.installments}x` : ''})</span></span>
        : <span className="text-xs text-slate-300">—</span>), sortValue: r => r.fee_amount || 0 },
    { key: 'net',    label: 'V.Liquido',    align: 'right', render: r => <span className={`text-xs font-black ${r.type === 'receivable' ? 'text-emerald-800' : 'text-rose-800'}`} data-testid={`fin-net-${r.id}`}>{fmtBRL(r.net_amount != null ? r.net_amount : r.amount)}</span>, sortValue: r => (r.net_amount != null ? r.net_amount : r.amount) },
    // iter156et: baixa parcial — valor ja recebido/pago e saldo em aberto no MESMO titulo
    { key: 'received', label: 'Recebido', align: 'right', render: r => (Number(r.received_amount) > 0
        ? <span className="text-xs" data-testid={`fin-received-${r.id}`}><span className="font-bold text-emerald-700">{fmtBRL(r.received_amount)}</span>{r.status === 'pending' && <span className="block text-[10px] text-amber-700 font-semibold">saldo {fmtBRL(Math.max(0, Number(r.amount || 0) - Number(r.received_amount || 0)))}</span>}</span>
        : <span className="text-xs text-slate-300">—</span>), sortValue: r => r.received_amount || 0 },
  ];
  const finDefaultVisible = ['color', 'ref', 'pgto', 'name', 'desc', 'plan', 'nvenda', 'venc', 'amount', 'fee', 'net', 'received'];
  const finDefaultOrder = ['color', 'ref', 'pgto', 'name', 'desc', 'plan', 'nvenda', 'lanc', 'docto', 'venc', 'receb', 'amount', 'fee', 'net', 'received', 'doc', 'notes', 'type'];

  // iter77bp: estado do estornar (volta status='paid' para 'pending' e zera paid_date)
  const reverseEntry = async (e) => {
    if (!(await confirmDialog({ title: `Estornar lancamento #${e.sale_number || (e.id ? parseInt(e.id.slice(-6), 16) : '')}?`, message: 'O lancamento voltara para status PENDENTE.', confirmLabel: 'Estornar', danger: true }))) return;
    try {
      await axios.put(`${ERP}/financial/${e.id}`, { status: 'pending', paid_date: null }, { withCredentials: true });
      toast.success('Lancamento estornado');
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao estornar'); }
  };

  // iter156fc: estorno da BAIXA feita pelo Financeiro (settle/pay) — mesmo quando o titulo nasceu de uma venda.
  const settledInFin = (e) => !!(e.settle_batch_id || e.settled_via === 'financeiro' || (e.partial_payments || []).length);
  const reverseSettlement = async (e) => {
    const partial = e.status !== 'paid';
    if (!(await confirmDialog({ title: partial ? 'Estornar baixa parcial?' : 'Estornar baixa?', message: `O titulo${e.entry_code ? ` ${e.entry_code}` : ''} voltara a ficar EM ABERTO pelo valor original (${fmtBRL(e.amount || 0)}). Recebimentos, juros/desconto e cheque desta baixa serao desfeitos.`, confirmLabel: 'Estornar baixa', danger: true }))) return;
    try {
      await axios.post(`${ERP}/financial/${e.id}/reverse-settlement`, {}, { withCredentials: true });
      toast.success('Baixa estornada — titulo em aberto novamente');
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao estornar baixa', { duration: 7000 }); }
  };

  const renderRowActions = (e) => (
    <div className="flex items-center gap-1 justify-end">
      {/* iter77bp: botao de visualizar (sempre disponivel) */}
      <button onClick={() => setViewing(e)} className="p-1 text-slate-400 hover:text-violet-600" title="Visualizar lancamento" data-testid={`fin-view-${e.id}`}><Eye size={13} /></button>
      {/* iter156bq: botao Editar — abre modal de edicao rapida (respeita permissao financial.edit via backend PUT) */}
      {e.status !== 'cancelled' && (
        <button onClick={() => setEditingEntry(e)} className="p-1 text-slate-400 hover:text-blue-600" title="Editar lancamento" data-testid={`fin-edit-${e.id}`}><Edit size={13} /></button>
      )}
      {e.status === 'pending' && <button onClick={() => markPaid(e.id)} className="text-[11px] font-bold px-2 py-1 rounded bg-amber-50 text-amber-700 hover:bg-amber-100" data-testid={`fin-pay-${e.id}`}>Baixar</button>}
      {e.status === 'pending' && Number(e.received_amount || 0) > 0 && settledInFin(e) && (
        <button onClick={() => reverseSettlement(e)} className="p-1 text-emerald-600 hover:text-emerald-800" title={`Estornar baixa parcial (${fmtBRL(e.received_amount)} recebido)`} data-testid={`fin-reverse-settle-${e.id}`}><RotateCcw size={13} /></button>
      )}
      {e.status === 'paid' && settledInFin(e) && (
        <button onClick={() => reverseSettlement(e)} className="text-[11px] font-bold px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1" title="Estornar a baixa feita pelo Financeiro" data-testid={`fin-reverse-settle-${e.id}`}><RotateCcw size={10} /> Estornar</button>
      )}
      {e.status === 'paid' && !settledInFin(e) && (finOrigin(e)
        ? <span className="relative group inline-flex">
            <button onClick={() => { const o = finOrigin(e); toast.info(`Lancamento gerado em ${o.label}. ${o.hint}`, { duration: 7000 }); }} className="p-1 text-slate-400 hover:text-violet-600 cursor-help" aria-label={`Origem: ${finOrigin(e).label}`} data-testid={`fin-reverse-blocked-${e.id}`}><ExternalLink size={13} /></button>
            <span className="pointer-events-none absolute right-0 bottom-full mb-1.5 z-30 hidden group-hover:block w-64 rounded-lg bg-slate-900 text-white text-[11px] leading-snug px-3 py-2 shadow-xl" data-testid={`fin-origin-tip-${e.id}`}>
              <span className="block font-bold text-amber-300 mb-0.5">Origem: {finOrigin(e).label}</span>
              <span className="block opacity-90">Estorno bloqueado no Financeiro. {finOrigin(e).hint}</span>
            </span>
          </span>
        : <button onClick={() => reverseEntry(e)} className="text-[11px] font-bold px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1" data-testid={`fin-reverse-${e.id}`}><RotateCcw size={10} /> Estornar</button>)}
      <button onClick={() => deleteEntry(e.id)} className="p-1 text-slate-400 hover:text-rose-500" title="Cancelar" data-testid={`fin-delete-${e.id}`}><Trash2 size={13} /></button>
    </div>
  );

  return (
    <div data-testid="unified-financial-page">
      {/* ===== iter156es: ABAS ESTILO NAVEGADOR — larguras iguais, limpas, delimitador suave ===== */}
      <div className="bg-slate-200/70 rounded-t-2xl px-2 pt-2 flex items-end overflow-x-auto scrollbar-hide" role="tablist" data-testid="fin-tabs">
        {tabs.map((t, idx) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <React.Fragment key={t.key}>
              {idx > 0 && <span className="w-px h-6 mb-2.5 shrink-0 bg-gradient-to-b from-transparent via-slate-400/50 to-transparent" aria-hidden />}
              <button
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                data-testid={`fin-tab-${t.key}`}
                className={`group relative flex-1 min-w-[190px] flex items-center justify-center gap-2 px-4 h-11 rounded-t-xl text-sm whitespace-nowrap transition-colors duration-200 cursor-pointer select-none ${active ? 'bg-white text-slate-900 font-bold z-10 shadow-[0_-3px_12px_rgba(15,23,42,0.06)]' : 'text-slate-500 font-medium hover:bg-white/50 hover:text-slate-800'}`}
              >
                {active && (
                  <motion.span
                    layoutId="fin-tab-topbar"
                    className={`absolute top-0 left-4 right-4 h-[3px] rounded-b-full bg-gradient-to-r ${t.bar}`}
                    transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  />
                )}
                <Icon size={15} className={active ? t.text : 'text-slate-400 group-hover:text-slate-600'} />
                <span>{t.label}</span>
                <span className={`inline-flex items-center text-[10px] font-bold min-w-[20px] justify-center px-1.5 py-0.5 rounded-full transition-colors duration-200 ${active ? t.pillActive : 'bg-slate-300/60 text-slate-600'}`} data-testid={`fin-tab-count-${t.key}`}>{t.count}</span>
                <span className={`hidden md:inline text-[11px] font-semibold tabular-nums ${active ? t.text : 'text-slate-400'}`} data-testid={`fin-tab-value-${t.key}`}>{fmtBRL(t.value)}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>


      {/* ===== iter156er: FILTROS EM PÍLULAS COM MENU (sem radios/chips) ===== */}
      <div className="bg-white border border-slate-200 border-t-0 rounded-b-xl px-3 py-2.5 mb-4" data-testid="fin-filter-panel">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wide text-slate-400 mr-1"><Filter size={12} /> Filtros</span>
          <FilterPill
            label="Status"
            value={filterStatus}
            defaultValue="todas"
            accent={activeTab}
            testid="fin-status"
            options={tab === 'receivable'
              ? [['todas','Todas'],['recebidas','Recebidas'],['areceber','A receber'],['atrasadas','Atrasadas']]
              : tab === 'payable'
                ? [['todas','Todas'],['pagas','Pagas'],['apagar','A pagar'],['atrasadas','Atrasadas']]
                : [['todas','Todas'],['quitadas','Quitadas'],['pendentes','Pendentes'],['atrasadas','Atrasadas']]}
            onChange={setFilterStatus}
          />
          <FilterPill
            label="Período"
            value={period}
            defaultValue="today"
            accent={activeTab}
            testid="fin-period"
            options={[['today','Hoje'],['week','Esta semana'],['month','Este mês'],['last_month','Mês anterior'],['year','Este ano'],['custom','Personalizado…']]}
            onChange={setPeriod}
          />
          {period === 'custom' && (
            <div className="flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded-full border border-slate-300 bg-slate-50" data-testid="fin-period-custom-range">
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-2 py-1 rounded-full text-[11px] font-semibold bg-transparent outline-none" data-testid="fin-date-from" />
              <span className="text-slate-400 text-xs">→</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-2 py-1 rounded-full text-[11px] font-semibold bg-transparent outline-none" data-testid="fin-date-to" />
            </div>
          )}
          <FilterPill
            label="Data"
            value={dateField}
            defaultValue="document_date"
            accent={activeTab}
            testid="fin-datefield"
            options={[['document_date','Documento'],['due_date','Vencimento'],['paid_date','Pagamento'],['created_at','Lançamento']]}
            onChange={setDateField}
          />
          <button type="button" onClick={() => setMoreFilters(v => !v)} data-testid="fin-more-filters"
            className={`inline-flex items-center gap-1.5 h-8 pl-3 pr-2.5 rounded-full text-[11px] font-bold border transition-colors duration-150 ${moreFilters || filterBy || showTransfers ? activeTab.pill : 'bg-white border-dashed border-slate-300 text-slate-600 hover:border-slate-400 hover:bg-slate-50'}`}>
            <Plus size={12} className={`transition-transform duration-200 ${moreFilters ? 'rotate-45' : ''}`} />
            {filterBy
              ? (() => {
                  const FILTER_LABELS = { client: 'Cliente', empresa: 'Empresa', funcionario: 'Funcionário', fornecedor: 'Fornecedor', chart_account: 'Pl. Contas', bank_account: 'Conta Banco', value: 'Valor', ref: 'Ref', document_number: 'Nº Documento', payment_method: 'Forma de Pagamento' };
                  return <><span className="opacity-70 font-medium">{FILTER_LABELS[filterBy] || filterBy}</span>{filterValue ? <span className="max-w-[160px] truncate" data-testid="fin-active-filter-value">{filterValueLabel || String(filterValue)}</span> : null}</>;
                })()
              : 'Mais filtros'}
            {showTransfers && <span className="ml-1 px-1.5 rounded-full bg-white/70 text-[9px]">transf.</span>}
          </button>
          <div className="ml-auto flex items-center gap-1.5">
            {(filterStatus !== 'todas' || period !== 'today' || dateField !== 'document_date' || filterBy || showTransfers) && (
              <button onClick={() => { setFilterStatus('todas'); setDateField('document_date'); setPeriod('today'); setFilterBy(''); setFilterValue(''); setFilterValueLabel(''); setDateFrom(todayStr); setDateTo(todayStr); setShowTransfers(false); }} className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-semibold text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors" data-testid="fin-filter-clear"><X size={12} /> Limpar</button>
            )}
            <button onClick={fetchEntries} className={`inline-flex items-center gap-1.5 h-8 px-4 rounded-full text-[11px] font-bold text-white shadow-sm bg-gradient-to-r ${activeTab.bar} hover:brightness-110 transition-[filter]`} data-testid="fin-filter-apply"><Search size={13} /> Filtrar</button>
          </div>
        </div>

        {moreFilters && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ duration: 0.18 }} className="overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-end pt-2 mt-2 border-t border-slate-100" data-testid="fin-more-filters-panel">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-0.5">Filtro por</label>
                <select value={filterBy} onChange={e => { setFilterBy(e.target.value); setFilterValue(''); setFilterValueLabel(''); }} className={inputClass + ' text-xs'} data-testid="fin-filter-by">
                  <option value="">— Selecione —</option>
                  <option value="client">Cliente</option>
                  <option value="empresa">Empresa</option>
                  <option value="funcionario">Funcionario</option>
                  <option value="fornecedor">Fornecedor</option>
                  <option value="chart_account">Pl.Contas</option>
                  <option value="bank_account">Conta Banco</option>
                  <option value="value">Valor</option>
                  <option value="ref">Ref</option>
                  <option value="document_number">Nº Documento</option>
                  <option value="payment_method">Forma de Pagamento</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-0.5">Valor do filtro</label>
                {renderFilterValueControl()}
              </div>
              <label className="flex items-center gap-1.5 pb-2 cursor-pointer text-[11px] whitespace-nowrap">
                <input type="checkbox" checked={showTransfers} onChange={e => setShowTransfers(e.target.checked)} className="accent-blue-600 w-3.5 h-3.5" data-testid="fin-show-transfers" />
                <span className="text-slate-700 font-semibold">Trazer transferências entre contas</span>
                <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-slate-200 text-slate-600 text-[9px] font-black cursor-help hover:bg-blue-200" title="Quando ativo, inclui na lista as transferências criadas no fechamento de caixa (saída de um caixa e entrada em outro). Por padrão ficam ocultas.">?</span>
              </label>
            </div>
          </motion.div>
        )}
      </div>

      {tab === 'all' ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="cashflow-cards">
          <div className="bg-white border border-emerald-200 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><ArrowDownRight size={12} className="text-emerald-600" /> Entradas</p>
            <p className="text-xl font-bold text-emerald-600" data-testid="cashflow-in">{fmtBRL(cfInNet)}</p>
            {Math.abs(cfInGross - cfInNet) > 0.005 && <p className="text-[10px] text-slate-400">bruto {fmtBRL(cfInGross)} · taxas -{fmtBRL(cfInGross - cfInNet)}</p>}
          </div>
          <div className="bg-white border border-rose-200 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><ArrowUpRight size={12} className="text-rose-600" /> Saidas</p>
            <p className="text-xl font-bold text-rose-600" data-testid="cashflow-out">{fmtBRL(cfOutTotal)}</p>
          </div>
          <div className={`rounded-xl p-4 text-center border border-l-4 transition-colors duration-300 ${cfBalance >= 0 ? `bg-cyan-50/60 border-cyan-200 ${activeTab.border}` : 'bg-rose-50 border-rose-200 border-l-rose-500'}`}>
            <p className="text-xs text-slate-600 font-bold">Saldo Liquido</p>
            <p className={`text-xl font-black ${cfBalance >= 0 ? 'text-cyan-700' : 'text-rose-700'}`} data-testid="cashflow-balance">{fmtBRL(cfBalance)}</p>
            <p className="text-[10px] text-slate-500">realizado {fmtBRL(cfPaidBalance)}</p>
          </div>
          <div className="bg-white border border-amber-200 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-500">Pendente</p>
            <p className="text-xl font-bold text-amber-600" data-testid="cashflow-pending">{fmtBRL(cfPendingIn - cfPendingOut)}</p>
            <p className="text-[10px] text-slate-400">a receber {fmtBRL(cfPendingIn)} · a pagar {fmtBRL(cfPendingOut)}</p>
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className={`bg-white border border-slate-200 border-l-4 ${activeTab.border} rounded-xl p-4 text-center transition-colors duration-300`}><p className="text-xs text-slate-500">Total</p><p className={`text-xl font-bold ${activeTab.text}`}>{fmtBRL(totalAll)}</p></div>
        <div className="bg-white border border-amber-200 rounded-xl p-4 text-center"><p className="text-xs text-slate-500">Pendente</p><p className="text-xl font-bold text-amber-600">{fmtBRL(totalPending)}</p></div>
        <div className="bg-white border border-emerald-200 rounded-xl p-4 text-center"><p className="text-xs text-slate-500">Pago/Recebido</p><p className="text-xl font-bold text-emerald-600">{fmtBRL(totalPaid)}</p></div>
      </div>
      )}

      {/* Status legend (matches Alvotec colors) */}
      <div className="flex items-center gap-3 mb-3 text-[11px]">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span><span className="text-slate-600">A receber/pagar</span></span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span><span className="text-slate-600">Quitada</span></span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span><span className="text-slate-600">Cancelada</span></span>
      </div>

      {showForm && (
        <FinancialLaunchModal
          companyId={companyId}
          initialType={formType}
          clients={clients}
          suppliers={suppliers}
          chartAccounts={chartAccounts}
          bankAccounts={bankAccounts}
          paymentMethods={paymentMethods}
          onClose={() => setShowForm(false)}
          onSaved={() => { fetchEntries(); }}
        />
      )}
      <DataTable
        prefKey={`financial-v3-${tab}`}
        rowClassName={(r) => r.origin === 'pedido' ? 'border-orange-400 bg-orange-50/40' : null}
        columns={finColumns}
        defaultVisible={finDefaultVisible}
        defaultOrder={finDefaultOrder}
        rows={entries}
        keyField="id"
        loading={loading}
        emptyText="Nenhum lancamento"
        renderActions={renderRowActions}
        enableRowSelection={true}
        selectionActions={({ rows: selectedRows, clear }) => {
          const pendingSelected = selectedRows.filter(r => r.status === 'pending');
          if (!pendingSelected.length) return null;
          const totPend = pendingSelected.reduce((s, r) => s + (r.amount || 0), 0);
          return (
            <button
              onClick={() => { openSettle(pendingSelected); clear(); }}
              className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-xs font-black hover:shadow-md flex items-center gap-1"
              data-testid="fin-bulk-settle-btn"
            >
              <Check size={12} /> Baixar {pendingSelected.length} titulo(s) · {fmtBRL(totPend)}
            </button>
          );
        }}
        mobileBadge={(r) => <span className={`block w-3 h-3 rounded-full ${STATUS_COLOR[r.status] || 'bg-slate-300'}`} />}
        mobilePrimary={(r) => r.party_name || r.customer_name || r.description || '—'}
        mobileSecondary={(r) => <>{(r.party_name || r.customer_name) ? <span className="text-slate-500">{r.description || '—'} · </span> : null}{fmtDate(r.due_date)} · <span className={r.type === 'receivable' ? 'text-emerald-700 font-bold' : 'text-rose-700 font-bold'}>{fmtBRL(r.amount)}</span></>}
        totalLabel={tab === 'all' ? `${entries.length} lancamentos · Saldo liquido: ${fmtBRL(cfBalance)}` : `${entries.length} lancamentos · Total: ${fmtBRL(totalAll)}`}
        drawerExtraSection={
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[11px] font-semibold text-slate-500 hover:text-violet-700 bg-white/60 hover:bg-violet-50 border border-slate-200 hover:border-violet-300 transition-colors"
            title="Importar lancamentos via planilha Excel"
            data-testid="fin-import-open-btn"
          >
            <FileUp size={12} />
            <span>Importar Planilha</span>
          </button>
        }
        toolbarLeft={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => openInsert('receivable')} className="btn-pill-success" data-testid="add-receivable-btn"><Plus size={13} />Inserir A Receber</button>
            <button onClick={() => openInsert('payable')} className="btn-pill-danger" data-testid="add-payable-btn"><Plus size={13} />Inserir A Pagar</button>
            <button onClick={exportCSV} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-emerald-200 text-emerald-700 rounded-lg text-xs font-semibold hover:bg-emerald-50" data-testid="fin-export-csv"><Download size={13} />Exportar Excel</button>
            {/* iter156bm: botao Desfazer — aparece 30s depois de cancelar um lancamento */}
            {lastCancelledId && (
              <button
                onClick={undoDelete}
                className="flex items-center gap-1.5 px-3 py-2 bg-white border border-violet-300 text-violet-700 rounded-lg text-xs font-black hover:bg-violet-50 shadow-sm animate-pulse"
                data-testid="fin-undo-delete"
                title="Desfazer o ultimo cancelamento (30s)"
              >
                <RotateCcw size={13} />Desfazer
              </button>
            )}
          </div>
        }
      />
      {/* iter77ct.92: modal disfarcada de importacao de lancamentos financeiros */}
      {importOpen && (
        <FinanceImportModal
          companyId={companyId}
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); fetchEntries(); }}
        />
      )}
      {/* iter77bp: visualizar lancamento */}
      {viewing && <FinancialViewModal entry={viewing} onClose={() => setViewing(null)} onSettle={() => { const v = viewing; setViewing(null); openSettle([v]); }} />}
      {/* iter156bq: modal de edicao rapida */}
      {editingEntry && (
        <FinancialEditModal
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSaved={() => { setEditingEntry(null); fetchEntries(); }}
        />
      )}
      {/* iter77ct.101: modal de baixa avancada (single ou bulk) */}
      {settleEntries && (
        <SettleFinancialModal
          entries={settleEntries}
          paymentMethods={paymentMethods}
          bankAccounts={bankAccounts}
          onClose={() => setSettleEntries(null)}
          onSaved={() => { setSettleEntries(null); fetchEntries(); }}
        />
      )}
    </div>
  );
};

/* ===== iter77ct.101: SettleFinancialModal — Baixa avancada de titulos =====
   Suporta:
    - 1 ou varios titulos numa mesma baixa (multi-selecao)
    - Uma ou varias formas de pagamento (split) com conta banco por forma
    - Juros/Multa/Desconto por titulo (ajusta o valor efetivo)
    - Data da baixa editavel (default hoje)
    - Baixa parcial: se pago < efetivo, backend gera saldo remanescente automatico
*/
const SettleFinancialModal = ({ entries, paymentMethods, bankAccounts, onClose, onSaved }) => {
  const todayStr = localDateISO();
  // Parser tolerante pt-BR (aceita "0,50", "1.234,56", "12", 12)
  const _p = (s) => {
    if (s == null || s === '') return 0;
    if (typeof s === 'number') return isNaN(s) ? 0 : s;
    const str = String(s).trim().replace(/[R$\s]/g, '');
    if (str.includes(',')) {
      const clean = str.replace(/\./g, '').replace(',', '.');
      const n = parseFloat(clean);
      return isNaN(n) ? 0 : n;
    }
    const n = parseFloat(str);
    return isNaN(n) ? 0 : n;
  };
  const fmtBRL = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const [paidDate, setPaidDate] = useState(todayStr);
  const [saving, setSaving] = useState(false);

  // Rows: um por titulo, com campos editaveis de ajuste
  const [rows, setRows] = useState(() => (entries || []).map(e => ({
    entry_id: e.id,
    description: e.description || e.customer_name || e.party_name || '-',
    ref: e.sale_number || (e.id ? parseInt(e.id.slice(-6), 16) : ''),
    type: e.type,
    amount: Number(e.amount || 0),
    orig_payment_method: e.payment_method || '',
    orig_bank_account_id: e.bank_account_id || '',
    discount: e.settle_discount > 0 ? Number(e.settle_discount).toFixed(2).replace('.', ',') : '',
    interest: e.settle_interest > 0 ? Number(e.settle_interest).toFixed(2).replace('.', ',') : '',
    fine: e.settle_fine > 0 ? Number(e.settle_fine).toFixed(2).replace('.', ',') : '',
    received_amount: Number(e.received_amount || 0),  // iter156et: ja recebido em baixas parciais
    // iter156fz: valor a pagar considera ajustes pre-gravados na edicao do titulo
    paid_amount: (Math.max(0, Number(e.amount || 0) - Number(e.settle_discount || 0) + Number(e.settle_interest || 0) + Number(e.settle_fine || 0) - Number(e.received_amount || 0))).toFixed(2).replace('.', ','),
  })));

  // Formas de pagamento: pre-preenche com a forma/conta do 1o titulo
  const first = entries?.[0] || {};
  const totalOriginal = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const [payments, setPayments] = useState(() => ([{
    payment_method: first.payment_method || '',
    bank_account_id: first.bank_account_id || '',
    amount: totalOriginal.toFixed(2).replace('.', ','),
    // iter156dt: campos de cheque por forma de pagamento
    cheque_number: '',
    cheque_bank: '',
    cheque_holder: '',
  }]));

  // iter156dt: options memoizadas para os SearchableSelects (evita re-render de +100 opcoes)
  const _pmOptions = React.useMemo(() => (
    [{ value: '', label: '— Selecione —' }].concat(
      (paymentMethods || []).map(pm => ({ value: pm.name, label: pm.name, subtitle: pm.type || '', keywords: `${pm.code || ''} ${pm.type || ''}` }))
    )
  ), [paymentMethods]);
  const _bankOptions = React.useMemo(() => (
    [{ value: '', label: '— Selecione —' }].concat(
      (bankAccounts || []).map(b => ({
        value: b.id,
        label: b.name || b.description || b.bank_name || 'Conta',
        subtitle: `${b.bank_name || ''}${b.agency ? ' · ' + b.agency : ''}${b.account_number ? '/' + b.account_number : ''}`.trim(),
        keywords: `${b.name || ''} ${b.bank_name || ''} ${b.agency || ''} ${b.account_number || ''}`,
      }))
    )
  ), [bankAccounts]);
  // iter156dt: detecta cheque por forma de pagamento (usa name — schema do payload)
  const _isChequePM = React.useCallback((pmName) => {
    if (!pmName) return false;
    const found = (paymentMethods || []).find(m => m.name === pmName);
    return ((found?.type || '').toLowerCase() === 'cheque') || (String(pmName).toLowerCase().includes('cheque'));
  }, [paymentMethods]);

  // Recalculo do efetivo por linha e totais
  const effectivePerRow = (r) => {
    const eff = Number(r.amount || 0) + _p(r.interest) + _p(r.fine) - _p(r.discount) - Number(r.received_amount || 0);
    return Math.round(eff * 100) / 100;
  };
  const paidPerRow = (r) => Math.round(_p(r.paid_amount) * 100) / 100;
  const totalEffective = rows.reduce((s, r) => s + effectivePerRow(r), 0);
  const totalPaid = rows.reduce((s, r) => s + paidPerRow(r), 0);
  const totalPayments = payments.reduce((s, p) => s + _p(p.amount), 0);
  const diff = Math.round((totalPaid - totalPayments) * 100) / 100;

  // Se so ha 1 forma de pagamento, mantem seu valor sincronizado com o total pago
  useEffect(() => {
    if (payments.length === 1) {
      setPayments(prev => [{ ...prev[0], amount: totalPaid.toFixed(2).replace('.', ',') }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPaid]);

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };
  // Ao alterar desconto/juros/multa, atualiza automaticamente o valor a pagar do titulo
  // para o efetivo (baixa total). Usuario pode override depois digitando outro valor.
  const updateRowAdjust = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r, [field]: value };
      const eff = Number(next.amount || 0) + _p(next.interest) + _p(next.fine) - _p(next.discount) - Number(next.received_amount || 0);
      next.paid_amount = (Math.max(0, eff)).toFixed(2).replace('.', ',');
      return next;
    }));
  };

  const addPayment = () => setPayments(prev => [...prev, { payment_method: '', bank_account_id: '', amount: '0,00', cheque_number: '', cheque_bank: '', cheque_holder: '' }]);
  const removePayment = (idx) => setPayments(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  const updatePayment = (idx, field, value) => setPayments(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  // iter156ep: BAIXA PARCIAL pelo valor da forma — se ha 1 titulo e a soma das formas ficou MENOR
  // que o "Valor a Pagar", ajusta o valor a pagar do titulo para a soma (o restante fica em aberto).
  // Antes o botao "Confirmar" ficava travado (diferenca) e o usuario nao sabia que precisava
  // editar a coluna "Valor a Pagar".
  const syncPartialFromPayments = () => {
    if (rows.length !== 1) return;
    const sum = Math.round(payments.reduce((s, p) => s + _p(p.amount), 0) * 100) / 100;
    const eff = effectivePerRow(rows[0]);
    if (sum > 0 && sum < eff && Math.abs(sum - paidPerRow(rows[0])) > 0.01) {
      setRows(prev => prev.map((r, i) => i === 0 ? { ...r, paid_amount: sum.toFixed(2).replace('.', ',') } : r));
      toast.info(`Baixa parcial: ${fmtBRL(sum)} agora e ${fmtBRL(eff - sum)} continua em aberto.`, { duration: 5000 });
    }
  };

  const handleSubmit = async () => {
    // Validacoes de UI
    if (!paidDate) { toast.error('Informe a data do recebimento/pagamento.'); return; }
    if (rows.some(r => paidPerRow(r) < 0)) { toast.error('Valor pago nao pode ser negativo.'); return; }
    for (const p of payments) {
      if (!p.payment_method) { toast.error('Selecione a forma de pagamento em todas as linhas.'); return; }
      if (!p.bank_account_id) { toast.error('Selecione a conta banco em todas as linhas.'); return; }
      if (_p(p.amount) <= 0) { toast.error('Valor da forma de pagamento deve ser maior que zero.'); return; }
      // iter156dt: se cheque, exige numero do cheque
      if (_isChequePM(p.payment_method) && !String(p.cheque_number || '').trim()) {
        toast.error('Informe o Numero do Cheque para a forma "' + p.payment_method + '".');
        return;
      }
    }
    if (Math.abs(diff) > 0.01) {
      if (rows.length === 1 && totalPayments > 0 && totalPayments < effectivePerRow(rows[0])) {
        syncPartialFromPayments();
        toast.info('Valor a pagar ajustado para a soma das formas. Clique em Confirmar novamente para concluir a baixa parcial.', { duration: 6000 });
        return;
      }
      toast.error(`A soma das formas (${fmtBRL(totalPayments)}) precisa igualar o total pago (${fmtBRL(totalPaid)}). Diferenca: ${fmtBRL(diff)}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        paid_date: paidDate,
        entries: rows.map(r => ({
          entry_id: r.entry_id,
          discount: _p(r.discount),
          interest: _p(r.interest),
          fine: _p(r.fine),
          paid_amount: paidPerRow(r),
        })),
        payments: payments.map(p => ({
          payment_method: p.payment_method,
          bank_account_id: p.bank_account_id,
          amount: _p(p.amount),
          // iter156dt: dados de cheque (opcionais)
          cheque_number: _isChequePM(p.payment_method) ? String(p.cheque_number || '').trim() : '',
          cheque_bank: _isChequePM(p.payment_method) ? String(p.cheque_bank || '').trim() : '',
          cheque_holder: _isChequePM(p.payment_method) ? String(p.cheque_holder || '').trim() : '',
        })),
      };
      const { data } = await axios.post(`${ERP}/financial/settle`, payload, { withCredentials: true });
      const remainderMsg = data?.partials?.length ? ` · baixa PARCIAL: ${data.partials.length} titulo(s) continua(m) em aberto` : '';
      toast.success(`Baixa realizada: ${data?.settled_count || rows.length} titulo(s) · ${fmtBRL(data?.total || totalPaid)}${remainderMsg}`);
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao realizar baixa');
    } finally {
      setSaving(false);
    }
  };

  const isSingle = rows.length === 1;
  const isReceivable = rows.some(r => r.type === 'receivable');
  const isPayable = rows.some(r => r.type === 'payable');
  const titleLabel = isReceivable && isPayable ? 'Baixa de Titulos'
    : isReceivable ? 'Recebimento de Titulos'
    : 'Pagamento de Titulos';

  return (
    <div className="fixed inset-0 z-[180] bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden" data-testid="settle-modal">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-violet-600 to-cyan-500 text-white">
          <div>
            <p className="text-[10px] font-bold uppercase opacity-80">{titleLabel}</p>
            <h3 className="text-lg font-black">{rows.length} titulo(s) · Total original {fmtBRL(totalOriginal)}</h3>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="settle-close"><X size={20} /></button>
        </div>

        {/* Body scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Data */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-black text-slate-500 uppercase mb-1">Data do Recebimento/Pagamento *</label>
              <input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} className={inputClass + ' text-sm'} data-testid="settle-paid-date" />
            </div>
            <div className="sm:col-span-2 flex items-end">
              <div className="w-full grid grid-cols-3 gap-2 text-center">
                <div className="bg-slate-50 border border-slate-200 rounded-lg py-2 px-3">
                  <p className="text-[10px] font-bold text-slate-500 uppercase">Original</p>
                  <p className="text-sm font-black text-slate-700">{fmtBRL(totalOriginal)}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg py-2 px-3">
                  <p className="text-[10px] font-bold text-amber-600 uppercase">Efetivo (c/ Ajustes)</p>
                  <p className="text-sm font-black text-amber-700">{fmtBRL(totalEffective)}</p>
                </div>
                <div className={`${totalPaid >= totalEffective ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'} border rounded-lg py-2 px-3`}>
                  <p className={`text-[10px] font-bold uppercase ${totalPaid >= totalEffective ? 'text-emerald-600' : 'text-blue-600'}`}>A Baixar</p>
                  <p className={`text-sm font-black ${totalPaid >= totalEffective ? 'text-emerald-700' : 'text-blue-700'}`}>{fmtBRL(totalPaid)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Titulos */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] font-black text-slate-500 uppercase">Titulos a Baixar</label>
              <span className="text-[10px] text-slate-400">Ajuste Desconto/Juros/Multa por titulo. O valor a pagar recalcula automaticamente.</span>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-bold">Titulo</th>
                      <th className="text-right px-2 py-1.5 font-bold">V.Original</th>
                      <th className="text-right px-2 py-1.5 font-bold">Desconto</th>
                      <th className="text-right px-2 py-1.5 font-bold">Juros</th>
                      <th className="text-right px-2 py-1.5 font-bold">Multa</th>
                      <th className="text-right px-2 py-1.5 font-bold">Efetivo</th>
                      <th className="text-right px-2 py-1.5 font-bold">Valor a Pagar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((r, idx) => {
                      const eff = effectivePerRow(r);
                      const paid = paidPerRow(r);
                      const saldo = Math.round((eff - paid) * 100) / 100;
                      return (
                        <tr key={r.entry_id} data-testid={`settle-row-${r.entry_id}`}>
                          <td className="px-2 py-1.5">
                            <div className="text-slate-900 font-medium truncate max-w-[220px]" title={r.description}>{r.description}</div>
                            <div className="text-[10px] text-slate-500">
                              <span className={`inline-block px-1 rounded ${r.type === 'receivable' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'} font-black mr-1`}>{r.type === 'receivable' ? 'RECEBER' : 'PAGAR'}</span>
                              #{r.ref}
                              {saldo > 0.01 && <span className="ml-2 text-blue-700 font-bold">· Saldo: {fmtBRL(saldo)} (baixa parcial — o titulo continua em aberto)</span>}
                              {Number(r.received_amount) > 0 && <span className="ml-2 text-emerald-700 font-bold" data-testid={`settle-received-${r.entry_id}`}>· Ja recebido: {fmtBRL(r.received_amount)}</span>}
                              {saldo < -0.01 && <span className="ml-2 text-amber-700 font-bold">· Excesso: {fmtBRL(-saldo)}</span>}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-slate-700">{fmtBRL(r.amount)}</td>
                          <td className="px-2 py-1.5 text-right">
                            <input type="text" inputMode="decimal" value={r.discount} onChange={e => updateRowAdjust(idx, 'discount', e.target.value)} placeholder="0,00" className="w-20 px-1.5 py-1 border border-slate-300 rounded text-right font-mono" data-testid={`settle-discount-${r.entry_id}`} />
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <input type="text" inputMode="decimal" value={r.interest} onChange={e => updateRowAdjust(idx, 'interest', e.target.value)} placeholder="0,00" className="w-20 px-1.5 py-1 border border-slate-300 rounded text-right font-mono" data-testid={`settle-interest-${r.entry_id}`} />
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <input type="text" inputMode="decimal" value={r.fine} onChange={e => updateRowAdjust(idx, 'fine', e.target.value)} placeholder="0,00" className="w-20 px-1.5 py-1 border border-slate-300 rounded text-right font-mono" data-testid={`settle-fine-${r.entry_id}`} />
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-amber-700 font-bold">{fmtBRL(eff)}</td>
                          <td className="px-2 py-1.5 text-right">
                            <input type="text" inputMode="decimal" value={r.paid_amount} onChange={e => updateRow(idx, 'paid_amount', e.target.value)} className="w-24 px-1.5 py-1 border border-emerald-300 bg-emerald-50 rounded text-right font-mono font-bold text-emerald-800" data-testid={`settle-paid-${r.entry_id}`} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="mt-1 text-[10px] text-slate-500">Dica: para BAIXA PARCIAL, digite o valor recebido em "Valor a Pagar" (ou no valor da forma de pagamento) — o restante continua em aberto no mesmo titulo.</p>
            {/* iter156fy: historico de baixas anteriores (baixa parcial de 1 titulo) */}
            {rows.length === 1 && Number(rows[0].received_amount) > 0 && (
              <details className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2" open data-testid="settle-history-block">
                <summary className="cursor-pointer text-[11px] font-black text-emerald-800 uppercase flex items-center gap-1.5"><Clock size={12} /> Baixas anteriores deste titulo · ja recebido {fmtBRL(rows[0].received_amount)}</summary>
                <div className="mt-2"><PaymentHistoryList entryId={rows[0].entry_id} compact /></div>
              </details>
            )}
          </div>

          {/* Formas de pagamento */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] font-black text-slate-500 uppercase">Formas de Pagamento</label>
              <button type="button" onClick={addPayment} className="text-[11px] font-bold px-2 py-1 rounded bg-violet-50 text-violet-700 hover:bg-violet-100 flex items-center gap-1" data-testid="settle-add-payment">
                <Plus size={11} /> Adicionar forma
              </button>
            </div>
            <div className="space-y-2">
              {payments.map((p, idx) => {
                const _showCheque = _isChequePM(p.payment_method);
                return (
                <div key={idx} className="p-2 bg-slate-50 border border-slate-200 rounded-lg space-y-2" data-testid={`settle-payment-${idx}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase mb-0.5">Forma de Pagamento *</label>
                      {/* iter156dt: Combobox no lugar do <select> longo */}
                      <Combobox
                        value={p.payment_method}
                        options={_pmOptions}
                        onChange={(v) => updatePayment(idx, 'payment_method', v)}
                        placeholder="Selecione..."
                        testId={`settle-payment-method-${idx}`}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase mb-0.5">Conta Banco *</label>
                      <Combobox
                        value={p.bank_account_id}
                        options={_bankOptions}
                        onChange={(v) => updatePayment(idx, 'bank_account_id', v)}
                        placeholder="Selecione..."
                        testId={`settle-bank-${idx}`}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-500 uppercase mb-0.5">Valor *</label>
                      <input type="text" inputMode="decimal" value={p.amount} onChange={e => updatePayment(idx, 'amount', e.target.value)} onBlur={syncPartialFromPayments} className="w-28 px-2 py-2 border-2 border-slate-300 rounded-lg text-sm text-right font-mono font-bold" data-testid={`settle-amount-${idx}`} />
                    </div>
                    <button type="button" onClick={() => removePayment(idx)} disabled={payments.length <= 1} className="p-2 text-rose-500 hover:bg-rose-50 rounded disabled:opacity-30" data-testid={`settle-remove-payment-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {/* iter156dt: campos de cheque — aparecem quando forma = cheque */}
                  {_showCheque && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-2 bg-amber-50 border border-amber-200 rounded" data-testid={`settle-cheque-${idx}`}>
                      <div>
                        <label className="block text-[9px] font-black text-amber-700 uppercase mb-0.5">* Numero Cheque</label>
                        <input value={p.cheque_number || ''} onChange={e => updatePayment(idx, 'cheque_number', e.target.value)} placeholder="000123" className="w-full px-2 py-1 border border-amber-300 rounded text-xs" data-testid={`settle-cheque-number-${idx}`} />
                      </div>
                      <div>
                        <label className="block text-[9px] font-black text-amber-700 uppercase mb-0.5">Banco Emissor</label>
                        <input value={p.cheque_bank || ''} onChange={e => updatePayment(idx, 'cheque_bank', e.target.value)} placeholder="001 · BB" className="w-full px-2 py-1 border border-amber-300 rounded text-xs" data-testid={`settle-cheque-bank-${idx}`} />
                      </div>
                      <div>
                        <label className="block text-[9px] font-black text-amber-700 uppercase mb-0.5">Titular</label>
                        <input value={p.cheque_holder || ''} onChange={e => updatePayment(idx, 'cheque_holder', e.target.value)} placeholder="Nome no cheque" className="w-full px-2 py-1 border border-amber-300 rounded text-xs" data-testid={`settle-cheque-holder-${idx}`} />
                      </div>
                    </div>
                  )}
                </div>
              );})}
            </div>
            {/* Sumario */}
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-xs">
              <div>
                <span className="text-slate-500">Soma das formas: </span>
                <span className="font-bold font-mono">{fmtBRL(totalPayments)}</span>
              </div>
              <div>
                <span className="text-slate-500">Total a baixar: </span>
                <span className="font-bold font-mono">{fmtBRL(totalPaid)}</span>
              </div>
              <div className={Math.abs(diff) < 0.01 ? 'text-emerald-700' : 'text-rose-700'}>
                <span>Diferenca: </span>
                <span className="font-bold font-mono" data-testid="settle-diff">{fmtBRL(diff)}</span>
                {Math.abs(diff) < 0.01 ? ' ✓' : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold text-slate-500 hover:text-slate-700" data-testid="settle-cancel">Cancelar</button>
          <button onClick={handleSubmit} disabled={saving || (Math.abs(diff) > 0.01 && !(rows.length === 1 && totalPayments > 0 && totalPayments < effectivePerRow(rows[0])))} className="btn-pill-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="settle-confirm">
            {saving ? 'Baixando...' : <><Check size={14} /><span>Confirmar Baixa</span></>}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===== iter156fy: PaymentHistoryList — historico de baixas (parciais/total) de um titulo ===== */
const PaymentHistoryList = ({ entryId, compact = false, onLoaded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${ERP}/financial/${entryId}/payments`, { withCredentials: true })
      .then(r => { if (alive) { setData(r.data); onLoaded?.(r.data); } })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [entryId]); // eslint-disable-line react-hooks/exhaustive-deps
  const fmtDT = (iso) => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };
  if (loading) return <p className="text-xs text-slate-400 py-2" data-testid="payment-history-loading">Carregando historico...</p>;
  const pays = data?.payments || [];
  const revs = data?.reversals || [];
  if (!pays.length && !revs.length) return <p className="text-xs text-slate-400 py-2 italic" data-testid="payment-history-empty">Nenhuma baixa registrada ate o momento.</p>;
  const timeline = [...pays.map(p => ({ ...p, _t: p.created_at || p.date })), ...revs.map(r => ({ ...r, _t: r.created_at }))]
    .sort((a, b) => String(a._t || '').localeCompare(String(b._t || '')));
  return (
    <ol className={`relative border-l-2 border-violet-100 ${compact ? 'ml-2 space-y-2' : 'ml-3 space-y-3'}`} data-testid="payment-history-list">
      {timeline.map((it, i) => it.kind === 'reversal' ? (
        <li key={`r${i}`} className="ml-4 relative" data-testid="payment-history-reversal">
          <span className="absolute -left-[23px] top-1 w-3.5 h-3.5 rounded-full bg-rose-500 ring-4 ring-white" />
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-black text-rose-700 flex items-center gap-1"><RotateCcw size={11} /> Estorno da baixa</p>
              <p className="text-xs font-mono font-bold text-rose-700">− {fmtBRL(it.amount)}</p>
            </div>
            <p className="text-[10px] text-rose-600 mt-0.5">{fmtDT(it.created_at)}{it.user_name ? ` · por ${it.user_name}` : ''}{it.payments_reversed > 1 ? ` · ${it.payments_reversed} baixas revertidas` : ''}</p>
          </div>
        </li>
      ) : (
        <li key={`p${i}`} className="ml-4 relative" data-testid="payment-history-item">
          <span className="absolute -left-[23px] top-1 w-3.5 h-3.5 rounded-full bg-emerald-500 ring-4 ring-white" />
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-bold text-slate-900">{fmtDateBR(it.date)} <span className="text-slate-400 font-normal">·</span> <span className="text-slate-600 font-medium">{it.payment_method || '—'}</span>{it.bank_account_name ? <span className="text-slate-400 font-normal"> · {it.bank_account_name}</span> : null}</p>
              <p className="text-sm font-mono font-black text-emerald-700" data-testid="payment-history-amount">{fmtBRL(it.amount)}</p>
            </div>
            <p className="text-[10px] text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
              <span className="inline-flex items-center gap-1"><UserCheck size={10} /> {it.user_name || 'usuario nao identificado'}</span>
              {it.created_at && <span>registrado em {fmtDT(it.created_at)}</span>}
              {it.origin_label && <span className="text-violet-600 font-bold">{it.origin_label}</span>}
              {(Number(it.discount) > 0 || Number(it.interest) > 0 || Number(it.fine) > 0) && (
                <span>{Number(it.discount) > 0 ? `desc. ${fmtBRL(it.discount)} ` : ''}{Number(it.interest) > 0 ? `juros ${fmtBRL(it.interest)} ` : ''}{Number(it.fine) > 0 ? `multa ${fmtBRL(it.fine)}` : ''}</span>
              )}
              {it.remaining !== undefined && it.remaining !== null && (Number(it.remaining) > 0.009 ? <span className="text-blue-700 font-bold">saldo apos: {fmtBRL(it.remaining)}</span> : <span className="text-emerald-700 font-bold">quitado</span>)}
            </p>
            {it.payments?.length > 1 && (
              <ul className="mt-1 text-[10px] text-slate-600 space-y-0.5">
                {it.payments.map((p, j) => <li key={j}>↳ {p.payment_method}{p.bank_account_name ? ` · ${p.bank_account_name}` : ''}{p.cheque_number ? ` · CH ${p.cheque_number}` : ''}: <span className="font-mono font-bold">{fmtBRL(p.amount)}</span></li>)}
              </ul>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};

/* ===== iter77bp/iter156fy: FinancialViewModal — visualizacao (somente leitura) + historico de baixas ===== */
const FinancialViewModal = ({ entry, onClose, onSettle }) => {
  const codigo = entry.sale_number || (entry.id ? parseInt(entry.id.slice(-6), 16) : '—');
  const [hist, setHist] = useState(null);
  const isRecv = entry.type === 'receivable';
  const amount = Number(entry.amount || 0);
  const received = hist ? Number(hist.summary.received || 0) : Number(entry.received_amount || (entry.status === 'paid' ? entry.amount : 0) || 0);
  const effective = hist ? Number(hist.summary.effective || amount) : amount;
  const remaining = Math.max(0, Math.round((effective - received) * 100) / 100);
  const pct = effective > 0 ? Math.min(100, Math.round((received / effective) * 100)) : 0;
  const statusCfg = entry.status === 'paid' ? { label: 'QUITADO', cls: 'bg-emerald-400/20 text-emerald-100 border-emerald-300/40' }
    : entry.status === 'cancelled' ? { label: 'CANCELADO', cls: 'bg-slate-400/20 text-slate-100 border-slate-300/40' }
    : received > 0 ? { label: 'PARCIAL', cls: 'bg-blue-400/20 text-blue-100 border-blue-300/40' }
    : { label: 'EM ABERTO', cls: 'bg-amber-400/20 text-amber-100 border-amber-300/40' };
  const Field = ({ label, children, mono }) => (
    <div>
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{label}</p>
      <p className={`text-sm text-slate-800 ${mono ? 'font-mono' : 'font-medium'}`}>{children}</p>
    </div>
  );
  return (
    <div className="fixed inset-0 z-[180] bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col overflow-hidden" data-testid="financial-view-modal">
        <div className={`px-5 py-4 text-white bg-gradient-to-r ${isRecv ? 'from-violet-600 to-cyan-500' : 'from-rose-600 to-orange-500'}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase opacity-80">{isRecv ? 'Conta a Receber' : 'Conta a Pagar'} · Lancamento #{codigo}</p>
              <h3 className="text-lg font-black truncate"><PartyTag r={entry} />{entry.party_name || entry.customer_name || entry.description || '—'}</h3>
              {(entry.party_name || entry.customer_name) && entry.description && <p className="text-xs opacity-85 truncate">{entry.description}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`px-2 py-0.5 rounded-full border text-[10px] font-black ${statusCfg.cls}`} data-testid="financial-view-status">{statusCfg.label}</span>
              <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="financial-view-close"><X size={20} /></button>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-white/15 backdrop-blur px-3 py-2">
              <p className="text-[10px] uppercase font-bold opacity-80">Valor {effective !== amount ? 'efetivo' : 'original'}</p>
              <p className="text-base font-black tabular-nums" data-testid="financial-view-amount">{fmtBRL(effective)}</p>
              {effective !== amount && <p className="text-[10px] opacity-75">original {fmtBRL(amount)}</p>}
            </div>
            <div className="rounded-xl bg-white/15 backdrop-blur px-3 py-2">
              <p className="text-[10px] uppercase font-bold opacity-80">{isRecv ? 'Recebido' : 'Pago'}</p>
              <p className="text-base font-black tabular-nums" data-testid="financial-view-received">{fmtBRL(received)}</p>
            </div>
            <div className="rounded-xl bg-white/15 backdrop-blur px-3 py-2">
              <p className="text-[10px] uppercase font-bold opacity-80">Saldo</p>
              <p className="text-base font-black tabular-nums" data-testid="financial-view-remaining">{fmtBRL(remaining)}</p>
            </div>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-white/20 overflow-hidden"><div className="h-full bg-white/90 transition-all" style={{ width: `${pct}%` }} /></div>
          <p className="text-[10px] mt-1 opacity-80">{pct}% liquidado{entry.status === 'pending' && entry.due_date && entry.due_date < localDateISO() ? ' · VENCIDO' : ''}</p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
            <Field label="F. Pagamento">{entry.payment_method || '—'}</Field>
            <Field label="Categoria / Pl. Contas">{entry.category || '—'}</Field>
            <Field label="N. Venda" mono>{entry.sale_number || '—'}</Field>
            <Field label="Origem">{entry.origin === 'pdv_expense' ? 'Despesa no PDV' : entry.origin === 'pdv_transfer' ? 'Transf. entre contas' : entry.sale_id ? 'PDV - Venda' : 'Lancamento Manual'}</Field>
            <Field label="Dt. Documento" mono>{fmtDateBR(entry.document_date)}</Field>
            <Field label="Dt. Lancamento" mono>{fmtDateBR((entry.created_at || '').slice(0, 10))}</Field>
            <Field label="Dt. Vencimento" mono>{fmtDateBR(entry.due_date)}</Field>
            <Field label={isRecv ? 'Dt. Recebimento' : 'Dt. Pagamento'} mono>{fmtDateBR(entry.paid_date) || (entry.last_payment_date ? `${fmtDateBR(entry.last_payment_date)} (ult. parcial)` : '—')}</Field>
            {entry.notes && <div className="col-span-2 sm:col-span-4"><Field label="Historico / Notas"><span className="whitespace-pre-line text-xs">{entry.notes}</span></Field></div>}
          </div>
          {(entry.cheque_number || entry.cheque_bank || entry.cheque_holder) && (
            <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="view-cheque-block">
              <p className="text-[10px] font-black text-amber-700 uppercase mb-1.5 flex items-center gap-1.5"><span className="inline-block w-4 h-4 rounded-full bg-amber-500 text-white text-[9px] font-black flex items-center justify-center">CH</span>Dados do Cheque</p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div><p className="text-[9px] font-black text-amber-700 uppercase">N Cheque</p><p className="text-slate-800 font-mono">{entry.cheque_number || '—'}</p></div>
                <div><p className="text-[9px] font-black text-amber-700 uppercase">Banco</p><p className="text-slate-800">{entry.cheque_bank || '—'}</p></div>
                <div><p className="text-[9px] font-black text-amber-700 uppercase">Titular</p><p className="text-slate-800">{entry.cheque_holder || '—'}</p></div>
              </div>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-black text-slate-500 uppercase tracking-wide flex items-center gap-1.5"><Clock size={12} /> Historico de baixas</p>
              {hist && <span className="text-[10px] text-slate-400">{hist.payments.length} baixa(s){hist.reversals.length ? ` · ${hist.reversals.length} estorno(s)` : ''}</span>}
            </div>
            <PaymentHistoryList entryId={entry.id} onLoaded={setHist} />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-sm font-bold" data-testid="financial-view-close-btn">Fechar</button>
          {entry.status === 'pending' && onSettle && (
            <button onClick={onSettle} className="px-4 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-sm font-bold flex items-center gap-1.5" data-testid="financial-view-settle-btn"><Check size={14} /> {remaining > 0 && received > 0 ? `Baixar saldo ${fmtBRL(remaining)}` : 'Baixar'}</button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ==== iter156bq: FinancialEditModal — edicao rapida de lancamento financeiro ==== */
const FinancialEditModal = ({ entry, onClose, onSaved }) => {
  // iter156fz: mesma linguagem visual da tela de BAIXA — permite ajustar BRUTO (original) e
  // LIQUIDO (efetivo) via desconto / juros / multa-taxa ANTES de baixar.
  const isPayable = entry?.type === 'payable';
  const codigo = entry?.sale_number || (entry?.id ? parseInt(entry.id.slice(-6), 16) : '—');
  const toStr = (n) => (Number(n) > 0 ? Number(n).toFixed(2).replace('.', ',') : '');
  const toNum = (v) => { const n = parseFloat(String(v ?? '').replace(/\./g, '').replace(',', '.')); return isNaN(n) ? 0 : n; };
  const [form, setForm] = useState({
    description: entry?.description || '',
    amount: entry?.amount || 0,
    fee_percent: Number(entry?.fee_percent) > 0 ? Number(entry.fee_percent).toFixed(2).replace('.', ',') : '',
    fee_amount: toStr(entry?.fee_amount),
    discount: toStr(entry?.settle_discount),
    interest: toStr(entry?.settle_interest),
    fine: toStr(entry?.settle_fine),
    due_date: entry?.due_date || '',
    document_date: entry?.document_date || '',
    category: entry?.category || '',
    payment_method: entry?.payment_method || '',
    bank_account_id: entry?.bank_account_id || '',
    notes: entry?.notes || '',
    cheque_number: entry?.cheque_number || '',
    cheque_bank: entry?.cheque_bank || '',
    cheque_holder: entry?.cheque_holder || '',
  });
  const [saving, setSaving] = useState(false);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  useEffect(() => {
    const q = entry?.company_id ? `?company_id=${entry.company_id}` : '';
    Promise.all([
      axios.get(`${ERP}/chart-accounts`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
      axios.get(`${ERP}/payment-methods${q}`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
      axios.get(`${ERP}/bank-accounts${q}`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
    ]).then(([ca, pm, ba]) => {
      setChartAccounts(ca.data.data || []);
      setPaymentMethods(pm.data.data || pm.data || []);
      setBankAccounts(ba.data.data || ba.data || []);
    });
  }, [entry?.company_id]);
  const _editIsCheque = React.useMemo(() => {
    const pmVal = form.payment_method || '';
    if (!pmVal) return false;
    if (pmVal.toLowerCase().includes('cheque')) return true;
    const found = paymentMethods.find(m => (m.type || m.name) === pmVal || m.name === pmVal);
    return ((found?.type || '').toLowerCase() === 'cheque');
  }, [form.payment_method, paymentMethods]);

  const bruto = Number(form.amount || 0);
  const taxa = toNum(form.fee_amount);
  const taxaPct = toNum(form.fee_percent);
  const liquido = Math.max(0, Math.round((bruto - taxa) * 100) / 100); // liquido = bruto - taxa (forma de pagamento)
  const desc = toNum(form.discount), juros = toNum(form.interest), multa = toNum(form.fine);
  const efetivo = Math.max(0, Math.round((bruto - desc + juros + multa) * 100) / 100); // valor a baixar (c/ ajustes)
  const received = Number(entry?.received_amount || 0);
  const saldo = Math.max(0, Math.round((efetivo - received) * 100) / 100);
  const isPaid = entry?.status === 'paid';
  const fmt2 = (n) => (Number(n) > 0 ? Number(n).toFixed(2).replace('.', ',') : '');
  const setFeePct = (v) => { const pct = toNum(v); setForm(f => ({ ...f, fee_percent: v, fee_amount: fmt2(Math.round(bruto * pct) / 100) })); };
  const setFeeAmt = (v) => { const amt = toNum(v); setForm(f => ({ ...f, fee_amount: v, fee_percent: bruto > 0 ? fmt2(Math.round((amt / bruto) * 10000) / 100) : '' })); };
  const setBruto = (v) => { const b = Number(v || 0); setForm(f => ({ ...f, amount: v, fee_amount: taxaPct > 0 ? fmt2(Math.round(b * taxaPct) / 100) : f.fee_amount })); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error('Descricao obrigatoria'); return; }
    if (bruto <= 0) { toast.error('Valor bruto deve ser maior que zero'); return; }
    if (taxa >= bruto) { toast.error('Taxa nao pode ser maior ou igual ao valor bruto'); return; }
    if (efetivo <= 0) { toast.error('Valor a baixar nao pode ser zero — reveja o desconto'); return; }
    if (received > 0 && efetivo < received) { toast.error(`Valor a baixar (${fmtBRL(efetivo)}) nao pode ser menor que o ja ${isPayable ? 'pago' : 'recebido'} (${fmtBRL(received)})`); return; }
    setSaving(true);
    try {
      await axios.put(`${ERP}/financial/${entry.id}`, {
        description: form.description.trim(),
        amount: bruto,
        fee_percent: taxaPct, fee_amount: taxa, net_amount: liquido, gross_amount: bruto,
        settle_discount: desc, settle_interest: juros, settle_fine: multa,
        due_date: form.due_date || null,
        document_date: form.document_date || null,
        category: form.category || null,
        payment_method: form.payment_method || null,
        bank_account_id: form.bank_account_id || null,
        notes: form.notes || '',
        cheque_number: _editIsCheque ? (form.cheque_number || '') : '',
        cheque_bank: _editIsCheque ? (form.cheque_bank || '') : '',
        cheque_holder: _editIsCheque ? (form.cheque_holder || '') : '',
      }, { withCredentials: true });
      toast.success('Lancamento atualizado');
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSaving(false);
    }
  };

  const Label = ({ children }) => <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wide mb-1">{children}</label>;
  const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-violet-400 focus:outline-none bg-white';
  const adjInput = (key, testId, placeholder = '0,00') => (
    <input value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} inputMode="decimal" placeholder={placeholder} disabled={isPaid}
      className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm text-right font-mono focus:border-violet-400 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400" data-testid={testId} />
  );

  return (
    <div className="fixed inset-0 z-[180] bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" data-testid="financial-edit-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col overflow-hidden">
        <div className={`px-5 py-4 text-white bg-gradient-to-r ${isPayable ? 'from-rose-600 to-orange-500' : 'from-violet-600 to-cyan-500'}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase opacity-80 flex items-center gap-1.5"><Edit size={11} /> Edicao de Titulo · {isPayable ? 'A Pagar' : 'A Receber'} · #{codigo}</p>
              <h3 className="text-lg font-black truncate"><PartyTag r={entry} />{entry?.party_name || entry?.customer_name || entry?.description || '—'}</h3>
            </div>
            <button type="button" onClick={onClose} className="text-white/80 hover:text-white shrink-0" data-testid="fin-edit-close"><X size={20} /></button>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-white/15 backdrop-blur px-3 py-2">
              <p className="text-[10px] uppercase font-bold opacity-80">Bruto (original)</p>
              <p className="text-base font-black tabular-nums" data-testid="fin-edit-card-bruto">{fmtBRL(bruto)}</p>
            </div>
            <div className="rounded-xl bg-white/15 backdrop-blur px-3 py-2">
              <p className="text-[10px] uppercase font-bold opacity-80">Liquido (bruto − taxa)</p>
              <p className="text-base font-black tabular-nums" data-testid="fin-edit-card-liquido">{fmtBRL(liquido)}</p>
              {taxa > 0 && <p className="text-[10px] opacity-80">taxa {fmtBRL(taxa)}{taxaPct > 0 ? ` (${fmt2(taxaPct)}%)` : ''}</p>}
            </div>
            <div className="rounded-xl bg-white/15 backdrop-blur px-3 py-2">
              <p className="text-[10px] uppercase font-bold opacity-80">{isPaid ? 'Quitado' : received > 0 ? 'Saldo a baixar' : 'A baixar (c/ ajustes)'}</p>
              <p className="text-base font-black tabular-nums" data-testid="fin-edit-card-saldo">{fmtBRL(isPaid ? efetivo : saldo)}</p>
              {efetivo !== bruto && <p className="text-[10px] opacity-80">{efetivo > bruto ? '+' : '−'} {fmtBRL(Math.abs(efetivo - bruto))} de ajustes</p>}
              {received > 0 && !isPaid && <p className="text-[10px] opacity-80">ja {isPayable ? 'pago' : 'recebido'} {fmtBRL(received)}</p>}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <Label>Descricao</Label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className={inputCls} data-testid="fin-edit-description" autoFocus />
          </div>

          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <p className="text-[10px] font-black text-slate-600 uppercase tracking-wide">Valores do titulo</p>
              <p className="text-[10px] text-slate-400">Liquido = Bruto − Taxa (tarifa da forma de pagamento)</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3">
              <div>
                <Label>Valor bruto</Label>
                <CurrencyInput value={form.amount} onChange={setBruto} placeholder="0,00" dataTestId="fin-edit-amount" />
              </div>
              <div>
                <Label>Taxa %</Label>
                <input value={form.fee_percent} onChange={e => setFeePct(e.target.value)} inputMode="decimal" placeholder="0,00" className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm text-right font-mono focus:border-violet-400 focus:outline-none" data-testid="fin-edit-fee-percent" />
              </div>
              <div>
                <Label>Taxa R$</Label>
                <input value={form.fee_amount} onChange={e => setFeeAmt(e.target.value)} inputMode="decimal" placeholder="0,00" className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm text-right font-mono focus:border-violet-400 focus:outline-none" data-testid="fin-edit-fee-amount" />
              </div>
              <div>
                <Label>Liquido</Label>
                <div className={`w-full px-2 py-2 rounded-lg text-sm text-right font-mono font-black border ${taxa > 0 ? 'bg-orange-50 border-orange-200 text-orange-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`} data-testid="fin-edit-liquido">{fmtBRL(liquido)}</div>
              </div>
            </div>
            <div className="px-3 py-2 bg-slate-50 border-y border-slate-200 flex items-center justify-between">
              <p className="text-[10px] font-black text-slate-600 uppercase tracking-wide">Ajustes para a baixa <span className="font-normal normal-case text-slate-400">(opcional)</span></p>
              <p className="text-[10px] text-slate-400">{isPaid ? 'Titulo quitado — ajustes bloqueados' : 'A baixar = Bruto − Desconto + Juros + Multa'}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3">
              <div><Label>Desconto</Label>{adjInput('discount', 'fin-edit-discount')}</div>
              <div><Label>Juros</Label>{adjInput('interest', 'fin-edit-interest')}</div>
              <div><Label>Multa</Label>{adjInput('fine', 'fin-edit-fine')}</div>
              <div>
                <Label>A baixar</Label>
                <div className={`w-full px-2 py-2 rounded-lg text-sm text-right font-mono font-black border ${efetivo !== bruto ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-slate-50 border-slate-200 text-slate-700'}`} data-testid="fin-edit-efetivo">{fmtBRL(efetivo)}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label>Vencimento</Label>
              <input type="date" value={form.due_date ? form.due_date.slice(0, 10) : ''} onChange={e => setForm({ ...form, due_date: e.target.value })} className={inputCls} data-testid="fin-edit-due-date" />
            </div>
            <div>
              <Label>Dt. Documento</Label>
              <input type="date" value={form.document_date ? form.document_date.slice(0, 10) : ''} onChange={e => setForm({ ...form, document_date: e.target.value })} className={inputCls} data-testid="fin-edit-document-date" />
            </div>
            <div className="col-span-2">
              <Label>Plano de Contas</Label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="fin-edit-category">
                <option value="">— Sem plano —</option>
                {chartAccounts.map(c => (
                  <option key={c.id} value={c.code || c.id}>{c.code ? `${c.code} · ` : ''}{c.name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <Label>Forma de Pagamento</Label>
              <select value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} className={inputCls} data-testid="fin-edit-payment-method">
                <option value="">— Sem forma —</option>
                {paymentMethods.map(pm => (
                  <option key={pm.id} value={pm.type || pm.name}>{pm.name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <Label>Conta Banco</Label>
              <select value={form.bank_account_id} onChange={e => setForm({ ...form, bank_account_id: e.target.value })} className={inputCls} data-testid="fin-edit-bank-account">
                <option value="">— Selecione —</option>
                {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>

          {_editIsCheque && (
            <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="fin-edit-cheque">
              <p className="text-[10px] font-black text-amber-700 uppercase mb-1.5">Dados do Cheque</p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[9px] font-black text-amber-700 uppercase mb-0.5">N Cheque</label>
                  <input value={form.cheque_number} onChange={e => setForm({ ...form, cheque_number: e.target.value })} placeholder="000123" className="w-full px-2 py-1 border border-amber-300 rounded text-xs" data-testid="fin-edit-cheque-number" />
                </div>
                <div>
                  <label className="block text-[9px] font-black text-amber-700 uppercase mb-0.5">Banco</label>
                  <input value={form.cheque_bank} onChange={e => setForm({ ...form, cheque_bank: e.target.value })} placeholder="001 · BB" className="w-full px-2 py-1 border border-amber-300 rounded text-xs" data-testid="fin-edit-cheque-bank" />
                </div>
                <div>
                  <label className="block text-[9px] font-black text-amber-700 uppercase mb-0.5">Titular</label>
                  <input value={form.cheque_holder} onChange={e => setForm({ ...form, cheque_holder: e.target.value })} placeholder="Nome no cheque" className="w-full px-2 py-1 border border-amber-300 rounded text-xs" data-testid="fin-edit-cheque-holder" />
                </div>
              </div>
            </div>
          )}

          <div>
            <Label>Observacoes</Label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className={`${inputCls} resize-none`} data-testid="fin-edit-notes" />
          </div>
        </div>

        <div className="px-5 py-3 flex items-center justify-between gap-2 bg-slate-50 border-t border-slate-100">
          <p className="text-[10px] text-slate-400 hidden sm:block">Os ajustes ficam gravados no titulo e ja aparecem preenchidos na tela de baixa.</p>
          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-bold hover:bg-slate-100">Cancelar</button>
            <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 disabled:opacity-50 text-white text-sm font-black shadow-md shadow-violet-500/30 flex items-center gap-1.5" data-testid="fin-edit-submit">
              <Check size={14} /> {saving ? 'Salvando...' : 'Salvar Alteracoes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};


/* ===== iter77ct.92: FinanceImportModal — Importacao "disfarcada" de lancamentos via Excel =====
   Modal aberto por um botao discreto no painel de configuracao de colunas. Permite:
   - Escolher escopo: Somente A Receber / Somente A Pagar / Ambos (usa coluna Tipo)
   - Baixar planilha modelo (adaptada ao escopo escolhido)
   - Upload do arquivo .xlsx, com preview de contagem/errors
   - Cria automaticamente F.Pgto e Pl.Contas inexistentes no cadastro (backend).            */
const FinanceImportModal = ({ companyId, onClose, onImported }) => {
  const [mode, setMode] = useState('both'); // both | receivable | payable
  const [file, setFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const downloadTemplate = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/finance/import/template?mode=${mode}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      const fname = mode === 'receivable'
        ? 'modelo_contas_a_receber.xlsx'
        : mode === 'payable'
          ? 'modelo_contas_a_pagar.xlsx'
          : 'modelo_lancamentos_financeiros.xlsx';
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Modelo baixado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao baixar modelo');
    } finally {
      setDownloading(false);
    }
  };

  // iter156h: novo fluxo preview -> confirm com de-para de forma pagto + confirmacao de novos clientes/fornecedores
  const [preview, setPreview] = useState(null);       // resposta do /preview
  const [pmMapping, setPmMapping] = useState({});      // {nome_planilha: id_forma_existente}
  const [autoCreateClients, setAutoCreateClients] = useState(true);
  const [autoCreateSuppliers, setAutoCreateSuppliers] = useState(true);

  const doUpload = async () => {
    setError('');
    setResult(null);
    setPreview(null);
    if (!file) { setError('Selecione um arquivo .xlsx'); return; }
    if (!companyId) { setError('Selecione uma filial antes de importar'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(
        `${API}/finance/import/preview?company_id=${encodeURIComponent(companyId)}&mode=${mode}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setPreview(data);
      // pre-seleciona o mapeamento com a 1a forma existente para reduzir cliques
      const firstPmId = (data.existing_payment_methods || [])[0]?.id || '';
      const initMap = {};
      (data.unmapped_payment_methods || []).forEach(u => { initMap[u.name] = firstPmId; });
      setPmMapping(initMap);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erro ao ler planilha';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setUploading(false);
    }
  };

  const confirmImport = async () => {
    if (!preview?.token) return;
    // Valida se todas as formas de pgto estao mapeadas
    const unmapped = (preview.unmapped_payment_methods || []).filter(u => !pmMapping[u.name]);
    if (unmapped.length > 0) {
      toast.error(`Mapeie ${unmapped.length} forma(s) de pagamento antes de confirmar.`);
      return;
    }
    setUploading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/finance/import/confirm`,
        {
          token: preview.token,
          auto_create_clients: autoCreateClients,
          auto_create_suppliers: autoCreateSuppliers,
          payment_method_mapping: pmMapping,
        },
        { withCredentials: true },
      );
      setResult(data);
      setPreview(null);
      if (data.created > 0) {
        toast.success(`${data.created} lancamento(s) importado(s)${data.clients_created ? ` · ${data.clients_created} cliente(s) cadastrado(s)` : ''}${data.suppliers_created ? ` · ${data.suppliers_created} fornecedor(es) cadastrado(s)` : ''}`);
      } else {
        toast.error('Nenhum lancamento criado — verifique os erros.');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erro ao confirmar importacao';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setUploading(false);
    }
  };

  const modes = [
    { key: 'receivable', label: 'Somente A Receber', desc: 'Todas as linhas viram contas a receber', cls: 'from-emerald-500 to-teal-600' },
    { key: 'payable', label: 'Somente A Pagar', desc: 'Todas as linhas viram contas a pagar', cls: 'from-rose-500 to-red-600' },
    { key: 'both', label: 'Ambos', desc: 'A coluna Tipo indica cada lancamento', cls: 'from-violet-500 to-indigo-600' },
  ];

  return (
    <div className="fixed inset-0 z-[190] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" data-testid="finance-import-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-violet-600 to-blue-600 text-white">
          <div className="flex items-center gap-2">
            <FileUp size={18} />
            <div>
              <h3 className="text-sm font-black leading-tight">Importar Lancamentos</h3>
              <p className="text-[10px] opacity-80 leading-tight">Contas a Pagar / Receber via planilha Excel</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="finance-import-close"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Escopo */}
          <div>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-2">Escopo da Importacao</p>
            <div className="grid grid-cols-3 gap-2">
              {modes.map(m => (
                <button key={m.key}
                  type="button"
                  onClick={() => { setMode(m.key); setResult(null); }}
                  className={`p-2.5 rounded-lg border text-left transition-all ${mode === m.key
                    ? `bg-gradient-to-br ${m.cls} text-white border-transparent shadow`
                    : 'bg-white border-slate-200 text-slate-600 hover:border-violet-300'}`}
                  data-testid={`finance-import-mode-${m.key}`}
                >
                  <p className="text-[11px] font-bold leading-tight">{m.label}</p>
                  <p className={`text-[9px] mt-1 leading-tight ${mode === m.key ? 'text-white/85' : 'text-slate-400'}`}>{m.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Baixar modelo */}
          <div className="rounded-lg border border-dashed border-slate-300 p-3 bg-slate-50/60 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-slate-700">Nao tem a planilha?</p>
              <p className="text-[10px] text-slate-500">Baixe o modelo com as colunas corretas para o escopo escolhido.</p>
            </div>
            <button onClick={downloadTemplate} disabled={downloading}
              className="px-3 py-1.5 rounded-md bg-white border border-violet-300 text-violet-700 hover:bg-violet-50 text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
              data-testid="finance-import-template-btn">
              <Download size={12} /> {downloading ? 'Baixando...' : 'Baixar Modelo'}
            </button>
          </div>

          {/* Upload */}
          <div>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-2">Arquivo .xlsx</p>
            <label className="flex items-center gap-2 border-2 border-dashed border-violet-300 rounded-lg px-3 py-4 cursor-pointer hover:bg-violet-50 transition-colors" data-testid="finance-import-drop">
              <FileUp size={18} className="text-violet-600" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-700 truncate">{file ? file.name : 'Selecione ou arraste o arquivo aqui'}</p>
                <p className="text-[10px] text-slate-500">Aceita .xlsx exportado do Excel/Google Sheets/LibreOffice</p>
              </div>
              <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); setError(''); }}
                data-testid="finance-import-file-input" />
            </label>
          </div>

          {/* iter156h: PREVIEW — checklist de novos cadastros + DE-PARA de forma de pagto */}
          {preview && !result && (
            <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 space-y-3" data-testid="finance-import-preview">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-black text-violet-800">Preview da importacao</p>
                  <p className="text-[10px] text-violet-600">{preview.valid_rows} linha(s) validas de {preview.total_rows}{preview.invalid_rows > 0 ? ` · ${preview.invalid_rows} com erro` : ''}</p>
                </div>
                <button onClick={() => { setPreview(null); setPmMapping({}); }} className="text-[10px] text-violet-600 hover:text-violet-900 font-bold" data-testid="finance-import-preview-cancel">Cancelar preview</button>
              </div>

              {/* DE-PARA Forma de Pagamento */}
              {(preview.unmapped_payment_methods || []).length > 0 && (
                <div className="bg-white rounded-lg p-2.5 border border-amber-200">
                  <p className="text-[10px] font-black text-amber-800 uppercase mb-1.5">Formas de pagamento nao cadastradas ({preview.unmapped_payment_methods.length})</p>
                  <p className="text-[10px] text-slate-500 mb-2">Associe cada uma a uma forma existente. Nao ha auto-criacao — o sistema NAO cadastra novas formas de pagamento automaticamente.</p>
                  <div className="space-y-1.5">
                    {preview.unmapped_payment_methods.map(u => (
                      <div key={u.name} className="grid grid-cols-2 gap-2 items-center" data-testid={`pm-map-row-${u.name}`}>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-slate-800 truncate">{u.name}</p>
                          <p className="text-[9px] text-slate-400">{u.count} lancamento(s)</p>
                        </div>
                        <select
                          value={pmMapping[u.name] || ''}
                          onChange={e => setPmMapping(prev => ({ ...prev, [u.name]: e.target.value }))}
                          className="w-full px-2 py-1 text-xs border border-slate-300 rounded"
                          data-testid={`pm-map-select-${u.name}`}
                        >
                          <option value="">— Selecione —</option>
                          {(preview.existing_payment_methods || []).map(pm => (
                            <option key={pm.id} value={pm.id}>{pm.name}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Auto-cadastro Clientes */}
              {(preview.new_clients || []).length > 0 && (
                <div className="bg-white rounded-lg p-2.5 border border-emerald-200" data-testid="finance-import-new-clients">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={autoCreateClients} onChange={e => setAutoCreateClients(e.target.checked)} className="w-4 h-4 accent-emerald-600 mt-0.5" data-testid="auto-create-clients" />
                    <div className="flex-1">
                      <p className="text-[10px] font-black text-emerald-800 uppercase">Cadastrar automaticamente {preview.new_clients.length} cliente(s) novo(s)</p>
                      <ul className="mt-1 text-[10px] text-slate-600 list-disc pl-4 max-h-24 overflow-y-auto">
                        {preview.new_clients.slice(0, 10).map((c, i) => (
                          <li key={i}><strong>{c.name}</strong>{c.document ? ` (${c.document})` : ''}</li>
                        ))}
                        {preview.new_clients.length > 10 && <li className="text-slate-400">…e mais {preview.new_clients.length - 10}</li>}
                      </ul>
                    </div>
                  </label>
                </div>
              )}

              {/* Auto-cadastro Fornecedores */}
              {(preview.new_suppliers || []).length > 0 && (
                <div className="bg-white rounded-lg p-2.5 border border-rose-200" data-testid="finance-import-new-suppliers">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={autoCreateSuppliers} onChange={e => setAutoCreateSuppliers(e.target.checked)} className="w-4 h-4 accent-rose-600 mt-0.5" data-testid="auto-create-suppliers" />
                    <div className="flex-1">
                      <p className="text-[10px] font-black text-rose-800 uppercase">Cadastrar automaticamente {preview.new_suppliers.length} fornecedor(es) novo(s)</p>
                      <ul className="mt-1 text-[10px] text-slate-600 list-disc pl-4 max-h-24 overflow-y-auto">
                        {preview.new_suppliers.slice(0, 10).map((sp, i) => (
                          <li key={i}><strong>{sp.name}</strong>{sp.document ? ` (${sp.document})` : ''}</li>
                        ))}
                        {preview.new_suppliers.length > 10 && <li className="text-slate-400">…e mais {preview.new_suppliers.length - 10}</li>}
                      </ul>
                    </div>
                  </label>
                </div>
              )}

              {/* Erros no preview */}
              {preview.invalid_rows > 0 && (
                <details className="bg-white rounded-lg p-2.5 border border-rose-200">
                  <summary className="cursor-pointer text-xs font-black text-rose-700">{preview.invalid_rows} linha(s) com erro serao ignoradas</summary>
                  <ul className="mt-1 text-[10px] text-rose-700 list-disc pl-4 max-h-32 overflow-y-auto">
                    {(preview.errors_list || []).map((er, i) => <li key={i}>{er}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700" data-testid="finance-import-error">
              <strong>Erro:</strong> {error}
            </div>
          )}

          {result && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 space-y-1" data-testid="finance-import-result">
              <p><strong>{result.created}</strong> lancamento(s) importado(s).</p>
              {result.clients_created > 0 && <p className="text-[11px]">{result.clients_created} cliente(s) cadastrado(s) automaticamente.</p>}
              {result.suppliers_created > 0 && <p className="text-[11px]">{result.suppliers_created} fornecedor(es) cadastrado(s) automaticamente.</p>}
              {result.chart_accounts_created > 0 && <p className="text-[11px]">{result.chart_accounts_created} plano(s) de contas criado(s) automaticamente.</p>}
              {result.errors > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-rose-700 font-bold">{result.errors} erro(s) encontrado(s)</summary>
                  <ul className="mt-1 text-[10px] text-rose-700 list-disc pl-4 max-h-32 overflow-y-auto">
                    {(result.errors_list || []).map((er, i) => <li key={i}>{er}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white border border-slate-300 text-slate-600 text-xs font-bold hover:bg-slate-100" data-testid="finance-import-cancel">Fechar</button>
          {result && result.created > 0 ? (
            <button onClick={onImported} className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-bold" data-testid="finance-import-done">
              Ver Lancamentos
            </button>
          ) : preview ? (
            <button onClick={confirmImport} disabled={uploading}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-bold disabled:opacity-50 flex items-center gap-1.5"
              data-testid="finance-import-confirm">
              <Check size={13} /> {uploading ? 'Confirmando...' : `Confirmar e importar ${preview.valid_rows}`}
            </button>
          ) : (
            <button onClick={doUpload} disabled={!file || uploading}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-blue-600 text-white text-xs font-bold disabled:opacity-50 flex items-center gap-1.5"
              data-testid="finance-import-submit">
              <FileUp size={13} /> {uploading ? 'Lendo...' : 'Analisar planilha'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ===== iter156dt: Combobox — combobox reutilizavel para substituir <select> nos dropdowns
   longos (Plano de Contas, Conta Banco, Forma de Pagamento). Usa position: fixed para funcionar
   dentro de modais com overflow-hidden. Aceita:
     - value: id/valor atual
     - options: [{value, label, subtitle?, keywords?}]
     - onChange(newValue)
     - placeholder, testId, className, disabled
     - allowClear (mostra opcao "Limpar" no topo)
     - onSearch(query) -> Promise<options[]>  (iter156du: busca remota no backend;
       usada quando a lista local eh grande demais para vir toda de uma vez —
       ex: Financeiro com 19000+ clientes; a UI passa a fazer live search server-side)
*/
const Combobox = ({ value, options, onChange, placeholder = 'Selecione', testId = 'search-select', className = '', disabled = false, allowClear = false, onSearch = null }) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [remoteOpts, setRemoteOpts] = React.useState(null); // null = nao usou search remoto ainda
  const [remoteLoading, setRemoteLoading] = React.useState(false);
  // iter156du: cache da opcao selecionada — se o usuario picou algo vindo do search
  // remoto e depois limpou/resetou remoteOpts, ainda queremos exibir o label no botao.
  const [pickedCache, setPickedCache] = React.useState(null);
  const [pos, setPos] = React.useState({ top: 0, left: 0, width: 280 });
  const wrapRef = React.useRef(null);
  const btnRef = React.useRef(null);
  const dropRef = React.useRef(null);
  // iter156du: selected precisa achar em local, remoto OU cache (para exibir label
  // mesmo apos limpar remoteOpts). Ordem: local > cache > remoto.
  const selected = React.useMemo(() => {
    const local = (options || []).find(o => o.value === value);
    if (local) return local;
    if (pickedCache && pickedCache.value === value) return pickedCache;
    const remote = (remoteOpts || []).find(o => o.value === value);
    return remote || null;
  }, [options, remoteOpts, pickedCache, value]);
  const recalcPos = React.useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: Math.max(240, r.width) });
  }, []);
  React.useEffect(() => {
    if (!open) return;
    recalcPos();
    const onDoc = (e) => {
      const inBtn = wrapRef.current && wrapRef.current.contains(e.target);
      const inDrop = dropRef.current && dropRef.current.contains(e.target);
      if (!inBtn && !inDrop) setOpen(false);
    };
    const onScroll = () => recalcPos();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, recalcPos]);
  // iter156du: se onSearch fornecido, dispara busca no backend com debounce 300ms.
  // O resultado remoto sobrescreve o filtro local quando o usuario digita >=2 chars.
  React.useEffect(() => {
    if (!onSearch) return;
    const q = search.trim();
    if (q.length < 2) { setRemoteOpts(null); return; }
    let cancelled = false;
    setRemoteLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await onSearch(q);
        if (!cancelled) setRemoteOpts(Array.isArray(res) ? res : []);
      } catch {
        if (!cancelled) setRemoteOpts([]);
      } finally {
        if (!cancelled) setRemoteLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); setRemoteLoading(false); };
  }, [search, onSearch]);
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    // iter156du: com onSearch + search>=2 usa resultado remoto direto
    if (onSearch && q.length >= 2 && remoteOpts) {
      return remoteOpts.slice(0, 100);
    }
    const list = options || [];
    if (!q) return list.slice(0, 100);
    return list.filter(o => {
      const hay = `${o.label || ''} ${o.subtitle || ''} ${o.keywords || ''}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 100);
  }, [options, search, onSearch, remoteOpts]);
  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testId}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => { if (!disabled) setOpen(v => !v); }}
        disabled={disabled}
        className={`${inputClass} flex items-center justify-between gap-2 text-left ${disabled ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`}
        data-testid={`${testId}-toggle`}
      >
        <span className={`truncate ${selected ? 'text-slate-900' : 'text-slate-400'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
      </button>
      {open && !disabled && (
        <div
          ref={dropRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
          className="bg-white border-2 border-violet-300 rounded-lg shadow-xl overflow-hidden"
        >
          <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={onSearch ? 'Digite 2+ letras para buscar no servidor...' : 'Digite para buscar...'}
              className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs focus:outline-none focus:border-violet-500"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {allowClear && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); setSearch(''); setRemoteOpts(null); }}
                className="w-full text-left px-3 py-1.5 text-xs italic text-slate-500 hover:bg-slate-50 border-b border-slate-100"
                data-testid={`${testId}-opt-clear`}
              >— Limpar selecao —</button>
            )}
            {remoteLoading && (
              <div className="text-center py-2 text-[10px] text-violet-500 italic">Buscando no servidor...</div>
            )}
            {!remoteLoading && filtered.length === 0 ? (
              <div className="text-center py-4 text-[11px] text-slate-400">
                {onSearch && search.trim().length < 2 ? 'Digite 2+ letras para buscar' : 'Nenhum resultado'}
              </div>
            ) : filtered.map(o => (
              <button
                key={o.value || '__empty__'}
                type="button"
                onClick={() => { setPickedCache(o); onChange(o.value, o); setOpen(false); setSearch(''); setRemoteOpts(null); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-violet-50 border-b border-slate-50 last:border-0 ${o.value === value ? 'bg-violet-50' : ''}`}
                data-testid={`${testId}-opt-${o.value || 'empty'}`}
              >
                <div className="font-semibold text-slate-800 truncate">{o.label}</div>
                {o.subtitle && <div className="text-[10px] text-slate-500 truncate">{o.subtitle}</div>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};



/* ===== FinancialLaunchModal — estilo Alvotec "Inserir Contas A Pagar / Receber" ===== */
const FinancialLaunchModal = ({ companyId, initialType, clients, suppliers, chartAccounts, bankAccounts, paymentMethods, onClose, onSaved }) => {
  const isPayable = initialType === 'payable';
  const today = localDateISO();
  const [partyType, setPartyType] = useState(isPayable ? 'fornecedor' : 'cliente');
  const [partySearch, setPartySearch] = useState('');
  const [party, setParty] = useState(null);
  // iter156dp: employees carregados sob demanda quando partyType=funcionario
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    if (partyType !== 'funcionario' || employees.length > 0) return;
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/employees`, { withCredentials: true });
        setEmployees(data.data || []);
      } catch { /* ignore */ }
    })();
  }, [partyType, employees.length]);
  const [documentDate, setDocumentDate] = useState(today);
  const [documentType, setDocumentType] = useState('fixa');
  const [documentNumber, setDocumentNumber] = useState('');
  const [chartAccountId, setChartAccountId] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [amount, setAmount] = useState(0); // iter156bm: numero (usado com CurrencyInput)
  const [installmentsMode, setInstallmentsMode] = useState('integral');
  const [numInstallments, setNumInstallments] = useState(1);
  const [dueDate, setDueDate] = useState(today);
  const [notes, setNotes] = useState('');
  const [settleMethod, setSettleMethod] = useState('');
  // iter156dt: campos de cheque — aparecem apenas quando forma de pagamento eh "cheque"
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeBank, setChequeBank] = useState('');
  const [chequeHolder, setChequeHolder] = useState('');
  const [keepOpen, setKeepOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // iter77dh: cadastro rapido de cliente (receivable) / fornecedor (payable)
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  // iter156bm: live search no banco de dados (debounced 300ms) — antes so filtrava pre-loaded list
  const [remoteParties, setRemoteParties] = useState([]);
  const [partySearchLoading, setPartySearchLoading] = useState(false);
  useEffect(() => {
    if (party) { setRemoteParties([]); return; }
    // iter156dp: plano_conta nao busca party — nome vem do proprio plano selecionado abaixo.
    if (partyType === 'plano_conta') { setRemoteParties([]); return; }
    const q = partySearch.trim();
    if (!q || q.length < 2) { setRemoteParties([]); return; }
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      try {
        setPartySearchLoading(true);
        // iter156dp: mapa dinamico de endpoints — inclui funcionario.
        const endpointMap = { cliente: 'clients', fornecedor: 'suppliers', funcionario: 'employees' };
        const endpoint = endpointMap[partyType] || 'clients';
        const { data } = await axios.get(`${ERP}/${endpoint}?search=${encodeURIComponent(q)}&limit=20`, { withCredentials: true, signal: ctl.signal });
        setRemoteParties(data.data || []);
      } catch { /* aborted or offline */ }
      finally { setPartySearchLoading(false); }
    }, 300);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [partySearch, partyType, party]);

  // Pre-fill defaults from /api/erp/company-settings + first available option as fallback
  useEffect(() => {
    let mounted = true;
    (async () => {
      let settings = {};
      try {
        const { data } = await axios.get(`${ERP}/company-settings`, { withCredentials: true });
        settings = data || {};
      } catch { /* ignore */ }
      if (!mounted) return;
      // Chart account by code (settings store code like '3.1.01'); fallback to first matching type
      const targetCode = isPayable ? settings.default_purchase_account : settings.default_sales_account;
      const allowedTypes = isPayable ? ['expense', 'liability'] : ['income', 'asset'];
      const chartByCode = chartAccounts.find(c => c.code === targetCode && allowedTypes.includes(c.type));
      const chartFallback = chartAccounts.find(c => allowedTypes.includes(c.type));
      const chosenChart = chartByCode || chartFallback;
      if (chosenChart) setChartAccountId(chosenChart.id);
      // Bank account: settings.default_bank_account_id OR first
      const chosenBank = bankAccounts.find(b => b.id === settings.default_bank_account_id) || bankAccounts[0];
      if (chosenBank) setBankAccountId(chosenBank.id);
      // iter156gl: em CONTAS A PAGAR a forma padrao vem da flag "default_for_expenses" da propria forma.
      // Sem nenhuma marcada, fica vazio e o usuario escolhe. Em receber, mantem o default das configuracoes.
      if (isPayable) {
        const defExp = (paymentMethods || []).find(p => p.default_for_expenses && p.active !== false);
        if (defExp) setSettleMethod(defExp.id);
      } else {
        const dpm = (settings.default_payment_method || '').toLowerCase();
        const chosenPm = (paymentMethods || []).find(p => (p.code || '').toLowerCase() === dpm || (p.name || '').toLowerCase() === dpm || (p.type || '').toLowerCase() === dpm);
        if (chosenPm) setSettleMethod(chosenPm.id);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartAccounts, bankAccounts, paymentMethods, isPayable]);

  // iter156bm: prioriza resultados vindos da API (buscas por qualquer letra digitada);
  // fallback para lista pre-carregada quando nao ha texto ou API ainda nao retornou.
  const partyList = useMemo(() => {
    const q = partySearch.toLowerCase().trim();
    if (q && q.length >= 2 && remoteParties.length > 0) {
      // Deduplica por id
      const seen = new Set();
      const merged = [];
      for (const p of remoteParties) {
        if (!seen.has(p.id)) { seen.add(p.id); merged.push(p); }
      }
      return merged.slice(0, 12);
    }
    // iter156dp: source local baseado no tipo — inclui funcionarios
    const src = partyType === 'cliente' ? clients : (partyType === 'funcionario' ? employees : suppliers);
    if (!q) return src.slice(0, 8);
    return src.filter(p => (p.name || '').toLowerCase().includes(q) || (p.document || '').includes(q)).slice(0, 8);
  }, [partySearch, partyType, clients, suppliers, employees, remoteParties]);

  // iter156dp: helpers de rotulo por tipo
  const partyLabel = { cliente: 'Cliente', fornecedor: 'Fornecedor', funcionario: 'Funcionario', plano_conta: 'Plano de Contas' }[partyType] || 'Cliente';
  const isPlanoContaType = partyType === 'plano_conta';

  // Forma de pagamento — agora puxa do cadastro Financeiro > Formas de Pagamento
  const paymentOptions = useMemo(() => (
    [{ value: '', label: 'Em Aberto - Padrao' }].concat(
      (paymentMethods || []).map(pm => ({ value: pm.id, label: pm.name, subtitle: pm.type || '', keywords: pm.code || '' }))
    )
  ), [paymentMethods]);

  // iter156dt: detecta se a forma escolhida eh Cheque (por type na PM)
  const selectedPaymentMethod = useMemo(() => (paymentMethods || []).find(m => m.id === settleMethod), [paymentMethods, settleMethod]);
  const isCheque = ((selectedPaymentMethod?.type || '').toLowerCase() === 'cheque') ||
                   ((selectedPaymentMethod?.name || '').toLowerCase().includes('cheque'));

  // iter156dt: options para os SearchableSelects longos (plano de contas, banco)
  const chartOptions = useMemo(() => (
    (chartAccounts || [])
      .filter(c => isPayable ? ['expense', 'liability'].includes(c.type) : ['income', 'asset'].includes(c.type))
      .map(c => ({ value: c.id, label: `${c.code || ''} · ${c.name}`.trim(), keywords: `${c.code || ''} ${c.name || ''}` }))
  ), [chartAccounts, isPayable]);
  const bankOptions = useMemo(() => (
    (bankAccounts || []).map(b => ({
      value: b.id,
      label: `${b.bank_name || b.name || 'Banco'}${b.agency ? ` · ${b.agency}` : ''}${b.account_number ? `/${b.account_number}` : ''}`,
      keywords: `${b.bank_name || ''} ${b.name || ''} ${b.agency || ''} ${b.account_number || ''}`,
    }))
  ), [bankAccounts]);

  const reset = () => {
    setPartySearch(''); setParty(null); setAmount(0); setDocumentNumber(''); setNotes('');
    setNumInstallments(1); setInstallmentsMode('integral'); setSettleMethod('');
    setDueDate(today); setDocumentDate(today); setChartAccountId(''); setBankAccountId('');
    setChequeNumber(''); setChequeBank(''); setChequeHolder('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!companyId) { toast.error('Selecione uma empresa'); return; }
    // iter156dp: quando tipo=plano_conta, NAO precisa selecionar party — o nome do
    // proprio plano de contas eh usado como party_name (campo desabilitado na UI).
    if (!isPlanoContaType && !party) {
      toast.error(`Selecione um ${partyLabel}`); return;
    }
    if (!chartAccountId) { toast.error('Selecione o Plano de Contas'); return; }
    if (!bankAccountId) { toast.error('Selecione a Conta Disponibilidade'); return; }
    const value = Number(amount) || 0;
    if (value <= 0) { toast.error('Valor invalido'); return; }
    // iter156dt: se cheque, cobra ao menos o numero do cheque
    if (isCheque && !chequeNumber.trim()) {
      toast.error('Informe o Numero do Cheque'); return;
    }
    setSubmitting(true);
    try {
      const chart = chartAccounts.find(c => c.id === chartAccountId);
      const pm = paymentMethods.find(m => m.id === settleMethod);
      // iter156dp: quando tipo=plano_conta, party_name = nome do plano de contas
      const effectivePartyName = isPlanoContaType ? (chart?.name || 'Plano de Contas') : party.name;
      const effectivePartyId = isPlanoContaType ? null : party.id;
      const payload = {
        company_id: companyId,
        type: isPayable ? 'payable' : 'receivable',
        description: notes || `${partyLabel}: ${effectivePartyName}`,
        amount: value,
        due_date: dueDate,
        category: chart?.name || chart?.code || '',
        payment_method: pm?.name || '',
        notes,
        party_type: partyType,
        party_id: effectivePartyId,
        party_name: effectivePartyName,
        document_date: documentDate,
        document_number: documentNumber,
        document_type: documentType,
        chart_account_id: chartAccountId,
        bank_account_id: bankAccountId,
        num_installments: Math.max(1, Number(numInstallments) || 1),
        installments_mode: installmentsMode,
        settle_status: 'open',
        settle_method: pm?.name || '',
        // iter156dt: dados de cheque (so preenchidos se forma de pagamento eh cheque)
        cheque_number: isCheque ? chequeNumber.trim() : '',
        cheque_bank: isCheque ? chequeBank.trim() : '',
        cheque_holder: isCheque ? chequeHolder.trim() : '',
      };
      const { data } = await axios.post(`${ERP}/financial`, payload, { withCredentials: true });
      toast.success(`${data.count || 1} lancamento(s) criado(s)!`);
      onSaved?.();
      if (keepOpen) { reset(); }
      else { onClose(); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="financial-launch-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`px-5 py-3 flex items-center justify-between border-b ${isPayable ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`}>
          <h2 className={`font-black text-base flex items-center gap-2 ${isPayable ? 'text-rose-800' : 'text-emerald-800'}`}>
            <Plus size={18} />Inserir Contas A {isPayable ? 'Pagar' : 'Receber'}
          </h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="financial-modal-close"><X size={18} /></button>
        </div>
        {/* Body */}
        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          {/* Tipo de Lancamento + Nome (Cliente/Fornecedor/Funcionario/Plano de Contas) */}
          <div className="md:col-span-1">
            <label className="block text-xs font-bold text-slate-600 mb-1">* Tipo de Lancamento</label>
            {/* iter156bm: dropdown sempre com Cliente + Fornecedor (default segue isPayable).
                iter156dp: adicionados Funcionario e Plano de Contas. */}
            <select value={partyType} onChange={(e) => { setPartyType(e.target.value); setParty(null); setPartySearch(''); setRemoteParties([]); }} className={inputClass} data-testid="party-type-select">
              <option value="cliente">Cliente</option>
              <option value="fornecedor">Fornecedor</option>
              <option value="funcionario">Funcionario</option>
              <option value="plano_conta">Plano de Contas</option>
            </select>
          </div>
          <div className="md:col-span-2 relative">
            <label className="block text-xs font-bold text-slate-600 mb-1">
              {isPlanoContaType
                ? <>Nome (usa o Plano de Contas)</>
                : <>* Nome do {partyLabel}</>}
            </label>
            <div className="flex gap-1.5">
              {/* iter156dp: quando plano_conta, input desabilitado — mostra o nome do plano selecionado
                  abaixo (chart_account_id). Placeholder guia o usuario. */}
              <input
                value={isPlanoContaType
                  ? (chartAccounts.find(c => c.id === chartAccountId)?.name || '')
                  : (party ? party.name : partySearch)}
                onChange={(e) => { if (isPlanoContaType) return; setParty(null); setPartySearch(e.target.value); }}
                placeholder={isPlanoContaType
                  ? 'Preenchido automaticamente pelo Plano de Contas abaixo'
                  : `Pesquise um ${partyLabel}...`}
                readOnly={isPlanoContaType}
                className={`${inputClass} flex-1 ${isPlanoContaType ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}
                data-testid="party-search-input"
              />
              {/* iter77dh: cadastro rapido — abre mini modal e ja auto-seleciona no retorno.
                  iter156dp: escondido para plano_conta (nao ha cadastro rapido de plano no fluxo). */}
              {!isPlanoContaType && partyType !== 'funcionario' && (
                <button
                  type="button"
                  onClick={() => setShowQuickCreate(true)}
                  className="px-3 py-2 bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white rounded-lg shadow flex items-center gap-1 shrink-0"
                  title={`Cadastro rapido de ${partyLabel}`}
                  data-testid="party-quick-create-btn"
                >
                  <Plus size={14} />
                  <span className="text-xs font-bold hidden sm:inline">Novo</span>
                </button>
              )}
            </div>
            {!isPlanoContaType && !party && partySearch && (partyList.length > 0 || partySearchLoading) && (
              <div className="absolute z-10 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {partySearchLoading && (
                  <div className="px-3 py-2 text-[10px] text-slate-400 italic">Buscando no banco de dados...</div>
                )}
                {partyList.map(p => (
                  <button key={p.id} type="button" onClick={() => { setParty(p); setPartySearch(p.name); }} className="w-full text-left px-3 py-2 text-xs hover:bg-violet-50 border-b border-slate-100 last:border-0" data-testid={`party-opt-${p.id}`}>
                    <p className="font-bold text-slate-900">{p.name}</p>
                    <p className="text-slate-500">{p.document || '—'}</p>
                  </button>
                ))}
                {!partySearchLoading && partyList.length === 0 && partySearch.length >= 2 && (
                  <div className="px-3 py-3 text-[10px] text-slate-400 italic">
                    Nada encontrado. Use o botao <span className="font-bold text-violet-600">+ Novo</span> para cadastrar.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Data Doc, Tipo Conta, Numero Doc */}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">* Data do Documento</label>
            <input type="date" value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} className={inputClass} data-testid="doc-date" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">* Tipo de Conta</label>
            <div className="flex gap-3 items-center pt-2">
              <label className="flex items-center gap-1.5 text-xs"><input type="radio" name="doc-type" value="fixa" checked={documentType === 'fixa'} onChange={() => setDocumentType('fixa')} data-testid="doc-type-fixa" />Fixa</label>
              <label className="flex items-center gap-1.5 text-xs"><input type="radio" name="doc-type" value="variavel" checked={documentType === 'variavel'} onChange={() => setDocumentType('variavel')} data-testid="doc-type-variavel" />Variavel</label>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">N do Documento</label>
            <input value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} placeholder="Numero do Documento..." className={inputClass} data-testid="doc-number" />
          </div>

          {/* Plano Contas + Conta Disponibilidade */}
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">* Plano de Contas</label>
            {/* iter156dt: combobox pesquisavel — substitui o <select> longo */}
            <Combobox
              value={chartAccountId}
              options={chartOptions}
              onChange={setChartAccountId}
              placeholder="Pesquise por codigo ou nome..."
              testId="chart-account-select"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">* Conta Banco</label>
            {/* iter156dt: combobox pesquisavel */}
            <Combobox
              value={bankAccountId}
              options={bankOptions}
              onChange={setBankAccountId}
              placeholder="Pesquise por banco/agencia..."
              testId="bank-account-select"
            />
          </div>

          {/* Valor + Parcelas + Data do Vencimento */}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">* Valor</label>
            {/* iter156bm: mascara BRL automatica — digitar "1049300" -> "10.493,00" */}
            <CurrencyInput
              value={amount}
              onChange={(v) => setAmount(v)}
              placeholder="0,00"
              dataTestId="amount-input"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1 flex items-center gap-1">
              <span>* N Parcelas</span>
              {/* iter156dm: tooltip explicativo dos 2 modos (INTEGRAL x RATEADAS) */}
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-[10px] font-black cursor-help hover:bg-violet-200 hover:text-violet-700"
                title={
                  installmentsMode === 'integral'
                    ? 'INTEGRAL: cria N parcelas com o VALOR CHEIO cada uma. Ex: R$ 1.000,00 x 10 = 10 parcelas de R$ 1.000,00 (uma por mes, total R$ 10.000,00). Uso: aluguel, mensalidade, salario.'
                    : 'RATEADAS: divide o VALOR pelo numero de parcelas. Ex: R$ 1.000,00 / 10 = 10 parcelas de R$ 100,00 (uma por mes, total R$ 1.000,00). Uso: compra parcelada, fatura de cartao dividida.'
                }
                data-testid="parcelas-tooltip"
              >?</span>
            </label>
            {/* iter156bm: layout inline moderno — toggle pill + input em uma linha alinhada
                iter156dm: input agora HABILITADO tambem no modo INTEGRAL. */}
            <div className="flex items-center gap-2">
              <div className="inline-flex bg-slate-100 rounded-lg p-0.5 shrink-0">
                <button
                  type="button"
                  onClick={() => setInstallmentsMode('integral')}
                  className={`px-3 py-1 rounded-md text-[11px] font-black uppercase tracking-wider transition-all ${installmentsMode === 'integral' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="inst-integral"
                >Integral</button>
                <button
                  type="button"
                  onClick={() => setInstallmentsMode('rateadas')}
                  className={`px-3 py-1 rounded-md text-[11px] font-black uppercase tracking-wider transition-all ${installmentsMode === 'rateadas' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="inst-rateadas"
                >Rateadas</button>
              </div>
              <input
                type="number"
                min="1"
                max="36"
                value={numInstallments}
                onChange={(e) => setNumInstallments(e.target.value)}
                className={`${inputClass} flex-1 tabular-nums text-center font-bold`}
                data-testid="num-installments"
              />
            </div>
            {/* iter156dm: preview em tempo real do que sera lancado */}
            {Number(numInstallments) > 1 && Number(amount) > 0 && (
              <p className="text-[10px] text-slate-500 mt-1" data-testid="parcelas-preview">
                {installmentsMode === 'integral'
                  ? <>Sera lancado <b>{numInstallments}x de R$ {Number(amount).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b> (total R$ {(Number(amount) * Number(numInstallments)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</>
                  : <>Sera lancado <b>{numInstallments}x de R$ {(Number(amount) / Number(numInstallments)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b> (total R$ {Number(amount).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</>
                }
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">* Data do Vencimento</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputClass} data-testid="due-date-input" />
          </div>

          {/* Historico + Forma de Pagamento */}
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">Historico da Conta</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Historico da conta..." className={inputClass} data-testid="notes-input" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">* Forma de Pagamento</label>
            {/* iter156dt: combobox pesquisavel — permite achar forma rapido em cadastros grandes */}
            <Combobox
              value={settleMethod}
              options={paymentOptions}
              onChange={setSettleMethod}
              placeholder="Escolha uma forma de pagamento..."
              testId="settle-method-select"
            />
            {paymentMethods.length === 0 && (
              <p className="text-[10px] text-amber-600 mt-1">Nenhuma forma cadastrada. Cadastre em <strong>Financeiro &rsaquo; Formas de Pagamento</strong>.</p>
            )}
          </div>

          {/* iter156dt: campos especificos de CHEQUE — aparecem so quando a forma de pagamento selecionada eh cheque */}
          {isCheque && (
            <div className="md:col-span-3 bg-amber-50 border-2 border-amber-200 rounded-lg p-3" data-testid="cheque-fields">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-black">CH</div>
                <p className="text-xs font-black text-amber-800 uppercase tracking-wider">Dados do Cheque</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">* Numero do Cheque</label>
                  <input value={chequeNumber} onChange={e => setChequeNumber(e.target.value)} placeholder="000123" className={inputClass} data-testid="cheque-number" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Banco Emissor</label>
                  <input value={chequeBank} onChange={e => setChequeBank(e.target.value)} placeholder="Ex: 001 · Banco do Brasil" className={inputClass} data-testid="cheque-bank" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Titular do Cheque</label>
                  <input value={chequeHolder} onChange={e => setChequeHolder(e.target.value)} placeholder="Nome impresso no cheque" className={inputClass} data-testid="cheque-holder" />
                </div>
              </div>
              <p className="text-[10px] text-amber-700 mt-2 italic">
                A <strong>Data de Vencimento</strong> acima (bom para/compensacao) e o vencimento do proprio cheque.
              </p>
            </div>
          )}
        </form>
        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={keepOpen} onChange={(e) => setKeepOpen(e.target.checked)} data-testid="keep-open" />Manter a tela aberta?
          </label>
          <div className="flex gap-2">
            <button onClick={onClose} type="button" className="btn-pill-ghost" data-testid="modal-close-btn">Fechar</button>
            <button onClick={submit} disabled={submitting} className={isPayable ? 'btn-pill-danger' : 'btn-pill-success'} data-testid="modal-save-btn">
              <Save size={13} />{submitting ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
      {/* iter77dh: mini modal de cadastro rapido de Cliente/Fornecedor */}
      {showQuickCreate && (
        <QuickPartyCreateModal
          type={partyType}
          onClose={() => setShowQuickCreate(false)}
          onCreated={(p) => {
            setShowQuickCreate(false);
            setParty(p);
            setPartySearch(p.name);
            toast.success(`${partyType === 'cliente' ? 'Cliente' : 'Fornecedor'} cadastrado e selecionado.`);
          }}
        />
      )}
    </div>
  );
};

// iter77dh: mini modal para cadastro rapido de Cliente ou Fornecedor
// Usa apenas os campos essenciais (nome, doc, phone). Retorna o objeto criado
// via `onCreated`. Nao mexe na lista global — a selecao automatica no form pai
// resolve o caso de uso imediato; ao reabrir o form, a lista sera atualizada.
const QuickPartyCreateModal = ({ type, onClose, onCreated }) => {
  const isClient = type === 'cliente';
  const [name, setName] = useState('');
  const [document_, setDocument] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { toast.error('Informe o nome'); return; }
    setSaving(true);
    try {
      const url = isClient ? `${ERP}/clients` : `${ERP}/suppliers`;
      const payload = { name: name.trim(), document: document_.trim(), phone: phone.trim(), email: email.trim() };
      const { data } = await axios.post(url, payload, { withCredentials: true });
      onCreated?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Erro ao cadastrar ${isClient ? 'cliente' : 'fornecedor'}`);
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="quick-party-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className={`px-5 py-3 border-b flex items-center justify-between ${isClient ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200' : 'bg-gradient-to-r from-rose-50 to-orange-50 border-rose-200'}`}>
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg text-white flex items-center justify-center shadow ${isClient ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : 'bg-gradient-to-br from-rose-500 to-orange-600'}`}>
              <Plus size={16} />
            </div>
            <h3 className="font-black text-sm text-slate-900">Cadastro rapido — {isClient ? 'Cliente' : 'Fornecedor'}</h3>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="quick-party-close"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">* Nome / Razao Social</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={isClient ? 'Nome completo do cliente' : 'Razao social do fornecedor'} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-violet-500 focus:outline-none" autoFocus data-testid="quick-party-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">CPF / CNPJ</label>
              <input value={document_} onChange={(e) => setDocument(e.target.value)} placeholder="000.000.000-00" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-violet-500 focus:outline-none" data-testid="quick-party-doc" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Telefone</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(00) 00000-0000" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-violet-500 focus:outline-none" data-testid="quick-party-phone" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemplo.com" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-violet-500 focus:outline-none" data-testid="quick-party-email" />
          </div>
          <p className="text-[10px] text-slate-500">Preencha o essencial e complete os demais dados depois no cadastro completo.</p>
          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-slate-300 text-xs font-bold text-slate-700 hover:bg-slate-50" data-testid="quick-party-cancel">Cancelar</button>
            <button type="submit" disabled={saving || !name.trim()} className={`flex-1 px-4 py-2 rounded-lg text-white text-xs font-bold shadow disabled:opacity-50 ${isClient ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700' : 'bg-gradient-to-r from-rose-500 to-orange-600 hover:from-rose-600 hover:to-orange-700'}`} data-testid="quick-party-save">
              {saving ? 'Salvando...' : `Cadastrar ${isClient ? 'Cliente' : 'Fornecedor'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ========== PHARMACY PDV HELPERS — tarjas, alergias, controlados ========== */
const TARJA_LABELS = {
  vermelha: { label: 'Tarja Vermelha', cls: 'bg-red-600 text-white border-red-700', dot: 'bg-red-500' },
  preta: { label: 'Tarja Preta', cls: 'bg-slate-900 text-white border-slate-950', dot: 'bg-slate-900' },
  amarela: { label: 'Tarja Amarela (Antibiotico)', cls: 'bg-amber-400 text-amber-950 border-amber-500', dot: 'bg-amber-400' },
  sem_tarja: { label: 'Sem Tarja', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-400' },
};
const tarjaInfo = (t) => TARJA_LABELS[(t || '').toLowerCase().trim()] || null;
const itemNeedsControl = (it) => !!(it?.pharmacy && it.pharmacy.controlled);
const itemExceedsCMED = (it) => {
  const pmc = Number(it?.pharmacy?.cmed_pmc || 0);
  const up = Number(it?.unit_price || 0);
  return pmc > 0 && up > pmc;
};
const cartHasControlled = (items) => items.some(itemNeedsControl);
const cartCMEDIssues = (items) => items.filter(itemExceedsCMED).map(it => ({ name: it.name, unit_price: it.unit_price, pmc: it.pharmacy.cmed_pmc }));
const cartAllergyHits = (items, allergies) => {
  if (!allergies || !allergies.trim()) return [];
  const terms = allergies.split(/[,;\n]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
  if (!terms.length) return [];
  const out = [];
  for (const it of items) {
    const ai = (it?.pharmacy?.active_ingredient || '').toLowerCase();
    const name = (it?.name || '').toLowerCase();
    for (const t of terms) {
      if (t.length >= 3 && (ai.includes(t) || name.includes(t))) {
        out.push({ name: it.name, term: t, active_ingredient: it.pharmacy?.active_ingredient || '' });
        break;
      }
    }
  }
  return out;
};

/* ========== PharmacyLotPickerModal — selecao de lote no PDV (FEFO) ========== */
const PharmacyLotPickerModal = ({ product, onPick, onSkip, onClose }) => {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pharmacy/lots?product_id=${product.id}&only_with_stock=true`, { withCredentials: true });
        if (alive) setLots(data.data || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Erro ao carregar lotes');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [product.id]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" data-testid="pharmacy-lot-picker-modal">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-cyan-50 to-violet-50">
          <div>
            <h3 className="text-sm font-black text-cyan-900">Selecione o Lote</h3>
            <p className="text-[11px] text-cyan-700 truncate max-w-[260px]">{product.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-cyan-100" data-testid="lot-picker-close"><X size={16} /></button>
        </div>
        <div className="p-3">
          {loading ? <p className="text-xs text-slate-400 text-center py-6">Carregando lotes...</p> : lots.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-xs text-amber-700 font-bold">Nenhum lote cadastrado com saldo.</p>
              <p className="text-[10px] text-amber-600 mt-1">Cadastre lotes em: Produtos &gt; Editar &gt; aba Farmacia.</p>
              <button onClick={() => { onSkip && onSkip(); }} className="mt-3 text-[11px] font-bold text-violet-700 underline" data-testid="lot-picker-skip">Adicionar sem lote</button>
            </div>
          ) : (
            <div className="space-y-2">
              {lots.map(l => {
                const exp = l.expiry_date || '';
                return (
                  <button
                    key={l.id}
                    onClick={() => onPick({ lot: l.lot, expiry_date: exp, lot_id: l.id })}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${l.is_expired ? 'border-red-300 bg-red-50 opacity-50 cursor-not-allowed' : 'border-cyan-200 bg-cyan-50 hover:border-cyan-500 hover:bg-cyan-100'}`}
                    disabled={l.is_expired}
                    data-testid="lot-picker-item"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-black text-sm text-cyan-900">{l.lot}</span>
                      <span className="text-xs font-bold text-cyan-700">{Number(l.quantity).toFixed(0)} un</span>
                    </div>
                    <div className="flex items-center justify-between mt-1 text-[10px]">
                      <span className={l.is_expired ? 'text-red-700 font-bold' : 'text-slate-600'}>Validade: {exp || '—'}{l.is_expired && ' (VENCIDO)'}</span>
                      {l.supplier_invoice && <span className="text-slate-500">NF: {l.supplier_invoice}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <button onClick={() => { onSkip && onSkip(); }} className="text-[11px] text-slate-500 underline hover:text-slate-700" data-testid="lot-picker-skip-link">Adicionar sem lote</button>
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-200" data-testid="lot-picker-cancel">Cancelar</button>
        </div>
      </div>
    </div>
  );
};

/* ========== PharmacyPrescriptionModal — captura rapida de receita no PDV ========== */
const PharmacyPrescriptionModal = ({ initialCustomerName = '', initialCustomerCpf = '', onClose, onSaved }) => {
  const [customerName, setCustomerName] = useState(initialCustomerName);
  const [customerCpf, setCustomerCpf] = useState(initialCustomerCpf);
  const [prescriberName, setPrescriberName] = useState('');
  const [prescriberCrm, setPrescriberCrm] = useState('');
  const [prescriberUf, setPrescriberUf] = useState('GO');
  const [prescriptionDate, setPrescriptionDate] = useState(() => localDateISO());
  const [notificationNumber, setNotificationNumber] = useState('');
  const [retained, setRetained] = useState(true);
  const [notes, setNotes] = useState('');
  const [imageB64, setImageB64] = useState('');
  const [imageMime, setImageMime] = useState('image/jpeg');
  const [saving, setSaving] = useState(false);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Imagem acima de 5MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = String(ev.target?.result || '');
      const comma = result.indexOf(',');
      setImageMime(f.type || 'image/jpeg');
      setImageB64(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!customerName.trim()) { toast.error('Informe o nome do consumidor'); return; }
    if (!prescriberName.trim()) { toast.error('Informe o nome do prescritor'); return; }
    if (!prescriberCrm.trim()) { toast.error('Informe o CRM do prescritor'); return; }
    setSaving(true);
    try {
      const payload = {
        customer_name: customerName.trim(),
        customer_cpf: customerCpf.replace(/\D+/g, ''),
        prescriber_name: prescriberName.trim(),
        prescriber_crm: prescriberCrm.trim(),
        prescriber_uf: prescriberUf,
        prescription_date: prescriptionDate,
        notes,
        notification_number: notificationNumber,
        retained,
        image_b64: imageB64,
        image_mime: imageMime,
      };
      const { data } = await axios.post(`${API}/pharmacy/prescriptions`, payload, { withCredentials: true });
      toast.success('Receita registrada');
      if (onSaved) onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar receita');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" data-testid="pharmacy-prescription-modal">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-violet-50 to-fuchsia-50">
          <div>
            <h3 className="text-base font-black text-violet-900">Receita Medica</h3>
            <p className="text-[11px] text-violet-700">Obrigatoria para venda de controlados</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-violet-100" data-testid="prescription-modal-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-[11px] font-bold text-slate-600 uppercase col-span-2">
              Consumidor *
              <input value={customerName} onChange={e => setCustomerName(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-900" data-testid="prescription-customer-name" />
            </label>
            <label className="text-[11px] font-bold text-slate-600 uppercase col-span-2">
              CPF do Consumidor
              <input value={customerCpf} onChange={e => setCustomerCpf(e.target.value)} placeholder="000.000.000-00" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-900" data-testid="prescription-customer-cpf" />
            </label>
            <label className="text-[11px] font-bold text-slate-600 uppercase col-span-2">
              Medico / Prescritor *
              <input value={prescriberName} onChange={e => setPrescriberName(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-900" data-testid="prescription-prescriber-name" />
            </label>
            <label className="text-[11px] font-bold text-slate-600 uppercase">
              CRM *
              <input value={prescriberCrm} onChange={e => setPrescriberCrm(e.target.value)} placeholder="000000" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-900" data-testid="prescription-prescriber-crm" />
            </label>
            <label className="text-[11px] font-bold text-slate-600 uppercase">
              UF
              <select value={prescriberUf} onChange={e => setPrescriberUf(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-900" data-testid="prescription-prescriber-uf">
                {['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'].map(u => <option key={u}>{u}</option>)}
              </select>
            </label>
            <label className="text-[11px] font-bold text-slate-600 uppercase">
              Data da Receita
              <input type="date" value={prescriptionDate} onChange={e => setPrescriptionDate(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-900" data-testid="prescription-date" />
            </label>
            <label className="text-[11px] font-bold text-slate-600 uppercase">
              Notificacao (Tarja Preta)
              <input value={notificationNumber} onChange={e => setNotificationNumber(e.target.value)} placeholder="Numero" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-900" data-testid="prescription-notification" />
            </label>
            <label className="flex items-center gap-2 col-span-2 text-xs font-bold text-slate-700">
              <input type="checkbox" checked={retained} onChange={e => setRetained(e.target.checked)} className="w-4 h-4" data-testid="prescription-retained" />
              <span>Receita retida pela farmacia</span>
            </label>
            <label className="text-[11px] font-bold text-slate-600 uppercase col-span-2">
              Foto / Scan da Receita
              <input type="file" accept="image/*" onChange={onPickFile} className="mt-1 w-full text-xs text-slate-700 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-violet-100 file:text-violet-700 file:font-bold" data-testid="prescription-image" />
              {imageB64 && <p className="text-[10px] text-emerald-600 font-bold mt-1">[OK] Imagem anexada</p>}
            </label>
            <label className="text-[11px] font-bold text-slate-600 uppercase col-span-2">
              Observacoes
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-900" data-testid="prescription-notes" />
            </label>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100" data-testid="prescription-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white text-sm font-black shadow-md" data-testid="prescription-save">
            {saving ? 'Salvando...' : 'Salvar Receita'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ========== PDV MODERN with QR Scanner ========== */
const QRScannerModal = ({ onScan, onClose }) => {
  const [ScannerComponent, setScannerComponent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    import('@yudiel/react-qr-scanner').then(mod => {
      if (mounted) setScannerComponent(() => mod.Scanner);
    }).catch(() => {
      if (mounted) setError('Camera nao disponivel');
    });
    return () => { mounted = false; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4" data-testid="qr-scanner-modal">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-lg">Escanear QR/Codigo</h3>
          <button onClick={onClose} className="text-white/70 hover:text-white p-2" data-testid="close-qr-btn"><X size={24} /></button>
        </div>
        <div className="rounded-2xl overflow-hidden bg-black aspect-square">
          {error ? (
            <div className="flex items-center justify-center h-full text-white/60 text-sm">{error}</div>
          ) : ScannerComponent ? (
            <ScannerComponent
              onScan={(result) => {
                if (result && result.length > 0) {
                  const text = result[0]?.rawValue || '';
                  if (text) onScan(text);
                }
              }}
              onError={() => setError('Erro ao acessar camera')}
              formats={['qr_code', 'ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e']}
              components={{ audio: false }}
              styles={{ container: { width: '100%', height: '100%' } }}
            />
          ) : (
            <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" /></div>
          )}
        </div>
        <p className="text-white/50 text-xs text-center mt-3">Aponte a camera para o QR Code ou codigo de barras do produto</p>
      </div>
    </div>
  );
};

const PAY_METHODS = [
  { key: 'dinheiro', label: 'Dinheiro', icon: '💵' },
  { key: 'pix', label: 'PIX', icon: '⚡' },
  { key: 'debito', label: 'Debito', icon: '💳' },
  { key: 'credito', label: 'Credito', icon: '💳' },
  { key: 'em_aberto', label: 'Em Aberto', icon: '📅', requiresDueDate: true },
  { key: 'credito_devolucao', label: 'Cred. Devol.', icon: '↩️' },
  { key: 'cashback', label: 'Cashback', icon: '🪙', requiresCashback: true },
];
const PAY_LABEL = Object.fromEntries(PAY_METHODS.map(m => [m.key, m.label]));

const fmtBRL = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* iter77bo: PaymentBadge — exibe forma(s) de pagamento de uma venda.
   - 1 forma: mostra apenas o label (ex: "Dinheiro").
   - 2+ formas: mostra a primeira + chevron "+N" com tooltip listando todas + valores. */
const PaymentBadge = ({ sale }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = React.useRef(null);
  const pays = Array.isArray(sale?.payments) ? sale.payments.filter(p => p && (parseFloat(p.amount) || 0) > 0) : [];
  React.useEffect(() => {
    if (!open) return;
    const close = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  if (pays.length === 0) {
    // iter77ct.74: prioriza o NOME customizado da forma quando disponivel na venda
    return <span className="text-xs text-slate-600">{sale?.payment_method || PAY_LABEL[(sale?.payment_method || '').toLowerCase()] || '—'}</span>;
  }
  if (pays.length === 1) {
    const m = (pays[0].method || '').toLowerCase();
    // iter77ct.74: p.name (nome custom) tem prioridade sobre PAY_LABEL (type generico)
    return <span className="text-xs text-slate-700 font-medium">{pays[0].name || PAY_LABEL[m] || m || '—'}</span>;
  }
  const first = pays[0];
  const firstM = (first.method || '').toLowerCase();
  return (
    <span ref={wrapperRef} className="relative inline-flex items-center gap-1 text-xs" data-testid="payment-badge-multi">
      <span className="text-slate-700 font-medium">{first.name || PAY_LABEL[firstM] || firstM}</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-violet-100 hover:bg-violet-200 text-violet-700 text-[10px] font-black"
        title="Ver todas as formas de pagamento"
        data-testid="payment-badge-toggle"
      >
        +{pays.length - 1} <ChevronDown size={10} />
      </button>
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 bg-white border border-slate-200 rounded-xl shadow-2xl py-1.5 px-2 min-w-[200px]" data-testid="payment-badge-tooltip">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider px-1 mb-1">Pagamentos ({pays.length})</p>
          {pays.map((p, idx) => (
            <div key={idx} className="flex items-center justify-between gap-3 px-1 py-1 text-xs">
              <span className="text-slate-700 font-medium">{p.name || PAY_LABEL[(p.method || '').toLowerCase()] || p.method}</span>
              <span className="font-mono font-bold text-slate-900">{fmtBRL(p.amount)}</span>
            </div>
          ))}
          <div className="border-t border-slate-100 mt-1 pt-1 px-1 flex items-center justify-between text-xs">
            <span className="text-slate-500 font-bold">Total</span>
            <span className="font-mono font-black text-violet-700">{fmtBRL(pays.reduce((a, p) => a + (parseFloat(p.amount) || 0), 0))}</span>
          </div>
        </div>
      )}
    </span>
  );
};


/* ========== PRICE TABLES (Tabela de Precos) ========== */
const PriceTablesPage = () => {
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [preview, setPreview] = useState(null);
  const blankForm = { name: '', description: '', percent_global: 0, category_overrides: [], product_overrides: [], is_default: false, available_in_pdv: true, available_in_pedido: true, available_in_catalog: false, company_ids: [], threshold_type: 'none', threshold_value: 0 };
  const [form, setForm] = useState(blankForm);
  // iter77ct.6: add products dialog
  const [addMode, setAddMode] = useState('all'); // all | category | supplier | search
  const [addCategoryId, setAddCategoryId] = useState('');
  const [addSupplierId, setAddSupplierId] = useState('');
  const [addSearch, setAddSearch] = useState('');
  const [adding, setAdding] = useState(false);
  // iter77ct.6: bulk apply controls
  const [bulkMode, setBulkMode] = useState('percent'); // percent | value
  const [bulkValue, setBulkValue] = useState('');
  const [bulkOp, setBulkOp] = useState('increase'); // increase | decrease
  // iter77ct.93: melhorias UX na lista de produtos
  const [productFilter, setProductFilter] = useState(''); // pesquisa dentro da lista
  const [sortBy, setSortBy] = useState(null); // sku|name|base|new|percent
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (k) => {
    if (sortBy === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(k); setSortDir('asc'); }
  };
  // Draft de edicao inline (mantem string do input para nao "buggar" com re-render
  // durante digitacao). Formato: { [product_id]: { price?: '12,50', percent?: '10' } }
  const [drafts, setDrafts] = useState({});
  // Modal de selecao quando busca traz varios produtos
  const [pickerCandidates, setPickerCandidates] = useState([]);
  const [pickerSelected, setPickerSelected] = useState(() => new Set());

  const fetchTables = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c, sup] = await Promise.all([
        axios.get(`${ERP}/price-tables`, { withCredentials: true }),
        axios.get(`${API}/categories`, { withCredentials: true }),
        axios.get(`${ERP}/suppliers`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
      ]);
      setTables(t.data.data || []);
      setCategories(c.data.data || c.data || []);
      setSuppliers(sup.data?.data || []);
    } catch { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTables(); }, [fetchTables]);

  const openCreate = () => { setEditing(null); setForm(blankForm); setShowForm(true); };
  const openEdit = async (t) => {
    setEditing(t);
    // iter77ct.6: enriquece product_overrides com name/sku/base_price para exibicao (caso salvo antes sem cache)
    let po = t.product_overrides || [];
    const missingIds = po.filter(p => !p.name || !p.base_price).map(p => p.product_id).filter(Boolean);
    if (missingIds.length > 0) {
      try {
        const { data } = await axios.get(`${ERP}/price-tables/product-source?mode=ids&ids=${missingIds.join(',')}`, { withCredentials: true });
        const byId = Object.fromEntries((data?.data || []).map(x => [x.id, x]));
        po = po.map(p => ({ ...p, name: p.name || byId[p.product_id]?.name || '', sku: p.sku || byId[p.product_id]?.sku || '', base_price: p.base_price || byId[p.product_id]?.sale_price || 0 }));
      } catch { /* ignore */ }
    }
    setForm({
      name: t.name || '',
      description: t.description || '',
      percent_global: t.percent_global ?? 0,
      category_overrides: t.category_overrides || [],
      product_overrides: po,
      is_default: !!t.is_default,
      available_in_pdv: t.available_in_pdv !== false,
      available_in_pedido: t.available_in_pedido !== false,
      available_in_catalog: !!t.available_in_catalog,
      company_ids: t.company_ids || [],
      // iter156cz: threshold "Acima de:"
      threshold_type: t.threshold_type || 'none',
      threshold_value: t.threshold_value ?? 0,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nome obrigatorio'); return; }
    try {
      const payload = {
        ...form,
        percent_global: parseFloat(form.percent_global) || 0,
        // iter156cz: normaliza threshold — se type=none, zera value pra 0.
        threshold_type: (form.threshold_type && form.threshold_type !== 'none') ? form.threshold_type : null,
        threshold_value: (form.threshold_type && form.threshold_type !== 'none') ? (parseFloat(form.threshold_value) || 0) : 0,
        category_overrides: (form.category_overrides || []).filter(co => co.category_id).map(co => ({ category_id: co.category_id, percent: parseFloat(co.percent) || 0 })),
        // iter77ct.6: persiste product_overrides com percent + cache (name/sku/base_price)
        product_overrides: (form.product_overrides || []).map(p => ({
          product_id: p.product_id,
          percent: parseFloat(p.percent) || 0,
          name: p.name || '',
          sku: p.sku || '',
          base_price: parseFloat(p.base_price) || 0,
        })),
      };
      if (editing) {
        await axios.put(`${ERP}/price-tables/${editing.id}`, payload, { withCredentials: true });
        toast.success('Tabela atualizada');
      } else {
        await axios.post(`${ERP}/price-tables`, payload, { withCredentials: true });
        toast.success('Tabela criada');
      }
      setShowForm(false);
      fetchTables();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const del = async (t) => {
    if (!(await confirmDialog({ title: `Excluir tabela "${t.name}"?`, confirmLabel: 'Confirmar', danger: true }))) return;
    try { await axios.delete(`${ERP}/price-tables/${t.id}`, { withCredentials: true }); toast.success('Excluida'); fetchTables(); }
    catch { toast.error('Erro'); }
  };

  const loadPreview = async (id) => {
    setPreviewId(id);
    setPreview(null);
    try { const { data } = await axios.get(`${ERP}/price-tables/${id}/preview`, { withCredentials: true }); setPreview(data); }
    catch { toast.error('Erro ao gerar preview'); }
  };

  const addCatOverride = () => setForm(f => ({ ...f, category_overrides: [...(f.category_overrides || []), { category_id: '', percent: 0 }] })); // eslint-disable-line no-unused-vars
  const updateCatOverride = (idx, patch) => setForm(f => ({ ...f, category_overrides: f.category_overrides.map((co, i) => i === idx ? { ...co, ...patch } : co) })); // eslint-disable-line no-unused-vars
  const removeCatOverride = (idx) => setForm(f => ({ ...f, category_overrides: f.category_overrides.filter((_, i) => i !== idx) })); // eslint-disable-line no-unused-vars
  // (Handlers acima ficam como no-op p/ backwards-compat de tabelas antigas — nao expostos na UI apos iter77ct.7)

  // iter77ct.6: gerencia lista de produtos com preco por-item (guarda percent + cache de nome/preco base)
  const addProductsToTable = async () => {
    setAdding(true);
    try {
      const params = new URLSearchParams();
      params.set('mode', addMode);
      if (addMode === 'category' && addCategoryId) params.set('category_id', addCategoryId);
      if (addMode === 'supplier' && addSupplierId) params.set('supplier_id', addSupplierId);
      if (addMode === 'search' && addSearch.trim()) params.set('search', addSearch.trim());
      const { data } = await axios.get(`${ERP}/price-tables/product-source?${params}`, { withCredentials: true });
      const fetched = data?.data || [];
      if (fetched.length === 0) { toast.info('Nenhum produto encontrado'); return; }
      const existing = new Set((form.product_overrides || []).map(p => p.product_id));
      const candidates = fetched.filter(p => !existing.has(p.id));
      if (candidates.length === 0) { toast.info('Todos ja estao na tabela'); return; }
      // iter77ct.93: na busca por texto, se ha mais de um match, abre picker para o usuario
      // escolher quais adicionar (evita adicionar tudo com o mesmo nome sem querer).
      if (addMode === 'search' && candidates.length > 1) {
        setPickerCandidates(candidates);
        setPickerSelected(new Set());
        return;
      }
      commitAdditions(candidates);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao buscar produtos'); }
    finally { setAdding(false); }
  };
  const commitAdditions = (selectedList) => {
    if (!selectedList || selectedList.length === 0) return;
    const additions = selectedList.map(p => ({
      product_id: p.id,
      name: p.name,
      sku: p.sku,
      base_price: p.sale_price,
      percent: 0,
      _edited: false,
    }));
    setForm(f => ({ ...f, product_overrides: [...(f.product_overrides || []), ...additions] }));
    toast.success(`${additions.length} produto(s) adicionado(s)`);
  };
  const confirmPickerSelection = () => {
    const chosen = pickerCandidates.filter(c => pickerSelected.has(c.id));
    if (chosen.length === 0) { toast.error('Selecione ao menos um produto'); return; }
    commitAdditions(chosen);
    setPickerCandidates([]);
    setPickerSelected(new Set());
  };
  const togglePickerItem = (id) => {
    setPickerSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const removeProductOverride = (product_id) => setForm(f => ({ ...f, product_overrides: (f.product_overrides || []).filter(p => p.product_id !== product_id) }));
  const clearProductOverrides = async () => {
    if ((form.product_overrides || []).length === 0) return;
    if (!(await confirmDialog({ title: 'Remover todos os produtos da tabela?', confirmLabel: 'Remover', danger: true }))) return;
    setForm(f => ({ ...f, product_overrides: [] }));
  };
  // Edicao por linha: atualiza percent (calcula novo preco) ou new_price (calcula percent)
  const editProductPercent = (product_id, percent) => {
    const pct = parseFloat(String(percent).replace(',', '.'));
    setForm(f => ({ ...f, product_overrides: (f.product_overrides || []).map(p => p.product_id === product_id ? { ...p, percent: Number.isFinite(pct) ? pct : 0, _edited: true } : p) }));
  };
  const editProductPrice = (product_id, new_price) => {
    const np = parseFloat(String(new_price).replace(',', '.'));
    setForm(f => ({ ...f, product_overrides: (f.product_overrides || []).map(p => {
      if (p.product_id !== product_id) return p;
      const base = Number(p.base_price) || 0;
      const pct = base > 0 && Number.isFinite(np) ? ((np - base) / base) * 100 : 0;
      return { ...p, percent: Math.round(pct * 100) / 100, _edited: true };
    }) }));
  };
  // iter77ct.93: commit-on-blur — enquanto o usuario digita, mantemos apenas o texto
  // no draft (nao mexemos no form). Isso resolve o "bug" do input pulando/formatando
  // toda vez que digitava uma letra (ex: "12,5" nao aceitava a virgula, ou "10" caia
  // pra "10.00" antes de terminar). Ao dar Tab/Enter/click fora, comitamos.
  const setDraft = (product_id, key, value) => {
    setDrafts(d => ({ ...d, [product_id]: { ...(d[product_id] || {}), [key]: value } }));
  };
  const commitDraftPrice = (product_id) => {
    const raw = drafts[product_id]?.price;
    if (raw === undefined || raw === '') { setDrafts(d => { const n = { ...d }; delete n[product_id]; return n; }); return; }
    editProductPrice(product_id, raw);
    setDrafts(d => { const n = { ...d }; if (n[product_id]) { const { price: _p, ...rest } = n[product_id]; if (Object.keys(rest).length) n[product_id] = rest; else delete n[product_id]; } return n; });
  };
  const commitDraftPercent = (product_id) => {
    const raw = drafts[product_id]?.percent;
    if (raw === undefined || raw === '') { setDrafts(d => { const n = { ...d }; delete n[product_id]; return n; }); return; }
    editProductPercent(product_id, raw);
    setDrafts(d => { const n = { ...d }; if (n[product_id]) { const { percent: _pct, ...rest } = n[product_id]; if (Object.keys(rest).length) n[product_id] = rest; else delete n[product_id]; } return n; });
  };
  // Aplicacao em lote — pergunta antes se ha itens editados manualmente
  const applyBulk = async () => {
    if (!(form.product_overrides || []).length) { toast.info('Adicione produtos primeiro'); return; }
    const val = parseFloat(bulkValue);
    if (!Number.isFinite(val) || val === 0) { toast.error('Informe um valor'); return; }
    const editedCount = (form.product_overrides || []).filter(p => p._edited).length;
    let overwriteEdited = true;
    if (editedCount > 0) {
      overwriteEdited = await confirmDialog({
        title: `Existem ${editedCount} item(ns) editados manualmente`,
        message: 'Deseja SOBRESCREVER as edicoes manuais tambem?\n\n• OK: aplica em TODOS\n• Cancelar: preserva itens ja editados',
        confirmLabel: 'Sobrescrever todos',
      });
    }
    const sign = bulkOp === 'increase' ? 1 : -1;
    setForm(f => ({ ...f, product_overrides: (f.product_overrides || []).map(p => {
      if (p._edited && !overwriteEdited) return p;
      const base = Number(p.base_price) || 0;
      let newPct;
      if (bulkMode === 'percent') {
        newPct = sign * val;
      } else {
        // valor absoluto: novo preco = base + sign*val → percent
        newPct = base > 0 ? ((sign * val) / base) * 100 : 0;
      }
      return { ...p, percent: Math.round(newPct * 100) / 100, _edited: false };
    }) }));
    toast.success('Aplicado em lote');
  };

  if (loading) return <Loader />;

  return (
    <div data-testid="price-tables-page" className="space-y-4">
      <div className="bg-gradient-to-br from-[#0a0a4a] via-violet-700 to-cyan-600 rounded-2xl p-5 text-white shadow-xl shadow-violet-900/30">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest opacity-70 font-bold">Tabela de Precos</p>
            <h2 className="text-xl md:text-2xl font-black mt-0.5">{tables.length} tabela{tables.length === 1 ? '' : 's'} cadastrada{tables.length === 1 ? '' : 's'}</h2>
            <p className="text-xs opacity-80 mt-1">Adicione produtos e defina precos personalizados por item. Compartilhe com filiais para replicar a mesma tabela.</p>
          </div>
          <button onClick={openCreate} className="bg-white/15 hover:bg-white/25 backdrop-blur px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2" data-testid="new-price-table-btn">
            <Plus size={16} />Nova Tabela
          </button>
        </div>
      </div>

      {tables.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
          <DollarSign size={36} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm font-semibold text-slate-500">Nenhuma tabela de preco</p>
          <p className="text-xs text-slate-400 mt-1">Crie a primeira para aplicar descontos/acrescimos em massa.</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {tables.map(t => (
            <div key={t.id} className="bg-white border-2 border-slate-200 rounded-2xl p-4 hover:border-indigo-400 transition-colors" data-testid="price-table-card">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-black text-slate-900">{t.name}</h3>
                    {t.is_default && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid="default-badge">PADRAO</span>}
                  </div>
                  {t.description && <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>}
                </div>
                <div className="px-2 py-1 rounded-lg text-xs font-black bg-indigo-50 text-indigo-700">
                  {(t.product_overrides || []).length} produto{(t.product_overrides || []).length === 1 ? '' : 's'}
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {t.available_in_pdv && <span className="text-[10px] font-bold bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full">PDV</span>}
                {t.available_in_pedido && <span className="text-[10px] font-bold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Pedido</span>}
                {t.available_in_catalog && <span className="text-[10px] font-bold bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full">Catalogo</span>}
                {(t.company_ids || []).length > 0 && <span className="text-[10px] font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{t.company_ids.length} filial(is)</span>}
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => openEdit(t)} className="flex-1 px-3 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center gap-1" data-testid="edit-table-btn"><Edit size={11} />Editar</button>
                <button onClick={() => loadPreview(t.id)} className="flex-1 px-3 py-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg flex items-center justify-center gap-1" data-testid="preview-table-btn"><Eye size={11} />Preview</button>
                <button onClick={() => del(t)} className="px-3 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg" data-testid="del-table-btn"><Trash2 size={11} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="price-table-form">
            <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="font-black text-slate-900">{editing ? 'Editar Tabela' : 'Nova Tabela de Preco'}</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Nome *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Tabela Atacado, Promocao Junina..." className={inputClass} data-testid="table-name" />
              </div>

              {/* iter156cz: "Acima de:" — a tabela so entra em vigor quando o carrinho
                  atinge esse minimo de qtd ou valor. Se nao atinge, o PDV avisa antes
                  de finalizar e, se o usuario confirmar, reverte para preco padrao. */}
              <div className="grid grid-cols-3 gap-2 border-2 border-amber-200 rounded-xl p-3 bg-amber-50/40" data-testid="price-table-threshold-section">
                <div className="col-span-3">
                  <p className="text-[11px] font-black text-amber-700 uppercase tracking-wider">Acima de: (regra de gatilho — opcional)</p>
                  <p className="text-[10px] text-amber-600 mt-0.5">A tabela so entra em vigor quando o carrinho atinge o minimo. Ideal pra atacado (ex.: acima de 10 unidades ou R$ 500).</p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Tipo</label>
                  <select
                    value={form.threshold_type || 'none'}
                    onChange={e => setForm({ ...form, threshold_type: e.target.value })}
                    className={inputClass}
                    data-testid="threshold-type"
                  >
                    <option value="none">Sem regra</option>
                    <option value="quantity">Quantidade</option>
                    <option value="value">Valor (R$)</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">
                    Minimo {form.threshold_type === 'quantity' ? '(unidades)' : form.threshold_type === 'value' ? '(R$)' : ''}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.threshold_value ?? 0}
                    onChange={e => setForm({ ...form, threshold_value: e.target.value })}
                    disabled={!form.threshold_type || form.threshold_type === 'none'}
                    className={`${inputClass} disabled:bg-slate-100 disabled:text-slate-400`}
                    placeholder={form.threshold_type === 'quantity' ? 'Ex.: 10' : form.threshold_type === 'value' ? 'Ex.: 500,00' : 'Selecione um tipo'}
                    data-testid="threshold-value"
                  />
                </div>
              </div>

              {/* iter77ct.6: Produtos — adicao por categoria/fornecedor/todos/individual + edicao inline */}
              <div className="border-2 border-indigo-200 rounded-xl p-3 bg-indigo-50/40" data-testid="price-table-products-section">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-[11px] font-black text-indigo-700 uppercase tracking-wider">Produtos por-item (opcional)</p>
                    <p className="text-[10px] text-indigo-500 mt-0.5">Adicione produtos e defina preco individual. Se um produto NAO estiver na lista, o PDV usa o percentual global/categoria.</p>
                  </div>
                  {(form.product_overrides || []).length > 0 && (
                    <button type="button" onClick={clearProductOverrides} className="text-[10px] font-bold text-rose-700 hover:underline" data-testid="clear-products-btn">Limpar todos</button>
                  )}
                </div>
                {/* Adicao de produtos */}
                <div className="bg-white rounded-lg border border-indigo-200 p-2.5 mb-2">
                  <div className="flex flex-wrap items-end gap-2">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Adicionar por</label>
                      <select value={addMode} onChange={e => setAddMode(e.target.value)} className={`${inputClass} min-w-[130px]`} data-testid="add-mode-select">
                        <option value="all">Todos os produtos</option>
                        <option value="category">Categoria</option>
                        <option value="supplier">Fornecedor</option>
                        <option value="search">Busca (nome/SKU)</option>
                      </select>
                    </div>
                    {addMode === 'category' && (
                      <div className="flex-1 min-w-[180px]">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Categoria</label>
                        <select value={addCategoryId} onChange={e => setAddCategoryId(e.target.value)} className={inputClass} data-testid="add-category-select">
                          <option value="">Selecione...</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                    )}
                    {addMode === 'supplier' && (
                      <div className="flex-1 min-w-[180px]">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Fornecedor</label>
                        <select value={addSupplierId} onChange={e => setAddSupplierId(e.target.value)} className={inputClass} data-testid="add-supplier-select">
                          <option value="">Selecione...</option>
                          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name || s.corporate_name || s.fantasy_name}</option>)}
                        </select>
                      </div>
                    )}
                    {addMode === 'search' && (
                      <div className="flex-1 min-w-[220px]">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Buscar produto (nome/SKU)</label>
                        <input value={addSearch} onChange={e => setAddSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addProductsToTable(); } }} placeholder="Digite pelo menos 2 letras..." className={inputClass} data-testid="add-search-input" />
                      </div>
                    )}
                    <button type="button" onClick={addProductsToTable} disabled={adding || (addMode === 'category' && !addCategoryId) || (addMode === 'supplier' && !addSupplierId) || (addMode === 'search' && addSearch.trim().length < 2)} className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white text-xs font-black flex items-center gap-1" data-testid="add-products-btn">
                      {adding ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={12} />} Adicionar
                    </button>
                  </div>
                </div>
                {/* Aplicacao em lote */}
                {(form.product_overrides || []).length > 0 && (
                  <div className="bg-white rounded-lg border border-indigo-200 p-2.5 mb-2 flex flex-wrap items-end gap-2">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Operacao</label>
                      <select value={bulkOp} onChange={e => setBulkOp(e.target.value)} className={`${inputClass} min-w-[110px]`} data-testid="bulk-op-select">
                        <option value="increase">Acrescimo</option>
                        <option value="decrease">Desconto</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Tipo</label>
                      <select value={bulkMode} onChange={e => setBulkMode(e.target.value)} className={`${inputClass} min-w-[100px]`} data-testid="bulk-mode-select">
                        <option value="percent">Percentual (%)</option>
                        <option value="value">Valor (R$)</option>
                      </select>
                    </div>
                    <div className="min-w-[120px]">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Valor</label>
                      <input type="number" step="0.01" value={bulkValue} onChange={e => setBulkValue(e.target.value)} placeholder={bulkMode === 'percent' ? '10' : '5,00'} className={`${inputClass} text-right`} data-testid="bulk-value-input" />
                    </div>
                    <button type="button" onClick={applyBulk} className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-black" data-testid="apply-bulk-btn">Aplicar em lote</button>
                    <span className="text-[10px] text-slate-500 ml-auto">{(form.product_overrides || []).length} produto(s)</span>
                  </div>
                )}
                {/* Tabela de produtos */}
                {(form.product_overrides || []).length > 0 && (
                  <>
                    {/* iter77ct.93: campo de pesquisa dentro da lista */}
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <div className="relative flex-1 min-w-[220px]">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          value={productFilter}
                          onChange={e => setProductFilter(e.target.value)}
                          placeholder="Pesquisar na lista (SKU ou nome)..."
                          className={`${inputClass} pl-8`}
                          data-testid="products-filter-input"
                        />
                      </div>
                      <span className="text-[10px] text-slate-500">
                        {productFilter ? `${(form.product_overrides || []).filter(p => (p.name || '').toLowerCase().includes(productFilter.toLowerCase()) || (p.sku || '').toLowerCase().includes(productFilter.toLowerCase())).length} de ${(form.product_overrides || []).length}` : `${(form.product_overrides || []).length} produto(s)`}
                      </span>
                    </div>
                  <div className="bg-white rounded-lg border border-indigo-200 overflow-hidden max-h-96 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-indigo-100 sticky top-0">
                        <tr className="text-left text-[10px] font-black text-indigo-700 uppercase select-none">
                          {[
                            { key: 'sku',     label: 'SKU',         w: 'w-24', align: 'left' },
                            { key: 'name',    label: 'Nome',        w: '',     align: 'left' },
                            { key: 'base',    label: 'Preco Base',  w: 'w-24', align: 'right' },
                            { key: 'new',     label: 'Novo Preco',  w: 'w-28', align: 'right' },
                            { key: 'percent', label: 'Variacao %',  w: 'w-24', align: 'right' },
                          ].map(col => {
                            const active = sortBy === col.key;
                            const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '↕';
                            const alignCls = col.align === 'right' ? 'text-right' : 'text-left';
                            return (
                              <th key={col.key} onClick={() => toggleSort(col.key)}
                                className={`px-2 py-1.5 ${col.w} ${alignCls} cursor-pointer hover:bg-indigo-200 transition-colors ${active ? 'bg-indigo-200' : ''}`}
                                data-testid={`po-sort-${col.key}`}>
                                <span className="inline-flex items-center gap-1">
                                  {col.label}
                                  <span className={`text-[8px] ${active ? 'opacity-100' : 'opacity-30'}`}>{arrow}</span>
                                </span>
                              </th>
                            );
                          })}
                          <th className="px-2 py-1.5 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Filtra + ordena a lista sem mutar `form.product_overrides`
                          let list = form.product_overrides || [];
                          if (productFilter) {
                            const q = productFilter.toLowerCase();
                            list = list.filter(p => (p.name || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q));
                          }
                          if (sortBy) {
                            const dir = sortDir === 'asc' ? 1 : -1;
                            list = [...list].sort((a, b) => {
                              let va, vb;
                              const baseA = Number(a.base_price) || 0;
                              const baseB = Number(b.base_price) || 0;
                              switch (sortBy) {
                                case 'sku':     va = (a.sku || '').toLowerCase(); vb = (b.sku || '').toLowerCase(); break;
                                case 'name':    va = (a.name || '').toLowerCase(); vb = (b.name || '').toLowerCase(); break;
                                case 'base':    va = baseA; vb = baseB; break;
                                case 'new':     va = baseA * (1 + (Number(a.percent) || 0) / 100); vb = baseB * (1 + (Number(b.percent) || 0) / 100); break;
                                case 'percent': va = Number(a.percent) || 0; vb = Number(b.percent) || 0; break;
                                default: return 0;
                              }
                              if (va < vb) return -1 * dir;
                              if (va > vb) return 1 * dir;
                              return 0;
                            });
                          }
                          return list.map((p) => {
                            const base = Number(p.base_price) || 0;
                            const pct = Number(p.percent) || 0;
                            const newPrice = base * (1 + pct / 100);
                            const draftPrice = drafts[p.product_id]?.price;
                            const draftPercent = drafts[p.product_id]?.percent;
                            return (
                              <tr key={p.product_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`po-row-${p.product_id}`}>
                                <td className="px-2 py-1 font-mono text-[11px] text-slate-600 truncate max-w-[100px]" title={p.sku}>{p.sku || '-'}</td>
                                <td className="px-2 py-1 text-slate-900 font-semibold truncate" title={p.name}>{p.name || p.product_id}</td>
                                <td className="px-2 py-1 text-right text-slate-500 font-mono">{fmtBRL(base)}</td>
                                <td className="px-2 py-1 text-right">
                                  {/* iter77ct.93: type=text (aceita virgula), commit-on-blur */}
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={draftPrice !== undefined ? draftPrice : newPrice.toFixed(2)}
                                    onChange={e => setDraft(p.product_id, 'price', e.target.value)}
                                    onBlur={() => commitDraftPrice(p.product_id)}
                                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                    className="w-24 px-1.5 py-0.5 border border-slate-300 rounded text-right text-[11px] focus:border-indigo-500 focus:outline-none"
                                    data-testid={`po-price-${p.product_id}`}
                                  />
                                </td>
                                <td className="px-2 py-1 text-right">
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={draftPercent !== undefined ? draftPercent : String(pct)}
                                    onChange={e => setDraft(p.product_id, 'percent', e.target.value)}
                                    onBlur={() => commitDraftPercent(p.product_id)}
                                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                    className={`w-20 px-1.5 py-0.5 border rounded text-right text-[11px] focus:outline-none ${pct >= 0 ? 'border-emerald-300 text-emerald-700 focus:border-emerald-500' : 'border-rose-300 text-rose-700 focus:border-rose-500'} font-bold`}
                                    data-testid={`po-percent-${p.product_id}`}
                                  />
                                </td>
                                <td className="px-2 py-1 text-right">
                                  <button type="button" onClick={() => removeProductOverride(p.product_id)} className="text-rose-500 hover:text-rose-700" data-testid={`po-remove-${p.product_id}`}><Trash2 size={12} /></button>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}
              </div>

              <div className="border border-slate-200 rounded-xl p-3 space-y-2">
                <p className="text-[10px] font-black text-slate-500 uppercase mb-1">Disponibilidade</p>
                {[
                  { key: 'is_default', label: 'Tabela padrao (selecionada automaticamente ao abrir o PDV)' },
                  { key: 'available_in_pdv', label: 'Disponivel no PDV' },
                  { key: 'available_in_pedido', label: 'Disponivel em Pedidos de Venda' },
                  { key: 'available_in_catalog', label: 'Aplica no Catalogo publico (so a padrao)' },
                ].map(opt => (
                  <label key={opt.key} className="flex items-center gap-2 cursor-pointer" data-testid={`toggle-${opt.key}`}>
                    <input type="checkbox" checked={!!form[opt.key]} onChange={e => setForm({ ...form, [opt.key]: e.target.checked })} className="w-4 h-4 accent-indigo-600" />
                    <span className="text-sm text-slate-700">{opt.label}</span>
                  </label>
                ))}
              </div>

              <div className="border border-slate-200 rounded-xl p-3">
                <p className="text-[10px] font-black text-slate-500 uppercase mb-2">Compartilhamento Multi-Loja</p>
                <CompaniesPicker value={form.company_ids} onChange={(ids) => setForm({ ...form, company_ids: ids })} testId="pricetable-companies-picker" label="" />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 p-3 flex gap-2 rounded-b-2xl">
              <button onClick={() => setShowForm(false)} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 rounded-xl font-bold text-sm">Cancelar</button>
              <button onClick={save} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-xl font-bold text-sm" data-testid="save-table-btn">{editing ? 'Salvar Alteracoes' : 'Criar Tabela'}</button>
            </div>
          </div>
        </div>
      )}

      {/* iter77ct.93: PICKER MODAL — abre quando busca por texto traz mais de um produto */}
      {pickerCandidates.length > 0 && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="product-picker-modal">
            <div className="border-b border-slate-100 p-4 flex items-center justify-between">
              <div>
                <h3 className="font-black text-slate-900">Selecione os produtos</h3>
                <p className="text-xs text-slate-500">{pickerCandidates.length} correspondencias encontradas · {pickerSelected.size} selecionados</p>
              </div>
              <button onClick={() => { setPickerCandidates([]); setPickerSelected(new Set()); }} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div className="p-3 border-b border-slate-100 flex gap-2">
              <button type="button" onClick={() => setPickerSelected(new Set(pickerCandidates.map(c => c.id)))} className="text-[11px] font-bold text-indigo-700 hover:underline" data-testid="picker-select-all">Selecionar todos</button>
              <button type="button" onClick={() => setPickerSelected(new Set())} className="text-[11px] font-bold text-slate-500 hover:underline" data-testid="picker-select-none">Limpar</button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-[10px] font-black text-slate-600 uppercase">
                    <th className="px-2 py-2 w-10"></th>
                    <th className="px-2 py-2 w-24">SKU</th>
                    <th className="px-2 py-2">Nome</th>
                    <th className="px-2 py-2 w-24 text-right">Preco</th>
                  </tr>
                </thead>
                <tbody>
                  {pickerCandidates.map(p => {
                    const isSel = pickerSelected.has(p.id);
                    return (
                      <tr key={p.id}
                        onClick={() => togglePickerItem(p.id)}
                        className={`border-t border-slate-100 cursor-pointer transition-colors ${isSel ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
                        data-testid={`picker-row-${p.id}`}>
                        <td className="px-2 py-1.5 text-center">
                          <input type="checkbox" checked={isSel} onChange={() => togglePickerItem(p.id)} onClick={e => e.stopPropagation()} className="w-3.5 h-3.5 accent-indigo-600" />
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[11px] text-slate-600 truncate">{p.sku || '-'}</td>
                        <td className="px-2 py-1.5 text-slate-900 font-medium truncate" title={p.name}>{p.name}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700 font-mono">{fmtBRL(p.sale_price)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-100 p-3 flex gap-2">
              <button onClick={() => { setPickerCandidates([]); setPickerSelected(new Set()); }} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2 rounded-xl font-bold text-sm">Cancelar</button>
              <button onClick={confirmPickerSelection} disabled={pickerSelected.size === 0} className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white py-2 rounded-xl font-bold text-sm" data-testid="picker-confirm-btn">
                Adicionar {pickerSelected.size > 0 ? `(${pickerSelected.size})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PREVIEW MODAL */}
      {previewId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="preview-modal">
            <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h3 className="font-black text-slate-900">Preview de Precos</h3>
                {preview && <p className="text-xs text-slate-500">{preview.table.name} · {preview.count} produtos</p>}
              </div>
              <button onClick={() => { setPreviewId(null); setPreview(null); }} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div className="p-4">
              {!preview ? <p className="text-center py-12 text-slate-400">Carregando...</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50"><tr>
                      <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 uppercase">Produto</th>
                      <th className="px-3 py-2 text-right text-[10px] font-black text-slate-500 uppercase">Base</th>
                      <th className="px-3 py-2 text-right text-[10px] font-black text-slate-500 uppercase">%</th>
                      <th className="px-3 py-2 text-right text-[10px] font-black text-slate-500 uppercase">Preco Final</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {preview.data.map(p => (
                        <tr key={p.id} className="hover:bg-slate-50" data-testid="preview-row">
                          <td className="px-3 py-2 text-slate-900">{p.name}</td>
                          <td className="px-3 py-2 text-right text-slate-500">{fmtBRL(p.base_price)}</td>
                          <td className={`px-3 py-2 text-right font-bold ${p.percent >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{p.percent >= 0 ? '+' : ''}{p.percent}%</td>
                          <td className="px-3 py-2 text-right font-black text-indigo-700">{fmtBRL(p.adjusted_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


/* ========== iter77bo: PDV ACTIONS MULTIFUNCTION MENU ========== */
/* ========== ProductBrowserModal — iter156u: consulta rapida de itens no PDV desktop.
    Tabela com Cod.Interno (SKU) | Cod.Barras | Nome | Valor | Qtd. Disponivel
    - Campo de busca (nome/SKU/barcode/referencia)
    - Ordenacao clicavel por coluna asc/desc
    - Backend endpoint reutilizado: GET /api/erp/pdv/products?q&company_id&limit=500
    ========== */
const renderBrowserHeader = ({ k, label, right, sortKey, sortDir, toggleSort }) => {
  const isActive = sortKey === k;
  return (
    <th
      onClick={() => toggleSort(k)}
      className={`px-3 py-2.5 text-[11px] font-black uppercase text-slate-600 select-none cursor-pointer hover:bg-slate-100 ${right ? 'text-right' : 'text-left'}`}
      data-testid={`browser-col-${k}`}
    >
      <span className={`inline-flex items-center gap-1 ${isActive ? 'text-violet-700' : ''}`}>
        {label}
        <span className={`text-[9px] leading-none transition-transform ${isActive ? '' : 'opacity-30'}`}>
          {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}
        </span>
      </span>
    </th>
  );
};

const ProductBrowserModal = ({ companyId, onClose }) => {
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const inputRef = useRef(null);

  useEffect(() => {
    try { inputRef.current && inputRef.current.focus({ preventScroll: true }); } catch { /* noop */ }
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  // Debounced fetch on query change (300ms) — evita cascata em cada tecla
  useEffect(() => {
    const t = setTimeout(() => setAppliedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!companyId) return;
    let alive = true;
    setLoading(true);
    const qp = new URLSearchParams({ company_id: companyId, limit: '500' });
    if (appliedQuery) qp.set('q', appliedQuery);
    axios.get(`${ERP}/pdv/products?${qp.toString()}`, { withCredentials: true })
      .then(({ data }) => { if (alive) setRows(data?.data || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [companyId, appliedQuery]);

  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(k);
      setSortDir('asc');
    }
  };

  const sorted = React.useMemo(() => {
    const arr = [...rows];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      // Numericos: price, stock
      if (sortKey === 'price' || sortKey === 'stock') {
        return (Number(va || 0) - Number(vb || 0)) * dir;
      }
      const sa = String(va || '').toLowerCase();
      const sb = String(vb || '').toLowerCase();
      return sa.localeCompare(sb, 'pt-BR', { sensitivity: 'base' }) * dir;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const H = (k, label, right = false) => renderBrowserHeader({ k, label, right, sortKey, sortDir, toggleSort });

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
      data-testid="product-browser-overlay"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-5xl shadow-2xl overflow-hidden ring-1 ring-slate-200 max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="product-browser-modal"
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-3 bg-gradient-to-r from-violet-600 to-cyan-500 text-white flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Search size={18} />
              <h3 className="text-lg font-black tracking-tight">Consultar Itens</h3>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-1.5"
              data-testid="product-browser-close"
            >
              <X size={16} />
            </button>
          </div>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Pesquisar por nome, codigo interno (SKU) ou codigo de barras..."
              className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-white text-slate-800 placeholder:text-slate-400 text-sm font-medium outline-none focus:ring-2 focus:ring-cyan-300 shadow-sm"
              data-testid="product-browser-search"
              autoComplete="off"
              inputMode="search"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                data-testid="product-browser-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-white/85 font-semibold">
            <span>{loading ? 'Carregando...' : `${sorted.length} produto(s)`}</span>
            <span className="text-white/60">Clique na coluna para ordenar</span>
          </div>
        </div>
        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10">
              <tr>
                {H('sku', 'Cod. Interno')}
                {H('barcode', 'Cod. Barras')}
                {H('name', 'Nome')}
                {H('price', 'Valor', true)}
                {H('stock', 'Qtd. Disponivel', true)}
              </tr>
            </thead>
            <tbody>
              {sorted.map((p, i) => (
                <tr
                  key={p.id || i}
                  className={`border-b border-slate-100 hover:bg-violet-50/40 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}
                  data-testid={`product-browser-row-${p.id}`}
                >
                  <td className="px-3 py-2 font-mono text-[12px] text-slate-700 whitespace-nowrap">{p.sku || '—'}</td>
                  <td className="px-3 py-2 font-mono text-[12px] text-slate-500 whitespace-nowrap">{p.barcode || '—'}</td>
                  <td className="px-3 py-2 text-slate-900 font-semibold">{p.name}</td>
                  <td className="px-3 py-2 text-right font-bold text-teal-700 whitespace-nowrap">R$ {Number(p.price || 0).toFixed(2).replace('.', ',')}</td>
                  <td className={`px-3 py-2 text-right font-black whitespace-nowrap ${Number(p.stock || 0) > 0 ? 'text-slate-900' : 'text-rose-500'}`}>
                    {Number.isInteger(Number(p.stock || 0)) ? Number(p.stock || 0) : Number(p.stock || 0).toFixed(2).replace('.', ',')}
                  </td>
                </tr>
              ))}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-10 text-center text-slate-400 text-sm">
                    {appliedQuery ? `Nenhum produto encontrado para "${appliedQuery}".` : 'Nenhum produto cadastrado nesta filial.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


const PDVActionsMenu = ({ onOpenSummary, onOpenClosure, onOpenExpense, onOpenProductBrowser, variant = 'dark' }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const close = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const isDark = variant === 'dark';
  const btnCls = isDark
    ? 'px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold flex items-center gap-1.5'
    : 'text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-lg flex items-center gap-1';
  const opts = [
    { key: 'summary', label: 'Resumo de Vendas', icon: Calculator, desc: 'Totais por forma de pagamento', onClick: onOpenSummary, kbd: 'F8' },
    { key: 'closure', label: 'Fechamento de Caixa', icon: BarChart3, desc: 'Vendas + despesas + saldos das contas' },
    { key: 'expense', label: 'Lancamento de Despesa', icon: Minus, desc: 'Lancar despesa rapida (vai pro financeiro)' },
    // iter156u: Consulta rapida de produtos — abre modal com tabela sortable (cod interno, cod barras, nome, valor, estoque)
    { key: 'browser', label: 'Consultar Itens', icon: Search, desc: 'Buscar preco, codigos e estoque disponivel na filial' },
  ];
  const handleClick = (k) => {
    setOpen(false);
    if (k === 'summary' && onOpenSummary) onOpenSummary();
    else if (k === 'closure' && onOpenClosure) onOpenClosure();
    else if (k === 'expense' && onOpenExpense) onOpenExpense();
    else if (k === 'browser' && onOpenProductBrowser) onOpenProductBrowser();
  };
  return (
    <div ref={wrapperRef} className="relative inline-flex" data-testid="pdv-actions-menu">
      <button onClick={() => setOpen(o => !o)} className={btnCls} data-testid="pdv-actions-toggle">
        <Calculator size={isDark ? 13 : 12} />
        <span className={isDark ? '' : 'hidden sm:inline'}>Acoes</span>
        <ChevronDown size={11} />
      </button>
      {open && (
        <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl py-1 min-w-[260px] overflow-hidden" data-testid="pdv-actions-dropdown">
          {opts.map(o => {
            const Icon = o.icon;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => handleClick(o.key)}
                className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-start gap-2.5"
                data-testid={`pdv-actions-opt-${o.key}`}
              >
                <Icon size={16} className="flex-shrink-0 mt-0.5 text-teal-600" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-900 leading-tight flex items-center gap-1.5">
                    {o.label}{o.kbd && <kbd className="text-[9px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded">{o.kbd}</kbd>}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{o.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ========== iter77bo: CASH EXPENSE — Lancamento Rapido de Despesa ========== */
const CashExpenseModal = ({ companyId, onClose, onSaved }) => {
  const [accounts, setAccounts] = useState([]);
  const [chartAccounts, setChartAccounts] = useState([]);  // iter77ct.14: plano de contas
  // iter156bi: campo `document_date` sempre pre-preenchido com hoje. Continua enviado ao backend
  // e so eh gravado como retroativo quando company_settings.pdv_allow_date_edit=true.
  const [form, setForm] = useState({ description: '', amount: 0, bank_account_id: '', category: '', document_date: localDateISO(), payment_method_id: '' });
  const [paymentMethods, setPaymentMethods] = useState([]); // iter156gl
  const [allowDateEdit, setAllowDateEdit] = useState(false); // iter156bh: reusa toggle pdv_allow_date_edit
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      axios.get(`${ERP}/bank-accounts?company_id=${companyId}&selectable_only=true`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
      axios.get(`${ERP}/chart-accounts?company_id=${companyId}`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
      axios.get(`${ERP}/company-settings?company_id=${companyId}`, { withCredentials: true }).catch(() => ({ data: {} })),
      axios.get(`${ERP}/payment-methods?company_id=${companyId}`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
    ]).then(([ba, ca, cs, pm]) => {
      const accs = ba.data.data || [];
      setAccounts(accs);
      // iter156gl: formas ativas do Financeiro; pre-seleciona SOMENTE a marcada como "padrao ao lancar despesa"
      const pms = (pm.data.data || pm.data || []).filter(m => m.active !== false && m.available_in_financial !== false && !m.is_cashback && (m.type || '') !== 'credito_devolucao');
      setPaymentMethods(pms);
      const _defPm = pms.find(m => m.default_for_expenses);
      if (_defPm) setForm(p => ({ ...p, payment_method_id: _defPm.id }));
      // iter156y: se ha conta com default_expense, pre-seleciona. Senao, so pre-seleciona se ha exatamente 1 conta.
      const _def = accs.find(a => a.default_expense);
      if (_def) setForm(p => ({ ...p, bank_account_id: _def.id }));
      else if (accs.length === 1) setForm(p => ({ ...p, bank_account_id: accs[0].id }));
      // Filtra plano de contas de tipo despesa (type=expense) — se nao ha filtro claro, mostra todos
      const chart = (ca.data.data || []).filter(c => !c.type || c.type === 'expense' || c.type === 'despesa' || c.type === 'both');
      setChartAccounts(chart.length > 0 ? chart : (ca.data.data || []));
      // iter156bh: quando o toggle pdv_allow_date_edit esta ligado, libera edicao do campo data
      if (cs?.data?.pdv_allow_date_edit) setAllowDateEdit(true);
    });
  }, [companyId]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // iter77cr: amount é number direto (via CurrencyInput). Sem replace/parseFloat.
      const amt = Number(form.amount) || 0;
      if (!form.description.trim() || amt <= 0) throw new Error('Preencha descricao e valor');
      // iter77ct.24: Conta Banco e Plano de Contas passam a ser OBRIGATORIOS
      if (!form.bank_account_id) throw new Error('Selecione a Conta Banco / Caixa');
      if (!form.category) throw new Error('Selecione o Plano de Contas');
      if (!form.payment_method_id) throw new Error('Selecione a Forma de Pagamento');
      const _pm = paymentMethods.find(m => m.id === form.payment_method_id);
      await axios.post(`${ERP}/pdv/expense`, {
        company_id: companyId,
        description: form.description.trim(),
        amount: amt,
        bank_account_id: form.bank_account_id || null,
        category: form.category || null,
        payment_method: _pm?.name || null,
        payment_method_id: form.payment_method_id || null,
        // iter156bi: sempre envia document_date (hoje por default). Backend so grava como retroativa
        // se pdv_allow_date_edit=true — caso contrario ignora e usa hoje internamente.
        document_date: form.document_date || null,
      }, { withCredentials: true });
      toast.success('Despesa lancada');
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erro ao lancar despesa');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[180] bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 my-4" data-testid="cash-expense-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-slate-900 flex items-center gap-2"><Minus size={18} className="text-rose-600" />Lancamento de Despesa</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="cash-expense-close"><X size={18} /></button>
        </div>
        {/* iter156bi: Data no topo, pre-preenchida com hoje, label simples "Data".
            Sempre visivel; edicao livre so quando toggle pdv_allow_date_edit=true. */}
        <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-slate-50 to-cyan-50/40 border border-slate-200 rounded-xl">
          <Calendar size={14} className="text-slate-500 flex-shrink-0" />
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Data</span>
          <input
            type="date"
            value={form.document_date}
            onChange={e => setForm({ ...form, document_date: e.target.value })}
            max={localDateISO()}
            disabled={!allowDateEdit}
            className={`ml-auto px-2 py-1 text-sm font-bold tabular-nums bg-transparent border-0 focus:outline-none focus:ring-0 ${allowDateEdit ? 'text-slate-900 cursor-pointer' : 'text-slate-500 cursor-not-allowed'}`}
            data-testid="cash-expense-document-date"
            title={allowDateEdit ? 'Clique para alterar' : 'Ative "Permitir edicao da data" nas Configuracoes para trocar'}
          />
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Descricao</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-400 focus:outline-none" placeholder="Ex: Conta de luz, lanche do funcionario..." data-testid="cash-expense-description" autoFocus />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Valor</label>
            {/* iter77cr: máscara de moeda padronizada — digita 400 → 4,00 */}
            <CurrencyInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} className="w-full font-mono font-bold" dataTestId="cash-expense-amount" placeholder="0,00" />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Forma de Pagamento <span className="text-rose-600">*</span></label>
            <select value={form.payment_method_id} onChange={e => setForm({ ...form, payment_method_id: e.target.value })} required className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-400 focus:outline-none" data-testid="cash-expense-payment-method">
              <option value="">— Selecione a forma de pagamento —</option>
              {paymentMethods.map(m => <option key={m.id} value={m.id}>{m.name}{m.default_for_expenses ? ' (padrao)' : ''}</option>)}
            </select>
            {paymentMethods.length === 0 && <p className="text-[10px] text-amber-600 mt-1">Nenhuma forma de pagamento liberada para o Financeiro. Cadastre em Cadastros &gt; Formas de Pagamento.</p>}
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Conta Banco / Caixa <span className="text-rose-600">*</span></label>
            <select value={form.bank_account_id} onChange={e => setForm({ ...form, bank_account_id: e.target.value })} required className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-400 focus:outline-none" data-testid="cash-expense-bank">
              <option value="">— Selecione a conta —</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.bank_name} {a.account_number ? `· ${a.account_number}` : ''} {a.balance != null ? `(saldo ${fmtBRL(a.balance)})` : ''}</option>)}
            </select>
            {accounts.length === 0 && <p className="text-[10px] text-amber-600 mt-1">Nenhuma conta com &quot;Mostrar no PDV&quot; ativa. Cadastre em Cadastros &gt; Conta Banco.</p>}
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Plano de Contas <span className="text-rose-600">*</span></label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} required className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-400 focus:outline-none" data-testid="cash-expense-category">
              <option value="">— Selecione o plano de contas —</option>
              {chartAccounts.map(c => (
                <option key={c.id} value={c.code || c.id}>{c.code ? `${c.code} · ` : ''}{c.name}</option>
              ))}
            </select>
            {chartAccounts.length === 0 && <p className="text-[10px] text-amber-600 mt-1">Nenhum plano de contas cadastrado. Cadastre em Cadastros &gt; Plano de Contas.</p>}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-bold text-slate-700 hover:bg-slate-50">Cancelar</button>
          <button type="submit" disabled={busy} className="px-5 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold disabled:opacity-50" data-testid="cash-expense-submit">{busy ? 'Salvando...' : 'Lancar Despesa'}</button>
        </div>
      </form>
    </div>
  );
};

/* ========== iter77bo: CASH TRANSFER — Transferencia entre contas ========== */
const CashTransferModal = ({ companyId, accounts, onClose, onSaved }) => {
  // iter156y: autopreenchimento — usa default_transfer_from/to das contas
  const _init = React.useMemo(() => {
    const _from = (accounts || []).find(a => a.default_transfer_from);
    const _to = (accounts || []).find(a => a.default_transfer_to && (!_from || a.id !== _from.id));
    return {
      from_account_id: _from?.id || '',
      to_account_id: _to?.id || '',
      amount: 0,
      description: '',
      // iter156bi: data sempre pre-preenchida com hoje
      document_date: localDateISO(),
    };
  }, [accounts]);
  const [form, setForm] = useState(_init);
  useEffect(() => { setForm(p => ({ ...p, from_account_id: _init.from_account_id, to_account_id: _init.to_account_id })); }, [_init.from_account_id, _init.to_account_id]);
  const [allowDateEdit, setAllowDateEdit] = useState(false); // iter156bh
  useEffect(() => {
    if (!companyId) return;
    axios.get(`${ERP}/company-settings?company_id=${companyId}`, { withCredentials: true })
      .then(({ data: cs }) => { if (cs?.pdv_allow_date_edit) setAllowDateEdit(true); })
      .catch(() => {});
  }, [companyId]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // iter77cr: amount é number direto via CurrencyInput
      const amt = Number(form.amount) || 0;
      if (!form.from_account_id || !form.to_account_id || form.from_account_id === form.to_account_id) {
        throw new Error('Selecione contas de origem e destino diferentes');
      }
      if (amt <= 0) throw new Error('Valor invalido');
      await axios.post(`${ERP}/pdv/transfer`, {
        company_id: companyId,
        from_account_id: form.from_account_id,
        to_account_id: form.to_account_id,
        amount: amt,
        description: form.description || '',
        // iter156bi: sempre envia document_date (hoje por default). Backend ignora se toggle off.
        document_date: form.document_date || null,
      }, { withCredentials: true });
      toast.success('Transferencia registrada');
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erro');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[185] bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 my-4" data-testid="cash-transfer-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-slate-900 flex items-center gap-2"><Send size={18} className="text-sky-600" />Transferencia entre Contas</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="cash-transfer-close"><X size={18} /></button>
        </div>
        {/* iter156bi: Data no topo, pre-preenchida com hoje. */}
        <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-slate-50 to-sky-50/40 border border-slate-200 rounded-xl">
          <Calendar size={14} className="text-slate-500 flex-shrink-0" />
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Data</span>
          <input
            type="date"
            value={form.document_date}
            onChange={e => setForm({ ...form, document_date: e.target.value })}
            max={localDateISO()}
            disabled={!allowDateEdit}
            className={`ml-auto px-2 py-1 text-sm font-bold tabular-nums bg-transparent border-0 focus:outline-none focus:ring-0 ${allowDateEdit ? 'text-slate-900 cursor-pointer' : 'text-slate-500 cursor-not-allowed'}`}
            data-testid="cash-transfer-document-date"
            title={allowDateEdit ? 'Clique para alterar' : 'Ative "Permitir edicao da data" nas Configuracoes para trocar'}
          />
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">De (Origem)</label>
            <select value={form.from_account_id} onChange={e => setForm({ ...form, from_account_id: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="cash-transfer-from">
              <option value="">— Selecione —</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.bank_name} {a.account_number ? `· ${a.account_number}` : ''} ({fmtBRL(a.balance)})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Para (Destino)</label>
            <select value={form.to_account_id} onChange={e => setForm({ ...form, to_account_id: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="cash-transfer-to">
              <option value="">— Selecione —</option>
              {accounts.filter(a => a.id !== form.from_account_id).map(a => <option key={a.id} value={a.id}>{a.bank_name} {a.account_number ? `· ${a.account_number}` : ''} ({fmtBRL(a.balance)})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Valor</label>
            {/* iter77cr: máscara de moeda padronizada */}
            <CurrencyInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} className="w-full font-mono font-bold" dataTestId="cash-transfer-amount" placeholder="0,00" />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Descricao (opcional)</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Ex: Suprimento de caixa" data-testid="cash-transfer-description" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-bold text-slate-700 hover:bg-slate-50">Cancelar</button>
          <button type="submit" disabled={busy} className="px-5 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold disabled:opacity-50" data-testid="cash-transfer-submit">{busy ? 'Salvando...' : 'Transferir'}</button>
        </div>
      </form>
    </div>
  );
};

/* ========== iter77bo: CASH CLOSURE — Fechamento de Caixa ========== */
const CashClosureModal = ({ companyId, onClose, onOpenExpense, pdvPerms = null }) => {
  const today = localDateISO();
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  // iter156bi: chip de periodo (mesmo padrao do dashboard/PDV). "Hoje" ativo por default.
  const [periodChip, setPeriodChip] = useState('today'); // 'yesterday' | 'today' | 'custom'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showLocalExpense, setShowLocalExpense] = useState(false); // iter77co: modal despesa DENTRO do closure → refresh via load()
  const [accounts, setAccounts] = useState([]);
  const [company, setCompany] = useState(null); // iter77cj: para cabeçalho da filial no print/WhatsApp
  // iter156bu: modal drill-down do KPI "Vendidas" (lista de produtos vendidos no periodo)
  const [showPiecesModal, setShowPiecesModal] = useState(false);
  const [piecesData, setPiecesData] = useState(null);
  const [piecesLoading, setPiecesLoading] = useState(false);
  const [piecesSearch, setPiecesSearch] = useState('');
  const openPiecesSold = async () => {
    if (!companyId) return;
    setShowPiecesModal(true);
    setPiecesData(null);
    setPiecesSearch('');
    setPiecesLoading(true);
    try {
      const { data: d } = await axios.get(
        `${ERP}/pdv/closure/pieces-sold?company_id=${companyId}&date_from=${dateFrom}&date_to=${dateTo}`,
        { withCredentials: true }
      );
      setPiecesData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar itens vendidos');
    } finally { setPiecesLoading(false); }
  };
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${ERP}/pdv/closure?company_id=${companyId}&date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true });
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar fechamento');
    } finally { setLoading(false); }
  }, [companyId, dateFrom, dateTo]);
  useEffect(() => { if (companyId) load(); }, [load, companyId]);
  useEffect(() => {
    if (!companyId) return;
    axios.get(`${ERP}/bank-accounts?company_id=${companyId}`, { withCredentials: true })
      .then(({ data: d }) => setAccounts(d.data || [])).catch(() => setAccounts([]));
    // iter77cj: puxa dados da filial (nome/código) para o cabeçalho
    axios.get(`${ERP}/companies`, { withCredentials: true })
      .then(({ data: d }) => {
        const found = (d.data || []).find(c => c.id === companyId);
        if (found) setCompany(found);
      }).catch(() => {});
  }, [companyId]);

  // iter77co: WhatsApp — emojis universais (Unicode 6.0-8.0) e distintos por seção.
  // Evitados emojis novos (🔷/🔀/▫️/🟢/🔴/✨) que aparecem como � em clientes WhatsApp legado/Web.
  const shareWhatsApp = () => {
    if (!data) { toast.info('Aguarde o carregamento'); return; }
    const fmt = (v) => `R$ ${(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const branchName = company?.name || 'Fechamento';
    const branchCode = company?.code ? ` (${company.code})` : '';
    const rangeStr = dateFrom === dateTo ? dateFrom.split('-').reverse().join('/') : `${dateFrom.split('-').reverse().join('/')} a ${dateTo.split('-').reverse().join('/')}`;
    const lines = [];
    lines.push(`🧾 *FECHAMENTO DE CAIXA*`);
    lines.push(`🏬 ${branchName}${branchCode}`);
    lines.push(`📆 ${rangeStr}`);
    lines.push('');
    lines.push(`💰 *Vendas totais:* ${fmt(data.sales_total)} (${data.sales_count || 0} venda${data.sales_count === 1 ? '' : 's'} · total)`);
    if ((data.credit_devolucao_used || 0) > 0) {
      lines.push(`♻️ _Crédito devolução usado:_ ${fmt(data.credit_devolucao_used)} _(informativo — já foi recebido)_`);
    }
    // iter156gm: "em aberto" nao repete aqui — ja vem como 1a linha das formas de pagamento (a receber)
    const expensesArr = data.expenses || [];
    const expensesTotal = expensesArr.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    if (expensesArr.length) lines.push(`💸 *Despesas:* ${fmt(expensesTotal)} (${expensesArr.length})`);
    const transfersArr = data.transfers || [];
    if (transfersArr.length) {
      const transfersTotal = transfersArr.reduce((s, t) => s + (Number(t.amount) || 0), 0);
      lines.push(`🔄 *Transferências:* ${fmt(transfersTotal)} (${transfersArr.length})`);
    }
    if (data.net_result !== undefined) lines.push(`💎 *Resultado líquido:* ${fmt(data.net_result)}`);
    // iter156m: Movimentacao de pecas — so aparece se o perfil tem permissao.
    if (pdvPerms?.show_pieces_movement && (data.pieces_current != null || data.pieces_sold != null)) {
      const fmtInt = (v) => Math.round(Number(v) || 0).toLocaleString('pt-BR');
      lines.push(`📦 *Pecas:* ontem ${fmtInt(data.pieces_yesterday)} · vendidas ${fmtInt(data.pieces_sold)} · atual ${fmtInt(data.pieces_current)}`);
    }
    // Formas de pagamento
    const byPM = data.by_payment_method || [];
    const pmList = Array.isArray(byPM) ? byPM : Object.entries(byPM).map(([k, v]) => ({ method: k, total: v.total || v, count: v.count || 0 }));
    if (pmList.length) {
      lines.push('');
      lines.push('💳 *Formas de pagamento:*');
      pmList.forEach(m => {
        // iter77ct.62: usa m.name (nome cadastrado) primeiro; fallback pra label ou type
        const name = (m.name || PAY_LABEL[m.method] || m.method || '—').toUpperCase();
        lines.push(`•  ${name}: ${fmt(m.total)}${m.count ? ` (${m.count})` : ''}${m.is_open ? ' _(a receber)_' : ''}`);
      });
    }
    // Despesas detalhadas
    if (expensesArr.length) {
      lines.push('');
      lines.push('💸 *Despesas:*');
      expensesArr.forEach(e => lines.push(`•  ${e.description || 'Despesa'}: ${fmt(e.amount)}`));
    }
    // Transferências detalhadas
    if (transfersArr.length) {
      lines.push('');
      lines.push('🔄 *Transferências:*');
      const acctName = (id) => {
        const a = accounts.find(x => x.id === id);
        return a ? (a.bank_name || a.account_number || '—') : '—';
      };
      transfersArr.forEach(t => lines.push(`•  ${acctName(t.from_account_id)} → ${acctName(t.to_account_id)}: ${fmt(t.amount)}`));
    }
    // Saldos por conta — sub-emojis universais (📥 ⬆️ ⬇️ 🎯)
    const accs = data.accounts || [];
    if (accs.length) {
      lines.push('');
      lines.push('🏦 *Saldos por conta:*');
      accs.forEach(a => {
        const nm = a.bank_name || 'Conta';
        lines.push(`•  *${nm}*`);
        lines.push(`   📥 Saldo Anterior: ${fmt(a.balance_prev)}`);
        lines.push(`   ⬆️ Total de Entradas: ${fmt(a.in_total)}`);
        if ((a.out_total || 0) > 0) lines.push(`   ⬇️ Total de Saídas: ${fmt(a.out_total)}`);
        lines.push(`   🎯 Saldo Final: ${fmt(a.balance_final)}`);
      });
    }
    lines.push('');
    lines.push('_Enviado pelo sistema 8IP ERP_');
    const msg = encodeURIComponent(lines.join('\n'));
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  // iter77cj: Print — layout resumido e moderno em nova janela
  const printReceipt = () => {
    if (!data) { toast.info('Aguarde o carregamento'); return; }
    const fmt = (v) => `R$ ${(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const branchName = company?.name || 'Filial';
    const branchCode = company?.code ? ` — ${company.code}` : '';
    const rangeStr = dateFrom === dateTo ? dateFrom.split('-').reverse().join('/') : `${dateFrom.split('-').reverse().join('/')} a ${dateTo.split('-').reverse().join('/')}`;
    const byPM = data.by_payment_method || [];
    const pmList = Array.isArray(byPM) ? byPM : Object.entries(byPM).map(([k, v]) => ({ method: k, total: v.total || v, count: v.count || 0 }));
    const expensesArr = data.expenses || [];
    const expensesTotal = expensesArr.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const transfersArr = data.transfers || [];
    const transfersTotal = transfersArr.reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const accs = data.accounts || [];
    const acctName = (id) => {
      const a = accounts.find(x => x.id === id);
      return a ? (a.bank_name || a.account_number || '—') : '—';
    };
    const w = window.open('', '_blank', 'width=520,height=800');
    if (!w) { toast.error('Bloqueio de pop-up. Permita pop-ups para imprimir.'); return; }
    const pmRows = pmList.map(m => `<tr${m.is_open ? ' style="background:#faf5ff"' : ''}><td style="padding:6px 4px;font-size:12px">${(m.name || PAY_LABEL[m.method] || m.method || '—').toUpperCase()}${m.is_open ? ' <span style="font-size:9px;font-weight:800;color:#7e22ce;border:1px solid #d8b4fe;border-radius:4px;padding:1px 4px;margin-left:4px">A RECEBER</span>' : ''}</td><td style="text-align:right;padding:6px 0;font-size:12px;font-weight:700;color:${m.is_open ? '#7e22ce' : '#0f172a'}">${fmt(m.total)}</td><td style="text-align:right;padding:6px 8px;font-size:11px;color:#64748b">${m.count || 0}</td></tr>`).join('');
    const expRows = expensesArr.map(e => `<tr><td style="padding:4px 0;font-size:11px;color:#334155">${e.description || 'Despesa'}</td><td style="text-align:right;padding:4px 0;font-size:11px;color:#dc2626;font-weight:700">${fmt(e.amount)}</td></tr>`).join('');
    const trfRows = transfersArr.map(t => `<tr><td style="padding:4px 0;font-size:11px;color:#334155">${acctName(t.from_account_id)} → ${acctName(t.to_account_id)}</td><td style="text-align:right;padding:4px 0;font-size:11px;color:#0369a1;font-weight:700">${fmt(t.amount)}</td></tr>`).join('');
    const accRows = accs.map(a => `<tr><td style="padding:6px 0;font-size:12px;font-weight:700">${a.bank_name || 'Conta'}</td><td style="text-align:right;padding:6px 0;font-size:11px;color:#64748b">${fmt(a.balance_prev)}</td><td style="text-align:right;padding:6px 4px;font-size:11px;color:#059669">${fmt(a.in_total)}</td><td style="text-align:right;padding:6px 4px;font-size:11px;color:#dc2626">${fmt(a.out_total)}</td><td style="text-align:right;padding:6px 0;font-size:12px;font-weight:800;color:#0f172a">${fmt(a.balance_final)}</td></tr>`).join('');
    const html = `<!doctype html><html><head><title>Fechamento — ${branchName}</title><meta charset="utf-8"><style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#0f172a;padding:24px;max-width:480px;margin:0 auto;background:#fff}
      .hdr{border-bottom:2px solid #0f172a;padding-bottom:12px;margin-bottom:16px}
      .hdr h1{font-size:16px;letter-spacing:0.5px;text-transform:uppercase;font-weight:900}
      .hdr .branch{font-size:14px;color:#334155;margin-top:2px;font-weight:600}
      .hdr .range{font-size:11px;color:#64748b;margin-top:4px}
      .kpi{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:14px}
      .kpi .box{border:1px solid #e2e8f0;border-radius:10px;padding:8px}
      .kpi .box p{font-size:10px;color:#64748b;text-transform:uppercase;font-weight:700}
      .kpi .box .v{font-size:16px;font-weight:900;margin-top:2px}
      .kpi .box.pos .v{color:#059669}
      .kpi .box.neg .v{color:#dc2626}
      .sec{margin:14px 0}
      .sec h2{font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;font-weight:800;margin-bottom:6px;border-bottom:1px dashed #cbd5e1;padding-bottom:4px}
      table{width:100%;border-collapse:collapse}
      .foot{margin-top:16px;padding-top:12px;border-top:2px solid #0f172a;text-align:center;font-size:10px;color:#94a3b8}
      button.p{position:fixed;top:12px;right:12px;background:#0f172a;color:#fff;border:0;padding:8px 14px;border-radius:8px;font-weight:700;cursor:pointer;font-size:12px}
      @media print{button.p{display:none}}
    </style></head><body>
      <button class="p" onclick="window.print()">🖨️ Imprimir</button>
      <div class="hdr">
        <h1>Fechamento de Caixa</h1>
        <div class="branch">${branchName}${branchCode}</div>
        <div class="range">📅 ${rangeStr}</div>
      </div>
      <div class="kpi">
        <div class="box pos"><p>Vendas (total)</p><p class="v">${fmt(data.sales_total)}</p></div>
        <div class="box neg"><p>Despesas</p><p class="v">${fmt(expensesTotal)}</p></div>
        <div class="box"><p>Transferências</p><p class="v">${fmt(transfersTotal)}</p></div>
        <div class="box ${(data.net_result || 0) >= 0 ? 'pos' : 'neg'}"><p>Resultado Líquido</p><p class="v">${fmt(data.net_result || 0)}</p></div>
      </div>
      ${(data.credit_devolucao_used || 0) > 0 ? `<div style="background:#fef3c7;border:2px solid #fbbf24;border-radius:8px;padding:10px 12px;margin:10px 0;display:flex;justify-content:space-between;align-items:center;"><div><p style="font-size:9px;font-weight:800;color:#92400e;text-transform:uppercase;">♻️ Crédito Devolução Utilizado</p><p style="font-size:9px;color:#a16207;">Informativo — já havia sido recebido, não é receita</p></div><p style="font-size:14px;font-weight:900;color:#92400e;">${fmt(data.credit_devolucao_used)}</p></div>` : ''}
      ${pmList.length ? `<div class="sec"><h2>Formas de Pagamento</h2><table>${pmRows}</table></div>` : ''}
      ${expensesArr.length ? `<div class="sec"><h2>Despesas</h2><table>${expRows}</table></div>` : ''}
      ${transfersArr.length ? `<div class="sec"><h2>Transferências</h2><table>${trfRows}</table></div>` : ''}
      ${accs.length ? `<div class="sec"><h2>Saldos por Conta</h2><table><thead><tr><th style="text-align:left;font-size:10px;color:#64748b;font-weight:700;padding-bottom:4px">Conta</th><th style="text-align:right;font-size:10px;color:#64748b;font-weight:700">Ant.</th><th style="text-align:right;font-size:10px;color:#059669;font-weight:700">Entr.</th><th style="text-align:right;font-size:10px;color:#dc2626;font-weight:700">Saíd.</th><th style="text-align:right;font-size:10px;color:#0f172a;font-weight:700">Final</th></tr></thead><tbody>${accRows}</tbody></table></div>` : ''}
      <div class="foot">Gerado em ${new Date().toLocaleString('pt-BR')} — 8IP ERP</div>
      <script>setTimeout(()=>window.print(),300);</script>
    </body></html>`;
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="fixed inset-0 z-[170] bg-black/60 backdrop-blur-sm flex items-start justify-center p-3 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-4" data-testid="cash-closure-modal">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-5 gap-3 border-b border-slate-200 sticky top-0 bg-white z-10 rounded-t-2xl">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-black text-slate-900 flex items-center gap-2 flex-wrap">
              <BarChart3 size={20} className="text-emerald-600" />Fechamento de Caixa
              {/* iter156dt8: badge quando o operador tem sales_only_own — deixa claro que ele
                  ve apenas o proprio caixa (vendas, despesas, transferencias e saldos filtrados
                  pelo created_by dele mesmo) */}
              {data?.own_view && (
                <span className="text-[10px] font-black tracking-wider uppercase px-2 py-1 rounded-full bg-violet-100 text-violet-800 border border-violet-300" data-testid="closure-own-view-badge" title="Voce esta vendo apenas os lancamentos que voce mesmo criou. Peca acesso ao admin para ver o caixa completo.">
                  Visao Pessoal
                </span>
              )}
            </h2>
            <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5 truncate">{company ? `${company.name}${company.code ? ` — ${company.code}` : ''}` : 'Vendas, despesas, transferencias e saldos por conta no periodo.'}</p>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <button type="button" onClick={() => setShowTransfer(true)} className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-[11px] sm:text-xs font-bold flex items-center gap-1.5" data-testid="cash-closure-transfer-btn"><Send size={13} /><span className="hidden xs:inline sm:inline">Transferencia</span><span className="xs:hidden sm:hidden">Transf.</span></button>
            <button type="button" onClick={() => setShowLocalExpense(true)} className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[11px] sm:text-xs font-bold flex items-center gap-1.5" data-testid="cash-closure-expense-btn"><Minus size={13} />Despesa</button>
            {/* iter77cj: Imprimir/PDF em janela dedicada (layout resumido moderno) */}
            <button type="button" onClick={printReceipt} className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-[11px] sm:text-xs font-bold flex items-center gap-1.5" data-testid="cash-closure-print-btn" title="Imprimir ou salvar como PDF"><Printer size={13} /><span className="hidden sm:inline">Imprimir/PDF</span><span className="sm:hidden">PDF</span></button>
            <button type="button" onClick={shareWhatsApp} className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] sm:text-xs font-bold flex items-center gap-1.5" data-testid="cash-closure-whatsapp-btn" title="Compartilhar via WhatsApp">💬<span className="hidden sm:inline"> WhatsApp</span></button>
            <button type="button" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg ml-auto sm:ml-0" data-testid="cash-closure-close"><X size={18} /></button>
          </div>
        </div>
        <div className="p-3 sm:p-5 space-y-4">
          {/* iter156bi: Filtro modernizado — chips Dia Anterior/Hoje/Personalizado.
              Hoje ativo por default; datepickers so aparecem em Personalizado. */}
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: 'yesterday', label: 'Dia Anterior' },
              { key: 'today', label: 'Hoje' },
              { key: 'custom', label: 'Personalizado' },
            ].map(chip => {
              const active = periodChip === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => {
                    setPeriodChip(chip.key);
                    if (chip.key === 'today') {
                      setDateFrom(today); setDateTo(today);
                    } else if (chip.key === 'yesterday') {
                      const y = new Date(); y.setDate(y.getDate() - 1);
                      const iso = y.toISOString().slice(0, 10);
                      setDateFrom(iso); setDateTo(iso);
                    }
                  }}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-black uppercase tracking-wider transition-all ${active
                    ? 'bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md shadow-violet-500/30'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700'}`}
                  data-testid={`cash-closure-chip-${chip.key}`}
                >
                  {chip.label}
                </button>
              );
            })}
            {periodChip === 'custom' && (
              <div className="flex items-center gap-2 ml-1">
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-2.5 py-1 text-xs border border-slate-200 rounded-full bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none" data-testid="cash-closure-date-from" />
                <span className="text-xs text-slate-400">ate</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-2.5 py-1 text-xs border border-slate-200 rounded-full bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none" data-testid="cash-closure-date-to" />
              </div>
            )}
            <button onClick={load} className="ml-auto px-3.5 py-1.5 rounded-full bg-slate-800 hover:bg-slate-900 text-white text-xs font-black uppercase tracking-wider transition-colors" data-testid="cash-closure-refresh">Atualizar</button>
            {loading && <span className="text-xs text-slate-400">Carregando...</span>}
          </div>
          {/* KPIs */}
          {data && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <p className="text-[10px] font-bold text-emerald-700 uppercase">Vendas Totais</p>
                <p className="text-xl font-black text-emerald-900 mt-0.5">{fmtBRL(data.sales_total)}</p>
                <p className="text-[10px] text-emerald-600 mt-0.5" title="Valor total (inclui vendas em aberto/fiado, boleto, cheque) — exclui apenas crédito devolução que já havia sido recebido antes">{data.sales_count} venda(s) · total</p>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
                <p className="text-[10px] font-bold text-rose-700 uppercase">Despesas</p>
                <p className="text-xl font-black text-rose-900 mt-0.5">{fmtBRL(data.expenses_total)}</p>
                <p className="text-[10px] text-rose-600 mt-0.5">{data.expenses?.length || 0} lancamento(s)</p>
              </div>
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
                <p className="text-[10px] font-bold text-violet-700 uppercase">Resultado</p>
                <p className={`text-xl font-black mt-0.5 ${data.net_result >= 0 ? 'text-violet-900' : 'text-rose-900'}`}>{fmtBRL(data.net_result)}</p>
                <p className="text-[10px] text-violet-600 mt-0.5">Vendas - Despesas</p>
              </div>
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
                <p className="text-[10px] font-bold text-sky-700 uppercase">Saldo Caixa Local</p>
                <p className="text-xl font-black text-sky-900 mt-0.5">{fmtBRL((data.accounts || []).reduce((s, a) => s + (a.balance_final || 0), 0))}</p>
                <p className="text-[10px] text-sky-600 mt-0.5">{data.accounts?.[0]?.bank_name || 'Caixa Local'}</p>
              </div>
            </div>
          )}
          {/* iter156m: Movimentacao de pecas — linha simples, moderna, gated por permissao pdv_show_pieces_movement */}
          {data && pdvPerms?.show_pieces_movement && (
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between gap-4 flex-wrap" data-testid="closure-pieces-line">
              <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                <Package size={14} className="text-slate-600" />
                <span>Movimentacao de pecas</span>
              </div>
              <div className="flex items-center gap-5 text-sm font-light tracking-tight text-slate-800">
                <span><span className="text-slate-500 font-normal">Ontem</span> <span className="font-black tabular-nums ml-1">{Math.round(Number(data.pieces_yesterday) || 0).toLocaleString('pt-BR')}</span></span>
                <span className="text-slate-300">·</span>
                <span
                  onClick={openPiecesSold}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPiecesSold(); } }}
                  className="cursor-pointer rounded-md px-1.5 -mx-1.5 py-0.5 hover:bg-rose-100 transition-colors"
                  title="Ver lista de produtos vendidos no periodo"
                  data-testid="pieces-sold-link"
                >
                  <span className="text-rose-500 font-normal underline decoration-dotted underline-offset-2">Vendidas</span> <span className="font-black tabular-nums ml-1 text-rose-700">{Math.round(Number(data.pieces_sold) || 0).toLocaleString('pt-BR')}</span>
                </span>
                <span className="text-slate-300">·</span>
                <span><span className="text-emerald-500 font-normal">Atual</span> <span className="font-black tabular-nums ml-1 text-emerald-700">{Math.round(Number(data.pieces_current) || 0).toLocaleString('pt-BR')}</span></span>
              </div>
            </div>
          )}
          {/* iter77cq: Crédito Devolução Utilizado — informativo, fora dos totais.
              iter156gm: card "Em Aberto (Fiado)" removido daqui — aparece apenas na tabela por forma (1a linha, tag A RECEBER). */}
          {data && (data.credit_devolucao_used || 0) > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(data.credit_devolucao_used || 0) > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-3 flex items-center justify-between" data-testid="closure-credit-used-info">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-amber-100 rounded-lg"><RotateCcw size={16} className="text-amber-700" /></div>
                    <div>
                      <p className="text-[10px] font-black text-amber-800 uppercase">Crédito Devolução Usado</p>
                      <p className="text-[10px] text-amber-600 leading-tight">Já havia sido recebido — não é receita</p>
                    </div>
                  </div>
                  <p className="text-lg font-black text-amber-800">{fmtBRL(data.credit_devolucao_used)}</p>
                </div>
              )}
            </div>
          )}
          {/* By Payment Method */}
          {data?.by_payment_method?.length > 0 && (
            <div>
              <h3 className="text-sm font-black text-slate-900 mb-2 flex items-center gap-1.5"><CreditCard size={15} className="text-violet-600" />Vendas por Forma de Pagamento</h3>
              <div className="border border-slate-200 rounded-xl overflow-x-auto">
                <table className="w-full text-xs min-w-[420px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-bold text-slate-600">Forma</th>
                      <th className="px-3 py-2 text-right font-bold text-slate-600">Qtd</th>
                      <th className="px-3 py-2 text-right font-bold text-slate-600">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_payment_method.map(m => (
                      // iter77ct.62: chave unica por method_id (permite 2 PIX distintos)
                      <tr key={m.method_id || m.method} className={`border-t border-slate-100 ${m.is_open ? 'bg-purple-50/60' : ''}`} data-testid={`closure-pm-row-${m.method}`}>
                        <td className="px-3 py-2 font-medium text-slate-800">
                          {/* iter77ct.65: mostra APENAS o nome customizado (Pix Online, Pix Maquina) — o tipo generico foi removido conforme feedback do usuario */}
                          {m.name || PAY_LABEL[m.method] || m.method}
                          {m.is_open && <span className="ml-1.5 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200" title="Venda em aberto — valor ainda nao entrou no caixa">a receber</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-600">{m.count}</td>
                        <td className={`px-3 py-2 text-right font-bold ${m.is_open ? 'text-purple-700' : 'text-emerald-700'}`}>{fmtBRL(m.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Bank Accounts (extrato resumido) */}
          {data?.accounts?.length > 0 && (
            <div>
              <h3 className="text-sm font-black text-slate-900 mb-2 flex items-center gap-1.5"><Landmark size={15} className="text-sky-600" />Saldo do Caixa Local</h3>
              <div className="border border-slate-200 rounded-xl overflow-x-auto">
                <table className="w-full text-xs min-w-[600px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-bold text-slate-600">Conta</th>
                      <th className="px-3 py-2 text-right font-bold text-slate-600">Saldo Dia Anterior</th>
                      <th className="px-3 py-2 text-right font-bold text-emerald-600">Entradas</th>
                      <th className="px-3 py-2 text-right font-bold text-rose-600">Saidas</th>
                      <th className="px-3 py-2 text-right font-bold text-slate-700">Saldo Final</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.accounts.map(a => (
                      <tr key={a.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-medium text-slate-800">{a.bank_name}{a.account_number ? ` · ${a.account_number}` : ''}</td>
                        <td className="px-3 py-2 text-right text-slate-600 font-mono">{fmtBRL(a.balance_prev)}</td>
                        <td className="px-3 py-2 text-right text-emerald-700 font-mono">+{fmtBRL(a.in_total)}</td>
                        <td className="px-3 py-2 text-right text-rose-700 font-mono">-{fmtBRL(a.out_total)}</td>
                        <td className="px-3 py-2 text-right font-bold font-mono text-slate-900">{fmtBRL(a.balance_final)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Expenses list */}
          {data?.expenses?.length > 0 && (
            <div>
              <h3 className="text-xs font-black text-slate-900 mb-1.5 flex items-center gap-1.5"><Minus size={13} className="text-rose-600" />Despesas Lançadas no PDV ({data.expenses.length})</h3>
              <div className="border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-[11px] min-w-[520px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-1 text-left font-bold text-slate-600">Data</th>
                      <th className="px-2 py-1 text-left font-bold text-slate-600">Descrição</th>
                      <th className="px-2 py-1 text-left font-bold text-slate-600">Conta</th>
                      <th className="px-2 py-1 text-right font-bold text-slate-600">Valor</th>
                      <th className="px-2 py-1 text-right font-bold text-slate-600 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expenses.map(e => {
                      const acc = accounts.find(a => a.id === e.bank_account_id);
                      const canDel = !!(window.__canDo && window.__canDo('delete', 'sales'));
                      return (
                        <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-2 py-1 text-slate-500">{e.paid_date}</td>
                          <td className="px-2 py-1 text-slate-800">{e.description}</td>
                          <td className="px-2 py-1 text-slate-600">{acc ? acc.bank_name : '—'}</td>
                          <td className="px-2 py-1 text-right text-rose-700 font-bold font-mono">{fmtBRL(e.amount)}</td>
                          <td className="px-2 py-1 text-right">
                            {canDel && (
                              <button
                                onClick={async () => {
                                  if (!(await confirmDialog({ title: 'Excluir esta despesa?\n\nO valor será revertido no saldo bancário.', confirmLabel: 'Confirmar', danger: true }))) return;
                                  try {
                                    const { data: r } = await axios.delete(`${ERP}/pdv/expense/${e.id}`, { withCredentials: true });
                                    toast.success(r.message || 'Excluído');
                                    load();
                                  } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
                                }}
                                className="p-1 text-slate-400 hover:text-rose-600 rounded"
                                title="Excluir despesa"
                                data-testid={`del-expense-${e.id}`}
                              >
                                <Trash2 size={11} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Transfers */}
          {data?.transfers?.length > 0 && (
            <div>
              <h3 className="text-xs font-black text-slate-900 mb-1.5 flex items-center gap-1.5"><Send size={13} className="text-sky-600" />Transferências entre Contas ({data.transfers.length})</h3>
              <div className="border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-[11px] min-w-[520px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-1 text-left font-bold text-slate-600">Data</th>
                      <th className="px-2 py-1 text-left font-bold text-slate-600">Origem</th>
                      <th className="px-2 py-1 text-left font-bold text-slate-600">Destino</th>
                      <th className="px-2 py-1 text-right font-bold text-slate-600">Valor</th>
                      <th className="px-2 py-1 text-right font-bold text-slate-600 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transfers.map(t => {
                      const from = accounts.find(a => a.id === t.from_account_id);
                      const to = accounts.find(a => a.id === t.to_account_id);
                      const canDel = !!(window.__canDo && window.__canDo('delete', 'sales'));
                      return (
                        <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-2 py-1 text-slate-500">{t.paid_date}</td>
                          <td className="px-2 py-1 text-slate-700">{from?.bank_name || '—'}</td>
                          <td className="px-2 py-1 text-slate-700">{to?.bank_name || '—'}</td>
                          <td className="px-2 py-1 text-right text-sky-700 font-bold font-mono">{fmtBRL(t.amount)}</td>
                          <td className="px-2 py-1 text-right">
                            {canDel && (
                              <button
                                onClick={async () => {
                                  if (!(await confirmDialog({ title: 'Excluir esta transferência?\n\nO valor será revertido nas duas contas.', confirmLabel: 'Confirmar', danger: true }))) return;
                                  try {
                                    const { data: r } = await axios.delete(`${ERP}/pdv/transfer/${t.id}`, { withCredentials: true });
                                    toast.success(r.message || 'Excluída');
                                    load();
                                  } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
                                }}
                                className="p-1 text-slate-400 hover:text-rose-600 rounded"
                                title="Excluir transferência"
                                data-testid={`del-transfer-${t.id}`}
                              >
                                <Trash2 size={11} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {data && data.sales_count === 0 && (data.expenses?.length || 0) === 0 && (
            <div className="text-center py-8 text-sm text-slate-400">Nenhum movimento no periodo selecionado.</div>
          )}
        </div>
      </div>
      {showTransfer && (
        <CashTransferModal
          companyId={companyId}
          accounts={accounts.filter(a => a.selectable_in_pdv ?? a.show_in_pdv)}
          onClose={() => setShowTransfer(false)}
          onSaved={() => { setShowTransfer(false); load(); }}
        />
      )}
      {/* iter77co: modal despesa aninhado — igual ao transfer, refresh automático via load() */}
      {showLocalExpense && (
        <CashExpenseModal
          companyId={companyId}
          onClose={() => setShowLocalExpense(false)}
          onSaved={() => { setShowLocalExpense(false); load(); }}
        />
      )}
      {/* iter156bu: modal drill-down do KPI "Vendidas" — lista de produtos vendidos */}
      {showPiecesModal && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" data-testid="pieces-sold-modal">
          <div className="bg-white w-full max-w-4xl max-h-[92vh] overflow-hidden rounded-t-2xl md:rounded-2xl shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-rose-600 to-orange-500 px-5 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3 text-white">
                <Package size={20} />
                <div>
                  <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Produtos Vendidos no Periodo</p>
                  <h3 className="text-lg font-black">
                    {piecesData ? `${piecesData.distinct_products} produto${piecesData.distinct_products === 1 ? '' : 's'} · ${Math.round(piecesData.total_qty).toLocaleString('pt-BR')} pecas` : 'Carregando...'}
                  </h3>
                </div>
              </div>
              <button onClick={() => setShowPiecesModal(false)} className="text-white/80 hover:text-white" data-testid="pieces-modal-close"><X size={18} /></button>
            </div>
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <div className="relative">
                <input
                  value={piecesSearch}
                  onChange={e => setPiecesSearch(e.target.value)}
                  placeholder="Buscar produto por nome ou SKU..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="pieces-search"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {piecesLoading ? (
                <div className="p-10 flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-rose-600 border-t-transparent" />
                </div>
              ) : !piecesData || piecesData.items.length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-400" data-testid="pieces-empty">
                  Nenhum produto vendido no periodo selecionado.
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr className="text-left text-slate-500 font-bold uppercase text-[10px]">
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">SKU</th>
                      <th className="px-3 py-2">Produto</th>
                      <th className="px-3 py-2 text-right">Qtde</th>
                      <th className="px-3 py-2 text-right">Vl Unit</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2 text-center">Vendas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {piecesData.items
                      .filter(it => {
                        if (!piecesSearch) return true;
                        const q = piecesSearch.toLowerCase();
                        return (it.product_name || '').toLowerCase().includes(q) || (it.sku || '').toLowerCase().includes(q);
                      })
                      .map((it, i) => (
                        <tr key={it.product_id || i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`pieces-row-${i}`}>
                          <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                          <td className="px-3 py-2 font-mono text-slate-600">{it.sku || '—'}</td>
                          <td className="px-3 py-2 text-slate-800 font-semibold">{it.product_name}</td>
                          <td className="px-3 py-2 text-right font-mono font-black text-rose-700 tabular-nums">{Number(it.quantity).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}{it.unit ? <span className="text-[9px] text-slate-400 ml-0.5">{it.unit}</span> : null}</td>
                          <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{fmtBRL(it.unit_price_last)}</td>
                          <td className="px-3 py-2 text-right font-bold text-emerald-700 tabular-nums">{fmtBRL(it.total_value)}</td>
                          <td className="px-3 py-2 text-center text-slate-500">{it.sales_count}</td>
                        </tr>
                      ))}
                  </tbody>
                  {piecesData && (
                    <tfoot className="bg-slate-100 font-black">
                      <tr>
                        <td colSpan={3} className="px-3 py-2.5 text-right text-slate-700 uppercase text-[10px]">Totais</td>
                        <td className="px-3 py-2.5 text-right text-rose-700 tabular-nums">{Number(piecesData.total_qty).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</td>
                        <td></td>
                        <td className="px-3 py-2.5 text-right text-emerald-700 tabular-nums">{fmtBRL(piecesData.total_value)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>
            <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex justify-between items-center text-xs">
              <span className="text-slate-500">Periodo: <strong>{piecesData?.date_from ? piecesData.date_from.split('-').reverse().join('/') : '—'}</strong> a <strong>{piecesData?.date_to ? piecesData.date_to.split('-').reverse().join('/') : '—'}</strong></span>
              <button onClick={() => setShowPiecesModal(false)} className="px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg font-bold" data-testid="pieces-close-btn">Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


/* ========== CASH SUMMARY (Resumo de Vendas) ========== */
const CashSummaryModal = ({ companyId, onClose }) => {
  const today = localDateISO();
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data: d } = await axios.get(`${ERP}/sales/summary?${params.toString()}`, { withCredentials: true });
      setData(d);
    } catch (err) { toast.error('Erro ao carregar resumo'); }
    finally { setLoading(false); }
  }, [companyId, dateFrom, dateTo]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const total = data?.total || 0;
  const methods = data?.by_payment_method || [];
  const maxMethod = methods.reduce((mx, m) => Math.max(mx, m.total), 0) || 1;

  const handlePrint = () => {
    const w = window.open('', '_blank', 'width=420,height=600');
    if (!w) return;
    const rows = methods.map(m => `<tr><td style="padding:4px 0">${(m.name || PAY_LABEL[m.method] || m.method || '').toUpperCase()}</td><td style="text-align:right;padding:4px 0">${fmtBRL(m.total)}</td><td style="text-align:right;padding:4px 8px;color:#666">${m.count}</td></tr>`).join('');
    w.document.write(`<html><head><title>Resumo Vendas</title></head><body style="font-family:monospace;padding:16px;max-width:340px"><h3 style="margin:0 0 4px;text-align:center">RESUMO DE VENDAS</h3><p style="text-align:center;margin:0 0 12px;color:#666;font-size:12px">${dateFrom} a ${dateTo}</p><hr/><table style="width:100%;border-collapse:collapse"><thead><tr><th style="text-align:left;padding:4px 0;font-size:12px">Forma</th><th style="text-align:right;font-size:12px">Total</th><th style="text-align:right;padding-left:8px;font-size:12px">Qtd</th></tr></thead><tbody>${rows}</tbody></table><hr/><div style="display:flex;justify-content:space-between;padding:4px 0"><strong>TOTAL</strong><strong>${fmtBRL(total)}</strong></div><div style="display:flex;justify-content:space-between;padding:2px 0;font-size:12px"><span>Vendas</span><span>${data?.sales_count || 0}</span></div><div style="display:flex;justify-content:space-between;padding:2px 0;font-size:12px"><span>Itens</span><span>${data?.items_count || 0}</span></div><div style="display:flex;justify-content:space-between;padding:2px 0;font-size:12px"><span>Desconto</span><span>${fmtBRL(data?.discount_total || 0)}</span></div></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 250);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="cash-summary-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center"><Calculator size={18} className="text-teal-600" /></div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Resumo de Vendas</h3>
              <p className="text-[11px] text-gray-400">Fechamento por forma de pagamento</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center" data-testid="close-summary"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] font-bold text-gray-400 uppercase">De</label><input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={inputClass} data-testid="summary-date-from" /></div>
            <div><label className="text-[10px] font-bold text-gray-400 uppercase">Ate</label><input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={inputClass} data-testid="summary-date-to" /></div>
          </div>
          {loading ? (
            <div className="py-12 text-center text-gray-400 text-sm">Carregando...</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="bg-gradient-to-br from-blue-600 to-violet-600 text-white rounded-2xl p-3 shadow-md shadow-violet-600/25"><p className="text-[10px] font-semibold opacity-80 uppercase">Total</p><p className="text-xl font-black mt-0.5" data-testid="summary-total">{fmtBRL(total)}</p></div>
                <div className="bg-white border border-gray-200 rounded-2xl p-3"><p className="text-[10px] font-semibold text-gray-400 uppercase">Vendas</p><p className="text-xl font-black text-gray-900 mt-0.5">{data?.sales_count || 0}</p></div>
                <div className="bg-white border border-gray-200 rounded-2xl p-3"><p className="text-[10px] font-semibold text-gray-400 uppercase">Itens</p><p className="text-xl font-black text-gray-900 mt-0.5">{data?.items_count || 0}</p></div>
                <div className="bg-white border border-gray-200 rounded-2xl p-3"><p className="text-[10px] font-semibold text-gray-400 uppercase">Desconto</p><p className="text-xl font-black text-rose-600 mt-0.5">{fmtBRL(data?.discount_total || 0)}</p></div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Por forma de pagamento</p>
                {methods.length === 0 ? (
                  <div className="bg-gray-50 border border-dashed border-gray-200 rounded-2xl py-8 text-center text-sm text-gray-400">Nenhuma venda no periodo</div>
                ) : (
                  <div className="space-y-2" data-testid="summary-methods-list">
                    {methods.map(m => {
                      const pct = (m.total / maxMethod) * 100;
                      return (
                        <div key={m.method_id || m.method} className="bg-white border border-gray-200 rounded-xl p-3" data-testid={`summary-row-${m.method_id || m.method}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-bold text-gray-900">{m.name || PAY_LABEL[m.method] || m.method}</span>
                            <span className="text-base font-black text-teal-700">{fmtBRL(m.total)}</span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-1.5"><div className="h-full bg-teal-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                          <div className="flex items-center justify-between text-[11px] text-gray-400">
                            <span>{m.count} venda{m.count === 1 ? '' : 's'}</span>
                            <span>{m.items} item{m.items === 1 ? '' : 's'}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-gray-100 p-3 flex gap-2 rounded-b-2xl">
          <button onClick={handlePrint} disabled={loading || methods.length === 0} className="flex-1 bg-white border border-gray-200 hover:border-teal-400 text-gray-700 hover:text-teal-700 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="print-summary"><Printer size={14} />Imprimir</button>
          <button onClick={onClose} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white py-2.5 rounded-xl font-semibold text-sm">Fechar</button>
        </div>
      </div>
    </div>
  );
};

/* ========== OFFLINE QUEUE (localStorage) ========== */
const QUEUE_KEY = 'pdv_offline_queue_v1';
const readQueue = () => { try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { return []; } };
const writeQueue = (q) => { try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch { /* ignore */ } };
// iter156dt4: injeta offline_sale_at com timestamp REAL do momento em que o operador
// finalizou a venda no dispositivo. Sem isso, o backend usa datetime.now() quando a fila
// eh descarregada — todas as vendas caiam no mesmo minuto de sync (confusao no relatorio).
const enqueueSale = (payload) => {
  const q = readQueue();
  const nowIso = new Date().toISOString();
  const payloadWithTs = { ...payload, offline_sale_at: payload.offline_sale_at || nowIso };
  q.push({ id: `off_${Date.now()}_${Math.random().toString(36).slice(2,8)}`, payload: payloadWithTs, ts: Date.now() });
  writeQueue(q);
};
const flushQueue = async (onSuccess) => {
  const q = readQueue();
  if (q.length === 0) return { sent: 0, remaining: 0 };
  let sent = 0;
  const remaining = [];
  for (const item of q) {
    try {
      const { data } = await axios.post(`${ERP}/pdv/sale`, item.payload, { withCredentials: true, timeout: 8000 });
      sent += 1;
      if (onSuccess) onSuccess(data);
    } catch {
      remaining.push(item);
    }
  }
  writeQueue(remaining);
  return { sent, remaining: remaining.length };
};


/* ========== PostSaleModal — Pos-venda: Sair ou Emitir cupom (PDF + WhatsApp) ========== */
const PostSaleModal = ({ sale, slug, onClose, onStay, onExit, allowNFe = false, allowNFCe = false, natops = [], defaultNatopId = '', printSaleModel = '' }) => {
  const [busy, setBusy] = useState(false);
  const [invoiceBusy, setInvoiceBusy] = useState(null); // 'nfe' | 'nfce' | null
  const [natopId, setNatopId] = useState(defaultNatopId || '');
  // iter77bf: auto-acao recebida do split-button "Finalizar Venda". Executa logo apos abrir o modal.
  const autoAction = sale?._autoAction;
  const [autoFired, setAutoFired] = useState(false);

  const openCupom = async () => {
    setBusy(true);
    try {
      const { data: blob } = await axios.get(`${ERP}/sales/${sale.id || sale._id}/receipt-pdf`, { responseType: 'blob', withCredentials: true });
      const objectUrl = window.URL.createObjectURL(blob);
      const win = window.open(objectUrl, '_blank');
      if (!win) {
        const a = document.createElement('a'); a.href = objectUrl; a.download = `cupom-${sale.number}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
      toast.success('Cupom aberto');
    } catch (err) { toast.error('Erro ao gerar cupom PDF'); }
    finally { setBusy(false); }
  };

  const emitInvoice = async (tipo, opts = {}) => {
    const autoIssue = !!opts.autoIssue; // iter77bf: se true, ja transmite ao SEFAZ apos criar
    if (!natopId) { toast.error('Selecione a Natureza de Operacao'); return; }
    setInvoiceBusy(tipo);
    try {
      // iter156db: envia tambem `payments` (array) para prefill correto no
      // InvoiceForm. Antes so mandava payment_method (string), entao ao abrir a
      // NF gerada o campo Formas de Pagamento vinha VAZIO — obrigando o usuario
      // a re-selecionar antes de transmitir. Reconstroi payments da venda:
      // - Se sale.payments (multi-pagamento do PDV): usa direto
      // - Senao: cria [{method: sale.payment_method, value: sale.total}]
      const _salePayments = (Array.isArray(sale.payments) && sale.payments.length > 0)
        ? sale.payments.map(p => ({
            method: p.method || p.payment_method || sale.payment_method || '',
            value: Number(p.amount ?? p.value ?? 0) || 0,
          })).filter(p => p.value > 0)
        : ((sale.payment_method && (Number(sale.total) || 0) > 0)
            ? [{ method: sale.payment_method, value: Number(sale.total) || 0 }]
            : []);
      const { data } = await axios.post(`${ERP}/invoices`, {
        tipo,
        company_id: sale.company_id,
        natureza_op_id: natopId,
        customer_name: sale.customer_name || '',
        customer_doc: sale.customer_doc || '',
        client_id: sale.client_id || null,
        items: sale.items || [],
        discount: sale.discount || 0,
        surcharge: sale.surcharge || 0,
        payment_method: sale.payment_method || (_salePayments[0]?.method || ''),
        payments: _salePayments,
        sale_id: sale.id || sale._id,
        sale_number: sale.number,
      }, { withCredentials: true });
      toast.success(`${tipo.toUpperCase()} #${data.numero} criada como rascunho`);
      // iter77bf: se autoIssue, transmite imediatamente ao SEFAZ.
      if (autoIssue && data.id) {
        toast.loading('Transmitindo ao SEFAZ...', { id: 'auto-issue' });
        try {
          const { data: issued } = await axios.post(`${ERP}/invoices/${data.id}/issue`, {}, { withCredentials: true, timeout: 30000 });
          toast.dismiss('auto-issue');
          if (issued.status === 'issued') {
            toast.success(`Autorizada! Protocolo ${issued.protocolo || '—'}`);
          } else if (issued.status === 'pending') {
            toast(`SEFAZ processando — verifique status em alguns minutos`, { icon: '⏳', duration: 4000 });
          } else {
            toast.error(`SEFAZ rejeitou: ${issued.sefaz_xmotivo || 'sem motivo'}. Veja em Fiscal > NF de Venda > Diagnostico.`, { duration: 5000 });
          }
        } catch (issueErr) {
          toast.dismiss('auto-issue');
          toast.error(issueErr.response?.data?.detail || 'Erro ao transmitir ao SEFAZ');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Erro ao emitir ${tipo.toUpperCase()}`);
    } finally { setInvoiceBusy(null); }
  };

  // iter77bf: dispara a auto-acao recebida do split-button.
  useEffect(() => {
    if (!autoAction || autoFired) return;
    setAutoFired(true);
    (async () => {
      if (autoAction === 'comprovante') {
        await openCupom();
        // Mantem modal aberto para usuario opcional WhatsApp/saida — comportamento natural.
      } else if (autoAction === 'nfce' || autoAction === 'nfe') {
        await emitInvoice(autoAction, { autoIssue: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAction]);

  const shareWhats = () => {
    const text = `*Cupom Nao-Fiscal ${sale.number}*\nTotal: ${fmtBRL(sale.total)}\nObrigado pela preferencia!`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const showInvoiceOptions = allowNFe || allowNFCe;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="post-sale-modal">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-700 px-5 py-4 text-white">
          <div className="flex items-center gap-3">
            <CheckCircle size={28} />
            <div>
              <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Venda Finalizada</p>
              <p className="text-xl font-black">Numero {sale.number}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-[10px] uppercase opacity-80 font-bold">Total</p>
              <p className="text-2xl font-black">{fmtBRL(sale.total)}</p>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <p className="text-sm text-slate-600 mb-2">O que deseja fazer agora?</p>
          {/* iter156ax: Se a venda ja emitiu NF-e/NFC-e automaticamente, mostra um
              destaque com botao PERSISTENTE de imprimir DANFE. Antes so aparecia
              num toast de 8s que era facil de perder. */}
          {sale.__emitted_invoice && (
            <div className={`rounded-xl p-3 border-2 ${sale.__emitted_invoice.status === 'issued' ? 'bg-emerald-50 border-emerald-300' : sale.__emitted_invoice.status === 'pending' ? 'bg-amber-50 border-amber-300' : 'bg-rose-50 border-rose-300'}`}>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={18} className={sale.__emitted_invoice.status === 'issued' ? 'text-emerald-600' : sale.__emitted_invoice.status === 'pending' ? 'text-amber-600' : 'text-rose-600'} />
                <p className="text-xs font-bold uppercase tracking-wider">
                  {(sale.__emitted_type || 'NF').toUpperCase()}{' '}
                  {sale.__emitted_invoice.status === 'issued' ? 'AUTORIZADA' : sale.__emitted_invoice.status === 'pending' ? 'PROCESSANDO' : 'REJEITADA'}
                </p>
              </div>
              {sale.__emitted_invoice.protocolo && (
                <p className="text-[11px] text-slate-600 mb-2">Protocolo: <span className="font-mono font-bold">{sale.__emitted_invoice.protocolo}</span></p>
              )}
              {sale.__emitted_invoice.status === 'issued' && (
                <button
                  onClick={async () => {
                    try {
                      const { data: pdfBlob } = await axios.get(`${ERP}/invoices/${sale.__emitted_invoice.id}/danfe`, { responseType: 'blob', withCredentials: true });
                      const url = window.URL.createObjectURL(pdfBlob);
                      window.open(url, '_blank');
                      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                    } catch { toast.error('Erro ao gerar DANFE'); }
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black text-sm shadow-lg shadow-emerald-500/30"
                  data-testid="post-sale-print-emitted-danfe"
                >
                  <Printer size={18} />
                  Imprimir DANFE {(sale.__emitted_type || 'NF').toUpperCase()}
                </button>
              )}
              {sale.__emitted_invoice.status !== 'issued' && sale.__emitted_invoice.sefaz_xmotivo && (
                <p className="text-[11px] text-slate-700 mt-1">{sale.__emitted_invoice.sefaz_xmotivo}</p>
              )}
            </div>
          )}
          {showInvoiceOptions && !sale.__emitted_invoice && (
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-2">
              <label className="block text-[10px] font-bold text-violet-700 uppercase tracking-wider">Natureza de Operacao</label>
              <select value={natopId} onChange={e => setNatopId(e.target.value)} className="w-full px-2.5 py-2 bg-white border border-violet-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:border-violet-600" data-testid="post-sale-natop">
                <option value="">— Selecione —</option>
                {natops.map(n => <option key={n.id} value={n.id}>{n.code} · {n.description}</option>)}
              </select>
            </div>
          )}
          {allowNFe && (
            <button onClick={() => emitInvoice('nfe')} disabled={!!invoiceBusy || !natopId} className="w-full flex items-center gap-3 px-4 py-3.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl font-bold shadow-md shadow-emerald-500/30" data-testid="post-sale-emit-nfe">
              {invoiceBusy === 'nfe' ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Receipt size={18} />}
              <span className="flex-1 text-left">Emitir NF-e (modelo 55)</span>
            </button>
          )}
          {allowNFCe && (
            <button onClick={() => emitInvoice('nfce')} disabled={!!invoiceBusy || !natopId} className="w-full flex items-center gap-3 px-4 py-3.5 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white rounded-xl font-bold shadow-md shadow-sky-500/30" data-testid="post-sale-emit-nfce">
              {invoiceBusy === 'nfce' ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Receipt size={18} />}
              <span className="flex-1 text-left">Emitir NFC-e (modelo 65)</span>
            </button>
          )}
          <button onClick={openCupom} disabled={busy} className="w-full flex items-center gap-3 px-4 py-3.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-xl font-bold shadow-md shadow-violet-500/30" data-testid="post-sale-print-cupom">
            <Printer size={18} />
            <span className="flex-1 text-left">Emitir Cupom Nao-Fiscal (PDF)</span>
          </button>
          {parsePrintModels(printSaleModel).map((mk, mi) => (
            <button
              key={mk}
              onClick={() => window.open(`${ERP}/sales/${sale.id || sale._id}/print/${mk.replace(/_/g, '-')}`, '_blank')}
              className="w-full flex items-center gap-3 px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-md shadow-blue-500/30"
              data-testid={mi === 0 ? 'post-sale-print-sale' : `post-sale-print-sale-${mk}`}
            >
              <Printer size={18} />
              <span className="flex-1 text-left">Imprimir Venda ({printModelLabel(mk)})</span>
            </button>
          ))}
          <button onClick={shareWhats} className="w-full flex items-center gap-3 px-4 py-3.5 bg-green-500 hover:bg-green-600 text-white rounded-xl font-bold shadow-md shadow-green-500/30" data-testid="post-sale-share-whats">
            <span className="text-lg">💬</span>
            <span className="flex-1 text-left">Compartilhar via WhatsApp</span>
          </button>
          <button onClick={onStay} className="w-full px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-sm" data-testid="post-sale-new">
            Iniciar nova venda
          </button>
          {/* iter156ax: 'Finalizar e Sair' agora usa onExit (fecha modal + sai do PDV);
              o backdrop e o X do topo usam onClose (so fecham o modal, mantem no PDV) */}
          <button onClick={onExit || onClose} className="w-full px-4 py-2.5 text-slate-400 hover:text-slate-600 text-xs font-semibold" data-testid="post-sale-exit">
            Finalizar e Sair
          </button>
        </div>
      </div>
    </div>
  );
};


/* iter77ah: ========== ADJUSTMENT TABS (Desconto / Acrescimo / Frete) ==========
   Compacta 3 ajustes em uma unica caixa com abas — economiza ~50% espaco vertical
   no PDV desktop, liberando area para a lista de itens e formas de pagamento. */
/* iter156g: parser tolerante a vírgula pt-BR — corrige bug em que descontos
   em centavos (ex: "0,50", "0,05") retornavam 0 via parseFloat nativo. */
const _parseNumBR = (s) => {
  if (s == null || s === '') return 0;
  if (typeof s === 'number') return isNaN(s) ? 0 : s;
  const str = String(s).trim().replace(/[R$\s]/g, '');
  // Se tiver virgula, ela e o decimal; pontos sao separadores de milhar
  if (str.includes(',')) {
    const clean = str.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(clean);
    return isNaN(n) ? 0 : n;
  }
  const n = parseFloat(str);
  return isNaN(n) ? 0 : n;
};

const AdjustmentTabs = ({
  subtotal, pdvPerms,
  discountType, setDiscountType, discountInput, setDiscountInput,
  surchargeType, setSurchargeType, surchargeInput, setSurchargeInput,
  freightType, setFreightType, freightInput, setFreightInput,
}) => {
  const [tab, setTab] = useState('discount'); // 'discount' | 'surcharge' | 'freight'
  const tabs = [
    { key: 'discount', label: 'Desconto', enabled: pdvPerms?.give_discount !== false, value: _parseNumBR(discountInput) },
    { key: 'surcharge', label: 'Acrescimo', enabled: pdvPerms?.give_surcharge !== false, value: _parseNumBR(surchargeInput) },
    { key: 'freight', label: 'Frete', enabled: true, value: _parseNumBR(freightInput) },
  ];
  // Resolve current tab spec
  const cur = tab === 'discount'
    ? { type: discountType, setType: setDiscountType, val: discountInput, setVal: setDiscountInput, enabled: pdvPerms?.give_discount !== false, testid: 'desktop-discount', accent: 'rose' }
    : tab === 'surcharge'
      ? { type: surchargeType, setType: setSurchargeType, val: surchargeInput, setVal: setSurchargeInput, enabled: pdvPerms?.give_surcharge !== false, testid: 'desktop-surcharge', accent: 'amber' }
      : { type: freightType, setType: setFreightType, val: freightInput, setVal: setFreightInput, enabled: true, testid: 'desktop-freight', accent: 'sky' };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-2.5 flex-shrink-0" data-testid="adjustment-tabs">
      {/* iter77ct.95: Tab strip modernizada — fundo escurecido, aba ativa em gradiente violeta */}
      <div role="tablist" className="flex bg-slate-800/95 rounded-lg p-1 mb-2 gap-0.5 shadow-inner">
        {tabs.map(t => {
          const active = tab === t.key;
          const hasValue = t.value > 0;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-black uppercase tracking-wider transition-all relative ${
                active
                  ? 'bg-gradient-to-r from-violet-500 to-indigo-500 text-white shadow-md shadow-violet-500/40'
                  : 'text-slate-300 hover:text-white hover:bg-slate-700/60'
              }`}
              data-testid={`adj-tab-${t.key}`}
            >
              {t.label}
              {hasValue && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-orange-500" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
      {/* Active tab body */}
      <div className="flex gap-1.5">
        <div className="flex bg-slate-100 rounded-lg p-0.5 flex-shrink-0">
          <button onClick={() => cur.setType('value')} disabled={!cur.enabled} className={`px-2.5 py-1 rounded-md text-[11px] font-black disabled:opacity-50 transition-colors ${cur.type === 'value' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600'}`} data-testid={`${cur.testid}-type-value`}>R$</button>
          <button onClick={() => cur.setType('percent')} disabled={!cur.enabled} className={`px-2.5 py-1 rounded-md text-[11px] font-black disabled:opacity-50 transition-colors ${cur.type === 'percent' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600'}`} data-testid={`${cur.testid}-type-percent`}>%</button>
        </div>
        <input
          type="text" inputMode="decimal"
          value={cur.val}
          onChange={e => cur.setVal(e.target.value)}
          disabled={!cur.enabled}
          placeholder={cur.enabled ? '0' : 'Sem permissao'}
          className="flex-1 min-w-0 px-2 py-1.5 border-2 border-slate-300 rounded-lg text-right text-base font-black text-slate-900 focus:border-slate-900 focus:ring-2 focus:ring-slate-300/40 outline-none transition-all disabled:bg-slate-50 disabled:text-slate-400"
          data-testid={cur.testid}
        />
      </div>
      {tab === 'discount' && pdvPerms?.give_discount && pdvPerms?.max_discount_pct < 100 && (
        <p className="text-[10px] text-amber-600 mt-1">Max: {pdvPerms.max_discount_pct}%</p>
      )}
      {tab === 'freight' && (
        <p className="text-[10px] text-slate-400 mt-1">Sera incluido no cupom impresso.</p>
      )}
    </div>
  );
};

/* ==========================================================================
   iter77db: Distribuir Lojas — modal informativo para dividir a quantidade
   de UM item entre lojas do cliente (nome digitado). Nao usa cadastro de
   filiais. Nao impacta estoque. Soma DEVE bater com a quantidade do item.
   ========================================================================== */
const DistributeStoresModal = ({ item, onClose, onSave }) => {
  const initial = Array.isArray(item?.store_distribution) && item.store_distribution.length > 0
    ? item.store_distribution.map(s => ({ store: s.store || '', quantity: Number(s.quantity) || 0 }))
    : [{ store: '', quantity: '' }];
  const [rows, setRows] = useState(initial);
  const targetQty = Number(item?.quantity || 0);
  const totalDist = rows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
  const diff = +(targetQty - totalDist).toFixed(4);
  // iter156dn: refs para focar automaticamente no campo Qtd da nova linha adicionada
  const qtyRefs = useRef([]);
  const focusRowIdx = useRef(null);
  useEffect(() => {
    if (focusRowIdx.current !== null && qtyRefs.current[focusRowIdx.current]) {
      qtyRefs.current[focusRowIdx.current].focus();
      qtyRefs.current[focusRowIdx.current].select?.();
      focusRowIdx.current = null;
    }
  });
  const updateRow = (i, patch) => setRows(rows.map((r, k) => k === i ? { ...r, ...patch } : r));
  const addRow = () => {
    setRows(prev => {
      focusRowIdx.current = prev.length; // proxima linha
      return [...prev, { store: '', quantity: '' }];
    });
  };
  const removeRow = (i) => setRows(rows.filter((_, k) => k !== i));
  const handleSave = () => {
    const cleaned = rows
      .map(r => ({ store: String(r.store || '').trim(), quantity: Number(r.quantity) || 0 }))
      .filter(r => r.store && r.quantity > 0);
    if (cleaned.length === 0) { toast.error('Adicione ao menos uma loja com quantidade.'); return; }
    const sum = +cleaned.reduce((s, r) => s + r.quantity, 0).toFixed(4);
    if (Math.abs(sum - targetQty) > 0.0001) {
      toast.error(`A soma das lojas (${sum}) deve ser igual a quantidade do item (${targetQty}).`);
      return;
    }
    onSave(cleaned);
  };
  const clearAll = () => setRows([{ store: '', quantity: '' }]);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" data-testid="distribute-stores-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 flex items-center justify-between border-b bg-gradient-to-r from-purple-50 to-fuchsia-50 border-purple-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white flex items-center justify-center shadow-md shadow-purple-500/40"><Store size={16} /></div>
            <div>
              <h2 className="font-black text-base text-slate-900">Distribuir Lojas</h2>
              <p className="text-[11px] text-slate-500 truncate max-w-[280px]">{item?.name} · qtd total: <strong>{targetQty}</strong></p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="distribute-close"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <p className="text-xs text-slate-500 bg-purple-50 border border-purple-100 rounded-lg p-2">
            Informe as lojas do <strong>seu cliente</strong> e a quantidade que vai para cada uma. E somente informativo (nao afeta estoque). A soma deve ser exatamente <strong>{targetQty}</strong>.
          </p>
          {rows.map((r, i) => {
            const isLast = i === rows.length - 1;
            return (
              <div key={i} className="flex items-center gap-2" data-testid={`distribute-row-${i}`}>
                {/* iter156dn: Lixeira agora fica a ESQUERDA (uso raro) */}
                <button
                  onClick={() => removeRow(i)}
                  disabled={rows.length === 1}
                  tabIndex={-1}
                  className="text-slate-400 hover:text-rose-500 disabled:opacity-30 flex-shrink-0"
                  data-testid={`distribute-remove-${i}`}
                  title="Remover linha"
                >
                  <Trash2 size={16} />
                </button>
                {/* iter156df: Qtd primeiro (com autoFocus na 1a linha), Nome da Loja depois */}
                <input
                  ref={el => { qtyRefs.current[i] = el; }}
                  type="number"
                  min="0"
                  step="any"
                  value={r.quantity}
                  onChange={e => updateRow(i, { quantity: e.target.value })}
                  onFocus={e => e.target.select()}
                  placeholder="Qtd"
                  autoFocus={i === 0}
                  className="w-24 px-3 py-2 border border-slate-300 rounded-lg text-sm text-right font-bold focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid={`distribute-store-qty-${i}`}
                />
                <input
                  type="text"
                  value={r.store}
                  onChange={e => updateRow(i, { store: e.target.value })}
                  placeholder={`Loja ${i + 1} (ex: Loja Centro)`}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid={`distribute-store-name-${i}`}
                />
                {/* iter156dn: botao "+" a direita — SO na ultima linha; Tab da Loja
                    para em "+" e Enter/Space adiciona nova linha com foco em Qtd.
                    Nas demais linhas, ocupa mesmo espaco (spacer invisivel) para
                    manter alinhamento visual. */}
                {isLast ? (
                  <button
                    type="button"
                    onClick={addRow}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addRow(); } }}
                    className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-fuchsia-600 text-white shadow-md shadow-purple-500/40 hover:shadow-lg hover:from-purple-600 hover:to-fuchsia-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-1"
                    data-testid={`distribute-add-inline-${i}`}
                    title="Adicionar nova linha (Enter)"
                  >
                    <Plus size={16} />
                  </button>
                ) : (
                  <span className="w-9 h-9 flex-shrink-0" aria-hidden="true" />
                )}
              </div>
            );
          })}
          <div className={`mt-2 px-3 py-2 rounded-lg border text-sm font-bold ${Math.abs(diff) < 0.0001 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid="distribute-summary">
            Soma distribuida: <span className="font-mono">{totalDist}</span> / {targetQty}
            {Math.abs(diff) < 0.0001
              ? <span className="ml-2">— OK</span>
              : <span className="ml-2">— faltam <span className="font-mono">{diff > 0 ? diff : `+${Math.abs(diff)}`}</span></span>}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
          <button onClick={clearAll} className="text-xs font-bold text-slate-500 hover:text-rose-600" data-testid="distribute-clear">Limpar tudo</button>
          <div className="flex gap-2">
            <button onClick={onClose} type="button" className="btn-pill-ghost" data-testid="distribute-cancel">Cancelar</button>
            <button onClick={handleSave} className="btn-pill-primary" data-testid="distribute-save">
              <Save size={13} /> Salvar Distribuicao
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ========== DESKTOP PDV (Modo Caixa amplo para desktop) ========== */
const DesktopPDV = ({
  stockLabel = (stk) => (Number(stk || 0) > 0 ? `${stk} em estoque` : 'sem estoque'),
  companyId, items, subtotal, total, discountValue, surchargeValue, freightValue,
  paymentMethod, setPaymentMethod, discountType, setDiscountType, discountInput, setDiscountInput,
  surchargeType, setSurchargeType, surchargeInput, setSurchargeInput,
  freightType, setFreightType, freightInput, setFreightInput,
  pdvPerms,
  selectedClient, setSelectedClient, clients, setClients = () => {}, lastAdded, isOnline, queueSize, processing,
  showScanner, setShowScanner, onAddItem, onRemoveItem, onUpdateQty, onSetQty, onUpdateUnitPrice, onFinalize, onExit, onOpenSummary,
  vendedorId, setVendedorId, employees = [], allEmployees = [],
  notes = '', setNotes,
  priceTableId, setPriceTableId, priceTables = [],
  payments = [], setPayments = () => {},
  pickInstallments = 1, setPickInstallments = () => {},
  manualConfirmDefault = false,
  saleType = 'venda', // 'venda' | 'pedido' | 'orcamento' — drives color theme + labels
  allowDateEdit = false,
  saleDate = '',
  setSaleDate = () => {},
  // Pharmacy gating (only used when isPharmacy=true in tenant)
  isPharmacy = false,
  prescriptionId = null,
  prescriptionLabel = '',
  onOpenPrescriptionModal = () => {},
  ignoreCmedBlock = false,
  setIgnoreCmedBlock = () => {},
  onOpenClientPicker = null,
  // iter77af: lista de tipos ativos (vinda da DB) — Set<string> de chaves type lowercase. null=ainda carregando.
  activePmTypes = null,
  // iter77ct.53: lista COMPLETA de PMs (para PDV renderizar por nome, nao apenas por type)
  activePmList = [],
  // iter77ce: crédito devolução do cliente selecionado (usado no PDV desktop para exibir saldo)
  customerCredits = [],
  totalAvailableCredit = 0,
  // iter77ct.111: saldo de Cashback do cliente selecionado (mesmo padrao do totalAvailableCredit)
  cashbackBalance = 0,
  // iter77bl: split-button "Finalizar Venda" no desktop (paridade com mobile iter77bf/77bg).
  allowNFe = false,
  allowNFCe = false,
  certConfigured = false,
  slug = '',
  // iter77bo: callbacks do botao multifuncional no header desktop
  onOpenClosure = () => {},
  onOpenExpense = () => {},
  // iter156u: modal Consultar Itens (Cod.Interno/Barras/Nome/Valor/Estoque, sortable + busca)
  onOpenProductBrowser = () => {},
  // iter77db: Distribuir Lojas
  distributeStoresEnabled = false,
  onOpenDistribute = () => {},
}) => {
  const [code, setCode] = useState('');
  const [qty, setQty] = useState(''); // iter77cd: vazio por padrão; preenche com 1 ao carregar produto
  const [unitPriceOverride, setUnitPriceOverride] = useState(''); // empty until product is picked or user types
  // iter77ct.56: rastreia se o usuario DIGITOU manualmente no campo VALOR UNITARIO — so aplicamos
  // override em manual mode se ele explicitamente alterou. Auto-preenchimento via useEffect NAO conta.
  const [unitPriceTouched, setUnitPriceTouched] = useState(false);
  // iter77ct.56: sempre que pendingProduct mudar (novo produto carregado no preview),
  // reseta a flag touched — o novo preco vem do produto, nao herda intencao anterior.
  // Nota: o setter e usado em vez de useEffect para evitar delay entre state updates.
  const [searchResults, setSearchResults] = useState([]);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  // ---- F2 manual confirm mode: when ON, bipping/picking a product loads it
  // into the preview panel (qty + unit price editable) and the user must press
  // ENTER / click "Adicionar Item" to push it to the items list. Defaults to
  // the company setting `pdv_manual_confirm`; F2 toggles locally.
  const [manualMode, setManualMode] = useState(!!manualConfirmDefault);
  useEffect(() => { setManualMode(!!manualConfirmDefault); }, [manualConfirmDefault]);
  const [pendingProduct, setPendingProduct] = useState(null); // product loaded into preview (manual mode)
  // iter77bp: quando o carrinho for esvaziado (apos finalizar venda), zerar qty/preco/preview para a proxima venda.
  // Observa items.length === 0 — se anteriormente havia itens, isso significa finalize/clear, e nao apenas estado inicial.
  const prevItemsCountRef = React.useRef(items.length);
  useEffect(() => {
    if (prevItemsCountRef.current > 0 && items.length === 0) {
      setQty(1);
      setUnitPriceOverride('');
      setUnitPriceTouched(false);
      setPendingProduct(null);
      setCode('');
      setSearchResults([]);
    }
    prevItemsCountRef.current = items.length;
  }, [items.length]);
  // ---- Multi-payment state (lifted to parent PDVPage) ----
  const [pickMethod, setPickMethod] = useState(''); // currently selected method to add
  const [pickAmount, setPickAmount] = useState(0); // iter77cr: number direto (via CurrencyInput)
  // iter77ch: para "em_aberto" o usuário pode escolher a data de vencimento
  const [pickDueDate, setPickDueDate] = useState('');
  const [pickDueDays, setPickDueDays] = useState(30);
  // iter156et: pickInstallments vem do PDVPage (lifted) — evita ReferenceError (tela branca) no mobile/finalize
  // iter143: espelha o paymentMethod auto-selecionado (default_payment_method das settings) no dropdown
  // visivel do Desktop PDV. Sem isso, o <select> data-testid=desktop-pay-method-select fica em "Selecionar..."
  // mesmo apos o parent hidratar paymentMethod/paymentMethodId. Guard: so espelha se pickMethod ainda vazio
  // — preserva escolha manual do usuario (adicionar outra forma de pagamento).
  React.useEffect(() => {
    if (!pickMethod && paymentMethod && Array.isArray(activePmList) && activePmList.length > 0) {
      const chosen = activePmList.find(p => (p.type || '').toLowerCase() === paymentMethod || (p.name || '').toLowerCase() === paymentMethod);
      if (chosen && chosen.id) setPickMethod(chosen.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod, activePmList.length]);
  const totalPaid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const remaining = Math.max(0, total - totalPaid);
  const changeAmount = Math.max(0, totalPaid - total);
  // iter144b: mantem pickAmount sincronizado com `remaining` conforme o usuario adiciona itens no PDV.
  // Sem isso, o valor ao lado da forma de pagamento (auto-selecionada via default) ficava zerado e o
  // usuario precisava trocar a forma ou digitar manualmente. Usa um ref para detectar edicao manual —
  // se o usuario tipou um valor diferente do ultimo auto-fill, paramos de auto-sincronizar. Reinicia
  // apos click no "+" (que zera pickAmount) ou troca de forma.
  // NOTE (testing agent iter145): moved BELOW `const remaining` — previously placed above, causing
  // ReferenceError (TDZ) at DesktopPDV mount, crashing entire PDV.
  const autoFilledAmountRef = React.useRef(0);
  React.useEffect(() => {
    if (!pickMethod) return;
    // Sync so quando pickAmount atual e o ultimo auto-preenchido (ou 0). Se usuario digitou outro
    // valor, respeita a escolha manual.
    if (pickAmount === 0 || pickAmount === autoFilledAmountRef.current) {
      const rem = Math.max(0, remaining);
      if (rem !== pickAmount) {
        setPickAmount(rem);
        autoFilledAmountRef.current = rem;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickMethod, remaining]);
  const codeRef = React.useRef(null);
  const unitPriceRef = React.useRef(null);
  const today = new Date().toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  // Fetch current company name (for header corner) + logo (iter77ct.97)
  const [companyName, setCompanyName] = useState('');
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  useEffect(() => {
    if (!companyId) { setCompanyName(''); setCompanyLogoUrl(''); return; }
    let m = true;
    axios.get(`${ERP}/companies?company_id=${companyId}`, { withCredentials: true })
      .then(({ data }) => {
        if (!m) return;
        const c = (data.data || []).find(c => c.id === companyId) || {};
        setCompanyName(c.name || '');
        setCompanyLogoUrl(c.logo_url || '');
      })
      .catch(() => {});
    return () => { m = false; };
  }, [companyId]);

  // Theme per sale type — `venda`=teal/slate, `pedido`=amber/orange, `orcamento`=cyan
  const isPedido = saleType === 'pedido' || saleType === 'orcamento';
  const theme = isPedido ? {
    headerBg: 'bg-gradient-to-r from-amber-900 to-orange-800',
    accent: 'text-amber-200',
    label: saleType === 'orcamento' ? 'Modo Orcamento' : 'Modo Pedido',
    itemsLabel: saleType === 'orcamento' ? 'Itens do Orcamento' : 'Itens do Pedido',
    finalizeBtn: 'bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 shadow-amber-600/30',
    addBtn: 'from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-amber-500/30',
    accentRing: 'focus:border-amber-500',
    finalizeLabel: saleType === 'orcamento' ? 'GERAR ORCAMENTO' : 'SALVAR PEDIDO',
  } : {
    headerBg: 'bg-gradient-to-r from-slate-900 to-slate-800',
    accent: 'text-white/90',
    label: 'Modo Caixa Desktop',
    itemsLabel: 'Itens da Venda',
    // iter77ct.99: botao Finalizar e AddItem em tom escuro para alinhar com o header
    finalizeBtn: 'bg-slate-900 hover:bg-slate-800 disabled:bg-gray-300 shadow-slate-900/40',
    addBtn: 'from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 shadow-slate-900/40',
    accentRing: 'focus:border-slate-900',
    finalizeLabel: 'FINALIZAR',
  };

  // Focus code input on mount + after each add
  useEffect(() => { const t = setTimeout(() => codeRef.current?.focus(), 100); return () => clearTimeout(t); }, []);
  useEffect(() => { codeRef.current?.focus(); }, [items.length]);

  // Live search as the user types (3+ chars)
  useEffect(() => {
    if (!code || code.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      // iter77ct.81: aumentado limite (100 produtos) e removido slice(0,8) — dropdown ja tem
      // max-h-[560px] com scroll interno, entao mostra TODOS os resultados; o usuario rola.
      // iter156l: ordena por relevancia (SKU/barcode exatos primeiro, depois prefix match, depois demais).
      // Antes: qualquer produto contendo os digitos "38258" aparecia — inclusive nomes/SKUs com dif.
      // Agora exato-SKU vem no topo, evitando confusao no operador.
      try {
        const { data } = await axios.get(`${ERP}/pdv/products?q=${encodeURIComponent(code)}&limit=100${companyId ? `&company_id=${companyId}` : ''}${priceTableId ? `&price_table_id=${priceTableId}` : ''}`, { withCredentials: true });
        const rows = data.data || [];
        const q = code.trim().toLowerCase();
        const score = (p) => {
          const sku = (p.sku || '').toLowerCase();
          const bar = (p.barcode || '').toLowerCase();
          if (sku === q || bar === q) return 0;              // Match exato: prioridade maxima
          if (sku.startsWith(q) || bar.startsWith(q)) return 1;  // Prefixo
          if (sku.includes(q) || bar.includes(q)) return 2;      // Substring de codigo
          return 3;                                          // Match em nome/descricao
        };
        const sorted = rows.slice().sort((a, b) => score(a) - score(b));
        setSearchResults(sorted);
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [code, priceTableId, companyId]);

  const handleAddByCode = async () => {
    // If we already have a pending product loaded into the preview (manual mode),
    // pressing ENTER / Adicionar Item confirms it and pushes to the items list.
    if (manualMode && pendingProduct) {
      // iter77dc.8: aceita quantidade fracionada (parseFloat com sanitizacao)
      const _pf = parseFloat(String(qty ?? '').replace(',', '.'));
      const n = (isFinite(_pf) && _pf > 0) ? _pf : 1;
      let overridePrice = null;
      // iter77ct.100: usa brlMaskParse — trata tanto "12,50" quanto "12.50" quanto "1250" (mascarado)
      const parsed = brlMaskParse(unitPriceOverride);
      const dbPrice = parseFloat(pendingProduct.price);
      // iter77ct.56: so aplica override se o usuario EFETIVAMENTE digitou no campo (unitPriceTouched=true).
      // Auto-preenchimento por useEffect nao conta como intencao do usuario.
      if (unitPriceTouched && unitPriceOverride && isFinite(parsed) && parsed >= 0) {
        // iter77ct.94: sem permissao "edit_unit_price" o usuario pode aumentar o preco
        // mas NAO pode reduzir. Bloqueia com mensagem clara.
        if (parsed < dbPrice && !pdvPerms?.edit_unit_price) {
          toast.error(`Sem permissao para reduzir o preco. Valor minimo: R$ ${dbPrice.toFixed(2)}`);
          return;
        }
        overridePrice = parsed;
      }
      // iter77cc: chamada única com quantidade — evita bug do closure stale
      // iter77ct.99: anexa `_original_price` para o parent (PDVPage._addItemActual) preservar o piso
      const productWithPrice = overridePrice != null
        ? { ...pendingProduct, price: overridePrice, _original_price: parseFloat(pendingProduct.price) }
        : pendingProduct;
      // iter77ct.56: reforca protecao — garante que o preco NUNCA seja de outro produto (defensive)
      if (productWithPrice.price == null || !isFinite(parseFloat(productWithPrice.price))) {
        toast.error(`Preco invalido para ${pendingProduct.name}`);
        return;
      }
      onAddItem(productWithPrice, n);
      // iter77cd: qty volta ao vazio após adicionar item (só mostra qty quando produto carregado)
      setCode(''); setQty(''); setUnitPriceOverride(''); setUnitPriceTouched(false); setSearchResults([]); setPendingProduct(null);
      return;
    }
    if (!code.trim()) return;
    try {
      const { data } = await axios.get(`${ERP}/pdv/products?q=${encodeURIComponent(code.trim())}${companyId ? `&company_id=${companyId}` : ''}${priceTableId ? `&price_table_id=${priceTableId}` : ''}`, { withCredentials: true });
      // iter156l: se houver EXACT match por SKU ou barcode, sempre prefere ele (nao o primeiro da lista
      // por acaso). Antes: usuario digitava 38258 e o sistema pegava o primeiro do array — que podia
      // ser um produto diferente com codigo relacionado. Agora exato SKU/barcode vem primeiro.
      const rows = data.data || [];
      const q = code.trim().toLowerCase();
      const prod = rows.find(p => (p.sku || '').toLowerCase() === q || (p.barcode || '').toLowerCase() === q) || rows[0];
      if (!prod) { toast.error(`Produto nao encontrado: ${code}`); return; }
      // Manual confirm mode: load into preview, do NOT add yet
      if (manualMode) {
        setPendingProduct(prod);
        setQty(1);
        setUnitPriceOverride(brlMaskDisplay(prod.price ?? 0));
        setUnitPriceTouched(false); // iter77ct.56: novo produto = reseta intencao previa do usuario
        setSearchResults([]);
        setCode('');
        return;
      }
      // Direct add (legacy behavior)
      // iter77dc.8: aceita quantidade fracionada
      const _pf2 = parseFloat(String(qty ?? '').replace(',', '.'));
      const n = (isFinite(_pf2) && _pf2 > 0) ? _pf2 : 1;
      // iter77ct.56: Non-manual mode = VALOR UNITARIO input eh DISABLED (linha 9843 disabled quando !manualMode).
      // Portanto qualquer valor em unitPriceOverride veio do useEffect de auto-preenchimento (linha 9629)
      // — e pode estar STALE do produto anterior. NAO usar como override aqui. Sempre usar o preco do
      // produto fresco recem-buscado. Isso corrige o bug reportado em iter77ct.56 (video anexo):
      // "puxa 2o produto e ele entra com preco do 1o" — causado por unitPriceOverride stale.
      // iter77cc: chamada única com quantidade
      onAddItem(prod, n);
      setCode(''); setQty(''); setUnitPriceOverride(''); setSearchResults([]);
    } catch { toast.error('Erro na busca'); }
  };

  // Click on a search-dropdown result: same dual behavior (manual=preview, normal=direct add)
  const handlePickResult = (p) => {
    if (manualMode) {
      setPendingProduct(p);
      setQty(1);
      setUnitPriceOverride(brlMaskDisplay(p.price ?? 0));
      setUnitPriceTouched(false); // iter77ct.56: novo produto = reseta intencao previa
    } else {
      onAddItem(p);
      // iter77ct.56: apos add direta, reseta o override — evita carregar valor do produto anterior
      // no proximo add via Enter.
      setUnitPriceOverride('');
      setUnitPriceTouched(false);
      setQty('');
    }
    setCode('');
    setSearchResults([]);
  };

  // Auto-fill unit price when user picks via dropdown (handled by parent via lastAdded)
  // Also keep override in sync when the user moves through searchResults visually
  // iter77ct.56: SEMPRE atualiza para o preco do produto atual (nao mais checar !unitPriceOverride).
  // Antes: se override tivesse valor stale do produto anterior, nao atualizava — causando bug do
  // 2o produto herdar preco do 1o. Agora, quando searchResults muda para 1 item, o override
  // reflete o produto atual. O input eh disabled em non-manual mode, entao nao ha risco de
  // sobrescrever input do usuario.
  useEffect(() => {
    if (!manualMode && searchResults.length === 1) {
      setUnitPriceOverride(brlMaskDisplay(searchResults[0].price ?? 0));
    }
    // Quando searchResults zera (sem resultado apos add ou clear), tambem zera o override.
    if (!manualMode && searchResults.length === 0 && !pendingProduct) {
      setUnitPriceOverride('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, manualMode]);

  // Keyboard shortcuts: F2 toggles MANUAL CONFIRM mode (qty+price liberados), F3 cliente, F4 finalizar, F6 cancelar item, F7 nova venda, F8 resumo, ESC sair
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        // iter156fb: ESC nao fecha direto — confirma com "Nao" pre-selecionado
        if (document.querySelector('[data-testid="confirm-dialog-overlay"]')) return;
        confirmDialog({ title: 'Fechar o modulo Desktop?', message: 'Deseja realmente sair do PDV Desktop? Itens nao finalizados no carrinho continuam salvos.', confirmLabel: 'Sim, fechar', cancelLabel: 'Nao', defaultCancel: true })
          .then(ok => { if (ok) onExit(); });
        return;
      }
      if (e.key === 'F2') {
        e.preventDefault();
        setManualMode(m => {
          const next = !m;
          toast.success(next ? 'F2: Confirmacao manual ATIVADA (qtd/valor liberados)' : 'F2: Confirmacao manual DESATIVADA (adicao direta)');
          // Sair do modo manual descarta o produto pendente
          if (!next) { setPendingProduct(null); setUnitPriceOverride(''); setQty(''); }
          return next;
        });
        return;
      }
      if (e.key === 'F3') { e.preventDefault(); setClientPickerOpen(true); return; }
      if (e.key === 'F4') { e.preventDefault(); onFinalize('stay'); return; }
      if (e.key === 'F6') { e.preventDefault(); if (items.length > 0) onRemoveItem(items.length - 1); return; }
      if (e.key === 'F7') { e.preventDefault(); items.forEach((_, idx) => onRemoveItem(items.length - 1 - idx)); return; }
      if (e.key === 'F8') { e.preventDefault(); onOpenSummary(); return; }
      // iter156l: F9 movido para Fechamento de Caixa (mais util no dia-a-dia do operador).
      // Camera QR-Code fica no F10 (uso raro no modo desktop, so mobile realmente usa).
      if (e.key === 'F9') { e.preventDefault(); onOpenClosure(); return; }
      if (e.key === 'F10') { e.preventDefault(); setShowScanner(true); return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [items, onExit, onFinalize, onRemoveItem, onOpenSummary, onOpenClosure, setShowScanner, pdvPerms]);

  // iter77af + iter77ct.53: pmList agora mostra CADA forma de pagamento individual (do DB) como
  // opcao — nao mais dedup por type. Cada entry: `value` (id unico do option: pm.id ou virtualKey),
  // `type` (usado pelo backend/legacy), `label` (nome exibido), `pmId` (id do DB, se aplicavel).
  // Assim "PIX" e "PIX Online" aparecem como opcoes separadas.
  const pmList = useMemo(() => {
    const iconByType = {
      dinheiro: '💵', pix: '📱', debito: '💳', credito: '💳',
      em_aberto: '📅', credito_devolucao: '🔄', cashback: '💰',
    };
    const rows = (activePmList || []).map(pm => {
      const t = String(pm.type || '').toLowerCase();
      return {
        value: pm.id,           // id unico do option
        key: t,                  // type (legado)
        pmId: pm.id,             // PM especifico
        type: t,
        label: pm.name || t,
        icon: iconByType[t] || '💳',
        requiresCashback: t === 'cashback',
        feeTiers: Array.isArray(pm.fee_tiers) ? pm.fee_tiers : [],  // iter156ep
        showFee: !!pm.show_fee_in_sales,  // iter156et
      };
    });
    // iter156gb: SO formas liberadas em Formas de Pagamento (escopo Vendas). "Em Aberto" deixa de ser
    // forcado; "Credito Devolucao" so aparece quando o cliente selecionado TEM saldo.
    if (!rows.some(m => m.type === 'credito_devolucao') && totalAvailableCredit > 0) {
      const meta = PAY_METHODS.find(p => p.key === 'credito_devolucao');
      if (meta) rows.push({ ...meta, value: 'credito_devolucao', type: 'credito_devolucao', pmId: null });
    }
    return rows;
  }, [activePmList, totalAvailableCredit]);
  const heroProduct = lastAdded || (items.length > 0 ? items[items.length - 1] : null);
  // While the user types in the search, show the FIRST match preview (image + price).
  // In MANUAL MODE: prioritize the pending product (the one being confirmed).
  // Falls back to the last added item once search is cleared.
  const previewProduct = (manualMode && pendingProduct)
    ? pendingProduct
    : ((searchResults && searchResults.length > 0) ? searchResults[0] : heroProduct);
  const previewImage = previewProduct?.image || (previewProduct?.images && previewProduct.images[0]) || null;
  // iter77ct.22: um "produto ativo" e aquele que ESTA sendo carregado no painel de pre-venda:
  // - manual mode com pendingProduct aguardando ENTER; OU
  // - busca ao vivo com resultados (o operador esta digitando).
  // Apos o item ser adicionado a lista de venda, ambos ficam vazios e VALOR UNITARIO/TOTAL
  // devem ZERAR (nao continuar exibindo o preco do ultimo produto que "virou" heroProduct).
  const hasActiveProduct = !!((manualMode && pendingProduct) || (searchResults && searchResults.length > 0));
  const previewUnit = (() => {
    if (!hasActiveProduct) return 0;
    // iter77ct.100: usa brlMaskParse (aceita "12,50", "12.50" e mascarado "1250")
    const p = brlMaskParse(unitPriceOverride);
    if (isFinite(p)) return p;
    return previewProduct?.unit_price ?? previewProduct?.price ?? 0;
  })();
  const previewTotal = hasActiveProduct ? previewUnit * (parseFloat(qty) || 1) : 0;

  return (
    <div className="fixed inset-0 z-40 bg-slate-50 overflow-hidden flex flex-col" data-testid="desktop-pdv">
      {/* HEADER */}
      <header className={`${theme.headerBg} text-white px-4 py-2 flex items-center gap-3 flex-shrink-0`}>
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* iter77ct.97: icone quadrado do modo caixa em tom escuro para alinhar com o header */}
          <div className={`w-9 h-9 rounded-xl ${isPedido ? 'bg-amber-500' : 'bg-white/10 ring-1 ring-white/20'} flex items-center justify-center shadow-lg ${isPedido ? 'shadow-amber-500/30' : ''}`}>
            {isPedido ? <FileText size={18} /> : <Monitor size={18} className="text-white/90" />}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest opacity-60">{theme.label}</p>
            <p className="text-sm font-bold">{items.length > 0 ? `${items.length} item${items.length === 1 ? '' : 's'}${isPedido ? '' : ' no caixa'}` : (isPedido ? 'Iniciar pedido' : 'Pronto para venda')}</p>
          </div>
          {companyName && (
            <div className="hidden lg:flex items-center gap-1.5 ml-2 pl-3 border-l border-white/15" data-testid="desktop-company-pill">
              <Building2 size={13} className="text-white/60" />
              <span className="text-xs font-semibold text-white/90 truncate max-w-[160px]">{companyName}</span>
            </div>
          )}
        </div>
        {/* Center pills: Tabela de Preco · Vendedor · Cliente
            iter77ct.67: removido flex-wrap para nao criar 2 linhas em zoom alto — usa overflow-x-auto e whitespace-nowrap. */}
        <div className="flex-1 flex items-center justify-center gap-2 flex-nowrap min-w-0 overflow-x-auto scrollbar-hide">
          {priceTables && priceTables.length > 0 && (
            <div className="flex items-center gap-1.5 bg-amber-500/15 border border-amber-400/30 rounded-lg pl-2.5 pr-1.5 py-1 flex-shrink-0" data-testid="desktop-header-pricetable">
              <FileText size={12} className="text-amber-300" />
              <select value={priceTableId || ''} onChange={e => setPriceTableId && setPriceTableId(e.target.value)} className="bg-transparent text-xs font-semibold text-amber-200 outline-none cursor-pointer pr-1 max-w-[180px] truncate appearance-none" data-testid="desktop-pricetable-select">
                <option value="" className="text-slate-900">Preco base</option>
                {priceTables.map(pt => <option key={pt.id} value={pt.id} className="text-slate-900">{pt.name}{pt.is_default ? ' (padrao)' : ''}</option>)}
              </select>
            </div>
          )}
          {employees.length > 0 && (
            <div className="flex items-center gap-1.5 bg-violet-500/15 border border-violet-400/30 rounded-lg pl-2.5 pr-1.5 py-1 flex-shrink-0" data-testid="desktop-header-vendedor">
              <UserCheck size={12} className="text-violet-300" />
              <select value={vendedorId || ''} onChange={e => setVendedorId(e.target.value)} className="bg-transparent text-xs font-semibold text-violet-200 outline-none cursor-pointer pr-1 max-w-[160px] truncate appearance-none" data-testid="desktop-vendedor-select">
                <option value="" className="text-slate-900">Sem vendedor</option>
                {employees.filter(emp => emp.type === 'vendedor' || !emp.type).map(emp => <option key={emp.id} value={emp.id} className="text-slate-900">{emp.name}</option>)}
              </select>
            </div>
          )}
          <button onClick={() => setClientPickerOpen(true)} className="flex items-center gap-1.5 bg-transparent hover:bg-orange-500/15 border border-orange-400/60 rounded-lg px-2.5 py-1 text-xs font-bold text-orange-300 transition-colors flex-shrink-0 whitespace-nowrap" data-testid="desktop-header-client">
            <Users size={12} />
            <span className="max-w-[160px] truncate">{selectedClient?.name || 'Consumidor'}</span>
            <kbd className="ml-0.5 text-[9px] bg-white/10 px-1 rounded">F3</kbd>
          </button>
          {/* iter77ct.65: chip MANUAL tambem removido do header (ativo/inativo — sempre invisivel).
              Feedback do modo aparece via toast (F2) + destaque no bloco VALOR UNITARIO abaixo. */}
        </div>
        <div className="flex items-center gap-1.5 text-xs flex-shrink-0">
          {/* iter77ct.79: Data SEMPRE visivel (essencial pra vendas retroativas).
              "Online" agora oculto abaixo de xl (foi movido de xl:inline-block da data pra ca).
              PDVActionsMenu compactado com variant="dark-compact" para ganhar espaco. */}
          {allowDateEdit ? (
            <input type="date" value={saleDate} onChange={e => setSaleDate(e.target.value)} max={localDateISO()}
              className="px-2 py-1 rounded-lg bg-white/10 text-white text-xs border border-cyan-300/40 hover:bg-white/20 focus:outline-none focus:ring-1 focus:ring-cyan-400" title="Data da venda (editavel — vendas retroativas)" data-testid="desktop-pdv-sale-date" />
          ) : (
            <span className="opacity-70 text-xs">{today}</span>
          )}
          {/* Online indicator — oculto abaixo de xl pra ganhar espaco em zoom alto */}
          <span className={`hidden xl:flex items-center gap-1 px-2.5 py-1 rounded-lg font-semibold ${isOnline ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`} data-testid="desktop-net-status">
            {isOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
            {isOnline ? 'Online' : 'Offline'}
          </span>
          {/* Fallback OFFLINE em widths menores — so aparece quando efetivamente offline */}
          {!isOnline && <span className="xl:hidden flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/25 text-amber-200 font-bold text-[11px]"><WifiOff size={11} />OFF</span>}
          {queueSize > 0 && <span className="px-2 py-1 rounded-lg bg-orange-500/20 text-orange-300 font-semibold text-[11px]">{queueSize}fila</span>}
          {/* iter77bo: botao multifuncional — Resumo / Fechamento de Caixa / Despesa / Consultar Itens */}
          <PDVActionsMenu
            onOpenSummary={onOpenSummary}
            onOpenClosure={onOpenClosure}
            onOpenExpense={onOpenExpense}
            onOpenProductBrowser={onOpenProductBrowser}
            variant="dark"
          />
          <button onClick={onExit} className="px-2.5 py-1.5 rounded-lg bg-rose-500/90 hover:bg-rose-500 text-white font-semibold flex items-center gap-1.5" data-testid="desktop-exit-btn"><X size={13} />Sair <kbd className="ml-1 text-[9px] bg-white/10 px-1 rounded">ESC</kbd></button>
        </div>
      </header>

      {/* MAIN GRID */}
      {/* iter77ai (rev4): 4/4/4 (terços iguais) — left enxuga (QR button removido + foto 248 + fields 200 cabem em col-span-4 ~480px),
          center mantem boa visibilidade da lista, right cresce para evitar clipping da Forma de Pagamento em viewports pequenos. */}
      <div className="flex-1 grid grid-cols-12 gap-3 p-3 overflow-hidden">
        {/* LEFT — SEARCH (top, modern glass) + HERO PRODUCT (compact below) */}
        <section className="col-span-4 flex flex-col gap-3 overflow-hidden">
          {/* SEARCH-FIRST BLOCK — sticky on top with modern glass surface */}
          <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-slate-200 shadow-lg shadow-slate-200/40 p-3 flex-shrink-0 relative">
            <label className="block text-sm font-black text-slate-800 uppercase tracking-wider mb-2">Codigo / Nome do Produto</label>
            {/* iter77ai (rev4): botao QR code removido do desktop — so faz sentido em mobile com camera (F9 ainda abre via shortcut). */}
            <input
              ref={codeRef}
              value={code}
              onChange={e => setCode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddByCode(); } }}
              placeholder="Escaneie um código, digite SKU ou nome do produto..."
              className="w-full pl-12 pr-4 py-4 border-2 border-slate-300 focus:border-slate-900 focus:ring-4 focus:ring-slate-300/40 rounded-2xl text-base font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg"
              style={{ fontSize: '15px', letterSpacing: '0.01em' }}
              autoFocus
              data-testid="desktop-code-input"
            />
            {/* iter77ct.81: icone dentro do input; iter77ct.99: cor slate dark (harmoniza com header) */}
            <div className="pointer-events-none absolute left-8 top-1/2 -translate-y-1/2 text-slate-500"><Search size={18} /></div>
            {searchResults.length > 0 && (
              <div className="absolute left-4 right-4 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl z-30 max-h-[560px] flex flex-col overflow-hidden" data-testid="desktop-search-results">
                {/* iter77ct.81/82: header sticky com contador. Rows agora sem truncate — nome ocupa 100% */}
                <div className="sticky top-0 z-10 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100 px-3 py-1.5 flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-wider text-orange-700">{searchResults.length} produto{searchResults.length !== 1 ? 's' : ''} encontrado{searchResults.length !== 1 ? 's' : ''}</span>
                  <span className="text-[9px] text-orange-500 font-semibold">role a barra para ver todos</span>
                </div>
                <div className="flex-1 overflow-y-auto scrollbar-thin">
                  {searchResults.map((p, idx) => (
                    <button key={p.id} onClick={() => handlePickResult(p)} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-orange-50 border-b border-slate-50 last:border-0 text-left transition-all group" data-testid={`desktop-result-item-${idx}`}>
                      {p.images?.[0]
                        ? <img src={p.images[0]} className="w-10 h-10 rounded-lg object-cover flex-shrink-0 ring-1 ring-slate-100" alt="" onError={e => { e.target.style.display = 'none'; }} />
                        : <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center flex-shrink-0"><Package size={14} className="text-slate-400" /></div>
                      }
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-900 break-words">{p.name}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {/* iter156l: SKU maior (text-xs, font-black, orange-emphasis) sem
                              aumentar altura do row — mesmo padding/gap; usuario reportou que
                              o codigo aparecia muito pequeno no PDV desktop. */}
                          <span className="text-xs font-black font-mono px-2 py-0.5 rounded bg-orange-100 text-orange-800 leading-tight tracking-tight">{p.sku || '—'}</span>
                          {stockLabel(p.stock) && <span className={`text-[10px] font-bold ${(p.stock || 0) > 0 ? 'text-emerald-600' : 'text-rose-500'}`}>{stockLabel(p.stock)}</span>}
                        </div>
                      </div>
                      <span className="text-base font-black text-orange-700 flex-shrink-0 whitespace-nowrap">{fmtBRL(p.price)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* iter77cc / iter77ct.67: layout responsivo em zoom.
                - Foto pode encolher ate 100px para nao empurrar os campos numericos ao esconder.
                - Fields column tem min 0 para permitir shrink sem clip. */}
            <div className="mt-4 grid gap-3 items-start" style={{ gridTemplateColumns: 'minmax(100px, 200px) minmax(0, 1fr)' }}>
              <div className="aspect-square rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden relative w-full max-w-[200px]" data-testid="desktop-hero-photo">
                {previewImage ? (
                  <img src={previewImage} alt={previewProduct?.name || ''} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                ) : companyLogoUrl ? (
                  // iter77ct.97: sem imagem do produto → mostra a logo da empresa (marca visual)
                  <img src={companyLogoUrl} alt={companyName} className="w-3/4 h-3/4 object-contain opacity-90" data-testid="desktop-hero-company-logo" onError={e => { e.target.style.display = 'none'; e.target.parentElement.querySelector('[data-fallback]')?.style?.setProperty?.('display', 'block'); }} />
                ) : (
                  <Package size={96} className="text-slate-300" data-fallback />
                )}
                {manualMode && pendingProduct && (
                  <div className="absolute top-2 left-2 right-2 px-2 py-1 bg-amber-500/95 text-white text-[10px] font-black rounded uppercase tracking-wider shadow-lg" data-testid="desktop-pending-badge">
                    ✋ Aguardando ENTER — {pendingProduct.name?.slice(0, 26)}
                  </div>
                )}
              </div>
              <div className="flex flex-col justify-between gap-3">
                <div>
                  <label className="block text-[13px] font-black text-slate-800 uppercase tracking-wider mb-1.5">* Quantidade</label>
                  <input type="text" inputMode="decimal" value={qty} onChange={e => setQty(e.target.value.replace(/[^0-9.,]/g, '').replace(/,/g, '.'))} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddByCode(); } }} disabled={!manualMode} className={`w-full px-3 py-3 border-2 border-slate-300 rounded-lg text-2xl font-black text-right text-slate-900 focus:border-slate-900 focus:ring-2 focus:ring-slate-300/40 outline-none transition-all ${!manualMode ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`} data-testid="desktop-qty" />
                </div>
                <div>
                  <label className="block text-[13px] font-black text-slate-800 uppercase tracking-wider mb-1.5">
                    * Valor Unitario
                    {/* iter77ct.67 removeu o F2 hint. iter77ct.71 removeu tambem o badge ✋ MANUAL — fica so o estado visual do input (editavel quando manualMode) */}
                  </label>
                  <div className="flex">
                    <span className={`px-2.5 py-3 border-2 border-slate-300 border-r-0 rounded-l-lg text-sm font-black flex items-center ${!manualMode ? 'bg-slate-100 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>R$</span>
                    {/* iter77ct.100: mascara BRL — digitos apenas com formato automatico "12,50" */}
                    <input ref={unitPriceRef} type="text" inputMode="decimal" value={unitPriceOverride} onChange={e => { setUnitPriceOverride(brlMaskDisplay(e.target.value)); setUnitPriceTouched(true); }} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddByCode(); } }} disabled={!manualMode} placeholder="0,00" title={!pdvPerms?.edit_unit_price ? 'Sem permissao para reduzir o preco — apenas aumentar' : ''} className={`flex-1 min-w-0 px-2.5 py-3 border-2 border-slate-300 rounded-r-lg text-2xl font-black text-right text-slate-900 focus:border-slate-900 focus:ring-2 focus:ring-slate-300/40 outline-none transition-all ${!manualMode ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`} data-testid="desktop-unit-price" />
                  </div>
                </div>
                <div>
                  <label className="block text-[13px] font-black text-slate-800 uppercase tracking-wider mb-1.5">* Valor Total</label>
                  <div className="flex">
                    <span className="px-2.5 py-3 bg-slate-100 border-2 border-slate-300 border-r-0 rounded-l-lg text-sm font-black text-slate-600 flex items-center">R$</span>
                    <input value={fmtBRL(previewTotal).replace('R$ ', '').replace('R$','').trim()} readOnly className="flex-1 min-w-0 px-2.5 py-3 bg-slate-50 border-2 border-slate-300 rounded-r-lg text-2xl font-black text-right text-orange-700" data-testid="desktop-total-preview" />
                  </div>
                </div>
              </div>
            </div>
            {/* Botao Adicionar Item — full width abaixo */}
            <button onClick={handleAddByCode} className={`w-full mt-3 bg-gradient-to-r ${theme.addBtn} text-white py-3 rounded-lg font-bold text-base flex items-center justify-center gap-2 shadow-md`} data-testid="desktop-add-btn"><Plus size={18} />Adicionar Item <kbd className="ml-1 text-[11px] bg-white/15 px-1.5 py-0.5 rounded">ENTER</kbd></button>
          </div>
          {/* Observacoes Gerais (sem preview do ultimo item — item ja vai direto pra lista) */}
          <div className="bg-white rounded-2xl border border-slate-200 p-3 flex flex-col gap-2 flex-shrink-0">
            <label className="block text-[11px] font-black text-slate-700 uppercase tracking-wider">Observacoes Gerais</label>
            <textarea
              value={notes}
              onChange={e => setNotes && setNotes(e.target.value)}
              placeholder="Faca aqui suas Observacoes..."
              rows={2}
              className="resize-none px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-orange-700 outline-none"
              data-testid="desktop-notes"
            />
          </div>
        </section>

        {/* CENTER — ITEMS LIST */}
        <section className="col-span-4 flex flex-col gap-3 overflow-hidden">
          <div className="bg-white rounded-2xl border border-gray-200 flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0 gap-2 flex-wrap">
            <p className="text-xs font-black text-slate-700 uppercase tracking-wider" data-testid="desktop-items-label">{theme.itemsLabel}</p>
            {(() => {
              // iter77ct.61: badge moderno destacado — antes era text-[10px] discreto.
              // Agora: pill com fundo gradiente sutil e fontes maiores para leitura rapida no PDV.
              const totalQty = items.reduce((s, it) => s + (parseFloat(it.quantity) || 0), 0);
              const totalQtyStr = Number.isInteger(totalQty) ? String(totalQty) : totalQty.toFixed(2);
              return (
                <div className="flex items-center gap-1.5 bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-full px-3 py-1 shadow-sm" data-testid="desktop-items-count">
                  <span className="text-base font-black text-slate-800 tabular-nums leading-none">{items.length}</span>
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{items.length === 1 ? 'item' : 'itens'}</span>
                  {totalQty !== items.length && (
                    <>
                      <span className="text-slate-300 text-sm leading-none">·</span>
                      <span className="text-base font-black text-emerald-600 tabular-nums leading-none">{totalQtyStr}</span>
                      <span className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">un.</span>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
            {clientPickerOpen && (
              <ClientPDVModal
                clients={clients}
                employees={allEmployees.length ? allEmployees : employees}
                companyId={companyId}
                onClose={() => setClientPickerOpen(false)}
                onSelectExisting={(c) => { setSelectedClient(c); setClientPickerOpen(false); }}
                onAttachDoc={({ doc_type, document }) => {
                  setSelectedClient({ id: null, name: `Consumidor (${doc_type.toUpperCase()})`, document });
                  setClientPickerOpen(false);
                }}
                onCreated={(c) => { setClients(prev => [c, ...prev]); setSelectedClient(c); setClientPickerOpen(false); }}
              />
            )}
            <div className="flex-1 overflow-y-auto p-2">
              {isPharmacy && items.length > 0 && (() => {
                const hasControlled = cartHasControlled(items);
                const cmedIssues = cartCMEDIssues(items);
                const allergies = (selectedClient && selectedClient.pharmacy && selectedClient.pharmacy.allergies) || '';
                const allergyHits = cartAllergyHits(items, allergies);
                if (!hasControlled && cmedIssues.length === 0 && allergyHits.length === 0) return null;
                return (
                  <div className="mb-2 space-y-1.5" data-testid="desktop-pharmacy-banner">
                    {hasControlled && (
                      <div className="px-3 py-2 rounded-lg bg-violet-50 border-2 border-violet-300 flex items-start gap-2" data-testid="desktop-pharmacy-controlled-banner">
                        <span className="text-violet-900 text-base font-black">[RX]</span>
                        <div className="flex-1 text-xs">
                          <p className="font-black text-violet-900">Medicamento controlado — receita obrigatoria</p>
                          <p className="text-violet-700">{prescriptionId ? `Receita: ${prescriptionLabel}` : 'CPF + dados do prescritor obrigatorios para finalizar.'}</p>
                        </div>
                        <button onClick={onOpenPrescriptionModal} className="text-[10px] font-black bg-violet-600 hover:bg-violet-700 text-white px-2.5 py-1.5 rounded-lg" data-testid="desktop-open-prescription">
                          {prescriptionId ? 'Trocar Receita' : 'Capturar Receita'}
                        </button>
                      </div>
                    )}
                    {cmedIssues.length > 0 && (
                      <div className="px-3 py-2 rounded-lg bg-red-50 border-2 border-red-300" data-testid="desktop-pharmacy-cmed-banner">
                        <p className="text-xs font-black text-red-900">! Preco acima do PMC CMED</p>
                        {cmedIssues.slice(0, 3).map((c, i) => (
                          <p key={i} className="text-[11px] text-red-700">{c.name}: R$ {Number(c.unit_price).toFixed(2)} (max R$ {Number(c.pmc).toFixed(2)})</p>
                        ))}
                        <label className="mt-1 flex items-center gap-1.5 text-[11px] font-bold text-red-800">
                          <input type="checkbox" checked={ignoreCmedBlock} onChange={e => setIgnoreCmedBlock(e.target.checked)} data-testid="desktop-pharmacy-cmed-override" />
                          Confirmar venda mesmo acima do PMC (autorizacao do supervisor)
                        </label>
                      </div>
                    )}
                    {allergyHits.length > 0 && (
                      <div className="px-3 py-2 rounded-lg bg-amber-50 border-2 border-amber-300" data-testid="desktop-pharmacy-allergy-banner">
                        <p className="text-xs font-black text-amber-900">! ALERGIA DECLARADA: {selectedClient?.name}</p>
                        {allergyHits.slice(0, 3).map((h, i) => (
                          <p key={i} className="text-[11px] text-amber-800">{h.name} — principio ativo: {h.active_ingredient || h.term}</p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-300 py-12"><ShoppingCart size={40} className="mb-2" /><p className="text-sm">Carrinho vazio</p></div>
              ) : (
                <div className="space-y-1.5">
                  {items.slice().reverse().map((item, revIdx) => {
                    const idx = items.length - 1 - revIdx;
                    const tj = isPharmacy ? tarjaInfo(item.pharmacy?.tarja) : null;
                    const overPmc = isPharmacy && itemExceedsCMED(item);
                    return (
                      <div key={`${item.product_id}-${idx}`} className={`bg-white border ${overPmc ? 'border-red-400' : 'border-gray-150'} hover:border-orange-300 rounded-xl px-2.5 py-2 flex items-center gap-2 group transition-colors`} data-testid="desktop-item-row">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-[11px] font-black text-gray-500">{items.length - idx}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                            {isPharmacy && item.pharmacy?.controlled && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-violet-600 text-white" data-testid="desktop-item-controlled-pill">RX {item.pharmacy.controlled_list || ''}</span>}
                            {isPharmacy && tj && (item.pharmacy?.tarja || '').toLowerCase() !== 'sem_tarja' && <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border ${tj.cls}`} data-testid="desktop-item-tarja-pill">{tj.label}</span>}
                            {isPharmacy && item.lot && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 border border-cyan-300" data-testid="desktop-item-lot-pill">Lote {item.lot}</span>}
                          </div>
                          <p className="text-[10px] text-gray-400">{item.sku} · <button onClick={() => {
                              // iter77ct.94: sempre abre o prompt (mesmo sem permissao),
                              // mas valida no submit: sem permissao so aceita ficar >= preco original do item.
                              const v = window.prompt('Novo valor unitario:', String(item.unit_price));
                              if (v == null) return;
                              const f = parseFloat(String(v).replace(',', '.'));
                              if (!isFinite(f) || f < 0) { toast.error('Valor invalido'); return; }
                              const originalPrice = Number(item.original_unit_price ?? item.unit_price);
                              if (!pdvPerms?.edit_unit_price && f < originalPrice) {
                                toast.error(`Sem permissao para reduzir o preco. Valor minimo: ${fmtBRL(originalPrice)}`);
                                return;
                              }
                              onUpdateUnitPrice && onUpdateUnitPrice(idx, f);
                            }} className={`font-semibold underline-offset-2 hover:underline ${overPmc ? 'text-red-600' : 'text-orange-700'}`} title={pdvPerms?.edit_unit_price ? 'Editar valor unitario' : 'Editar valor unitario (apenas para MAIOR)'} data-testid="desktop-edit-price">{fmtBRL(item.unit_price)}</button> un.{overPmc && <span className="text-[9px] text-red-600 ml-1">(&gt; PMC {fmtBRL(Number(item.pharmacy.cmed_pmc))})</span>}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => onUpdateQty(idx, -1)} className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center"><Minus size={12} /></button>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={String(item.quantity).replace('.', ',')}
                            onChange={(e) => onSetQty && onSetQty(idx, e.target.value)}
                            onFocus={(e) => e.target.select()}
                            className="text-sm font-black w-12 text-center border border-slate-200 rounded px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-orange-400"
                            title="Aceita quantidade fracionada (ex: 3,5)"
                            data-testid={`desktop-item-qty-${idx}`}
                          />
                          <button onClick={() => onUpdateQty(idx, 1)} className="w-7 h-7 rounded-lg bg-white border border-orange-300 hover:bg-orange-50 flex items-center justify-center transition-colors"><Plus size={12} className="text-orange-700" /></button>
                        </div>
                        <span className="text-sm font-black text-gray-900 w-20 text-right">{fmtBRL(item.total)}</span>
                        {distributeStoresEnabled && (() => {
                          const dist = Array.isArray(item.store_distribution) ? item.store_distribution : [];
                          const cnt = dist.length;
                          return (
                            <button
                              onClick={() => onOpenDistribute(idx)}
                              className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase flex items-center gap-1 transition-colors ${cnt > 0 ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-purple-50 border-2 border-purple-300 text-purple-700 hover:bg-purple-100'}`}
                              title={cnt > 0 ? dist.map(d => `${d.store}: ${d.quantity}`).join(' · ') : 'Distribuir por lojas do cliente'}
                              data-testid={`desktop-item-distribute-${idx}`}
                            >
                              <Store size={11} />{cnt > 0 ? `${cnt} loja${cnt > 1 ? 's' : ''}` : 'Lojas'}
                            </button>
                          );
                        })()}
                        <button onClick={() => onRemoveItem(idx)} className="text-gray-300 hover:text-rose-500"><X size={14} /></button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* RIGHT — DISCOUNT/SURCHARGE → PAYMENT LIST → "RECEBIMENTO" (purple) → FINALIZE */}
        <section className="col-span-4 flex flex-col gap-2.5 overflow-hidden">
          {/* iter77ah: Desconto / Acrescimo / Frete como abas compactas — economiza ~50% espaco vertical */}
          <AdjustmentTabs
            subtotal={subtotal}
            pdvPerms={pdvPerms}
            discountType={discountType} setDiscountType={setDiscountType}
            discountInput={discountInput} setDiscountInput={setDiscountInput}
            surchargeType={surchargeType} setSurchargeType={setSurchargeType}
            surchargeInput={surchargeInput} setSurchargeInput={setSurchargeInput}
            freightType={freightType} setFreightType={setFreightType}
            freightInput={freightInput} setFreightInput={setFreightInput}
          />

          {/* Forma de Pagamento — Multi-pagamento (dropdown + lista) */}
          <div className="bg-white rounded-2xl border border-slate-200 p-3 flex-1 overflow-y-auto space-y-2.5">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-black text-slate-700 uppercase tracking-wider">Forma de Pagamento</p>
              {/* iter77ce: badge de crédito devolução disponível */}
              {selectedClient && totalAvailableCredit > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-violet-100 to-blue-100 text-violet-800 border border-violet-300 px-2 py-1 rounded-full" title={`Cliente ${selectedClient.name} tem R$ ${totalAvailableCredit.toFixed(2)} de crédito devolução disponível`} data-testid="desktop-credit-badge">
                  ↩️ Crédito: R$ {totalAvailableCredit.toFixed(2)}
                </span>
              )}
              {/* iter77ct.111: badge de saldo Cashback disponivel (mesmo padrao do Credito) */}
              {selectedClient && cashbackBalance > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border border-amber-300 px-2 py-1 rounded-full" title={`Cliente ${selectedClient.name} tem R$ ${cashbackBalance.toFixed(2)} de Cashback disponivel`} data-testid="desktop-cashback-badge">
                  🪙 Cashback: R$ {cashbackBalance.toFixed(2)}
                </span>
              )}
            </div>
            {/* iter77ct.62: grid responsivo — select ganha min-width 0 para nao empurrar o botao "+" para fora
                quando zoom aumenta. Amount reduzido para 100px (era 120px) e usa flex-shrink-0 explicito no botao. */}
            <div className="grid grid-cols-[minmax(0,1fr)_100px_auto] gap-1.5">
              <select
                value={pickMethod}
                onChange={e => {
                  const newMethod = e.target.value;
                  setPickMethod(newMethod);
                  if (!newMethod) return;
                  // iter77ct.2: ao escolher forma de pagamento, auto-preenche o valor com o restante da venda
                  const rem = Math.max(0, remaining);
                  const pickedPm = pmList.find(m => m.value === newMethod) || {};
                  if (pickedPm.type === 'credito_devolucao' && selectedClient && totalAvailableCredit > 0) {
                    const autoAmt = Math.min(rem > 0 ? rem : totalAvailableCredit, totalAvailableCredit);
                    setPickAmount(autoAmt);
                  } else if (pickedPm.type === 'cashback' && selectedClient && cashbackBalance > 0) {
                    // iter77ct.111: auto-preenche o menor entre restante e saldo cashback
                    const autoAmt = Math.min(rem > 0 ? rem : cashbackBalance, cashbackBalance);
                    setPickAmount(autoAmt);
                  } else if (rem > 0) {
                    setPickAmount(rem);
                  }
                }}
                className="px-2.5 py-2.5 border-2 border-slate-200 rounded-lg text-sm font-bold focus:border-orange-700 outline-none bg-white min-w-0 truncate"
                data-testid="desktop-pay-method-select"
              >
                <option value="">Selecionar...</option>
                {pmList.map(m => {
                  const isCred = m.type === 'credito_devolucao';
                  const isCash = m.type === 'cashback';
                  // iter77ct.111: mostra saldo cashback no label (mesmo padrao credito_devolucao)
                  let balanceInfo = '';
                  if (isCred && selectedClient) {
                    balanceInfo = totalAvailableCredit > 0 ? ` (Saldo: R$ ${totalAvailableCredit.toFixed(2)})` : ' (Sem saldo)';
                  } else if (isCash && selectedClient) {
                    balanceInfo = cashbackBalance > 0 ? ` (Saldo: R$ ${cashbackBalance.toFixed(2)})` : ' (Sem saldo)';
                  }
                  const disabled = (isCred && !(selectedClient && totalAvailableCredit > 0))
                    || (isCash && !(selectedClient && cashbackBalance > 0));
                  return (
                    <option key={m.value} value={m.value} disabled={disabled}>
                      {m.icon} {m.label}{balanceInfo}
                    </option>
                  );
                })}
              </select>
              {/* iter77cr: CurrencyInput padronizado — digite 5499 → aparece R$ 54,99 */}
              <CurrencyInput
                value={pickAmount}
                onChange={(v) => setPickAmount(v)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (!pickMethod) { toast.info('Selecione uma forma'); return; }
                    const pickedPm = pmList.find(m => m.value === pickMethod) || {};
                    // iter156bj: "Sem Pagamento" (cortesia) — zera venda e auto-finaliza
                    if (pickedPm.type === 'sem_pagamento') {
                      setPayments([{ method: 'sem_pagamento', method_id: pickedPm.pmId, label: pickedPm.label || 'Sem Pagamento', amount: 0 }]);
                      setPaymentMethod('sem_pagamento');
                      setPickMethod(''); setPickAmount(0); setPickDueDate('');
                      toast.success('Cortesia aplicada — valor zerado. Clique em Finalizar.');
                      return;
                    }
                    let v = Number(pickAmount) || 0;
                    if (v <= 0) { toast.error('Valor invalido'); return; }
                    // iter77ct.111: clamp de saldo p/ cashback e credito_devolucao (nao permite exceder)
                    if (pickedPm.type === 'cashback') {
                      if (cashbackBalance <= 0) { toast.error('Cliente nao tem saldo de Cashback'); return; }
                      if (v > cashbackBalance + 0.01) {
                        toast.warning(`Valor ajustado para R$ ${cashbackBalance.toFixed(2)} (saldo maximo de Cashback disponivel)`);
                        v = cashbackBalance;
                        setPickAmount(v);
                      }
                    }
                    if (pickedPm.type === 'credito_devolucao') {
                      if (totalAvailableCredit <= 0) { toast.error('Cliente nao tem credito de devolucao'); return; }
                      if (v > totalAvailableCredit + 0.01) {
                        toast.warning(`Valor ajustado para R$ ${totalAvailableCredit.toFixed(2)} (saldo maximo de credito disponivel)`);
                        v = totalAvailableCredit;
                        setPickAmount(v);
                      }
                    }
                    const label = pickedPm.label || pickMethod;
                    const methodType = pickedPm.type || pickMethod;
                    const extra = methodType === 'em_aberto' ? { due_date: pickDueDate || undefined, due_days: pickDueDays } : {};
                    if (methodType === 'credito') extra.installments = Math.max(1, Number(pickInstallments) || 1);
                    // iter77ct.53: method_id (PM.id do DB) para o backend rotear o banco correto
                    const methodId = pickedPm.pmId || undefined;
                    setPayments([...payments, { method: methodType, method_id: methodId, label, amount: v, ...extra }]);
                    setPaymentMethod(payments.length === 0 ? methodType : paymentMethod);
                    setPickMethod(''); setPickAmount(0); setPickDueDate(''); setPickInstallments(1);
                  }
                }}
                placeholder={remaining > 0 ? remaining.toFixed(2).replace('.', ',') : '0,00'}
                className="font-bold text-right"
                dataTestId="desktop-pay-amount"
              />
              <button onClick={() => {
                if (!pickMethod) { toast.info('Selecione uma forma'); return; }
                const pickedPm = pmList.find(m => m.value === pickMethod) || {};
                // iter156bj: "Sem Pagamento" (cortesia) — zera venda automaticamente
                if (pickedPm.type === 'sem_pagamento') {
                  setPayments([{ method: 'sem_pagamento', method_id: pickedPm.pmId, label: pickedPm.label || 'Sem Pagamento', amount: 0 }]);
                  setPaymentMethod('sem_pagamento');
                  setPickMethod(''); setPickAmount(0); setPickDueDate('');
                  toast.success('Cortesia aplicada — valor zerado. Clique em Finalizar.');
                  return;
                }
                let raw = (Number(pickAmount) || 0) <= 0 ? remaining : Number(pickAmount);
                if (!isFinite(raw) || raw <= 0) { toast.error('Valor invalido'); return; }
                // iter77ct.111: clamp saldo p/ cashback e credito_devolucao no + tambem
                if (pickedPm.type === 'cashback') {
                  if (cashbackBalance <= 0) { toast.error('Cliente nao tem saldo de Cashback'); return; }
                  if (raw > cashbackBalance + 0.01) {
                    toast.warning(`Valor ajustado para R$ ${cashbackBalance.toFixed(2)} (saldo maximo de Cashback disponivel)`);
                    raw = cashbackBalance;
                    setPickAmount(raw);
                  }
                }
                if (pickedPm.type === 'credito_devolucao') {
                  if (totalAvailableCredit <= 0) { toast.error('Cliente nao tem credito de devolucao'); return; }
                  if (raw > totalAvailableCredit + 0.01) {
                    toast.warning(`Valor ajustado para R$ ${totalAvailableCredit.toFixed(2)} (saldo maximo de credito disponivel)`);
                    raw = totalAvailableCredit;
                    setPickAmount(raw);
                  }
                }
                const label = pickedPm.label || pickMethod;
                const methodType = pickedPm.type || pickMethod;
                const extra = methodType === 'em_aberto' ? { due_date: pickDueDate || undefined, due_days: pickDueDays } : {};
                if (methodType === 'credito') extra.installments = Math.max(1, Number(pickInstallments) || 1);
                const methodId = pickedPm.pmId || undefined;
                setPayments([...payments, { method: methodType, method_id: methodId, label, amount: raw, ...extra }]);
                setPaymentMethod(payments.length === 0 ? methodType : paymentMethod);
                setPickMethod(''); setPickAmount(0); setPickDueDate(''); setPickInstallments(1);
              }} className="px-3 bg-white border-2 border-orange-700 text-orange-700 hover:bg-orange-50 rounded-lg flex items-center justify-center transition-colors flex-shrink-0" data-testid="desktop-pay-add"><Plus size={16} /></button>
            </div>
            {/* iter156et: parcelas do cartao — SOMENTE as cadastradas na Forma de Pagamento; valor da parcela sempre; taxa/liquido so se show_fee_in_sales */}
            {(() => {
              const _pm = pmList.find(m => m.value === pickMethod);
              if (!_pm || _pm.type !== 'credito') return null;
              const tiers = (_pm.feeTiers || []).slice().sort((a, b) => Number(a.installments) - Number(b.installments));
              const opts = tiers.length ? tiers.map(t => Number(t.installments) || 1) : [1];
              const feeFor = (n) => { const ex = tiers.find(t => Number(t.installments) === n); return ex ? (Number(ex.fee_percent) || 0) : 0; };
              const n = opts.includes(Number(pickInstallments)) ? Number(pickInstallments) : opts[0];
              if (n !== Number(pickInstallments)) setTimeout(() => setPickInstallments(n), 0);
              const amt = Number(pickAmount) || remaining || 0;
              const pct = feeFor(n);
              const parcela = n > 0 ? amt / n : amt;
              return (
                <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-3 items-center -mt-1 p-2 bg-violet-50 border border-violet-200 rounded-lg" data-testid="pdv-installments-box">
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] font-black text-violet-800 uppercase">Parcelas</label>
                    <select
                      value={n}
                      onChange={e => setPickInstallments(Number(e.target.value) || 1)}
                      className="px-2 py-1.5 border border-violet-300 rounded-lg text-sm font-bold bg-white"
                      data-testid="pdv-installments-select"
                    >
                      {opts.map(k => <option key={k} value={k}>{k}x</option>)}
                    </select>
                  </div>
                  <div className="text-right text-[11px] text-violet-900 leading-tight" data-testid="pdv-installments-net">
                    <div><span className="opacity-70">{n}x de</span> <strong className="text-sm" data-testid="pdv-installment-value">{fmtBRL(parcela)}</strong></div>
                    {_pm.showFee && pct > 0 && (
                      <div className="opacity-80" data-testid="pdv-installments-fee-info">taxa {pct.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}% ({fmtBRL(amt * pct / 100)}) · liquido <strong>{fmtBRL(amt - amt * pct / 100)}</strong></div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* iter77ch: campos extras quando em_aberto (fiado) — checa pelo TYPE do PM selecionado (iter77ct.53) */}
            {(pmList.find(m => m.value === pickMethod)?.type === 'em_aberto') && (
              <div className="grid grid-cols-2 gap-1.5 -mt-1 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                <div>
                  <label className="text-[10px] font-black text-amber-800 uppercase block mb-0.5">Prazo (dias)</label>
                  <input
                    type="number" min="1" max="365" value={pickDueDays}
                    onChange={e => {
                      const d = parseInt(e.target.value) || 30;
                      setPickDueDays(d);
                      // recalcula sugestão de data
                      const dt = new Date(); dt.setDate(dt.getDate() + d);
                      setPickDueDate(dt.toISOString().slice(0, 10));
                    }}
                    className="w-full px-2 py-1.5 border border-amber-300 rounded text-sm font-bold text-center"
                    data-testid="pay-em-aberto-days"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black text-amber-800 uppercase block mb-0.5">Vencimento</label>
                  <input
                    type="date" value={pickDueDate || (() => { const d = new Date(); d.setDate(d.getDate() + pickDueDays); return d.toISOString().slice(0, 10); })()}
                    onChange={e => setPickDueDate(e.target.value)}
                    className="w-full px-2 py-1.5 border border-amber-300 rounded text-sm font-bold"
                    data-testid="pay-em-aberto-date"
                  />
                </div>
              </div>
            )}
            {payments.length > 0 && (
              <div className="space-y-1" data-testid="desktop-payments-list">
                {payments.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 px-2.5 py-2 bg-orange-50 border border-orange-100 rounded-lg">
                    <CheckCircle size={14} className="text-orange-700 flex-shrink-0" />
                    <span className="flex-1 text-sm font-bold text-slate-800">{p.label}{p.installments > 1 ? <span className="ml-1 text-[10px] font-black text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">{p.installments}x</span> : null}</span>
                    <span className="text-sm font-black text-orange-700">{fmtBRL(p.amount)}</span>
                    <button onClick={() => setPayments(payments.filter((_, j) => j !== i))} className="text-rose-400 hover:text-rose-600" data-testid={`desktop-pay-remove-${i}`}><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
            {/* Resumo de Restante / Troco */}
            <div className="grid grid-cols-2 gap-2 pt-1.5 border-t border-slate-100">
              <div className={`p-2.5 rounded-lg ${remaining > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50 border border-slate-200'}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Restante</p>
                <p className={`text-base font-black ${remaining > 0 ? 'text-amber-700' : 'text-slate-400'}`} data-testid="desktop-remaining">{fmtBRL(remaining)}</p>
              </div>
              <div className={`p-2.5 rounded-lg ${changeAmount > 0 ? 'bg-emerald-50 border border-emerald-300' : 'bg-slate-50 border border-slate-200'}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Troco</p>
                <p className={`text-base font-black ${changeAmount > 0 ? 'text-emerald-700' : 'text-slate-400'}`} data-testid="desktop-change">{fmtBRL(changeAmount)}</p>
              </div>
            </div>
          </div>

          {/* RECEBIMENTO (roxo) — iter77ct.62: mais compacto para deixar espaco extra as formas de pagamento
              em telas menores/com zoom. Antes: text-3xl total, subtotal/desconto/etc em coluna. Agora: text-2xl
              + linha horizontal condensada de detalhes que so aparece se ha algo relevante. */}
          <div className="bg-gradient-to-br from-blue-600 to-violet-600 text-white rounded-2xl p-3 shadow-xl shadow-violet-600/30 flex-shrink-0">
            <div className="flex items-baseline justify-between">
              <p className="text-[10px] uppercase tracking-widest opacity-75 font-bold">Recebimento</p>
              <p className="text-xl font-bold opacity-90" data-testid="desktop-units-label">{items.reduce((s, i) => s + i.quantity, 0)} un.</p>
            </div>
            <p className="text-2xl font-black mt-0.5 tracking-tight" data-testid="desktop-total">{fmtBRL(total)}</p>
            {(discountValue > 0 || surchargeValue > 0 || freightValue > 0) && (
              <div className="mt-1.5 pt-1.5 border-t border-white/15 flex flex-wrap gap-x-3 gap-y-0.5 text-xl font-bold opacity-95" data-testid="desktop-subtotal-line">
                <span>Sub: {fmtBRL(subtotal)}</span>
                {discountValue > 0 && <span className="text-rose-200">-{fmtBRL(discountValue)}</span>}
                {surchargeValue > 0 && <span className="text-amber-200">+{fmtBRL(surchargeValue)}</span>}
                {freightValue > 0 && <span className="text-emerald-200">frete {fmtBRL(freightValue)}</span>}
              </div>
            )}
          </div>

          {/* iter77bl: split-button no desktop (paridade com mobile) — 4 opcoes inline */}
          {/* iter156r: bloqueia finalizacao ate o operador adicionar ao menos 1 pagamento
              via botao "+" (venda tipo=venda). Pedidos/orcamentos passam livremente. */}
          <FinalizeSplitButton
            processing={processing}
            disabled={processing || items.length === 0 || (saleType === 'venda' && (payments?.length || 0) === 0)}
            allowNFe={allowNFe}
            allowNFCe={allowNFCe}
            certConfigured={certConfigured}
            slug={slug}
            onFinalize={(action) => onFinalize(action)}
          />
        </section>
      </div>

      {/* FOOTER SHORTCUTS
          iter77ct.67: forcado single-line via flex-nowrap + overflow-x-auto + whitespace-nowrap em cada chip. */}
      <footer className="bg-slate-900 text-white/90 px-5 py-2.5 flex items-center justify-between text-[13px] flex-shrink-0 gap-3 flex-nowrap" data-testid="desktop-footer">
        <div className="flex items-center gap-4 overflow-x-auto flex-nowrap min-w-0 scrollbar-hide">
          <span className="flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"><kbd className="bg-amber-500/30 text-amber-200 px-2 py-0.5 rounded text-[11px] font-mono font-bold">F2</kbd>Editar Preco</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"><kbd className="bg-white/10 px-2 py-0.5 rounded text-[11px] font-mono font-bold">F3</kbd>Cliente</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"><kbd className="bg-emerald-500/30 text-emerald-300 px-2 py-0.5 rounded text-[11px] font-mono font-bold">F4</kbd>Finalizar</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"><kbd className="bg-white/10 px-2 py-0.5 rounded text-[11px] font-mono font-bold">F6</kbd>Excluir ult.</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"><kbd className="bg-white/10 px-2 py-0.5 rounded text-[11px] font-mono font-bold">F7</kbd>Cancelar tudo</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"><kbd className="bg-orange-500/30 text-orange-300 px-2 py-0.5 rounded text-[11px] font-mono font-bold">F8</kbd>Resumo</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"><kbd className="bg-violet-500/30 text-violet-300 px-2 py-0.5 rounded text-[11px] font-mono font-bold">F9</kbd>Fechamento</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"><kbd className="bg-white/10 px-2 py-0.5 rounded text-[11px] font-mono font-bold">F10</kbd>Camera</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"><kbd className="bg-rose-500/30 text-rose-300 px-2 py-0.5 rounded text-[11px] font-mono font-bold">ESC</kbd>Sair</span>
        </div>
        <span className="opacity-50 hidden lg:inline text-xs whitespace-nowrap flex-shrink-0">v1 — Modo Desktop {isOnline ? '· online' : '· offline (vendas em fila)'}</span>
      </footer>
    </div>
  );
};

/* ===== ClientSearchPanel — busca incremental de clientes (PDV mobile + desktop) =====
 * - Autocompleta por nome / telefone / documento (case-insensitive, sem acento)
 * - Atalhos teclado: setas ↑/↓ navegam, Enter seleciona, Esc fecha
 * - Opcoes fixas: "Consumidor Final" e (opcional) "+ Cadastrar novo"
 */
const ClientSearchPanel = ({ clients, selectedClient, onSelect, onClear, onClose, onQuickCreate = null, testId = 'client-search-panel' }) => {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const normalize = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // iter77ct.109: busca inteligente
  //   - Se query e majoritariamente digitos (CPF/CNPJ/telefone): substring em document/phone
  //   - Se query e texto: cada palavra do nome do cliente PODE COMECAR com o(s) token(s) digitado(s)
  //     (word-boundary prefix). Antes: substring bruta, o que retornava resultados desordenados
  //     (ex: "thali" trazia "AGUIDA THALIA" antes de "THALITA ALVES"). Agora: nomes cujo INICIO
  //     de qualquer palavra bate ficam no topo, ordenados alfabeticamente.
  const filtered = useMemo(() => {
    const raw = (query || '').trim();
    if (!raw) return clients.slice(0, 30);
    const digitsOnly = raw.replace(/\D+/g, '');
    const isNumeric = digitsOnly.length >= 3 && digitsOnly.length >= raw.length * 0.6;
    if (isNumeric) {
      const q = digitsOnly;
      return clients.filter(c => (
        normalize(c.document).includes(q) || normalize(c.phone).includes(q)
      )).slice(0, 30);
    }
    // texto: cada token deve iniciar alguma palavra do nome
    const tokens = normalize(raw).split(/\s+/).filter(Boolean);
    if (!tokens.length) return clients.slice(0, 30);
    const wordStartsWith = (name, tok) => {
      const words = normalize(name).split(/[\s\-.]+/).filter(Boolean);
      return words.some(w => w.startsWith(tok));
    };
    const matches = clients.filter(c => tokens.every(t => wordStartsWith(c.name || '', t)));
    // Ordena: primeira palavra que comeca com o token PRIMEIRO (mais relevante)
    matches.sort((a, b) => {
      const aStarts = normalize(a.name).startsWith(tokens[0]);
      const bStarts = normalize(b.name).startsWith(tokens[0]);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return (a.name || '').localeCompare(b.name || '', 'pt-BR');
    });
    return matches.slice(0, 30);
  }, [clients, query]);
  useEffect(() => { setHighlight(0); }, [query]);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 30); }, []);
  const handleKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const choice = filtered[highlight];
      if (choice) onSelect(choice);
      else if (query.trim().length === 0) onClear();
    }
    else if (e.key === 'Escape') { e.preventDefault(); onClose?.(); }
  };
  return (
    <div className="bg-white border border-violet-200 rounded-xl shadow-lg overflow-hidden" data-testid={testId}>
      <div className="p-3 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-violet-50">
        <div className="relative">
          <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-500" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Digite nome, telefone ou CPF..."
            className="w-full pl-9 pr-9 py-2 border border-slate-200 rounded-full text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-300"
            style={{ fontSize: '16px' }}
            data-testid={`${testId}-input`}
          />
          {query && <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid={`${testId}-clear`}><X size={14} /></button>}
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5 px-1">{filtered.length} cliente(s) {query && `· busca "${query}"`}</p>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {onQuickCreate && (
          <button onClick={onQuickCreate} className="w-full text-left px-4 py-2.5 text-sm hover:bg-violet-50 border-b border-slate-100 flex items-center gap-2 text-violet-700 font-bold" data-testid={`${testId}-quick-create`}>
            <Plus size={14} /><span>Cadastrar novo cliente</span>
          </button>
        )}
        <button onClick={onClear} className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 border-b border-slate-100 text-slate-700" data-testid={`${testId}-consumidor`}>
          <span className="font-medium">Consumidor Final</span> <span className="text-slate-400 text-xs">(sem identificacao)</span>
        </button>
        {filtered.map((c, idx) => (
          <button
            key={c.id}
            onClick={() => onSelect(c)}
            onMouseEnter={() => setHighlight(idx)}
            className={`w-full text-left px-4 py-2.5 text-sm border-b border-slate-50 transition-colors ${idx === highlight ? 'bg-violet-50' : 'hover:bg-slate-50'} ${selectedClient?.id === c.id ? 'border-l-2 border-l-violet-500' : ''}`}
            data-testid={`${testId}-opt-${c.id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-bold text-slate-900 truncate">{c.name}</p>
                <p className="text-[11px] text-slate-500 truncate">{[c.phone, c.document].filter(Boolean).join(' · ') || 'sem contato'}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* iter77o: chip de cashback / credito loja no canto */}
                {Number(c.cashback_balance || 0) > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-black bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full" data-testid={`${testId}-cashback-${c.id}`}>
                    🪙 R$ {Number(c.cashback_balance).toFixed(2).replace('.', ',')}
                  </span>
                )}
                {Number(c.store_credit_balance || 0) > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-black bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full" data-testid={`${testId}-credit-${c.id}`}>
                    💳 R$ {Number(c.store_credit_balance).toFixed(2).replace('.', ',')}
                  </span>
                )}
                {selectedClient?.id === c.id && <span className="text-[10px] text-violet-600 font-black">✓ ATUAL</span>}
              </div>
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="py-8 text-center text-slate-400 text-xs">
            <p className="mb-2">Nenhum cliente encontrado para "{query}"</p>
            {onQuickCreate && <button onClick={onQuickCreate} className="btn-pill-primary"><Plus size={12} />Cadastrar "{query}"</button>}
          </div>
        )}
      </div>
    </div>
  );
};

/* ===== ClientPDVModal — Cadastro rapido de cliente no PDV (3 abas) =====
 * Aba 1: CPF/CNPJ na nota?   — Apenas anexa o documento a venda (sem criar cliente)
 * Aba 2: Pesquisar Cliente    — Busca clientes existentes
 * Aba 3: Novo Cliente         — Cadastro rapido (Fisica ou Juridica)
 */
const UFS_BR = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];
const onlyDigits = (s) => (s || '').toString().replace(/\D/g, '');
const maskCPF = (v) => { const d = onlyDigits(v).slice(0,11); return d.replace(/(\d{3})(\d)/,'$1.$2').replace(/(\d{3})(\d)/,'$1.$2').replace(/(\d{3})(\d{1,2})$/,'$1-$2'); };
const maskCNPJ = (v) => { const d = onlyDigits(v).slice(0,14); return d.replace(/^(\d{2})(\d)/,'$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/,'$1.$2.$3').replace(/\.(\d{3})(\d)/,'.$1/$2').replace(/(\d{4})(\d)/,'$1-$2'); };
const maskPhone = (v) => { const d = onlyDigits(v).slice(0,11); if (d.length<=10) return d.replace(/(\d{2})(\d)/,'($1) $2').replace(/(\d{4})(\d)/,'$1-$2'); return d.replace(/(\d{2})(\d)/,'($1) $2').replace(/(\d{5})(\d)/,'$1-$2'); };

const ClientPDVModal = ({ clients, onClose, onSelectExisting, onAttachDoc, onCreated, companyId, initialTab = 'search', employees = [] }) => {
  const [tab, setTab] = useState(initialTab); // 'search' (default iter77o) | 'doc' | 'new'
  // iter141: toggle "Funcionario" na aba pesquisa — permite atribuir uma venda a um funcionario
  // (a venda entra no financeiro normalmente com a forma de pagamento escolhida).
  const [searchAsEmployee, setSearchAsEmployee] = useState(false);
  // Aba 1 — CPF/CNPJ na nota
  const [docType, setDocType] = useState('cpf');
  const [docValue, setDocValue] = useState('');
  // Aba 2 — Pesquisar
  const [searchQ, setSearchQ] = useState('');
  const normalize = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const filtered = (() => {
    const q = normalize(searchQ).trim();
    // iter141: quando checkbox "Funcionario" ativo, busca lista de funcionarios; caso contrario clientes.
    const source = searchAsEmployee ? employees : clients;
    if (!q) return source.slice(0, 30);
    if (searchAsEmployee) {
      return source.filter(e =>
        normalize(e.name).includes(q) ||
        normalize(e.phone).includes(q) ||
        normalize(e.document).includes(q) ||
        normalize(e.email).includes(q)
      ).slice(0, 30);
    }
    return source.filter(c =>
      normalize(c.name).includes(q) ||
      normalize(c.trade_name).includes(q) ||
      normalize(c.legal_name).includes(q) ||
      normalize(c.phone).includes(q) ||
      normalize(c.document).includes(q)
    ).slice(0, 30);
  })();
  const [searchSel, setSearchSel] = useState(null);
  // Aba 3 — Novo cliente
  const [personType, setPersonType] = useState('fisica');
  const [form, setForm] = useState({
    name: '', gender: '', trade_name: '', legal_name: '',
    phone: '', state: '', country: 'Brasil',
  });
  const [saving, setSaving] = useState(false);

  const handleAttachDoc = () => {
    const d = onlyDigits(docValue);
    const need = docType === 'cpf' ? 11 : 14;
    if (d.length !== need) { toast.error(`${docType.toUpperCase()} deve ter ${need} digitos`); return; }
    onAttachDoc({ doc_type: docType, document: docType === 'cpf' ? maskCPF(docValue) : maskCNPJ(docValue) });
  };

  const handleSelectSearch = () => {
    if (!searchSel) { toast.info(searchAsEmployee ? 'Selecione um funcionario da lista' : 'Selecione um cliente da lista'); return; }
    if (searchAsEmployee) {
      // iter141: monta um "cliente" com dados do funcionario. Marca is_employee para o PDVPage
      // salvar a venda com employee_id sem quebrar o fluxo padrao (mesma criacao de financeiro).
      onSelectExisting({
        id: null,
        name: searchSel.name,
        phone: searchSel.phone || '',
        document: searchSel.document || '',
        email: searchSel.email || '',
        is_employee: true,
        employee_id: searchSel.id,
      });
      return;
    }
    onSelectExisting(searchSel);
  };

  const handleSaveNew = async () => {
    if (personType === 'fisica') {
      if (!form.name.trim()) { toast.error('Nome obrigatorio'); return; }
      if (!form.gender) { toast.error('Selecione o sexo'); return; }
    } else {
      if (!form.name.trim()) { toast.error('Nome Fantasia obrigatorio'); return; }
      if (!form.legal_name.trim()) { toast.error('Razao Social obrigatoria'); return; }
    }
    const phoneDigits = onlyDigits(form.phone);
    if (phoneDigits.length < 10) { toast.error('Telefone celular invalido'); return; }
    if (!form.state) { toast.error('Selecione o Estado (UF)'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        person_type: personType,
        gender: personType === 'fisica' ? form.gender : '',
        trade_name: personType === 'juridica' ? form.name.trim() : '',
        legal_name: personType === 'juridica' ? form.legal_name.trim() : '',
        phone: maskPhone(form.phone),
        state: form.state,
        country: form.country,
        doc_type: personType === 'fisica' ? 'cpf' : 'cnpj',
        company_ids: companyId ? [companyId] : [],
      };
      const { data } = await axios.post(`${ERP}/clients`, payload, { withCredentials: true });
      toast.success(`Cliente ${data.name} cadastrado`);
      onCreated({
        id: data.id, name: data.name, phone: payload.phone, document: '',
        person_type: data.person_type, trade_name: data.trade_name, legal_name: data.legal_name, state: data.state,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cadastrar cliente');
    } finally { setSaving(false); }
  };

  const tabBtn = (k, label) => (
    <button onClick={() => setTab(k)} className={`flex-1 px-3 py-2.5 text-sm font-bold border-b-2 transition-colors ${tab === k ? 'border-orange-500 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-700'}`} data-testid={`pdv-client-tab-${k}`}>{label}</button>
  );

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" data-testid="pdv-client-modal">
      <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-slate-700" />
            <h2 className="font-black text-base text-slate-900">Cliente</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="pdv-client-close"><X size={18} /></button>
        </div>
        <div className="flex border-b border-slate-200 px-2">
          {tabBtn('doc', 'CPF/CNPJ na nota?')}
          {tabBtn('search', 'Pesquisar Cliente')}
          {tabBtn('new', 'Novo Cliente')}
        </div>

        {/* Aba 1 — CPF/CNPJ na nota */}
        {tab === 'doc' && (
          <div className="p-5 space-y-4" data-testid="pdv-client-doc-pane">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="doc-type-cpf">
                <input type="radio" checked={docType === 'cpf'} onChange={() => { setDocType('cpf'); setDocValue(''); }} />
                <span className="text-sm font-semibold text-slate-700">CPF</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="doc-type-cnpj">
                <input type="radio" checked={docType === 'cnpj'} onChange={() => { setDocType('cnpj'); setDocValue(''); }} />
                <span className="text-sm font-semibold text-slate-700">CNPJ</span>
              </label>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">* {docType.toUpperCase()}:</label>
              <input
                value={docValue}
                onChange={(e) => setDocValue(docType === 'cpf' ? maskCPF(e.target.value) : maskCNPJ(e.target.value))}
                placeholder={docType === 'cpf' ? 'XXX.XXX.XXX-XX' : 'XX.XXX.XXX/XXXX-XX'}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-orange-500 focus:outline-none"
                style={{ fontSize: '16px' }}
                data-testid="pdv-client-doc-input"
              />
              <p className="text-[11px] text-slate-400 mt-1.5">Documento sera incluido na nota fiscal sem criar cadastro completo.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={handleAttachDoc} className="px-4 py-2 rounded-lg border border-blue-500 text-blue-700 hover:bg-blue-50 text-sm font-bold flex items-center gap-2" data-testid="pdv-client-doc-confirm">
                <Check size={14} /><span>Selecionar</span>
              </button>
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-bold flex items-center gap-2"><X size={14} /><span>Fechar</span></button>
            </div>
          </div>
        )}

        {/* Aba 2 — Pesquisar Cliente */}
        {tab === 'search' && (
          <div className="p-5 space-y-3" data-testid="pdv-client-search-pane">
            <div className="flex items-center justify-between gap-3">
              <label className="block text-xs font-bold text-slate-500">* Localize {searchAsEmployee ? 'o Funcionario' : 'o Cliente'}:</label>
              {/* iter141: toggle discreto "Funcionario" — troca a fonte da busca para funcionarios */}
              <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="pdv-client-search-employee-toggle">
                <input
                  type="checkbox"
                  checked={searchAsEmployee}
                  onChange={(e) => { setSearchAsEmployee(e.target.checked); setSearchSel(null); setSearchQ(''); }}
                  className="w-3.5 h-3.5 accent-orange-500 cursor-pointer"
                />
                <span className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 transition-colors">Funcionario</span>
              </label>
            </div>
            <div className="relative">
              <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={searchQ}
                onChange={(e) => { setSearchQ(e.target.value); setSearchSel(null); }}
                placeholder={searchAsEmployee ? 'Nome do funcionario, telefone, CPF...' : 'Nome, telefone, CPF/CNPJ...'}
                className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-orange-500 focus:outline-none"
                style={{ fontSize: '16px' }}
                data-testid="pdv-client-search-input"
                autoFocus
              />
            </div>
            <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-lg">
              {filtered.length === 0 && <div className="py-10 text-center text-slate-400 text-xs">{searchAsEmployee ? 'Nenhum funcionario encontrado' : 'Nenhum cliente encontrado'}</div>}
              {filtered.map(c => (
                <button
                  key={c.id}
                  onClick={() => setSearchSel(c)}
                  onDoubleClick={() => searchAsEmployee ? handleSelectSearch() : onSelectExisting(c)}
                  className={`w-full text-left px-3 py-2 border-b border-slate-50 text-sm transition-colors ${searchSel?.id === c.id ? 'bg-orange-50 border-l-4 border-l-orange-500' : 'hover:bg-slate-50'}`}
                  data-testid={`pdv-client-search-opt-${c.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      {/* iter156ac: PJ mostra Nome Fantasia como principal (mais amigavel na busca do NF-e).
                          Razao Social vira secundaria em azul. PF mantem c.name. */}
                      <div className="font-bold text-slate-900 truncate">
                        {(c.doc_type === 'cnpj' && (c.trade_name || c.legal_name)) ? (c.trade_name || c.legal_name) : c.name}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {c.doc_type === 'cnpj' && c.trade_name && c.legal_name && c.trade_name !== c.legal_name ? (
                          <span className="text-sky-700 font-semibold">{c.legal_name}</span>
                        ) : null}
                        {c.doc_type === 'cnpj' && c.trade_name && c.legal_name && c.trade_name !== c.legal_name ? ' · ' : ''}
                        {[c.phone, c.document].filter(Boolean).join(' · ') || (searchAsEmployee ? (c.type || 'funcionario') : 'sem contato')}
                      </div>
                    </div>
                    {/* iter141: chip do tipo do funcionario quando em modo funcionario; senao cashback/credito loja */}
                    {searchAsEmployee ? (
                      <span className="inline-flex items-center text-[10px] font-black bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full uppercase" data-testid={`pdv-emp-type-${c.id}`}>
                        {c.type || 'funcionario'}
                      </span>
                    ) : (
                      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                        {Number(c.cashback_balance || 0) > 0 && (
                          <span className="inline-flex items-center text-[10px] font-black bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full" data-testid={`pdv-client-cashback-${c.id}`}>
                            🪙 R$ {Number(c.cashback_balance).toFixed(2).replace('.', ',')}
                          </span>
                        )}
                        {Number(c.store_credit_balance || 0) > 0 && (
                          <span className="inline-flex items-center text-[10px] font-black bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full" data-testid={`pdv-client-credit-${c.id}`}>
                            💳 R$ {Number(c.store_credit_balance).toFixed(2).replace('.', ',')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={handleSelectSearch} className="px-4 py-2 rounded-lg border border-blue-500 text-blue-700 hover:bg-blue-50 text-sm font-bold flex items-center gap-2" data-testid="pdv-client-search-confirm">
                <Check size={14} /><span>Selecionar</span>
              </button>
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-bold flex items-center gap-2"><X size={14} /><span>Fechar</span></button>
            </div>
          </div>
        )}

        {/* Aba 3 — Novo Cliente */}
        {tab === 'new' && (
          <div className="p-5 space-y-4" data-testid="pdv-client-new-pane">
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1.5">Pessoa:</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="person-type-fisica">
                  <input type="radio" checked={personType === 'fisica'} onChange={() => setPersonType('fisica')} />
                  <span className="text-sm font-semibold text-slate-700">Fisica</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="person-type-juridica">
                  <input type="radio" checked={personType === 'juridica'} onChange={() => setPersonType('juridica')} />
                  <span className="text-sm font-semibold text-slate-700">Juridica</span>
                </label>
              </div>
            </div>

            {personType === 'fisica' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-1">
                  <label className="block text-xs font-bold text-slate-500 mb-1">* Nome:</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nome..." className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-orange-500 focus:outline-none" style={{ fontSize: '16px' }} data-testid="new-client-name" />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-xs font-bold text-slate-500 mb-1">* Sexo:</label>
                  <select value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))} className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:border-orange-500 focus:outline-none" data-testid="new-client-gender">
                    <option value="">Selecione</option>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">* Nome Fantasia:</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nome..." className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-orange-500 focus:outline-none" style={{ fontSize: '16px' }} data-testid="new-client-trade" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">* Razao Social:</label>
                  <input value={form.legal_name} onChange={e => setForm(f => ({ ...f, legal_name: e.target.value }))} placeholder="Razao Social..." className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-orange-500 focus:outline-none" style={{ fontSize: '16px' }} data-testid="new-client-legal" />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">* Telefone Celular:</label>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: maskPhone(e.target.value) }))} placeholder="(00) 00000-0000" className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-orange-500 focus:outline-none" style={{ fontSize: '16px' }} inputMode="tel" data-testid="new-client-phone" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">* Estado:</label>
                <select value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:border-orange-500 focus:outline-none" data-testid="new-client-state">
                  <option value="">Selecione</option>
                  {UFS_BR.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">* Pais:</label>
                <select value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:border-orange-500 focus:outline-none" data-testid="new-client-country">
                  <option value="Brasil">Brasil</option>
                </select>
              </div>
            </div>

            <a href="#/clientes" onClick={(e) => { e.preventDefault(); onClose?.(); window.dispatchEvent(new CustomEvent('erp:navigate', { detail: { section: 'clients' } })); }} className="inline-flex items-center gap-1.5 text-blue-600 hover:underline text-sm font-semibold" data-testid="new-client-full-link">
              <ExternalLink size={13} /><span>Acessar cadastro completo</span>
            </a>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button onClick={handleSaveNew} disabled={saving} className="px-4 py-2 rounded-lg border border-blue-500 text-blue-700 hover:bg-blue-50 disabled:opacity-50 text-sm font-bold flex items-center gap-2" data-testid="new-client-save">
                <Save size={14} /><span>{saving ? 'Salvando...' : 'Salvar'}</span>
              </button>
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-bold flex items-center gap-2"><X size={14} /><span>Fechar</span></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


const PDVPage = ({ companyId, onSaleComplete, saleType = 'venda', initialItems = null, initialPedidoId = null, initialSaleDate = null, editPedidoId = null, editPedidoType = null, initialClient = null, initialVendedorId = null, initialPaymentMethod = '', initialPayments = null, initialDiscount = 0, initialSurcharge = 0, initialFreight = 0, initialNotes = '', fullscreen = false, onToggleFullscreen = null, onClose = null, canEditItem = true, pdvPerms: pdvPermsProp = null }) => {
  const { slug = '' } = useParams();
  const { isPharmacy, isCashback } = useBusinessType();
  const [mode, setMode] = useState('pdv'); // 'pdv' | 'catalog'
  const [showSummary, setShowSummary] = useState(false);
  // iter77bo: novos modais — fechamento de caixa + lancamento de despesa
  const [showClosure, setShowClosure] = useState(false);
  const [showExpense, setShowExpense] = useState(false);
  // iter156u: modal "Consultar Itens" — tabela sortable com Cod Interno/Barras/Nome/Valor/Estoque
  const [showProductBrowser, setShowProductBrowser] = useState(false);
  // iter77w: inicializa SINCRONAMENTE com base no viewport para evitar flash mobile->desktop.
  // Async loader so DESCE para mobile se o tenant tiver pdv_desktop_auto=false.
  const [desktopMode, setDesktopMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return window.matchMedia('(min-width: 1024px)').matches; } catch { return false; }
  });
  const [manualConfirmDefault, setManualConfirmDefault] = useState(false); // from /company-settings: pdv_manual_confirm
  const [allowDateEdit, setAllowDateEdit] = useState(false); // from /company-settings: pdv_allow_date_edit
  const [allowNFe, setAllowNFe] = useState(false);   // pdv_emit_nfe
  const [allowNFCe, setAllowNFCe] = useState(false); // pdv_emit_nfce
  const [defaultNatopId, setDefaultNatopId] = useState(''); // pdv_default_natop_id
  const [natops, setNatops] = useState([]);
  // iter77bg: cert A1 status (used to render NFe/NFCe split-button options as "disabled/riscado" when missing)
  const [certConfigured, setCertConfigured] = useState(false);
  // iter77w: setting de estoque — quando true permite vender mesmo sem saldo
  const [allowNegativeStockSale, setAllowNegativeStockSale] = useState(false);
  // iter156gj: perfil pode ocultar a quantidade em estoque; so mostra "sem estoque" quando a filial controla (bloqueia negativo)
  const hideStockQty = !!pdvPermsProp?.hide_stock_qty;
  const stockLabel = (stk) => {
    const n = Number(stk || 0);
    if (hideStockQty) return (n <= 0 && !allowNegativeStockSale) ? 'sem estoque' : null;
    return n > 0 ? `${n} em estoque` : 'sem estoque';
  };
  const stockShortLabel = (stk) => {
    const n = Number(stk || 0);
    if (hideStockQty) return (n <= 0 && !allowNegativeStockSale) ? 'Sem estoque' : null;
    return `Est: ${n}`;
  };
  const stockToast = (name, stk) => hideStockQty ? `${name}: sem estoque suficiente` : `${name} tem apenas ${stk} em estoque`;
  // iter156ea: pdv_no_group_items — cada bipagem do mesmo produto vira linha nova (nao soma qty)
  const [noGroupItems, setNoGroupItems] = useState(false);
  // iter77db: Distribuir Lojas (venda) — quando ON, cada item permite alocar quantidades por loja informativa do cliente
  const [distributeStoresEnabled, setDistributeStoresEnabled] = useState(false);
  const [distributeModalIdx, setDistributeModalIdx] = useState(null); // idx do item em edicao no modal
  // iter77dc: modelo de impressao de venda (Configuracao > Venda)
  const [printSaleModel, setPrintSaleModel] = useState('');
  // iter156eh: ao confirmar pedido, a data inicial e a do PEDIDO (origem) — nao a de hoje
  const [saleDate, setSaleDate] = useState(() => initialSaleDate || localDateISO());
  // Auto-open desktop mode if (a) screen is desktop-sized AND (b) tenant setting `pdv_desktop_auto` is enabled.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let mounted = true;
    (async () => {
      try {
        const [{ data: comp }, { data: cs }, natRes, certRes] = await Promise.all([
          axios.get(`${ERP}/companies?company_id=${companyId}`, { withCredentials: true }),
          axios.get(`${ERP}/company-settings${companyId ? `?company_id=${companyId}` : ''}`, { withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(`${ERP}/natureza-operacao?active=true&tipo=saida`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
          axios.get(`${ERP}/fiscal-cert${companyId ? `?company_id=${companyId}` : ''}`, { withCredentials: true }).catch(() => ({ data: { data: null } })),
        ]);
        const company = (comp.data || []).find(c => c.id === companyId);
        const settings = company?.settings || {};
        const autoOn = settings.pdv_desktop_auto !== false; // default ON
        // iter77w: so DESCE para mobile se o tenant explicitamente desativou desktop auto.
        // O estado ja inicializou com base no viewport — nao precisa subir aqui (evita flash).
        if (mounted && !autoOn) setDesktopMode(false);
        if (mounted && cs && cs.pdv_manual_confirm) setManualConfirmDefault(true);
        if (mounted && cs && cs.pdv_allow_date_edit) setAllowDateEdit(true);
        if (mounted && cs) setNoGroupItems(!!cs.pdv_no_group_items);
        if (mounted && cs && cs.pdv_emit_nfe) setAllowNFe(true);
        if (mounted && cs && cs.pdv_emit_nfce) setAllowNFCe(true);
        if (mounted && cs && cs.pdv_default_natop_id) setDefaultNatopId(cs.pdv_default_natop_id);
        if (mounted && cs && cs.distribute_stores_enabled) setDistributeStoresEnabled(true);
        if (mounted && cs && cs.print_sale_model) setPrintSaleModel(String(cs.print_sale_model));
        // iter77bg: cert A1 configurado? (sem certificado, NFe/NFCe aparecem riscadas)
        if (mounted) {
          const certData = certRes?.data?.data;
          setCertConfigured(!!certData && !certData.expired);
        }
        // iter77w + iter77ct.31: le permissao de venda com estoque negativo.
        // Prioridade (espelha backend erp.py:4273): 1) per-filial (company.allow_negative_stock_sale)
        // se explicitamente true/false, ganha. 2) fallback global tenant.
        if (mounted) {
          const branchFlag = (company && (company.allow_negative_stock_sale === true || company.allow_negative_stock_sale === false))
            ? company.allow_negative_stock_sale
            : null;
          if (branchFlag !== null) setAllowNegativeStockSale(!!branchFlag);
          else if (cs) setAllowNegativeStockSale(!!cs.allow_negative_stock_sale);
        }
        if (mounted) setNatops(natRes.data?.data || []);
      } catch { /* mantem o estado inicial baseado em viewport */ }
    })();
    return () => { mounted = false; };
  }, [companyId]);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [queueSize, setQueueSize] = useState(typeof window !== 'undefined' ? readQueue().length : 0);
  const [lastAdded, setLastAdded] = useState(null);
  const [priceTables, setPriceTables] = useState([]);
  const [priceTableId, setPriceTableId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [items, setItems] = useState(initialItems || []);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  // iter141: lista completa de funcionarios (todos os tipos) para o picker "Funcionario" no PDV
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedClient, setSelectedClient] = useState(initialClient);
  // iter77bz: quando o PDV abre em uma filial sem cliente pré-selecionado (venda nova sem hidratar de pedido),
  // busca a filial e usa seu default_client_id para pré-selecionar o "Consumidor <Nome Filial>".
  useEffect(() => {
    if (initialClient || !companyId) return;
    if (selectedClient) return; // não sobrescreve seleção manual
    let cancelled = false;
    (async () => {
      try {
        const { data: comp } = await axios.get(`${ERP}/companies/${companyId}`, { withCredentials: true });
        if (cancelled) return;
        const defId = comp?.default_client_id;
        if (!defId) return;
        const { data: cl } = await axios.get(`${ERP}/clients/${defId}`, { withCredentials: true });
        if (cancelled) return;
        if (cl?.id) setSelectedClient({ id: cl.id, name: cl.name, document: cl.document });
      } catch (_e) { /* silencioso */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, initialClient]);
  // iter156at: vendedor permanece selecionado entre vendas ate o usuario trocar
  // ou sair da tela do PDV. Persistimos em sessionStorage (por companyId) para
  // sobreviver ao reset pos-finalizacao.
  const _vendedorStorageKey = `pdv:vendedor:${companyId || 'nocomp'}`;
  const [vendedorId, setVendedorId] = useState(() => {
    if (initialVendedorId) return initialVendedorId;  // pedido em edicao tem prioridade
    try {
      const v = window.sessionStorage.getItem(_vendedorStorageKey);
      return v || '';
    } catch { return ''; }
  });
  // Persist mudancas no sessionStorage
  useEffect(() => {
    try {
      if (vendedorId) window.sessionStorage.setItem(_vendedorStorageKey, vendedorId);
      else window.sessionStorage.removeItem(_vendedorStorageKey);
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendedorId, companyId]);
  // Limpa quando o componente PDV desmonta (usuario sai da tela)
  useEffect(() => {
    return () => {
      try { window.sessionStorage.removeItem(_vendedorStorageKey); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  const [paymentMethod, setPaymentMethod] = useState(initialPaymentMethod);
  // iter156r: rastreia se o usuario EXPLICITAMENTE selecionou uma forma de pagamento
  // (chip mobile clicado ou botao "+" pressionado no desktop). Auto-fill via settings
  // (default_payment_method) NAO seta esta flag — evita venda salva silenciosamente
  // com forma pre-preenchida quando o operador so clicou em "Finalizar".
  // Inicia `true` quando ha initialPaymentMethod/initialPayments (pedido em edicao).
  const [paymentTouched, setPaymentTouched] = useState(
    Boolean(initialPaymentMethod) || Boolean(Array.isArray(initialPayments) && initialPayments.length > 0)
  );
  // iter77ct.53: rastreia o PM.id (do DB) selecionado no mobile PDV, para o backend rotear ao banco correto.
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [pickInstallments, setPickInstallments] = useState(1);  // iter156et: parcelas (cartao) — lifted p/ desktop + mobile
  const [customerCredits, setCustomerCredits] = useState([]);
  const [creditApplied, setCreditApplied] = useState(0);
  const [discountType, setDiscountType] = useState('value');
  const [discountInput, setDiscountInput] = useState(initialDiscount ? String(initialDiscount) : '');
  // Acréscimo (surcharge): separate from discount; can be R$ or %
  // iter77al: quando hidratado de um pedido, o valor salvo eh absoluto (R$) — forcar type='value'.
  const [surchargeType, setSurchargeType] = useState(initialSurcharge ? 'value' : 'value');
  const [surchargeInput, setSurchargeInput] = useState(initialSurcharge ? String(initialSurcharge) : '');
  // iter77ah: Frete — campo separado, default em % (conforme pedido do usuario), entra no total e no cupom.
  // iter77al: quando hidratado de um pedido, o valor salvo eh absoluto (R$) — forcar type='value'.
  const [freightType, setFreightType] = useState(initialFreight ? 'value' : 'percent');
  const [freightInput, setFreightInput] = useState(initialFreight ? String(initialFreight) : '');
  // Multi-payment state (lifted from DesktopPDV so the API payload uses it)
  // iter77al: hydrate from saved pedido (initialPayments) or auto-build a single-entry from initialPaymentMethod+total.
  const [payments, setPayments] = useState(() => {
    if (Array.isArray(initialPayments) && initialPayments.length > 0) {
      return initialPayments
        .filter(p => Number(p.amount) > 0)
        .map(p => ({ method: p.method || p.type || '', label: p.label || p.method || '', amount: Number(p.amount) || 0 }));
    }
    // Fallback: when only payment_method was saved (legacy or single-payment pedido),
    // synthesize one payment line covering the saved total so RESTANTE=0 and FINALIZAR can proceed.
    if (initialPaymentMethod && Array.isArray(initialItems) && initialItems.length > 0) {
      const sub = initialItems.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
      const t = Math.max(0, sub - (Number(initialDiscount) || 0) + (Number(initialSurcharge) || 0) + (Number(initialFreight) || 0));
      if (t > 0) return [{ method: initialPaymentMethod, label: initialPaymentMethod, amount: t }];
    }
    return [];
  });
  // Post-finalize modal data (sale object with id/number/total)
  const [postSaleData, setPostSaleData] = useState(null);
  // iter77bf: acao escolhida no split-button "Finalizar Venda" — passed as parameter to finalizeSale to avoid stale closure
  // PDV granular permissions resolved from /api/erp/allowed-modules
  // iter77ct.102: default RESTRITIVO enquanto a API nao responde — evita janela de
  // vulnerabilidade onde o usuario poderia acabar aproveitando o fallback permissivo
  // antes do prop chegar. Sync sempre que pdvPermsProp mudar (mesmo quando vira null).
  const [pdvPerms, setPdvPerms] = useState(pdvPermsProp || { edit_unit_price: false, give_discount: false, max_discount_pct: 0, give_surcharge: false });
  useEffect(() => { if (pdvPermsProp !== undefined) setPdvPerms(pdvPermsProp || { edit_unit_price: false, give_discount: false, max_discount_pct: 0, give_surcharge: false }); }, [pdvPermsProp]);
  // iter140: defensive self-fetch — if parent did not deliver pdvPermsProp (prop-drill omission),
  // hit /api/erp/allowed-modules directly to hydrate real permissions. Prevents restrictive fallback lock-in.
  useEffect(() => {
    if (pdvPermsProp) return; // parent already delivered
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/allowed-modules`, { withCredentials: true });
        const perms = data?.pdv_perms;
        if (!cancelled && perms) {
          setPdvPerms({
            edit_unit_price: !!perms.edit_unit_price,
            give_discount: !!perms.give_discount,
            max_discount_pct: Number(perms.max_discount_pct ?? 0),
            give_surcharge: !!perms.give_surcharge,
          });
        }
      } catch (_) { /* keep restrictive default */ }
    })();
    return () => { cancelled = true; };
  }, [pdvPermsProp]);
  const [notes, setNotes] = useState(initialNotes || '');
  const [processing, setProcessing] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showClientPicker, setShowClientPicker] = useState(false);
  // Pharmacy state: prescription captured during checkout flow (for controlled meds)
  const [prescriptionId, setPrescriptionId] = useState(null);
  const [prescriptionLabel, setPrescriptionLabel] = useState('');
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [ignoreCmedBlock, setIgnoreCmedBlock] = useState(false);
  // Pharmacy lot picker — opens automatically when adding a controlled med
  const [pendingLotProduct, setPendingLotProduct] = useState(null);
  // Cashback (iter76) — balance fetched on client change (only when module enabled)
  const [cashbackBalance, setCashbackBalance] = useState(0);
  const [cashbackAmountInput, setCashbackAmountInput] = useState('');
  // iter77af: formas de pagamento ATIVAS no PDV (scope=sales). Inclui cashback condicional
  // baseado em client_id (regra aplicada no backend). Estados das chaves: dinheiro/pix/debito/credito/cashback/outro/...
  const [activePmTypes, setActivePmTypes] = useState(null); // null = ainda carregando; Set => keys ativas
  const [activePmList, setActivePmList] = useState([]); // iter77ct.53: lista completa de PMs (para PDV mostrar por nome)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const qp = new URLSearchParams({ scope: 'sales' });
        if (selectedClient?.id) qp.set('client_id', selectedClient.id);
        if (companyId) qp.set('company_id', companyId); // iter77bq: filtra formas por filial
        // iter143: busca em paralelo settings pra hidratar o default payment method (auto-select no PDV)
        const [pmRes, settingsRes] = await Promise.allSettled([
          axios.get(`${ERP}/payment-methods?${qp.toString()}`, { withCredentials: true }),
          axios.get(`${ERP}/company-settings${companyId ? `?company_id=${companyId}` : ''}`, { withCredentials: true }),
        ]);
        if (!alive) return;
        const pms = pmRes.status === 'fulfilled' ? (pmRes.value.data?.data || []) : [];
        const types = new Set(pms.map(m => String(m.type || '').toLowerCase()).filter(Boolean));
        setActivePmTypes(types);
        setActivePmList(pms);
        // iter143: auto-select do payment method padrao — SOMENTE quando (a) nenhum PM ja selecionado
        // (paymentMethod vazio) e (b) settings tem default_payment_method configurado. Prioridade
        // de match: code, name (case-insensitive), type. Seta tanto paymentMethod (type/key) quanto
        // paymentMethodId (para o backend rotear no PM correto e evitar bug dos buckets duplicados iter142).
        if (!paymentMethod && settingsRes.status === 'fulfilled') {
          const dpm = String(settingsRes.value.data?.default_payment_method || '').trim().toLowerCase();
          if (dpm) {
            const chosen = pms.find(p =>
              (p.code || '').toLowerCase() === dpm ||
              (p.name || '').toLowerCase() === dpm ||
              (p.type || '').toLowerCase() === dpm
            );
            if (chosen) {
              setPaymentMethod(String(chosen.type || '').toLowerCase() || chosen.name);
              setPaymentMethodId(chosen.id);
            }
          }
        }
      } catch { if (alive) { setActivePmTypes(new Set()); setActivePmList([]); } }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.id, companyId]);
  useEffect(() => {
    if (!isCashback || !selectedClient?.id) { setCashbackBalance(0); setCashbackAmountInput(''); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/erp/cashback/balance/${selectedClient.id}`, { withCredentials: true });
        if (alive) setCashbackBalance(Number(data.balance || 0));
      } catch { /* silently ignore */ }
    })();
    return () => { alive = false; };
  }, [isCashback, selectedClient?.id]);
  // Catalog mode state
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const today = new Date().toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  const noZoomStyle = { fontSize: '16px' };
  const isPedido = saleType === 'pedido';

  useEffect(() => {
    const f = async () => {
      // iter77bi: Promise.allSettled para resiliencia — uma falha (ex: 403 em /clients para operator)
      // nao deve abortar setEmployees/setPriceTables. Cada chamada e processada independentemente.
      const results = await Promise.allSettled([
        // iter77ct.109 (Bug tenant Ferragista/VestStock 19k+ clientes): eleva limite p/ 50000.
        // Backend hard-cap 100000. Antes cortava em 5000 => THALITA importada nao aparecia.
        axios.get(`${ERP}/clients?limit=50000${companyId ? `&company_id=${companyId}` : ''}`, { withCredentials: true }),
        axios.get(`${ERP}/employees?type=vendedor${companyId ? `&company_id=${companyId}` : ''}`, { withCredentials: true }),
        axios.get(`${ERP}/price-tables/active?scope=${isPedido ? 'pedido' : 'pdv'}${companyId ? `&company_id=${companyId}` : ''}`, { withCredentials: true }),
        // iter141: fetch todos os funcionarios (qualquer tipo) para o picker "Funcionario" no client search
        axios.get(`${ERP}/employees${companyId ? `?company_id=${companyId}` : ''}`, { withCredentials: true }),
      ]);
      const [c, e, pt, allE] = results;
      if (c.status === 'fulfilled') setClients(c.value.data.data || []);
      const emps = e.status === 'fulfilled' ? (e.value.data.data || []) : [];
      setEmployees(emps);
      setAllEmployees(allE.status === 'fulfilled' ? (allE.value.data.data || []) : emps);
      // iter77bi: Auto-link vendedor: prioriza linked_user_id (vinculo explicito via cadastro de Usuario),
      // depois fallback para match por email do usuario logado.
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        const myId = me.data?.id || me.data?.user_id || me.data?._id || '';
        const myEmail = (me.data?.email || '').trim().toLowerCase();
        if (!vendedorId) {
          let match = null;
          if (myId) match = emps.find(emp => emp.linked_user_id && String(emp.linked_user_id) === String(myId));
          if (!match && myEmail) match = emps.find(emp => (emp.email || '').trim().toLowerCase() === myEmail);
          if (match) setVendedorId(match.id);
        }
      } catch { /* anonymous or no /me */ }
      if (pt.status === 'fulfilled') {
        const tables = pt.value.data.data || [];
        setPriceTables(tables);
        const def = tables.find(t => t.is_default);
        if (def) setPriceTableId(def.id);
      }
    };
    f();
  }, [isPedido]);



  useEffect(() => {
    if (mode !== 'catalog') return;
    const f = async () => {
      try { const { data } = await axios.get(`${ERP}/pdv/catalog/categories`, { withCredentials: true }); setCategories(data.data || []); }
      catch { /* ignore */ }
    };
    f();
  }, [mode]);

  const fetchCatalogProducts = useCallback(async () => {
    if (mode !== 'catalog') return;
    setCatalogLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.set('category_id', selectedCategory);
      if (catalogSearch) params.set('q', catalogSearch);
      if (priceTableId) params.set('price_table_id', priceTableId);
      if (companyId) params.set('company_id', companyId);
      params.set('limit', '200');
      const { data } = await axios.get(`${ERP}/pdv/products?${params.toString()}`, { withCredentials: true });
      setCatalogProducts(data.data || []);
    } catch { /* ignore */ } finally { setCatalogLoading(false); }
  }, [mode, selectedCategory, catalogSearch, priceTableId, companyId]);

  useEffect(() => {
    const t = setTimeout(fetchCatalogProducts, 250);
    return () => clearTimeout(t);
  }, [fetchCatalogProducts]);

  const searchProducts = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    try {
      const params = new URLSearchParams({ q });
      if (priceTableId) params.set('price_table_id', priceTableId);
      if (companyId) params.set('company_id', companyId);
      const { data } = await axios.get(`${ERP}/pdv/products?${params.toString()}`, { withCredentials: true });
      setResults(data.data || []);
    }
    catch { /* ignore */ }
  }, [priceTableId, companyId]);

  useEffect(() => {
    const timer = setTimeout(() => searchProducts(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchProducts]);

  const _addItemActual = (product, lotInfo = null, addQty = 1) => {
    // iter77dc.8: aceita quantidade fracionada (ex: 3.5 kg) — parseFloat com fallback
    const parsed = parseFloat(String(addQty ?? '1').replace(',', '.'));
    const qtyToAdd = (isFinite(parsed) && parsed > 0) ? parsed : 1;
    const stock = product.stock != null ? Number(product.stock) : null;
    const sameLines = items.filter(i => i.product_id === product.id && (i.lot || '') === (lotInfo?.lot || '') && (i.variant_id || '') === (product.variant_id || ''));
    // iter156ea: com pdv_no_group_items ligado, cada bipagem vira uma linha nova (nunca agrupa).
    const existing = noGroupItems ? null : sameLines[0];
    const currentQty = sameLines.reduce((s, i) => s + Number(i.quantity || 0), 0);
    // iter77w: bloqueio so quando setting allow_negative_stock_sale=false (default).
    if (!allowNegativeStockSale && stock !== null && currentQty + qtyToAdd > stock) {
      toast.error(stockToast(product.name, stock));
      return;
    }
    // iter77ct.99: defesa em profundidade — se o produto vem com _original_price (override
    // no PDV desktop), garantimos que o price commitado nunca fique abaixo dele quando o
    // usuario nao tem `edit_unit_price`. Corrige possiveis vazamentos de UI que fossem descobertos.
    const originalPrice = Number(product._original_price ?? product.price);
    const finalPrice = Number(product.price);
    if (!pdvPerms?.edit_unit_price && isFinite(originalPrice) && isFinite(finalPrice) && finalPrice < originalPrice) {
      toast.error(`Sem permissao para reduzir o preco. Valor minimo: R$ ${originalPrice.toFixed(2)}`);
      return;
    }
    // iter77cc: uso funcional de setItems + parâmetro qtyToAdd — corrige bug em que loop
    // externo de N chamadas só carregava 1 unidade por causa de closure com `items` stale.
    if (existing) {
      setItems(prev => prev.map(i => (i.product_id === product.id && (i.lot || '') === (lotInfo?.lot || '') && (i.variant_id || '') === (product.variant_id || '')) ? { ...i, quantity: i.quantity + qtyToAdd, total: (i.quantity + qtyToAdd) * i.unit_price } : i));
    } else {
      setItems(prev => [...prev, { product_id: product.id, name: product.name, sku: product.sku, variant_id: product.variant_id || null, variant_label: product.variant_label || '', quantity: qtyToAdd, unit_price: finalPrice, original_unit_price: originalPrice, base_price: Number(product.base_price ?? product.price) || 0, total: finalPrice * qtyToAdd, image: product.images?.[0] || '', stock: product.stock, pharmacy: product.pharmacy || null, lot: lotInfo?.lot || '', expiry_date: lotInfo?.expiry_date || '' }]);
    }
    setLastAdded({ name: product.name, sku: product.sku, price: product.price, image: product.images?.[0] || '', stock: product.stock, pharmacy: product.pharmacy || null });
    if (mode === 'pdv') { setSearchTerm(''); setResults([]); }
    // Pharmacy alerts on add
    if (isPharmacy && product.pharmacy) {
      const t = tarjaInfo(product.pharmacy.tarja);
      if (product.pharmacy.controlled) {
        const lotMsg = lotInfo?.lot ? ` Lote ${lotInfo.lot}` : '';
        toast.error(`${product.name} - CONTROLADO (${product.pharmacy.controlled_list || 'lista'})${lotMsg} - exige receita`, { duration: 3500, id: `ctrl-${product.id}-${lotInfo?.lot || ''}` });
      } else if (t && (product.pharmacy.tarja || '').toLowerCase() !== 'sem_tarja') {
        toast(`${product.name} - ${t.label}`, { duration: 1800, id: `tarja-${product.id}` });
      } else {
        toast.success(product.name, { duration: 1200 });
      }
      // Cross-check allergy with selected client (if any)
      const allergies = (selectedClient && selectedClient.pharmacy && selectedClient.pharmacy.allergies) || '';
      if (allergies) {
        const hits = cartAllergyHits([{ name: product.name, pharmacy: product.pharmacy }], allergies);
        if (hits.length > 0) {
          toast.error(`ALERGIA: ${selectedClient.name} e alergico(a) a ${hits[0].active_ingredient || hits[0].term}`, { duration: 5000, id: `allergy-${product.id}` });
        }
      }
    } else {
      toast.success(product.name, { duration: 1200 });
    }
  };

  const addItem = (product, addQty = 1) => {
    // Pharmacy: when the product is controlled, OPEN LOT PICKER FIRST.
    // The picker confirms which lot is being sold (FEFO + Anvisa rastreio).
    if (isPharmacy && product?.pharmacy?.controlled) {
      setPendingLotProduct(product);
      return;
    }
    _addItemActual(product, null, addQty);
  };

  const handleQRScan = async (code) => {
    setShowScanner(false);
    try {
      const { data } = await axios.get(`${ERP}/pdv/products?q=${code}${companyId ? `&company_id=${companyId}` : ''}${priceTableId ? `&price_table_id=${priceTableId}` : ''}`, { withCredentials: true });
      if (data.data?.length > 0) {
        addItem(data.data[0]);
      } else {
        toast.error(`Produto nao encontrado: ${code}`);
        setSearchTerm(code);
      }
    } catch { toast.error('Erro na busca'); }
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateQty = (idx, delta) => setItems(items.map((i, j) => {
    if (j !== idx) return i;
    const next = Math.max(1, i.quantity + delta);
    // iter156ea: soma as outras linhas do mesmo produto (modo "nao agrupar")
    const otherQty = items.reduce((s, o, k) => (k !== idx && o.product_id === i.product_id && (o.lot || '') === (i.lot || '')) ? s + Number(o.quantity || 0) : s, 0);
    // iter77w: bloqueio so quando allow_negative_stock_sale=false (default)
    if (!allowNegativeStockSale && delta > 0 && i.stock != null && otherQty + next > Number(i.stock)) {
      toast.error(stockToast(i.name, i.stock));
      return i;
    }
    return { ...i, quantity: next, total: next * i.unit_price };
  }));
  // iter77dc.8: aceita quantidade fracionada (ex: 3.5 kg) editada direto no input
  const setQty = (idx, rawValue) => {
    // iter156dt2: aplica mascara auto quando unit=KG/L/M — pt-BR ("500" KG -> "0,500").
    const item = items[idx];
    const _u = (item?.fiscal?.unit_commercial || item?.unit || 'UN');
    const masked = smartQtyMask(rawValue, _u);
    if (masked === '') return; // ignora vazio momentaneamente enquanto digita
    const val = smartQtyParse(masked);
    if (!isFinite(val) || val <= 0) return;
    setItems(items.map((i, j) => {
      if (j !== idx) return i;
      const otherQty = items.reduce((s, o, k) => (k !== idx && o.product_id === i.product_id && (o.lot || '') === (i.lot || '')) ? s + Number(o.quantity || 0) : s, 0);
      if (!allowNegativeStockSale && i.stock != null && otherQty + val > Number(i.stock)) {
        toast.error(stockToast(i.name, i.stock));
        const cap = Math.max(0, Number(i.stock) - otherQty);
        return { ...i, quantity: cap, total: cap * i.unit_price };
      }
      return { ...i, quantity: val, total: val * i.unit_price };
    }));
  };
  const itemQty = (productId) => items.reduce((s, i) => i.product_id === productId ? s + Number(i.quantity || 0) : s, 0);
  const subtotal = items.reduce((s, i) => s + i.total, 0);
  // iter156bj: "Sem Pagamento" (cortesia) — quando selecionado, zera o total da venda automaticamente.
  const hasSemPagamento = payments.some(p => p.method === 'sem_pagamento');
  // Cap discount input by max allowed percentage from profile (admins=100)
  const maxDiscPct = Math.max(0, Math.min(100, Number(pdvPerms?.max_discount_pct ?? 100)));
  const rawDiscPct = discountType === 'percent' ? _parseNumBR(discountInput) : (subtotal > 0 ? _parseNumBR(discountInput) / subtotal * 100 : 0);
  const effectiveDiscPct = pdvPerms?.give_discount === false ? 0 : Math.min(rawDiscPct, maxDiscPct);
  // iter156bj: cortesia sobrescreve discountValue = subtotal (100%), ignorando cap do perfil
  const discountValue = hasSemPagamento
    ? subtotal
    : (pdvPerms?.give_discount === false ? 0 : (subtotal * effectiveDiscPct / 100));
  const surchargeValue = pdvPerms?.give_surcharge === false ? 0 : (surchargeType === 'percent' ? subtotal * _parseNumBR(surchargeInput) / 100 : _parseNumBR(surchargeInput));
  // iter77ah: freight (sempre permitido — nao requer permissao especifica)
  // iter156g: _parseNumBR aceita virgula pt-BR (fix: descontos em centavos retornavam 0)
  const freightValue = freightType === 'percent' ? subtotal * _parseNumBR(freightInput) / 100 : _parseNumBR(freightInput);
  const total = Math.max(0, subtotal - discountValue + surchargeValue + freightValue);

  const finalizeSale = async (actionParam) => {
    if (!companyId) { toast.error('Selecione uma empresa'); return; }
    if (items.length === 0) { toast.error('Adicione itens'); return; }
    // iter77cg: guard contra double-submit (previne vendas duplicadas por clique acidental duplo)
    if (processing) { return; }
    if (!isPedido && !editPedidoId && !paymentMethod && (!payments || payments.length === 0)) { toast.error('Selecione a forma de pagamento antes de finalizar'); return; }
    // iter156r: venda so pode ser finalizada quando o operador EXPLICITAMENTE selecionou
    // a forma de pagamento (chip mobile ou "+" desktop). Auto-fill via settings nao conta.
    if (!isPedido && !editPedidoId && !paymentTouched && (!payments || payments.length === 0)) {
      toast.error('Confirme a forma de pagamento clicando na opcao (mobile) ou no botao "+" (desktop) antes de finalizar');
      return;
    }
    // PHARMACY GATING — Farmacia Exclusivo
    if (isPharmacy && !isPedido && saleType === 'venda') {
      const hasControlled = cartHasControlled(items);
      if (hasControlled) {
        const cpf = (selectedClient?.document || '').replace(/\D+/g, '');
        if (!cpf) {
          toast.error('CPF do consumidor e obrigatorio para medicamentos controlados. Clique em "Consumidor" para informar.');
          setShowClientPicker(true);
          return;
        }
        if (!prescriptionId) {
          // AUTO-ABRE o modal de receita ao tentar finalizar (UX requested by user iter75).
          toast('Capture a receita medica antes de finalizar.', { icon: '📋', duration: 2200 });
          setShowPrescriptionModal(true);
          return;
        }
      }
      const cmedIssues = cartCMEDIssues(items);
      if (cmedIssues.length > 0 && !ignoreCmedBlock) {
        const first = cmedIssues[0];
        toast.error(`Preco acima do PMC: ${first.name} R$ ${Number(first.unit_price).toFixed(2)} > R$ ${Number(first.pmc).toFixed(2)}. Ajuste o preco no carrinho.`, { duration: 4500 });
        return;
      }
    }
    // iter156cz: verifica gatilho "Acima de:" da tabela de preco (quando aplicavel).
    // Se o carrinho nao atinge a qtd/valor minimo, alerta o operador. Se ele decidir
    // continuar, os precos dos itens sao REVERTIDOS para o base_price (preco padrao
    // do estoque, sem o ajuste percentual da tabela).
    let effectiveItems = items;  // usado no payload (permite reversao sem mutar const)
    const activeTable = (priceTables || []).find(t => t.id === priceTableId);
    if (activeTable && activeTable.threshold_type && activeTable.threshold_type !== 'none') {
      const minVal = Number(activeTable.threshold_value) || 0;
      if (minVal > 0) {
        const totalQty = items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
        const totalValue = items.reduce((s, it) => s + (Number(it.unit_price) || 0) * (Number(it.quantity) || 0), 0);
        const isQty = activeTable.threshold_type === 'quantity';
        const currentVal = isQty ? totalQty : totalValue;
        if (currentVal < minVal) {
          const missing = minVal - currentVal;
          const fmt = isQty ? (v) => `${Number(v).toFixed(0)} unid.` : (v) => `R$ ${Number(v).toFixed(2)}`;
          const ok = await confirmDialog({
            title: `Tabela "${activeTable.name}" nao atingiu o minimo`,
            body: `A tabela exige um minimo de ${fmt(minVal)} para entrar em vigor.\n\nCarrinho atual: ${fmt(currentVal)}\nFaltam: ${fmt(missing)}\n\nSe continuar, os precos serao REVERTIDOS ao preco padrao do estoque (sem o percentual da tabela).`,
            confirmLabel: 'Continuar com preco padrao',
            cancelLabel: 'Voltar e ajustar',
            danger: false,
          });
          if (!ok) return;
          // Reverte para base_price em variavel local (payload usa `effectiveItems`).
          effectiveItems = items.map(it => {
            const base = Number(it.base_price) || Number(it.original_unit_price) || Number(it.unit_price) || 0;
            return { ...it, unit_price: base, total: base * (Number(it.quantity) || 0) };
          });
          // Atualiza state para o cart reflectir a reversao apos finalizar
          setItems(effectiveItems);
          if (setPriceTableId) setPriceTableId('');
          toast('Precos revertidos para padrao — tabela nao aplicada.', { icon: 'ℹ️', duration: 2500 });
        }
      }
    }

    setProcessing(true);
    try {
      let data;
      if (editPedidoId) {
        // EDIT MODE: update an existing pending pedido/orcamento via PUT
        const payload = {
          company_id: companyId,
          type: editPedidoType || 'pedido',
          items: effectiveItems.map(it => ({ product_id: it.product_id, name: it.name, sku: it.sku || '', variant_id: it.variant_id || null, variant_label: it.variant_label || '', quantity: it.quantity, unit_price: it.unit_price, store_distribution: it.store_distribution || [] })),
          discount: discountValue,
          surcharge: surchargeValue,
          freight: freightValue,
          payment_method: paymentMethod,
          payment_method_id: paymentMethodId || undefined,
          payments: payments && payments.length > 0 ? payments : undefined,
          installments: (!payments || payments.length === 0) && paymentMethod === 'credito' ? Math.max(1, Number(pickInstallments) || 1) : undefined,  // iter156ep: mobile (forma unica)
          customer_name: selectedClient?.name || '',
          customer_doc: selectedClient?.document || '',
          client_id: selectedClient?.id || null,
          notes,
        };
        ({ data } = await axios.put(`${ERP}/sales/${editPedidoId}`, payload, { withCredentials: true }));
        toast.success(`Pedido ${data.number} atualizado — R$ ${data.total?.toFixed(2)}`);
      } else if (initialPedidoId) {
        // Confirming an existing pedido → convert to venda
        ({ data } = await axios.post(`${ERP}/sales/${initialPedidoId}/confirm-pedido`, {
          // iter156eh: so envia sale_date se o operador ALTEROU a data (senao backend mantem a do pedido)
          sale_date: (allowDateEdit && saleDate && saleDate !== (initialSaleDate || '')) ? saleDate : undefined,
          payment_method: paymentMethod,
          payment_method_id: paymentMethodId || undefined,
          discount: discountValue,
          surcharge: surchargeValue,
          freight: freightValue,
          items: effectiveItems
        }, { withCredentials: true }));
        toast.success(`Venda ${data.number} confirmada — R$ ${data.total?.toFixed(2)}`);
      } else {
        // Cashback (iter76): when method=cashback and user did not build a manual split,
        // auto-construct payments[] = [cashback_used + dinheiro_remainder] so backend can debit FIFO.
        let resolvedPayments = payments && payments.length > 0 ? payments : undefined;
        let resolvedMethod = paymentMethod;
        if (paymentMethod === 'cashback' && !resolvedPayments) {
          const total = Math.max(0, subtotal - discountValue + surchargeValue);
          const cbUse = Math.min(cashbackBalance, total, Number(cashbackAmountInput || cashbackBalance) || cashbackBalance);
          const remainder = +(total - cbUse).toFixed(2);
          resolvedPayments = [{ method: 'cashback', amount: cbUse }];
          if (remainder > 0) {
            resolvedPayments.push({ method: 'dinheiro', amount: remainder });
            resolvedMethod = 'dinheiro'; // bank/cash entry uses non-cashback method
          }
        }
        const payload = {
          company_id: companyId,
          // iter77cg: idempotency_key único por finalização (mesmo em double-click, backend retorna venda existente)
          client_uuid: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `pdv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
          type: saleType,
          items: effectiveItems,
          discount: discountValue,
          surcharge: surchargeValue,
          freight: freightValue,
          payments: resolvedPayments,
          change_amount: resolvedPayments ? Math.max(0, resolvedPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0) - (subtotal - discountValue + surchargeValue + freightValue)) : 0,
          payment_method: resolvedMethod,
          // iter77ct.53: envia pmId (PM.id do DB) para o backend rotear o banco correto
          payment_method_id: paymentMethodId || undefined,
          customer_name: selectedClient?.name || '',
          customer_doc: selectedClient?.document || '',
          client_id: selectedClient?.id || null,
          // iter156fr: venda para funcionario (checkbox "Funcionario") — backend nao aplica cliente padrao
          employee_id: selectedClient?.employee_id || undefined,
          vendedor_id: vendedorId || null,
          notes,
          // iter77ct.54: so envia sale_date se o usuario mudou pra uma data DIFERENTE de hoje.
          // Se enviar hoje em YYYY-MM-DD, backend defaulta pra 12:00 UTC (09:00 BRT) e
          // sobrescreve a hora real. Nao enviando, backend usa datetime.now() -> hora correta.
          sale_date: (allowDateEdit && saleDate && saleDate !== localDateISO()) ? saleDate : undefined,
          prescription_id: prescriptionId || undefined,
          ignore_cmed_block: ignoreCmedBlock || undefined,
        };
        try {
          ({ data } = await axios.post(`${ERP}/pdv/sale`, payload, { withCredentials: true, timeout: 8000 }));
          toast.success(`${isPedido ? 'Pedido' : 'Venda'} ${data.number} — R$ ${data.total?.toFixed(2)}`);
          // Apply customer credits (FIFO) if user selected 'credito_devolucao'
          // iter77ce: também aplica quando `payments[]` (multi-pagamento desktop) contém credito_devolucao.
          let creditToApply = 0;
          if (paymentMethod === 'credito_devolucao' && creditApplied > 0) {
            creditToApply = creditApplied;
          } else if (Array.isArray(resolvedPayments)) {
            const cdSum = resolvedPayments
              .filter(p => (p.method || '').toLowerCase() === 'credito_devolucao')
              .reduce((s, p) => s + (Number(p.amount) || 0), 0);
            if (cdSum > 0) creditToApply = cdSum;
          }
          if (creditToApply > 0 && data?.id && !isPedido) {
            let remaining = creditToApply;
            for (const c of customerCredits) {
              if (remaining <= 0) break;
              const useAmt = Math.min(remaining, c.amount_available || 0);
              if (useAmt > 0) {
                try { await axios.post(`${ERP}/customer-credits/${c.id}/apply`, { amount: useAmt, sale_id: data.id }, { withCredentials: true }); } catch { /* swallow */ }
                remaining -= useAmt;
              }
            }
          }
        } catch (netErr) {
          const isNetwork = !netErr.response || netErr.code === 'ECONNABORTED' || netErr.message === 'Network Error';
          if (isNetwork && !isPedido) {
            enqueueSale(payload);
            setQueueSize(readQueue().length);
            toast.success('Venda salva offline — sincroniza quando voltar a internet', { duration: 3500 });
            data = { offline: true, total: total, number: 'OFFLINE' };
          } else {
            throw netErr;
          }
        }
      }
      // iter77bm: SEMANTICS REDEFINED — main button = 'stay' (default); dropdown 'exit' = navigate to sales list.
      // Action behavior:
      //   - 'stay' (or undefined) -> save + clear cart + STAY on PDV (no modal)
      //   - 'exit' -> save + clear cart + navigate to Sales list (calls onClose)
      //   - 'comprovante' -> save + open Cupom PDF in new tab + STAY on PDV
      //   - 'nfce' / 'nfe' -> save + emit + transmit SEFAZ + toast "Imprimir DANFE" + STAY on PDV
      // Capture sale context BEFORE clearing PDV state (used for invoice emission)
      const _saleSnapshot = {
        company_id: companyId,
        items: items,
        customer_name: selectedClient?.name || '',
        customer_doc: selectedClient?.document || '',
        client_id: selectedClient?.id || null,
        discount: discountValue,
        surcharge: surchargeValue,
        freight: freightValue,
        payment_method: paymentMethod,
      };
      setItems([]); setSelectedClient(null); setPaymentMethod(''); setPaymentMethodId(null); setPaymentTouched(false); setDiscountInput(''); setSurchargeInput(''); setFreightInput(''); setPayments([]); setNotes(''); /* iter156at: NAO reseta vendedorId — mantido ate o operador trocar ou sair da tela */ setLastAdded(null);
      setPrescriptionId(null); setPrescriptionLabel(''); setIgnoreCmedBlock(false);
      setCashbackBalance(0); setCashbackAmountInput('');
      if (onSaleComplete) onSaleComplete(data, actionParam);
      // iter77bm: 'exit' = sair do PDV apos salvar -> navega para a lista de vendas via onClose (se disponivel).
      if (actionParam === 'exit') {
        if (typeof onClose === 'function') onClose();
        return;
      }
      // Auto-actions only for new vendas (skip when offline, pedido confirm, or 'stay' default)
      if (data && !data.offline && saleType === 'venda' && actionParam && actionParam !== 'stay') {
        const saleObj = { ...data, ..._saleSnapshot };
        if (actionParam === 'comprovante') {
          // Open Cupom Nao-Fiscal PDF inline
          try {
            const { data: blob } = await axios.get(`${ERP}/sales/${saleObj.id || saleObj._id}/receipt-pdf`, { responseType: 'blob', withCredentials: true });
            const objectUrl = window.URL.createObjectURL(blob);
            const win = window.open(objectUrl, '_blank');
            if (!win) {
              const a = document.createElement('a'); a.href = objectUrl; a.download = `cupom-${saleObj.number}.pdf`;
              document.body.appendChild(a); a.click(); a.remove();
            }
            setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
            toast.success('Cupom Nao-Fiscal gerado');
          } catch { toast.error('Erro ao gerar cupom PDF'); }
        } else if (actionParam === 'nfce' || actionParam === 'nfe') {
          // Inline NFC-e/NF-e emission + SEFAZ transmit + Print DANFE button
          if (!certConfigured) {
            toast.error('Certificado digital A1 nao configurado. Configure em Fiscal > Certificado.', { duration: 4500 });
          } else if (!defaultNatopId) {
            toast.error('Natureza de Operacao padrao nao configurada. Configure em Configuracoes > PDV > Natureza padrao.', { duration: 4500 });
          } else {
            const tipo = actionParam;
            const tipoLabel = tipo.toUpperCase();
            toast.loading(`Emitindo ${tipoLabel}...`, { id: 'auto-emit' });
            try {
              const { data: inv } = await axios.post(`${ERP}/invoices`, {
                tipo,
                company_id: saleObj.company_id,
                natureza_op_id: defaultNatopId,
                customer_name: saleObj.customer_name || '',
                customer_doc: saleObj.customer_doc || '',
                client_id: saleObj.client_id || null,
                items: saleObj.items || [],
                discount: saleObj.discount || 0,
                surcharge: saleObj.surcharge || 0,
                payment_method: saleObj.payment_method || '',
                sale_id: saleObj.id || saleObj._id,
                sale_number: saleObj.number,
              }, { withCredentials: true });
              toast.loading('Transmitindo ao SEFAZ...', { id: 'auto-emit' });
              const { data: issued } = await axios.post(`${ERP}/invoices/${inv.id}/issue`, {}, { withCredentials: true, timeout: 30000 });
              toast.dismiss('auto-emit');
              // iter156ax: sempre mostra o PostSaleModal com a NFC-e/NF-e emitida
              // para que o usuario tenha um botao PERSISTENTE de imprimir DANFE
              // (antes so aparecia um toast de 8s que era facil de perder).
              setPostSaleData({
                ...saleObj,
                __emitted_invoice: { ...inv, ...issued },
                __emitted_type: tipo,
              });
              if (issued.status === 'issued') {
                toast.success(`${tipoLabel} autorizada — protocolo ${issued.protocolo || '—'}`);
              } else if (issued.status === 'pending') {
                toast(`SEFAZ processando ${tipoLabel} — verifique status em alguns minutos`, { icon: '⏳', duration: 4500 });
              } else {
                toast.error(`SEFAZ rejeitou ${tipoLabel}: ${issued.sefaz_xmotivo || 'sem motivo'}. Veja em Fiscal > NF de Venda.`, { duration: 5500 });
              }
            } catch (emitErr) {
              toast.dismiss('auto-emit');
              toast.error(emitErr.response?.data?.detail || `Erro ao emitir ${tipoLabel}`);
            }
          }
        }
      } else if (data && !data.offline && saleType === 'venda' && !actionParam) {
        // Manual flow (no split-button action specified, e.g. confirm pedido) — keep PostSaleModal as fallback
        setPostSaleData({ ...data, ..._saleSnapshot });
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setProcessing(false); }
  };

  // Online/offline + auto-flush queue
  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      flushQueue().then(({ sent, remaining }) => {
        if (sent > 0) toast.success(`${sent} venda(s) sincronizada(s)`);
        setQueueSize(remaining);
      });
    };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    // Initial flush on mount if online
    if (navigator.onLine && readQueue().length > 0) {
      flushQueue().then(({ remaining }) => setQueueSize(remaining));
    }
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, []);
  // Fetch customer credits when a client is selected (for "Credito Devolucao" form pgto)
  useEffect(() => {
    if (!selectedClient?.id) { setCustomerCredits([]); setCreditApplied(0); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/customer-credits?client_id=${selectedClient.id}&status=available`, { withCredentials: true });
        setCustomerCredits(data.data || []);
      } catch { setCustomerCredits([]); }
    })();
    setCreditApplied(0);
  }, [selectedClient?.id]);
  // When user selects "credito_devolucao" method, auto-apply min(total, sum of credits)
  useEffect(() => {
    if (paymentMethod !== 'credito_devolucao') { setCreditApplied(0); return; }
    const available = customerCredits.reduce((s, c) => s + (c.amount_available || 0), 0);
    setCreditApplied(Math.min(total || 0, available));
  }, [paymentMethod, customerCredits, total]);
  const totalAvailableCredit = customerCredits.reduce((s, c) => s + (c.amount_available || 0), 0);

  // iter77af + iter77ct.53: agora cada FORMA DE PAGAMENTO INDIVIDUAL (do DB) vira uma opcao no PDV,
  // mostrando pm.name como label — nao mais deduplicado por type. Assim "PIX" e "PIX Online" aparecem
  // como botoes separados. `key` mantido como pm.type para preservar checks legados (em_aberto,
  // credito_devolucao, cashback), mas cada entry carrega `pmId` (id do PM especifico) para o backend
  // resolver o banco correto ao salvar a venda.
  // IMPORTANTE: useMemo deve vir ANTES de qualquer early-return (rules-of-hooks).
  const payMethods = useMemo(() => {
    // Icones e labels default por type
    const iconByType = {
      dinheiro: '💵', pix: '📱', debito: '💳', credito: '💳',
      em_aberto: '📅', credito_devolucao: '🔄', cashback: '💰',
    };
    // 1) Constroi entrada por PM individual do DB
    const fromDb = (activePmList || []).map(pm => ({
      key: String(pm.type || '').toLowerCase(),
      pmId: pm.id,
      label: pm.name || String(pm.type || ''),
      icon: iconByType[String(pm.type || '').toLowerCase()] || '💳',
      type: String(pm.type || '').toLowerCase(),
      requiresCashback: String(pm.type || '').toLowerCase() === 'cashback',
      is_cashback: !!pm.is_cashback,
      fee_tiers: Array.isArray(pm.fee_tiers) ? pm.fee_tiers : [],  // iter156ep
      show_fee_in_sales: !!pm.show_fee_in_sales,  // iter156et
    }));
    // 2) iter156gb: SO formas liberadas em Formas de Pagamento (escopo Vendas). "Em Aberto" nao e mais
    // forcado; "Credito Devolucao" virtual so quando o cliente selecionado TEM saldo.
    if (!fromDb.some(m => m.key === 'credito_devolucao') && selectedClient && totalAvailableCredit > 0) {
      const defaultMeta = PAY_METHODS.find(p => p.key === 'credito_devolucao');
      if (defaultMeta) fromDb.push({ ...defaultMeta, pmId: null, type: 'credito_devolucao' });
    }
    // 3) Filtro cashback — mesma logica anterior
    return fromDb.filter(m => !m.requiresCashback || (isCashback && cashbackBalance > 0));
  }, [activePmList, isCashback, cashbackBalance, selectedClient, totalAvailableCredit]);

  if (!companyId) return <NoCompany />;

  return (
    <div data-testid="pdv-page" className={fullscreen ? 'max-w-3xl mx-auto' : 'max-w-lg mx-auto'}>
      {isCashback && selectedClient && cashbackBalance > 0 && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border-2 border-amber-300 flex items-center justify-between gap-2 text-xs" data-testid="cashback-balance-banner">
          <span className="text-amber-900"><strong>🪙 {selectedClient.name}</strong> tem <strong>R$ {cashbackBalance.toFixed(2)}</strong> de Cashback disponivel.</span>
          {paymentMethod === 'cashback' && Number(cashbackAmountInput || 0) > 0 && (
            <span className="text-amber-900 font-bold">Usando R$ {Number(cashbackAmountInput).toFixed(2)}</span>
          )}
        </div>
      )}
      {selectedClient && totalAvailableCredit > 0 && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-gradient-to-r from-blue-50 to-violet-50 border border-violet-200 flex items-center justify-between text-xs" data-testid="credit-available-banner">
          <span className="text-violet-800"><strong>💳 {selectedClient.name}</strong> tem R$ {totalAvailableCredit.toFixed(2)} de credito loja disponivel.</span>
          {paymentMethod === 'credito_devolucao' && creditApplied > 0 && (
            <span className="text-violet-900 font-bold">Aplicando R$ {creditApplied.toFixed(2)}</span>
          )}
        </div>
      )}
      {isPharmacy && items.length > 0 && (() => {
        const hasControlled = cartHasControlled(items);
        const cmedIssues = cartCMEDIssues(items);
        const allergies = (selectedClient && selectedClient.pharmacy && selectedClient.pharmacy.allergies) || '';
        const allergyHits = cartAllergyHits(items, allergies);
        if (!hasControlled && cmedIssues.length === 0 && allergyHits.length === 0) return null;
        return (
          <div className="mb-3 space-y-2" data-testid="pharmacy-pdv-banner">
            {hasControlled && (
              <div className="px-3 py-2 rounded-lg bg-violet-50 border-2 border-violet-300 flex items-start gap-2" data-testid="pharmacy-controlled-banner">
                <span className="text-violet-900 text-base">[RX]</span>
                <div className="flex-1 text-xs">
                  <p className="font-black text-violet-900">Medicamento controlado no carrinho</p>
                  <p className="text-violet-700">CPF + receita medica obrigatorios.{prescriptionId ? ` Receita: ${prescriptionLabel}` : ''}</p>
                </div>
                <button onClick={() => setShowPrescriptionModal(true)} className="text-[10px] font-black bg-violet-600 hover:bg-violet-700 text-white px-2.5 py-1.5 rounded-lg" data-testid="open-prescription-modal">
                  {prescriptionId ? 'Trocar' : 'Capturar Receita'}
                </button>
              </div>
            )}
            {cmedIssues.length > 0 && (
              <div className="px-3 py-2 rounded-lg bg-red-50 border-2 border-red-300 flex items-start gap-2" data-testid="pharmacy-cmed-banner">
                <span className="text-red-700 text-base">!</span>
                <div className="flex-1 text-xs">
                  <p className="font-black text-red-900">Preco acima do PMC CMED</p>
                  {cmedIssues.slice(0, 3).map((c, i) => (
                    <p key={i} className="text-red-700">{c.name}: R$ {Number(c.unit_price).toFixed(2)} (max R$ {Number(c.pmc).toFixed(2)})</p>
                  ))}
                  <label className="mt-1 flex items-center gap-1.5 text-[11px] font-bold text-red-800">
                    <input type="checkbox" checked={ignoreCmedBlock} onChange={e => setIgnoreCmedBlock(e.target.checked)} data-testid="pharmacy-cmed-override" />
                    Confirmar venda mesmo acima do PMC (autorizacao do supervisor)
                  </label>
                </div>
              </div>
            )}
            {allergyHits.length > 0 && (
              <div className="px-3 py-2 rounded-lg bg-amber-50 border-2 border-amber-300 flex items-start gap-2" data-testid="pharmacy-allergy-banner">
                <span className="text-amber-700 text-base">!</span>
                <div className="flex-1 text-xs">
                  <p className="font-black text-amber-900">ALERGIA DECLARADA: {selectedClient?.name}</p>
                  {allergyHits.slice(0, 3).map((h, i) => (
                    <p key={i} className="text-amber-800">{h.name} - principio ativo: {h.active_ingredient || h.term}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}
      {showScanner && <QRScannerModal onScan={handleQRScan} onClose={() => setShowScanner(false)} />}
      {showSummary && <CashSummaryModal companyId={companyId} onClose={() => setShowSummary(false)} />}
      {showClosure && <CashClosureModal companyId={companyId} onClose={() => setShowClosure(false)} onOpenExpense={() => setShowExpense(true)} pdvPerms={pdvPerms} />}
      {/* iter156u: Consultar Itens (Cod.Interno/Barras/Nome/Valor/Estoque disponivel) */}
      {showProductBrowser && (
        <ProductBrowserModal
          companyId={companyId}
          onClose={() => setShowProductBrowser(false)}
        />
      )}
      {showExpense && <CashExpenseModal companyId={companyId} onClose={() => setShowExpense(false)} onSaved={() => { /* refresh closure if open via remount */ }} />}
      {isPharmacy && pendingLotProduct && (
        <PharmacyLotPickerModal
          product={pendingLotProduct}
          onPick={(lotInfo) => {
            _addItemActual(pendingLotProduct, lotInfo);
            setPendingLotProduct(null);
          }}
          onSkip={() => {
            _addItemActual(pendingLotProduct, null);
            setPendingLotProduct(null);
          }}
          onClose={() => setPendingLotProduct(null)}
        />
      )}
      {isPharmacy && showPrescriptionModal && (
        <PharmacyPrescriptionModal
          initialCustomerName={selectedClient?.name || ''}
          initialCustomerCpf={selectedClient?.document || ''}
          onClose={() => setShowPrescriptionModal(false)}
          onSaved={(pres) => {
            setPrescriptionId(pres.id);
            setPrescriptionLabel(`${pres.prescriber_name} (${pres.prescriber_crm})`);
            setShowPrescriptionModal(false);
          }}
        />
      )}
      {/* iter156ax: PostSaleModal fecha SO o modal — nao bubbla pra fechar a pagina do PDV.
          Antes: onClose do modal chamava o onClose do PDVPage, fazendo o PDV inteiro fechar
          apos a venda finalizada. Agora o usuario decide se sai ou fica. */}
      {postSaleData && <PostSaleModal sale={postSaleData} slug={slug} onClose={() => setPostSaleData(null)} onStay={() => setPostSaleData(null)} onExit={() => { setPostSaleData(null); if (onClose) onClose(); }} allowNFe={allowNFe} allowNFCe={allowNFCe} natops={natops} defaultNatopId={defaultNatopId} printSaleModel={printSaleModel} />}
      {distributeModalIdx !== null && items[distributeModalIdx] && (
        <DistributeStoresModal
          item={items[distributeModalIdx]}
          onClose={() => setDistributeModalIdx(null)}
          onSave={(dist) => {
            setItems(items.map((it, i) => i === distributeModalIdx ? { ...it, store_distribution: dist } : it));
            setDistributeModalIdx(null);
            toast.success('Distribuicao por lojas salva no item.');
          }}
        />
      )}
      {desktopMode && (
        <DesktopPDV
          stockLabel={stockLabel}
          companyId={companyId}
          items={items}
          subtotal={subtotal}
          total={total}
          discountValue={discountValue}
          surchargeValue={surchargeValue}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          discountType={discountType}
          setDiscountType={setDiscountType}
          discountInput={discountInput}
          setDiscountInput={setDiscountInput}
          surchargeType={surchargeType}
          setSurchargeType={setSurchargeType}
          surchargeInput={surchargeInput}
          setSurchargeInput={setSurchargeInput}
          freightType={freightType}
          setFreightType={setFreightType}
          freightInput={freightInput}
          setFreightInput={setFreightInput}
          freightValue={freightValue}
          pdvPerms={pdvPerms}
          vendedorId={vendedorId}
          setVendedorId={setVendedorId}
          employees={employees}
          allEmployees={allEmployees}
          notes={notes}
          setNotes={setNotes}
          priceTableId={priceTableId}
          setPriceTableId={setPriceTableId}
          priceTables={priceTables}
          payments={payments}
          setPayments={setPayments}
          pickInstallments={pickInstallments}
          setPickInstallments={setPickInstallments}
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          clients={clients}
          setClients={setClients}
          lastAdded={lastAdded}
          isOnline={isOnline}
          queueSize={queueSize}
          processing={processing}
          showScanner={showScanner}
          setShowScanner={setShowScanner}
          onAddItem={addItem}
          onRemoveItem={removeItem}
          onUpdateQty={updateQty}
          onSetQty={setQty}
          onUpdateUnitPrice={(idx, newPrice) => {
            setItems(items.map((it, i) => i === idx ? { ...it, unit_price: newPrice, total: newPrice * it.quantity } : it));
          }}
          onFinalize={finalizeSale}
          onExit={() => {
            // If a parent navigation handler exists, navigate AWAY from the PDV page entirely
            // (typically to Sales history). Skip resetting desktopMode locally to avoid the
            // mobile PDV flashing briefly before navigation lands.
            if (onClose) {
              onClose();
            } else {
              setDesktopMode(false);
            }
          }}
          onOpenSummary={() => setShowSummary(true)}
          manualConfirmDefault={manualConfirmDefault}
          saleType={saleType}
          allowDateEdit={allowDateEdit}
          saleDate={saleDate}
          setSaleDate={setSaleDate}
          isPharmacy={isPharmacy}
          prescriptionId={prescriptionId}
          prescriptionLabel={prescriptionLabel}
          onOpenPrescriptionModal={() => setShowPrescriptionModal(true)}
          ignoreCmedBlock={ignoreCmedBlock}
          setIgnoreCmedBlock={setIgnoreCmedBlock}
          activePmTypes={activePmTypes}
          activePmList={activePmList}
          customerCredits={customerCredits}
          totalAvailableCredit={totalAvailableCredit}
          cashbackBalance={cashbackBalance}
          allowNFe={allowNFe}
          allowNFCe={allowNFCe}
          certConfigured={certConfigured}
          slug={slug}
          onOpenClosure={() => setShowClosure(true)}
          onOpenExpense={() => setShowExpense(true)}
          onOpenProductBrowser={() => setShowProductBrowser(true)}
          distributeStoresEnabled={distributeStoresEnabled}
          onOpenDistribute={(idx) => setDistributeModalIdx(idx)}
        />
      )}
      {/* Date + Client + Fullscreen toggle */}
      <div className={`${desktopMode ? 'hidden' : 'flex'} items-center justify-between mb-3 gap-2`}>
        {allowDateEdit ? (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-500 font-bold uppercase">Data:</span>
            <input type="date" value={saleDate} onChange={e => setSaleDate(e.target.value)} max={localDateISO()} className="px-2 py-1 border border-cyan-300 rounded-md text-xs text-slate-700 bg-cyan-50/30 focus:outline-none focus:ring-2 focus:ring-cyan-500/30" data-testid="pdv-sale-date" />
          </div>
        ) : (
          <p className="text-xs text-gray-400 font-medium" data-testid="pdv-date">{today}</p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {priceTables.length > 0 && (
            <select value={priceTableId} onChange={e => setPriceTableId(e.target.value)} className="text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg border-2 border-indigo-200 focus:outline-none focus:border-indigo-500" data-testid="pdv-price-table-select">
              <option value="">Preco padrao</option>
              {priceTables.map(t => <option key={t.id} value={t.id}>{t.name} ({t.percent_global >= 0 ? '+' : ''}{t.percent_global}%)</option>)}
            </select>
          )}
          {!isOnline && <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-1 rounded-lg flex items-center gap-1" data-testid="offline-badge"><WifiOff size={11} />OFFLINE</span>}
          {queueSize > 0 && <span className="text-[10px] font-bold text-orange-700 bg-orange-50 px-2 py-1 rounded-lg" data-testid="queue-badge">{queueSize} na fila</span>}
          <PDVActionsMenu
            onOpenSummary={() => setShowSummary(true)}
            onOpenClosure={() => setShowClosure(true)}
            onOpenExpense={() => setShowExpense(true)}
            variant="light"
          />
          <button onClick={() => setDesktopMode(true)} title="Modo Desktop (caixa amplo)" className="hidden md:flex text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg items-center gap-1" data-testid="open-desktop-pdv">
            <Monitor size={12} /><span>Modo Desktop</span>
          </button>
          <button onClick={() => setShowClientPicker(!showClientPicker)} className="text-xs font-medium text-teal-600 bg-teal-50 px-3 py-1.5 rounded-lg flex items-center space-x-1" data-testid="pdv-client-toggle">
            <Users size={12} /><span>{selectedClient?.name || 'Consumidor'}</span><ChevronDown size={12} />
          </button>
          {onToggleFullscreen && (
            <button onClick={onToggleFullscreen} title={fullscreen ? 'Sair do modo Caixa' : 'Modo Caixa (tela cheia)'} className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors ${fullscreen ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-white border border-gray-200 text-gray-600 hover:border-teal-400 hover:text-teal-700'}`} data-testid="pdv-fullscreen-toggle">
              {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
              <span className="hidden sm:inline">{fullscreen ? 'Sair' : 'Modo Caixa'}</span>
            </button>
          )}
        </div>
      </div>
      {showClientPicker && (
        <ClientPDVModal
          clients={clients}
          companyId={companyId}
          onClose={() => setShowClientPicker(false)}
          onSelectExisting={(c) => { setSelectedClient(c); setShowClientPicker(false); }}
          onAttachDoc={({ doc_type, document }) => {
            setSelectedClient({ id: null, name: `Consumidor (${doc_type.toUpperCase()})`, document });
            setShowClientPicker(false);
          }}
          onCreated={(c) => { setClients(prev => [c, ...prev]); setSelectedClient(c); setShowClientPicker(false); }}
        />
      )}
      {/* Vendedor select */}
      {employees.length > 0 && (
        <div className="mb-3" data-testid="vendedor-row">
          <label className="text-[10px] font-bold text-gray-400 uppercase">Vendedor</label>
          <select value={vendedorId} onChange={e => setVendedorId(e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg bg-white" style={noZoomStyle} data-testid="vendedor-select">
            <option value="">Sem vendedor</option>
            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
          </select>
        </div>
      )}
      {/* Mode toggle: PDV | Catálogo */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-xl p-1" data-testid="pdv-mode-toggle">
        <button onClick={() => setMode('pdv')} data-testid="mode-pdv" className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all flex items-center justify-center space-x-1.5 ${mode === 'pdv' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500'}`}>
          <Search size={13} /><span>PDV</span>
        </button>
        <button onClick={() => setMode('catalog')} data-testid="mode-catalog" className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all flex items-center justify-center space-x-1.5 ${mode === 'catalog' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500'}`}>
          <Package size={13} /><span>Catalogo</span>
        </button>
      </div>
      {/* PDV MODE: Search + QR */}
      {mode === 'pdv' && (
        <div className="relative mb-4">
          <div className="flex items-center bg-white border-2 border-slate-300 rounded-2xl overflow-hidden shadow-sm focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-500/20 transition-all">
            <div className="flex items-center flex-1 px-4"><Search size={18} className="text-slate-500" /><input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Nome, SKU ou codigo..." className="flex-1 px-3 py-3.5 border-none outline-none bg-transparent text-slate-900 placeholder-slate-400 font-medium" style={noZoomStyle} inputMode="search" autoComplete="off" data-testid="pdv-search" /></div>
            <button onClick={() => setShowScanner(true)} className="px-4 py-3.5 bg-teal-600 hover:bg-teal-700 text-white transition-colors" data-testid="pdv-qr-btn"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="3" height="3" /><line x1="21" y1="14" x2="21" y2="21" /><line x1="14" y1="21" x2="21" y2="21" /></svg></button>
          </div>
          {results.length > 0 && (<div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-2xl shadow-2xl z-20 max-h-56 overflow-y-auto" data-testid="pdv-results">{results.map(p => { const t = isPharmacy ? tarjaInfo(p.pharmacy?.tarja) : null; return (<button key={p.id} onClick={() => addItem(p)} className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-teal-50 border-b border-gray-50 last:border-0 text-left" data-testid="pdv-result-item">{p.images?.[0] ? <img src={p.images[0]} className="w-11 h-11 rounded-xl object-cover flex-shrink-0" alt="" onError={e => { e.target.style.display = 'none'; }} /> : <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0"><Package size={16} className="text-gray-400" /></div>}<div className="min-w-0 flex-1"><div className="flex items-center gap-1.5 flex-wrap"><p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>{isPharmacy && p.pharmacy?.controlled && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-violet-600 text-white" data-testid="pharmacy-controlled-pill">RX {p.pharmacy.controlled_list || ''}</span>}{isPharmacy && t && (p.pharmacy?.tarja || '').toLowerCase() !== 'sem_tarja' && <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border ${t.cls}`} data-testid="pharmacy-tarja-pill">{t.label}</span>}</div>{isPharmacy && p.pharmacy?.active_ingredient && <p className="text-[10px] text-violet-600 italic truncate">{p.pharmacy.active_ingredient}</p>}<p className="text-[11px] text-gray-400">{p.sku}{stockShortLabel(p.stock) ? ` • ${stockShortLabel(p.stock)}` : ''}</p></div><span className="text-sm font-bold text-teal-600">R$ {p.price.toFixed(2)}</span></button>); })}</div>)}
        </div>
      )}
      {/* CATALOG MODE: category chips + product grid */}
      {mode === 'catalog' && (
        <div className="mb-4" data-testid="catalog-mode">
          <div className="mb-3">
            <input value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)} placeholder="Buscar no catalogo..." className="w-full px-4 py-3 bg-white border border-gray-200 rounded-2xl outline-none focus:border-teal-500" style={noZoomStyle} inputMode="search" data-testid="catalog-search" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1" data-testid="catalog-category-chips">
            <button onClick={() => setSelectedCategory(null)} className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border ${!selectedCategory ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200'}`}>Todos</button>
            {categories.map(c => (
              <button key={c.id} onClick={() => setSelectedCategory(c.id)} data-testid={`cat-${c.slug || c.id}`} className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap ${selectedCategory === c.id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200'}`}>{c.name}</button>
            ))}
          </div>
          {catalogLoading ? <div className="py-12 text-center text-gray-400 text-sm">Carregando...</div> : (
            catalogProducts.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-300 rounded-2xl py-10 text-center"><Package size={28} className="mx-auto text-gray-300 mb-2" /><p className="text-sm text-gray-400">Nenhum produto</p></div>
            ) : (
              <div className="grid grid-cols-2 gap-3" data-testid="catalog-grid">
                {catalogProducts.map(p => {
                  const qty = itemQty(p.id);
                  return (
                    <button key={p.id} onClick={() => addItem(p)} data-testid="catalog-card" className="bg-white border border-gray-200 rounded-2xl overflow-hidden text-left hover:border-teal-400 transition-all active:scale-95 relative">
                      <div className="aspect-square bg-gray-100 relative">
                        {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} /> : <div className="w-full h-full flex items-center justify-center"><Package size={28} className="text-gray-300" /></div>}
                        {qty > 0 && (
                          <span className="absolute top-2 right-2 bg-teal-600 text-white text-[11px] font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md">{qty}</span>
                        )}
                      </div>
                      <div className="p-2.5">
                        <p className="text-xs font-medium text-gray-900 line-clamp-2 min-h-[2.2em]">{p.name}</p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-sm font-bold text-teal-600">R$ {(p.price || 0).toFixed(2)}</span>
                          {stockShortLabel(p.stock) && <span className="text-[10px] text-gray-400">{stockShortLabel(p.stock)}</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          )}
        </div>
      )}
      {/* Items (shared cart) */}
      <div className="mb-4">{items.length === 0 ? (<div className="bg-white border border-dashed border-gray-300 rounded-2xl py-12 text-center"><ShoppingCart size={32} className="mx-auto text-gray-300 mb-2" /><p className="text-sm text-gray-400">{mode === 'pdv' ? 'Busque ou escaneie produtos' : 'Toque em um produto do catalogo'}</p></div>) : (<div className="space-y-2">{items.map((item, idx) => { const t = isPharmacy ? tarjaInfo(item.pharmacy?.tarja) : null; const overPmc = isPharmacy && itemExceedsCMED(item); return (<div key={idx} className={`bg-white border ${overPmc ? 'border-red-400' : 'border-gray-200'} rounded-2xl p-3 flex flex-wrap items-center gap-x-3 gap-y-2`} data-testid="pdv-item-row">{item.image ? <img src={item.image} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" alt="" onError={e => { e.target.style.display = 'none'; }} /> : <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0"><Package size={16} className="text-gray-400" /></div>}<div className="min-w-0 flex-1 basis-[150px]"><div className="flex items-center gap-1.5 flex-wrap"><p className="text-sm font-medium text-gray-900 line-clamp-2 leading-tight">{item.name}</p>{isPharmacy && item.pharmacy?.controlled && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-violet-600 text-white" data-testid="item-controlled-pill">RX {item.pharmacy.controlled_list || ''}</span>}{isPharmacy && t && (item.pharmacy?.tarja || '').toLowerCase() !== 'sem_tarja' && <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border ${t.cls}`} data-testid="item-tarja-pill">{t.label}</span>}{isPharmacy && item.lot && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 border border-cyan-300" data-testid="item-lot-pill">Lote {item.lot}</span>}</div><button onClick={() => { const v = window.prompt('Novo valor unitario:', String(item.unit_price)); if (v == null) return; const f = parseFloat(String(v).replace(',', '.')); if (!isFinite(f) || f < 0) { toast.error('Valor invalido'); return; } const originalPrice = Number(item.original_unit_price ?? item.unit_price); if (!pdvPerms?.edit_unit_price && f < originalPrice) { toast.error(`Sem permissao para reduzir o preco. Valor minimo: R$ ${originalPrice.toFixed(2)}`); return; } setItems(items.map((it, i) => i === idx ? { ...it, unit_price: f, total: f * it.quantity } : it)); }} className={`text-[11px] font-semibold underline-offset-2 hover:underline ${overPmc ? 'text-red-600' : 'text-teal-600'}`} title={pdvPerms?.edit_unit_price ? 'Editar valor unitario' : 'Editar valor unitario (apenas para MAIOR)'} data-testid="pdv-edit-price">R$ {item.unit_price.toFixed(2)} un. {overPmc && <span className="text-[10px]">(&gt; PMC R$ {Number(item.pharmacy.cmed_pmc).toFixed(2)})</span>}</button></div><div className="flex items-center gap-1.5 ml-auto shrink-0"><div className="flex items-center space-x-1.5"><button onClick={() => updateQty(idx, -1)} data-testid="pdv-qty-minus" className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 active:scale-95 flex items-center justify-center transition-transform"><Minus size={16} /></button><input type="text" inputMode="decimal" value={String(item.quantity).replace('.', ',')} onChange={(e) => setQty(idx, e.target.value)} onFocus={(e) => e.target.select()} className="text-sm font-bold w-12 text-center border border-slate-200 rounded-lg px-1 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400" title="Aceita quantidade fracionada (ex: 3,5)" data-testid={`pdv-qty-input-${idx}`} /><button onClick={() => updateQty(idx, 1)} data-testid="pdv-qty-plus" className="w-10 h-10 rounded-xl bg-teal-50 hover:bg-teal-100 active:scale-95 flex items-center justify-center transition-transform"><Plus size={16} className="text-teal-600" /></button></div><div className="text-right w-16"><p className="text-sm font-bold">R$ {item.total.toFixed(2)}</p></div>{distributeStoresEnabled && (() => { const dist = Array.isArray(item.store_distribution) ? item.store_distribution : []; const cnt = dist.length; return (<button onClick={() => setDistributeModalIdx(idx)} title={cnt > 0 ? dist.map(d => `${d.store}: ${d.quantity}`).join(' · ') : 'Distribuir por lojas do cliente'} className={`px-1.5 py-1 rounded-lg text-[9px] font-black uppercase flex items-center gap-1 transition-colors ${cnt > 0 ? 'bg-purple-600 text-white' : 'bg-purple-50 border-2 border-purple-300 text-purple-700'}`} data-testid={`mobile-item-distribute-${idx}`}><Store size={10} />{cnt > 0 ? cnt : 'Lojas'}</button>); })()}<button onClick={() => removeItem(idx)} className="text-gray-300 hover:text-red-500 p-1"><X size={16} /></button></div></div>); })}</div>)}</div>
      {/* Payment + Discount + Finalize */}
      {items.length > 0 && (<div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="p-4 border-b border-gray-100"><div className="flex items-center justify-between mb-2.5"><p className="text-[10px] font-bold text-gray-400 uppercase">Forma de Pagamento</p>{payMethods.length > 3 && <p className="text-[9px] text-gray-400 sm:hidden flex items-center gap-0.5">deslize <ChevronRight size={10} /></p>}</div>
        {/* iter156gc: MOBILE = carrossel horizontal (snap); DESKTOP (sm+) = grade como antes. Ordem = Formas de Pagamento. */}
        <div className="flex sm:grid sm:grid-cols-4 gap-2.5 overflow-x-auto sm:overflow-visible snap-x snap-mandatory -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 sm:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="pdv-mobile-pay-carousel">{payMethods.map(m => {
          const isCredDev = m.key === 'credito_devolucao';
          const disabled = isCredDev && !(selectedClient && totalAvailableCredit > 0);
          // iter77ct.53: com multiplos PMs do mesmo type, o selecionado precisa casar por pmId tambem
          const isSelected = paymentMethod === m.key && (m.pmId ? paymentMethodId === m.pmId : !paymentMethodId);
          return (
            <button
              key={m.pmId || m.key}
              onClick={() => {
                if (disabled) { toast.error(selectedClient ? 'Sem crédito de devolução para este cliente.' : 'Selecione um cliente para usar Crédito Devolução.'); return; }
                // iter156bj: "Sem Pagamento" (cortesia) — zera venda automaticamente no chip mobile
                if (m.key === 'sem_pagamento') {
                  setPayments([{ method: 'sem_pagamento', method_id: m.pmId, label: m.label || 'Sem Pagamento', amount: 0 }]);
                  setPaymentMethod('sem_pagamento');
                  setPaymentMethodId(m.pmId || null);
                  setPaymentTouched(true);
                  toast.success('Cortesia aplicada — valor zerado. Clique em Finalizar.');
                  return;
                }
                setPaymentMethod(m.key); setPaymentMethodId(m.pmId || null); setPaymentTouched(true);
              }}
              disabled={disabled}
              data-testid={`pay-${m.key}${m.pmId ? '-' + m.pmId : ''}`}
              title={isCredDev && selectedClient && totalAvailableCredit > 0 ? `Saldo disponível: R$ ${totalAvailableCredit.toFixed(2)}` : (disabled ? 'Sem saldo disponível' : m.label)}
              className={`relative py-3 px-2 rounded-2xl text-center transition-all border-2 min-h-[68px] active:scale-95 shrink-0 w-[31%] min-w-[104px] sm:w-auto sm:min-w-0 snap-start ${disabled ? 'border-slate-200 text-slate-300 bg-slate-50 cursor-not-allowed' : isSelected ? (isCredDev ? 'border-violet-500 bg-gradient-to-br from-violet-50 to-blue-50 text-violet-700 shadow-md shadow-violet-500/25' : 'border-teal-500 bg-teal-50 text-teal-700 shadow-md shadow-teal-500/20') : (isCredDev && totalAvailableCredit > 0 ? 'border-violet-300 bg-violet-50/50 text-violet-700 hover:border-violet-400' : 'border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-700')}`}
            >
              <span className="text-2xl block leading-none mb-1">{m.icon}</span>
              <span className="text-[11px] font-bold leading-tight block">{m.label}</span>
              {isCredDev && selectedClient && totalAvailableCredit > 0 && (
                <span className="mt-0.5 text-[9px] font-black text-violet-700 bg-white border border-violet-300 rounded-full px-1 py-[1px] block leading-tight" data-testid="credit-balance-pill">
                  R$ {totalAvailableCredit.toFixed(2)}
                </span>
              )}
            </button>
          );
        })}</div>{selectedClient && totalAvailableCredit === 0 && payMethods.some(m => m.key === 'credito_devolucao') && (
          <p className="text-[10px] text-slate-400 mt-2 italic">Sem crédito de devolução disponível para <strong>{selectedClient.name}</strong>.</p>
        )}
        {/* iter156et: parcelas do cartao (mobile) — somente as cadastradas; valor da parcela; taxa/liquido se show_fee_in_sales */}
        {paymentMethod === 'credito' && (!payments || payments.length === 0) && (() => {
          const _pm = (payMethods || []).find(m => m.pmId === paymentMethodId) || {};
          const tiers = (Array.isArray(_pm.fee_tiers) ? _pm.fee_tiers : []).slice().sort((a, b) => Number(a.installments) - Number(b.installments));
          const opts = tiers.length ? tiers.map(t => Number(t.installments) || 1) : [1];
          const n = opts.includes(Number(pickInstallments)) ? Number(pickInstallments) : opts[0];
          if (n !== Number(pickInstallments)) setTimeout(() => setPickInstallments(n), 0);
          const pct = (tiers.find(t => Number(t.installments) === n) || {}).fee_percent || 0;
          const amt = Number(total) || 0;
          return (
            <div className="mt-2 flex items-center gap-2 p-2 bg-violet-50 border border-violet-200 rounded-xl" data-testid="pdv-mobile-installments-box">
              <span className="text-[10px] font-black text-violet-800 uppercase">Parcelas</span>
              <select value={n} onChange={e => setPickInstallments(Number(e.target.value) || 1)} className="px-2 py-2 border border-violet-300 rounded-lg text-sm font-bold bg-white" data-testid="pdv-mobile-installments-select">
                {opts.map(k => <option key={k} value={k}>{k}x</option>)}
              </select>
              <div className="flex-1 text-right text-[11px] text-violet-900 leading-tight">
                <div>{n}x de <strong className="text-sm">{fmtBRL(amt / (n || 1))}</strong></div>
                {_pm.show_fee_in_sales && Number(pct) > 0 && <div className="opacity-80">taxa {Number(pct).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}% · liquido <strong>{fmtBRL(amt - amt * Number(pct) / 100)}</strong></div>}
              </div>
            </div>
          );
        })()}</div>
        <div className="p-3 sm:p-4 border-b border-gray-100 grid grid-cols-2 gap-2 sm:gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5">Desconto</p>
            <div className="flex items-stretch border border-gray-200 rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-teal-400 focus-within:border-teal-400">
              <div className="flex bg-gray-100 p-0.5 shrink-0">
                <button type="button" onClick={() => setDiscountType('value')} disabled={!pdvPerms?.give_discount} className={`px-2 rounded-lg text-[11px] font-black disabled:opacity-50 ${discountType === 'value' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`} data-testid="discount-value">R$</button>
                <button type="button" onClick={() => setDiscountType('percent')} disabled={!pdvPerms?.give_discount} className={`px-2 rounded-lg text-[11px] font-black disabled:opacity-50 ${discountType === 'percent' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`} data-testid="discount-percent">%</button>
              </div>
              <input type="number" step="0.01" value={discountInput} onChange={e => setDiscountInput(e.target.value)} disabled={!pdvPerms?.give_discount} placeholder={pdvPerms?.give_discount ? '0' : 'Sem perm.'} className="min-w-0 w-full flex-1 px-2 py-2 text-right text-sm font-semibold outline-none disabled:bg-slate-50 disabled:text-slate-400" style={noZoomStyle} inputMode="decimal" data-testid="discount-input" />
            </div>
            {pdvPerms?.give_discount && pdvPerms?.max_discount_pct < 100 && (<p className="text-[10px] text-amber-600 mt-1">Max: {pdvPerms.max_discount_pct}%</p>)}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5">Acrescimo</p>
            <div className="flex items-stretch border border-gray-200 rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-teal-400 focus-within:border-teal-400">
              <div className="flex bg-gray-100 p-0.5 shrink-0">
                <button type="button" onClick={() => setSurchargeType('value')} disabled={!pdvPerms?.give_surcharge} className={`px-2 rounded-lg text-[11px] font-black disabled:opacity-50 ${surchargeType === 'value' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`} data-testid="surcharge-value">R$</button>
                <button type="button" onClick={() => setSurchargeType('percent')} disabled={!pdvPerms?.give_surcharge} className={`px-2 rounded-lg text-[11px] font-black disabled:opacity-50 ${surchargeType === 'percent' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`} data-testid="surcharge-percent">%</button>
              </div>
              <input type="number" step="0.01" value={surchargeInput} onChange={e => setSurchargeInput(e.target.value)} disabled={!pdvPerms?.give_surcharge} placeholder={pdvPerms?.give_surcharge ? '0' : 'Sem perm.'} className="min-w-0 w-full flex-1 px-2 py-2 text-right text-sm font-semibold outline-none disabled:bg-slate-50 disabled:text-slate-400" style={noZoomStyle} inputMode="decimal" data-testid="surcharge-input" />
            </div>
          </div>
        </div>
        <div className="p-4 bg-gray-50"><div className="flex items-center justify-between mb-1 text-sm"><span className="text-gray-500">Subtotal ({items.length} {items.length === 1 ? 'item' : 'itens'}{(() => { const q = items.reduce((s, it) => s + (parseFloat(it.quantity) || 0), 0); return q !== items.length ? ` · ${Number.isInteger(q) ? q : q.toFixed(2)} un.` : ''; })()})</span><span className="font-medium">R$ {subtotal.toFixed(2)}</span></div>{discountValue > 0 && <div className="flex items-center justify-between mb-1 text-sm"><span className="text-red-500">Desconto</span><span className="text-red-500">-R$ {discountValue.toFixed(2)}</span></div>}<div className="flex items-center justify-between pt-2 border-t border-gray-200 mt-2"><span className="text-lg font-bold text-gray-900">Total</span><span className="text-2xl font-bold text-teal-600" data-testid="pdv-total">R$ {total.toFixed(2)}</span></div></div>
        <div className="p-4">
          {(saleType === 'venda' && !isPedido && !editPedidoId && !initialPedidoId) ? (
            <FinalizeSplitButton
              processing={processing}
              allowNFe={allowNFe}
              allowNFCe={allowNFCe}
              certConfigured={certConfigured}
              slug={slug}
              onFinalize={(action) => finalizeSale(action)}
              disabled={
                processing || items.length === 0 ||
                (!isPedido && !editPedidoId && !paymentTouched && (!payments || payments.length === 0))
              }
            />
          ) : (
            <button
              onClick={() => finalizeSale()}
              disabled={
                processing || items.length === 0 ||
                (!isPedido && !editPedidoId && !paymentTouched && (!payments || payments.length === 0))
              }
              title={(!isPedido && !editPedidoId && !paymentTouched && (!payments || payments.length === 0)) ? 'Selecione a forma de pagamento antes de finalizar' : ''}
              data-testid="pdv-finalize"
              className="w-full bg-teal-600 hover:bg-teal-700 active:bg-teal-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-4 rounded-xl font-bold text-base transition-colors flex items-center justify-center space-x-2 shadow-lg shadow-teal-600/20"
            >
              {processing ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : <><CheckCircle size={20} /><span>{editPedidoId ? 'Atualizar Pedido' : initialPedidoId ? 'Confirmar Venda' : isPedido ? 'Salvar Pedido' : 'Finalizar Venda'}</span></>}
            </button>
          )}
        </div>
      </div>)}
    </div>
  );
};

/* ========== FinalizeSplitButton — iter77bg: split-button "Finalizar Venda" com 4 opcoes + cert-aware riscado ========== */
const FinalizeSplitButton = ({ processing, disabled, allowNFe, allowNFCe, certConfigured = false, slug = '', onFinalize }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const Spinner = <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />;

  // iter77bg: NFe/NFCe options render as "riscado" (line-through) when cert is missing.
  // Clicking redirects user to fiscal cert config page instead of trying to emit.
  const goToCertConfig = () => {
    setOpen(false);
    toast.error('Configure o certificado digital A1 antes de emitir NF-e/NFC-e (Configuracoes > Fiscal > Certificado).', { duration: 5000 });
    if (slug) {
      try { window.location.href = `/${slug}/configuracoes`; } catch { /* ignore */ }
    }
  };

  // iter77bm: opcoes do dropdown.
  // - 'exit' = "Finalizar e Sair" -> salva e NAVEGA para a lista de vendas (handler em PDVPage chama onClose).
  // - 'comprovante' / 'nfce' / 'nfe' = emite + permanece no PDV (handler inline).
  // O botao MAIN "Finalizar Venda" usa 'stay' (salva + permanece no PDV).
  const options = [
    { key: 'exit', label: 'Finalizar e Sair', desc: 'Salvar e ir para a lista de vendas', icon: LogOut, cls: 'text-slate-700 hover:bg-slate-50', needsCert: false },
    { key: 'comprovante', label: 'Finalizar e Emitir Comprovante', desc: 'Cupom nao-fiscal (PDF) — permanece no PDV', icon: Printer, cls: 'text-violet-700 hover:bg-violet-50', needsCert: false },
    ...(allowNFCe ? [{ key: 'nfce', label: 'Finalizar e Emitir NFC-e', desc: 'Cupom Fiscal Eletronico (modelo 65) — permanece no PDV', icon: Receipt, cls: 'text-fuchsia-700 hover:bg-fuchsia-50', needsCert: true }] : []),
    ...(allowNFe ? [{ key: 'nfe', label: 'Finalizar e Emitir NF-e', desc: 'Nota Fiscal Eletronica (modelo 55) — permanece no PDV', icon: FileText, cls: 'text-emerald-700 hover:bg-emerald-50', needsCert: true }] : []),
  ];

  return (
    <div className="relative w-full" ref={wrapperRef} data-testid="pdv-finalize-split">
      <div className="flex items-stretch shadow-lg shadow-teal-600/20 rounded-xl overflow-hidden">
        <button
          onClick={() => onFinalize('stay')}
          disabled={disabled}
          data-testid="pdv-finalize"
          className="flex-1 bg-teal-600 hover:bg-teal-700 active:bg-teal-800 disabled:bg-gray-300 text-white py-4 font-bold text-base transition-colors flex items-center justify-center space-x-2"
        >
          {processing ? Spinner : <><CheckCircle size={20} /><span>Finalizar Venda</span></>}
        </button>
        <button
          onClick={() => !disabled && setOpen(o => !o)}
          disabled={disabled}
          data-testid="pdv-finalize-toggle"
          className="px-3 bg-teal-700 hover:bg-teal-800 active:bg-teal-900 disabled:bg-gray-400 text-white font-bold border-l border-teal-500"
          title="Outras opcoes"
        >
          <ChevronDown size={18} />
        </button>
      </div>
      {open && options.length > 0 && (
        <div className="absolute z-50 bottom-full mb-1 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-2xl py-1 overflow-hidden" data-testid="pdv-finalize-menu">
          {options.map(opt => {
            const Icon = opt.icon;
            const certBlocked = opt.needsCert && !certConfigured;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => {
                  if (certBlocked) { goToCertConfig(); return; }
                  setOpen(false);
                  onFinalize(opt.key);
                }}
                className={`w-full px-4 py-3 flex items-start gap-3 text-left text-sm ${certBlocked ? 'text-slate-400 hover:bg-amber-50 line-through decoration-2 decoration-amber-500/60' : opt.cls}`}
                data-testid={`pdv-finalize-opt-${opt.key}${certBlocked ? '-disabled' : ''}`}
                title={certBlocked ? 'Certificado digital A1 nao configurado — clique para configurar' : ''}
              >
                <Icon size={18} className={`flex-shrink-0 mt-0.5 ${certBlocked ? 'opacity-50' : ''}`} />
                <div className="flex-1">
                  <p className={`font-bold leading-tight ${certBlocked ? 'no-underline' : ''}`}>{opt.label}</p>
                  <p className={`text-[11px] ${certBlocked ? 'text-amber-600 no-underline font-semibold' : 'text-slate-500'}`}>
                    {certBlocked ? 'Certificado A1 nao configurado — clique para configurar' : opt.desc}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};


/* ========== COMPANY SETTINGS ========== */

/* ========== SETTINGS HUB (iter156gf: aba Catalogo migrada para Itens > Catalogo; aqui so Empresa & Defaults) ========== */
const SettingsHub = () => {
  return (
    <div data-testid="settings-hub" className="space-y-4">
      <CompanySettingsPage />
    </div>
  );
};


const CompanySettingsPage = ({ companyId: companyIdProp, onlyTab = null }) => {
  const [settings, setSettings] = useState({
    default_sales_account: '', default_purchase_account: '', default_payment_method: '',
    pdv_desktop_auto: true, stock_blind_receipt: false, manual_stock_feed_on_transfer: false, allow_negative_stock_sale: false, sales_user_sees_only_own: false,
    catalog_as_pdv: false, pdv_company_id: '',
    catalog_creates_purchase_request: false, catalog_purchase_request_allowed_company_ids: [],
    // iter77ct.37: preco individual por filial
    per_branch_pricing_enabled: false,
  });
  // iter77ca: cliente padrão do PDV — carregado da company atual (x-company-id no header)
  const [currentCompanyRecord, setCurrentCompanyRecord] = useState(null);
  const [defaultClientId, setDefaultClientId] = useState('');
  const [companyClients, setCompanyClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [natops, setNatops] = useState([]);
  const [tenantSettings, setTenantSettings] = useState({}); // /api/settings (catalog flags)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(onlyTab || 'geral');
  const [bulkAction, setBulkAction] = useState(null); // 'zero' | 'delete' | null
  useEffect(() => {
    const f = async () => {
      try {
        const [s, a, pm, co, ts, no] = await Promise.all([
          axios.get(`${ERP}/company-settings${companyIdProp ? `?company_id=${companyIdProp}` : ''}`, { withCredentials: true }),
          axios.get(`${ERP}/chart-accounts`, { withCredentials: true }),
          axios.get(`${ERP}/payment-methods`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
          axios.get(`${ERP}/companies`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
          axios.get(`${API}/settings`, { withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(`${ERP}/natureza-operacao?active=true&tipo=saida`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
        ]);
        setSettings(prev => ({
          ...prev,
          ...(s.data || {}),
          // Hydrate new catalog-purchase fields from /api/settings (canonical store_config)
          catalog_creates_purchase_request: !!(ts.data?.catalog_creates_purchase_request),
          catalog_purchase_request_allowed_company_ids: Array.isArray(ts.data?.catalog_purchase_request_allowed_company_ids)
            ? ts.data.catalog_purchase_request_allowed_company_ids
            : [],
        }));
        setAccounts(a.data.data || []);
        setPaymentMethods(pm.data.data || []);
        setCompanies(co.data.data || []);
        setTenantSettings(ts.data || {});
        setNatops(no.data.data || []);
      } catch {} finally { setLoading(false); }
    };
    f();
  }, []);

  // iter77cb: efeito dedicado ao Cliente Padrão do PDV — REAGE à troca de filial no topbar (companyIdProp).
  // Fallback: se prop não veio, usa x-company-id do header (compatibilidade com uso embutido).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = axios.defaults.headers?.common || {};
        const activeCompanyId = companyIdProp || headers['x-company-id'];
        if (!activeCompanyId) { setCurrentCompanyRecord(null); setDefaultClientId(''); setCompanyClients([]); return; }
        const { data: comp } = await axios.get(`${ERP}/companies/${activeCompanyId}`, { withCredentials: true });
        if (cancelled) return;
        setCurrentCompanyRecord(comp);
        setDefaultClientId(comp.default_client_id || '');
        const { data: cl } = await axios.get(`${ERP}/clients?company_id=${activeCompanyId}`, { withCredentials: true });
        if (cancelled) return;
        // strict — dropdown mostra APENAS clientes com company_ids contendo esta filial
        const strict = (cl.data || []).filter(c => Array.isArray(c.company_ids) && c.company_ids.includes(activeCompanyId));
        setCompanyClients(strict);
      } catch (_e) { /* silencioso */ }
    })();
    return () => { cancelled = true; };
  }, [companyIdProp]);
  const handleSave = async () => {
    setSaving(true);
    try {
      const { _id, id, ...rest } = settings;
      await axios.put(`${ERP}/company-settings${companyIdProp ? `?company_id=${companyIdProp}` : ''}`, rest, { withCredentials: true });
      // iter77ca: persiste default_client_id na filial ativa
      if (currentCompanyRecord?.id) {
        try {
          await axios.put(`${ERP}/companies/${currentCompanyRecord.id}`, { default_client_id: defaultClientId || null }, { withCredentials: true });
        } catch (_e) { /* non-fatal — talvez sem permissão */ }
      }
      // Sync catalog-related flags to /api/settings for the public catalog
      try {
        await axios.put(`${API}/settings`, {
          ...tenantSettings,
          catalog_as_pdv: !!settings.catalog_as_pdv,
          pdv_company_id: settings.pdv_company_id || '',
          catalog_creates_purchase_request: !!settings.catalog_creates_purchase_request,
          catalog_purchase_request_allowed_company_ids: settings.catalog_purchase_request_allowed_company_ids || [],
          block_catalog_no_stock: !!tenantSettings.block_catalog_no_stock,
        }, { withCredentials: true });
      } catch { /* non-fatal */ }
      toast.success('Configuracoes salvas!');
    }
    catch (err) {
      // iter156am: expor a mensagem real do backend em vez de "Erro ao salvar" generico
      const resp = err?.response;
      let msg = 'Erro ao salvar';
      try {
        const d = resp?.data;
        if (d) {
          if (typeof d === 'string') msg = d;
          else if (typeof d.detail === 'string') msg = d.detail;
          else if (d.message) msg = d.message;
          else if (d.detail?.message) msg = d.detail.message;
          else if (d.detail?.detail) msg = d.detail.detail;
          else msg = JSON.stringify(d).slice(0, 300);
        } else if (err?.message) {
          msg = err.message;
        }
      } catch { /* keep default */ }
      // eslint-disable-next-line no-console
      console.error('[company-settings save error]', resp?.status, resp?.data, err);
      toast.error(`Erro ao salvar: ${msg}`, { duration: 12000 });
    }
    finally { setSaving(false); }
  };
  if (loading) return <Loader />;
  // iter156gf: aba Catalogo saiu de Configuracoes -> vive em Itens > Catalogo (onlyTab="catalogo")
  const tabs = [
    { id: 'geral', label: 'Geral', icon: SettingsIcon },
    { id: 'vendas', label: 'Vendas / PDV', icon: ShoppingCart },
    { id: 'estoque', label: 'Estoque', icon: Boxes },
    { id: 'catalogo', label: 'Catalogo', icon: Sparkles },
  ].filter(t => onlyTab ? t.id === onlyTab : t.id !== 'catalogo');
  const update = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const card = "bg-white border border-gray-200 rounded-2xl p-5 shadow-sm";
  const labelCls = "block text-xs font-semibold text-gray-500 mb-1";
  return (
    <div data-testid="company-settings-page">
      {/* iter77dc.9: banner de herança da matriz — visivel apenas quando ha companyIdProp
          (usuario esta editando configs de uma filial especifica). Se desmarcado, os
          inputs abaixo ficam desabilitados e a filial usa a config da Matriz. */}
      {companyIdProp && (() => {
        const isMatriz = !!currentCompanyRecord?.is_matriz;
        const own = !!settings.use_own_settings;
        if (isMatriz) {
          return (
            <div className="mb-4 p-3 rounded-2xl border-2 border-cyan-200 bg-cyan-50 flex items-start gap-3" data-testid="settings-matriz-banner">
              <div className="w-9 h-9 rounded-lg bg-cyan-600 text-white flex items-center justify-center flex-shrink-0"><Crown size={16} /></div>
              <div className="text-xs text-cyan-900">
                <p className="font-black uppercase tracking-wider text-[10px] mb-0.5">Empresa Matriz</p>
                <p>As configuracoes salvas aqui sao a <strong>base padrao</strong>. As filiais que nao marcarem "configuracoes proprias" vao herdar destas.</p>
              </div>
            </div>
          );
        }
        return (
          <label className="mb-4 p-3 rounded-2xl border-2 border-purple-300 bg-purple-50 flex items-start gap-3 cursor-pointer transition-colors hover:bg-purple-100" data-testid="settings-own-toggle-wrap">
            <input
              type="checkbox"
              checked={own}
              onChange={async (e) => {
                const next = e.target.checked;
                update('use_own_settings', next);
                // salva imediatamente para efeito rapido
                try {
                  setSaving(true);
                  const { _id, id, ...rest } = settings;
                  await axios.put(`${ERP}/company-settings?company_id=${companyIdProp}`, { ...rest, use_own_settings: next }, { withCredentials: true });
                  // recarrega da fonte para refletir herdanca / override
                  const { data } = await axios.get(`${ERP}/company-settings?company_id=${companyIdProp}`, { withCredentials: true });
                  setSettings(prev => ({ ...prev, ...(data || {}) }));
                  toast.success(next ? 'Filial agora tem configuracoes proprias' : 'Filial passou a herdar da Matriz');
                } catch { toast.error('Erro ao alternar'); }
                finally { setSaving(false); }
              }}
              className="mt-0.5 w-4 h-4 accent-purple-600"
              data-testid="settings-use-own"
            />
            <div className="text-xs text-purple-900">
              <p className="font-black uppercase tracking-wider text-[10px] mb-0.5">Configuracoes proprias desta filial</p>
              <p>Marcado: essa filial tem <strong>configuracoes independentes</strong> em todas as abas (as alteracoes salvas aqui so afetam esta filial). Desmarcado: herda TUDO da Matriz (o formulario fica somente-leitura e mostra os valores da Matriz).</p>
            </div>
          </label>
        );
      })()}
      {/* Tab strip */}
      <div className={`${onlyTab ? 'hidden' : 'flex'} flex-wrap gap-2 mb-5 bg-slate-100 p-1.5 rounded-2xl ${companyIdProp && !currentCompanyRecord?.is_matriz && !settings.use_own_settings ? 'opacity-70' : ''}`} data-testid="settings-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl text-xs md:text-sm font-bold transition-all ${activeTab === t.id ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
            data-testid={`settings-tab-${t.id}`}
          >
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      <fieldset
        disabled={!!(companyIdProp && !currentCompanyRecord?.is_matriz && !settings.use_own_settings)}
        className="disabled:opacity-60 disabled:pointer-events-none border-0 p-0 m-0"
        data-testid="settings-fieldset"
      >

      {activeTab === 'geral' && (
        <div className={card} data-testid="tab-geral">
          <h3 className="font-bold text-gray-900 mb-2">Geral</h3>
          <p className="text-xs text-gray-500 mb-4">Parametros gerais que afetam todos os modulos.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Conta Padrao para Recebimentos (Banco)</label>
              <select value={settings.default_bank_account_id || ''} onChange={e => update('default_bank_account_id', e.target.value)} className={inputClass} data-testid="default-bank-account">
                <option value="">Nenhuma</option>
                {paymentMethods.map(pm => pm.bank_account_id ? <option key={pm.id} value={pm.bank_account_id}>{pm.name}</option> : null)}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">Usada quando a forma de pagamento nao tem banco vinculado.</p>
            </div>
            <div>
              <label className={labelCls}>Forma de Pagamento Padrao</label>
              <select value={settings.default_payment_method || ''} onChange={e => update('default_payment_method', e.target.value)} className={inputClass} data-testid="default-payment-method">
                <option value="">Nenhuma</option>
                {paymentMethods.map(pm => <option key={pm.id} value={pm.code || pm.name}>{pm.name}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'vendas' && (
        <div className={card} data-testid="tab-vendas">
          <h3 className="font-bold text-gray-900 mb-2">Vendas e PDV</h3>
          <p className="text-xs text-gray-500 mb-4">Comportamento do Ponto de Venda e do Pedido.</p>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Plano de Conta padrao para Vendas</label>
              <select value={settings.default_sales_account || ''} onChange={e => update('default_sales_account', e.target.value)} className={inputClass} data-testid="default-sales-account">
                <option value="">Nenhum</option>
                {accounts.filter(a => a.type === 'income').map(a => <option key={a.id} value={a.code}>{a.code} - {a.name}</option>)}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">Lancamentos de vendas no financeiro usarao este plano de conta.</p>
            </div>
            {/* iter77ca: Cliente Padrão do PDV (filial ativa) */}
            {currentCompanyRecord && (
              <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-xl">
                <label className="block text-xs font-bold text-cyan-800 mb-1 flex items-center gap-1.5">
                  <Users size={13} /> Cliente Padrão do PDV — filial <em className="font-normal">{currentCompanyRecord.name}</em>
                </label>
                <p className="text-[11px] text-cyan-700 mb-2">
                  Cliente pré-selecionado automaticamente ao abrir uma nova venda no PDV desta filial. O caixa pode trocar durante a venda.
                </p>
                <select
                  value={defaultClientId || ''}
                  onChange={e => setDefaultClientId(e.target.value)}
                  className={inputClass}
                  data-testid="settings-default-pdv-client"
                >
                  <option value="">— Sem cliente padrão (Consumidor Final) —</option>
                  {companyClients.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.document ? ` · ${c.document}` : ''}{c.is_default_pdv_consumer ? ' ★ auto' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <label className="flex items-start gap-3 p-3 bg-violet-50 border border-violet-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={settings.pdv_desktop_auto !== false} onChange={e => update('pdv_desktop_auto', e.target.checked)} className="mt-0.5 w-4 h-4 accent-violet-600" data-testid="toggle-pdv-desktop-auto" />
              <div>
                <span className="text-sm font-bold text-slate-800">Abrir PDV em Modo Desktop automaticamente</span>
                <p className="text-[11px] text-slate-500">Quando ligado, telas maiores que 1024px abrirao o PDV ja no layout amplo de caixa.</p>
              </div>
            </label>
            {/* iter77ct.37: preco individual por filial */}
            <label className="flex items-start gap-3 p-3 bg-fuchsia-50 border-2 border-fuchsia-200 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.per_branch_pricing_enabled} onChange={e => update('per_branch_pricing_enabled', e.target.checked)} className="mt-0.5 w-4 h-4 accent-fuchsia-600" data-testid="toggle-per-branch-pricing" />
              <div>
                <span className="text-sm font-bold text-slate-800 flex items-center gap-1.5">Tratar Preco de Venda de forma INDIVIDUAL por Filial <span className="text-[9px] font-black text-white bg-fuchsia-600 px-1.5 py-0.5 rounded-full uppercase">multi-loja</span></span>
                <p className="text-[11px] text-slate-500">Quando <strong>ligado</strong>: ao editar o preco (ou custo) de um produto pela tela de <strong>Produtos</strong>, o valor sera salvo como <strong>preco EXCLUSIVO da filial atual</strong> (nao afeta as demais). O cadastro global do produto permanece intacto e serve como fallback (valor herdado) para filiais sem preco proprio. Isso ja acontece automaticamente pelo modal Compartilhar Produto &mdash; o toggle apenas expande esse comportamento para a edicao direta do cadastro.<br />Quando <strong>desligado</strong>: editar o preco altera o cadastro global e reflete em todas as filiais (comportamento padrao).</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.pdv_manual_confirm} onChange={e => update('pdv_manual_confirm', e.target.checked)} className="mt-0.5 w-4 h-4 accent-amber-600" data-testid="toggle-pdv-manual-confirm" />
              <div>
                <span className="text-sm font-bold text-slate-800">Quantidade liberada no PDV (confirmacao manual)</span>
                <p className="text-[11px] text-slate-500">Quando ligado, ao bipar/selecionar um produto, ele aparece no painel de pre-venda (Quantidade / Valor Unitario / Total) para o atendente conferir e ajustar — somente apos clicar em <strong>ENTER / Adicionar Item</strong> o item entra na lista. Equivale ao atalho <kbd className="px-1 text-[10px] bg-white border rounded">F2</kbd>.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 bg-cyan-50 border border-cyan-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.pdv_allow_date_edit} onChange={e => update('pdv_allow_date_edit', e.target.checked)} className="mt-0.5 w-4 h-4 accent-cyan-600" data-testid="toggle-pdv-allow-date-edit" />
              <div>
                <span className="text-sm font-bold text-slate-800">Permitir edicao da data da venda no PDV</span>
                <p className="text-[11px] text-slate-500">Quando ligado, o caixa pode lancar uma venda em data retroativa (ex: vendas que ficaram pra registrar depois). Quando desligado, o sistema sempre usa a data/hora atual.</p>
              </div>
            </label>
            {/* iter156ei: preview do calculo do imposto na edicao da NF-e */}
            <label className="flex items-start gap-3 p-3 bg-violet-50 border border-violet-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={settings.nfe_tax_preview !== false} onChange={e => update('nfe_tax_preview', e.target.checked)} className="mt-0.5 w-4 h-4 accent-violet-600" data-testid="toggle-nfe-tax-preview" />
              <div>
                <span className="text-sm font-bold text-slate-800">Mostrar "Calculo do Imposto" na edicao da NF-e</span>
                <p className="text-[11px] text-slate-500">Exibe a setinha "Tributos aplicados" na tela da nota com o bloco CALCULO DO IMPOSTO (BC ICMS, ICMS, BC ST, ICMS-ST, produtos, frete, desconto, IPI, total) calculado pela mesma rotina da emissao — para validar antes de enviar a SEFAZ. Desligue se nao usa NF-e.</p>
              </div>
            </label>
            {/* iter156ea: nao agrupar produtos iguais no PDV */}
            <label className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.pdv_no_group_items} onChange={e => update('pdv_no_group_items', e.target.checked)} className="mt-0.5 w-4 h-4 accent-rose-600" data-testid="toggle-pdv-no-group-items" />
              <div>
                <span className="text-sm font-bold text-slate-800">Nao agrupar produtos iguais no PDV</span>
                <p className="text-[11px] text-slate-500">Quando <strong>ligado</strong>: ao bipar/adicionar o mesmo produto novamente, o PDV (Desktop e Mobile) cria <strong>uma nova linha</strong> em vez de somar a quantidade — util para itens serializados ou descontos individuais por linha. As linhas sao gravadas separadas na venda e aparecem assim na visualizacao da venda e no comprovante. Quando <strong>desligado</strong>: o mesmo produto e agrupado em uma unica linha (comportamento padrao).</p>
              </div>
            </label>
            {/* iter77db: Distribuir Lojas */}
            <label className="flex items-start gap-3 p-3 bg-purple-50 border-2 border-purple-200 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.distribute_stores_enabled} onChange={e => update('distribute_stores_enabled', e.target.checked)} className="mt-0.5 w-4 h-4 accent-purple-600" data-testid="toggle-distribute-stores" />
              <div>
                <span className="text-sm font-bold text-slate-800 flex items-center gap-1.5">Distribuir Lojas nos itens da venda <span className="text-[9px] font-black text-white bg-purple-600 px-1.5 py-0.5 rounded-full uppercase">informativo</span></span>
                <p className="text-[11px] text-slate-500">Quando <strong>ligado</strong>: cada item adicionado ao PDV/Pedido ganha um botao <strong>Lojas</strong> para dividir a quantidade entre as lojas do <strong>seu cliente</strong> (ex.: cliente com 10 lojas, voce distribui 20un em 5un para cada). O nome da loja e digitado na hora — nao usa cadastro de filiais. E somente informativo (aparece no comprovante/relatorio), <strong>nao impacta estoque</strong>. A soma das quantidades das lojas deve ser exatamente igual a quantidade do item.</p>
              </div>
            </label>
            {/* iter77dc: Modelo de Impressao de Venda */}
            <div className="p-3 bg-blue-50 border-2 border-blue-200 rounded-xl">
              <label className={`${labelCls} flex items-center gap-1.5 text-blue-900`}>
                <Printer size={13} /> Modelo de Impressao — Venda
              </label>
              <p className="text-[11px] text-slate-500 mb-2">Marque <strong>um ou mais</strong> modelos: cada um vira uma opcao no menu de impressao da venda (lista de vendas e pos-venda). O Cupom Nao-Fiscal continua sempre disponivel. Nenhum marcado = nao mostrar.</p>
              <div className="space-y-1.5" data-testid="print-sale-model-select">
                {PRINT_SALE_MODELS.map(m => {
                  const cur = parsePrintModels(settings.print_sale_model);
                  const on = cur.includes(m.key);
                  return (
                    <label key={m.key} className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${on ? 'bg-white border-blue-400' : 'bg-white/60 border-slate-200 hover:border-slate-300'}`} data-testid={`print-sale-model-${m.key}`}>
                      <input type="checkbox" checked={on} onChange={e => { const next = e.target.checked ? [...cur, m.key] : cur.filter(k => k !== m.key); update('print_sale_model', PRINT_SALE_MODELS.map(x => x.key).filter(k => next.includes(k)).join(',')); }} className="mt-0.5 accent-blue-600 w-4 h-4" data-testid={`print-sale-model-check-${m.key}`} />
                      <span className="text-xs text-slate-700"><strong>Imprimir Venda ({m.label})</strong><span className="block text-[11px] text-slate-500">{m.desc}</span></span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="pt-2 pb-1">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Emissao de Nota Fiscal no PDV</p>
              <p className="text-[10px] text-slate-400">Ao ligar, no fim da venda o caixa podera escolher o tipo de documento a emitir (sera salvo como rascunho ate a integracao SEFAZ).</p>
            </div>
            <label className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.pdv_emit_nfe} onChange={e => update('pdv_emit_nfe', e.target.checked)} className="mt-0.5 w-4 h-4 accent-emerald-600" data-testid="toggle-pdv-emit-nfe" />
              <div>
                <span className="text-sm font-bold text-slate-800">Permitir emitir NF-e no PDV</span>
                <p className="text-[11px] text-slate-500">Habilita a opcao de gerar NF-e (modelo 55) ao finalizar a venda.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 bg-sky-50 border border-sky-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.pdv_emit_nfce} onChange={e => update('pdv_emit_nfce', e.target.checked)} className="mt-0.5 w-4 h-4 accent-sky-600" data-testid="toggle-pdv-emit-nfce" />
              <div>
                <span className="text-sm font-bold text-slate-800">Permitir emitir NFC-e no PDV</span>
                <p className="text-[11px] text-slate-500">Habilita a opcao de gerar NFC-e (modelo 65 - consumidor) ao finalizar a venda.</p>
              </div>
            </label>
            <div>
              <label className={labelCls}>Natureza de Operacao padrao do PDV</label>
              <select value={settings.pdv_default_natop_id || ''} onChange={e => update('pdv_default_natop_id', e.target.value)} className={inputClass} data-testid="default-pdv-natop">
                <option value="">Nenhuma — pedir ao usuario</option>
                {natops.map(n => <option key={n.id} value={n.id}>{n.code} - {n.description}</option>)}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">Quando definida, sera pre-selecionada nas opcoes de emissao do PDV.</p>
            </div>
          </div>
          {/* iter156gg: Pedido de Venda -> Financeiro / Estoque */}
          <div className="space-y-3 mt-4" data-testid="pedido-flags-block">
            <label className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.pedido_feeds_financial} onChange={e => update('pedido_feeds_financial', e.target.checked)} className="mt-0.5 w-4 h-4 accent-orange-600" data-testid="toggle-pedido-feeds-financial" />
              <div>
                <span className="text-sm font-bold text-slate-800">Pedido de Venda alimenta o Financeiro</span>
                <p className="text-[11px] text-slate-500">Ao gerar um PEDIDO, cria um titulo a receber (em aberto) destacado como PEDIDO no Financeiro. Ao confirmar o pedido em venda, o mesmo titulo e reaproveitado (sem duplicar).</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.pedido_debits_stock} onChange={e => update('pedido_debits_stock', e.target.checked)} className="mt-0.5 w-4 h-4 accent-orange-600" data-testid="toggle-pedido-debits-stock" />
              <div>
                <span className="text-sm font-bold text-slate-800">Pedido de Venda baixa o estoque</span>
                <p className="text-[11px] text-slate-500">Ao gerar um PEDIDO, o estoque da filial ja e debitado (reserva fisica). Ao confirmar em venda nao debita de novo; se o pedido for excluido/cancelado, o estoque volta.</p>
              </div>
            </label>
          </div>
        </div>
      )}

      {activeTab === 'estoque' && (
        <div className={card} data-testid="tab-estoque">
          <h3 className="font-bold text-gray-900 mb-2">Estoque</h3>
          <p className="text-xs text-gray-500 mb-4">Comportamento de Movimentacoes e Transferencias.</p>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Plano de Conta padrao para Compras / Saidas</label>
              <select value={settings.default_purchase_account || ''} onChange={e => update('default_purchase_account', e.target.value)} className={inputClass} data-testid="default-purchase-account">
                <option value="">Nenhum</option>
                {accounts.filter(a => a.type === 'expense').map(a => <option key={a.id} value={a.code}>{a.code} - {a.name}</option>)}
              </select>
            </div>
            <label className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.allow_negative_stock_sale} onChange={e => update('allow_negative_stock_sale', e.target.checked)} className="mt-0.5 w-4 h-4 accent-emerald-600" data-testid="toggle-allow-negative-stock" />
              <div>
                <span className="text-sm font-bold text-slate-800">Permitir venda com estoque negativo (PDV)</span>
                <p className="text-[11px] text-slate-500">Por padrao o PDV bloqueia vendas quando o saldo eh insuficiente. Marque para permitir continuar a venda mesmo sem estoque (o saldo ficara negativo apos a venda).</p>
              </div>
            </label>
            {/* iter77ad: setting 'Usuario ve somente as proprias vendas' MOVIDO para o Perfil de Acesso */}
            <label className="flex items-start gap-3 p-3 bg-indigo-50 border border-indigo-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.stock_blind_receipt} onChange={e => update('stock_blind_receipt', e.target.checked)} className="mt-0.5 w-4 h-4 accent-indigo-600" data-testid="toggle-stock-blind-receipt" />
              <div>
                <span className="text-sm font-bold text-slate-800">Recebimento Cego em Transferencias</span>
                <p className="text-[11px] text-slate-500">A empresa destino NAO ve as quantidades enviadas pela origem e precisa contar fisicamente antes de confirmar.</p>
              </div>
            </label>
            {/* iter77ct.71: parametro que controla se o estoque e alimentado imediatamente
                ao confirmar recebimento (padrao) ou apenas apos clique explicito no botao
                "Alimentar Estoque" dentro do detalhe da transferencia. */}
            <label className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.manual_stock_feed_on_transfer} onChange={e => update('manual_stock_feed_on_transfer', e.target.checked)} className="mt-0.5 w-4 h-4 accent-amber-600" data-testid="toggle-manual-stock-feed" />
              <div>
                <span className="text-sm font-bold text-slate-800">Alimentar Estoque Manualmente apos Recebimento</span>
                <p className="text-[11px] text-slate-500">Quando ATIVO, a confirmacao de recebimento NAO credita o estoque automaticamente. O usuario precisa clicar em &ldquo;Alimentar Estoque&rdquo; no detalhe da transferencia. Uma vez alimentado, o botao fica inativo (nao permite duplo credito).</p>
              </div>
            </label>

            {/* iter156dk: permitir duplicar produto em Movimento/Inventario. */}
            <div className="p-3 bg-violet-50 border border-violet-100 rounded-xl space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={!!settings.stock_allow_duplicate_items} onChange={e => update('stock_allow_duplicate_items', e.target.checked)} className="mt-0.5 w-4 h-4 accent-violet-600" data-testid="toggle-stock-dup-items" />
                <div>
                  <span className="text-sm font-bold text-slate-800">Permitir duplicar item no movimento (entrada/saida/inventario)</span>
                  <p className="text-[11px] text-slate-500">Util em inventario quando o mesmo produto esta em locais fisicos diferentes e voce quer contar por local.</p>
                </div>
              </label>
              {settings.stock_allow_duplicate_items && (
                <div className="ml-7 space-y-1.5">
                  <label className="flex items-center gap-2 cursor-pointer text-xs">
                    <input type="radio" name="stock_dup_mode" checked={(settings.stock_duplicate_mode || 'aggregate') === 'aggregate'} onChange={() => update('stock_duplicate_mode', 'aggregate')} className="w-3.5 h-3.5 accent-violet-600" data-testid="stock-dup-mode-agg" />
                    <span className="font-semibold text-slate-700">Aglutinar quantidades no mesmo item</span>
                    <span className="text-[10px] text-slate-400">(soma a nova qtd no item ja existente)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-xs">
                    <input type="radio" name="stock_dup_mode" checked={(settings.stock_duplicate_mode || 'aggregate') === 'separate'} onChange={() => update('stock_duplicate_mode', 'separate')} className="w-3.5 h-3.5 accent-violet-600" data-testid="stock-dup-mode-sep" />
                    <span className="font-semibold text-slate-700">Lancar em nova linha</span>
                    <span className="text-[10px] text-slate-400">(cada duplicata vira uma linha separada; ideal para contagem por local)</span>
                  </label>
                </div>
              )}
            </div>

            {/* iter156eb: setting Bipar auto-adiciona qty=1 em Movimentos de Estoque */}
            <label className="flex items-start gap-3 p-3 bg-lime-50 border border-lime-100 rounded-xl cursor-pointer">
              <input type="checkbox" checked={!!settings.stock_scan_add_one} onChange={e => update('stock_scan_add_one', e.target.checked)} className="mt-0.5 w-4 h-4 accent-lime-600" data-testid="toggle-stock-scan-add-one" />
              <div>
                <span className="text-sm font-bold text-slate-800">Bipar codigo de barras adiciona automatico (Qtde = 1)</span>
                <p className="text-[11px] text-slate-500">Quando ATIVO: em Movimento de Estoque (entrada/saida/inventario), ao dar Enter apos digitar/bipar um codigo de barras ou SKU exato, o produto eh adicionado a lista com Qtde=1. Bipar de novo o mesmo codigo INCREMENTA a quantidade. Fluxo pensado para leitores de codigo de barras.</p>
              </div>
            </label>

            {/* Bulk operations on stock */}
            <div className="border-t border-slate-200 pt-4 mt-2">
              <p className="text-xs font-black text-slate-700 uppercase mb-2">Operacoes em massa</p>
              <p className="text-[11px] text-slate-500 mb-3">Acoes irreversiveis. Selecione as lojas que serao afetadas.</p>
              <div className="grid grid-cols-1 gap-3">
                <button onClick={() => setBulkAction('zero')} className="flex items-start gap-3 p-3 bg-amber-50 border-2 border-amber-200 hover:border-amber-400 rounded-xl text-left transition-colors" data-testid="bulk-zero-stock-btn">
                  <span className="text-2xl">📉</span>
                  <div>
                    <span className="text-sm font-bold text-amber-800 block">Zerar estoque por loja</span>
                    <p className="text-[11px] text-amber-700">Define stock=0 em todos os produtos das lojas selecionadas. O cadastro do produto e mantido.</p>
                  </div>
                </button>
              </div>
              {/* iter77dc.10: botao "Excluir produtos sem movimento" REMOVIDO por causar
                  exclusao GLOBAL indevida (apagava produtos em todas as filiais mesmo com apenas
                  uma selecionada). Essa acao agora existe SOMENTE no Super Admin com snapshot
                  automatico + botao de reverter. */}
              <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                <p className="text-[11px] text-slate-600"><strong className="text-slate-800">Excluir produtos em massa?</strong> A exclusao de produtos sem movimento agora fica <strong>apenas no Super Admin</strong> (com snapshot automatico e opcao de reverter). Isso evita o risco de apagar produtos de outras filiais sem querer.</p>
              </div>
            </div>
            {bulkAction && <BulkStockActionModal mode={bulkAction} onClose={() => setBulkAction(null)} />}
          </div>
        </div>
      )}

      {activeTab === 'catalogo' && (
        <div className={card} data-testid="tab-catalogo">
          <h3 className="font-bold text-gray-900 mb-2">Catalogo Publico</h3>
          <p className="text-xs text-gray-500 mb-4">Integracao da vitrine publica com o ERP. Escolha um dos modos abaixo (mutuamente exclusivos).</p>
          <div className="space-y-4">
            {/* MODE: NENHUM */}
            <label className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition-all ${!settings.catalog_as_pdv && !settings.catalog_creates_purchase_request ? 'border-slate-400 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}>
              <input type="radio" name="catalog-mode" checked={!settings.catalog_as_pdv && !settings.catalog_creates_purchase_request} onChange={() => setSettings(s => ({ ...s, catalog_as_pdv: false, catalog_creates_purchase_request: false, pdv_company_id: '', catalog_purchase_request_allowed_company_ids: [] }))} className="mt-0.5 w-4 h-4 accent-slate-600" data-testid="catalog-mode-none" />
              <div>
                <span className="text-sm font-bold text-slate-800">Nenhum (somente vitrine)</span>
                <p className="text-[11px] text-slate-500">O checkout do catalogo apenas redireciona para WhatsApp. Nao gera nenhum registro no ERP.</p>
              </div>
            </label>

            {/* MODE: VENDA */}
            <label className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition-all ${settings.catalog_as_pdv && !settings.catalog_creates_purchase_request ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 hover:border-cyan-300'}`}>
              <input type="radio" name="catalog-mode" checked={!!settings.catalog_as_pdv && !settings.catalog_creates_purchase_request} onChange={() => setSettings(s => ({ ...s, catalog_as_pdv: true, catalog_creates_purchase_request: false, catalog_purchase_request_allowed_company_ids: [] }))} className="mt-0.5 w-4 h-4 accent-cyan-600" data-testid="catalog-mode-sales" />
              <div className="flex-1">
                <span className="text-sm font-bold text-slate-800">Catalogo gera Pedido de Venda</span>
                <p className="text-[11px] text-slate-500">O checkout cria um <strong>Pedido de Venda</strong> em status &quot;Pendente&quot; na empresa abaixo, para o atendente confirmar.</p>
              </div>
            </label>
            {settings.catalog_as_pdv && !settings.catalog_creates_purchase_request && (
              <div className="ml-7 -mt-2">
                <label className={labelCls}>Empresa de destino dos pedidos do Catalogo *</label>
                <select value={settings.pdv_company_id || ''} onChange={e => update('pdv_company_id', e.target.value)} className={inputClass} data-testid="catalog-target-company">
                  <option value="">Padrao (primeira empresa ativa)</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.code} | {c.name}</option>)}
                </select>
              </div>
            )}

            {/* MODE: COMPRA */}
            <label className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition-all ${settings.catalog_creates_purchase_request ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-violet-300'}`}>
              <input type="radio" name="catalog-mode" checked={!!settings.catalog_creates_purchase_request} onChange={() => setSettings(s => ({ ...s, catalog_as_pdv: false, catalog_creates_purchase_request: true, pdv_company_id: '' }))} className="mt-0.5 w-4 h-4 accent-violet-600" data-testid="catalog-mode-purchase" />
              <div className="flex-1">
                <span className="text-sm font-bold text-slate-800">Catalogo gera Solicitacao de Compra</span>
                <p className="text-[11px] text-slate-500">O checkout cria uma <strong>Solicitacao de Compra</strong> para a filial que o cliente selecionar (entre as marcadas abaixo). Util para filiais que pedem reposicao via catalogo.</p>
              </div>
            </label>
            {settings.catalog_creates_purchase_request && (
              <div className="ml-7 -mt-2" data-testid="catalog-prequest-companies-list">
                <label className={labelCls}>Filiais disponiveis para selecao no checkout *</label>
                <p className="text-[11px] text-slate-400 mb-2">Marque as lojas/filiais que o cliente podera escolher no catalogo ao finalizar a solicitacao.</p>
                <div className="space-y-1 border border-slate-200 rounded-lg p-2 max-h-64 overflow-y-auto bg-white">
                  {companies.length === 0 ? (
                    <p className="text-xs text-slate-400 italic px-2 py-3">Nenhuma empresa cadastrada.</p>
                  ) : companies.map(c => {
                    const selected = (settings.catalog_purchase_request_allowed_company_ids || []).includes(c.id);
                    return (
                      <label key={c.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer ${selected ? 'bg-violet-100' : 'hover:bg-slate-50'}`} data-testid={`prequest-company-row-${c.id}`}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const prev = settings.catalog_purchase_request_allowed_company_ids || [];
                            const next = e.target.checked ? [...prev, c.id] : prev.filter(x => x !== c.id);
                            update('catalog_purchase_request_allowed_company_ids', next);
                          }}
                          className="w-4 h-4 accent-violet-600"
                          data-testid={`prequest-company-check-${c.id}`}
                        />
                        <span className="text-xs font-mono text-slate-400 w-12">{c.code}</span>
                        <span className="text-sm text-slate-800">{c.name}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">A Solicitacao aparece em <strong>Compras &raquo; Solicitacoes de Compra</strong>, podendo virar uma Compra ao ser aprovada.</p>
              </div>
            )}

            <div className="pt-2 border-t border-gray-100">
              <p className="text-[11px] text-gray-400">A configuracao visual do catalogo (cores, capa, fotos, campos) fica em <strong>Itens &raquo; Catalogo</strong>.</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'fiscal' && null}

      <div className="mt-5">
        <button onClick={handleSave} disabled={saving} className={btnPrimary} data-testid="save-company-settings">
          <Save size={14} /><span>{saving ? 'Salvando...' : 'Salvar Configuracoes'}</span>
        </button>
      </div>
      </fieldset>
    </div>
  );
};

/* ============ End of ConfiguracoesPage ============ */

/* ========== BulkStockActionModal — Zerar ou excluir produtos em massa por loja ========== */
const BulkStockActionModal = ({ mode, onClose }) => {
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState({});
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null);
  const isZero = mode === 'zero';
  const TITLE = isZero ? 'Zerar estoque por loja' : 'Excluir produtos sem movimento';
  const SUBTITLE = isZero
    ? 'Define o estoque dos produtos para 0 nas lojas selecionadas. O cadastro do produto e mantido.'
    : 'Remove DEFINITIVAMENTE produtos que NUNCA tiveram venda, movimentacao ou transferencia. Escopo: lojas selecionadas.';
  const CTA = isZero ? 'Zerar estoque' : 'Excluir produtos';
  useEffect(() => {
    axios.get(`${ERP}/companies`, { withCredentials: true })
      .then(r => setCompanies(r.data?.data || []))
      .catch(() => {});
  }, []);
  const toggleAll = (v) => setSelected(Object.fromEntries(companies.map(c => [c.id, v])));
  const run = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (!ids.length) { toast.info('Selecione ao menos uma loja'); return; }
    const expected = isZero ? 'ZERAR' : 'EXCLUIR';
    const typed = window.prompt(`Esta acao e IRREVERSIVEL.\n\nLojas afetadas: ${ids.length}\n\nDigite ${expected} para confirmar:`);
    if (typed !== expected) { toast.info('Cancelado'); return; }
    setWorking(true);
    try {
      const url = isZero ? `${API}/products/bulk-zero-stock` : `${API}/products/bulk-delete-no-movements`;
      const { data } = await axios.post(url, { company_ids: ids }, { withCredentials: true });
      setResult(data);
      if (isZero) toast.success(`Zerados ${data.zeroed} estoques em ${data.companies} loja(s)`);
      else toast.success(`Excluidos ${data.deleted} produtos · ${data.skipped_with_movements || 0} mantidos (com movimentos)`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setWorking(false); }
  };
  const headerClass = isZero ? 'bg-amber-600' : 'bg-rose-600';
  const noteClass = isZero ? 'bg-amber-50 border-amber-500' : 'bg-rose-50 border-rose-500';
  const ctaClass = isZero ? 'bg-amber-600 hover:bg-amber-700' : 'bg-rose-600 hover:bg-rose-700';
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="bulk-stock-modal">
      <div className="bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl shadow-2xl">
        <div className={`sticky top-0 ${headerClass} px-4 py-3 flex items-center justify-between`}>
          <h3 className="text-white font-bold text-sm flex items-center gap-2">{TITLE}</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className={`${noteClass} border-l-4 rounded-lg p-3 text-[11px] text-slate-700`}>
            <p className="font-bold mb-1">Atencao!</p>
            <p>{SUBTITLE}</p>
          </div>
          <div className="flex items-center justify-between text-xs">
            <button onClick={() => toggleAll(true)} className="text-blue-600 font-semibold">Marcar todas</button>
            <button onClick={() => toggleAll(false)} className="text-slate-500 font-semibold">Limpar</button>
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="bulk-stock-companies-list">
            {companies.map(c => (
              <label key={c.id} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 hover:border-blue-300 cursor-pointer">
                <input type="checkbox" checked={!!selected[c.id]} onChange={e => setSelected(s => ({ ...s, [c.id]: e.target.checked }))} className="w-4 h-4 accent-blue-600" data-testid={`bulk-company-${c.id}`} />
                <span className="text-xs font-semibold text-slate-900 flex-1">{c.code} · {c.name}</span>
              </label>
            ))}
            {companies.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Nenhuma empresa cadastrada</p>}
          </div>
          {result && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1" data-testid="bulk-stock-result">
              {isZero ? (
                <>
                  <p><strong>{result.zeroed}</strong> rows zeradas em <strong>{result.companies}</strong> loja(s)</p>
                  <p className="text-slate-500">Total de produtos no tenant: {result.products_in_tenant}</p>
                </>
              ) : (
                <>
                  <p className="text-rose-700"><strong>{result.deleted}</strong> produtos excluidos</p>
                  <p className="text-amber-700"><strong>{result.skipped_with_movements}</strong> mantidos (possuem movimentos)</p>
                  {result.errors > 0 && <p className="text-rose-700">{result.errors} erros</p>}
                </>
              )}
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-2.5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-1.5 text-xs font-semibold text-slate-500">Fechar</button>
          <button onClick={run} disabled={working} className={`px-4 py-2 text-xs font-bold text-white rounded-lg shadow ${ctaClass} disabled:opacity-50`} data-testid="bulk-stock-confirm-btn">
            {working ? 'Processando...' : CTA}
          </button>
        </div>
      </div>
    </div>
  );
};



/* ========== CLIENTS PAGE (Shared across companies) ========== */
/* ===== ClientShareModal (iter77ca) — modal para compartilhar cliente com filiais ===== */
const ClientShareModal = ({ client, companies = [], onClose, onSaved }) => {
  const [selected, setSelected] = useState(new Set(client.company_ids || []));
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const handleSave = async () => {
    setSaving(true);
    try {
      // Envia apenas o novo array company_ids; backend faz merge/overwrite (update_client sobrescreve).
      await axios.put(`${ERP}/clients/${client.id}`, { ...client, company_ids: Array.from(selected) }, { withCredentials: true });
      toast.success('Compartilhamento atualizado!');
      onSaved && onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };
  const allBranches = selected.size === 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="client-share-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 flex items-center justify-between border-b bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-600 to-blue-600 text-white flex items-center justify-center shadow-md shadow-cyan-500/40"><Share2 size={16} /></div>
            <div>
              <h2 className="font-black text-base text-slate-900">Compartilhar cliente</h2>
              <p className="text-[11px] text-slate-500">{client.name}{client.document ? ` · ${client.document}` : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="client-share-close"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <p className="text-xs text-slate-600 mb-3">Marque as filiais em que este cliente pode ser usado (PDV, financeiro, vendas). <strong>Nenhuma marcada</strong> = cliente global (visível em todas — modo legado).</p>
          <div className="space-y-2">
            {companies.map(c => (
              <label key={c.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selected.has(c.id) ? 'bg-cyan-50 border-cyan-300' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={() => toggle(c.id)}
                  className="w-4 h-4 accent-cyan-600"
                  data-testid={`client-share-toggle-${c.id}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 truncate">{c.name}</p>
                  {c.code && <p className="text-[10px] text-slate-500 font-mono">{c.code}</p>}
                </div>
                {c.is_matriz && <span className="text-[9px] font-black uppercase bg-violet-100 text-violet-700 border border-violet-300 px-1.5 py-0.5 rounded-full">MATRIZ</span>}
              </label>
            ))}
            {companies.length === 0 && (
              <p className="text-xs text-slate-400 italic text-center py-4">Nenhuma filial cadastrada.</p>
            )}
          </div>
          {allBranches && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-[11px] text-amber-800">
              <strong>Atenção:</strong> este cliente ficará <em>global</em> (visível em todas as filiais). Recomendamos marcar ao menos uma filial para isolar créditos de devolução.
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-pill-ghost">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="btn-pill-primary disabled:opacity-50" data-testid="client-share-save">
            <Save size={13} />{saving ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ClientsPage = ({ companyId, companies = [] }) => {
  const { isPharmacy } = useBusinessType();
  const { user } = useAuth();
  const isAdminOrSuper = user?.role === 'admin' || user?.role === 'superadmin';
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  // iter77ct: ajuste manual de Cashback e Credito Loja (admin/superadmin apenas)
  const [creditAdjust, setCreditAdjust] = useState({ cashback: '', cashback_reason: '', store: '', store_reason: '' });
  const [creditBalances, setCreditBalances] = useState({ cashback: 0, store: 0 });
  const [savingCredit, setSavingCredit] = useState(false);
  const [form, setForm] = useState({
    name: '', doc_type: 'cpf', document: '', email: '', phone: '', birth_date: '', notes: '', company_ids: [], pharmacy: {},
    // iter77z: campos fiscais para emissao de NF-e
    person_type: 'fisica', legal_name: '', trade_name: '',
    ie_indicator: '9', ie: '', im: '', suframa: '', foreign_client: false,
    end_cep: '', end_logradouro: '', end_numero: '', end_complemento: '', end_bairro: '',
    end_municipio: '', end_cod_municipio: '', end_uf: '', end_pais: 'Brasil', end_cod_pais: '1058',
  });
  const blankClient = useMemo(() => ({
    name: '', doc_type: 'cpf', document: '', email: '', phone: '', birth_date: '', notes: '', company_ids: [], pharmacy: {},
    person_type: 'fisica', legal_name: '', trade_name: '',
    ie_indicator: '9', ie: '', im: '', suframa: '', foreign_client: false,
    end_cep: '', end_logradouro: '', end_numero: '', end_complemento: '', end_bairro: '',
    end_municipio: '', end_cod_municipio: '', end_uf: '', end_pais: 'Brasil', end_cod_pais: '1058',
  }), []);
  const [tab, setTab] = useState('basic');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shareTarget, setShareTarget] = useState(null); // iter77ca: cliente sendo compartilhado
  // iter77z: cashback balance do cliente (exibido na aba Basico, read-only)
  const [cashbackBalance, setCashbackBalance] = useState(null);
  useEffect(() => {
    if (!editing?.id) { setCashbackBalance(null); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/cashback/balance/${editing.id}`, { withCredentials: true });
        setCashbackBalance(Number(data?.balance ?? 0));
      } catch { setCashbackBalance(null); }
    })();
  }, [editing?.id]);
  // iter77y: lookup CNPJ via BrasilAPI no cadastro de cliente PJ
  const [cnpjLookingUp, setCnpjLookingUp] = useState(false);
  // iter77z: lookup CEP para autopreencher endereco fiscal
  const [cepLookingUp, setCepLookingUp] = useState(false);
  const lookupCepAndFill = async () => {
    const digits = (form.end_cep || '').replace(/\D/g, '');
    if (digits.length !== 8) { toast.info('Digite um CEP com 8 digitos'); return; }
    setCepLookingUp(true);
    try {
      const { data } = await axios.get(`${API}/lookup/cep/${digits}`, { withCredentials: true });
      setForm(f => ({
        ...f,
        end_cep: data.cep,
        end_logradouro: f.end_logradouro || data.logradouro,
        end_bairro: f.end_bairro || data.bairro,
        end_municipio: data.municipio,
        end_cod_municipio: data.codigo_municipio_ibge || f.end_cod_municipio,
        end_uf: data.uf,
      }));
      toast.success('Endereco preenchido');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao consultar CEP'); }
    finally { setCepLookingUp(false); }
  };
  const lookupCnpjAndFill = async () => {
    const digits = (form.document || '').replace(/\D/g, '');
    if (digits.length !== 14) { toast.info('Digite um CNPJ com 14 digitos para buscar'); return; }
    setCnpjLookingUp(true);
    try {
      const { data } = await axios.get(`${API}/lookup/cnpj/${digits}`, { withCredentials: true });
      setForm(f => ({
        ...f,
        name: f.name || data.razao_social || data.nome_fantasia,
        document: data.cnpj,
        phone: f.phone || data.telefone || '',
        email: f.email || data.email || '',
        // iter77am: endereco unificado — so end_* fiscal (campo 'address' removido da UI Basicos)
        person_type: 'juridica',
        legal_name: f.legal_name || data.razao_social,
        trade_name: f.trade_name || data.nome_fantasia,
        end_cep: data.cep || f.end_cep,
        end_logradouro: data.logradouro || f.end_logradouro,
        end_numero: data.numero || f.end_numero,
        end_complemento: data.complemento || f.end_complemento,
        end_bairro: data.bairro || f.end_bairro,
        end_municipio: data.municipio || f.end_municipio,
        end_cod_municipio: data.codigo_municipio_ibge || f.end_cod_municipio,
        end_uf: data.uf || f.end_uf,
      }));
      toast.success(`Dados de ${data.razao_social || 'cliente'} carregados`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao consultar CNPJ');
    } finally { setCnpjLookingUp(false); }
  };
  const updPharma = (patch) => setForm(p => ({ ...p, pharmacy: { ...(p.pharmacy || {}), ...patch } }));
  const fetchClients = useCallback(async () => {
    try {
      // iter77ca: filtrar pela filial ativa quando selecionada.
      // Sem isso, clientes com company_ids=[filial X] apareciam em todas filiais.
      // iter77ct.109: limite elevado p/ 50000 (tenants com 19000+ clientes).
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (companyId) params.set('company_id', companyId);
      params.set('limit', '50000');
      const { data } = await axios.get(`${ERP}/clients?${params.toString()}`, { withCredentials: true });
      setClients(data.data || []);
    }
    catch { /* ignore */ } finally { setLoading(false); }
  }, [searchTerm, companyId]);
  useEffect(() => { fetchClients(); }, [fetchClients]);
  const reset = () => { setForm({ ...blankClient }); setEditing(null); setTab('basic'); setCreditAdjust({ cashback: '', cashback_reason: '', store: '', store_reason: '' }); setCreditBalances({ cashback: 0, store: 0 }); };
  const openNew = () => { reset(); setShowForm(true); };
  const openEdit = async (c) => {
    setEditing(c);
    setForm({
      ...blankClient,
      ...c,
      pharmacy: c.pharmacy || {},
      company_ids: c.company_ids || [],
    });
    setTab('basic');
    // iter77ct: pre-carrega saldos (cashback e credito loja) — vem no GET /clients/{id}
    const initCash = Number(c.cashback_balance || 0);
    const initStore = Number(c.store_credit_balance || 0);
    setCreditBalances({ cashback: initCash, store: initStore });
    setCreditAdjust({ cashback: initCash.toFixed(2), cashback_reason: '', store: initStore.toFixed(2), store_reason: '' });
    setShowForm(true);
    // iter77ct: refetch para ter os valores mais atualizados do servidor
    try {
      const { data } = await axios.get(`${ERP}/clients/${c.id}`, { withCredentials: true });
      const cash = Number(data?.cashback_balance || 0);
      const store = Number(data?.store_credit_balance || 0);
      setCreditBalances({ cashback: cash, store });
      setCreditAdjust(prev => ({
        cashback: cash.toFixed(2),
        cashback_reason: prev.cashback_reason,
        store: store.toFixed(2),
        store_reason: prev.store_reason,
      }));
    } catch { /* fallback ao list value */ }
  };
  // iter77ct: aplica ajuste manual de cashback e/ou credito loja (admin/superadmin only)
  const handleCreditAdjust = async () => {
    if (!editing?.id || !isAdminOrSuper) return;
    const newCashback = Number(creditAdjust.cashback);
    const newStore = Number(creditAdjust.store);
    if (Number.isNaN(newCashback) || newCashback < 0) { toast.error('Cashback invalido'); return; }
    if (Number.isNaN(newStore) || newStore < 0) { toast.error('Credito Loja invalido'); return; }
    const cashDiff = Math.abs(newCashback - creditBalances.cashback) >= 0.01;
    const storeDiff = Math.abs(newStore - creditBalances.store) >= 0.01;
    if (!cashDiff && !storeDiff) { toast.info('Nenhuma alteracao de saldo'); return; }
    if (cashDiff && !creditAdjust.cashback_reason.trim()) { toast.error('Informe o motivo do ajuste de Cashback'); return; }
    if (storeDiff && !creditAdjust.store_reason.trim()) { toast.error('Informe o motivo do ajuste de Credito Loja'); return; }
    setSavingCredit(true);
    try {
      if (cashDiff) {
        await axios.post(`${ERP}/clients/${editing.id}/adjust-cashback`,
          { new_balance: newCashback, reason: creditAdjust.cashback_reason },
          { withCredentials: true });
      }
      if (storeDiff) {
        await axios.post(`${ERP}/clients/${editing.id}/adjust-store-credit`,
          { new_balance: newStore, reason: creditAdjust.store_reason },
          { withCredentials: true });
      }
      toast.success('Saldo(s) ajustado(s) com sucesso');
      setCreditBalances({ cashback: newCashback, store: newStore });
      setCreditAdjust(prev => ({ ...prev, cashback_reason: '', store_reason: '' }));
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro no ajuste'); }
    finally { setSavingCredit(false); }
  };
  const handleSave = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      // iter77am: unificacao endereco + email — endereco fiscal estruturado e o unico,
      // mas tambem populamos o legacy `address` (string) para compat com modulos antigos
      // (recibos, PDV, etc). E o `email_nfe` herda o email principal quando vazio.
      const addrParts = [form.end_logradouro, form.end_numero, form.end_complemento, form.end_bairro, form.end_municipio, form.end_uf, form.end_cep].filter(Boolean);
      payload.address = addrParts.length > 0 ? addrParts.join(', ') : '';
      payload.email_nfe = form.email || '';
      payload.pharmacy = isPharmacy && form.pharmacy && Object.values(form.pharmacy).some(v => v !== '' && v != null && v !== false) ? form.pharmacy : null;
      if (editing) { await axios.put(`${ERP}/clients/${editing.id}`, payload, { withCredentials: true }); toast.success('Cliente atualizado!'); }
      else { await axios.post(`${ERP}/clients`, payload, { withCredentials: true }); toast.success('Cliente criado!'); }
      setShowForm(false); reset(); fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSubmitting(false); }
  };
  if (loading) return <Loader />;
  return (
    <div data-testid="clients-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-gray-500">{clients.length} clientes</p>
        <div className="flex flex-wrap items-center gap-2">
          <ImportToolbar templateUrl={`${API}/erp/clients/import/template`} uploadUrl={`${API}/erp/clients/import`} label="clientes" onSuccess={fetchClients} withCompanyPicker={true} pickerTitle="Em quais lojas o cliente deve aparecer?" pickerHint="Marque as lojas onde os clientes importados ficarao disponiveis. Deixe TODAS marcadas para compartilhar entre todas as lojas (comportamento padrao)." />
          <button onClick={openNew} className={btnPrimary} data-testid="add-client-btn"><Plus size={16} /><span>Novo Cliente</span></button>
        </div>
      </div>
      <div className="mb-4"><input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar cliente..." className={inputClass} data-testid="client-search" /></div>
      <FormModal open={showForm} title={editing ? 'Editar Cliente' : 'Novo Cliente'} icon={<Users size={18} />}
        onClose={() => { setShowForm(false); reset(); }} onSave={handleSave} submitting={submitting}
        saveLabel={editing ? 'Atualizar' : 'Criar'} testId="client-modal">
        {/* iter77z: abas Basico / Fiscal sempre visiveis; Farmacia so quando aplicavel */}
        <div className="flex gap-1 mb-3 border-b border-slate-200">
          <button type="button" onClick={() => setTab('basic')} className={`px-3 py-2 text-xs font-bold uppercase border-b-2 ${tab === 'basic' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-400'}`} data-testid="client-tab-basic">Dados Basicos</button>
          <button type="button" onClick={() => setTab('fiscal')} className={`px-3 py-2 text-xs font-bold uppercase border-b-2 ${tab === 'fiscal' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-400'}`} data-testid="client-tab-fiscal">Fiscal (NF-e)</button>
          {editing && (
            <button type="button" onClick={() => setTab('outros')} className={`px-3 py-2 text-xs font-bold uppercase border-b-2 ${tab === 'outros' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-400'}`} data-testid="client-tab-outros">Outros</button>
          )}
          {isPharmacy && (
            <button type="button" onClick={() => setTab('pharmacy')} className={`px-3 py-2 text-xs font-bold uppercase border-b-2 ${tab === 'pharmacy' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-400'}`} data-testid="client-tab-pharmacy">
              Farmacia <span className="ml-1 text-[8px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded">RX</span>
            </button>
          )}
        </div>
        {/* iter77z: cashback display no topo do form quando editando E saldo > 0 */}
        {editing && cashbackBalance !== null && cashbackBalance > 0 && (
          <div className="mb-3 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="client-cashback-display">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center"><CircleDollarSign size={16} className="text-emerald-700" /></div>
              <div>
                <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Cashback disponivel</span>
                <p className="text-lg font-black text-emerald-700 leading-tight">R$ {cashbackBalance.toFixed(2).replace('.', ',')}</p>
              </div>
            </div>
            <span className="text-[10px] text-emerald-600/70 font-bold uppercase">Credito na loja</span>
          </div>
        )}
        {/* iter77ct: Cashback + Credito Loja — editaveis apenas por admin/superadmin — aba Outros */}
        {editing && tab === 'outros' && (
          <div className="mb-3 rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 via-fuchsia-50 to-pink-50 p-3" data-testid="client-credit-adjust-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center"><CircleDollarSign size={14} className="text-violet-700" /></div>
                <div>
                  <p className="text-[11px] font-black text-violet-700 uppercase tracking-wider leading-none">Saldos financeiros do cliente</p>
                  <p className="text-[10px] text-violet-500 mt-0.5">{isAdminOrSuper ? 'Somente admin/superadmin pode ajustar' : 'Somente visualizacao — ajuste requer perfil admin'}</p>
                </div>
              </div>
              {!isAdminOrSuper && <span className="text-[9px] font-bold text-slate-400 uppercase bg-slate-100 px-2 py-0.5 rounded">Read-only</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Cashback */}
              <div className="bg-white/70 rounded-lg border border-emerald-200 p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Cashback</span>
                  <span className="text-[10px] text-emerald-600/70">Atual: R$ {Number(creditBalances.cashback).toFixed(2).replace('.', ',')}</span>
                </div>
                <input
                  type="number" step="0.01" min="0"
                  value={creditAdjust.cashback}
                  onChange={e => setCreditAdjust(p => ({ ...p, cashback: e.target.value }))}
                  disabled={!isAdminOrSuper || savingCredit}
                  className={`${inputClass} disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed`}
                  data-testid="client-cashback-adjust-input"
                  placeholder="0,00"
                />
                <input
                  type="text"
                  value={creditAdjust.cashback_reason}
                  onChange={e => setCreditAdjust(p => ({ ...p, cashback_reason: e.target.value }))}
                  disabled={!isAdminOrSuper || savingCredit}
                  className={`${inputClass} mt-1.5 text-[11px] disabled:bg-slate-100`}
                  data-testid="client-cashback-adjust-reason"
                  placeholder="Motivo do ajuste (auditoria)"
                />
              </div>
              {/* Credito Loja */}
              <div className="bg-white/70 rounded-lg border border-violet-200 p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold text-violet-700 uppercase tracking-wider">Credito Loja</span>
                  <span className="text-[10px] text-violet-600/70">Atual: R$ {Number(creditBalances.store).toFixed(2).replace('.', ',')}</span>
                </div>
                <input
                  type="number" step="0.01" min="0"
                  value={creditAdjust.store}
                  onChange={e => setCreditAdjust(p => ({ ...p, store: e.target.value }))}
                  disabled={!isAdminOrSuper || savingCredit}
                  className={`${inputClass} disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed`}
                  data-testid="client-store-credit-adjust-input"
                  placeholder="0,00"
                />
                <input
                  type="text"
                  value={creditAdjust.store_reason}
                  onChange={e => setCreditAdjust(p => ({ ...p, store_reason: e.target.value }))}
                  disabled={!isAdminOrSuper || savingCredit}
                  className={`${inputClass} mt-1.5 text-[11px] disabled:bg-slate-100`}
                  data-testid="client-store-credit-adjust-reason"
                  placeholder="Motivo do ajuste (auditoria)"
                />
              </div>
            </div>
            {isAdminOrSuper && (
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={handleCreditAdjust}
                  disabled={savingCredit}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 disabled:from-slate-300 disabled:to-slate-400 text-white text-xs font-bold shadow"
                  data-testid="client-credit-adjust-save"
                >
                  {savingCredit ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                  <span>Aplicar ajuste</span>
                </button>
              </div>
            )}
          </div>
        )}
        <div className={tab === 'basic' ? 'grid grid-cols-1 md:grid-cols-3 gap-3' : 'hidden'} data-testid="client-form">
          {/* iter156ad: TIPO DOC em primeiro plano — destaque com pill toggle, define layout do formulario */}
          <div className="md:col-span-3">
            <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-2">Tipo de Cadastro</label>
            <div className="inline-flex bg-slate-100 p-1 rounded-xl border border-slate-200" role="tablist">
              <button
                type="button"
                onClick={() => setForm({ ...form, doc_type: 'cpf', person_type: 'fisica' })}
                className={`px-6 py-2 rounded-lg text-sm font-black transition-all ${form.doc_type === 'cpf' ? 'bg-white text-violet-700 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="client-doctype-cpf"
              >
                <span className="inline-flex items-center gap-2"><Users size={14} /> CPF <span className="text-[10px] font-normal opacity-70">(Pessoa Fisica)</span></span>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, doc_type: 'cnpj', person_type: 'juridica' })}
                className={`px-6 py-2 rounded-lg text-sm font-black transition-all ${form.doc_type === 'cnpj' ? 'bg-white text-violet-700 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="client-doctype-cnpj"
              >
                <span className="inline-flex items-center gap-2"><Building2 size={14} /> CNPJ <span className="text-[10px] font-normal opacity-70">(Pessoa Juridica)</span></span>
              </button>
            </div>
          </div>
          {/* CPF: apenas "Nome" (uma coluna cheia). CNPJ: nao mostra "Nome" (usa Razao Social/Nome Fantasia abaixo). */}
          {form.doc_type === 'cpf' && (
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Nome Completo *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} required placeholder="Nome completo" data-testid="client-name" />
            </div>
          )}
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">{form.doc_type === 'cnpj' ? 'CNPJ' : 'CPF'}{form.doc_type === 'cnpj' && <span className="text-violet-600 font-bold ml-1">(autopreenche)</span>}</label>
            <div className="flex gap-2">
              <input value={form.document} onChange={e => setForm({ ...form, document: e.target.value })} onBlur={() => { if (form.doc_type === 'cnpj') lookupCnpjAndFill(); }} className={inputClass} placeholder={form.doc_type === 'cnpj' ? 'XX.XXX.XXX/XXXX-XX' : 'XXX.XXX.XXX-XX'} data-testid="client-document" />
              {form.doc_type === 'cnpj' && (
                <button type="button" onClick={lookupCnpjAndFill} disabled={cnpjLookingUp || !form.document} className="px-3 py-2 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 disabled:from-slate-300 disabled:to-slate-400 text-white text-xs font-bold shadow-md flex items-center gap-1 flex-shrink-0" title="Buscar dados na Receita Federal" data-testid="client-cnpj-lookup">
                  {cnpjLookingUp ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
                </button>
              )}
            </div>
          </div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Telefone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputClass} placeholder="(00) 00000-0000" /></div>
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">E-mail</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputClass} placeholder="email@empresa.com" data-testid="client-email" /></div>
          {/* iter156ac: CNPJ → mostra Razao Social + Nome Fantasia. Ambos populados via BrasilAPI. */}
          {form.doc_type === 'cnpj' && (
            <>
              <div className="md:col-span-3">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Razao Social <span className="text-slate-400 font-normal">(oficial - vai na NF-e)</span></label>
                <input value={form.legal_name || form.name || ''} onChange={e => setForm({ ...form, legal_name: e.target.value, name: e.target.value, person_type: 'juridica' })} className={inputClass} placeholder="EMPRESA XYZ LTDA" data-testid="client-legal-name" required />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Nome Fantasia <span className="text-slate-400 font-normal">(nome comercial - exibido no PDV/Buscas)</span></label>
                <input value={form.trade_name || ''} onChange={e => setForm({ ...form, trade_name: e.target.value })} className={inputClass} placeholder="Loja XYZ" data-testid="client-trade-name" />
              </div>
            </>
          )}
          {/* iter77am: Data de Nascimento (PF apenas) substitui o campo Endereco — endereco vai exclusivo na aba Fiscal */}
          {form.doc_type === 'cpf' && (
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Data de Nascimento</label><input type="date" value={form.birth_date || ''} onChange={e => setForm({ ...form, birth_date: e.target.value })} className={inputClass} data-testid="client-birth-date" /></div>
          )}
          <div className="md:col-span-3"><label className="block text-xs font-semibold text-gray-500 mb-1">Observacoes</label><input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={inputClass} placeholder="Notas" /></div>
          {/* iter77ca: CompaniesPicker foi movido para o ícone "Compartilhar" na lista de clientes (Share2 icon) */}
        </div>
        {/* iter77z: aba Fiscal para emissao de NF-e */}
        <div className={tab === 'fiscal' ? 'space-y-3' : 'hidden'} data-testid="client-form-fiscal">
          <div className="bg-violet-50 border-l-4 border-violet-500 px-3 py-2 text-[11px] text-violet-900 rounded-r">
            Dados necessarios para emissao de NF-e ao cliente. Indicador IE (1=Contribuinte, 2=Isento, 9=Nao contribuinte) e codigo IBGE do municipio (7 digitos) sao obrigatorios pela SEFAZ.
            <br /><strong>iter77am:</strong> Endereco e e-mail vem unificados: o endereco fiscal abaixo eh o unico do cliente, e o e-mail informado na aba <em>Dados Basicos</em> sera usado tambem no envio da NF-e.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Tipo de Pessoa</label>
              <select value={form.person_type} onChange={e => setForm({ ...form, person_type: e.target.value })} className={inputClass} data-testid="client-person-type">
                <option value="fisica">Pessoa Fisica</option>
                <option value="juridica">Pessoa Juridica</option>
              </select>
            </div>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={!!form.foreign_client} onChange={e => setForm({ ...form, foreign_client: e.target.checked })} className="w-4 h-4 accent-violet-600" data-testid="client-foreign" />
              <span className="text-sm font-bold text-slate-700">Cliente do Exterior</span>
            </label>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Indicador IE</label>
              <select value={form.ie_indicator} onChange={e => setForm({ ...form, ie_indicator: e.target.value })} className={inputClass} data-testid="client-ie-indicator">
                <option value="1">1 - Contribuinte ICMS</option>
                <option value="2">2 - Contribuinte ICMS (isento de inscricao)</option>
                <option value="9">9 - Nao Contribuinte</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Inscricao Estadual (IE)</label>
              <input value={form.ie} onChange={e => setForm({ ...form, ie: e.target.value })} className={inputClass} placeholder="000.000.000.000" disabled={form.ie_indicator !== '1'} data-testid="client-ie" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Inscricao Municipal (IM)</label>
              <input value={form.im} onChange={e => setForm({ ...form, im: e.target.value })} className={inputClass} placeholder="opcional" data-testid="client-im" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">SUFRAMA</label>
              <input value={form.suframa} onChange={e => setForm({ ...form, suframa: e.target.value })} className={inputClass} placeholder="Zona Franca" data-testid="client-suframa" />
            </div>
            {form.person_type === 'juridica' && (<>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Razao Social</label>
                <input value={form.legal_name} onChange={e => setForm({ ...form, legal_name: e.target.value })} className={inputClass} data-testid="client-legal-name" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Nome Fantasia</label>
                <input value={form.trade_name} onChange={e => setForm({ ...form, trade_name: e.target.value })} className={inputClass} data-testid="client-trade-name" />
              </div>
            </>)}
          </div>
          <div className="border-t border-slate-200 pt-3">
            <p className="text-xs font-black text-violet-700 uppercase mb-2 tracking-wider">Endereco Fiscal</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">CEP <span className="text-violet-600 font-bold">(autopreenche)</span></label>
                <div className="flex gap-1">
                  <input value={form.end_cep} onChange={e => setForm({ ...form, end_cep: e.target.value })} onBlur={lookupCepAndFill} className={inputClass} placeholder="00000-000" data-testid="client-end-cep" />
                  <button type="button" onClick={lookupCepAndFill} disabled={cepLookingUp || !form.end_cep} className="px-2 py-2 rounded-lg bg-violet-500 hover:bg-violet-600 disabled:bg-slate-300 text-white text-xs font-bold shadow flex-shrink-0" data-testid="client-cep-lookup">
                    {cepLookingUp ? <RefreshCw size={12} className="animate-spin" /> : <Search size={12} />}
                  </button>
                </div>
              </div>
              <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Logradouro</label><input value={form.end_logradouro} onChange={e => setForm({ ...form, end_logradouro: e.target.value })} className={inputClass} data-testid="client-end-logradouro" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Numero</label><input value={form.end_numero} onChange={e => setForm({ ...form, end_numero: e.target.value })} className={inputClass} data-testid="client-end-numero" /></div>
              <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Complemento</label><input value={form.end_complemento} onChange={e => setForm({ ...form, end_complemento: e.target.value })} className={inputClass} placeholder="Apto, Sala, etc" data-testid="client-end-complemento" /></div>
              <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Bairro</label><input value={form.end_bairro} onChange={e => setForm({ ...form, end_bairro: e.target.value })} className={inputClass} data-testid="client-end-bairro" /></div>
              <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Municipio</label><input value={form.end_municipio} onChange={e => setForm({ ...form, end_municipio: e.target.value })} className={inputClass} data-testid="client-end-municipio" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">UF</label><input value={form.end_uf} onChange={e => setForm({ ...form, end_uf: e.target.value.toUpperCase() })} maxLength={2} className={inputClass} placeholder="SP" data-testid="client-end-uf" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Cod. Mun. IBGE</label><input value={form.end_cod_municipio} onChange={e => setForm({ ...form, end_cod_municipio: e.target.value })} maxLength={7} className={inputClass} placeholder="7 digitos" data-testid="client-end-cod-municipio" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Pais</label><input value={form.end_pais} onChange={e => setForm({ ...form, end_pais: e.target.value })} className={inputClass} data-testid="client-end-pais" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Cod. Pais</label><input value={form.end_cod_pais} onChange={e => setForm({ ...form, end_cod_pais: e.target.value })} maxLength={4} className={inputClass} placeholder="1058" data-testid="client-end-cod-pais" /></div>
            </div>
          </div>
        </div>
        {isPharmacy && (
          <div className={tab === 'pharmacy' ? 'space-y-3' : 'hidden'} data-testid="client-form-pharmacy">
            <div className="bg-emerald-50 border-l-4 border-emerald-500 px-3 py-2 text-[11px] text-emerald-900 rounded-r">
              Dados clinicos do cliente — usados pelo PDV Farmacia para alertas de alergia, interacao medicamentosa e historico.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Alergias Conhecidas</label>
                <textarea value={form.pharmacy?.allergies || ''} onChange={e => updPharma({ allergies: e.target.value })} rows={2} className={inputClass} placeholder="Ex: Penicilina, AAS, Dipirona..." data-testid="client-ph-allergies" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Comorbidades / Condicoes Cronicas</label>
                <textarea value={form.pharmacy?.chronic_conditions || ''} onChange={e => updPharma({ chronic_conditions: e.target.value })} rows={2} className={inputClass} placeholder="Hipertensao, Diabetes, ..." data-testid="client-ph-conditions" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Medicamentos de Uso Continuo</label>
                <textarea value={form.pharmacy?.current_medications || ''} onChange={e => updPharma({ current_medications: e.target.value })} rows={2} className={inputClass} data-testid="client-ph-meds" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Plano de Saude</label>
                <input value={form.pharmacy?.health_plan || ''} onChange={e => updPharma({ health_plan: e.target.value })} className={inputClass} placeholder="Unimed, SUS, etc" data-testid="client-ph-health-plan" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Carteirinha</label>
                <input value={form.pharmacy?.health_plan_card || ''} onChange={e => updPharma({ health_plan_card: e.target.value })} className={inputClass} data-testid="client-ph-card" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Medico de Confianca</label>
                <input value={form.pharmacy?.preferred_prescriber || ''} onChange={e => updPharma({ preferred_prescriber: e.target.value })} className={inputClass} data-testid="client-ph-prescriber" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">CRM do Medico</label>
                <input value={form.pharmacy?.preferred_prescriber_crm || ''} onChange={e => updPharma({ preferred_prescriber_crm: e.target.value })} className={inputClass} placeholder="CRM/UF 000000" data-testid="client-ph-crm" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-500 mb-1">Observacoes Clinicas</label>
                <textarea value={form.pharmacy?.clinical_notes || ''} onChange={e => updPharma({ clinical_notes: e.target.value })} rows={2} className={inputClass} data-testid="client-ph-clinical" />
              </div>
              <label className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer md:col-span-1">
                <input type="checkbox" checked={!!form.pharmacy?.loyalty_program} onChange={e => updPharma({ loyalty_program: e.target.checked })} className="w-4 h-4 accent-amber-600" data-testid="client-ph-loyalty" />
                <span className="text-sm font-bold text-amber-900">Programa Farmacia Popular / Fidelidade</span>
              </label>
              {form.pharmacy?.loyalty_program && (
                <div className="md:col-span-1">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">ID do Programa</label>
                  <input value={form.pharmacy?.loyalty_id || ''} onChange={e => updPharma({ loyalty_id: e.target.value })} className={inputClass} data-testid="client-ph-loyalty-id" />
                </div>
              )}
            </div>
          </div>
        )}
      </FormModal>
      {/* iter77ca: modal de compartilhamento com filiais */}
      {shareTarget && <ClientShareModal client={shareTarget} companies={companies} onClose={() => setShareTarget(null)} onSaved={() => { setShareTarget(null); fetchClients(); }} />}
      <p className="text-[10px] text-gray-400 mb-3 px-1">
        {companyId ? 'Mostrando clientes vinculados a filial ativa. Use o icone de compartilhar para liberar em outras filiais.' : 'Nenhuma filial selecionada — exibindo todos os clientes do tenant.'}
      </p>
      {/* iter156b: refactor para DataTable com colunas customizaveis e paginacao */}
      {(() => {
        const clientColumns = [
          {
            key: 'name',
            label: 'Nome',
            render: c => {
              const nShared = (c.company_ids || []).length;
              return (
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold text-sm text-slate-900 truncate">{c.name}</span>
                  {nShared > 0 ? (
                    <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-cyan-100 text-cyan-800 border border-cyan-300 px-1.5 py-0.5 rounded-full flex-shrink-0" title={`Compartilhado com ${nShared} filial(is)`}>
                      <Share2 size={9} />{nShared}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-slate-100 text-slate-500 border border-slate-300 px-1.5 py-0.5 rounded-full flex-shrink-0" title="Cliente visivel em todas as filiais (legado)">
                      Global
                    </span>
                  )}
                </span>
              );
            },
            sortValue: c => (c.name || '').toLowerCase(),
          },
          {
            key: 'doc',
            label: 'Documento',
            render: c => <span className="text-xs text-slate-700">{c.doc_type?.toUpperCase() || '—'}: {c.document || '—'}</span>,
            sortValue: c => c.document || '',
          },
          {
            key: 'phone',
            label: 'Telefone',
            render: c => <span className="text-xs text-slate-700">{c.phone || '—'}</span>,
            sortValue: c => c.phone || '',
          },
          {
            key: 'email',
            label: 'E-mail',
            render: c => <span className="text-xs text-slate-700 truncate max-w-[220px] inline-block">{c.email || '—'}</span>,
            sortValue: c => c.email || '',
          },
          {
            key: 'city',
            label: 'Cidade / UF',
            render: c => <span className="text-xs text-slate-700">{c.end_municipio || '—'}{c.end_uf ? ` / ${c.end_uf}` : ''}</span>,
            sortValue: c => c.end_municipio || '',
          },
          {
            key: 'person_type',
            label: 'Tipo',
            render: c => <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${c.person_type === 'juridica' ? 'bg-violet-50 text-violet-700' : 'bg-emerald-50 text-emerald-700'}`}>{c.person_type === 'juridica' ? 'PJ' : 'PF'}</span>,
            sortValue: c => c.person_type || 'fisica',
          },
          {
            key: 'birth',
            label: 'Nascimento',
            render: c => <span className="text-xs text-slate-600">{c.birth_date ? fmtDateBR(c.birth_date) : '—'}</span>,
            sortValue: c => c.birth_date || '',
          },
          {
            key: 'shared',
            label: 'Filiais',
            render: c => <span className="text-xs font-bold text-slate-700">{(c.company_ids || []).length || 'Todas'}</span>,
            sortValue: c => (c.company_ids || []).length,
          },
        ];
        const clientDefaultVisible = ['name', 'doc', 'phone', 'city', 'person_type'];
        const clientDefaultOrder = ['name', 'doc', 'phone', 'email', 'city', 'person_type', 'birth', 'shared'];
        const renderClientActions = (c) => (
          <div className="flex items-center gap-1 justify-end">
            <button onClick={(e) => { e.stopPropagation(); setShareTarget(c); }} className="p-2 text-gray-400 hover:text-cyan-600" title="Compartilhar com filiais" data-testid={`share-client-btn-${c.id}`}><Share2 size={14} /></button>
            <button onClick={(e) => { e.stopPropagation(); openEdit(c); }} className="p-2 text-gray-400 hover:text-violet-600" title="Editar" data-testid={`edit-client-btn-${c.id}`}><Edit size={14} /></button>
            <button onClick={async (e) => { e.stopPropagation(); if (!(await confirmDialog({ title: 'Excluir cliente?\n\nSe tiver vendas/financeiro, sera apenas inativado.', confirmLabel: 'Confirmar', danger: true }))) return; try { const { data } = await axios.delete(`${ERP}/clients/${c.id}`, { withCredentials: true }); if (data.deactivated) toast.info(data.message); else toast.success(data.message || 'Excluido'); fetchClients(); } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } }} className="p-2 text-gray-400 hover:text-red-500" title="Excluir" data-testid={`delete-client-btn-${c.id}`}><Trash2 size={14} /></button>
          </div>
        );
        const totalPJ = clients.filter(c => c.person_type === 'juridica').length;
        const totalPF = clients.length - totalPJ;
        return (
          <DataTable
            prefKey="clients"
            columns={clientColumns}
            defaultVisible={clientDefaultVisible}
            defaultOrder={clientDefaultOrder}
            defaultPageSize={25}
            rows={clients}
            keyField="id"
            emptyText="Nenhum cliente cadastrado"
            renderActions={renderClientActions}
            mobilePrimary={(c) => c.name}
            mobileSecondary={(c) => <>{c.doc_type?.toUpperCase()}: {c.document || '—'}{c.phone ? ` · ${c.phone}` : ''}</>}
            totalLabel={`${clients.length} clientes · PF: ${totalPF} · PJ: ${totalPJ}`}
          />
        );
      })()}
    </div>
  );
};

/* ========== SUPPLIERS PAGE (Shared across companies) ========== */
const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', doc_type: 'cnpj', document: '', email: '', phone: '', address: '', contact_person: '', notes: '', company_ids: [], is_transportadora: false, transp_ie: '', transp_uf: '', transp_municipio: '', transp_endereco: '', transp_antt: '' });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // iter77y: lookup CNPJ via BrasilAPI no cadastro de fornecedor
  const [cnpjLookingUp, setCnpjLookingUp] = useState(false);
  const lookupCnpjAndFill = async () => {
    const digits = (form.document || '').replace(/\D/g, '');
    if (digits.length !== 14) { toast.info('Digite um CNPJ com 14 digitos para buscar'); return; }
    setCnpjLookingUp(true);
    try {
      const { data } = await axios.get(`${API}/lookup/cnpj/${digits}`, { withCredentials: true });
      const addr = [data.logradouro, data.numero, data.complemento, data.bairro, data.municipio, data.uf, data.cep].filter(Boolean).join(', ');
      setForm(f => ({
        ...f,
        name: f.name || data.razao_social || data.nome_fantasia,
        document: data.cnpj,
        phone: f.phone || data.telefone || '',
        email: f.email || data.email || '',
        address: f.address || addr,
      }));
      toast.success(`Dados de ${data.razao_social || 'fornecedor'} carregados`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao consultar CNPJ');
    } finally { setCnpjLookingUp(false); }
  };
  const fetchSuppliers = useCallback(async () => {
    try { const { data } = await axios.get(`${ERP}/suppliers${searchTerm ? `?search=${searchTerm}` : ''}`, { withCredentials: true }); setSuppliers(data.data || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, [searchTerm]);
  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);
  const reset = () => { setForm({ name: '', doc_type: 'cnpj', document: '', email: '', phone: '', address: '', contact_person: '', notes: '', company_ids: [], is_transportadora: false, transp_ie: '', transp_uf: '', transp_municipio: '', transp_endereco: '', transp_antt: '' }); setEditing(null); };
  const openNew = () => { reset(); setShowForm(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name || '', doc_type: s.doc_type || 'cnpj', document: s.document || '', email: s.email || '', phone: s.phone || '', address: s.address || '', contact_person: s.contact_person || '', notes: s.notes || '', company_ids: s.company_ids || [], is_transportadora: !!s.is_transportadora, transp_ie: s.transp_ie || '', transp_uf: s.transp_uf || '', transp_municipio: s.transp_municipio || '', transp_endereco: s.transp_endereco || '', transp_antt: s.transp_antt || '' }); setShowForm(true); };
  const handleSave = async () => {
    setSubmitting(true);
    try {
      if (editing) { await axios.put(`${ERP}/suppliers/${editing.id}`, form, { withCredentials: true }); toast.success('Fornecedor atualizado!'); }
      else { await axios.post(`${ERP}/suppliers`, form, { withCredentials: true }); toast.success('Fornecedor criado!'); }
      setShowForm(false); reset(); fetchSuppliers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSubmitting(false); }
  };
  if (loading) return <Loader />;
  return (
    <div data-testid="suppliers-page">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{suppliers.length} fornecedores</p>
        <button onClick={openNew} className={btnPrimary} data-testid="add-supplier-btn"><Plus size={16} /><span>Novo Fornecedor</span></button>
      </div>
      <div className="mb-4"><input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar fornecedor..." className={inputClass} data-testid="supplier-search" /></div>
      <FormModal open={showForm} title={editing ? 'Editar Fornecedor' : 'Novo Fornecedor'} icon={<Building2 size={18} />}
        onClose={() => { setShowForm(false); reset(); }} onSave={handleSave} submitting={submitting}
        saveLabel={editing ? 'Atualizar' : 'Criar'} testId="supplier-modal">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="supplier-form">
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Razao Social *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} required placeholder="Nome da empresa" data-testid="supplier-name" /></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">CNPJ <span className="text-violet-600 font-bold">(autopreenche)</span></label>
            <div className="flex gap-2">
              <input value={form.document} onChange={e => setForm({ ...form, document: e.target.value })} onBlur={lookupCnpjAndFill} className={inputClass} placeholder="00.000.000/0000-00" data-testid="supplier-document" />
              <button type="button" onClick={lookupCnpjAndFill} disabled={cnpjLookingUp || !form.document} className="px-3 py-2 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 disabled:from-slate-300 disabled:to-slate-400 text-white text-xs font-bold shadow-md flex items-center gap-1 flex-shrink-0" title="Buscar dados na Receita Federal" data-testid="supplier-cnpj-lookup">
                {cnpjLookingUp ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
              </button>
            </div>
          </div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Contato</label><input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} className={inputClass} placeholder="Nome do contato" /></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Telefone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputClass} placeholder="(00) 00000-0000" /></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">E-mail</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputClass} /></div>
          <div className="md:col-span-3"><label className="block text-xs font-semibold text-gray-500 mb-1">Endereco</label><input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className={inputClass} placeholder="Endereco completo" /></div>
          <div className="md:col-span-3"><label className="block text-xs font-semibold text-gray-500 mb-1">Observacoes</label><input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={inputClass} /></div>
          <div className="md:col-span-3"><CompaniesPicker value={form.company_ids} onChange={(ids) => setForm({ ...form, company_ids: ids })} testId="supplier-companies-picker" /></div>
          {/* iter77ct.57: flag transportadora + campos opcionais para popular NF-e */}
          <div className="md:col-span-3 border-t border-slate-200 pt-3 mt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.is_transportadora} onChange={e => setForm({ ...form, is_transportadora: e.target.checked })} className="w-4 h-4 accent-violet-600" data-testid="supplier-is-transportadora" />
              <Truck size={14} className="text-violet-600" />
              <span className="text-xs font-bold text-slate-700">Este fornecedor tambem eh TRANSPORTADORA</span>
              <span className="text-[10px] text-slate-400">(fica disponivel na aba Transporte da NF-e)</span>
            </label>
          </div>
          {form.is_transportadora && (
            <>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Inscricao Estadual (transp.)</label><input value={form.transp_ie} onChange={e => setForm({ ...form, transp_ie: e.target.value })} className={inputClass} placeholder="Somente numeros" data-testid="supplier-transp-ie" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">UF (transp.)</label><input value={form.transp_uf} onChange={e => setForm({ ...form, transp_uf: e.target.value.toUpperCase().slice(0, 2) })} className={inputClass} placeholder="GO" maxLength={2} data-testid="supplier-transp-uf" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">RNTC / ANTT</label><input value={form.transp_antt} onChange={e => setForm({ ...form, transp_antt: e.target.value })} className={inputClass} placeholder="Opcional" data-testid="supplier-transp-antt" /></div>
              <div><label className="block text-xs font-semibold text-gray-500 mb-1">Municipio (transp.)</label><input value={form.transp_municipio} onChange={e => setForm({ ...form, transp_municipio: e.target.value })} className={inputClass} placeholder="Se diferente do endereco" data-testid="supplier-transp-municipio" /></div>
              <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Endereco (transp.)</label><input value={form.transp_endereco} onChange={e => setForm({ ...form, transp_endereco: e.target.value })} className={inputClass} placeholder="Se diferente do endereco geral" data-testid="supplier-transp-endereco" /></div>
            </>
          )}
        </div>
      </FormModal>
      <p className="text-[10px] text-gray-400 mb-3 px-1">Fornecedores ficam compartilhados conforme as filiais selecionadas no cadastro</p>
      <div className="space-y-2">
        {suppliers.map(s => (
          <div key={s.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid="supplier-row">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm text-gray-900 flex items-center gap-2">
                {s.name}
                {s.is_transportadora && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200 flex items-center gap-1" data-testid="supplier-transp-badge"><Truck size={10} /> TRANSP.</span>}
              </p>
              <p className="text-xs text-gray-500">CNPJ: {s.document || '-'} {s.contact_person ? `• ${s.contact_person}` : ''} {s.phone ? `• ${s.phone}` : ''}</p>
            </div>
            <div className="flex items-center space-x-1 flex-shrink-0">
              <button onClick={() => openEdit(s)} className="p-2 text-gray-400 hover:text-violet-600" data-testid="edit-supplier-btn"><Edit size={14} /></button>
              <button onClick={async () => { if (!(await confirmDialog({ title: 'Excluir fornecedor?\n\nSe tiver lancamentos financeiros, sera apenas inativado.', confirmLabel: 'Confirmar', danger: true }))) return; try { const { data } = await axios.delete(`${ERP}/suppliers/${s.id}`, { withCredentials: true }); if (data.deactivated) toast.info(data.message); else toast.success(data.message || 'Excluido'); fetchSuppliers(); } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } }} className="p-2 text-gray-400 hover:text-red-500" data-testid="delete-supplier-btn"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {suppliers.length === 0 && <EmptyState icon={Building2} text="Nenhum fornecedor cadastrado" />}
      </div>
    </div>
  );
};

const EmployeesPage = ({ companyId = null, companies = [] }) => {
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'vendedor', phone: '', document: '', email: '', commission_pct: 0, company_ids: [] });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const types = [{ key: 'vendedor', label: 'Vendedor' }, { key: 'caixa', label: 'Caixa' }, { key: 'entregador', label: 'Entregador' }, { key: 'gerente', label: 'Gerente' }, { key: 'outro', label: 'Outro' }];
  const typeColors = { vendedor: 'bg-teal-50 text-teal-700', caixa: 'bg-blue-50 text-blue-700', entregador: 'bg-amber-50 text-amber-700', gerente: 'bg-violet-50 text-violet-700', outro: 'bg-gray-100 text-gray-600' };
  const resetForm = () => { setForm({ name: '', type: 'vendedor', phone: '', document: '', email: '', commission_pct: 0, company_ids: companyId ? [companyId] : [] }); setEditing(null); };
  const fetchEmployees = useCallback(async () => {
    try {
      // iter141: escopo por filial — so exibe funcionarios compartilhados com a filial selecionada (legado sem company_ids = todas)
      const url = companyId ? `${ERP}/employees?company_id=${companyId}` : `${ERP}/employees`;
      const { data } = await axios.get(url, { withCredentials: true });
      setEmployees(data.data || []);
    }
    catch { /* ignore */ } finally { setLoading(false); }
  }, [companyId]);
  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);
  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const payload = { ...form, commission_pct: parseFloat(form.commission_pct) || 0 };
      if (editing) { await axios.put(`${ERP}/employees/${editing.id}`, payload, { withCredentials: true }); toast.success('Funcionario atualizado!'); }
      else { await axios.post(`${ERP}/employees`, payload, { withCredentials: true }); toast.success('Funcionario criado!'); }
      setShowForm(false); resetForm(); fetchEmployees();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setSubmitting(false); }
  };
  const startEdit = (emp) => { setEditing(emp); setForm({ name: emp.name, type: emp.type, phone: emp.phone || '', document: emp.document || '', email: emp.email || '', commission_pct: emp.commission_pct || 0, company_ids: emp.company_ids || [] }); setShowForm(true); };
  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Excluir funcionario?\n\nSe tiver vendas como vendedor, sera apenas inativado.', confirmLabel: 'Confirmar', danger: true }))) return;
    try { const { data } = await axios.delete(`${ERP}/employees/${id}`, { withCredentials: true }); if (data.deactivated) toast.info(data.message); else toast.success(data.message || 'Excluido'); fetchEmployees(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };
  if (loading) return <Loader />;
  return (
    <div data-testid="employees-page">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">{employees.length} funcionarios</p>
        <button onClick={() => { resetForm(); setShowForm(true); }} className={btnPrimary} data-testid="add-employee-btn"><Plus size={16} /><span>Novo Funcionario</span></button>
      </div>
      <FormModal open={showForm} title={editing ? 'Editar Funcionario' : 'Novo Funcionario'} icon={<UserCheck size={18} />}
        onClose={() => { setShowForm(false); resetForm(); }} onSave={() => handleSubmit({ preventDefault: () => {} })} submitting={submitting}
        saveLabel={editing ? 'Atualizar' : 'Criar'} testId="employee-modal">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="employee-form">
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Nome *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} required placeholder="Nome completo" data-testid="employee-name" /></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Tipo *</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className={inputClass} data-testid="employee-type">
              {types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Telefone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputClass} inputMode="tel" data-testid="employee-phone" /></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">CPF/Documento</label><input value={form.document} onChange={e => setForm({ ...form, document: e.target.value })} className={inputClass} /></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">E-mail</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputClass} /></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Comissao (%)</label><input type="number" step="0.1" value={form.commission_pct} onChange={e => setForm({ ...form, commission_pct: e.target.value })} className={inputClass} inputMode="decimal" placeholder="0.0" data-testid="employee-commission" /></div>
          <div className="md:col-span-2"><CompaniesPicker value={form.company_ids} onChange={(ids) => setForm({ ...form, company_ids: ids })} testId="employee-companies-picker" /></div>
        </div>
      </FormModal>
      <div className="space-y-2">
        {employees.map(e => (
          <div key={e.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid="employee-row">
            <div className="flex items-center space-x-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0"><span className="text-sm font-bold text-teal-700">{e.name?.charAt(0) || '?'}</span></div>
              <div className="min-w-0"><p className="font-medium text-sm text-gray-900 truncate">{e.name}</p><p className="text-xs text-gray-500 truncate">{e.phone || e.email || '-'}{e.commission_pct ? ` · ${e.commission_pct}%` : ''}</p></div>
            </div>
            <div className="flex items-center space-x-1.5 flex-shrink-0">
              <span className={`text-[10px] font-semibold px-2 py-1 rounded ${typeColors[e.type] || 'bg-gray-100 text-gray-600'}`}>{(types.find(t => t.key === e.type) || {}).label || e.type}</span>
              <button onClick={() => startEdit(e)} className="p-1.5 text-gray-400 hover:text-violet-600" data-testid="edit-employee-btn"><Edit size={14} /></button>
              <button onClick={() => handleDelete(e.id)} className="p-1.5 text-gray-400 hover:text-red-500" data-testid="delete-employee-btn"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {employees.length === 0 && <EmptyState icon={UserCheck} text="Nenhum funcionario cadastrado" />}
      </div>
    </div>
  );
};

/* ========== PRODUCTS MANAGEMENT (Absorbed from AdminDashboard) ========== */
const ProductsManagement = ({ slug, companyId, productionEnabled, osEnabled }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [shareProduct, setShareProduct] = useState(null);
  const [printProduct, setPrintProduct] = useState(null);
  const [printTemplate, setPrintTemplate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  // iter77ct.37: le a flag `per_branch_pricing_enabled` do tenant para decidir se
  // o Save do ProductForm envia preco para o cadastro global ou como override da filial.
  const [perBranchPricing, setPerBranchPricing] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/erp/company-settings`, { withCredentials: true });
        setPerBranchPricing(!!data?.per_branch_pricing_enabled);
      } catch { /* ignore */ }
    })();
  }, []);
  // iter77x: modal moderno centralizado para confirmacao de delete (substitui window.confirm)
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [priceFilter, setPriceFilter] = useState({ min: '', max: '' });
  const [statusFilter, setStatusFilter] = useState('active'); // active | inactive | all
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [pageLimit, setPageLimit] = useState(50);
  // Reset slice back to 50 whenever the filter/search changes, so the user
  // sees the most relevant matches first and a fresh "Carregar mais" button.
  useEffect(() => { setPageLimit(50); }, [search, filterCat, priceFilter.min, priceFilter.max, statusFilter]);
  const fetchAll = useCallback(async () => {
    try {
      // iter77ct.106 (FIX Bug): backend default limit=5000 truncava listagens de tenants grandes
      // (ex: Ferragista Lideranca com 8000+ produtos aparecia como "5000 produtos"). Elevado para 50000
      // para cobrir os maiores casos reais. `total` retornado pelo backend continua sendo count_documents real.
      const base = `${API}/products?status_filter=${statusFilter}&limit=50000${companyId ? `&company_id=${companyId}` : ''}`;
      const [p, c] = await Promise.all([
        axios.get(base, { withCredentials: true }),
        axios.get(`${API}/categories`, { withCredentials: true })
      ]);
      setProducts(p.data.data || []);
      setCategories(c.data.data || []);
    } catch {} finally { setLoading(false); }
  }, [companyId, statusFilter]);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  const handleDelete = async (id, name = '') => {
    // iter77x: abre modal centralizado em vez de window.confirm
    setConfirmDelete({ id, name });
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    const { id } = confirmDelete;
    setConfirmDelete(null);
    try {
      const { data } = await axios.delete(`${API}/products/${id}`, { withCredentials: true });
      if (data.deactivated) toast.info(data.message || 'Produto inativado (possui movimentos)');
      else toast.success(data.message || 'Excluido');
      fetchAll();
    }
    catch (err) {
      // iter77x: mensagem mais amigavel para 404 (produto ja removido)
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 404) toast.info('Este produto ja foi removido. Atualizando lista...');
      else toast.error(detail || 'Erro ao excluir');
      fetchAll();
    }
  };

  // Computed stats over the FULL product set (not filtered). These reflect the
  // tenant's true inventory snapshot.
  const stats = useMemo(() => {
    let totalStockUnits = 0;
    let totalCost = 0;
    let totalSale = 0;
    let inStockCount = 0;
    products.forEach(p => {
      const stk = Number(p.stock || 0);
      const cost = Number(p.cost_price || 0);
      const sale = Number(p.price || 0);
      totalStockUnits += stk;
      totalCost += stk * cost;
      totalSale += stk * sale;
      if (stk > 0) inStockCount++;
    });
    return {
      total_products: products.length,
      in_stock_count: inStockCount,
      total_stock_units: totalStockUnits,
      total_cost: totalCost,
      total_sale: totalSale,
      potential_profit: totalSale - totalCost
    };
  }, [products]);

  // Filter pipeline: search hits name/sku/barcode/brand; category filter;
  // optional price min/max (uses sale price).
  // iter156fu: busca tolerante — ignora acentos, espacos duplicados e ordem das palavras.
  const normTxt = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
  const filtered = useMemo(() => {
    const q = normTxt(search);
    const tokens = q.split(' ').filter(Boolean);
    return products.filter(p => {
      if (filterCat && p.category_id !== filterCat) return false;
      const price = Number(p.price || 0);
      if (priceFilter.min && price < Number(priceFilter.min)) return false;
      if (priceFilter.max && price > Number(priceFilter.max)) return false;
      if (!q) return true;
      const hay = normTxt(`${p.name || ''} ${p.sku || ''} ${p.barcode || ''} ${p.brand || ''} ${p.reference || ''}`);
      return tokens.every(t => hay.includes(t));
    });
  }, [products, search, filterCat, priceFilter]);

  // iter156fu: sem resultado na filial atual -> procura nas OUTRAS filiais e avisa onde o produto esta
  const [otherBranchHits, setOtherBranchHits] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  useEffect(() => {
    const q = search.trim();
    if (filtered.length > 0 || q.length < 2 || !companyId) { setOtherBranchHits([]); return; }
    const t = setTimeout(async () => {
      try {
        const [{ data }, comps] = await Promise.all([
          axios.get(`${API}/products?search=${encodeURIComponent(q)}&limit=10&status_filter=all`, { withCredentials: true }),
          allCompanies.length ? Promise.resolve({ data: { data: allCompanies } }) : axios.get(`${ERP}/companies`, { withCredentials: true }),
        ]);
        if (!allCompanies.length) setAllCompanies(comps.data.data || []);
        setOtherBranchHits((data.data || []).filter(p => p.id && !products.some(x => x.id === p.id)));
      } catch { setOtherBranchHits([]); }
    }, 400);
    return () => clearTimeout(t);
  }, [search, filtered.length, companyId]); // eslint-disable-line react-hooks/exhaustive-deps
  const branchNames = (p) => {
    const ids = p.company_ids || [];
    if (!ids.length) return 'todas as filiais';
    return ids.map(id => { const c = allCompanies.find(x => x.id === id); return c ? `${c.code ? c.code + ' · ' : ''}${c.name}` : '—'; }).join(', ');
  };

  const fmt = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtInt = (n) => Math.round(Number(n || 0)).toLocaleString('pt-BR');

  if (loading) return <Loader />;

  // iter77by: card modernizado + valores EXATOS (sem abreviação K/M) — visual alinhado ao Dashboard.
  const StatCard = ({ icon: Icon, label, value, sub, gradient, iconBg, testId }) => (
    <div
      className="group relative overflow-hidden bg-white rounded-2xl border border-slate-200/70 shadow-sm hover:shadow-xl hover:shadow-teal-500/10 hover:border-teal-300 hover:-translate-y-0.5 transition-all duration-200 flex-1 basis-[180px] min-w-[180px] max-w-[300px] p-4"
      data-testid={testId || `stat-${String(label).toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r ${gradient}`} />
      <div className={`absolute -right-4 -top-4 w-20 h-20 rounded-full bg-gradient-to-br ${gradient} opacity-[0.06] group-hover:opacity-[0.12] transition-opacity`} />
      <div className="flex items-start justify-between mb-3 mt-1 relative">
        <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider leading-tight pr-2">{label}</span>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${iconBg}`}><Icon size={16} /></div>
      </div>
      <p className="text-lg md:text-xl font-black text-slate-900 tracking-tight break-words leading-tight tabular-nums relative" title={value}>{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-1 truncate relative" title={sub}>{sub}</p>}
    </div>
  );

  return (
    <div data-testid="products-page">
      {/* ===== Top stats (iter77by: layout flex-wrap responsivo + valores completos) ===== */}
      <div className="flex flex-wrap gap-3 md:gap-4 mb-4">
        <StatCard
          icon={Package}
          label="Produtos"
          value={fmtInt(stats.total_products)}
          sub={`${fmtInt(stats.in_stock_count)} com estoque`}
          gradient="from-teal-500 to-cyan-600"
          iconBg="bg-teal-50 text-teal-600"
          testId="stat-produtos"
        />
        <StatCard
          icon={Boxes}
          label="Itens em Estoque"
          value={fmtInt(stats.total_stock_units)}
          sub="unidades"
          gradient="from-blue-500 to-indigo-600"
          iconBg="bg-blue-50 text-blue-600"
          testId="stat-itens-estoque"
        />
        <StatCard
          icon={ArrowDownRight}
          label="Valor de Custo"
          value={`R$ ${fmt(stats.total_cost)}`}
          sub="Total investido em estoque"
          gradient="from-amber-500 to-orange-600"
          iconBg="bg-amber-50 text-amber-600"
          testId="stat-valor-custo"
        />
        <StatCard
          icon={ArrowUpRight}
          label="Valor de Venda"
          value={`R$ ${fmt(stats.total_sale)}`}
          sub={`Lucro potencial: R$ ${fmt(stats.potential_profit)}`}
          gradient="from-emerald-500 to-teal-600"
          iconBg="bg-emerald-50 text-emerald-600"
          testId="stat-valor-venda"
        />
      </div>

      {/* ===== Action bar ===== */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-xs text-gray-500" data-testid="filtered-count">
          {filtered.length === products.length ? `${products.length} produtos` : `${filtered.length} de ${products.length} produtos`}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <ProductImportMenu onRefresh={fetchAll} companyId={companyId} />
          <button onClick={() => { setEditingProduct(null); setShowForm(true); }} className={btnPrimary} data-testid="add-product-btn"><Plus size={16} /><span>Novo Produto</span></button>
        </div>
      </div>

      {/* ===== Slim search + filter toggles ===== */}
      <div className="bg-white border border-gray-200 rounded-xl p-2.5 mb-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nome, SKU, codigo de barras ou marca..."
              className="w-full pl-9 pr-9 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:bg-white"
              data-testid="product-search-input"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label="Limpar busca" data-testid="clear-search-btn">
                <X size={14} />
              </button>
            )}
          </div>
          <button type="button" onClick={() => setShowBarcodeScanner(true)} className="p-2 bg-teal-50 border border-teal-200 text-teal-700 rounded-lg hover:bg-teal-100" title="Escanear codigo de barras" data-testid="scan-product-btn">
            <QrCode size={14} />
          </button>
          <button type="button" onClick={() => setShowFilters(s => !s)} className={`p-2 border rounded-lg ${showFilters || filterCat || priceFilter.min || priceFilter.max || statusFilter !== 'active' ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`} title="Filtros avancados" data-testid="toggle-filters-btn">
            <Filter size={14} />
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2.5 pt-2.5 border-t border-gray-100">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500" data-testid="filter-status">
              <option value="active">Apenas Ativos (padrao)</option>
              <option value="inactive">Apenas Inativos</option>
              <option value="all">Todos (Ativos + Inativos)</option>
            </select>
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500" data-testid="filter-category">
              <option value="">Todas categorias</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">R$ min</span>
              <input type="number" value={priceFilter.min} onChange={e => setPriceFilter(p => ({ ...p, min: e.target.value }))} placeholder="0,00" className="w-full pl-16 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500" data-testid="filter-price-min" />
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">R$ max</span>
              <input type="number" value={priceFilter.max} onChange={e => setPriceFilter(p => ({ ...p, max: e.target.value }))} placeholder="0,00" className="w-full pl-16 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-500" data-testid="filter-price-max" />
            </div>
            {(filterCat || priceFilter.min || priceFilter.max || statusFilter !== 'active') && (
              <button onClick={() => { setFilterCat(''); setPriceFilter({ min: '', max: '' }); setStatusFilter('active'); }} className="sm:col-span-3 text-xs text-teal-600 hover:text-teal-800 self-start font-semibold mt-1">Limpar filtros</button>
            )}
          </div>
        )}
      </div>

      {showForm && <MiniProductForm product={editingProduct} categories={categories} companyId={companyId} perBranchPricing={perBranchPricing} productionEnabled={productionEnabled} osEnabled={osEnabled} onClose={() => { setShowForm(false); setEditingProduct(null); fetchAll(); }} onCreated={(payload) => {
        // iter77v: ao SALVAR um novo produto, NAO faz POST imediato — abre share modal
        // com o payload pendente. Apenas apos confirmar (com 1+ filial selecionada),
        // o backend cria o produto + product_stock rows. Cancelar = nada eh salvo.
        setShowForm(false); setEditingProduct(null);
        setShareProduct({ _pendingPayload: payload, name: payload.name, sku: payload.sku, _auto: true });
      }} />}
      {shareProduct && <ShareProductModal product={shareProduct} defaultCompanyId={companyId} autoOpenedAfterCreate={!!shareProduct._auto} pendingPayload={shareProduct._pendingPayload} onClose={() => setShareProduct(null)} onSaved={fetchAll} />}
      {printProduct && <LabelPrintModal template={{ module: 'produto' }} initialProducts={[printProduct]} onClose={() => setPrintProduct(null)} />}
      {/* iter77x: modal centralizado moderno para confirmacao de exclusao */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="confirm-delete-modal">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-5 py-4 bg-gradient-to-r from-rose-500 to-rose-600 text-white flex items-center gap-3">
              <Trash2 size={22} />
              <h3 className="font-black text-base">Excluir produto?</h3>
            </div>
            <div className="p-5 space-y-3">
              {confirmDelete.name && (
                <p className="text-sm font-bold text-slate-900 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{confirmDelete.name}</p>
              )}
              <p className="text-sm text-slate-600 leading-relaxed">
                Se o produto <strong>nao tiver movimentos</strong> (vendas, compras, entradas, saidas), ele sera <strong className="text-rose-700">excluido permanentemente</strong>.
              </p>
              <p className="text-sm text-slate-600 leading-relaxed">
                Se houver <strong>qualquer movimento</strong>, ele sera apenas <strong className="text-amber-700">INATIVADO</strong> para preservar o historico (voce pode reativa-lo depois pelo cadastro do produto).
              </p>
            </div>
            <div className="bg-slate-50 px-5 py-3 flex items-center justify-end gap-2 border-t border-slate-200">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-200 rounded-lg" data-testid="confirm-delete-cancel">Cancelar</button>
              <button onClick={doDelete} className="px-5 py-2 text-sm font-extrabold text-white bg-rose-600 hover:bg-rose-700 rounded-lg shadow-md flex items-center gap-2" data-testid="confirm-delete-ok"><Trash2 size={14} />Excluir</button>
            </div>
          </div>
        </div>
      )}

      {(() => {
        const fmtBRL = (n) => `R$ ${Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const productColumns = [
          { key: 'image', label: 'Foto', render: r => r.images?.[0]
              ? <img src={r.images[0]} className="w-9 h-9 rounded object-cover" alt="" onError={e => { e.target.style.display = 'none'; }} />
              : <div className="w-9 h-9 rounded bg-slate-100 flex items-center justify-center"><Package size={14} className="text-slate-400" /></div> },
          { key: 'name',     label: 'Nome',      render: r => <span className="text-xs font-medium text-slate-900">{r.name}</span>, sortValue: r => r.name },
          { key: 'sku',      label: 'SKU',       render: r => <span className="font-mono text-xs text-slate-600">{r.sku || '—'}</span>, sortValue: r => r.sku },
          { key: 'barcode',  label: 'Codigo Barras', render: r => <span className="font-mono text-xs text-slate-600">{r.barcode || '—'}</span>, sortValue: r => r.barcode },
          { key: 'category', label: 'Categoria', render: r => <span className="text-xs text-slate-600">{categories.find(c => c.id === r.category_id)?.name || '—'}</span>, sortValue: r => categories.find(c => c.id === r.category_id)?.name },
          { key: 'brand',    label: 'Marca',     render: r => <span className="text-xs text-slate-600">{r.brand || '—'}</span>, sortValue: r => r.brand },
          { key: 'cost',     label: 'Custo',     align: 'right', render: r => <span className="text-xs text-slate-600">{fmtBRL(r.cost_price)}</span>, sortValue: r => r.cost_price || 0 },
          { key: 'price',    label: 'Preco',     align: 'right', render: r => <span className="text-xs font-bold text-slate-900">{fmtBRL(r.price)}</span>, sortValue: r => r.price || 0 },
          { key: 'margin',   label: 'Margem',    align: 'right', render: r => {
              const c = Number(r.cost_price || 0); const p = Number(r.price || 0);
              const m = c > 0 ? ((p - c) / c) * 100 : 0;
              return <span className={`text-xs font-bold ${m >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{m.toFixed(1)}%</span>;
            }, sortValue: r => { const c = Number(r.cost_price || 0); const p = Number(r.price || 0); return c > 0 ? ((p - c) / c) * 100 : 0; } },
          { key: 'stock',    label: 'Estoque',   align: 'right', render: r => (
            <div className="text-right">
              <span className={`text-xs font-bold ${(r.stock || 0) <= 0 ? 'text-rose-600' : 'text-slate-900'}`}>{r.stock || 0}</span>
              {r.has_variants && r.show_variant_stock_in_list && (r.variants || []).length > 0 && (
                <div className="text-[10px] text-slate-500 leading-tight mt-0.5 max-w-[200px] ml-auto" data-testid={`variant-stock-breakdown-${r.id}`}>
                  {(r.variants || []).map(v => `${Object.values(v.options || {}).join('/')}: ${Number((v.stock_by_company && companyId) ? (v.stock_by_company[companyId] || 0) : (v.stock || 0))}`).join(' · ')}
                </div>
              )}
            </div>
          ), sortValue: r => r.stock || 0 },
          { key: 'active',   label: 'Ativo',     render: r => <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${r.active === false ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-700'}`}>{r.active === false ? 'INATIVO' : 'ATIVO'}</span>, sortValue: r => r.active === false ? 0 : 1 },
        ];
        const visDefault = ['image', 'name', 'sku', 'category', 'price', 'stock', 'active'];
        const orderDefault = ['image', 'name', 'sku', 'barcode', 'category', 'brand', 'cost', 'price', 'margin', 'stock', 'active'];
        const renderProductActions = (p) => (
          <div className="flex items-center gap-1 justify-end">
            <button onClick={(e) => { e.stopPropagation(); setPrintProduct(p); }} className="p-1 text-slate-500 hover:text-orange-700 hover:bg-orange-50 rounded" title="Imprimir etiqueta" data-testid="print-label-btn"><Tag size={12} /></button>
            <button onClick={(e) => { e.stopPropagation(); setEditingProduct(p); setShowForm(true); }} className="p-1 text-slate-500 hover:text-violet-700 rounded" title="Editar" data-testid="edit-product-btn"><Edit size={12} /></button>
            <button onClick={(e) => { e.stopPropagation(); setShareProduct(p); }} className="p-1 text-slate-500 hover:text-blue-700 rounded" title="Compartilhar com empresas" data-testid="share-product-btn"><Share2 size={12} /></button>
            <button onClick={(e) => { e.stopPropagation(); handleDelete(p.id, p.name); }} className="p-1 text-rose-600 hover:bg-rose-50 rounded" title="Excluir" data-testid="delete-product-btn"><Trash2 size={12} /></button>
          </div>
        );
        return (
          <>
          {otherBranchHits.length > 0 && (
            <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="products-other-branch-hint">
              <p className="text-xs font-bold text-amber-800 flex items-center gap-1.5"><AlertTriangle size={13} /> Nenhum resultado nesta filial, mas encontramos {otherBranchHits.length} produto(s) em outras filiais:</p>
              <ul className="mt-1.5 space-y-0.5">
                {otherBranchHits.slice(0, 5).map(p => (
                  <li key={p.id} className="text-xs text-amber-900"><span className="font-semibold">{p.name}</span>{p.sku ? ` · ${p.sku}` : ''}{p.active === false ? ' · INATIVO' : ''} — <span className="text-amber-700">vinculado a: {branchNames(p)}</span></li>
                ))}
              </ul>
              <p className="text-[11px] text-amber-700 mt-1.5">Troque a filial no topo da tela ou use "Compartilhar com empresas" para vincular o produto a esta filial.</p>
            </div>
          )}
          <DataTable
            prefKey="products-list"
            columns={productColumns}
            defaultVisible={visDefault}
            defaultOrder={orderDefault}
            rows={filtered}
            keyField="id"
            emptyText={products.length === 0 ? 'Nenhum produto cadastrado' : 'Nenhum produto bate com os filtros'}
            renderActions={renderProductActions}
            mobileBadge={(r) => r.images?.[0] ? <img src={r.images[0]} className="w-9 h-9 rounded object-cover" alt="" /> : <div className="w-9 h-9 rounded bg-slate-100 flex items-center justify-center"><Package size={14} className="text-slate-400" /></div>}
            mobilePrimary={(r) => r.name}
            mobileSecondary={(r) => <>{r.sku || '—'} · {fmtBRL(r.price)} · Est: {r.stock || 0}</>}
            totalLabel={`${filtered.length} produtos`}
          />
          </>
        );
      })()}

      {showBarcodeScanner && (
        <QRScannerModal
          onScan={(text) => { setSearch(text); setShowBarcodeScanner(false); toast.success(`Buscando "${text}"`); }}
          onClose={() => setShowBarcodeScanner(false)}
        />
      )}
    </div>
  );
};

/* ========== EXCEL IMPORT TOOLBAR (shared) ========== */
// Currency input formatted as Brazilian Real while typing. Treats the raw
// digit stream as integer cents (e.g. "456" -> "4,56"). Returns the float
// value (4.56) to the parent through onChange.
const CurrencyInput = ({ value, onChange, placeholder = '', className = '', dataTestId, autoFocus = false, onKeyDown = null, inputRef = null }) => {
  const numToText = (n) => {
    const v = Number(n || 0);
    return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const [text, setText] = useState(numToText(value));
  useEffect(() => {
    // Re-sync display when external value changes (e.g. product loaded from API)
    setText(numToText(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const handle = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) { setText(''); onChange(0); return; }
    const intCents = parseInt(raw, 10);
    const dec = intCents / 100;
    setText(dec.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    onChange(dec);
  };
  return (
    <div className="relative w-full">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">R$</span>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={text}
        onChange={handle}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={`w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 ${className}`}
        data-testid={dataTestId}
      />
    </div>
  );
};

/* ========== ProductImportMenu — Compact dropdown grouping XLSX flows next to "Novo Produto" ========== */
const ProductImportMenu = ({ onRefresh, companyId }) => {
  const { user: _menuUser } = useAuth();
  const isSuperAdmin = (_menuUser?.role || '').toLowerCase() === 'superadmin'; // iter156gk: acoes de correcao so no ambiente Super Admin
  const [open, setOpen] = useState(false);
  const [activeFlow, setActiveFlow] = useState(null); // 'import' | 'update' | null
  const ref = React.useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const downloadCreateTemplate = async () => {
    setOpen(false);
    try {
      const resp = await axios.get(`${API}/products/import/template`, { responseType: 'blob', withCredentials: true });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url; a.download = 'modelo-produtos.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao baixar modelo'); }
  };
  const [repairing, setRepairing] = useState(false);
  const repairStockRows = async () => {
    setRepairing(true);
    try {
      const { data } = await axios.post(`${API}/products/repair-stock-rows`, {}, { withCredentials: true });
      setOpen(false);
      if (data.fixed > 0) {
        toast.success(`${data.fixed} produto(s) com estoque duplicado corrigido(s): ${data.items.slice(0, 3).map(i => `${i.product_name} = ${i.stock}`).join('; ')}${data.fixed > 3 ? '…' : ''}`, { duration: 9000 });
        onRefresh && onRefresh();
      } else toast.info(`Nenhum registro duplicado de estoque encontrado${data.stamped ? ` (${data.stamped} registro(s) legado(s) normalizado(s))` : ''}.`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao corrigir estoque'); }
    finally { setRepairing(false); }
  };
  const repairBranchLinks = async () => {
    setRepairing(true);
    try {
      const { data } = await axios.post(`${API}/products/repair-branch-links`, {}, { withCredentials: true });
      setOpen(false);
      if (data.fixed > 0) {
        toast.success(`${data.fixed} vinculo(s) corrigido(s): ${data.items.slice(0, 3).map(i => `${i.product_name} → ${i.company_name}`).join('; ')}${data.fixed > 3 ? '…' : ''}`, { duration: 8000 });
        onRefresh && onRefresh();
      } else toast.info('Nenhuma inconsistencia encontrada — todos os produtos com estoque ja estao vinculados as suas filiais.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao corrigir vinculos'); }
    finally { setRepairing(false); }
  };
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-xs font-semibold hover:border-blue-400 hover:text-blue-700 transition-colors"
        data-testid="product-import-menu-btn"
      >
        <FileUp size={14} /><span>Acoes</span><ChevronDown size={12} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-30 overflow-hidden" data-testid="product-import-menu-dropdown">
          <button onClick={() => { setOpen(false); setActiveFlow('import'); }} className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-start gap-2.5 border-b border-slate-100" data-testid="menu-import-products">
            <Plus size={16} className="text-blue-600 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-slate-900">Importar produtos</p>
              <p className="text-[10px] text-slate-500">Cadastrar novos a partir do XLSX</p>
            </div>
          </button>
          <button onClick={() => { setOpen(false); setActiveFlow('update'); }} className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-start gap-2.5 border-b border-slate-100" data-testid="menu-update-products">
            <RefreshCw size={16} className="text-violet-600 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-slate-900">Atualizar via planilha</p>
              <p className="text-[10px] text-slate-500">Cadastro ou estoque por loja</p>
            </div>
          </button>
          <button onClick={downloadCreateTemplate} className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-start gap-2.5 border-b border-slate-100" data-testid="menu-download-template">
            <Download size={16} className="text-emerald-600 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-slate-900">Baixar modelo</p>
              <p className="text-[10px] text-slate-500">Planilha .xlsx pre-formatada</p>
            </div>
          </button>
          {/* iter77ct.42: compartilhamento em massa */}
          <button onClick={() => { setOpen(false); setActiveFlow('bulk-share'); }} className="w-full text-left px-3 py-2.5 hover:bg-fuchsia-50 flex items-start gap-2.5 border-b border-slate-100" data-testid="menu-bulk-share">
            <Share2 size={16} className="text-fuchsia-600 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-slate-900">Compartilhar em massa</p>
              <p className="text-[10px] text-slate-500">Compartilha (ou descompartilha) TODOS os produtos com uma filial</p>
            </div>
          </button>
          {isSuperAdmin && (<>
          {/* iter156fx: corrige produto com estoque numa filial em que nao esta vinculado */}
          <button onClick={repairBranchLinks} disabled={repairing} className="w-full text-left px-3 py-2.5 hover:bg-amber-50 flex items-start gap-2.5 disabled:opacity-60" data-testid="menu-repair-branch-links">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-slate-900">{repairing ? 'Verificando...' : 'Corrigir vinculos de filial'}</p>
              <p className="text-[10px] text-slate-500">Vincula a filial em produtos que receberam estoque nela (compra/XML) mas nao aparecem</p>
            </div>
          </button>
          {/* iter156gd: funde rows duplicadas de estoque por filial */}
          <button onClick={repairStockRows} disabled={repairing} className="w-full text-left px-3 py-2.5 hover:bg-red-50 flex items-start gap-2.5 disabled:opacity-60 border-t border-slate-100" data-testid="menu-repair-stock-rows">
            <Boxes size={16} className="text-red-600 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-slate-900">{repairing ? 'Verificando...' : 'Corrigir estoque por filial'}</p>
              <p className="text-[10px] text-slate-500">Funde registros duplicados de estoque (produto x filial) que causam divergencia no rastreio</p>
            </div>
          </button>
          {/* iter156gk: regulariza divergencias do rastreio (estoque atual -> rastreado) */}
          <button onClick={() => { setOpen(false); setActiveFlow('divergences'); }} className="w-full text-left px-3 py-2.5 hover:bg-purple-50 flex items-start gap-2.5 border-t border-slate-100" data-testid="menu-fix-divergences">
            <Activity size={16} className="text-purple-600 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-slate-900">Regularizar divergencias do rastreio</p>
              <p className="text-[10px] text-slate-500">Varre a filial atual, lista produtos cujo estoque difere do rastreio e corrige para o valor rastreado</p>
            </div>
          </button>
          </>)}
        </div>
      )}
      {activeFlow === 'import' && (
        <ImportToolbar
          templateUrl={`${API}/products/import/template`}
          uploadUrl={`${API}/products/import`}
          label="produtos"
          onSuccess={() => { onRefresh && onRefresh(); setActiveFlow(null); }}
          autoOpen
          hideButtons
          onClose={() => setActiveFlow(null)}
          withCompanyPicker
        />
      )}
      {activeFlow === 'update' && (
        <UpdateProductsToolbar autoOpen hideButtons onClose={() => setActiveFlow(null)} onSuccess={() => { onRefresh && onRefresh(); setActiveFlow(null); }} />
      )}
      {/* iter77ct.42: modal de compartilhamento em massa */}
      {activeFlow === 'bulk-share' && (
        <BulkShareProductsModal
          onClose={() => setActiveFlow(null)}
          onDone={() => { onRefresh && onRefresh(); setActiveFlow(null); }}
        />
      )}
      {activeFlow === 'divergences' && (
        <StockDivergencesModal companyId={companyId} onClose={() => setActiveFlow(null)} onDone={() => { onRefresh && onRefresh(); }} />
      )}
    </div>
  );
};

/* ========== iter156gk: DIVERGENCIAS DO RASTREIO (Super Admin) ========== */
const StockDivergencesModal = ({ companyId, onClose, onDone }) => {
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState({ scanned: 0, total: 0 });
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState({});
  const [fixing, setFixing] = useState(false);
  const fmtQ = (n) => Number(n || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
  const scan = async () => {
    setScanning(true); setItems(null); setSelected({});
    const found = [];
    try {
      let offset = 0;
      for (;;) {
        const { data } = await axios.post(`${ERP}/audit-logs/stock-divergences/scan`, { company_id: companyId, offset, limit: 150 }, { withCredentials: true, timeout: 120000 });
        found.push(...(data.items || []));
        setProgress({ scanned: data.next_offset, total: data.total });
        if (data.done) break;
        offset = data.next_offset;
      }
      setItems(found);
      setSelected(Object.fromEntries(found.map(f => [f.product_id, true])));
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao varrer divergencias'); setItems([]); }
    finally { setScanning(false); }
  };
  useEffect(() => { if (companyId) scan(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = (items || []).filter(i => selected[i.product_id]);
  const fix = async () => {
    if (!chosen.length) return;
    if (!window.confirm(`Corrigir o estoque de ${chosen.length} produto(s) para o valor RASTREADO? Sera gravado um Ajuste + Balanco por produto.`)) return;
    setFixing(true);
    try {
      const { data } = await axios.post(`${ERP}/audit-logs/stock-divergences/fix`, { company_id: companyId, items: chosen.map(c => ({ product_id: c.product_id, gap: c.gap })) }, { withCredentials: true, timeout: 180000 });
      toast.success(`${data.fixed} produto(s) regularizado(s)${data.errors?.length ? ` · ${data.errors.length} erro(s)` : ''}`);
      onDone && onDone();
      await scan();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao corrigir'); }
    finally { setFixing(false); }
  };
  return (
    <div className="fixed inset-0 z-[180] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="stock-divergences-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h3 className="text-base font-black text-slate-900">Divergencias do rastreio — filial atual</h3>
            <p className="text-xs text-slate-500 mt-1">Produtos cujo estoque atual difere do saldo reconstruido pelos movimentos. A correcao leva o estoque ao valor rastreado e grava Ajuste + Balanco.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="divergences-close"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-auto p-5">
          {!companyId && <p className="text-center text-sm text-amber-700 font-bold py-10" data-testid="divergences-no-company">Selecione uma filial no topo da tela para varrer as divergencias.</p>}
          {scanning && (
            <div className="text-center py-10">
              <p className="text-sm font-bold text-slate-700">Varrendo produtos… {progress.scanned}/{progress.total}</p>
              <div className="h-1.5 bg-slate-100 rounded-full mt-3 overflow-hidden"><div className="h-full bg-purple-600 transition-all" style={{ width: progress.total ? `${Math.round(progress.scanned / progress.total * 100)}%` : '5%' }} /></div>
            </div>
          )}
          {!scanning && items && items.length === 0 && <p className="text-center text-sm text-emerald-700 font-bold py-10" data-testid="divergences-none">Nenhuma divergencia encontrada nesta filial.</p>}
          {!scanning && items && items.length > 0 && (
            <table className="w-full text-xs">
              <thead><tr className="text-left text-slate-500 border-b"><th className="py-2 w-6"><input type="checkbox" checked={chosen.length === items.length} onChange={e => setSelected(Object.fromEntries(items.map(i => [i.product_id, e.target.checked])))} data-testid="divergences-select-all" /></th><th>Produto</th><th className="text-right">Estoque atual</th><th className="text-right">Rastreado</th><th className="text-right">Diferenca</th></tr></thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.product_id} className="border-b border-slate-100" data-testid={`divergence-row-${i.product_id}`}>
                    <td className="py-2"><input type="checkbox" checked={!!selected[i.product_id]} onChange={e => setSelected({ ...selected, [i.product_id]: e.target.checked })} /></td>
                    <td className="py-2"><p className="font-bold text-slate-900">{i.name}</p><p className="text-[10px] text-slate-500 font-mono">{i.sku}</p></td>
                    <td className="py-2 text-right font-bold text-rose-700">{fmtQ(i.current)}</td>
                    <td className="py-2 text-right font-bold text-emerald-700">{fmtQ(i.tracked)}</td>
                    <td className={`py-2 text-right font-black ${i.gap < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{i.gap > 0 ? '+' : ''}{fmtQ(i.gap)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="p-4 border-t border-slate-100 flex items-center justify-between gap-3">
          <button onClick={scan} disabled={scanning || fixing} className="text-xs font-bold text-slate-600 hover:text-slate-900 disabled:opacity-50" data-testid="divergences-rescan">Varrer novamente</button>
          <button onClick={fix} disabled={scanning || fixing || !chosen.length} className="px-4 py-2 rounded-xl bg-purple-600 text-white text-xs font-black disabled:opacity-50" data-testid="divergences-fix-btn">{fixing ? 'Corrigindo…' : `Corrigir ${chosen.length} selecionado(s) para o rastreado`}</button>
        </div>
      </div>
    </div>
  );
};

/* ========== iter77ct.42: BULK SHARE PRODUCTS MODAL ==========
 * Aplica compartilhamento (ou descompartilhamento) de TODOS os produtos ativos
 * do tenant com uma filial escolhida. Confirmacao dupla ao descompartilhar.
 */
const BulkShareProductsModal = ({ onClose, onDone }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]); // iter77ct.43: multi-select
  const [action, setAction] = useState('share'); // 'share' | 'unshare'
  const [withStock, setWithStock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const CONFIRM_KEY = 'CONFIRMAR';

  useEffect(() => {
    axios.get(`${ERP}/companies`, { withCredentials: true })
      .then(r => setCompanies(r.data.data || []))
      .catch(() => {});
  }, []);

  const toggleCompany = (cid) => setSelectedIds(ids => ids.includes(cid) ? ids.filter(x => x !== cid) : [...ids, cid]);
  const selectAll = () => setSelectedIds(companies.map(c => c.id));
  const clearAll = () => setSelectedIds([]);

  const submit = async () => {
    if (selectedIds.length === 0) { toast.error('Escolha ao menos uma filial'); return; }
    if (action === 'unshare' && confirmText.trim().toUpperCase() !== CONFIRM_KEY) {
      toast.error(`Digite ${CONFIRM_KEY} para confirmar`);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/products/bulk-share`,
        { company_ids: selectedIds, action, with_stock: action === 'share' ? withStock : false },
        { withCredentials: true }
      );
      const n = selectedIds.length;
      const msg = action === 'share'
        ? `${data.stock_rows_created || 0} novos vinculos criados em ${n} filial(is)`
        : `${data.stock_rows_deleted || 0} vinculos removidos de ${n} filial(is)`;
      toast.success(msg);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro no compartilhamento em massa');
    } finally {
      setLoading(false);
    }
  };

  const dangerous = action === 'unshare';

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-share-modal">
      <div className="bg-white w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl shadow-2xl">
        <div className={`sticky top-0 px-4 py-3 flex items-center justify-between ${dangerous ? 'bg-gradient-to-r from-rose-600 to-red-500' : 'bg-gradient-to-r from-fuchsia-600 to-violet-500'}`}>
          <h3 className="text-white font-bold text-sm flex items-center gap-2"><Share2 size={16} /> Compartilhamento em Massa</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 border-l-4 border-slate-400 rounded-lg p-3 text-[11px] text-slate-700">
            <strong>O que essa acao faz?</strong>
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              <li><strong>Compartilhar:</strong> torna TODOS os produtos ativos disponiveis nas filiais escolhidas (sem alterar os produtos ja compartilhados com elas).</li>
              <li><strong>Descompartilhar:</strong> remove as filiais escolhidas de TODOS os produtos, apagando estoques e precos exclusivos delas. Nao afeta as outras filiais.</li>
            </ul>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Acao</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setAction('share')} className={`px-3 py-2.5 rounded-lg text-xs font-bold border-2 transition-colors ${action === 'share' ? 'bg-fuchsia-500 border-fuchsia-500 text-white shadow-md' : 'bg-white border-slate-300 text-slate-600 hover:border-fuchsia-300'}`} data-testid="bulk-share-action-share">
                <Share2 size={14} className="inline mr-1" /> Compartilhar
              </button>
              <button type="button" onClick={() => setAction('unshare')} className={`px-3 py-2.5 rounded-lg text-xs font-bold border-2 transition-colors ${action === 'unshare' ? 'bg-rose-500 border-rose-500 text-white shadow-md' : 'bg-white border-slate-300 text-slate-600 hover:border-rose-300'}`} data-testid="bulk-share-action-unshare">
                <Trash2 size={14} className="inline mr-1" /> Descompartilhar
              </button>
            </div>
          </div>

          {/* iter77ct.43: multi-select de filiais com checkboxes */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase">Filiais ({selectedIds.length} selecionada{selectedIds.length !== 1 ? 's' : ''})</label>
              <div className="flex gap-1 text-[10px]">
                <button type="button" onClick={selectAll} className="px-2 py-0.5 text-blue-600 hover:bg-blue-50 rounded font-bold" data-testid="bulk-share-select-all">Todas</button>
                <button type="button" onClick={clearAll} className="px-2 py-0.5 text-slate-500 hover:bg-slate-100 rounded font-bold" data-testid="bulk-share-clear-all">Limpar</button>
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-slate-100">
              {companies.map(c => {
                const checked = selectedIds.includes(c.id);
                return (
                  <label key={c.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs hover:bg-slate-50 ${checked ? 'bg-fuchsia-50' : ''}`} data-testid={`bulk-share-branch-${c.id}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleCompany(c.id)} className={dangerous ? 'accent-rose-600' : 'accent-fuchsia-600'} />
                    <span className="font-mono text-[10px] font-bold text-slate-500">{c.code}</span>
                    <span className="text-slate-900 font-semibold flex-1 truncate">{c.name}</span>
                    {c.is_matriz && <span className="text-[9px] font-black text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">MATRIZ</span>}
                  </label>
                );
              })}
              {companies.length === 0 && <div className="px-3 py-6 text-center text-[11px] text-slate-400">Nenhuma filial cadastrada.</div>}
            </div>
          </div>

          {action === 'share' && (
            <label className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg cursor-pointer">
              <input type="checkbox" checked={withStock} onChange={e => setWithStock(e.target.checked)} className="mt-0.5 accent-emerald-600" data-testid="bulk-share-with-stock" />
              <div>
                <span className="text-xs font-bold text-slate-800">Criar linha de estoque zerada para cada produto</span>
                <p className="text-[10px] text-slate-500">Assim os produtos aparecem no PDV das filiais imediatamente (com estoque 0). Voce ajusta os saldos depois via inventario ou entrada de estoque.</p>
              </div>
            </label>
          )}

          {dangerous && (
            <div className="bg-rose-50 border-2 border-rose-300 rounded-lg p-3 space-y-2">
              <p className="text-[11px] text-rose-800 font-bold flex items-center gap-1.5"><X size={14} /> Acao destrutiva</p>
              <p className="text-[11px] text-rose-700">Para confirmar, digite <code className="bg-rose-100 px-1.5 py-0.5 rounded font-mono font-bold">{CONFIRM_KEY}</code>:</p>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={CONFIRM_KEY}
                className="w-full px-3 py-2 border-2 border-rose-300 rounded-lg text-sm focus:border-rose-500 focus:outline-none font-mono"
                data-testid="bulk-share-confirm-input"
              />
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold text-slate-500 hover:text-slate-700">Cancelar</button>
          <button
            onClick={submit}
            disabled={loading || selectedIds.length === 0 || (dangerous && confirmText.trim().toUpperCase() !== CONFIRM_KEY)}
            className={`px-4 py-2 text-xs font-bold text-white rounded-lg shadow-md flex items-center gap-1.5 disabled:opacity-50 ${dangerous ? 'bg-rose-600 hover:bg-rose-700' : 'bg-fuchsia-600 hover:bg-fuchsia-700'}`}
            data-testid="bulk-share-submit"
          >
            <Save size={13} /> {loading ? 'Processando...' : `${dangerous ? 'Descompartilhar' : 'Compartilhar'} em ${selectedIds.length || 0} filial(is)`}
          </button>
        </div>
      </div>
    </div>
  );
};



const ImportToolbar = ({ templateUrl, uploadUrl, label, onSuccess, withCompanyPicker = false, autoOpen = false, hideButtons = false, onClose, pickerTitle, pickerHint }) => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState({});
  // iter156cs: modal de detalhes quando ha duplicados/pulados/erros
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const inputRef = React.useRef(null);

  // When mounted via menu (autoOpen), open picker (or file input) immediately
  useEffect(() => {
    if (!autoOpen) return;
    if (withCompanyPicker) setPickerOpen(true);
    else setTimeout(() => inputRef.current?.click(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  useEffect(() => {
    if (!withCompanyPicker || !pickerOpen) return;
    axios.get(`${ERP}/companies`, { withCredentials: true })
      .then(r => {
        const list = r.data.data || [];
        setCompanies(list);
        // Default: pre-select ALL companies (most common case for shared catalog)
        setSelectedCompanies(Object.fromEntries(list.map(c => [c.id, true])));
      })
      .catch(() => {});
  }, [withCompanyPicker, pickerOpen]);

  const downloadTemplate = async () => {
    try {
      const resp = await axios.get(templateUrl, { responseType: 'blob', withCredentials: true });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `modelo-${label}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erro ao baixar modelo'); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      // Append company_ids query param when picker is enabled and the user selected any
      let url = uploadUrl;
      if (withCompanyPicker) {
        const ids = Object.keys(selectedCompanies).filter(k => selectedCompanies[k]);
        if (ids.length) {
          const sep = url.includes('?') ? '&' : '?';
          url = `${url}${sep}company_ids=${ids.join(',')}`;
        }
      }
      const { data } = await axios.post(url, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      const sharedMsg = data.shared_rows ? ` · compartilhado com ${data.shared_companies} loja(s)` : '';
      // iter77ct.34: mostra criados + atualizados no toast
      const updMsg = data.updated ? ` · ${data.updated} atualizado(s)` : '';
      // iter156cs: toast agora inclui dup/skipped/errors quando > 0 pra o usuario NAO
      // achar que "so importou X" quando na verdade a planilha tinha muito mais linhas.
      const dupMsg = data.duplicates ? ` · ${data.duplicates} duplicado(s)` : '';
      const skpMsg = data.skipped ? ` · ${data.skipped} pulado(s)` : '';
      const errMsg = data.errors ? ` · ${data.errors} erro(s)` : '';
      // iter156ct: produtos ja cadastrados que foram compartilhados agora com a filial-alvo
      const linkMsg = data.linked_existing ? ` · ${data.linked_existing} compartilhado(s)` : '';
      toast.success(`${data.created} registro(s) importado(s)${updMsg}${linkMsg}${dupMsg}${skpMsg}${errMsg}${sharedMsg}`);
      // iter156cs: se houve LINHAS que nao entraram, abre modal detalhado
      // com os motivos (primeiros 20 de cada categoria) para o usuario diagnosticar.
      const hasSkipped = (data.duplicates || 0) + (data.skipped || 0) + (data.errors || 0) + (data.linked_existing || 0) > 0;
      if (hasSkipped) {
        setShowDetailsModal(true);
        // NAO fecha o toolbar aqui — usuario precisa ver o modal primeiro
        setPickerOpen(false);
      } else {
        onSuccess && onSuccess();
        setPickerOpen(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao importar');
    } finally { setUploading(false); }
  };

  const handleImportClick = () => {
    if (withCompanyPicker) {
      setPickerOpen(true);
    } else {
      inputRef.current?.click();
    }
  };

  const proceedWithUpload = () => {
    const ids = Object.keys(selectedCompanies).filter(k => selectedCompanies[k]);
    if (companies.length > 0 && !ids.length) {
      toast.info('Selecione ao menos uma loja (ou desmarque a opcao "Compartilhar")');
      return;
    }
    inputRef.current?.click();
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {!hideButtons && (
        <>
          <button type="button" onClick={downloadTemplate} className="flex items-center space-x-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:border-teal-300 hover:text-teal-700 transition-colors" data-testid={`download-${label}-template`}>
            <Download size={14} /><span>Baixar Modelo</span>
          </button>
          <button type="button" onClick={handleImportClick} disabled={uploading} className="flex items-center space-x-1.5 px-3 py-2 bg-teal-50 border border-teal-200 text-teal-700 rounded-lg text-xs font-semibold hover:bg-teal-100 disabled:opacity-50 transition-colors" data-testid={`import-${label}-btn`}>
            <FileUp size={14} /><span>{uploading ? 'Importando...' : 'Importar XLSX'}</span>
          </button>
        </>
      )}
      <input ref={inputRef} type="file" accept=".xlsx,.xlsm" onChange={onFile} className="hidden" />
      {result && (
        <div className="ml-2 text-xs flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1" data-testid={`import-${label}-result`}>
          <span className="text-green-700 font-semibold">{result.created} criados</span>
          {result.updated > 0 && <span className="text-violet-700 font-semibold">{result.updated} atualizados</span>}
          {result.shared_rows > 0 && <span className="text-blue-700">{result.shared_rows} estoques/loja</span>}
          {result.duplicates > 0 && <span className="text-amber-600">{result.duplicates} dup.</span>}
          {result.skipped > 0 && <span className="text-gray-500">{result.skipped} pulados</span>}
          {result.errors > 0 && <span className="text-red-600">{result.errors} erros</span>}
          <button onClick={() => setResult(null)} className="text-gray-400 hover:text-gray-600">x</button>
        </div>
      )}

      {pickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="import-company-picker-modal">
          <div className="bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-3 flex items-center justify-between">
              <h3 className="text-white font-bold text-sm flex items-center gap-2"><Share2 size={16} /> {pickerTitle || 'Compartilhar com lojas'}</h3>
              <button onClick={() => { setPickerOpen(false); if (onClose) onClose(); }} className="text-white/80 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-3 text-[11px] text-slate-700">
                {pickerHint || <>Selecione as lojas onde os produtos importados serao <strong>compartilhados</strong>. O estoque informado na planilha sera replicado em cada loja marcada. Para nao compartilhar, desmarque todas.</>}
              </div>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={() => setSelectedCompanies(Object.fromEntries(companies.map(c => [c.id, true])))} className="text-blue-600 font-semibold">Marcar todas</button>
                <button type="button" onClick={() => setSelectedCompanies({})} className="text-slate-500 font-semibold">Limpar</button>
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="import-companies-list">
                {companies.map(c => (
                  <label key={c.id} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 hover:border-blue-300 cursor-pointer">
                    <input type="checkbox" checked={!!selectedCompanies[c.id]} onChange={e => setSelectedCompanies(s => ({ ...s, [c.id]: e.target.checked }))} className="w-4 h-4 accent-blue-600" data-testid={`import-company-${c.id}`} />
                    <span className="text-xs font-semibold text-slate-900 flex-1">{c.code} · {c.name}</span>
                  </label>
                ))}
                {companies.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Nenhuma empresa cadastrada</p>}
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-2.5 flex justify-end gap-2">
              <button onClick={() => setPickerOpen(false)} className="px-4 py-1.5 text-xs font-semibold text-slate-500">Cancelar</button>
              <button onClick={proceedWithUpload} disabled={uploading} className={btnPrimary} data-testid="import-companies-proceed-btn"><FileUp size={13} /><span>{uploading ? 'Enviando...' : 'Selecionar Planilha'}</span></button>
            </div>
          </div>
        </div>
      )}

      {/* iter156cs: modal de detalhes do resultado quando houve linhas nao importadas */}
      {showDetailsModal && result && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="import-details-modal">
          <div className="bg-white w-full max-w-2xl rounded-t-2xl md:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
            <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-cyan-600 px-4 py-3 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-white font-bold text-sm flex items-center gap-2"><FileUp size={16} /> Resultado da Importacao</h3>
              <button onClick={() => { setShowDetailsModal(false); onSuccess && onSuccess(); }} className="text-white/80 hover:text-white" data-testid="import-details-close-btn"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-2xl font-black text-emerald-700">{result.created || 0}</p>
                  <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Criados</p>
                </div>
                {result.linked_existing > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-2xl font-black text-blue-700">{result.linked_existing}</p>
                    <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Compartilhados</p>
                  </div>
                )}
                {result.updated > 0 && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <p className="text-2xl font-black text-violet-700">{result.updated}</p>
                    <p className="text-[10px] font-bold text-violet-600 uppercase tracking-wider">Atualizados</p>
                  </div>
                )}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-2xl font-black text-amber-700">{result.duplicates || 0}</p>
                  <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Duplicados</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-2xl font-black text-slate-700">{result.skipped || 0}</p>
                  <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Pulados</p>
                </div>
                {result.errors > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-2xl font-black text-red-700">{result.errors}</p>
                    <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider">Erros</p>
                  </div>
                )}
              </div>
              {Array.isArray(result.linked_existing_list) && result.linked_existing_list.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider mb-1">Compartilhados com a filial (primeiros {result.linked_existing_list.length})</p>
                  <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-2 text-[11px] text-slate-700 max-h-40 overflow-y-auto font-mono">
                    {result.linked_existing_list.map((l, i) => <div key={i} className="py-0.5">{l}</div>)}
                  </div>
                </div>
              )}
              {Array.isArray(result.duplicates_list) && result.duplicates_list.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1">Duplicados (primeiros {result.duplicates_list.length})</p>
                  <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-2 text-[11px] text-slate-700 max-h-40 overflow-y-auto font-mono">
                    {result.duplicates_list.map((l, i) => <div key={i} className="py-0.5">{l}</div>)}
                  </div>
                </div>
              )}
              {Array.isArray(result.skipped_list) && result.skipped_list.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Pulados (primeiros {result.skipped_list.length})</p>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 text-[11px] text-slate-700 max-h-40 overflow-y-auto font-mono">
                    {result.skipped_list.map((l, i) => <div key={i} className="py-0.5">{l}</div>)}
                  </div>
                </div>
              )}
              {Array.isArray(result.errors_list) && result.errors_list.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider mb-1">Erros (primeiros {result.errors_list.length})</p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-[11px] text-slate-700 max-h-40 overflow-y-auto font-mono">
                    {result.errors_list.map((l, i) => <div key={i} className="py-0.5">{l}</div>)}
                  </div>
                </div>
              )}
              <div className="text-[11px] text-slate-500 leading-relaxed bg-slate-50 rounded-lg p-2.5">
                <strong>Dicas:</strong> Duplicados sao produtos com <strong>SKU+Nome</strong> ou <strong>Codigo de Barras</strong> ja existentes na filial. <strong>Compartilhados</strong> sao produtos que ja estavam cadastrados no tenant mas nao estavam vinculados a filial selecionada — agora foram vinculados automaticamente. Ajuste a planilha e reimporte apenas as linhas restantes se necessario.
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-2.5 flex justify-end">
              <button onClick={() => { setShowDetailsModal(false); onSuccess && onSuccess(); }} className={btnPrimary} data-testid="import-details-ok-btn"><CheckCircle size={13} /><span>Entendi</span></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ========== UpdateProductsToolbar — XLSX cadastro update + per-company stock update ========== */
const UpdateProductsToolbar = ({ onSuccess, autoOpen = false, hideButtons = false, onClose }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('cadastro'); // cadastro | stock
  const [companies, setCompanies] = useState([]);
  const [stockCompanyId, setStockCompanyId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = React.useRef(null);

  useEffect(() => { if (autoOpen) setOpen(true); }, [autoOpen]);

  const closeModal = () => { setOpen(false); if (onClose) onClose(); };

  useEffect(() => {
    if (!open) return;
    axios.get(`${ERP}/companies`, { withCredentials: true })
      .then(r => { const c = r.data.data || []; setCompanies(c); if (c[0]) setStockCompanyId(c[0].id); })
      .catch(() => {});
  }, [open]);

  const downloadTemplate = async () => {
    try {
      const resp = await axios.get(`${API}/products/import/template`, { responseType: 'blob', withCredentials: true });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url;
      a.download = mode === 'stock' ? 'modelo-atualizacao-estoque.xlsx' : 'modelo-atualizacao-produtos.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao baixar modelo'); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    if (mode === 'stock' && !stockCompanyId) { toast.error('Selecione a empresa'); return; }
    setUploading(true); setResult(null);
    try {
      const fd = new FormData(); fd.append('file', f);
      const url = mode === 'cadastro'
        ? `${API}/products/update-import`
        : `${API}/products/stock-import?company_id=${stockCompanyId}`;
      const { data } = await axios.post(url, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      toast.success(`${data.updated} produtos atualizados${data.not_found ? `, ${data.not_found} nao encontrados` : ''}`);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar');
    } finally { setUploading(false); }
  };

  return (
    <>
      {!hideButtons && (
        <button onClick={() => { setOpen(true); setResult(null); }} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-blue-200 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-50 transition-colors" data-testid="open-update-toolbar">
          <RefreshCw size={13} /><span>Atualizar via Planilha</span>
        </button>
      )}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="update-products-modal">
          <div className="bg-white w-full max-w-xl rounded-t-2xl md:rounded-2xl shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-3 flex items-center justify-between">
              <h3 className="text-white font-bold text-sm flex items-center gap-2"><RefreshCw size={16} /> Atualizar Produtos via Planilha</h3>
              <button onClick={closeModal} className="text-white/80 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setMode('cadastro')} className={`p-3 rounded-xl text-sm font-bold border-2 transition-all ${mode === 'cadastro' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-blue-300'}`} data-testid="update-mode-cadastro">
                  <Edit size={16} className="inline mr-1" /> Cadastro
                  <p className="text-[10px] font-normal mt-1">Preco, custo, barcode, marca etc.</p>
                </button>
                <button onClick={() => setMode('stock')} className={`p-3 rounded-xl text-sm font-bold border-2 transition-all ${mode === 'stock' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-blue-300'}`} data-testid="update-mode-stock">
                  <Boxes size={16} className="inline mr-1" /> Estoque por Loja
                  <p className="text-[10px] font-normal mt-1">Quantidade por empresa (filial).</p>
                </button>
              </div>
              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-3 text-[11px] text-slate-700">
                <p className="font-bold text-amber-700 mb-1 flex items-center gap-1"><AlertTriangle size={12} /> Como funciona</p>
                {mode === 'cadastro' ? (
                  <p>O sistema identifica o produto pela coluna <strong>Nome</strong> (exato, case-insensitive) OU <strong>SKU</strong> e atualiza os demais campos. <strong>O estoque NAO e alterado</strong>. Para estoque, use a aba Estoque por Loja.</p>
                ) : (
                  <p>Atualiza apenas a coluna <strong>Estoque</strong> da empresa selecionada abaixo. O produto continua compartilhado entre empresas, mas cada loja tem seu proprio saldo.</p>
                )}
              </div>
              {mode === 'stock' && (
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-0.5">Empresa de destino</label>
                  <select value={stockCompanyId} onChange={e => setStockCompanyId(e.target.value)} className={inputClass} data-testid="stock-import-company">
                    {companies.map(c => <option key={c.id} value={c.id}>{c.code} | {c.name}</option>)}
                  </select>
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={downloadTemplate} className="btn-pill-outline" data-testid="download-update-template"><Download size={13} /><span>Baixar Modelo</span></button>
                <input ref={inputRef} type="file" accept=".xlsx" onChange={onFile} className="hidden" />
                <button onClick={() => inputRef.current?.click()} disabled={uploading} className={btnPrimary} data-testid="upload-update-xlsx"><FileUp size={13} /><span>{uploading ? 'Enviando...' : 'Selecionar Planilha'}</span></button>
              </div>
              {result && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1" data-testid="update-result">
                  <p><strong>{result.updated}</strong> atualizados {result.unchanged ? `· ${result.unchanged} sem mudancas` : ''}</p>
                  {result.not_found > 0 && <p className="text-amber-700"><strong>{result.not_found}</strong> nao encontrados (Nome/SKU sem match)</p>}
                  {result.errors > 0 && <p className="text-rose-700"><strong>{result.errors}</strong> erros</p>}
                  {(result.not_found_list || []).slice(0, 5).map((m, i) => <p key={i} className="text-[10px] text-slate-500">• {m}</p>)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};


/* ========== ShareProductModal — replicate cadastro stock rows across companies ========== */
const ShareProductModal = ({ product, defaultCompanyId, autoOpenedAfterCreate, pendingPayload, onClose, onSaved }) => {
  const [companies, setCompanies] = useState([]);
  const [existing, setExisting] = useState([]);
  const [sharedAll, setSharedAll] = useState(false); // iter77ct.45: modo shared-with-all
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // iter77ct.30: preços editáveis por filial (só válido no modo "produto já existe")
  const [branchPrices, setBranchPrices] = useState({});  // company_id -> {price, cost_price}
  const [priceSaving, setPriceSaving] = useState({});
  const [unsharing, setUnsharing] = useState({});
  // iter156ek: state de produtos IRMAOS (duplicados por SKU/barcode).
  // Quando o tenant tem cadastros duplicados (mesmo produto criado 1x por filial),
  // o modal detecta e mostra aviso com opcao de MERGE.
  const [siblingsInfo, setSiblingsInfo] = useState({ has_siblings: false, siblings: [], product_sku: '' });
  const [mergeSiblings, setMergeSiblings] = useState(false);
  const isPending = !!pendingPayload;
  const refresh = async () => {
    // iter156e FIX: os 2 requests sao INDEPENDENTES. Antes usavamos Promise.all e se
    // /products/{id}/companies retornasse 404 (produto de outro tenant, produto deletado
    // ou drift de dados), o Promise.all rejeitava e o catch silenciava tudo -> companies=[]
    // e o modal mostrava "Nenhuma filial cadastrada". Agora buscamos separado.
    let cs = [];
    try {
      const { data } = await axios.get(`${ERP}/companies`, { withCredentials: true });
      // iter144: filtra filiais INATIVAS do modal de compartilhamento — nao faz sentido
      // compartilhar produto com filial desativada. Estas continuam aparecendo em relatorios
      // historicos, mas nao devem ser opcoes para novos compartilhamentos.
      cs = (data.data || []).filter(c => c.active !== false);
    } catch (_e) {
      cs = [];
    }
    let existingRows = [];
    let sharedAllFlag = false;
    let sibs = { has_siblings: false, siblings: [], product_sku: '' };
    if (!isPending && product?.id) {
      try {
        const { data } = await axios.get(`${API}/products/${product.id}/companies`, { withCredentials: true });
        existingRows = data.data || [];
        sharedAllFlag = !!data.shared_all;
        sibs = {
          has_siblings: !!data.has_siblings,
          siblings: data.siblings || [],
          product_sku: data.product_sku || '',
          product_name: data.product_name || '',
        };
      } catch (_e) {
        // 404 ou erro: assume "sem compartilhamento" — o modal ainda funciona para o usuario
        // (re)configurar os checkboxes normalmente.
        existingRows = [];
        sharedAllFlag = false;
      }
    }
    setSiblingsInfo(sibs);
    // Se tem irmaos detectados, ligar o merge por default (usuario pode desligar)
    setMergeSiblings(sibs.has_siblings);
    // iter156br: BUG fix — quando o usuario tem `allowed_company_ids` restrito, `/companies`
    // devolvia apenas a filial dele, e o modal escondia as outras onde o produto ja estava
    // compartilhado. Agora unifica `cs` com as filiais vindas de existingRows (que a rota
    // /products/{id}/companies retorna sem filtro). Assim o usuario visualiza TUDO onde o
    // produto esta compartilhado, mesmo as filiais fora do seu escopo.
    const seenCids = new Set(cs.map(c => c.id));
    existingRows.forEach(r => {
      if (!seenCids.has(r.company_id)) {
        cs.push({
          id: r.company_id,
          name: r.company_name || '(Sem nome)',
          code: r.company_code || '',
          active: true,
          _read_only_scope: true, // marca visual — a filial esta fora do escopo do usuario
        });
        seenCids.add(r.company_id);
      }
    });
    cs.sort((a, b) => (a.code || '').localeCompare(b.code || '') || (a.name || '').localeCompare(b.name || ''));
    setCompanies(cs);
    setExisting(existingRows);
    setSharedAll(sharedAllFlag);
    // iter77ct.30: seed local branchPrices state
    const seed = {};
    existingRows.forEach(r => {
      seed[r.company_id] = {
        price: r.price != null ? String(r.price) : '',
        cost_price: r.cost_price != null ? String(r.cost_price) : '',
      };
    });
    setBranchPrices(seed);
    // iter144c: seed simples do estado de checkboxes — TODAS as filiais em que o produto ja esta
    // compartilhado (existing rows OU sharedAll implicito) ficam marcadas. Sem isso o usuario nao ve
    // onde o produto ja esta e passa impressao errada. Apenas para produtos ja criados; no fluxo
    // pending (criacao) mantem o comportamento antigo (apenas defaultCompanyId marcada).
    if (isPending) {
      if (defaultCompanyId) setSelected({ [defaultCompanyId]: true });
    } else if (sharedAllFlag) {
      // Compartilhado com TODAS -> marca todas as filiais visiveis
      const initial = {};
      cs.forEach(c => { initial[c.id] = true; });
      setSelected(initial);
    } else {
      // Marca apenas as filiais que ja tem row product_stock
      const initial = {};
      existingRows.forEach(r => { initial[r.company_id] = true; });
      // Garante que a filial atual do usuario tambem apareca marcada como base
      if (defaultCompanyId && !existingRows.some(e => e.company_id === defaultCompanyId)) {
        initial[defaultCompanyId] = true;
      }
      setSelected(initial);
    }
    setLoading(false);
  };
  useEffect(() => { setLoading(true); refresh(); /* eslint-disable-next-line */ }, [product?.id, defaultCompanyId, isPending]);
  const existingIds = new Set(existing.map(e => e.company_id));
  // iter144c: layout SIMPLES — a lista renderizada eh sempre `companies` (todas as filiais visiveis
  // ao usuario). Cada filial e um unico checkbox. Checkboxes marcados no seed inicial refletem o
  // estado atual de compartilhamento (existing OU sharedAll). Save aplica o diff no submit.
  const allSelected = companies.length > 0 && companies.every(c => selected[c.id]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else {
      const next = {};
      companies.forEach(c => { next[c.id] = true; });
      setSelected(next);
    }
  };
  const submit = async () => {
    const ids = Object.keys(selected).filter(id => selected[id]);
    if (!ids.length) { toast.info('Selecione ao menos uma filial'); return; }
    // iter156eg: TRAVA de seguranca — se o usuario esta DESMARCANDO uma filial que
    // tem estoque > 0, pede confirmacao explicita listando essas filiais.
    // Evita erro humano do tipo "descompartilhar sem querer e perder o estoque".
    // Backend tambem tem validacao com force=true, mas UX melhor eh avisar aqui.
    if (!isPending) {
      const selectedSet = new Set(ids);
      const branchesWithStock = existing.filter(r =>
        !selectedSet.has(r.company_id) && Number(r.stock || 0) > 0
      );
      if (branchesWithStock.length > 0) {
        const lista = branchesWithStock
          .map(b => `- ${b.company_code || ''} ${b.company_name || ''} (estoque: ${Number(b.stock).toLocaleString('pt-BR')})`.trim())
          .join('\n');
        const ok = await confirmDialog({
          title: `Descompartilhar filiais com estoque?\n\nAs filiais abaixo tem estoque deste produto e serao desvinculadas. O estoque ficara zerado nelas ate um novo compartilhamento:\n\n${lista}\n\nDeseja compartilhar assim mesmo?`,
          confirmLabel: 'Sim, descompartilhar',
          danger: true,
        });
        if (!ok) return;
      }
    }
    setSaving(true);
    try {
      if (isPending) {
        const { data } = await axios.post(`${API}/products`, { ...pendingPayload, company_ids: ids }, { withCredentials: true });
        // iter156ec: backend reutiliza produto existente quando SKU/barcode ja existe (auto-share).
        // Avisa claramente — antes dizia "cadastrado" e o usuario nao achava o item pelo nome novo.
        if (data?.auto_shared) {
          const info = data.auto_share_info || {};
          const nomeExist = info.existing_name || data.name;
          const partes = [`O SKU "${info.existing_sku || data.sku}" ja existia no produto "${nomeExist}".`];
          partes.push(info.shared_company_ids?.length ? 'Ele foi vinculado a esta filial (nenhum produto novo foi criado).' : 'Ele ja estava vinculado a esta filial (nenhum produto novo foi criado).');
          if (info.reactivated) partes.push('O produto estava INATIVO e foi reativado.');
          if (info.name_differs) partes.push(`Busque por "${nomeExist}" para encontra-lo, ou edite o nome do produto.`);
          toast.warning(partes.join(' '), { duration: 12000 });
        } else {
          toast.success(`Produto cadastrado em ${ids.length} filial(is)`);
        }
      } else {
        // iter156d: usa o novo endpoint SET-SHARES que faz sync completo (full replace).
        // iter156eg: envia force=true — o usuario ja confirmou o descompartilhamento
        // de filiais com estoque no dialogo acima.
        // iter156ek: envia merge_siblings — se true, o backend une produtos duplicados
        // (mesmo SKU/barcode em outros _id) neste produto, transferindo estoque.
        const { data } = await axios.post(
          `${API}/products/${product.id}/set-shares`,
          { company_ids: ids, initial_stock: 0, force: true, merge_siblings: mergeSiblings },
          { withCredentials: true }
        );
        const created = data?.created || 0;
        const removed = data?.stock_deleted || 0;
        const isSharedAll = !!data?.shared_all;
        const merged = data?.siblings_merged || 0;
        const parts = [];
        if (created > 0) parts.push(`${created} adicionada(s)`);
        if (removed > 0) parts.push(`${removed} removida(s)`);
        if (merged > 0) parts.push(`${merged} produto(s) irmao(s) unidos`);
        if (isSharedAll) parts.push('compartilhado com TODAS as filiais');
        if (parts.length === 0) parts.push('sem alteracoes');
        toast.success(`Compartilhamento atualizado: ${parts.join(', ')}`);
      }
      onSaved && onSaved(); onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend pode retornar 409 com { detail: { code: 'branches_with_stock', branches: [...] } }
      // quando force=false. Nao deveria acontecer no fluxo normal (frontend ja passa force=true),
      // mas se acontecer, mostra a lista amigavel.
      if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'branches_with_stock') {
        const lista = (detail.branches || [])
          .map(b => `- ${b.company_code || ''} ${b.company_name || ''} (estoque: ${Number(b.stock).toLocaleString('pt-BR')})`.trim())
          .join('\n');
        toast.error(`Descompartilhamento bloqueado (estoque > 0):\n${lista}`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Erro ao salvar');
      }
    }
    finally { setSaving(false); }
  };
  // iter77ct.30 + iter77ct.36: salva preco especifico daquela filial.
  // Se ambos os campos vazios -> REMOVE o override (volta a herdar do cadastro global).
  const savePrice = async (cid) => {
    const bp = branchPrices[cid] || {};
    setPriceSaving(s => ({ ...s, [cid]: true }));
    try {
      const payload = { company_id: cid };
      const p = parseFloat(String(bp.price || '').replace(',', '.'));
      const cp = parseFloat(String(bp.cost_price || '').replace(',', '.'));
      if (bp.price !== '' && bp.price != null && isFinite(p)) payload.price = p;
      if (bp.cost_price !== '' && bp.cost_price != null && isFinite(cp)) payload.cost_price = cp;
      const { data } = await axios.put(`${API}/products/${product.id}/branch-price`, payload, { withCredentials: true });
      if (data.removed) {
        toast.success('Preco exclusivo removido — filial volta a herdar do cadastro global');
      } else {
        toast.success(`Preco exclusivo desta filial: R$ ${Number(payload.price ?? 0).toFixed(2)}`);
      }
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setPriceSaving(s => ({ ...s, [cid]: false })); }
  };
  // iter77ct.30: descompartilha uma filial especifica
  const doUnshare = async (cid, cname) => {
    if (!confirm(`Descompartilhar "${product.name}" da filial ${cname}?\n\nO produto nao aparecera mais no PDV/estoque desta filial.`)) return;
    setUnsharing(s => ({ ...s, [cid]: true }));
    try {
      await axios.post(`${API}/products/${product.id}/unshare`, { company_id: cid }, { withCredentials: true });
      toast.success('Filial descompartilhada');
      await refresh();
      onSaved && onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setUnsharing(s => ({ ...s, [cid]: false })); }
  };
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const handleCancel = () => {
    if (isPending && !saving) setShowCancelConfirm(true);
    else onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="share-product-modal">
      <div className="bg-white w-full max-w-lg rounded-t-2xl md:rounded-2xl shadow-2xl max-h-[95vh] flex flex-col">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-3 flex items-center justify-between">
          <h3 className="text-white font-bold text-sm flex items-center gap-2"><Share2 size={16} /> {isPending ? 'Em quais filiais este produto fica disponivel?' : (autoOpenedAfterCreate ? 'Em qual filial este produto fica disponivel?' : 'Compartilhamento & Preco por Filial')}</h3>
          <button onClick={handleCancel} className="text-white/80 hover:text-white" data-testid="share-modal-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <p className="text-xs text-slate-600"><strong>{product.name}</strong>{product.sku ? ` (${product.sku})` : ''}</p>
          <div className={`${isPending || autoOpenedAfterCreate ? 'bg-violet-50 border-violet-500 text-violet-900' : 'bg-blue-50 border-blue-500 text-slate-700'} border-l-4 rounded-lg p-3 text-[11px]`}>
            {isPending
              ? <>Marque as filiais onde este produto deve aparecer. Por padrao ja vem marcada a <strong>sua filial atual</strong>. O produto so sera cadastrado apos voce clicar em <strong>Confirmar</strong>.</>
              : autoOpenedAfterCreate
                ? <>Por padrao o produto comeca disponivel apenas na <strong>sua filial atual</strong>. Marque <strong>Todas</strong> ou as filiais especificas onde ele deve aparecer.</>
                : <>Marque as filiais onde este produto deve aparecer. Desmarque para remover o compartilhamento. Clique em <strong>Salvar</strong> para aplicar.</>
            }
          </div>
          {!loading && companies.length > 0 && !isPending && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">{Object.values(selected).filter(Boolean).length} de {companies.length} filial(is) selecionada(s)</span>
              <button onClick={toggleAll} type="button" className="text-[11px] font-black text-violet-700 hover:text-violet-900 underline decoration-dotted" data-testid="share-toggle-all">
                {allSelected ? 'Limpar' : 'Todas'}
              </button>
            </div>
          )}
          {/* iter156ek: banner de PRODUTOS IRMAOS detectados por SKU/barcode.
              Explica ao usuario que existem duplicatas e oferece opcao de UNIFICAR
              (transfere estoques e deleta os irmaos, mantendo apenas este produto). */}
          {!loading && !isPending && siblingsInfo?.has_siblings && (
            <div className="p-3 rounded-lg border-2 border-amber-400 bg-amber-50" data-testid="share-siblings-banner">
              <div className="flex items-start gap-2">
                <div className="text-amber-600 mt-0.5">
                  <AlertTriangle size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-black text-amber-900 mb-1">
                    Detectados {siblingsInfo.siblings.length} produto(s) duplicado(s) com o mesmo SKU
                    {siblingsInfo.product_sku ? ` (${siblingsInfo.product_sku})` : ''}
                  </p>
                  <p className="text-[11px] text-amber-800 mb-2">
                    Como o SKU eh o mesmo, sao <strong>o mesmo produto</strong> cadastrado varias vezes (1x por filial).
                    As filiais onde os irmaos existem ja aparecem <strong>marcadas</strong> abaixo. Ao salvar com &quot;Unificar irmaos&quot;,
                    o sistema <strong>transfere o estoque de cada filial</strong> para este produto e apaga os irmaos. Cada filial mantem
                    seu estoque proprio (nao zera).
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer text-[11px] font-bold text-amber-900">
                    <input
                      type="checkbox"
                      checked={mergeSiblings}
                      onChange={e => setMergeSiblings(e.target.checked)}
                      className="w-4 h-4 accent-amber-600 cursor-pointer"
                      data-testid="share-merge-siblings-toggle"
                    />
                    Unificar irmaos ao salvar (recomendado)
                  </label>
                </div>
              </div>
            </div>
          )}
          {!loading && isPending && companies.length > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">{Object.values(selected).filter(Boolean).length} de {companies.length} nova(s) selecionada(s)</span>
              <button onClick={toggleAll} type="button" className="text-[11px] font-black text-violet-700 hover:text-violet-900 underline decoration-dotted" data-testid="share-toggle-all">
                {allSelected ? 'Limpar' : 'Todas'}
              </button>
            </div>
          )}
          {loading ? <Loader /> : (
            <div className="space-y-1.5 max-h-[50vh] overflow-y-auto" data-testid="share-companies-list">
              {/* iter144c: layout SIMPLIFICADO — 1 checkbox por filial. Marcado = compartilhado.
                  Save aplica o diff. Preco/custo individual e Estoque sao tratados no cadastro do produto. */}
              {companies.map(c => {
                const isDefault = c.id === defaultCompanyId;
                const isChecked = !!selected[c.id];
                const isReadOnlyScope = !!c._read_only_scope; // iter156br: filial fora do escopo do usuario
                return (
                  <label key={c.id} className={`flex items-center gap-2 p-2 rounded-lg border transition-colors cursor-pointer ${isChecked ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200 hover:border-blue-300'} ${isDefault ? 'ring-1 ring-violet-300' : ''} ${isReadOnlyScope ? 'opacity-90' : ''}`}>
                    <input type="checkbox" checked={isChecked} onChange={e => setSelected(s => ({ ...s, [c.id]: e.target.checked }))} className="w-4 h-4 accent-emerald-600 cursor-pointer" data-testid={`share-company-${c.id}`} />
                    <span className="text-xs font-semibold text-slate-900 flex-1">
                      {c.code} · {c.name}
                      {c.is_matriz && <span className="ml-1 text-[9px] font-extrabold text-violet-700">(MATRIZ)</span>}
                      {isDefault && <span className="ml-1 text-[9px] font-extrabold text-violet-700">(SUA FILIAL)</span>}
                      {isReadOnlyScope && <span className="ml-1 text-[9px] font-extrabold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title="Filial fora do seu escopo de acesso — voce pode ver mas nao acessar rotinas dela">(FORA DO ESCOPO)</span>}
                    </span>
                  </label>
                );
              })}
              {companies.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Nenhuma filial cadastrada</p>}
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-2.5 flex justify-end gap-2">
          <button onClick={handleCancel} className="px-4 py-1.5 text-xs font-semibold text-slate-500" data-testid="share-skip-btn">{isPending ? 'Cancelar' : (autoOpenedAfterCreate ? 'Pular' : 'Fechar')}</button>
          <button onClick={submit} disabled={saving || Object.values(selected).filter(Boolean).length === 0} className={btnPrimary} data-testid="share-submit-btn"><Share2 size={13} /><span>{saving ? (isPending ? 'Cadastrando...' : 'Salvando...') : (isPending ? 'Confirmar e Cadastrar' : 'Salvar')}</span></button>
        </div>
      </div>
      {showCancelConfirm && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cancel-create-confirm">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center gap-3">
              <X size={22} />
              <h3 className="font-black text-base">Cancelar cadastro?</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-slate-700 leading-relaxed">Voce esta a um passo de salvar este produto. Se cancelar agora, <strong className="text-amber-700">o produto NAO sera cadastrado</strong> e voce voltara a lista.</p>
            </div>
            <div className="bg-slate-50 px-5 py-3 flex items-center justify-end gap-2 border-t border-slate-200">
              <button onClick={() => setShowCancelConfirm(false)} className="px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-200 rounded-lg" data-testid="cancel-confirm-back">Voltar a editar</button>
              <button onClick={() => { setShowCancelConfirm(false); onClose(); }} className="px-5 py-2 text-sm font-extrabold text-white bg-amber-600 hover:bg-amber-700 rounded-lg shadow-md" data-testid="cancel-confirm-discard">Sim, cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



/* ========== PharmacyLotsManager — gerencia lotes do produto (Farmacia, Anvisa-compliant) ========== */
const PharmacyLotsManager = ({ productId }) => {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingLot, setEditingLot] = useState(null);
  const [form, setForm] = useState({ lot: '', expiry_date: '', quantity: 0, manufacture_date: '', supplier_invoice: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchLots = async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pharmacy/lots?product_id=${productId}&include_expired=true`, { withCredentials: true });
      setLots(data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar lotes');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchLots(); /* eslint-disable-next-line */ }, [productId]);

  const openNew = () => { setEditingLot(null); setForm({ lot: '', expiry_date: '', quantity: 0, manufacture_date: '', supplier_invoice: '', notes: '' }); setShowForm(true); };
  const openEdit = (l) => { setEditingLot(l); setForm({ lot: l.lot, expiry_date: l.expiry_date || '', quantity: l.quantity || 0, manufacture_date: l.manufacture_date || '', supplier_invoice: l.supplier_invoice || '', notes: l.notes || '' }); setShowForm(true); };

  const save = async () => {
    if (!form.lot || !form.lot.trim()) { toast.error('Informe o numero do lote'); return; }
    setSaving(true);
    try {
      const payload = { ...form, product_id: productId, quantity: Number(form.quantity) || 0 };
      if (editingLot) {
        await axios.put(`${API}/pharmacy/lots/${editingLot.id}`, payload, { withCredentials: true });
        toast.success('Lote atualizado');
      } else {
        await axios.post(`${API}/pharmacy/lots`, payload, { withCredentials: true });
        toast.success('Lote cadastrado');
      }
      setShowForm(false); fetchLots();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar lote');
    } finally { setSaving(false); }
  };

  const del = async (l) => {
    if (!(await confirmDialog({ title: `Excluir lote "${l.lot}"?`, confirmLabel: 'Confirmar', danger: true }))) return;
    try {
      await axios.delete(`${API}/pharmacy/lots/${l.id}`, { withCredentials: true });
      toast.success('Lote excluido'); fetchLots();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao excluir'); }
  };

  if (!productId) {
    return <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Salve o produto primeiro para gerenciar lotes.</div>;
  }

  return (
    <div className="border-t border-slate-200 pt-3 mt-1 md:col-span-2" data-testid="pharmacy-lots-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-black uppercase tracking-wider text-cyan-700">Lotes em Estoque</p>
        <button type="button" onClick={openNew} className="text-[10px] font-bold px-2 py-1 rounded bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="add-lot-btn">+ Novo Lote</button>
      </div>
      {loading ? <p className="text-xs text-slate-400">Carregando...</p> : lots.length === 0 ? (
        <p className="text-xs text-slate-400 bg-slate-50 border border-dashed border-slate-300 rounded-lg px-3 py-3 text-center">Nenhum lote cadastrado.</p>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr><th className="px-2 py-1.5 text-left font-bold">Lote</th><th className="px-2 py-1.5 text-left font-bold">Validade</th><th className="px-2 py-1.5 text-right font-bold">Qtd</th><th className="px-2 py-1.5 text-left font-bold">NF</th><th className="px-2 py-1.5"></th></tr>
            </thead>
            <tbody>
              {lots.map(l => (
                <tr key={l.id} className={`border-t border-slate-100 ${l.is_expired ? 'bg-red-50' : ''}`} data-testid="lot-row">
                  <td className="px-2 py-1.5 font-mono font-bold text-slate-800">{l.lot}</td>
                  <td className={`px-2 py-1.5 ${l.is_expired ? 'text-red-700 font-bold' : 'text-slate-700'}`}>{l.expiry_date || '—'}{l.is_expired && <span className="ml-1 text-[9px] bg-red-600 text-white px-1 rounded">VENCIDO</span>}</td>
                  <td className="px-2 py-1.5 text-right font-bold text-slate-800">{l.quantity}</td>
                  <td className="px-2 py-1.5 text-slate-600">{l.supplier_invoice || '—'}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button type="button" onClick={() => openEdit(l)} className="text-violet-600 hover:text-violet-800 mr-2" data-testid="lot-edit-btn"><Edit size={12} /></button>
                    <button type="button" onClick={() => del(l)} className="text-red-600 hover:text-red-800" data-testid="lot-delete-btn"><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" data-testid="lot-form-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-cyan-50">
              <h4 className="text-sm font-black text-cyan-900">{editingLot ? 'Editar Lote' : 'Novo Lote'}</h4>
              <button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-cyan-100 rounded"><X size={16} /></button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <label className="text-[10px] font-bold text-slate-600 uppercase col-span-2">Numero do Lote *
                <input value={form.lot} onChange={e => setForm({ ...form, lot: e.target.value })} className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm font-mono font-bold text-slate-900" data-testid="lot-form-lot" autoFocus />
              </label>
              <label className="text-[10px] font-bold text-slate-600 uppercase">Validade
                <input type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm text-slate-900" data-testid="lot-form-expiry" />
              </label>
              <label className="text-[10px] font-bold text-slate-600 uppercase">Quantidade
                <input type="number" min="0" step="0.01" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm text-slate-900" data-testid="lot-form-qty" />
              </label>
              <label className="text-[10px] font-bold text-slate-600 uppercase">Fabricacao
                <input type="date" value={form.manufacture_date} onChange={e => setForm({ ...form, manufacture_date: e.target.value })} className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm text-slate-900" data-testid="lot-form-mfg" />
              </label>
              <label className="text-[10px] font-bold text-slate-600 uppercase">NF de Entrada
                <input value={form.supplier_invoice} onChange={e => setForm({ ...form, supplier_invoice: e.target.value })} className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm text-slate-900" data-testid="lot-form-invoice" />
              </label>
              <label className="text-[10px] font-bold text-slate-600 uppercase col-span-2">Observacoes
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm text-slate-900" data-testid="lot-form-notes" />
              </label>
            </div>
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 rounded text-xs font-bold text-slate-600 hover:bg-slate-200" data-testid="lot-form-cancel">Cancelar</button>
              <button type="button" onClick={save} disabled={saving} className="px-3 py-1.5 rounded bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-300 text-white text-xs font-black" data-testid="lot-form-save">{saving ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// iter77di: painel de gerenciamento de Variacoes de Produto (Fase 1).
// Estrutura: `variant_attributes` = [{name:"Tamanho", values:["P","M","G"]}, ...] (ate 3);
// `variants` = matriz gerada = [{id, sku, options:{}, price?, stock, image_url?}].
// Ao gerar/regenerar, faz merge com o que ja existe (preserva sku/stock/preco/imagem
// das combinacoes que ja tinham dados).
const VariantsPanel = ({ form, setForm, inputClass }) => {
  const attrs = form.variant_attributes || [];
  const variants = form.variants || [];
  // iter156ef: base reutilizavel de atributos do tenant (Tamanho, Cor...) — cadastrado uma vez, serve pra todos
  const [baseAttrs, setBaseAttrs] = useState([]);
  useEffect(() => {
    axios.get(`${ERP}/variant-attributes`, { withCredentials: true }).then(({ data }) => setBaseAttrs(data.data || [])).catch(() => {});
  }, []);
  const setAttrs = (fn) => setForm(f => ({ ...f, variant_attributes: fn(f.variant_attributes || []) }));
  const enableVariants = (on) => setForm(f => ({
    ...f, has_variants: on,
    variant_attributes: on ? (f.variant_attributes?.length ? f.variant_attributes : []) : [],
    variants: on ? f.variants : [],
  }));
  const setAttrName = (idx, name) => setAttrs(list => list.map((a, i) => i === idx ? { ...a, name } : a));
  // FIX: input controlado por texto cru (values_text) — antes o split por virgula "engolia" a virgula
  // digitada e impedia cadastrar mais de um valor.
  const setAttrValuesText = (idx, text) => setAttrs(list => list.map((a, i) => i === idx
    ? { ...a, values_text: text, values: text.split(',').map(s => s.trim()).filter(Boolean) } : a));
  const toggleAttrValue = (idx, val) => setAttrs(list => list.map((a, i) => {
    if (i !== idx) return a;
    const has = (a.values || []).includes(val);
    const values = has ? a.values.filter(v => v !== val) : [...(a.values || []), val];
    return { ...a, values, values_text: values.join(', ') };
  }));
  const addAttr = () => { if (attrs.length < 3) setAttrs(list => [...list, { name: '', values: [], values_text: '' }]); };
  const addFromBase = (baseId) => {
    const b = baseAttrs.find(x => x.id === baseId);
    if (!b || attrs.length >= 3) return;
    if (attrs.some(a => (a.name || '').trim().toLowerCase() === b.name.toLowerCase())) { toast.info(`${b.name} ja esta no produto`); return; }
    setAttrs(list => [...list, { name: b.name, values: [...b.values], values_text: b.values.join(', ') }]);
  };
  const removeAttr = (idx) => setAttrs(list => list.filter((_, i) => i !== idx));

  // EAN-13 derivado do codigo do pai: prefixo 12 digitos do pai + sequencia → recalcula DV.
  const ean13 = (base12) => {
    let sum = 0;
    for (let i = 0; i < 12; i++) sum += parseInt(base12[i], 10) * (i % 2 === 0 ? 1 : 3);
    return base12 + ((10 - (sum % 10)) % 10);
  };
  const variantBarcode = (seq) => {
    const parent = String(form.barcode || '').replace(/\D/g, '');
    let root = parent.length >= 12 ? parent.slice(0, 10) : (Date.now().toString().slice(-10));
    return ean13(root + String(seq).padStart(2, '0'));
  };

  const generateMatrix = () => {
    const validAttrs = attrs.filter(a => (a.name || '').trim() && (a.values || []).length > 0);
    if (validAttrs.length === 0) { toast.error('Escolha pelo menos 1 atributo com valores (ex: Tamanho: P, M, G).'); return; }
    const combos = validAttrs.reduce(
      (acc, attr) => acc.flatMap(prev => attr.values.map(v => [...prev, { name: attr.name.trim(), value: v }])),
      [[]]
    );
    const existingByKey = new Map();
    for (const v of variants) existingByKey.set(JSON.stringify(v.options || {}), v);
    const skuBase = (form.sku || '').trim();
    const usedBarcodes = new Set(variants.map(v => v.barcode).filter(Boolean));
    let seq = 1;
    const nextBarcode = () => { let bc = variantBarcode(seq++); while (usedBarcodes.has(bc)) bc = variantBarcode(seq++); usedBarcodes.add(bc); return bc; };
    const newVariants = combos.map((combo, idx) => {
      const options = Object.fromEntries(combo.map(c => [c.name, c.value]));
      const existing = existingByKey.get(JSON.stringify(options));
      if (existing) return { ...existing, options, barcode: existing.barcode || nextBarcode() };
      const suffix = combo.map(c => c.value).join('-').toUpperCase().replace(/[^A-Z0-9-]/g, '');
      return { id: `v_${Date.now()}_${idx}`, sku: skuBase ? `${skuBase}-${suffix}` : suffix, barcode: nextBarcode(), options, price: '', stock: 0, image_url: '' };
    });
    setForm(f => ({ ...f, variants: newVariants }));
    toast.success(`${newVariants.length} variacoes geradas com SKU e codigo de barras.`);
  };
  const updateVariant = (idx, patch) => setForm(f => ({ ...f, variants: (f.variants || []).map((v, i) => i === idx ? { ...v, ...patch } : v) }));
  const removeVariant = (idx) => setForm(f => ({ ...f, variants: (f.variants || []).filter((_, i) => i !== idx) }));

  if (!form.has_variants) {
    return (
      <div className="text-center py-8 bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl" data-testid="variants-disabled">
        <Boxes size={28} className="mx-auto text-slate-300 mb-2" />
        <p className="text-sm font-bold text-slate-700 mb-1">Este produto nao tem variacoes</p>
        <p className="text-xs text-slate-500 mb-4">Ative para cadastrar grade (Tamanho, Cor...). Cada variacao ganha SKU e codigo de barras proprios e estoque separado.</p>
        <button type="button" onClick={() => enableVariants(true)} className="px-5 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white text-xs font-bold shadow" data-testid="enable-variants-btn">
          Ativar variacoes
        </button>
      </div>
    );
  }
  const unusedBase = baseAttrs.filter(b => !attrs.some(a => (a.name || '').trim().toLowerCase() === b.name.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="variants-panel">
      <div className="flex items-center justify-between bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
        <div>
          <p className="text-sm font-black text-violet-800">Variacoes ativadas</p>
          <p className="text-[10px] text-violet-600">Atributos cadastrados aqui ficam salvos na base e podem ser reaproveitados em qualquer outro produto.</p>
        </div>
        <button type="button" onClick={() => enableVariants(false)} className="text-[10px] font-bold text-red-600 hover:text-red-800" data-testid="disable-variants-btn">Desativar</button>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <label className="text-[11px] font-black uppercase tracking-wider text-slate-700">Atributos (ate 3)</label>
          <div className="flex items-center gap-2">
            {unusedBase.length > 0 && (
              <select value="" onChange={e => addFromBase(e.target.value)} disabled={attrs.length >= 3} className="px-2 py-1 text-[11px] font-bold border border-violet-300 text-violet-700 rounded-lg bg-white disabled:opacity-40" data-testid="add-attr-from-base">
                <option value="">+ Usar atributo ja cadastrado…</option>
                {unusedBase.map(b => <option key={b.id} value={b.id}>{b.name} ({b.values.length} valores)</option>)}
              </select>
            )}
            <button type="button" onClick={addAttr} disabled={attrs.length >= 3} className="text-[10px] font-bold text-violet-600 hover:text-violet-800 disabled:opacity-40" data-testid="add-attr-btn">
              <Plus size={12} className="inline" /> Novo atributo
            </button>
          </div>
        </div>
        {attrs.length === 0 ? (
          <p className="text-xs text-slate-400 bg-slate-50 border border-dashed border-slate-300 rounded-lg px-3 py-4 text-center">Nenhum atributo. Use um ja cadastrado ou crie um novo.</p>
        ) : attrs.map((a, i) => {
          const base = baseAttrs.find(b => b.name.toLowerCase() === (a.name || '').trim().toLowerCase());
          const chips = [...new Set([...(base?.values || []), ...(a.values || [])])];
          return (
            <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-2 mb-2" data-testid={`attr-row-${i}`}>
              <div className="grid grid-cols-12 gap-2 items-start">
                <div className="col-span-4">
                  <label className="text-[9px] font-bold text-slate-500 uppercase">Nome</label>
                  <input value={a.name || ''} onChange={e => setAttrName(i, e.target.value)} placeholder="Ex: Tamanho" className={inputClass} data-testid={`attr-name-${i}`} />
                </div>
                <div className="col-span-7">
                  <label className="text-[9px] font-bold text-slate-500 uppercase">Valores (separados por virgula)</label>
                  <input value={a.values_text ?? (a.values || []).join(', ')} onChange={e => setAttrValuesText(i, e.target.value)} placeholder="Ex: P, M, G, GG" className={inputClass} data-testid={`attr-values-${i}`} />
                </div>
                <div className="col-span-1 flex items-end h-full pb-1">
                  <button type="button" onClick={() => removeAttr(i)} className="text-red-500 hover:text-red-700" title="Remover atributo" data-testid={`attr-remove-${i}`}><Trash2 size={14} /></button>
                </div>
              </div>
              {chips.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`attr-chips-${i}`}>
                  {chips.map(v => {
                    const on = (a.values || []).includes(v);
                    return <button type="button" key={v} onClick={() => toggleAttrValue(i, v)} className={`px-2 py-0.5 rounded-full text-[11px] font-bold border transition-colors ${on ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-300 hover:border-violet-400'}`} data-testid={`attr-chip-${i}-${v}`}>{v}</button>;
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {attrs.length > 0 && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">{variants.length > 0 ? `${variants.length} variacao(oes) na grade.` : 'Sem variacoes ainda.'}</p>
          <button type="button" onClick={generateMatrix} className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white text-xs font-bold shadow" data-testid="generate-variants-btn">
            Gerar grade (SKU + cod. barras)
          </button>
        </div>
      )}

      {variants.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-slate-600 uppercase text-[9px] font-black">
              <tr><th className="px-2 py-1.5 text-left">Variacao</th><th className="px-2 py-1.5 text-left">SKU</th><th className="px-2 py-1.5 text-left">Cod. barras</th><th className="px-2 py-1.5 text-right">Preco</th><th className="px-2 py-1.5 text-right">Estoque</th><th className="px-2 py-1.5"></th></tr>
            </thead>
            <tbody>
              {variants.map((v, i) => (
                <tr key={v.id || i} className="border-t border-slate-100" data-testid={`variant-row-${i}`}>
                  <td className="px-2 py-1 font-bold text-slate-800">{Object.values(v.options || {}).join(' / ')}</td>
                  <td className="px-2 py-1"><input value={v.sku || ''} onChange={e => updateVariant(i, { sku: e.target.value })} className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-mono focus:border-violet-500 focus:outline-none" data-testid={`variant-sku-${i}`} /></td>
                  <td className="px-2 py-1"><input value={v.barcode || ''} onChange={e => updateVariant(i, { barcode: e.target.value.replace(/\D/g, '') })} className="w-36 px-2 py-1 text-xs border border-slate-300 rounded font-mono focus:border-violet-500 focus:outline-none" data-testid={`variant-barcode-${i}`} /></td>
                  <td className="px-2 py-1 text-right"><input type="number" step="0.01" min="0" value={v.price ?? ''} onChange={e => updateVariant(i, { price: e.target.value })} placeholder="=pai" className="w-20 px-2 py-1 text-xs text-right border border-slate-300 rounded focus:border-violet-500 focus:outline-none" data-testid={`variant-price-${i}`} /></td>
                  <td className="px-2 py-1 text-right font-mono text-slate-700" title={`Estoque total (todas as filiais) — por filial: ${Object.entries(v.stock_by_company || {}).map(([, q]) => q).join(' / ') || '—'}. Altere via Movimentacao de Estoque`} data-testid={`variant-stock-${i}`}>{Number(v.stock || 0).toLocaleString('pt-BR')}</td>
                  <td className="px-2 py-1 text-right"><button type="button" onClick={() => removeVariant(i)} className="text-red-500 hover:text-red-700" data-testid={`variant-remove-${i}`}><Trash2 size={12} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <label className="flex items-start gap-2 p-2 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer">
        <input type="checkbox" checked={!!form.show_variant_stock_in_list} onChange={e => setForm(f => ({ ...f, show_variant_stock_in_list: e.target.checked }))} className="mt-0.5 w-4 h-4 accent-violet-600" data-testid="toggle-show-variant-stock" />
        <span className="text-xs text-slate-700"><strong>Mostrar estoque por variacao na lista de Produtos</strong><br /><span className="text-[10px] text-slate-500">A linha do produto exibe o detalhamento (ex: P: 3 · M: 5 · G: 0) abaixo do estoque total.</span></span>
      </label>
    </div>
  );
};


/* iter156dg: Combobox de busca de Materia-prima na aba Producao.
   Substitui o <select> nativo (inviavel com muitos produtos).
   - Digite parte do nome ou SKU para filtrar.
   - Ao clicar em um item, invoca onPick(product). */
const BomProductCombobox = ({ value, products, onPick, testId }) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  // iter156dr: dropdown agora usa position: fixed com coordenadas medidas do botao
  // (portal-like), evitando o clipping pelo modal com overflow-hidden.
  const [pos, setPos] = React.useState({ top: 0, left: 0, width: 280 });
  const wrapRef = React.useRef(null);
  const btnRef = React.useRef(null);
  const dropRef = React.useRef(null);
  const selected = React.useMemo(() => products.find(p => p.id === value), [products, value]);
  const recalcPos = React.useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: Math.max(280, r.width) });
  }, []);
  React.useEffect(() => {
    if (!open) return;
    recalcPos();
    const onDoc = (e) => {
      const inBtn = wrapRef.current && wrapRef.current.contains(e.target);
      const inDrop = dropRef.current && dropRef.current.contains(e.target);
      if (!inBtn && !inDrop) setOpen(false);
    };
    const onScroll = () => recalcPos();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, recalcPos]);
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products.slice(0, 50);
    return products.filter(p => {
      const n = (p.name || '').toLowerCase();
      const s = (p.sku || '').toLowerCase();
      const c = String(p.code || '').toLowerCase();
      return n.includes(q) || s.includes(q) || c.includes(q);
    }).slice(0, 50);
  }, [products, search]);
  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-full px-2 py-1 border rounded text-xs text-left flex items-center justify-between gap-1 ${value ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50 text-slate-400'}`}
        data-testid={`${testId}-toggle`}
      >
        <span className="truncate">{selected ? `${selected.name}${selected.sku ? ` (${selected.sku})` : ''}` : '— Selecione uma MP —'}</span>
        <ChevronDown size={12} className="text-slate-400 flex-shrink-0" />
      </button>
      {open && (
        <div
          ref={dropRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
          className="bg-white border-2 border-emerald-300 rounded-lg shadow-xl overflow-hidden"
        >
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nome, SKU ou código..."
              className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs focus:outline-none focus:border-emerald-500"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="text-center py-4 text-[11px] text-slate-400">Nenhuma MP encontrada</div>
            ) : filtered.map(p => {
              const u = ((p.fiscal?.unit_commercial) || p.unit || 'UN').toUpperCase();
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { onPick(p); setOpen(false); setSearch(''); }}
                  className={`w-full text-left px-2 py-1.5 text-xs hover:bg-emerald-50 border-b border-slate-50 flex items-center justify-between gap-2 ${p.id === value ? 'bg-emerald-50' : ''}`}
                  data-testid={`${testId}-opt-${p.id}`}
                >
                  <span className="truncate">
                    <span className="font-semibold text-slate-800">{p.name}</span>
                    {p.sku && <span className="text-slate-400 ml-1">({p.sku})</span>}
                  </span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 flex-shrink-0">{u}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


const MiniProductForm = ({ product, categories, companyId, perBranchPricing, productionEnabled, osEnabled, onClose, onCreated }) => {
  const { isPharmacy } = useBusinessType();
  const [activeTab, setActiveTab] = useState('basic');
  const [form, setForm] = useState({
    name: product?.name || '', sku: product?.sku || '', barcode: product?.barcode || '', description: product?.description || '',
    category_id: product?.category_id || '', brand: product?.brand || '', grade: product?.grade || '',
    // iter77ag: campo fornecedor — alimentado tambem pelo flow Sugestao de Compra -> Vincular Fornecedor
    supplier_id: product?.supplier_id || '',
    price: product?.price ?? '', cost_price: product?.cost_price ?? '',
    box_quantity: product?.box_quantity || '', box_price: product?.box_price || '',
    weight: product?.weight || '', reference: product?.reference || '', stock: product?.stock || 0,
    images: product?.images || [], video_url: product?.video_url || '', price_on_request: product?.price_on_request || false,
    fiscal: product?.fiscal || { origem: '0' },
    pharmacy: product?.pharmacy || {},
    lot_control: !!product?.lot_control,
    // iter77x: ativo/inativo (default true para novos; respeita valor existente em edicao)
    active: product?.active !== false,
    // iter77dc: show_in_catalog — visivel no catalogo publico? Default TRUE para novos.
    // Ao desmarcar, o produto some do catalogo publico mas continua vendavel no PDV.
    show_in_catalog: product?.show_in_catalog !== false,
    // iter77di: variacoes (Fase 1). variant_attributes = [{name, values:[]}]; variants = matriz gerada.
    has_variants: !!product?.has_variants,
    show_variant_stock_in_list: !!product?.show_variant_stock_in_list,
    variant_attributes: Array.isArray(product?.variant_attributes) ? product.variant_attributes : [],
    variants: Array.isArray(product?.variants) ? product.variants : [],
    // iter156dz: Servico? (usado no modulo OS/Locacao — nao movimenta estoque)
    is_service: !!product?.is_service,
    // iter156ea: para LOCACAO — retem estoque enquanto OS ativa
    is_rental: !!product?.is_rental,
  });
  // iter77ag: lista de fornecedores para o select
  const [suppliers, setSuppliers] = useState([]);
  // iter156aq: Producao — BOM (bill of materials) do produto acabado
  const [isManufactured, setIsManufactured] = useState(!!product?.is_manufactured);
  const [bom, setBom] = useState(Array.isArray(product?.bom) ? product.bom : []);
  const [allProducts, setAllProducts] = useState([]);
  useEffect(() => {
    if (!productionEnabled) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products?limit=1000`, { withCredentials: true });
        setAllProducts((data.data || data).filter(p => !product || p.id !== product.id));
      } catch { /* silent */ }
    })();
  }, [productionEnabled, product]);
  useEffect(() => {
    if (!productionEnabled || !product?.id) return;
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/products/${product.id}/bom`, { withCredentials: true });
        setIsManufactured(!!data.is_manufactured);
        setBom(Array.isArray(data.bom) ? data.bom : []);
      } catch { /* module might not be enabled */ }
    })();
  }, [productionEnabled, product?.id]);
  const addBomItem = () => setBom(prev => [...prev, { product_id: '', qty: 1, unit: 'UN', mp_stock_unit: 'UN' }]);
  const updBomItem = (i, patch) => setBom(prev => prev.map((it, ix) => ix === i ? { ...it, ...patch } : it));
  const rmBomItem = (i) => setBom(prev => prev.filter((_, ix) => ix !== i));
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/erp/suppliers`, { withCredentials: true });
        setSuppliers(data?.data || []);
      } catch { /* ignore */ }
    })();
  }, []);
  const updFiscal = (patch) => setForm(p => ({ ...p, fiscal: { ...(p.fiscal || {}), ...patch } }));
  const updPharma = (patch) => setForm(p => ({ ...p, pharmacy: { ...(p.pharmacy || {}), ...patch } }));
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [generatingSku, setGeneratingSku] = useState(false);
  const generateSku = async () => {
    setGeneratingSku(true);
    try {
      const { data } = await axios.get(`${API}/products/next-sku`, { withCredentials: true });
      setForm(p => ({ ...p, sku: data.sku }));
      toast.success(`SKU gerado: ${data.sku}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar SKU');
    } finally { setGeneratingSku(false); }
  };
  const addImage = () => { if (imageUrl.trim()) { setForm(p => ({ ...p, images: [...p.images, imageUrl.trim()] })); setImageUrl(''); } };
  const resolveImg = (url) => !url ? '' : url.startsWith('http') ? url : `${API.replace(/\/api$/, '')}${url}`;
  const uploadImages = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const file of list) {
        if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: máx 5MB`); continue; }
        const fd = new FormData(); fd.append('file', file);
        const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        setForm(p => ({ ...p, images: [...p.images, data.url] }));
      }
      toast.success('Imagens enviadas');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro no upload'); }
    finally { setUploading(false); }
  };
  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    // Build a clean payload (omits id/created_at/tenant_id and other extras
    // that might come from a loaded product object). Only the fields the
    // backend ProductCreate model expects.
    const payload = {
      name: (form.name || '').trim(),
      sku: (form.sku || '').trim(),
      barcode: form.barcode ? String(form.barcode).trim() : null,
      description: form.description || null,
      brand: form.brand || null,
      grade: form.grade || null,
      reference: form.reference || null,
      category_id: form.category_id || null,
      supplier_id: form.supplier_id || null,
      price: typeof form.price === 'number' ? form.price : (parseFloat(form.price) || 0),
      cost_price: form.cost_price === '' || form.cost_price == null ? null : (typeof form.cost_price === 'number' ? form.cost_price : parseFloat(form.cost_price)),
      box_quantity: form.box_quantity ? parseInt(form.box_quantity, 10) : null,
      box_price: form.box_price ? parseFloat(form.box_price) : null,
      weight: form.weight ? parseFloat(form.weight) : null,
      stock: form.stock === '' || form.stock == null ? 0 : parseInt(form.stock, 10) || 0,
      images: Array.isArray(form.images) ? form.images : [],
      video_url: form.video_url || null,
      price_on_request: !!form.price_on_request,
      fiscal: (() => {
        // Coerce numeric fiscal fields and strip empties
        const f = form.fiscal || {};
        const numKeys = ['icms_aliquota','icms_aliquota_st','icms_mva','icms_red_bc','ipi_aliquota','pis_aliquota','cofins_aliquota','gross_weight','net_weight'];
        const out = {};
        Object.entries(f).forEach(([k, v]) => {
          if (v == null || v === '') return;
          if (numKeys.includes(k)) {
            const n = parseFloat(String(v).replace(',', '.'));
            if (!isNaN(n)) out[k] = n;
          } else {
            out[k] = String(v).trim();
          }
        });
        return out;
      })(),
      pharmacy: isPharmacy ? (() => {
        const p = form.pharmacy || {};
        const numKeys = ['cmed_pmc', 'cmed_pf'];
        const boolKeys = ['controlled', 'generic', 'similar', 'needs_prescription', 'prescription_retention'];
        const out = {};
        Object.entries(p).forEach(([k, v]) => {
          if (v == null || v === '') return;
          if (numKeys.includes(k)) {
            const n = parseFloat(String(v).replace(',', '.'));
            if (!isNaN(n)) out[k] = n;
          } else if (boolKeys.includes(k)) {
            out[k] = !!v;
          } else {
            out[k] = String(v).trim();
          }
        });
        return Object.keys(out).length > 0 ? out : null;
      })() : null,
      lot_control: !!form.lot_control,
      // iter77x: envia active no payload (form padrao = true para novos)
      active: !!form.active,
      // iter77dc: envia show_in_catalog (default true; controla visibilidade no catalogo publico)
      show_in_catalog: !!form.show_in_catalog,
      // iter77di: variacoes
      has_variants: !!form.has_variants,
      variant_attributes: (form.variant_attributes || []).map(({ values_text, ...a }) => a),
      variants: form.variants || [],
      show_variant_stock_in_list: !!form.show_variant_stock_in_list,
      // iter156dz: envia is_service (usado pelo modulo OS/Locacao)
      is_service: !!form.is_service,
      // iter156ea: envia is_rental (produto disponivel para locacao)
      is_rental: !!form.is_rental,
    };
    try {
      if (product) {
        // iter156ap: em UPDATE, nunca envia campos de estoque no payload.
        // Backend tambem descarta, mas defense-in-depth: evita QUALQUER chance
        // de o estoque ser alterado via cadastro de produto. Estoque so muda
        // por: vendas, devolucoes, cancelamentos, movimentos (entrada/saida/
        // balanco), transferencias e modulo de compras.
        const payloadNoStock = { ...payload };
        delete payloadNoStock.stock;
        delete payloadNoStock.initial_stock;
        if (Array.isArray(payloadNoStock.variants)) {
          payloadNoStock.variants = payloadNoStock.variants.map(v => {
            if (!v || typeof v !== 'object') return v;
            const { stock: _s, ...rest } = v;
            return rest;
          });
        }
        // iter77ct.37: quando o tenant tem `per_branch_pricing_enabled` E temos um
        // company_id selecionado, o preco/custo/preco-caixa NAO alteram o cadastro
        // global — vao para o override da filial atual (`erp_product_prices`).
        if (perBranchPricing && companyId) {
          const priceForBranch = payloadNoStock.price;
          const costForBranch = payloadNoStock.cost_price;
          const boxForBranch = payloadNoStock.box_price;
          // Global PUT sem preco/custo/preco-caixa
          const globalPayload = { ...payloadNoStock };
          delete globalPayload.price;
          delete globalPayload.cost_price;
          delete globalPayload.box_price;
          await axios.put(`${API}/products/${product.id}`, globalPayload, { withCredentials: true });
          // Override de preco para a filial atual
          const branchPayload = { company_id: companyId };
          if (typeof priceForBranch === 'number' && isFinite(priceForBranch)) branchPayload.price = priceForBranch;
          if (typeof costForBranch === 'number' && isFinite(costForBranch)) branchPayload.cost_price = costForBranch;
          if (typeof boxForBranch === 'number' && isFinite(boxForBranch)) branchPayload.box_price = boxForBranch;
          if (branchPayload.price != null || branchPayload.cost_price != null || branchPayload.box_price != null) {
            await axios.put(`${API}/products/${product.id}/branch-price`, branchPayload, { withCredentials: true });
          }
          toast.success('Produto atualizado! Preco/Custo salvos como exclusivos desta filial.');
        } else {
          await axios.put(`${API}/products/${product.id}`, payloadNoStock, { withCredentials: true });
          toast.success('Produto atualizado!');
        }
        // iter156aq: salva a BOM (Producao) se o modulo estiver habilitado
        if (productionEnabled && product.id) {
          try {
            const bomPayload = {
              is_manufactured: !!isManufactured,
              bom: (bom || []).filter(b => b.product_id && b.qty > 0).map(b => ({ product_id: b.product_id, qty: parseFloat(b.qty) || 0, unit: (b.unit || 'UN').toUpperCase() })),
            };
            await axios.put(`${ERP}/products/${product.id}/bom`, bomPayload, { withCredentials: true });
          } catch (bomErr) {
            toast.error(bomErr.response?.data?.detail || 'Erro ao salvar BOM');
          }
        }
        onClose();
      } else {
        // iter77v: NAO faz POST imediato. Passa o payload para o ShareProductModal
        // que so cria o produto apos o usuario confirmar quais filiais. Se cancelar,
        // NADA eh persistido (era o bug: produto aparecia mesmo apos Fechar).
        if (typeof onCreated === 'function') {
          onCreated(payload);
        } else {
          // fallback (sem share flow): cria direto
          const { data } = await axios.post(`${API}/products`, payload, { withCredentials: true });
          toast.success('Produto criado!');
          onClose();
        }
      }
    } catch (err) {
      // Surface the real backend message + first validation issue (if any)
      const detail = err.response?.data?.detail;
      let msg = 'Erro ao salvar';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail[0]) {
        const d0 = detail[0];
        msg = `${(d0.loc || []).slice(1).join('.')}: ${d0.msg}`;
      }
      toast.error(msg);
    }
    finally { setSubmitting(false); }
  };
  // Gera codigo EAN-13 valido a partir de timestamp + random + digito verificador
  const generateBarcode = () => {
    const ts = Date.now().toString().slice(-10);
    const rnd = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    const base = (ts + rnd).slice(0, 12);
    let sum = 0;
    for (let i = 0; i < 12; i++) sum += parseInt(base[i], 10) * (i % 2 === 0 ? 1 : 3);
    const check = (10 - (sum % 10)) % 10;
    const code = base + check;
    setForm(p => ({ ...p, barcode: code }));
    toast.success('Codigo de barras gerado');
  };
  const currencyInput = "w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="product-form-modal">
      <form
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          // iter77ct.39: NUNCA submeter o form quando o usuario aperta Enter num input
          // (leitores USB de codigo de barras enviam Enter no fim da leitura). O Save
          // so acontece ao clicar em "Atualizar" / "Salvar". Textareas mantem Enter
          // (quebra de linha) e o botao submit continua funcionando (nao dispara este handler).
          if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
            e.preventDefault();
          }
        }}
        className="bg-white w-full max-w-4xl max-h-[94vh] overflow-y-auto rounded-t-2xl md:rounded-2xl shadow-2xl"
        data-testid="product-form"
      >
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-3 flex items-center justify-between z-10">
          <h3 className="text-white font-bold text-sm flex items-center gap-2"><Package size={16} /> {product ? 'Editar Produto' : 'Novo Produto'}</h3>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" data-testid="product-form-close"><X size={18} /></button>
        </div>
        {/* Tabs */}
        <div className="sticky top-[42px] bg-white border-b border-slate-200 z-10 px-3 flex gap-0 overflow-x-auto">
          {[
            { k: 'basic',  label: 'Dados Basicos' },
            { k: 'fiscal', label: 'Fiscal' },
            ...(isPharmacy ? [{ k: 'pharmacy', label: 'Farmacia', highlight: true }] : []),
            { k: 'variacoes', label: 'Variacoes' },
            ...(productionEnabled ? [{ k: 'producao', label: 'Producao', highlight: true }] : []),
            { k: 'outros', label: 'Outros' },
          ].map(t => (
            <button
              key={t.k}
              type="button"
              onClick={() => setActiveTab(t.k)}
              className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 whitespace-nowrap ${activeTab === t.k ? (t.highlight ? 'border-emerald-600 text-emerald-700' : 'border-violet-600 text-violet-700') : 'border-transparent text-slate-400 hover:text-slate-700'}`}
              data-testid={`product-tab-${t.k}`}
            >
              {t.label}
              {t.k === 'pharmacy' && <span className="ml-1 text-[8px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded">RX</span>}
            </button>
          ))}
        </div>
        <div className="p-5">
        <div className={activeTab === 'basic' ? '' : 'hidden'} data-testid="product-tab-basic-content">
      {/* iter156ea: 3 flags compactas no canto superior direito.
          - Ativo (sempre visivel)
          - Disponivel no Catalogo (sempre visivel; iter77dc)
          - E um servico? (visivel so quando osEnabled)
          - E para locacao? (visivel so quando osEnabled)
          Usa botoes-pill toggle com estado colorido. Poupa 3 linhas do form. */}
      <div className="mb-3 flex flex-wrap items-center justify-end gap-1.5" data-testid="product-flags-bar">
        <button type="button" onClick={() => setForm({ ...form, active: !form.active })} className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full border transition-all ${form.active ? 'bg-emerald-500 border-emerald-600 text-white shadow-sm' : 'bg-slate-100 border-slate-300 text-slate-500 hover:bg-slate-200'}`} title={form.active ? 'Vendavel no PDV e movimentos' : 'Oculto do PDV, catalogo e movimentos'} data-testid="product-flag-active">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${form.active ? 'bg-white' : 'bg-slate-400'}`}></span>
          {form.active ? 'Ativo' : 'Inativo'}
        </button>
        <button type="button" onClick={() => setForm({ ...form, show_in_catalog: !form.show_in_catalog })} className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full border transition-all ${form.show_in_catalog ? 'bg-cyan-500 border-cyan-600 text-white shadow-sm' : 'bg-slate-100 border-slate-300 text-slate-500 hover:bg-slate-200'}`} title={form.show_in_catalog ? 'Aparece no catalogo publico' : 'Oculto do catalogo publico'} data-testid="product-flag-catalog">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${form.show_in_catalog ? 'bg-white' : 'bg-slate-400'}`}></span>
          {form.show_in_catalog ? 'No Catalogo' : 'Sem Catalogo'}
        </button>
        {osEnabled && (
          <button type="button" onClick={() => setForm({ ...form, is_service: !form.is_service, is_rental: form.is_service ? form.is_rental : false })} className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full border transition-all ${form.is_service ? 'bg-amber-500 border-amber-600 text-white shadow-sm' : 'bg-slate-100 border-slate-300 text-slate-500 hover:bg-slate-200'}`} title={form.is_service ? 'Servico — nao movimenta estoque' : 'Produto normal (movimenta estoque)'} data-testid="product-flag-service">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${form.is_service ? 'bg-white' : 'bg-slate-400'}`}></span>
            {form.is_service ? 'Servico' : 'Produto'}
          </button>
        )}
        {osEnabled && !form.is_service && (
          <button type="button" onClick={() => setForm({ ...form, is_rental: !form.is_rental })} className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full border transition-all ${form.is_rental ? 'bg-violet-500 border-violet-600 text-white shadow-sm' : 'bg-slate-100 border-slate-300 text-slate-500 hover:bg-slate-200'}`} title={form.is_rental ? 'Locacao — estoque retido enquanto OS ativa; retorna ao finalizar' : 'Nao eh para locacao'} data-testid="product-flag-rental">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${form.is_rental ? 'bg-white' : 'bg-slate-400'}`}></span>
            {form.is_rental ? 'Locacao' : 'Nao Locavel'}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="md:col-span-2"><label className="block text-xs font-semibold text-gray-500 mb-1">Nome *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} required data-testid="product-name" /></div>
        <div><label className="block text-xs font-semibold text-gray-500 mb-1">SKU *</label>
          <div className="flex gap-2">
            <input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} className={inputClass} required data-testid="product-sku" placeholder="0001 ou personalizado" />
            <button
              type="button"
              onClick={generateSku}
              disabled={generatingSku}
              title="Gerar SKU sequencial automatico (0001, 0002, ...)"
              className="group relative flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 disabled:from-slate-300 disabled:to-slate-400 text-white text-xs font-bold shadow-md hover:shadow-lg transition-all flex-shrink-0"
              data-testid="product-sku-auto"
            >
              {generatingSku ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  <span className="hidden sm:inline">Gerando</span>
                </>
              ) : (
                <>
                  <Sparkles size={14} className="group-hover:scale-110 transition-transform" />
                  <span className="hidden sm:inline">Auto</span>
                </>
              )}
            </button>
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-gray-500 mb-1">Codigo de Barras</label>
          <div className="flex gap-1.5">
            <input
              value={form.barcode}
              onChange={e => setForm({ ...form, barcode: e.target.value })}
              onKeyDown={(e) => {
                // iter77ct.39: leitores USB de codigo de barras enviam Enter no fim
                // da leitura. Sem esse guard, o Enter dispara handleSubmit e o form
                // fecha antes do usuario terminar o cadastro. Bloqueia o submit AQUI.
                if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); }
              }}
              className={inputClass}
              placeholder="Escaneie, digite ou gere automaticamente"
              inputMode="numeric"
              data-testid="product-barcode"
            />
            <button type="button" onClick={() => setShowBarcodeScanner(true)} className="px-3 py-2.5 bg-teal-50 border border-teal-200 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors flex items-center gap-1.5" title="Escanear QR/Codigo de Barras" data-testid="product-barcode-scan-btn">
              <QrCode size={16} /><span className="text-xs font-semibold hidden sm:inline">Escanear</span>
            </button>
            <button type="button" onClick={generateBarcode} className="px-3 py-2.5 bg-gray-50 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-1.5" title="Gerar Codigo Automaticamente" data-testid="product-barcode-generate-btn">
              <Sparkles size={16} /><span className="text-xs font-semibold hidden sm:inline">Gerar</span>
            </button>
          </div>
        </div>
        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Categoria</label><select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} className={inputClass}><option value="">Sem categoria</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Fornecedor</label>
          <select value={form.supplier_id} onChange={e => setForm({ ...form, supplier_id: e.target.value })} className={inputClass} data-testid="product-supplier-select">
            <option value="">Sem fornecedor</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}{s.cnpj ? ` — ${s.cnpj}` : ''}</option>)}
          </select>
          <p className="text-[10px] text-slate-400 mt-1">Tambem preenchido automaticamente pelo flow "Vincular Fornecedor" em Sugestao de Compra.</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Preço de Custo</label>
          <CurrencyInput
            value={form.cost_price === '' ? 0 : Number(form.cost_price || 0)}
            onChange={v => setForm(f => ({ ...f, cost_price: v }))}
            placeholder="0,00"
            className="w-full"
            dataTestId="product-cost-price"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Valor de Venda *</label>
          <CurrencyInput
            value={form.price === '' ? 0 : Number(form.price || 0)}
            onChange={v => setForm(f => ({ ...f, price: v }))}
            placeholder="0,00"
            className="w-full"
            dataTestId="product-price"
          />
          {form.cost_price && form.price && parseFloat(form.price) > 0 && parseFloat(form.cost_price) >= 0 && (
            <p className="text-[10px] text-gray-500 mt-1" data-testid="product-margin">Margem: {(((parseFloat(form.price) - parseFloat(form.cost_price)) / parseFloat(form.price)) * 100).toFixed(1).replace('.', ',')}% (R$ {(parseFloat(form.price) - parseFloat(form.cost_price)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1" title="Estoque controlado automaticamente por Movimentacao (entrada/saida/balanco), Transferencia recebida e Compras (futuro). Para ajustar manualmente, use Itens > Movimentacao de Estoque.">
            <span>Estoque</span>
            <span className="text-[10px] text-amber-600 font-normal">(somente leitura)</span>
          </label>
          <input
            type="number"
            value={form.stock}
            readOnly
            disabled
            className={`${inputClass} bg-slate-50 text-slate-500 cursor-not-allowed`}
            data-testid="product-stock"
            title="Use Movimentacao de Estoque ou Transferencia para alterar o estoque"
          />
          <p className="text-[10px] text-slate-400 mt-0.5">Controlado por Movimentacao, Transferencia e Compras.</p>
        </div>
        {/* iter156bw: Unidade de medida (antes ficava na aba Outros como "Unidade comercial — NF-e").
            Dropdown com padroes. Quando eh unidade fracionaria (KG, G, L, ML, M, CM, M2, M3), a
            quantidade em movimentos de estoque e vendas passa a aceitar decimais automaticamente. */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1" title="Escolha a unidade padrao do produto. Para peso pequeno digite 0.500 (=500g), para volume pequeno 0.200 (=200ml).">
            <span>Unidade de medida</span>
            <span className="text-[9px] text-slate-400 font-normal">(NF-e / PDV / Estoque)</span>
          </label>
          <select
            value={(form.fiscal?.unit_commercial || 'UN').toUpperCase()}
            onChange={e => updFiscal({ unit_commercial: e.target.value })}
            className={inputClass}
            data-testid="product-unit-measure"
          >
            {/* iter156ds/dt: MAIS USADAS primeiro (1 canônica por família fracionária),
                depois CONTAGEM INTEIRAS mantida integralmente conforme pedido do usuario. */}
            <optgroup label="⭐ Fracionárias (usam decimais — 0.500 = 500g)">
              <option value="UN">UN — Unidade (peça avulsa)</option>
              <option value="KG">KG — Peso (kg/g: 0.500 = 500g)</option>
              <option value="L">L — Volume (litros/ml: 0.200 = 200ml)</option>
              <option value="M">M — Comprimento (metros/cm: 0.30 = 30cm)</option>
              <option value="M2">M2 — Área (metros quadrados)</option>
            </optgroup>
            <optgroup label="📦 Contagem (inteiras)">
              <option value="PC">PC — Peça</option>
              <option value="CX">CX — Caixa</option>
              <option value="DZ">DZ — Dúzia</option>
              <option value="PAR">PAR — Par</option>
              <option value="FRD">FRD — Fardo</option>
              <option value="KIT">KIT — Kit</option>
              <option value="JG">JG — Jogo</option>
              <option value="CJ">CJ — Conjunto</option>
            </optgroup>
            {/* iter156ds: preserva valor de produtos LEGADOS com unidades antigas
                (G, ML, MG, TON, M3, CM). Aparece so quando o produto atual esta usando
                uma delas — evita perder o dado ao salvar. */}
            {!['UN','KG','L','M','M2','PC','CX','DZ','PAR','FRD','KIT','JG','CJ'].includes((form.fiscal?.unit_commercial || 'UN').toUpperCase()) && (
              <optgroup label="Legado (produto ja cadastrado)">
                <option value={(form.fiscal?.unit_commercial || '').toUpperCase()}>
                  {(form.fiscal?.unit_commercial || '').toUpperCase()} — Unidade legada (recomendo migrar)
                </option>
              </optgroup>
            )}
          </select>
          <p className="text-[10px] text-slate-400 mt-0.5">
            <b>UN</b> avulso · <b>KG</b> peso · <b>L</b> volume · <b>M</b> comprimento · <b>M2</b> área. Para quantidades menores, use decimais (ex: 0.5 KG = 500g, 0.2 L = 200ml).
          </p>
        </div>
      </div>
      <div className="mb-4" data-testid="product-images-field">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-semibold text-gray-500">Imagens do Produto</label>
          <span className="text-[10px] text-gray-400">{form.images.length} {form.images.length === 1 ? 'imagem' : 'imagens'}</span>
        </div>
        <p className="text-[11px] text-gray-500 mb-2">JPG, PNG ou WebP • Máx 5MB cada • Recomendado <strong>1000×1000px</strong> (quadrada). Use a primeira imagem como principal.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {form.images.map((img, i) => (
            <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-50" data-testid="product-image-thumb">
              <img src={resolveImg(img)} className="w-full h-full object-cover" alt="" onError={e => { e.target.style.display = 'none'; }} />
              {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-teal-600 text-white text-[9px] font-bold text-center py-0.5">PRINCIPAL</span>}
              <button type="button" onClick={() => setForm(p => ({ ...p, images: p.images.filter((_, j) => j !== i) }))} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center shadow" data-testid="remove-product-image"><X size={10} /></button>
            </div>
          ))}
          <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center cursor-pointer hover:border-teal-400 transition-colors" data-testid="upload-product-image-label">
            <Plus size={16} className="text-gray-400" />
            <span className="text-[9px] text-gray-500 mt-1">{uploading ? 'Enviando...' : 'Upload'}</span>
            <input type="file" accept="image/*" multiple onChange={e => uploadImages(e.target.files)} className="hidden" data-testid="product-image-input" disabled={uploading} />
          </label>
        </div>
        <div className="flex gap-2">
          <input value={imageUrl} onChange={e => setImageUrl(e.target.value)} className={inputClass} placeholder="Ou cole uma URL de imagem" data-testid="product-image-url" />
          <button type="button" onClick={addImage} className="px-3 py-2 bg-gray-100 rounded-lg text-xs font-semibold text-gray-600" data-testid="add-product-image-url">+ URL</button>
        </div>
      </div>
      </div>{/* close basic tab */}

      {/* ============ FISCAL TAB (enxuta) ============ */}
      <div className={activeTab === 'fiscal' ? '' : 'hidden'} data-testid="product-tab-fiscal-content">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1 justify-between">
              <span>NCM</span>
              <button type="button" onClick={async () => {
                if (!form.name.trim()) { toast.error('Preencha o nome do produto primeiro'); return; }
                const cat = categories.find(c => c.id === form.category_id);
                toast.loading('Consultando IA para sugerir NCM...', { id: 'ncm-ai' });
                try {
                  const { data } = await axios.post(`${API}/erp/ncm/suggest`, {
                    name: form.name, description: form.description, category: cat?.name || '',
                  }, { withCredentials: true, timeout: 30000 });
                  toast.dismiss('ncm-ai');
                  const cands = data?.candidates || [];
                  if (!cands.length) return toast.error('IA nao retornou candidatos. Tente preencher mais detalhes.');
                  // Pick best (highest confidence) and apply; offer alternatives in toast
                  const top = cands[0];
                  updFiscal({ ncm: top.ncm });
                  const alt = cands.slice(1).map(c => `${c.ncm} (${Math.round(c.confidence*100)}%)`).join(', ');
                  toast.success(`NCM sugerido: ${top.ncm} - ${top.description.slice(0,60)} (${Math.round(top.confidence*100)}%)${alt ? `\nAlternativas: ${alt}` : ''}`, { duration: 8000 });
                } catch (e) {
                  toast.dismiss('ncm-ai');
                  toast.error(e.response?.data?.detail || 'Erro ao sugerir NCM');
                }
              }} className="text-[10px] font-bold text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-300 rounded-full px-2 py-0.5 flex items-center gap-1" title="Sugerir NCM via IA com base no nome/categoria" data-testid="fiscal-ncm-ai">
                ✨ IA Sugerir
              </button>
            </label>
            <input value={form.fiscal?.ncm || ''} onChange={e => updFiscal({ ncm: e.target.value.replace(/[^0-9]/g,'').slice(0,8) })} className={inputClass} placeholder="0000.00.00" inputMode="numeric" data-testid="fiscal-ncm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">CEST</label>
            <input value={form.fiscal?.cest || ''} onChange={e => updFiscal({ cest: e.target.value.replace(/[^0-9]/g,'').slice(0,7) })} className={inputClass} placeholder="00.000.00" inputMode="numeric" data-testid="fiscal-cest" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Origem da mercadoria</label>
            <select value={form.fiscal?.origem ?? '0'} onChange={e => updFiscal({ origem: e.target.value })} className={inputClass} data-testid="fiscal-origem">
              <option value="0">0 - Nacional, exceto as indicadas nos codigos 3,4,5,8</option>
              <option value="1">1 - Estrangeira - Importacao direta, exceto codigo 6</option>
              <option value="2">2 - Estrangeira - Adquirida no mercado interno, exceto codigo 7</option>
              <option value="3">3 - Nacional, conteudo de importacao superior a 40% e ate 70%</option>
              <option value="4">4 - Nacional, processos basicos (DL 288, 356, 415 e Lei 8.248)</option>
              <option value="5">5 - Nacional, conteudo de importacao ate 40%</option>
              <option value="6">6 - Estrangeira - Importacao direta, sem similar nacional</option>
              <option value="7">7 - Estrangeira - Mercado interno, sem similar nacional</option>
              <option value="8">8 - Nacional, conteudo de importacao superior a 70%</option>
            </select>
          </div>
        </div>
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg px-4 py-3 text-sm text-amber-900" data-testid="fiscal-warning">
          <strong>Atencao!</strong> As tributacoes do produto (ICMS, IPI, PIS, COFINS, CFOP) sao inseridas em <strong>Administracao &gt; Config. de NF &gt; Natureza de Operacao</strong>.
        </div>
      </div>

      {/* ============ FARMACIA TAB (condicional ao BT pharmacy_module) ============ */}
      {isPharmacy && (
      <div className={activeTab === 'pharmacy' ? '' : 'hidden'} data-testid="product-tab-pharmacy-content">
        <div className="bg-emerald-50 border-l-4 border-emerald-500 px-3 py-2 mb-4 text-[11px] text-emerald-900 rounded-r">
          <strong>Modulo Farmacia ativado.</strong> Campos especificos de medicamentos, farmacos e correlatos. Validar via portal Anvisa antes de comercializar.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Principio Ativo</label>
            <input value={form.pharmacy?.active_ingredient || ''} onChange={e => updPharma({ active_ingredient: e.target.value })} className={inputClass} placeholder="Ex: Paracetamol 500mg" data-testid="ph-active-ingredient" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Registro ANVISA</label>
            <input value={form.pharmacy?.anvisa_register || ''} onChange={e => updPharma({ anvisa_register: e.target.value })} className={inputClass} placeholder="1.0000.0000.000-0" data-testid="ph-anvisa" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Registro MS (correlatos)</label>
            <input value={form.pharmacy?.ms_register || ''} onChange={e => updPharma({ ms_register: e.target.value })} className={inputClass} data-testid="ph-ms-register" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Tarja</label>
            <select value={form.pharmacy?.tarja || 'livre'} onChange={e => updPharma({ tarja: e.target.value })} className={inputClass} data-testid="ph-tarja">
              <option value="livre">Livre (sem tarja)</option>
              <option value="amarela">Tarja Amarela (Antibiotico)</option>
              <option value="vermelha">Tarja Vermelha (Receita Branca)</option>
              <option value="preta">Tarja Preta (Receita Especial/Notificada)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Tipo</label>
            <select value={form.pharmacy?.med_type || 'similar'} onChange={e => updPharma({ med_type: e.target.value, generic: e.target.value === 'generico', similar: e.target.value === 'similar' })} className={inputClass} data-testid="ph-type">
              <option value="referencia">Referencia</option>
              <option value="generico">Generico</option>
              <option value="similar">Similar</option>
              <option value="correlato">Correlato</option>
              <option value="cosmetico">Cosmetico</option>
              <option value="suplemento">Suplemento</option>
              <option value="manipulado">Manipulado</option>
              <option value="outro">Outro</option>
            </select>
          </div>
          <div className="md:col-span-2 border-t border-slate-200 pt-3 mt-1">
            <p className="text-[11px] font-black uppercase tracking-wider text-rose-600 mb-2">Medicamento Controlado (Portaria 344/98)</p>
          </div>
          <label className="flex items-center gap-2 p-2.5 bg-rose-50 border border-rose-200 rounded-lg cursor-pointer">
            <input type="checkbox" checked={!!form.pharmacy?.controlled} onChange={e => updPharma({ controlled: e.target.checked })} className="w-4 h-4 accent-rose-600" data-testid="ph-controlled" />
            <div>
              <span className="text-sm font-bold text-rose-900">E Controlado</span>
              <p className="text-[10px] text-rose-700">Exigira receita + escrituracao SNGPC</p>
            </div>
          </label>
          {form.pharmacy?.controlled && (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Lista (Portaria 344)</label>
                <select value={form.pharmacy?.controlled_list || ''} onChange={e => updPharma({ controlled_list: e.target.value })} className={inputClass} data-testid="ph-controlled-list">
                  <option value="">Selecione</option>
                  <option value="A1">A1 — Entorpecente</option>
                  <option value="A2">A2 — Entorpecente</option>
                  <option value="A3">A3 — Psicotropico</option>
                  <option value="B1">B1 — Psicotropico</option>
                  <option value="B2">B2 — Psicotropico anorexigeno</option>
                  <option value="C1">C1 — Outras sub. sujeitas a controle especial</option>
                  <option value="C2">C2 — Retinoide sistemico</option>
                  <option value="C3">C3 — Imunossupressor (talidomida)</option>
                  <option value="C4">C4 — Antiretroviral</option>
                  <option value="C5">C5 — Anabolizante</option>
                  <option value="D1">D1 — Precursor de entorpecente</option>
                  <option value="D2">D2 — Insumo quimico</option>
                  <option value="E">E — Plantas que podem originar substancias entorpecentes</option>
                  <option value="F">F — Substancias de uso proscrito</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Livro de Registro</label>
                <select value={form.pharmacy?.controlled_book || 'azul'} onChange={e => updPharma({ controlled_book: e.target.value })} className={inputClass} data-testid="ph-controlled-book">
                  <option value="amarelo">Amarelo (A1, A2, A3, B1, B2)</option>
                  <option value="azul">Azul (C1, C2, C3, C4, C5)</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
            </>
          )}
          <label className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer md:col-span-1">
            <input type="checkbox" checked={!!form.pharmacy?.needs_prescription} onChange={e => updPharma({ needs_prescription: e.target.checked })} className="w-4 h-4 accent-amber-600" data-testid="ph-needs-prescription" />
            <span className="text-sm font-bold text-amber-900">Exige Receita</span>
          </label>
          <label className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer md:col-span-1">
            <input type="checkbox" checked={!!form.pharmacy?.prescription_retention} onChange={e => updPharma({ prescription_retention: e.target.checked })} className="w-4 h-4 accent-amber-600" data-testid="ph-prescription-retention" />
            <span className="text-sm font-bold text-amber-900">Receita Retida</span>
          </label>

          <div className="md:col-span-2 border-t border-slate-200 pt-3 mt-1">
            <p className="text-[11px] font-black uppercase tracking-wider text-blue-600 mb-2">Composicao e Preco CMED</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Fabricante / Laboratorio</label>
            <input value={form.pharmacy?.manufacturer || ''} onChange={e => updPharma({ manufacturer: e.target.value })} className={inputClass} data-testid="ph-manufacturer" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Apresentacao</label>
            <input value={form.pharmacy?.presentation || ''} onChange={e => updPharma({ presentation: e.target.value })} className={inputClass} placeholder="Ex: Comp. 500mg c/ 20" data-testid="ph-presentation" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Classe Terapeutica</label>
            <input value={form.pharmacy?.therapeutic_class || ''} onChange={e => updPharma({ therapeutic_class: e.target.value })} className={inputClass} placeholder="Ex: Analgesico" data-testid="ph-therapeutic-class" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Codigo ATC</label>
            <input value={form.pharmacy?.atc_code || ''} onChange={e => updPharma({ atc_code: e.target.value })} className={inputClass} placeholder="N02BE01" data-testid="ph-atc" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Preco Maximo Consumidor (PMC)</label>
            <CurrencyInput value={form.pharmacy?.cmed_pmc ?? 0} onChange={v => updPharma({ cmed_pmc: v })} className={currencyInput} data-testid="ph-cmed-pmc" />
            <p className="text-[10px] text-gray-400 mt-0.5">Preco maximo regulamentado pela CMED.</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Preco de Fabrica (PF)</label>
            <CurrencyInput value={form.pharmacy?.cmed_pf ?? 0} onChange={v => updPharma({ cmed_pf: v })} className={currencyInput} data-testid="ph-cmed-pf" />
          </div>
          <div className="md:col-span-2 border-t border-slate-200 pt-3 mt-1">
            <label className="flex items-center gap-2 p-2.5 bg-cyan-50 border border-cyan-200 rounded-lg cursor-pointer">
              <input type="checkbox" checked={!!form.lot_control} onChange={e => setForm(p => ({ ...p, lot_control: e.target.checked }))} className="w-4 h-4 accent-cyan-600" data-testid="ph-lot-control" />
              <div>
                <span className="text-sm font-bold text-cyan-900">Controle de Lote e Validade</span>
                <p className="text-[10px] text-cyan-700">Cada entrada/saida exigira informar lote + data de validade (rastreabilidade).</p>
              </div>
            </label>
          </div>
          {form.lot_control && <PharmacyLotsManager productId={product?.id} />}
        </div>
      </div>
      )}

      {/* ============ OUTROS TAB ============ */}
      {/* iter77di: aba Variacoes — cadastro de atributos (Tamanho, Cor, etc) + matriz de variantes */}
      <div className={activeTab === 'variacoes' ? '' : 'hidden'} data-testid="product-tab-variacoes-content">
        <VariantsPanel form={form} setForm={setForm} inputClass={inputClass} />
      </div>

      {/* iter156aq: Aba Producao — BOM (materia-prima -> acabado). So aparece se productionEnabled=true */}
      {productionEnabled && (
        <div className={activeTab === 'producao' ? '' : 'hidden'} data-testid="product-tab-producao-content">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isManufactured} onChange={e => setIsManufactured(e.target.checked)} className="w-4 h-4 accent-emerald-600" data-testid="product-is-manufactured" />
              <span className="text-sm font-bold text-slate-900">Este produto e um <strong>Produto Acabado</strong> (produzido a partir de outras materias-primas)</span>
            </label>
            <p className="text-[11px] text-slate-500 mt-1 ml-6">Quando marcado, voce podera criar uma <strong>Ordem de Producao</strong> em Itens &gt; Ordens de Producao. Ao produzir: as MPs abaixo sao baixadas do estoque e a quantidade produzida do acabado e adicionada.</p>
          </div>
          {isManufactured && (
            <>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-bold text-slate-700">Materias-primas (BOM) — por 1 unidade de acabado</h4>
                <button type="button" onClick={addBomItem} className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold" data-testid="bom-add-btn">+ MP</button>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200"><tr>
                    <th className="px-2 py-2 text-left font-bold text-slate-500">Materia-prima</th>
                    <th className="px-2 py-2 text-right font-bold text-slate-500 w-24">Qtd</th>
                    <th className="px-2 py-2 text-center font-bold text-slate-500 w-20">Unid.</th>
                    <th className="px-2 py-2 w-10"></th>
                  </tr></thead>
                  <tbody>
                    {bom.map((it, i) => {
                      // iter156dg: unidade de estoque da MP (definida pelo backend via /bom).
                      // Se ausente (novo item ou legado), pega da lista carregada.
                      const mpFromList = allProducts.find(p => p.id === it.product_id);
                      const mpStockUnit = (it.mp_stock_unit
                        || (mpFromList?.fiscal?.unit_commercial)
                        || mpFromList?.unit
                        || 'UN').toUpperCase();
                      // Familia de unidades compativel (mesmo grupo de conversao)
                      const UNIT_FAMILIES = {
                        peso: ['MG', 'G', 'KG', 'TON'],
                        volume: ['ML', 'L', 'M3'],
                        comprimento: ['CM', 'M'],
                      };
                      const family = Object.entries(UNIT_FAMILIES).find(([, arr]) => arr.includes(mpStockUnit))?.[1] || [mpStockUnit];
                      // Conversao para preview em tempo real
                      const FACTORS = { MG: 0.001, G: 1, KG: 1000, TON: 1000000, ML: 1, L: 1000, M3: 1000000, CM: 1, M: 100 };
                      const bomUnitU = (it.unit || 'UN').toUpperCase();
                      const bomQty = parseFloat(it.qty) || 0;
                      let previewQty = null;
                      if (bomUnitU !== mpStockUnit && FACTORS[bomUnitU] && FACTORS[mpStockUnit]) {
                        previewQty = (bomQty * FACTORS[bomUnitU]) / FACTORS[mpStockUnit];
                      }
                      return (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-2 py-2">
                            {/* iter156dg: combobox com busca — substitui <select> ineficiente com muitos produtos */}
                            <BomProductCombobox
                              value={it.product_id}
                              products={allProducts}
                              onPick={(p) => {
                                const stockU = ((p?.fiscal?.unit_commercial) || p?.unit || 'UN').toUpperCase();
                                updBomItem(i, {
                                  product_id: p.id,
                                  product_name: p.name,
                                  product_sku: p.sku,
                                  mp_stock_unit: stockU,
                                  // Auto-preenche BOM unit com a unidade do estoque da MP.
                                  unit: stockU,
                                });
                              }}
                              testId={`bom-product-${i}`}
                            />
                          </td>
                          <td className="px-2 py-2">
                            {/* iter156dt2: input BOM qty com mascara pt-BR quando unit=KG/L/M */}
                            <input type="text" inputMode="decimal"
                              value={(() => {
                                const _u = (it.unit || 'UN').toUpperCase();
                                const _dec = decimalsForUnit(_u);
                                if (_dec === 0) return String(it.qty ?? '');
                                const n = Number(it.qty) || 0;
                                return n === 0 ? '' : n.toLocaleString('pt-BR', { minimumFractionDigits: _dec, maximumFractionDigits: _dec });
                              })()}
                              onChange={e => {
                                const _u = (it.unit || 'UN').toUpperCase();
                                const masked = smartQtyMask(e.target.value, _u);
                                const val = masked === '' ? 0 : smartQtyParse(masked);
                                updBomItem(i, { qty: isFinite(val) ? val : 0 });
                              }}
                              onFocus={e => e.target.select()}
                              className="w-full px-2 py-1 border border-slate-200 rounded text-xs text-right"
                              data-testid={`bom-qty-${i}`}
                            />
                          </td>
                          <td className="px-2 py-2">
                            {/* iter156dg: dropdown de unidade — restrito a familia da unidade de estoque da MP */}
                            <select
                              value={bomUnitU}
                              onChange={e => updBomItem(i, { unit: e.target.value })}
                              className="w-full px-1 py-1 border border-slate-200 rounded text-xs text-center bg-white"
                              disabled={!it.product_id}
                              data-testid={`bom-unit-${i}`}
                              title={`Estoque da MP em ${mpStockUnit}`}
                            >
                              {family.map(u => (
                                <option key={u} value={u}>{u}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-2 text-center">
                            <button type="button" onClick={() => rmBomItem(i)} className="text-rose-500 hover:text-rose-700" data-testid={`bom-remove-${i}`}>
                              <Trash2 size={14} />
                            </button>
                          </td>
                          {/* Linha extra de preview quando ha conversao aplicada */}
                          {previewQty !== null && it.product_id && (
                            <td colSpan={4} className="px-2 pb-2 -mt-1">
                              <div className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-block" data-testid={`bom-preview-${i}`}>
                                Baixa no estoque: <b>{previewQty.toLocaleString('pt-BR', { maximumFractionDigits: 4 })} {mpStockUnit}</b> por unidade produzida
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {bom.length === 0 && (<tr><td colSpan={4} className="text-center py-6 text-slate-400 text-xs">Nenhuma materia-prima ainda. Clique em &ldquo;+ MP&rdquo; para adicionar.</td></tr>)}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-slate-500 mt-2">A BOM sera salva junto com o produto quando voce clicar em Salvar.</p>
            </>
          )}
        </div>
      )}

      <div className={activeTab === 'outros' ? '' : 'hidden'} data-testid="product-tab-outros-content">
        {/* iter77dd: campos secundarios do produto — os toggleaveis no catalogo publico ficam aqui.
            Foram movidos da aba Basico e novos foram criados (grade, box_quantity, box_price, description, marca).
            Ao habilitar o toggle correspondente em Config > Catalogo, o campo aparece no card do produto. */}
        <div className="mb-3 text-[11px] text-slate-600 bg-cyan-50/60 border border-cyan-200 rounded-lg px-3 py-2">
          <strong>Dica:</strong> os campos abaixo aparecem no <strong>catalogo publico</strong> quando habilitados em <em>Config &gt; Catalogo (Campos visiveis no site)</em>. Preencha para exibir aos clientes.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Descricao</label>
            <textarea value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} className={`${inputClass} resize-y`} placeholder="Descricao detalhada do produto exibida no catalogo" data-testid="outros-description" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Marca</label>
            <input value={form.brand || ''} onChange={e => setForm(p => ({ ...p, brand: e.target.value }))} className={inputClass} placeholder="Ex: CACAU KIDS" data-testid="outros-brand" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Grade</label>
            <input value={form.grade || ''} onChange={e => setForm(p => ({ ...p, grade: e.target.value }))} className={inputClass} placeholder="Ex: 01/03 anos, P/M/G, 36/44..." data-testid="outros-grade" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Peso (kg)</label>
            <input type="number" step="0.01" value={form.weight || ''} onChange={e => setForm(p => ({ ...p, weight: e.target.value }))} className={inputClass} placeholder="0.00" inputMode="decimal" data-testid="outros-weight" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Referencia</label>
            <input value={form.reference || ''} onChange={e => setForm(p => ({ ...p, reference: e.target.value }))} className={inputClass} placeholder="Ex: codigo do fornecedor" data-testid="outros-reference" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Qtd no Kit</label>
            <input type="number" min="0" step="1" value={form.box_quantity || ''} onChange={e => setForm(p => ({ ...p, box_quantity: e.target.value }))} className={inputClass} placeholder="Ex: 24 (pecas por kit)" inputMode="numeric" data-testid="outros-box-quantity" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Valor por item (unitario)</label>
            <CurrencyInput
              value={form.box_price === '' ? 0 : Number(form.box_price || 0)}
              onChange={v => setForm(f => ({ ...f, box_price: v }))}
              placeholder="0,00"
              className="w-full"
              dataTestId="outros-box-price"
            />
            <p className="text-[10px] text-gray-400 mt-0.5">Usado em produtos vendidos como kit — preco unitario de cada peca dentro do kit. Aparece no catalogo junto com o Valor principal.</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Video URL (YouTube)</label>
            <input value={form.video_url || ''} onChange={e => setForm(p => ({ ...p, video_url: e.target.value }))} className={inputClass} placeholder="https://youtu.be/..." data-testid="product-video-url" />
            <p className="text-[10px] text-gray-400 mt-0.5">Exibe um video no catalogo publico (se preenchido).</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Peso Liquido (kg) <span className="text-[9px] text-slate-400 font-normal">— NF-e</span></label>
            <input type="number" step="0.001" value={form.fiscal?.net_weight ?? ''} onChange={e => updFiscal({ net_weight: e.target.value })} className={inputClass} placeholder="0.000" data-testid="outros-net-weight" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Peso Bruto (kg) <span className="text-[9px] text-slate-400 font-normal">— NF-e</span></label>
            <input type="number" step="0.001" value={form.fiscal?.gross_weight ?? ''} onChange={e => updFiscal({ gross_weight: e.target.value })} className={inputClass} placeholder="0.000" data-testid="outros-gross-weight" />
          </div>
        </div>
      </div>
      </div>{/* close p-5 wrapper */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-2.5 flex justify-end gap-2 z-10">
        <button type="button" onClick={onClose} className="px-4 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">Cancelar</button>
        <button type="submit" disabled={submitting || uploading} className={btnPrimary} data-testid="save-product-btn"><Save size={13} /><span>{submitting ? 'Salvando...' : product ? 'Atualizar' : 'Criar'}</span></button>
      </div>
      {showBarcodeScanner && (
        <QRScannerModal
          onScan={(text) => { setForm(p => ({ ...p, barcode: text })); setShowBarcodeScanner(false); toast.success('Codigo lido'); }}
          onClose={() => setShowBarcodeScanner(false)}
        />
      )}
      </form>
    </div>
  );
};

/* ========== CATEGORIES MANAGEMENT ========== */
const CategoriesManagement = ({ slug }) => {
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', parent_id: '' });
  const [loading, setLoading] = useState(true);
  const fetchCats = useCallback(async () => { try { const { data } = await axios.get(`${API}/categories`, { withCredentials: true }); setCategories(data.data || []); } catch { /* ignore */ } finally { setLoading(false); } }, []);
  useEffect(() => { fetchCats(); }, [fetchCats]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      if (editing) { await axios.put(`${API}/categories/${editing.id}`, payload, { withCredentials: true }); toast.success('Atualizada!'); }
      else { await axios.post(`${API}/categories`, payload, { withCredentials: true }); toast.success('Criada!'); }
      setShowForm(false); setEditing(null); setForm({ name: '', slug: '', parent_id: '' }); fetchCats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };
  const handleDelete = async (id) => { if (!(await confirmDialog({ title: 'Excluir?', confirmLabel: 'Confirmar', danger: true }))) return; try { await axios.delete(`${API}/categories/${id}`, { withCredentials: true }); toast.success('Excluida'); fetchCats(); } catch { toast.error('Erro'); } };
  // iter77bt: separar categorias-pai (sem parent) das subcategorias (com parent)
  const parents = categories.filter(c => !c.parent_id);
  const subsByParent = categories.reduce((acc, c) => {
    if (c.parent_id) { (acc[c.parent_id] = acc[c.parent_id] || []).push(c); }
    return acc;
  }, {});
  if (loading) return <Loader />;
  return (
    <div data-testid="categories-page">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">{categories.length} categorias ({parents.length} principais)</p>
        <button onClick={() => { setEditing(null); setForm({ name: '', slug: '', parent_id: '' }); setShowForm(true); }} className={btnPrimary} data-testid="add-category-btn"><Plus size={16} /><span>Nova</span></button>
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-teal-200 rounded-xl p-5 mb-5" data-testid="category-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Nome *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value, slug: editing ? form.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '-') })} className={inputClass} required data-testid="category-name" /></div>
            <div><label className="block text-xs font-semibold text-gray-500 mb-1">Slug</label><input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className={inputClass} /></div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Categoria pai (opcional)</label>
              <select value={form.parent_id || ''} onChange={e => setForm({ ...form, parent_id: e.target.value })} className={inputClass} data-testid="category-parent">
                <option value="">— Nenhuma (categoria principal) —</option>
                {parents.filter(p => p.id !== editing?.id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">Se selecionar, esta virara subcategoria.</p>
            </div>
          </div>
          <div className="flex gap-2"><button type="submit" className={btnPrimary}><Save size={14} /><span>{editing ? 'Atualizar' : 'Criar'}</span></button><button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2.5 text-sm text-gray-500">Cancelar</button></div>
        </form>
      )}
      <div className="space-y-2">
        {parents.map(c => (
          <div key={c.id} data-testid="category-row">
            <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm text-gray-900">{c.name}</p>
                <p className="text-xs text-gray-500 font-mono">{c.slug}</p>
                {subsByParent[c.id]?.length > 0 && <span className="text-[10px] font-bold text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded">{subsByParent[c.id].length} subs</span>}
              </div>
              <div className="flex items-center space-x-1">
                <button onClick={() => { setEditing(c); setForm({ name: c.name, slug: c.slug, parent_id: c.parent_id || '' }); setShowForm(true); }} className="p-2 text-gray-400 hover:text-teal-600"><Edit size={14} /></button>
                <button onClick={() => handleDelete(c.id)} className="p-2 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            </div>
            {subsByParent[c.id]?.length > 0 && (
              <div className="ml-6 mt-1 space-y-1">
                {subsByParent[c.id].map(sub => (
                  <div key={sub.id} className="bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 flex items-center justify-between" data-testid="subcategory-row">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-300">↳</span>
                      <p className="text-xs font-medium text-gray-800">{sub.name}</p>
                      <p className="text-[10px] text-gray-400 font-mono">{sub.slug}</p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button onClick={() => { setEditing(sub); setForm({ name: sub.name, slug: sub.slug, parent_id: sub.parent_id || '' }); setShowForm(true); }} className="p-1 text-gray-400 hover:text-teal-600"><Edit size={12} /></button>
                      <button onClick={() => handleDelete(sub.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {categories.length === 0 && <EmptyState icon={FileText} text="Nenhuma categoria" />}
      </div>
    </div>
  );
};

/* ========== ORDERS MANAGEMENT ========== */
const OrdersManagement = ({ slug }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { const f = async () => { try { const { data } = await axios.get(`${API}/orders`, { withCredentials: true }); setOrders(data.data || []); } catch {} finally { setLoading(false); } }; f(); }, []);
  if (loading) return <Loader />;
  return (
    <div data-testid="orders-page">
      <p className="text-sm text-gray-500 mb-5">{orders.length} pedidos</p>
      <div className="space-y-2">
        {orders.map(o => (
          <div key={o.id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid="order-row">
            <div className="flex items-center justify-between">
              <div><p className="font-medium text-sm text-gray-900">Pedido #{o.id?.slice(-6)}</p><p className="text-xs text-gray-500">{o.created_at ? new Date(o.created_at).toLocaleDateString('pt-BR') : ''} • {o.items?.length || 0} itens</p></div>
              <span className="font-bold text-sm text-gray-900">R$ {(o.total || 0).toFixed(2)}</span>
            </div>
          </div>
        ))}
        {orders.length === 0 && <EmptyState icon={ShoppingCart} text="Nenhum pedido" />}
      </div>
    </div>
  );
};

/* ========== SHARED ========== */
// ============================================================================
// iter156aq: PONTO ELETRONICO — registro de jornada dos funcionarios
// ============================================================================
const PontoPage = ({ companyId }) => {
  const [tab, setTab] = useState('bater'); // 'bater' | 'relatorio'
  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <UserCheck size={22} className="text-teal-600" />
        <h1 className="text-xl font-black text-slate-900">Ponto Eletronico</h1>
      </div>
      <div className="flex gap-2 mb-4 border-b border-slate-200">
        <button onClick={() => setTab('bater')} className={`px-4 py-2 text-sm font-bold ${tab==='bater'?'text-teal-700 border-b-2 border-teal-600':'text-slate-500'}`} data-testid="ponto-tab-bater">Bater Ponto</button>
        <button onClick={() => setTab('relatorio')} className={`px-4 py-2 text-sm font-bold ${tab==='relatorio'?'text-teal-700 border-b-2 border-teal-600':'text-slate-500'}`} data-testid="ponto-tab-relatorio">Relatorio</button>
      </div>
      {tab === 'bater' ? <PontoBater companyId={companyId} /> : <PontoRelatorio companyId={companyId} />}
    </div>
  );
};

const PontoBater = ({ companyId }) => {
  const [today, setToday] = useState({ data: [], next_type: 'entrada' });
  const load = async () => {
    try {
      const { data } = await axios.get(`${ERP}/time-punches/today`, { withCredentials: true });
      setToday(data);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  // iter156ey: fluxo unificado com alerta de sequencia/duplicidade e resumo do dia
  const ponto = usePontoFlow(companyId, 'web', null, load);
  const saving = ponto.busy;
  const punch = (punch_type) => ponto.start(punch_type);

  const LABELS = PONTO_LABELS;
  const COLORS = PONTO_COLORS;
  return (
    <div>
      {ponto.modals}
      <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-2xl p-5 mb-5">
        <p className="text-xs uppercase tracking-wider text-teal-700 font-bold mb-1">Proximo registro sugerido</p>
        <p className="text-2xl font-black text-slate-900">{today.next_type ? LABELS[today.next_type] : 'Jornada completa hoje'}</p>
        <p className="text-xs text-slate-500 mt-1">{today.data.length} registro(s) hoje</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['entrada','almoco_ini','almoco_fim','saida'].map(t => (
          <button key={t} disabled={saving} onClick={() => punch(t)}
            className={`bg-gradient-to-br ${COLORS[t]} disabled:opacity-40 text-white p-5 rounded-2xl font-bold shadow-lg hover:scale-105 transition-transform`}
            data-testid={`ponto-btn-${t}`}
          >
            <div className="text-3xl mb-1">{t==='entrada'?'▶':t==='almoco_ini'?'☕':t==='almoco_fim'?'↩':'■'}</div>
            <div className="text-xs uppercase tracking-wider opacity-90">{LABELS[t]}</div>
          </button>
        ))}
      </div>
      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-3">Meus registros de hoje</h3>
        {today.data.length === 0 ? (
          <p className="text-xs text-slate-400">Nenhum ponto batido hoje.</p>
        ) : (
          <ul className="space-y-2">
            {today.data.map((p) => (
              <li key={p.id} className="flex items-center gap-3 text-sm">
                <span className="w-2 h-2 rounded-full bg-teal-500" />
                <span className="font-mono text-slate-500 text-xs">{new Date(p.ts).toLocaleTimeString('pt-BR')}</span>
                <span className="font-bold text-slate-800">{LABELS[p.punch_type]}</span>
                {p.lat && <span className="text-[10px] text-slate-400">📍</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const PontoRelatorio = ({ companyId }) => {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  // iter156bl: filtro por funcionario + lista de empregados. Chip periodo para modernizar UX.
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [periodChip, setPeriodChip] = useState('month'); // 'today'|'week'|'month'|'custom'
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState({ data: [], total_hours: 0, total_rows: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Empregados sao populados a partir dos proprios registros de ponto no periodo:
    // fica sempre em sincronia com quem realmente bateu ponto (inclusive users virtuais).
    axios.get(`${ERP}/employees`, { withCredentials: true }).then(({ data: d }) => {
      setEmployees(d.data || []);
    }).catch(() => setEmployees([]));
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (companyId) params.set('company_id', companyId);
      if (employeeFilter) params.set('employee_id', employeeFilter);
      const { data } = await axios.get(`${ERP}/time-punches/report?${params}`, { withCredentials: true });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao buscar relatorio'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, employeeFilter, companyId]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // iter156bl: lista de "funcionarios" unica derivada dos proprios rows do relatorio + de /employees
  const employeeOptions = React.useMemo(() => {
    const seen = new Set();
    const opts = [];
    (data.data || []).forEach(r => {
      const k = r.employee_id || r.employee_name;
      if (!seen.has(k)) { seen.add(k); opts.push({ id: r.employee_id, name: r.employee_name }); }
    });
    (employees || []).forEach(e => {
      if (!seen.has(e.id)) { seen.add(e.id); opts.push({ id: e.id, name: e.name || e.email }); }
    });
    return opts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [data.data, employees]);

  return (
    <div>
      {/* iter156bl: Filtros modernos e slim — chips + dropdown de funcionario em uma unica linha. */}
      <div className="bg-white/70 backdrop-blur border border-slate-200 rounded-2xl p-3 mb-4 flex flex-wrap items-center gap-2 shadow-sm">
        {[
          { key: 'today', label: 'Hoje' },
          { key: 'week', label: 'Esta Semana' },
          { key: 'month', label: 'Este Mes' },
          { key: 'custom', label: 'Personalizado' },
        ].map(chip => {
          const active = periodChip === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => {
                setPeriodChip(chip.key);
                const t = new Date();
                if (chip.key === 'today') {
                  const iso = t.toISOString().slice(0, 10);
                  setDateFrom(iso); setDateTo(iso);
                } else if (chip.key === 'week') {
                  const d = new Date(t); d.setDate(d.getDate() - d.getDay());
                  setDateFrom(d.toISOString().slice(0, 10));
                  setDateTo(t.toISOString().slice(0, 10));
                } else if (chip.key === 'month') {
                  const d = new Date(t.getFullYear(), t.getMonth(), 1);
                  setDateFrom(d.toISOString().slice(0, 10));
                  setDateTo(t.toISOString().slice(0, 10));
                }
              }}
              className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider transition-all ${active
                ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-md shadow-teal-500/30'
                : 'bg-white border border-slate-200 text-slate-600 hover:border-teal-300 hover:text-teal-700'}`}
              data-testid={`ponto-report-chip-${chip.key}`}
            >
              {chip.label}
            </button>
          );
        })}
        {periodChip === 'custom' && (
          <div className="flex items-center gap-1.5 ml-1">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-2.5 py-1 text-xs border border-slate-200 rounded-full bg-white focus:border-teal-400 outline-none" data-testid="ponto-report-from" />
            <span className="text-[10px] text-slate-400">ate</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-2.5 py-1 text-xs border border-slate-200 rounded-full bg-white focus:border-teal-400 outline-none" data-testid="ponto-report-to" />
          </div>
        )}
        <div className="flex items-center gap-1.5 ml-auto">
          <UserCheck size={13} className="text-slate-400" />
          <select value={employeeFilter} onChange={e => setEmployeeFilter(e.target.value)} className="px-2.5 py-1 text-xs border border-slate-200 rounded-full bg-white focus:border-teal-400 outline-none min-w-[140px]" data-testid="ponto-report-employee">
            <option value="">Todos os funcionarios</option>
            {employeeOptions.map(e => <option key={e.id || e.name} value={e.id || ''}>{e.name}</option>)}
          </select>
          <button onClick={load} disabled={loading} className="px-3.5 py-1.5 rounded-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-[11px] font-black uppercase tracking-wider disabled:opacity-50 shadow-md" data-testid="ponto-report-load">
            {loading ? '...' : 'Buscar'}
          </button>
        </div>
      </div>
      <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-xl p-4 mb-4">
        <p className="text-xs uppercase tracking-wider text-teal-700 font-bold">Total no periodo</p>
        <p className="text-3xl font-black text-slate-900">{data.total_hours} <span className="text-lg font-normal text-slate-500">horas</span></p>
        <p className="text-xs text-slate-500 mt-1">{data.total_rows} dia(s) com registros</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            <th className="px-3 py-2 text-left font-bold text-slate-500">Funcionario</th>
            <th className="px-3 py-2 text-left font-bold text-slate-500">Dia</th>
            <th className="px-3 py-2 text-left font-bold text-slate-500">Entrada</th>
            {/* iter156bl: novas colunas Saida do Intervalo e Chegada do Intervalo */}
            <th className="px-3 py-2 text-left font-bold text-slate-500">Saida Intervalo</th>
            <th className="px-3 py-2 text-left font-bold text-slate-500">Chegada Intervalo</th>
            <th className="px-3 py-2 text-left font-bold text-slate-500">Saida</th>
            <th className="px-3 py-2 text-right font-bold text-slate-500">Horas</th>
            <th className="px-3 py-2 text-center font-bold text-slate-500">Registros</th>
          </tr></thead>
          <tbody>
            {data.data.map((r, i) => (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-bold text-slate-900">{r.employee_name}</td>
                <td className="px-3 py-2 text-slate-600">{r.day}</td>
                <td className="px-3 py-2 text-slate-600">{r.first_entrada ? new Date(r.first_entrada).toLocaleTimeString('pt-BR') : '—'}</td>
                <td className="px-3 py-2 text-amber-700">{r.first_almoco_ini ? new Date(r.first_almoco_ini).toLocaleTimeString('pt-BR') : '—'}</td>
                <td className="px-3 py-2 text-orange-700">{r.last_almoco_fim ? new Date(r.last_almoco_fim).toLocaleTimeString('pt-BR') : '—'}</td>
                <td className="px-3 py-2 text-slate-600">{r.last_saida ? new Date(r.last_saida).toLocaleTimeString('pt-BR') : (r.still_open ? <span className="text-amber-600 font-bold">EM ABERTO</span> : '—')}</td>
                <td className="px-3 py-2 text-right font-bold text-slate-900">{r.worked_hours.toFixed(2)}h</td>
                <td className="px-3 py-2 text-center text-slate-500">{r.punches_count}</td>
              </tr>
            ))}
            {data.data.length === 0 && (<tr><td colSpan={8} className="text-center py-8 text-slate-400 text-xs">Sem registros no periodo</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// iter156aq: ORDENS DE PRODUCAO — historico e criacao de OPs
// ============================================================================
const ProductionOrdersPage = ({ companyId, companies }) => {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'new' | 'edit' | 'view'
  const [modalOrder, setModalOrder] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (companyId) p.set('company_id', companyId);
      const { data } = await axios.get(`${ERP}/production-orders?${p}`, { withCredentials: true });
      setRows(data.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao carregar ordens'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const openNew = () => { setModalOrder(null); setModalMode('new'); };
  const openEdit = (r) => { setModalOrder(r); setModalMode('edit'); };
  const openView = (r) => { setModalOrder(r); setModalMode('view'); };
  const closeModal = () => { setModalMode(null); setModalOrder(null); };

  // iter156et: finalizar informando a quantidade realmente produzida (diferenca = perda)
  const [finalizeTarget, setFinalizeTarget] = useState(null);
  const finalizeOrder = (r) => setFinalizeTarget(r);
  const fmtQty = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
  const cancelOrder = async (r) => {
    if (!window.confirm(`Cancelar ordem de ${r.product_name}? Esta acao remove a ordem em producao.`)) return;
    try {
      await axios.delete(`${ERP}/production-orders/${r.id}`, { withCredentials: true });
      toast.success('Ordem cancelada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao cancelar'); }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package size={22} className="text-indigo-600" />
          <h1 className="text-xl font-black text-slate-900">Ordens de Producao</h1>
        </div>
        <button onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2" data-testid="production-new-btn">
          <Package size={14} /> Nova Producao
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-end gap-3">
        <div><label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">De</label><input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm" /></div>
        <div><label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Ate</label><input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm" /></div>
        <button onClick={load} disabled={loading} className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg text-sm font-bold disabled:opacity-50">{loading?'Carregando...':'Buscar'}</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-xs min-w-[720px]">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            <th className="px-3 py-2 text-left font-bold text-slate-500">Data</th>
            <th className="px-3 py-2 text-left font-bold text-slate-500">Produto</th>
            <th className="px-3 py-2 text-right font-bold text-slate-500">Qtd a Produzir</th>
            <th className="px-3 py-2 text-right font-bold text-slate-500">Qtd Produzida</th>
            <th className="px-3 py-2 text-right font-bold text-slate-500">Perda</th>
            <th className="px-3 py-2 text-right font-bold text-slate-500">Custo Total</th>
            <th className="px-3 py-2 text-right font-bold text-slate-500">Custo Unit.</th>
            <th className="px-3 py-2 text-center font-bold text-slate-500">Status</th>
            <th className="px-3 py-2 text-center font-bold text-slate-500 w-40">Acoes</th>
          </tr></thead>
          <tbody>
            {rows.map(r => {
              const st = r.status || 'em_producao';
              const isInProd = st === 'em_producao';
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`prod-row-${r.id}`}>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{new Date(r.produced_at).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2 font-bold text-slate-900">{r.product_name} <span className="text-slate-400 font-normal">({r.product_sku})</span></td>
                  <td className="px-3 py-2 text-right font-bold text-indigo-700">{fmtQty(r.quantity)}</td>
                  <td className="px-3 py-2 text-right font-bold text-emerald-700" data-testid={`prod-produced-${r.id}`}>{isInProd ? <span className="text-slate-300">—</span> : fmtQty(r.produced_quantity ?? r.quantity)}</td>
                  <td className="px-3 py-2 text-right" data-testid={`prod-loss-${r.id}`}>
                    {isInProd ? <span className="text-slate-300">—</span> : (Number(r.loss_quantity || 0) > 0
                      ? <span className="font-bold text-rose-600">{fmtQty(r.loss_quantity)} <span className="text-[10px] font-semibold text-rose-400">({Number(r.loss_pct || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)</span></span>
                      : <span className="text-slate-400">0</span>)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">R$ {Number(r.total_cost||0).toFixed(2).replace('.', ',')}</td>
                  <td className="px-3 py-2 text-right text-slate-700">R$ {Number(r.unit_cost||0).toFixed(2).replace('.', ',')}</td>
                  <td className="px-3 py-2 text-center">
                    {isInProd
                      ? <span className="text-[10px] font-black px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300">EM PRODUCAO</span>
                      : <span className="text-[10px] font-black px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">FINALIZADO</span>
                    }
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => openView(r)} title="Visualizar" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`prod-view-${r.id}`}>
                        <Eye size={14} />
                      </button>
                      {isInProd && (
                        <>
                          <button onClick={() => openEdit(r)} title="Editar" className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600" data-testid={`prod-edit-${r.id}`}>
                            <Edit size={14} />
                          </button>
                          <button onClick={() => finalizeOrder(r)} title="Finalizar (baixa MPs e sobe acabado)" className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600" data-testid={`prod-finalize-${r.id}`}>
                            <CheckCircle size={14} />
                          </button>
                          <button onClick={() => cancelOrder(r)} title="Cancelar" className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500" data-testid={`prod-cancel-${r.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (<tr><td colSpan={9} className="text-center py-8 text-slate-400 text-xs">Nenhuma ordem no periodo</td></tr>)}
          </tbody>
        </table>
      </div>
      {finalizeTarget && (
        <FinalizeProductionModal order={finalizeTarget} onClose={() => setFinalizeTarget(null)} onDone={() => { setFinalizeTarget(null); load(); }} />
      )}
      {modalMode && (
        <NewProductionModal
          mode={modalMode}
          order={modalOrder}
          companyId={companyId}
          companies={companies}
          onClose={closeModal}
          onDone={() => { closeModal(); load(); }}
        />
      )}
    </div>
  );
};

// iter156et: modal de finalizacao — informa a qtd produzida; a diferenca para a planejada e registrada como PERDA
const FinalizeProductionModal = ({ order, onClose, onDone }) => {
  const planned = Number(order.quantity || 0);
  const [produced, setProduced] = useState(String(order.quantity ?? '').replace('.', ','));
  const [saving, setSaving] = useState(false);
  const producedNum = smartQtyParse(produced);
  const valid = isFinite(producedNum) && producedNum > 0 && producedNum <= planned + 1e-9;
  const loss = valid ? Math.max(0, planned - producedNum) : 0;
  const lossPct = planned > 0 ? (loss / planned) * 100 : 0;
  const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
  const submit = async () => {
    if (!valid) { toast.error(`Quantidade produzida deve ser entre 0 e ${fmt(planned)}`); return; }
    setSaving(true);
    try {
      await axios.post(`${ERP}/production-orders/${order.id}/finalize`, { produced_quantity: producedNum }, { withCredentials: true });
      toast.success(loss > 0 ? `Ordem finalizada — perda registrada: ${fmt(loss)}` : 'Ordem finalizada — estoque atualizado');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao finalizar'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="production-finalize-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-black text-slate-900">Finalizar Producao</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-700"><span className="font-bold">{order.product_name}</span> <span className="text-slate-400">({order.product_sku})</span></p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Qtd a Produzir</label>
              <input value={fmt(planned)} disabled className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-600" data-testid="finalize-planned-qty" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Qtd Produzida *</label>
              <input type="text" inputMode="decimal" value={produced} onChange={e => setProduced(e.target.value)} onFocus={e => e.target.select()} autoFocus className={`w-full px-3 py-2 border rounded-lg text-sm font-bold ${valid ? 'border-slate-200' : 'border-rose-400 bg-rose-50'}`} data-testid="finalize-produced-qty" />
            </div>
          </div>
          <div className={`rounded-lg p-3 border ${loss > 0 ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="finalize-loss-preview">
            <p className="text-[10px] uppercase font-bold text-slate-500">Perda no processo</p>
            <p className={`text-lg font-black ${loss > 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{fmt(loss)} <span className="text-xs font-semibold">({lossPct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)</span></p>
            <p className="text-[11px] text-slate-500 mt-1">As materias-primas sao baixadas pela quantidade planejada ({fmt(planned)}); o acabado entra no estoque pela quantidade produzida.</p>
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-slate-900">Cancelar</button>
          <button onClick={submit} disabled={saving || !valid} className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg text-sm font-bold disabled:opacity-50" data-testid="finalize-confirm-btn">{saving ? 'Finalizando...' : 'Finalizar (baixa MPs + sobe acabado)'}</button>
        </div>
      </div>
    </div>
  );
};

const NewProductionModal = ({ mode = 'new', order = null, companyId, companies, onClose, onDone }) => {
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const readonly = isView;
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState(order?.product_id || '');
  const [companyIdSel, setCompanyIdSel] = useState(order?.company_id || companyId || '');
  const [quantity, setQuantity] = useState(order?.quantity != null ? String(order.quantity).replace('.', ',') : '');
  const [bom, setBom] = useState(order ? { bom: order.bom_snapshot || [] } : null);
  const [notes, setNotes] = useState(order?.notes || '');
  const [saving, setSaving] = useState(false);
  // Unit para mascara pt-BR (baseada no acabado escolhido)
  const acabadoUnit = (() => {
    const p = products.find(x => x.id === productId);
    return (p?.fiscal?.unit_commercial || p?.unit || 'UN');
  })();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products?limit=500`, { withCredentials: true });
        setProducts((data.data || data).filter(p => p.is_manufactured));
      } catch {}
    })();
  }, []);
  useEffect(() => {
    if (!productId) { setBom(null); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/products/${productId}/bom`, { withCredentials: true });
        setBom(data);
      } catch { setBom(null); }
    })();
  }, [productId]);

  const submit = async () => {
    if (!productId || !companyIdSel) {
      toast.error('Preencha produto e filial'); return;
    }
    const qty = smartQtyParse(quantity);
    if (!isFinite(qty) || qty <= 0) {
      toast.error('Quantidade deve ser > 0'); return;
    }
    setSaving(true);
    try {
      if (isEdit && order?.id) {
        await axios.put(`${ERP}/production-orders/${order.id}`, { quantity: qty, notes }, { withCredentials: true });
        toast.success('Ordem atualizada');
      } else {
        await axios.post(`${ERP}/production-orders`, { product_id: productId, company_id: companyIdSel, quantity: qty, notes }, { withCredentials: true });
        toast.success('Ordem criada em producao — finalize quando estiver pronto');
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  // Total display por MP: qty (por unidade) x quantity produzida, no formato pt-BR
  const totalMpQty = (b) => {
    const qNum = smartQtyParse(quantity);
    if (!isFinite(qNum) || qNum <= 0) return '—';
    const total = Number(b.qty || b.bom_qty || 0) * qNum;
    return total.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
  };

  const title = isView ? 'Visualizar Ordem' : (isEdit ? 'Editar Ordem em Producao' : 'Nova Ordem de Producao');
  const st = order?.status || 'em_producao';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-black text-slate-900">{title}</h3>
            {order && (
              st === 'em_producao'
                ? <span className="text-[10px] font-black px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300">EM PRODUCAO</span>
                : <span className="text-[10px] font-black px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">FINALIZADO</span>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Produto Acabado *</label>
            {isEdit || isView ? (
              <input value={order?.product_name ? `${order.product_name} (${order.product_sku || ''})` : ''} disabled className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-600" />
            ) : (
              <select value={productId} onChange={e => setProductId(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="production-product-select">
                <option value="">— Selecione um produto acabado —</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
              </select>
            )}
            {!isEdit && !isView && products.length === 0 && (<p className="text-[11px] text-amber-600 mt-1">Nenhum produto marcado como acabado. Ative &ldquo;Producao&rdquo; na aba do produto e cadastre a BOM.</p>)}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Filial *</label>
              {isEdit || isView ? (
                <input value={(companies || []).find(c => c.id === companyIdSel)?.nome_fantasia || (companies || []).find(c => c.id === companyIdSel)?.name || ''} disabled className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-600" />
              ) : (
                <select value={companyIdSel} onChange={e => setCompanyIdSel(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="production-company-select">
                  <option value="">— Filial —</option>
                  {(companies || []).map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.name}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">
                Qtd a Produzir *
                {decimalsForUnit(acabadoUnit) > 0 && <span className="ml-1 text-[9px] font-black px-1 py-0.5 rounded bg-violet-100 text-violet-700">{String(acabadoUnit).toUpperCase()} · {decimalsForUnit(acabadoUnit)} dec</span>}
              </label>
              <input
                type="text" inputMode="decimal"
                value={quantity}
                onChange={e => setQuantity(smartQtyMask(e.target.value, acabadoUnit))}
                onFocus={e => e.target.select()}
                disabled={readonly}
                className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm ${readonly ? 'bg-slate-50 text-slate-600' : ''}`}
                data-testid="production-quantity-input"
              />
            </div>
          </div>
          {isView && st !== 'em_producao' && order?.produced_quantity != null && (
            <div className="grid grid-cols-3 gap-3" data-testid="production-view-loss">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5"><p className="text-[10px] uppercase font-bold text-emerald-700">Qtd Produzida</p><p className="text-base font-black text-emerald-800">{Number(order.produced_quantity).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</p></div>
              <div className={`${Number(order.loss_quantity || 0) > 0 ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'} border rounded-lg p-2.5`}><p className="text-[10px] uppercase font-bold text-rose-700">Perda</p><p className="text-base font-black text-rose-700">{Number(order.loss_quantity || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} <span className="text-xs">({Number(order.loss_pct || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)</span></p></div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5"><p className="text-[10px] uppercase font-bold text-slate-500">Custo da Perda</p><p className="text-base font-black text-slate-800">R$ {Number(order.loss_cost || 0).toFixed(2).replace('.', ',')}</p></div>
            </div>
          )}
          {bom && bom.bom && bom.bom.length > 0 && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
              <p className="text-[10px] uppercase font-bold text-indigo-700 mb-2">MPs necessarias (por unidade)</p>
              <table className="w-full text-xs">
                <thead><tr className="text-left text-slate-500">
                  <th className="pb-1">Materia-prima</th><th className="pb-1 text-right">Qtd/un</th><th className="pb-1 text-right">Total ({quantity || '?'})</th>
                </tr></thead>
                <tbody>
                  {bom.bom.map((b, i) => (
                    <tr key={i} className="border-t border-indigo-200/50">
                      <td className="py-1 text-slate-800 font-semibold">{b.product_name}</td>
                      <td className="py-1 text-right text-slate-600">{Number(b.qty || b.bom_qty || 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 })} {b.unit || b.bom_unit}</td>
                      <td className="py-1 text-right text-slate-900 font-bold">{totalMpQty(b)} {b.unit || b.bom_unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Observacoes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} disabled={readonly} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm ${readonly ? 'bg-slate-50 text-slate-600' : ''}`} />
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-slate-900">{readonly ? 'Fechar' : 'Cancelar'}</button>
          {!readonly && (
            <button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg text-sm font-bold disabled:opacity-50" data-testid="production-submit-btn">
              {saving ? 'Salvando...' : (isEdit ? 'Atualizar' : 'Criar em Producao')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// iter156aq: MDF-e — Manifesto Eletronico
// ============================================================================
const MDFePage = ({ companyId, companies }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (companyId) p.set('company_id', companyId);
      const { data } = await axios.get(`${ERP}/mdfe?${p}`, { withCredentials: true });
      setRows(data.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao carregar MDF-e'); }
    finally { setLoading(false); }
  };
  // iter156cu: state para duplicar MDF-e (pre-preenche formulario com dados de um existente)
  const [duplicateFrom, setDuplicateFrom] = useState(null);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const doAction = async (id, action, body = {}) => {
    try {
      await axios.post(`${ERP}/mdfe/${id}/${action}`, body, { withCredentials: true });
      toast.success('OK');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText size={22} className="text-cyan-600" />
          <h1 className="text-xl font-black text-slate-900">MDF-e — Manifesto Eletronico</h1>
        </div>
        <button onClick={() => setShowNew(true)} className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2" data-testid="mdfe-new-btn">
          <FileText size={14} /> Novo MDF-e
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            <th className="px-3 py-2 text-left font-bold text-slate-500">Numero</th>
            <th className="px-3 py-2 text-left font-bold text-slate-500">Data</th>
            <th className="px-3 py-2 text-left font-bold text-slate-500">Origem→Destino</th>
            <th className="px-3 py-2 text-left font-bold text-slate-500">Veiculo</th>
            <th className="px-3 py-2 text-right font-bold text-slate-500">Valor</th>
            <th className="px-3 py-2 text-center font-bold text-slate-500">Status</th>
            <th className="px-3 py-2 text-center font-bold text-slate-500">Acoes</th>
          </tr></thead>
          <tbody>
            {loading && (<tr><td colSpan={7} className="text-center py-8 text-slate-400">Carregando...</td></tr>)}
            {!loading && rows.map(m => (
              <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`mdfe-row-${m.number}`}>
                <td className="px-3 py-2 font-bold text-slate-900">#{m.number}/{m.series}</td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{new Date(m.created_at).toLocaleString('pt-BR')}</td>
                <td className="px-3 py-2 text-slate-800 font-semibold">{m.uf_origem} → {m.uf_destino}</td>
                <td className="px-3 py-2 text-slate-500">{m.veiculo?.placa}</td>
                <td className="px-3 py-2 text-right text-slate-700">R$ {Number(m.valor_total||0).toFixed(2)}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                    m.status==='emitido'?'bg-emerald-100 text-emerald-700':
                    m.status==='rascunho'?'bg-slate-100 text-slate-600':
                    m.status==='cancelado'?'bg-rose-100 text-rose-700':
                    'bg-indigo-100 text-indigo-700'
                  }`}>{m.status}</span>
                </td>
                <td className="px-3 py-2 text-center text-xs">
                  <div className="flex items-center justify-center gap-1">
                    {m.status==='rascunho' && (
                      <button onClick={() => doAction(m.id, 'emitir')} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors" title="Emitir" data-testid={`mdfe-emitir-${m.number}`}>
                        <Send size={14} />
                      </button>
                    )}
                    {m.status==='emitido' && (
                      <>
                        <button onClick={() => doAction(m.id, 'encerrar')} className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors" title="Encerrar" data-testid={`mdfe-encerrar-${m.number}`}>
                          <CheckCircle size={14} />
                        </button>
                        <button onClick={() => { const j = prompt('Justificativa (min 15 chars):'); if (j && j.length >= 15) doAction(m.id, 'cancelar', { justificativa: j }); }} className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors" title="Cancelar" data-testid={`mdfe-cancelar-${m.number}`}>
                          <XCircle size={14} />
                        </button>
                      </>
                    )}
                    {(m.status==='emitido' || m.status==='encerrado') && (
                      <button
                        onClick={async () => {
                          try {
                            const { data: blob } = await axios.get(`${ERP}/mdfe/${m.id}/pdf`, {
                              responseType: 'blob',
                              withCredentials: true,
                            });
                            const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                            window.open(url, '_blank');
                            setTimeout(() => URL.revokeObjectURL(url), 60000);
                          } catch (e) {
                            let msg = 'Erro ao gerar PDF';
                            try {
                              const errBlob = e.response?.data;
                              if (errBlob && typeof errBlob.text === 'function') {
                                const txt = await errBlob.text();
                                try { msg = JSON.parse(txt).detail || msg; } catch { msg = txt.slice(0, 200) || msg; }
                              } else if (e.response?.data?.detail) {
                                msg = e.response.data.detail;
                              }
                            } catch { /* ignore */ }
                            toast.error(msg);
                          }
                        }}
                        className="p-1.5 rounded-lg text-cyan-700 hover:bg-cyan-50 transition-colors"
                        title="Imprimir DAMDFE (PDF)"
                        data-testid={`mdfe-print-${m.number}`}
                      >
                        <Printer size={14} />
                      </button>
                    )}
                    {/* iter156cu: duplicar — abre modal de novo MDF-e com dados pre-preenchidos */}
                    <button
                      onClick={() => setDuplicateFrom(m)}
                      className="p-1.5 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors"
                      title="Duplicar MDF-e"
                      data-testid={`mdfe-duplicate-${m.number}`}
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && rows.length===0 && (<tr><td colSpan={7} className="text-center py-8 text-slate-400 text-xs">Nenhum MDF-e emitido ainda</td></tr>)}
          </tbody>
        </table>
      </div>
      {showNew && <NewMDFeModal companyId={companyId} companies={companies} onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }} />}
      {/* iter156cu: modal de duplicacao — reaproveita NewMDFeModal com dados iniciais */}
      {duplicateFrom && <NewMDFeModal companyId={companyId} companies={companies} initialData={duplicateFrom} onClose={() => setDuplicateFrom(null)} onDone={() => { setDuplicateFrom(null); load(); }} />}
    </div>
  );
};

const NewMDFeModal = ({ companyId, companies, onClose, onDone, initialData = null }) => {
  // iter156cu: quando duplicando, prefill form + selectedChaves. Se nao veio initialData,
  // usa defaults como antes.
  const _init = initialData || {};
  const [form, setForm] = useState({
    company_id: _init.company_id || companyId || '',
    uf_origem: _init.uf_origem || 'GO',
    uf_destino: _init.uf_destino || 'SP',
    veiculo: {
      placa: _init.veiculo?.placa || '',
      rntrc: _init.veiculo?.rntrc || '',
      tipo: _init.veiculo?.tipo || '01',
    },
    motorista: {
      nome: _init.motorista?.nome || '',
      cpf: _init.motorista?.cpf || '',
    },
    produto_predominante: _init.produto_predominante || '',
    valor_total: '',  // recalcula automaticamente pelas NF-e selecionadas
    nfe_chaves: '',
    notes: _init.notes || '',
  });
  const [saving, setSaving] = useState(false);
  // iter156bp: NF-e picker
  const [availNfes, setAvailNfes] = useState([]);
  const [loadingNfes, setLoadingNfes] = useState(false);
  const [selectedChaves, setSelectedChaves] = useState(new Set());
  const [nfeSearch, setNfeSearch] = useState('');
  const [showManual, setShowManual] = useState(false);

  const loadAvailableNfes = async (cid) => {
    if (!cid) { setAvailNfes([]); return; }
    setLoadingNfes(true);
    try {
      const { data } = await axios.get(`${ERP}/mdfe-available-nfes?company_id=${cid}&limit=300`, { withCredentials: true });
      setAvailNfes(data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar NF-e');
    } finally { setLoadingNfes(false); }
  };
  useEffect(() => { loadAvailableNfes(form.company_id); /* eslint-disable-next-line */ }, [form.company_id]);

  // Auto-recalcula valor_total conforme selecao (somatorio das NF-e escolhidas)
  useEffect(() => {
    const totalCalc = availNfes
      .filter(n => selectedChaves.has(n.chave_acesso))
      .reduce((s, n) => s + Number(n.total || 0), 0);
    if (selectedChaves.size > 0) {
      setForm(f => ({ ...f, valor_total: totalCalc.toFixed(2) }));
    }
    // eslint-disable-next-line
  }, [selectedChaves, availNfes]);

  const toggleChave = (chave) => {
    setSelectedChaves(prev => {
      const n = new Set(prev);
      if (n.has(chave)) n.delete(chave); else n.add(chave);
      return n;
    });
  };
  const toggleAllVisible = (chavesVisiveis, allSelected) => {
    setSelectedChaves(prev => {
      const n = new Set(prev);
      chavesVisiveis.forEach(c => { if (allSelected) n.delete(c); else n.add(c); });
      return n;
    });
  };

  const nfesFiltradas = availNfes.filter(n => {
    if (!nfeSearch) return true;
    const q = nfeSearch.toLowerCase();
    return (
      String(n.numero || '').includes(q) ||
      (n.customer_name || '').toLowerCase().includes(q) ||
      (n.customer_doc || '').includes(q) ||
      (n.chave_acesso || '').includes(q)
    );
  });
  const chavesVisiveis = nfesFiltradas.map(n => n.chave_acesso);
  const allVisibleSelected = chavesVisiveis.length > 0 && chavesVisiveis.every(c => selectedChaves.has(c));

  const submit = async () => {
    setSaving(true);
    try {
      // Combina chaves selecionadas + chaves manuais (fallback)
      const manuais = form.nfe_chaves.split(/[\s,]+/).map(s => s.trim()).filter(s => s.length === 44);
      const chaves = Array.from(new Set([...Array.from(selectedChaves), ...manuais]));
      if (chaves.length === 0) {
        toast.error('Selecione ao menos uma NF-e para vincular');
        setSaving(false);
        return;
      }
      const body = {
        ...form,
        valor_total: parseFloat(String(form.valor_total).replace(',', '.')) || 0,
        nfe_chaves: chaves,
      };
      await axios.post(`${ERP}/mdfe`, body, { withCredentials: true });
      toast.success(`MDF-e criado (rascunho) com ${chaves.length} NF-e vinculada(s) — clique em Emitir para enviar`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao criar'); }
    finally { setSaving(false); }
  };
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updSub = (obj, k, v) => setForm(f => ({ ...f, [obj]: { ...f[obj], [k]: v } }));
  const inp = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
          <h3 className="text-lg font-black">{initialData ? `Duplicar MDF-e${initialData.number ? ` #${initialData.number}` : ''}` : 'Novo MDF-e'}</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Filial *</label>
            <select value={form.company_id} onChange={e => upd('company_id', e.target.value)} className={inp} data-testid="mdfe-company-select">
              <option value="">— Filial —</option>
              {(companies || []).map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.name}</option>)}
            </select>
          </div>
          <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Produto Predominante *</label>
            <input type="text" value={form.produto_predominante} onChange={e => upd('produto_predominante', e.target.value)} className={inp} placeholder="Ex: Roupas, Medicamentos..." /></div>
          <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">UF Origem *</label><input type="text" maxLength={2} value={form.uf_origem} onChange={e => upd('uf_origem', e.target.value.toUpperCase())} className={inp} /></div>
          <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">UF Destino *</label><input type="text" maxLength={2} value={form.uf_destino} onChange={e => upd('uf_destino', e.target.value.toUpperCase())} className={inp} /></div>
          <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Placa Veiculo *</label><input type="text" value={form.veiculo.placa} onChange={e => updSub('veiculo','placa', e.target.value.toUpperCase())} className={inp} placeholder="ABC1D23" data-testid="mdfe-placa" /></div>
          <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">RNTRC</label><input type="text" value={form.veiculo.rntrc} onChange={e => updSub('veiculo','rntrc', e.target.value)} className={inp} /></div>
          <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Motorista Nome *</label><input type="text" value={form.motorista.nome} onChange={e => updSub('motorista','nome', e.target.value)} className={inp} /></div>
          <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Motorista CPF *</label><input type="text" value={form.motorista.cpf} onChange={e => updSub('motorista','cpf', e.target.value.replace(/\D/g,''))} className={inp} maxLength={11} /></div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Valor Total * <span className="text-slate-400 normal-case font-normal">(auto-calculado pela soma das NF-e; editavel)</span></label>
            <input type="text" inputMode="decimal" value={form.valor_total} onChange={e => upd('valor_total', e.target.value)} className={inp} />
          </div>
        </div>

        {/* iter156bp: seletor de NF-e ja emitidas — vinculo automatico */}
        <div className="px-5 pb-2">
          <div className="flex items-center justify-between mb-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 block">NF-e para vincular ao MDF-e</label>
              <p className="text-[10px] text-slate-400">Selecione as NF-e ja emitidas nesta filial. O valor total sera somado automaticamente.</p>
            </div>
            <div className="text-[11px] font-bold text-cyan-700 bg-cyan-50 px-3 py-1 rounded-full" data-testid="mdfe-selected-count">
              {selectedChaves.size} selecionada(s)
            </div>
          </div>

          <div className="relative mb-2">
            <input
              value={nfeSearch}
              onChange={e => setNfeSearch(e.target.value)}
              placeholder="Buscar por numero, cliente, CNPJ/CPF ou chave..."
              className={inp}
              data-testid="mdfe-nfe-search"
            />
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200 text-[10px] font-bold text-slate-600 uppercase">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={() => toggleAllVisible(chavesVisiveis, allVisibleSelected)}
                  disabled={chavesVisiveis.length === 0}
                  data-testid="mdfe-nfe-select-all"
                />
                Selecionar todas visiveis ({chavesVisiveis.length})
              </label>
              <span className="text-slate-400 normal-case font-normal">status=emitida | tipo=NF-e</span>
            </div>
            {loadingNfes ? (
              <div className="p-6 text-center text-xs text-slate-400">Carregando NF-e...</div>
            ) : !form.company_id ? (
              <div className="p-6 text-center text-xs text-amber-600">Selecione uma Filial primeiro.</div>
            ) : nfesFiltradas.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400" data-testid="mdfe-nfe-empty">
                {availNfes.length === 0
                  ? 'Nenhuma NF-e emitida encontrada nesta filial.'
                  : 'Nenhuma NF-e casa com essa busca.'}
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                {nfesFiltradas.map(n => {
                  const sel = selectedChaves.has(n.chave_acesso);
                  return (
                    <label
                      key={n.chave_acesso}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-xs ${sel ? 'bg-cyan-50' : 'hover:bg-slate-50'}`}
                      data-testid={`mdfe-nfe-option-${n.numero}`}
                    >
                      <input type="checkbox" checked={sel} onChange={() => toggleChave(n.chave_acesso)} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-black text-slate-800">NF-e #{n.numero}/{n.serie}</span>
                          <span className="text-[10px] text-slate-400">{n.created_at ? new Date(n.created_at).toLocaleDateString('pt-BR') : ''}</span>
                        </div>
                        <div className="text-slate-600 truncate">{n.customer_name || '—'} {n.customer_doc && <span className="text-slate-400 font-mono ml-1">{n.customer_doc}</span>}</div>
                        <div className="font-mono text-[9px] text-slate-400 truncate">{(n.chave_acesso || '').replace(/(\d{4})(?=\d)/g, '$1 ')}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-black text-slate-800">R$ {Number(n.total || 0).toFixed(2).replace('.', ',')}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-2">
            <button type="button" onClick={() => setShowManual(v => !v)} className="text-[11px] text-cyan-700 hover:underline font-bold" data-testid="mdfe-toggle-manual">
              {showManual ? '− Ocultar campo manual' : '+ Adicionar chave(s) manualmente'}
            </button>
            {showManual && (
              <textarea
                value={form.nfe_chaves}
                onChange={e => upd('nfe_chaves', e.target.value)}
                rows={2}
                className={`${inp} mt-1`}
                placeholder="Cole chaves de acesso adicionais aqui (44 digitos cada, separadas por virgula ou linha)"
                data-testid="mdfe-nfe-manual"
              />
            )}
          </div>
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-slate-900">Cancelar</button>
          <button onClick={submit} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white px-5 py-2 rounded-lg text-sm font-bold disabled:opacity-50" data-testid="mdfe-submit-btn">{saving?'Salvando...':'Salvar Rascunho'}</button>
        </div>
      </div>
    </div>
  );
};

const Loader = () => <div className="flex justify-center items-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent" /></div>;
const NoCompany = () => <div className="text-center py-12"><Building2 size={40} className="mx-auto text-gray-300 mb-3" /><p className="text-gray-500 font-medium">Selecione uma empresa</p><p className="text-xs text-gray-400 mt-1">Use o seletor na barra lateral</p></div>;
const EmptyState = ({ icon: Icon, text }) => <div className="text-center py-12 bg-white border border-gray-200 rounded-xl"><Icon size={40} className="mx-auto text-gray-300 mb-3" /><p className="text-gray-500 text-sm">{text}</p></div>;

export { ERPDashboard };
export default ERPDashboard;
